import React, { useState } from "react";
import {
  HipaaSourceVerificationsSection,
  SingleBotKnowledgeSourcesTab,
  useBotVerificationsData,
  UNKNOWN_BOT_ID,
  type SourceVerification,
} from "@/components/hipaa-source-verifications";
import { LinkedSourceRow, SourceReferenceField } from "@/components/hipaa-linked-source";
import { HipaaAccessControlSection } from "@/components/hipaa-access-control-section";
import { HipaaAuditControlsSection } from "@/components/hipaa-audit-controls-section";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertTriangle, ShieldAlert, FileText, User, Users, Activity,
  Plus, Trash2, RefreshCcw, Eye, Pencil, Calendar,
  CheckCircle2, Clock, XCircle, Building2, Shield, Lock,
  ChevronDown, ChevronUp,
} from "lucide-react";
import { apiRequest, queryClient, fieldError } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { fErr, fieldCls, clearErr, extractServerErrors, required } from "@/lib/form-validation";

// ── Types ──────────────────────────────────────────────────────────────────────

type HipaaSummary = {
  incidents: { open: number; confirmedBreachesAwaitingHhs: number };
  baa:       { active: number; expired: number; expiringSoon: number };
  training:  { trainedUsers: number; totalUsers: number; coveragePct: number };
  phiAccess: { eventsLast30Days: number };
  sourceVerifications?: {
    total: number; blocked: number; allowed: number;
    activeExceptions: number; expiredExceptions: number; openAlerts: number;
  };
};

type PhiAccessItem = {
  id: string; userId: string | null; recordType: string | null;
  recordId: string | null; recordCount: number | null;
  action: string | null; outcome: string | null;
  ipAddress: string | null; timestamp: string;
};

type Incident = {
  id: string; incidentType: string; severity: string; status: string;
  breachStatus: string; phiInvolved: boolean; affectedCount: number | null;
  description: string; notes: string | null;
  discoveredAt: string; reportedAt: string | null; resolvedAt: string | null;
  hhsReportedAt: string | null; individualsNotifiedAt: string | null;
  mediaNotifiedAt: string | null; createdAt: string; updatedAt: string;
};

type Baa = {
  id: string; associateName: string; associateType: string;
  serviceDescription: string; contactEmail: string | null;
  agreementDate: string | null; expiresAt: string | null;
  status: string; documentUrl: string | null; notes: string | null;
  createdAt: string; updatedAt: string;
};

type TrainingRecord = {
  id: string; userId: string; trainingType: string; trainingTitle: string;
  completedAt: string; expiresAt: string | null; evidenceUrl: string | null;
  notes: string | null; createdAt: string;
};

type PagedResult<T> = { total: number; page: number; pageSize: number; items: T[] };

// HIPAA-03 — Security Official
type SecurityOfficial = {
  id: string;
  officialName: string;
  officialEmail: string;
  jobTitle: string;
  responsibilities: string;
  authorityScope: string | null;
  appointmentDate: string;
  effectiveFrom: string;
  effectiveUntil: string | null;
  appointedBy: string;
  status: "Active" | "Inactive" | "Replaced";
  lastReviewDate: string | null;
  nextReviewDate: string | null;
  reviewNotes: string | null;
  createdAt: string;
  updatedAt: string;
};

type SecurityOfficialListResult = {
  currentOfficial: SecurityOfficial | null;
  hasActiveOfficial: boolean;
  history: SecurityOfficial[];
  total: number;
};

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmtDate(iso: string | null | undefined) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function fmtDateTime(iso: string | null | undefined) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    month: "short", day: "numeric", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function isExpired(iso: string | null | undefined) {
  if (!iso) return false;
  return new Date(iso) < new Date();
}

function isExpiringSoon(iso: string | null | undefined) {
  if (!iso) return false;
  const d = new Date(iso);
  const now = new Date();
  return d > now && d < new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
}

// ── Dropdown constants (must match backend ValidXxx sets exactly) ──────────────

const INCIDENT_TYPES   = ["UnauthorizedAccess","ImproperDisclosure","LostDevice","SystemBreach","ImproperDisposal","Other"];
const SEVERITIES       = ["Low","Medium","High","Critical"];
const INCIDENT_STATUSES = ["Open","UnderInvestigation","Remediated","Closed"];
const BREACH_STATUSES  = ["Pending","NotABreach","ConfirmedBreach","Excluded"];
const ASSOCIATE_TYPES  = ["CloudStorage","EHR","Analytics","Billing","Communication","IdentityProvider","Other"];
const BAA_STATUSES     = ["Active","Pending","Expired","Terminated"];
const TRAINING_TYPES   = ["HipaaPrivacy","HipaaSecurity","PhiHandling","BreachPrevention","SecurityAwareness","Other"];

// Human-readable labels
const INCIDENT_TYPE_LABELS: Record<string,string> = {
  UnauthorizedAccess: "Unauthorized Access", ImproperDisclosure: "Improper Disclosure",
  LostDevice: "Lost Device", SystemBreach: "System Breach",
  ImproperDisposal: "Improper Disposal", Other: "Other",
};
const TRAINING_TYPE_LABELS: Record<string,string> = {
  HipaaPrivacy: "HIPAA Privacy", HipaaSecurity: "HIPAA Security",
  PhiHandling: "PHI Handling", BreachPrevention: "Breach Prevention",
  SecurityAwareness: "Security Awareness", Other: "Other",
};

// ── Badge color helpers ────────────────────────────────────────────────────────

function severityBadge(s: string) {
  const cls =
    s === "Critical" ? "border-red-500/40 bg-red-500/10 text-red-600 dark:text-red-400" :
    s === "High"     ? "border-orange-500/40 bg-orange-500/10 text-orange-600 dark:text-orange-400" :
    s === "Medium"   ? "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400" :
                       "border-muted text-muted-foreground";
  return <Badge variant="outline" className={`text-[10px] h-4 px-1.5 ${cls}`}>{s}</Badge>;
}

function statusBadge(s: string) {
  const cls =
    s === "Open"               ? "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400" :
    s === "UnderInvestigation" ? "border-blue-500/40 bg-blue-500/10 text-blue-600 dark:text-blue-400" :
    s === "Remediated"         ? "border-primary/40 bg-primary/10 text-primary" :
    s === "Closed"             ? "border-muted text-muted-foreground" :
    s === "Active"             ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" :
    s === "Terminated"         ? "border-muted text-muted-foreground" :
    s === "Pending"            ? "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400" :
    s === "Expired"            ? "border-red-500/40 bg-red-500/10 text-red-600 dark:text-red-400" :
                                  "border-muted text-muted-foreground";
  return <Badge variant="outline" className={`text-[10px] h-4 px-1.5 ${cls}`}>{s === "UnderInvestigation" ? "Investigating" : s}</Badge>;
}

function breachBadge(s: string) {
  const cls =
    s === "ConfirmedBreach" ? "border-red-500/40 bg-red-500/10 text-red-600 dark:text-red-400" :
    s === "NotABreach"      ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" :
    s === "Excluded"        ? "border-muted text-muted-foreground" :
                               "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400";
  return <Badge variant="outline" className={`text-[10px] h-4 px-1.5 ${cls}`}>{s === "ConfirmedBreach" ? "Confirmed Breach" : s}</Badge>;
}

// ── Shared UI: simple select ──────────────────────────────────────────────────

function Sel({ id, value, onChange, options, placeholder }: {
  id?: string; value: string; onChange: (v: string) => void;
  options: string[]; placeholder?: string;
}) {
  return (
    <select
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full text-sm border rounded-md p-1.5 bg-background"
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
  );
}

// ── Empty state ────────────────────────────────────────────────────────────────

function EmptyState({ icon: Icon, message, sub }: {
  icon: React.ElementType; message: string; sub?: string;
}) {
  return (
    <div className="text-center py-10 text-sm text-muted-foreground">
      <Icon className="w-8 h-8 mx-auto mb-2 opacity-25" aria-hidden="true" />
      <p>{message}</p>
      {sub && <p className="text-xs mt-1">{sub}</p>}
    </div>
  );
}

// ── Risk level helpers (HIPAA-02) ─────────────────────────────────────────────

const RISK_LEVELS    = ["Low","Medium","High","Critical"] as const;
const RISK_STATUSES  = ["Open","InProgress","Mitigated","Accepted","Closed"] as const;
const RISK_CATS      = ["Technical","Administrative","Physical","Operational","ThirdParty","Other"];

function riskLevelBadge(level: string) {
  const cls =
    level === "Critical" ? "border-red-500/40 bg-red-500/10 text-red-600 dark:text-red-400" :
    level === "High"     ? "border-orange-500/40 bg-orange-500/10 text-orange-600 dark:text-orange-400" :
    level === "Medium"   ? "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400" :
                           "border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400";
  return <Badge variant="outline" className={`text-[10px] h-4 px-1.5 ${cls}`}>{level}</Badge>;
}

function riskStatusBadge(s: string) {
  const cls =
    s === "Open"       ? "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400" :
    s === "InProgress" ? "border-blue-500/40 bg-blue-500/10 text-blue-600 dark:text-blue-400" :
    s === "Mitigated"  ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" :
    s === "Accepted"   ? "border-purple-500/40 bg-purple-500/10 text-purple-600 dark:text-purple-400" :
                         "border-muted text-muted-foreground";
  return <Badge variant="outline" className={`text-[10px] h-4 px-1.5 ${cls}`}>{s === "InProgress" ? "In Progress" : s}</Badge>;
}

// Preview score for the form — must match CalcRiskScore/CalcRiskLevel on server
function previewScore(l: number, i: number) { return l * i; }
function previewLevel(score: number) {
  return score <= 5 ? "Low" : score <= 10 ? "Medium" : score <= 17 ? "High" : "Critical";
}

// ── Types ─────────────────────────────────────────────────────────────────────

type RiskRegister = {
  id: string; riskTitle: string; riskDescription: string; riskCategory: string;
  assetOrSystem: string | null; threat: string; vulnerability: string;
  likelihood: number; impact: number; inherentRiskScore: number; riskLevel: string;
  existingControls: string | null; controlReferences: string | null;
  treatmentPlan: string | null; mitigationActions: string | null;
  riskOwner: string | null; targetRemediationDate: string | null;
  residualLikelihood: number | null; residualImpact: number | null;
  residualRiskScore: number | null; residualRiskLevel: string | null;
  status: string; remediationEvidence: string | null; reviewDate: string | null;
  createdAt: string; updatedAt: string;
};

type RiskDetail = { risk: RiskRegister; auditHistory: { action: string; changes: string | null; timestamp: string }[] };

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION — HIPAA-03 Security Responsibility
// ═══════════════════════════════════════════════════════════════════════════════

const RESPONSIBILITY_OPTIONS = [
  "HIPAA Security Rule policies and procedures",
  "Security risk management",
  "Access control oversight",
  "Security incident coordination",
  "Security awareness/security training oversight",
  "Audit and monitoring oversight",
  "Security policy review",
  "Risk remediation oversight",
  "Vendor/security control oversight",
];

function officialStatusBadge(s: string) {
  const cls =
    s === "Active"   ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" :
    s === "Replaced" ? "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400" :
                       "border-muted text-muted-foreground";
  return <Badge variant="outline" className={`text-[10px] h-4 px-1.5 ${cls}`}>{s}</Badge>;
}

// ═══════════════════════════════════════════════════════════════════════════
// HIPAA-04 — Workforce Security
// ═══════════════════════════════════════════════════════════════════════════

type WorkforceRecord = {
  id: string;
  employeeName: string;
  email: string;
  jobTitle: string;
  department: string;
  workforceRole: string;
  supervisorUserId: string | null;
  employmentStatus: "Active" | "Suspended" | "Terminated" | "OnLeave";
  ephiAccessStatus: "Granted" | "Restricted" | "Revoked";
  authorizedRoles: string;
  authorizedDataScopes: string;
  accessGrantedAt: string | null;
  accessRevokedAt: string | null;
  statusChangedAt: string | null;
  lastAccessReviewDate: string | null;
  nextAccessReviewDate: string | null;
  reviewNotes: string | null;
  createdAt: string;
  updatedAt: string;
};

type WorkforceListResult = {
  total: number; page: number; pageSize: number;
  items: WorkforceRecord[];
};

const EMPLOYMENT_STATUSES = ["Active", "Suspended", "Terminated", "OnLeave"] as const;
const EPHI_STATUSES       = ["Granted", "Restricted", "Revoked"] as const;
const COMMON_SCOPES = [
  "PatientDemographics", "MedicationRecords", "LabResults",
  "ImagingRecords", "MentalHealthRecords", "SubstanceAbuseRecords",
  "FullMedicalHistory", "BillingAndInsurance",
];

function workforceEmpBadge(s: string) {
  const map: Record<string, string> = {
    Active: "bg-green-100 text-green-800",
    Suspended: "bg-yellow-100 text-yellow-800",
    Terminated: "bg-red-100 text-red-800",
    OnLeave: "bg-blue-100 text-blue-800",
  };
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${map[s] ?? "bg-gray-100 text-gray-700"}`}>{s}</span>;
}

function workforceEphiBadge(s: string) {
  const map: Record<string, string> = {
    Granted: "bg-green-100 text-green-800",
    Restricted: "bg-orange-100 text-orange-800",
    Revoked: "bg-red-100 text-red-800",
  };
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${map[s] ?? "bg-gray-100 text-gray-700"}`}>{s}</span>;
}

const EMPTY_WORKFORCE = {
  employeeName: "", email: "", jobTitle: "", department: "",
  workforceRole: "", authorizedRoles: "", authorizedDataScopes: "",
  employmentStatus: "Active" as WorkforceRecord["employmentStatus"], ephiAccessStatus: "Granted" as WorkforceRecord["ephiAccessStatus"],
  supervisorUserId: "", lastAccessReviewDate: "", nextAccessReviewDate: "", reviewNotes: "",
};

function HipaaWorkforceSecuritySection({ canManage }: { canManage: boolean }) {
  const { toast } = useToast();
  const [tab, setTab] = useState<"list" | "add" | "check">("list");
  const [form, setForm] = useState({ ...EMPTY_WORKFORCE });
  const [scopeChecks, setScopeChecks] = useState<string[]>([]);
  const [editing, setEditing] = useState<WorkforceRecord | null>(null);
  const [fieldErrs, setFieldErrs] = useState<Record<string, string>>({});
  const [filterStatus, setFilterStatus] = useState("");
  const [filterEphi, setFilterEphi]     = useState("");
  const [detailId, setDetailId] = useState<string | null>(null);
  const [checkId, setCheckId]   = useState("");
  const [checkScope, setCheckScope] = useState("");
  const [checkPurpose, setCheckPurpose] = useState("");
  const [checkResult, setCheckResult] = useState<{ allowed: boolean; reason: string | null } | null>(null);

  const BASE = "/api/organization/compliance/hipaa/workforce";

  const params = new URLSearchParams();
  if (filterStatus) params.set("employmentStatus", filterStatus);
  if (filterEphi)   params.set("ephiAccessStatus",  filterEphi);
  const listUrl = `${BASE}?${params.toString()}`;

  const { data, isLoading, refetch } = useQuery<WorkforceListResult>({
    queryKey: [BASE, filterStatus, filterEphi],
    queryFn: async () => {
      const token = localStorage.getItem("authToken") ?? sessionStorage.getItem("authToken") ?? "";
      const r = await fetch(listUrl, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error("Failed to load workforce records.");
      return r.json();
    },
  });

  const { data: detail } = useQuery<WorkforceRecord>({
    queryKey: [BASE, detailId],
    enabled: !!detailId,
    queryFn: async () => {
      const token = localStorage.getItem("authToken") ?? sessionStorage.getItem("authToken") ?? "";
      const r = await fetch(`${BASE}/${detailId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error("Failed to load record.");
      return r.json();
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (payload: typeof EMPTY_WORKFORCE) => {
      const token = localStorage.getItem("authToken") ?? sessionStorage.getItem("authToken") ?? "";
      const url    = editing ? `${BASE}/${editing.id}` : BASE;
      const method = editing ? "PUT" : "POST";
      const r = await fetch(url, {
        method, headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...payload,
          authorizedDataScopes: scopeChecks.join(","),
          supervisorUserId: payload.supervisorUserId || null,
        }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        const fe = body.errors as Record<string, string[]> | undefined;
        if (fe) { const m: Record<string, string> = {}; for (const k in fe) m[k.toLowerCase()] = fe[k][0]; throw m; }
        throw new Error(body.message ?? "Unable to save");
      }
      return r.json();
    },
    onSuccess: () => {
      toast({ title: editing ? "Record updated." : "Workforce member added." });
      setForm({ ...EMPTY_WORKFORCE }); setScopeChecks([]); setEditing(null);
      setFieldErrs({}); setTab("list"); refetch();
    },
    onError: (e: unknown) => {
      if (typeof e === "object" && e !== null && !("message" in e)) {
        setFieldErrs(e as Record<string, string>);
      } else {
        toast({ title: "Unable to save", description: (e as Error).message, variant: "destructive" });
      }
    },
  });

  const statusMutation = useMutation({
    mutationFn: async ({ id, empStatus, ephiStatus }: { id: string; empStatus?: string; ephiStatus?: string }) => {
      const token = localStorage.getItem("authToken") ?? sessionStorage.getItem("authToken") ?? "";
      const r = await fetch(`${BASE}/${id}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ employmentStatus: empStatus, ephiAccessStatus: ephiStatus }),
      });
      if (!r.ok) throw new Error("Status update failed.");
      return r.json();
    },
    onSuccess: () => { toast({ title: "Status updated." }); refetch(); },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  const handleSubmit = () => {
    const errs: Record<string, string> = {};
    if (!form.employeeName.trim()) errs.employeename = "Employee name is required.";
    if (!form.email.trim())        errs.email         = "Email is required.";
    if (!form.jobTitle.trim())     errs.jobtitle      = "Job title is required.";
    if (!form.department.trim())   errs.department    = "Department is required.";
    if (!form.workforceRole.trim()) errs.workforcerole = "Workforce role is required.";
    if (scopeChecks.length === 0)  errs.authorizeddatascopes = "At least one data scope is required.";
    if (Object.keys(errs).length) { setFieldErrs(errs); return; }
    saveMutation.mutate(form);
  };

  const startEdit = (w: WorkforceRecord) => {
    setEditing(w);
    setForm({
      employeeName: w.employeeName, email: w.email, jobTitle: w.jobTitle,
      department: w.department, workforceRole: w.workforceRole,
      authorizedRoles: w.authorizedRoles, authorizedDataScopes: w.authorizedDataScopes,
      employmentStatus: w.employmentStatus, ephiAccessStatus: w.ephiAccessStatus,
      supervisorUserId: w.supervisorUserId ?? "",
      lastAccessReviewDate: w.lastAccessReviewDate?.slice(0, 10) ?? "",
      nextAccessReviewDate: w.nextAccessReviewDate?.slice(0, 10) ?? "",
      reviewNotes: w.reviewNotes ?? "",
    });
    setScopeChecks(w.authorizedDataScopes.split(",").map(s => s.trim()).filter(Boolean));
    setTab("add");
  };

  const runAccessCheck = async () => {
    if (!checkId || !checkScope) return;
    const token = localStorage.getItem("authToken") ?? sessionStorage.getItem("authToken") ?? "";
    try {
      const r = await fetch(`${BASE}/${checkId}/access-check`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ requestedScope: checkScope, purpose: checkPurpose }),
      });
      const body = await r.json();
      setCheckResult(body);
    } catch {
      toast({ title: "Unable to complete access check", variant: "destructive" });
    }
  };

  const f = (k: keyof typeof EMPTY_WORKFORCE) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm(p => ({ ...p, [k]: e.target.value }));

  const toggleScope = (s: string) =>
    setScopeChecks(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-600" />
              Workforce Security — ePHI Access Registry
            </CardTitle>
            <CardDescription>
              HIPAA § 164.308(a)(3) — Manage workforce ePHI authorization, supervision, and Joiner/Mover/Leaver lifecycle.
            </CardDescription>
          </div>
          {canManage && (
            <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2" onClick={() => { setEditing(null); setForm({ ...EMPTY_WORKFORCE }); setScopeChecks([]); setTab("add"); }}>
              <Plus className="h-4 w-4" /> Add Member
            </Button>
          )}
        </div>
        {/* Tab row */}
        <div className="flex gap-2 mt-3 border-b pb-0">
          {(["list", "add", "check"] as const).map(t => (
            <button key={t} onClick={() => { if (t !== "add") { setEditing(null); setFieldErrs({}); } setTab(t); }}
              className={`px-4 py-1.5 text-sm font-medium border-b-2 transition-colors ${tab === t ? "border-blue-600 text-blue-700" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
              {t === "list" ? "Registry" : t === "add" ? (editing ? "Edit Member" : "Add Member") : "Access Check"}
            </button>
          ))}
        </div>
      </CardHeader>

      <CardContent>
        {/* ── Registry list ── */}
        {tab === "list" && (
          <>
            <div className="flex gap-3 mb-4 flex-wrap">
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                className="text-sm border rounded px-2 py-1">
                <option value="">All employment statuses</option>
                {EMPLOYMENT_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <select value={filterEphi} onChange={e => setFilterEphi(e.target.value)}
                className="text-sm border rounded px-2 py-1">
                <option value="">All ePHI statuses</option>
                {EPHI_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <Button size="sm" variant="outline" onClick={() => refetch()}>
                <RefreshCcw className="h-3 w-3 mr-1" /> Refresh
              </Button>
            </div>

            {isLoading ? (
              <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-10 w-full" />)}</div>
            ) : !data?.items?.length ? (
              <p className="text-muted-foreground text-sm py-6 text-center">No workforce records found.</p>
            ) : (
              <div className="space-y-2">
                {data.items.map(w => (
                  <div key={w.id}
                    className="border rounded-lg p-3 flex items-center justify-between gap-4 hover:bg-muted/30 transition">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm truncate">{w.employeeName}</div>
                      <div className="text-xs text-muted-foreground">
                        {w.jobTitle} · {w.department} · {w.workforceRole}
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        {workforceEmpBadge(w.employmentStatus)}
                        {workforceEphiBadge(w.ephiAccessStatus)}
                        {w.authorizedDataScopes.split(",").filter(Boolean).slice(0, 3).map(s => (
                          <span key={s} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">{s.trim()}</span>
                        ))}
                        {w.authorizedDataScopes.split(",").filter(Boolean).length > 3 && (
                          <span className="text-xs text-muted-foreground">+{w.authorizedDataScopes.split(",").length - 3} more</span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button size="sm" variant="outline" onClick={() => { setDetailId(w.id); }}>
                        <Eye className="h-3 w-3" />
                      </Button>
                      {canManage && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => startEdit(w)}>
                            <Pencil className="h-3 w-3" />
                          </Button>
                          {w.employmentStatus !== "Terminated" && (
                            <Button size="sm" variant="destructive"
                              onClick={() => { if (confirm(`Terminate ${w.employeeName} and immediately revoke ePHI access?`)) statusMutation.mutate({ id: w.id, empStatus: "Terminated" }); }}>
                              Terminate
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
                <p className="text-xs text-muted-foreground pt-1">Total: {data.total} records</p>
              </div>
            )}
          </>
        )}

        {/* ── Add / Edit form ── */}
        {tab === "add" && (
          <div className="space-y-4 max-w-2xl">
            {editing && (
              <div className="bg-amber-50 border border-amber-200 rounded p-2 text-sm text-amber-800 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0" /> Editing: <strong>{editing.employeeName}</strong>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Employee Name *</Label>
                <Input value={form.employeeName} onChange={f("employeeName")} placeholder="Jane Smith" />
                {fieldErrs.employeename && <p className="text-xs text-red-500 mt-0.5">{fieldErrs.employeename}</p>}
              </div>
              <div>
                <Label>Email *</Label>
                <Input value={form.email} onChange={f("email")} placeholder="jane@hospital.org" type="email" />
                {fieldErrs.email && <p className="text-xs text-red-500 mt-0.5">{fieldErrs.email}</p>}
              </div>
              <div>
                <Label>Job Title *</Label>
                <Input value={form.jobTitle} onChange={f("jobTitle")} placeholder="Registered Nurse" />
                {fieldErrs.jobtitle && <p className="text-xs text-red-500 mt-0.5">{fieldErrs.jobtitle}</p>}
              </div>
              <div>
                <Label>Department *</Label>
                <Input value={form.department} onChange={f("department")} placeholder="Nursing" />
                {fieldErrs.department && <p className="text-xs text-red-500 mt-0.5">{fieldErrs.department}</p>}
              </div>
              <div>
                <Label>Workforce Role *</Label>
                <Input value={form.workforceRole} onChange={f("workforceRole")} placeholder="Nurse" />
                {fieldErrs.workforcerole && <p className="text-xs text-red-500 mt-0.5">{fieldErrs.workforcerole}</p>}
              </div>
              <div>
                <Label>Authorized Job Roles</Label>
                <Input value={form.authorizedRoles} onChange={f("authorizedRoles")} placeholder="ClinicalStaff, Nurse" />
              </div>
              <div>
                <Label>Employment Status</Label>
                <select value={form.employmentStatus} onChange={f("employmentStatus")}
                  className="w-full border rounded px-2 py-1.5 text-sm">
                  {EMPLOYMENT_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <Label>ePHI Access Status</Label>
                <select value={form.ephiAccessStatus} onChange={f("ephiAccessStatus")}
                  className="w-full border rounded px-2 py-1.5 text-sm">
                  {EPHI_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <Label>Last Review Date</Label>
                <Input type="date" value={form.lastAccessReviewDate} onChange={f("lastAccessReviewDate")} />
              </div>
              <div>
                <Label>Next Review Date</Label>
                <Input type="date" value={form.nextAccessReviewDate} onChange={f("nextAccessReviewDate")} />
              </div>
            </div>

            {/* ePHI Scope checkboxes */}
            <div>
              <Label>Authorized ePHI Data Scopes *</Label>
              {fieldErrs.authorizeddatascopes && (
                <p className="text-xs text-red-500 mb-1">{fieldErrs.authorizeddatascopes}</p>
              )}
              <div className="grid grid-cols-2 gap-1.5 mt-1">
                {COMMON_SCOPES.map(s => (
                  <label key={s} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={scopeChecks.includes(s)} onChange={() => toggleScope(s)} />
                    {s}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label>Review Notes</Label>
              <Textarea value={form.reviewNotes} onChange={f("reviewNotes")} rows={2} placeholder="Annual access review notes…" />
            </div>

            <div className="flex gap-2">
              <Button onClick={handleSubmit} disabled={saveMutation.isPending}>
                {saveMutation.isPending ? "Saving…" : editing ? "Update Member" : "Add Member"}
              </Button>
              <Button variant="outline" onClick={() => { setEditing(null); setFieldErrs({}); setTab("list"); }}>Cancel</Button>
            </div>
          </div>
        )}

        {/* ── Access Check ── */}
        {tab === "check" && (
          <div className="space-y-4 max-w-lg">
            <p className="text-sm text-muted-foreground">
              Validate whether a workforce member may access a specific ePHI data scope.
            </p>
            <div>
              <Label>Workforce Record ID</Label>
              <Input value={checkId} onChange={e => setCheckId(e.target.value)} placeholder="Paste the UUID from the registry" />
            </div>
            <div>
              <Label>Requested ePHI Scope</Label>
              <select value={checkScope} onChange={e => setCheckScope(e.target.value)}
                className="w-full border rounded px-2 py-1.5 text-sm">
                <option value="">Select scope…</option>
                {COMMON_SCOPES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <Label>Purpose / Business Justification *</Label>
              <Textarea value={checkPurpose} onChange={e => setCheckPurpose(e.target.value)}
                rows={2} placeholder="E.g. Treating physician reviewing patient chart for scheduled appointment." />
            </div>
            <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2" onClick={runAccessCheck} disabled={!checkId || !checkScope}><CheckCircle2 className="h-4 w-4" /> Run Access Check</Button>

            {checkResult && (
              <div className={`mt-3 p-4 rounded-lg border ${checkResult.allowed ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
                <div className="flex items-center gap-2 font-medium text-sm">
                  {checkResult.allowed
                    ? <><CheckCircle2 className="h-4 w-4 text-green-600" /> Access Allowed</>
                    : <><XCircle className="h-4 w-4 text-red-600" /> Access Denied</>}
                </div>
                {checkResult.reason && (
                  <p className="text-xs text-muted-foreground mt-1">Reason: {checkResult.reason}</p>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>

      {/* ── Detail dialog ── */}
      <Dialog open={!!detailId} onOpenChange={open => { if (!open) setDetailId(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Workforce Member Detail</DialogTitle>
            <DialogDescription>ePHI access record and current status.</DialogDescription>
          </DialogHeader>
          {detail ? (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-muted-foreground">Name:</span> {detail.employeeName}</div>
                <div><span className="text-muted-foreground">Email:</span> {detail.email}</div>
                <div><span className="text-muted-foreground">Title:</span> {detail.jobTitle}</div>
                <div><span className="text-muted-foreground">Department:</span> {detail.department}</div>
                <div><span className="text-muted-foreground">Role:</span> {detail.workforceRole}</div>
                <div className="flex items-center gap-1">
                  <span className="text-muted-foreground">Employment:</span> {workforceEmpBadge(detail.employmentStatus)}
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-muted-foreground">ePHI Access:</span> {workforceEphiBadge(detail.ephiAccessStatus)}
                </div>
                {detail.accessGrantedAt && (
                  <div><span className="text-muted-foreground">Granted:</span> {new Date(detail.accessGrantedAt).toLocaleDateString()}</div>
                )}
                {detail.accessRevokedAt && (
                  <div className="text-red-700"><span className="text-muted-foreground">Revoked:</span> {new Date(detail.accessRevokedAt).toLocaleDateString()}</div>
                )}
              </div>
              <div>
                <span className="text-muted-foreground text-xs uppercase font-medium">Authorized Scopes</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {detail.authorizedDataScopes.split(",").filter(Boolean).map(s => (
                    <span key={s} className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">{s.trim()}</span>
                  ))}
                </div>
              </div>
              {detail.reviewNotes && (
                <div>
                  <span className="text-muted-foreground text-xs uppercase font-medium">Review Notes</span>
                  <p className="mt-0.5 text-sm">{detail.reviewNotes}</p>
                </div>
              )}
            </div>
          ) : <Skeleton className="h-24 w-full" />}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailId(null)}>Close</Button>
            {canManage && detail && detail.employmentStatus !== "Terminated" && (
              <Button variant="destructive"
                onClick={() => {
                  if (confirm(`Terminate ${detail.employeeName}?`))
                    statusMutation.mutate({ id: detail.id, empStatus: "Terminated" });
                  setDetailId(null);
                }}>
                Terminate
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// HIPAA-05 — Information Access Management
// ═══════════════════════════════════════════════════════════════════════════

type AccessPolicy = {
  id: string;
  workforceRole: string;
  userId: string | null;
  dataCategory: string;
  dataScope: string;
  accessLevel: "None" | "Read" | "Write" | "Admin";
  isMinimumNecessary: boolean;
  purpose: string | null;
  isPrivileged: boolean;
  requiresApproval: boolean;
  isActive: boolean;
  effectiveFrom: string;
  effectiveUntil: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  lastReviewedAt: string | null;
  nextReviewDate: string | null;
  reviewNotes: string | null;
  createdAt: string;
  updatedAt: string;
};

type PolicyListResult = { total: number; page: number; pageSize: number; items: AccessPolicy[] };
type PolicySummary = {
  total: number; active: number; inactive: number; expired: number;
  privileged: number; requireApproval: number; roleBased: number; userSpecific: number;
  byAccessLevel: { none: number; read: number; write: number; admin: number };
};
type AccessCheckResult = {
  allowed: boolean; minimumNecessary: boolean; accessLevel: string;
  reason: string | null; policyId?: string; isPrivileged?: boolean; requiresApproval?: boolean;
};

const ACCESS_LEVELS  = ["None", "Read", "Write", "Admin"] as const;
const DATA_CATEGORIES = [
  "ClinicalData", "AdministrativeData", "FinancialData",
  "BehavioralHealth", "ResearchData", "OperationalData",
];
const DATA_SCOPES_BY_CAT: Record<string, string[]> = {
  ClinicalData:       ["PatientDemographics","MedicationRecords","LabResults","ImagingRecords","MentalHealthRecords","SubstanceAbuseRecords","FullMedicalHistory"],
  AdministrativeData: ["PatientRegistration","AppointmentScheduling","ConsentForms","DischargeRecords"],
  FinancialData:      ["BillingAndInsurance","ClaimsData","PaymentRecords"],
  BehavioralHealth:   ["PsychiatryNotes","SubstanceAbuseRecords","CrisisInterventionRecords"],
  ResearchData:       ["DeidentifiedDatasets","ClinicalTrialData","StudyEnrollmentData"],
  OperationalData:    ["StaffCredentials","AuditLogs","SystemAccessLogs"],
};

function accessLevelBadge(l: string) {
  const map: Record<string, string> = {
    None:  "bg-red-100 text-red-800",
    Read:  "bg-blue-100 text-blue-800",
    Write: "bg-yellow-100 text-yellow-800",
    Admin: "bg-purple-100 text-purple-800",
  };
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${map[l] ?? "bg-gray-100"}`}>{l}</span>;
}

const EMPTY_POLICY = {
  workforceRole: "", userId: "", dataCategory: "ClinicalData", dataScope: "",
  accessLevel: "Read" as AccessPolicy["accessLevel"], isMinimumNecessary: true, purpose: "",
  isPrivileged: false, requiresApproval: false, isActive: true,
  effectiveFrom: "", effectiveUntil: "", approvedBy: "", reviewNotes: "",
  nextReviewDate: "",
};

function HipaaInformationAccessSection({ canManage }: { canManage: boolean }) {
  const { toast } = useToast();
  const [tab, setTab]           = useState<"list" | "add" | "check" | "summary">("list");
  const [form, setForm]         = useState({ ...EMPTY_POLICY });
  const [editing, setEditing]   = useState<AccessPolicy | null>(null);
  const [fieldErrs, setFieldErrs] = useState<Record<string, string>>({});
  const [filterRole, setFilterRole]   = useState("");
  const [filterCat, setFilterCat]     = useState("");
  const [filterLevel, setFilterLevel] = useState("");
  const [filterActive, setFilterActive] = useState("");
  const [ckRole, setCkRole]     = useState("");
  const [ckScope, setCkScope]   = useState("");
  const [ckCat, setCkCat]       = useState("");
  const [ckLevel, setCkLevel]   = useState("Read");
  const [ckPurpose, setCkPurpose] = useState("");
  const [ckResult, setCkResult] = useState<AccessCheckResult | null>(null);

  const BASE = "/api/organization/compliance/hipaa/information-access";

  const params = new URLSearchParams();
  if (filterRole)   params.set("workforceRole", filterRole);
  if (filterCat)    params.set("dataCategory",  filterCat);
  if (filterLevel)  params.set("accessLevel",   filterLevel);
  if (filterActive) params.set("isActive",      filterActive);

  const token = () => localStorage.getItem("authToken") ?? sessionStorage.getItem("authToken") ?? "";

  const { data, isLoading, refetch } = useQuery<PolicyListResult>({
    queryKey: [BASE, "list", filterRole, filterCat, filterLevel, filterActive],
    queryFn: async () => {
      const r = await fetch(`${BASE}?${params}`, { headers: { Authorization: `Bearer ${token()}` } });
      if (!r.ok) throw new Error("Failed to load policies.");
      return r.json();
    },
  });

  const { data: summary } = useQuery<PolicySummary>({
    queryKey: [BASE, "summary"],
    enabled: tab === "summary",
    queryFn: async () => {
      const r = await fetch(`${BASE}/summary`, { headers: { Authorization: `Bearer ${token()}` } });
      if (!r.ok) throw new Error("Unable to complete action");
      return r.json();
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (payload: typeof EMPTY_POLICY) => {
      const url    = editing ? `${BASE}/${editing.id}` : BASE;
      const method = editing ? "PUT" : "POST";
      const r = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
        body: JSON.stringify({
          workforceRole:      payload.workforceRole,
          userId:             payload.userId || null,
          dataCategory:       payload.dataCategory,
          dataScope:          payload.dataScope,
          accessLevel:        payload.accessLevel,
          isMinimumNecessary: payload.isMinimumNecessary,
          purpose:            payload.purpose || null,
          isPrivileged:       payload.isPrivileged,
          requiresApproval:   payload.requiresApproval,
          isActive:           payload.isActive,
          effectiveFrom:      payload.effectiveFrom || undefined,
          effectiveUntil:     payload.effectiveUntil || null,
          approvedBy:         payload.approvedBy || null,
          reviewNotes:        payload.reviewNotes || null,
          nextReviewDate:     payload.nextReviewDate || null,
        }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        const fe = body.errors as Record<string, string[]> | undefined;
        if (fe) { const m: Record<string, string> = {}; for (const k in fe) m[k.toLowerCase()] = fe[k][0]; throw m; }
        throw new Error(body.message ?? "Unable to save");
      }
      return r.json();
    },
    onSuccess: () => {
      toast({ title: editing ? "Policy updated." : "Policy created." });
      setForm({ ...EMPTY_POLICY }); setEditing(null); setFieldErrs({}); setTab("list");
      queryClient.invalidateQueries({ queryKey: [BASE] });
    },
    onError: (e: unknown) => {
      if (typeof e === "object" && e !== null && !("message" in e)) {
        setFieldErrs(e as Record<string, string>);
      } else {
        toast({ title: "Unable to complete action", description: (e as Error).message, variant: "destructive" });
      }
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, activate }: { id: string; activate: boolean }) => {
      const r = await fetch(`${BASE}/${id}/${activate ? "activate" : "deactivate"}`, {
        method: "PATCH", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
        body: "{}",
      });
      if (!r.ok) throw new Error("Toggle failed.");
      return r.json();
    },
    onSuccess: () => { toast({ title: "Policy updated." }); refetch(); queryClient.invalidateQueries({ queryKey: [BASE, "summary"] }); },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  const handleSubmit = () => {
    const errs: Record<string, string> = {};
    if (!form.workforceRole.trim()) errs.workforcerole = "Workforce role is required.";
    if (!form.dataCategory)         errs.datacategory  = "Data category is required.";
    if (!form.dataScope.trim())     errs.datascope     = "Data scope is required.";
    if (Object.keys(errs).length)   { setFieldErrs(errs); return; }
    saveMutation.mutate(form);
  };

  const startEdit = (p: AccessPolicy) => {
    setEditing(p);
    setForm({
      workforceRole: p.workforceRole, userId: p.userId ?? "",
      dataCategory: p.dataCategory, dataScope: p.dataScope,
      accessLevel: p.accessLevel, isMinimumNecessary: p.isMinimumNecessary,
      purpose: p.purpose ?? "", isPrivileged: p.isPrivileged,
      requiresApproval: p.requiresApproval, isActive: p.isActive,
      effectiveFrom: p.effectiveFrom.slice(0, 10),
      effectiveUntil: p.effectiveUntil?.slice(0, 10) ?? "",
      approvedBy: p.approvedBy ?? "", reviewNotes: p.reviewNotes ?? "",
      nextReviewDate: p.nextReviewDate?.slice(0, 10) ?? "",
    });
    setTab("add");
  };

  const runCheck = async () => {
    if (!ckRole || !ckScope || !ckPurpose) return;
    try {
      const r = await fetch(`${BASE}/access-check`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ workforceRole: ckRole, dataCategory: ckCat, dataScope: ckScope, accessLevel: ckLevel, purpose: ckPurpose }),
      });
      setCkResult(await r.json());
    } catch {
      toast({ title: "Unable to complete check", variant: "destructive" });
    }
  };

  const f = (k: keyof typeof EMPTY_POLICY, asBoolean = false) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm(p => ({ ...p, [k]: asBoolean ? (e.target as HTMLInputElement).checked : e.target.value }));

  const scopeOptions = DATA_SCOPES_BY_CAT[form.dataCategory] ?? [];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-indigo-600" />
              Information Access — Policy Registry
            </CardTitle>
            <CardDescription>
              HIPAA § 164.308(a)(4) — Least-privilege ePHI access policies with minimum-necessary enforcement.
            </CardDescription>
          </div>
          {canManage && (
            <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2" onClick={() => { setEditing(null); setForm({ ...EMPTY_POLICY }); setTab("add"); }}>
              <Plus className="h-4 w-4" /> Add Policy
            </Button>
          )}
        </div>
        <div className="flex gap-2 mt-3 border-b pb-0 flex-wrap">
          {(["list", "add", "check", "summary"] as const).map(t => (
            <button key={t} onClick={() => { if (t !== "add") { setEditing(null); setFieldErrs({}); } setTab(t); }}
              className={`px-4 py-1.5 text-sm font-medium border-b-2 transition-colors ${tab === t ? "border-indigo-600 text-indigo-700" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
              {t === "list" ? "Policies" : t === "add" ? (editing ? "Edit Policy" : "New Policy") : t === "check" ? "Access Check" : "Summary"}
            </button>
          ))}
        </div>
      </CardHeader>

      <CardContent>
        {/* ── Policy list ── */}
        {tab === "list" && (
          <>
            <div className="flex gap-2 mb-4 flex-wrap">
              <Input placeholder="Filter by role…" value={filterRole} onChange={e => setFilterRole(e.target.value)} className="w-36 text-sm h-8" />
              <select value={filterCat} onChange={e => setFilterCat(e.target.value)} className="text-sm border rounded px-2 py-1">
                <option value="">All categories</option>
                {DATA_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <select value={filterLevel} onChange={e => setFilterLevel(e.target.value)} className="text-sm border rounded px-2 py-1">
                <option value="">All levels</option>
                {ACCESS_LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
              <select value={filterActive} onChange={e => setFilterActive(e.target.value)} className="text-sm border rounded px-2 py-1">
                <option value="">Active + inactive</option>
                <option value="true">Active only</option>
                <option value="false">Inactive only</option>
              </select>
              <Button size="sm" variant="outline" onClick={() => refetch()}><RefreshCcw className="h-3 w-3 mr-1" />Refresh</Button>
            </div>

            {isLoading ? (
              <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-10 w-full" />)}</div>
            ) : !data?.items?.length ? (
              <p className="text-muted-foreground text-sm py-6 text-center">No policies found. Add one to begin enforcing minimum-necessary access.</p>
            ) : (
              <div className="space-y-2">
                {data.items.map(p => (
                  <div key={p.id}
                    className={`border rounded-lg p-3 flex items-center justify-between gap-4 transition ${p.isActive ? "hover:bg-muted/30" : "opacity-60 bg-muted/20"}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{p.workforceRole}</span>
                        <span className="text-muted-foreground text-xs">→</span>
                        <span className="text-sm">{p.dataScope}</span>
                        {accessLevelBadge(p.accessLevel)}
                        {p.isMinimumNecessary && (
                          <span className="px-1.5 py-0.5 bg-green-50 text-green-700 border border-green-200 rounded text-xs">Min Nec</span>
                        )}
                        {p.isPrivileged && (
                          <span className="px-1.5 py-0.5 bg-orange-50 text-orange-700 border border-orange-200 rounded text-xs">Privileged</span>
                        )}
                        {!p.isActive && (
                          <span className="px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded text-xs">Inactive</span>
                        )}
                        {p.userId && (
                          <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">User-specific</span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {p.dataCategory} · {p.purpose ?? "No purpose documented"}
                        {p.effectiveUntil && ` · Expires ${new Date(p.effectiveUntil).toLocaleDateString()}`}
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      {canManage && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => startEdit(p)}><Pencil className="h-3 w-3" /></Button>
                          <Button size="sm" variant={p.isActive ? "outline" : "default"}
                            onClick={() => toggleMutation.mutate({ id: p.id, activate: !p.isActive })}>
                            {p.isActive ? "Deactivate" : "Activate"}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
                <p className="text-xs text-muted-foreground pt-1">Total: {data.total} policies</p>
              </div>
            )}
          </>
        )}

        {/* ── Add / Edit form ── */}
        {tab === "add" && (
          <div className="space-y-4 max-w-2xl">
            {editing && (
              <div className="bg-amber-50 border border-amber-200 rounded p-2 text-sm text-amber-800 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0" /> Editing: <strong>{editing.workforceRole} → {editing.dataScope}</strong>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Workforce Role *</Label>
                <Input value={form.workforceRole} onChange={f("workforceRole")} placeholder="Nurse, Physician, Administrator…" />
                {fieldErrs.workforcerole && <p className="text-xs text-red-500 mt-0.5">{fieldErrs.workforcerole}</p>}
              </div>
              <div>
                <Label>User ID <span className="text-muted-foreground text-xs">(optional — user-specific override)</span></Label>
                <Input value={form.userId} onChange={f("userId")} placeholder="UUID or leave blank for role-based" />
              </div>
              <div>
                <Label>Data Category *</Label>
                <select value={form.dataCategory} onChange={f("dataCategory")} className="w-full border rounded px-2 py-1.5 text-sm">
                  {DATA_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                {fieldErrs.datacategory && <p className="text-xs text-red-500 mt-0.5">{fieldErrs.datacategory}</p>}
              </div>
              <div>
                <Label>Data Scope *</Label>
                <select value={form.dataScope} onChange={f("dataScope")} className="w-full border rounded px-2 py-1.5 text-sm">
                  <option value="">Select scope…</option>
                  {scopeOptions.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                {fieldErrs.datascope && <p className="text-xs text-red-500 mt-0.5">{fieldErrs.datascope}</p>}
              </div>
              <div>
                <Label>Access Level</Label>
                <select value={form.accessLevel} onChange={f("accessLevel")} className="w-full border rounded px-2 py-1.5 text-sm">
                  {ACCESS_LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div>
                <Label>Approved By</Label>
                <Input value={form.approvedBy} onChange={f("approvedBy")} placeholder="CISO, Compliance Officer…" />
              </div>
              <div>
                <Label>Effective From</Label>
                <Input type="date" value={form.effectiveFrom} onChange={f("effectiveFrom")} />
              </div>
              <div>
                <Label>Effective Until <span className="text-muted-foreground text-xs">(leave blank = permanent)</span></Label>
                <Input type="date" value={form.effectiveUntil} onChange={f("effectiveUntil")} />
              </div>
              <div>
                <Label>Next Review Date</Label>
                <Input type="date" value={form.nextReviewDate} onChange={f("nextReviewDate")} />
              </div>
            </div>

            <div>
              <Label>Purpose / Justification</Label>
              <Textarea value={form.purpose} onChange={f("purpose")} rows={2} placeholder="Describe the legitimate healthcare purpose for this access…" />
            </div>
            <div>
              <Label>Review Notes</Label>
              <Textarea value={form.reviewNotes} onChange={f("reviewNotes")} rows={2} placeholder="Annual review findings, scope changes…" />
            </div>

            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.isMinimumNecessary} onChange={f("isMinimumNecessary", true)} />
                Minimum Necessary
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.isPrivileged} onChange={f("isPrivileged", true)} />
                Privileged Access
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.requiresApproval} onChange={f("requiresApproval", true)} />
                Requires Approval
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.isActive} onChange={f("isActive", true)} />
                Active
              </label>
            </div>

            <div className="flex gap-2">
              <Button onClick={handleSubmit} disabled={saveMutation.isPending}>
                {saveMutation.isPending ? "Saving…" : editing ? "Update Policy" : "Create Policy"}
              </Button>
              <Button variant="outline" onClick={() => { setEditing(null); setFieldErrs({}); setTab("list"); }}>Cancel</Button>
            </div>
          </div>
        )}

        {/* ── Access Check ── */}
        {tab === "check" && (
          <div className="space-y-4 max-w-lg">
            <p className="text-sm text-muted-foreground">
              Evaluate whether a role may access a specific ePHI scope at a given access level, applying minimum-necessary restrictions.
            </p>
            <div>
              <Label>Workforce Role *</Label>
              <Input value={ckRole} onChange={e => setCkRole(e.target.value)} placeholder="Nurse, Physician…" />
            </div>
            <div>
              <Label>Data Category</Label>
              <select value={ckCat} onChange={e => setCkCat(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm">
                <option value="">Select category…</option>
                {DATA_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <Label>Requested Data Scope *</Label>
              <select value={ckScope} onChange={e => setCkScope(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm">
                <option value="">Select scope…</option>
                {(DATA_SCOPES_BY_CAT[ckCat] ?? Object.values(DATA_SCOPES_BY_CAT).flat()).map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Requested Access Level</Label>
              <select value={ckLevel} onChange={e => setCkLevel(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm">
                {ACCESS_LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div>
              <Label>Purpose / Justification *</Label>
              <Textarea value={ckPurpose} onChange={e => setCkPurpose(e.target.value)} rows={2}
                placeholder="Treating physician reviewing pre-op chart." />
            </div>
            <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2" onClick={runCheck} disabled={!ckRole || !ckScope || !ckPurpose}><CheckCircle2 className="h-4 w-4" /> Run Access Check</Button>

            {ckResult && (
              <div className={`mt-3 p-4 rounded-lg border space-y-2 ${ckResult.allowed ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
                <div className="flex items-center gap-2 font-medium text-sm">
                  {ckResult.allowed
                    ? <><CheckCircle2 className="h-4 w-4 text-green-600" />Access Allowed</>
                    : <><XCircle className="h-4 w-4 text-red-600" />Access Denied</>}
                </div>
                <div className="text-xs space-y-1">
                  <div>Access Level: <strong>{ckResult.accessLevel}</strong></div>
                  <div>Minimum Necessary: <strong>{ckResult.minimumNecessary ? "Yes" : "No"}</strong></div>
                  {ckResult.isPrivileged && <div className="text-orange-700">⚠ This is a privileged access policy.</div>}
                  {ckResult.requiresApproval && <div className="text-amber-700">⚠ Approval required before access.</div>}
                  {ckResult.reason && <div className="text-muted-foreground">Reason: {ckResult.reason}</div>}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Summary ── */}
        {tab === "summary" && (
          summary ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: "Total Policies",    value: summary.total },
                { label: "Active",            value: summary.active,          color: "text-green-700" },
                { label: "Inactive",          value: summary.inactive,        color: "text-gray-500" },
                { label: "Expired",           value: summary.expired,         color: "text-red-700" },
                { label: "Privileged",        value: summary.privileged,      color: "text-orange-700" },
                { label: "Require Approval",  value: summary.requireApproval, color: "text-amber-700" },
                { label: "Role-Based",        value: summary.roleBased },
                { label: "User-Specific",     value: summary.userSpecific,    color: "text-blue-700" },
              ].map(({ label, value, color }) => (
                <div key={label} className="border rounded-lg p-4 text-center">
                  <div className={`text-2xl font-bold ${color ?? ""}`}>{value}</div>
                  <div className="text-xs text-muted-foreground mt-1">{label}</div>
                </div>
              ))}
              <div className="col-span-2 md:col-span-4 border rounded-lg p-4">
                <div className="text-xs text-muted-foreground uppercase font-medium mb-2">By Access Level</div>
                <div className="flex gap-6">
                  {Object.entries(summary.byAccessLevel).map(([level, count]) => (
                    <div key={level} className="text-center">
                      <div className="text-lg font-bold">{count}</div>
                      <div>{accessLevelBadge(level.charAt(0).toUpperCase() + level.slice(1))}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : <Skeleton className="h-40 w-full" />
        )}
      </CardContent>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════════════════

const EMPTY_OFFICIAL = {
  officialName: "", officialEmail: "", jobTitle: "",
  responsibilities: [] as string[], authorityScope: "", appointedBy: "",
  appointmentDate: "", effectiveFrom: "", effectiveUntil: "",
  lastReviewDate: "", nextReviewDate: "", reviewNotes: "",
};

function HipaaSecurityResponsibilitySection({ canManage }: { canManage: boolean }) {
  const { toast } = useToast();
  const [tab, setTab] = useState<"current" | "appoint" | "history">("current");
  const [form, setForm]     = useState({ ...EMPTY_OFFICIAL });
  const [editing, setEditing] = useState<SecurityOfficial | null>(null);
  const [fieldErrs, setFieldErrs] = useState<Record<string,string>>({});

  const BASE = "/api/organization/compliance/hipaa/security-official";
  const RQ   = [BASE];
  const HQ   = [BASE, "review-history"];

  const { data, isLoading } = useQuery<SecurityOfficialListResult>({
    queryKey: RQ,
    queryFn:  () => apiRequest("GET", BASE).then(r => r.json()),
  });

  const { data: histData } = useQuery<{ history: SecurityOfficial[]; total: number }>({
    queryKey: HQ,
    queryFn:  () => apiRequest("GET", `${BASE}/review-history`).then(r => r.json()),
    enabled:  tab === "history",
  });

  function toFormDate(iso: string | null | undefined) {
    if (!iso) return "";
    return iso.substring(0, 10);
  }

  function loadForEdit(o: SecurityOfficial) {
    const saved = o.responsibilities.split(",").map(s => s.trim()).filter(Boolean);
    setForm({
      officialName:    o.officialName,
      officialEmail:   o.officialEmail,
      jobTitle:        o.jobTitle,
      responsibilities: saved,
      authorityScope:  o.authorityScope ?? "",
      appointedBy:     o.appointedBy,
      appointmentDate: toFormDate(o.appointmentDate),
      effectiveFrom:   toFormDate(o.effectiveFrom),
      effectiveUntil:  toFormDate(o.effectiveUntil),
      lastReviewDate:  toFormDate(o.lastReviewDate),
      nextReviewDate:  toFormDate(o.nextReviewDate),
      reviewNotes:     o.reviewNotes ?? "",
    });
    setEditing(o);
    setTab("appoint");
  }

  function toggleResponsibility(r: string) {
    setForm(f => ({
      ...f,
      responsibilities: f.responsibilities.includes(r)
        ? f.responsibilities.filter(x => x !== r)
        : [...f.responsibilities, r],
    }));
  }

  const saveMut = useMutation({
    mutationFn: async () => {
      setFieldErrs({});
      const body = {
        officialName:    form.officialName,
        officialEmail:   form.officialEmail,
        jobTitle:        form.jobTitle,
        responsibilities: form.responsibilities.join(", "),
        authorityScope:  form.authorityScope || null,
        appointedBy:     form.appointedBy,
        appointmentDate: form.appointmentDate ? new Date(form.appointmentDate).toISOString() : null,
        effectiveFrom:   form.effectiveFrom   ? new Date(form.effectiveFrom).toISOString()   : null,
        effectiveUntil:  form.effectiveUntil  ? new Date(form.effectiveUntil).toISOString()  : null,
        lastReviewDate:  form.lastReviewDate  ? new Date(form.lastReviewDate).toISOString()  : null,
        nextReviewDate:  form.nextReviewDate  ? new Date(form.nextReviewDate).toISOString()  : null,
        reviewNotes:     form.reviewNotes || null,
      };
      const url    = editing ? `${BASE}/${editing.id}` : BASE;
      const method = editing ? "PUT" : "POST";
      const res    = await apiRequest(method, url, body);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const errs: Record<string,string> = {};
        if (err.errors) {
          for (const [k, msgs] of Object.entries(err.errors)) {
            errs[k] = Array.isArray(msgs) ? (msgs as string[]).join(", ") : String(msgs);
          }
        } else if (err.message) {
          errs._general = err.message;
        }
        setFieldErrs(errs);
        throw new Error(err.message || "Validation error");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RQ });
      queryClient.invalidateQueries({ queryKey: HQ });
      setForm({ ...EMPTY_OFFICIAL });
      setEditing(null);
      setTab("current");
      toast({ title: editing ? "Security official updated." : "Security official appointed.", description: "The registry has been updated." });
    },
    onError: (e: Error) => {
      toast({ title: "Unable to save", description: e.message, variant: "destructive" });
    },
  });

  const deactivateMut = useMutation({
    mutationFn: (id: string) =>
      apiRequest("PATCH", `${BASE}/${id}/deactivate`, {}).then(r => {
        if (!r.ok) throw new Error("Deactivation failed.");
        return r.json();
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RQ });
      queryClient.invalidateQueries({ queryKey: HQ });
      toast({ title: "Official deactivated." });
    },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  const current   = data?.currentOfficial ?? null;
  const hasActive = data?.hasActiveOfficial ?? false;

  const reviewDue = current?.nextReviewDate
    ? new Date(current.nextReviewDate) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
    : false;
  const effectiveExpired = current?.effectiveUntil
    ? new Date(current.effectiveUntil) < new Date()
    : false;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-primary" aria-hidden="true" />
              Security Responsibility
            </CardTitle>
            <CardDescription className="text-xs mt-0.5">
              Security Official accountability registry — 45 CFR § 164.308(a)(2)
            </CardDescription>
          </div>
          {!hasActive && (
            <Badge variant="outline" className="border-red-500/40 bg-red-500/10 text-red-600 dark:text-red-400 text-xs shrink-0">
              No active official
            </Badge>
          )}
        </div>
        {/* Tab bar */}
        <div className="flex gap-1 mt-2 border-b pb-0">
          {(["current","appoint","history"] as const).map(t => (
            <button key={t} onClick={() => { setTab(t); setEditing(null); setForm({...EMPTY_OFFICIAL}); setFieldErrs({}); }}
              className={`text-xs px-3 py-1.5 rounded-t border-b-2 font-medium transition-colors ${
                tab === t ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
              }`}>
              {t === "current" ? "Current Official" : t === "appoint" ? (editing ? "Edit Official" : "Appoint / Update") : "Review History"}
            </button>
          ))}
        </div>
      </CardHeader>

      <CardContent className="pt-4">
        {/* ── TAB: Current Official ── */}
        {tab === "current" && (
          <div>
            {isLoading && <Skeleton className="h-40 w-full" />}
            {!isLoading && !hasActive && (
              <div className="rounded-lg border border-red-500/20 bg-red-500/5 p-6 text-center">
                <ShieldAlert className="w-8 h-8 mx-auto mb-2 text-red-500/60" aria-hidden="true" />
                <p className="text-sm font-medium text-red-600 dark:text-red-400">No active security official assigned</p>
                <p className="text-xs text-muted-foreground mt-1">
                  HIPAA § 164.308(a)(2) requires a designated security official.
                  {canManage && " Use the \"Appoint / Update\" tab to assign one."}
                </p>
              </div>
            )}
            {!isLoading && current && (
              <div className="space-y-4">
                {/* Status banners */}
                {effectiveExpired && (
                  <div className="flex items-center gap-2 rounded-md border border-red-500/20 bg-red-500/5 px-3 py-2 text-xs text-red-600 dark:text-red-400">
                    <XCircle className="w-3.5 h-3.5 shrink-0" />
                    Effective period has expired — appoint a new official.
                  </div>
                )}
                {reviewDue && !effectiveExpired && (
                  <div className="flex items-center gap-2 rounded-md border border-amber-500/20 bg-amber-500/5 px-3 py-2 text-xs text-amber-600 dark:text-amber-400">
                    <Clock className="w-3.5 h-3.5 shrink-0" />
                    Review due {fmtDate(current.nextReviewDate)} — upcoming review required.
                  </div>
                )}

                {/* Official card */}
                <div className="rounded-lg border bg-card p-4 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm">{current.officialName}</span>
                        {officialStatusBadge(current.status)}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{current.jobTitle}</p>
                      <p className="text-xs text-muted-foreground">{current.officialEmail}</p>
                    </div>
                    {canManage && (
                      <div className="flex gap-1 shrink-0">
                        <Button size="sm" variant="outline" className="text-xs h-7 px-2"
                          onClick={() => loadForEdit(current)}>
                          <Pencil className="w-3 h-3 mr-1" /> Edit
                        </Button>
                        <Button size="sm" variant="outline" className="text-xs h-7 px-2 text-amber-600 border-amber-500/40"
                          onClick={() => deactivateMut.mutate(current.id)}
                          disabled={deactivateMut.isPending}>
                          Deactivate
                        </Button>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs">
                    <div><span className="text-muted-foreground">Appointed by:</span> {current.appointedBy}</div>
                    <div><span className="text-muted-foreground">Appointment date:</span> {fmtDate(current.appointmentDate)}</div>
                    <div><span className="text-muted-foreground">Effective from:</span> {fmtDate(current.effectiveFrom)}</div>
                    <div>
                      <span className="text-muted-foreground">Effective until:</span>{" "}
                      {current.effectiveUntil
                        ? <span className={effectiveExpired ? "text-red-500" : ""}>{fmtDate(current.effectiveUntil)}</span>
                        : "Indefinite"}
                    </div>
                    <div><span className="text-muted-foreground">Last review:</span> {fmtDate(current.lastReviewDate)}</div>
                    <div>
                      <span className="text-muted-foreground">Next review:</span>{" "}
                      <span className={reviewDue ? "text-amber-600 dark:text-amber-400 font-medium" : ""}>
                        {fmtDate(current.nextReviewDate)}
                      </span>
                    </div>
                    {current.authorityScope && (
                      <div className="col-span-2"><span className="text-muted-foreground">Authority scope:</span> {current.authorityScope}</div>
                    )}
                  </div>

                  {/* Responsibilities */}
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1.5">Documented responsibilities</p>
                    <div className="flex flex-wrap gap-1.5">
                      {current.responsibilities.split(",").map(r => r.trim()).filter(Boolean).map(r => (
                        <span key={r} className="inline-flex items-center gap-1 text-[10px] rounded border bg-muted/40 px-1.5 py-0.5">
                          <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                          {r}
                        </span>
                      ))}
                    </div>
                  </div>

                  {current.reviewNotes && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1">Review notes</p>
                      <p className="text-xs text-muted-foreground border rounded p-2 bg-muted/20">{current.reviewNotes}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── TAB: Appoint / Edit ── */}
        {tab === "appoint" && canManage && (
          <div className="space-y-3 max-w-xl">
            {fieldErrs._general && (
              <p className="text-xs text-red-600 border border-red-500/20 bg-red-500/5 rounded px-3 py-2">
                {fieldErrs._general}
              </p>
            )}
            {hasActive && !editing && (
              <div className="flex items-center gap-2 rounded-md border border-amber-500/20 bg-amber-500/5 px-3 py-2 text-xs text-amber-600 dark:text-amber-400">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                Appointing a new official will automatically replace the current active official.
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs" htmlFor="so-name">Full name *</Label>
                <Input id="so-name" value={form.officialName} onChange={e => setForm(f => ({...f, officialName: e.target.value}))}
                  className={`h-8 text-sm mt-0.5 ${fieldErrs.officialName ? "border-red-500" : ""}`} placeholder="Jane Smith" />
                {fieldErrs.officialName && <p className="text-[11px] text-red-500 mt-0.5">{fieldErrs.officialName}</p>}
              </div>
              <div>
                <Label className="text-xs" htmlFor="so-email">Email address *</Label>
                <Input id="so-email" type="email" value={form.officialEmail} onChange={e => setForm(f => ({...f, officialEmail: e.target.value}))}
                  className={`h-8 text-sm mt-0.5 ${fieldErrs.officialEmail ? "border-red-500" : ""}`} placeholder="jane@example.com" />
                {fieldErrs.officialEmail && <p className="text-[11px] text-red-500 mt-0.5">{fieldErrs.officialEmail}</p>}
              </div>
              <div>
                <Label className="text-xs" htmlFor="so-title">Job title *</Label>
                <Input id="so-title" value={form.jobTitle} onChange={e => setForm(f => ({...f, jobTitle: e.target.value}))}
                  className={`h-8 text-sm mt-0.5 ${fieldErrs.jobTitle ? "border-red-500" : ""}`} placeholder="HIPAA Security Officer" />
                {fieldErrs.jobTitle && <p className="text-[11px] text-red-500 mt-0.5">{fieldErrs.jobTitle}</p>}
              </div>
              <div>
                <Label className="text-xs" htmlFor="so-apptby">Appointed by *</Label>
                <Input id="so-apptby" value={form.appointedBy} onChange={e => setForm(f => ({...f, appointedBy: e.target.value}))}
                  className={`h-8 text-sm mt-0.5 ${fieldErrs.appointedBy ? "border-red-500" : ""}`} placeholder="CEO / Board" />
                {fieldErrs.appointedBy && <p className="text-[11px] text-red-500 mt-0.5">{fieldErrs.appointedBy}</p>}
              </div>
              <div>
                <Label className="text-xs" htmlFor="so-apptdate">Appointment date *</Label>
                <Input id="so-apptdate" type="date" value={form.appointmentDate} onChange={e => setForm(f => ({...f, appointmentDate: e.target.value}))}
                  className="h-8 text-sm mt-0.5" />
              </div>
              <div>
                <Label className="text-xs" htmlFor="so-from">Effective from *</Label>
                <Input id="so-from" type="date" value={form.effectiveFrom} onChange={e => setForm(f => ({...f, effectiveFrom: e.target.value}))}
                  className="h-8 text-sm mt-0.5" />
              </div>
              <div>
                <Label className="text-xs" htmlFor="so-until">Effective until (optional)</Label>
                <Input id="so-until" type="date" value={form.effectiveUntil} onChange={e => setForm(f => ({...f, effectiveUntil: e.target.value}))}
                  className="h-8 text-sm mt-0.5" />
              </div>
              <div>
                <Label className="text-xs" htmlFor="so-scope">Authority scope (optional)</Label>
                <Input id="so-scope" value={form.authorityScope} onChange={e => setForm(f => ({...f, authorityScope: e.target.value}))}
                  className="h-8 text-sm mt-0.5" placeholder="Enterprise-wide" />
              </div>
              <div>
                <Label className="text-xs" htmlFor="so-lastreview">Last review date</Label>
                <Input id="so-lastreview" type="date" value={form.lastReviewDate} onChange={e => setForm(f => ({...f, lastReviewDate: e.target.value}))}
                  className="h-8 text-sm mt-0.5" />
              </div>
              <div>
                <Label className="text-xs" htmlFor="so-nextreview">Next review date</Label>
                <Input id="so-nextreview" type="date" value={form.nextReviewDate} onChange={e => setForm(f => ({...f, nextReviewDate: e.target.value}))}
                  className="h-8 text-sm mt-0.5" />
              </div>
            </div>

            {/* Responsibilities checklist */}
            <div>
              <Label className="text-xs">Documented responsibilities *</Label>
              {fieldErrs.responsibilities && <p className="text-[11px] text-red-500 mt-0.5">{fieldErrs.responsibilities}</p>}
              <div className="mt-1.5 space-y-1 border rounded p-3 bg-muted/10">
                {RESPONSIBILITY_OPTIONS.map(r => (
                  <label key={r} className="flex items-center gap-2 cursor-pointer text-xs hover:bg-muted/40 rounded px-1 py-0.5">
                    <input type="checkbox" checked={form.responsibilities.includes(r)} onChange={() => toggleResponsibility(r)}
                      className="rounded" />
                    {r}
                  </label>
                ))}
              </div>
              {form.responsibilities.length > 0 && (
                <p className="text-[11px] text-muted-foreground mt-1">{form.responsibilities.length} responsibility/responsibilities selected</p>
              )}
            </div>

            {/* Review notes */}
            <div>
              <Label className="text-xs" htmlFor="so-notes">Review notes (optional)</Label>
              <Textarea id="so-notes" value={form.reviewNotes} onChange={e => setForm(f => ({...f, reviewNotes: e.target.value}))}
                className="text-sm mt-0.5 h-20" placeholder="Annual review notes, changes to scope, etc." />
            </div>

            <div className="flex gap-2">
              <Button size="sm" onClick={() => saveMut.mutate()} disabled={saveMut.isPending}
                className="text-xs">
                {saveMut.isPending ? "Saving…" : editing ? "Update Official" : "Appoint Official"}
              </Button>
              <Button size="sm" variant="outline" className="text-xs" onClick={() => { setEditing(null); setForm({...EMPTY_OFFICIAL}); setFieldErrs({}); }}>
                Clear
              </Button>
            </div>
          </div>
        )}

        {tab === "appoint" && !canManage && (
          <EmptyState icon={ShieldAlert} message="You do not have permission to appoint or edit security officials." />
        )}

        {/* ── TAB: Review History ── */}
        {tab === "history" && (
          <div>
            {!histData && <Skeleton className="h-40 w-full" />}
            {histData && histData.total === 0 && (
              <EmptyState icon={Calendar} message="No security officials recorded yet." sub="Appoint a security official to start the history." />
            )}
            {histData && histData.history.length > 0 && (
              <div className="space-y-2">
                {histData.history.map(o => (
                  <div key={o.id} className={`rounded-lg border p-3 text-xs space-y-1 ${o.status === "Active" ? "border-emerald-500/30 bg-emerald-500/5" : "bg-muted/20"}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{o.officialName}</span>
                        {officialStatusBadge(o.status)}
                      </div>
                      <span className="text-muted-foreground">{o.jobTitle}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-muted-foreground">
                      <span>Appointed: {fmtDate(o.appointmentDate)}</span>
                      <span>Effective: {fmtDate(o.effectiveFrom)} – {o.effectiveUntil ? fmtDate(o.effectiveUntil) : "Indefinite"}</span>
                      {o.lastReviewDate && <span>Last review: {fmtDate(o.lastReviewDate)}</span>}
                      {o.nextReviewDate && <span>Next review: {fmtDate(o.nextReviewDate)}</span>}
                    </div>
                    {o.responsibilities && (
                      <div className="flex flex-wrap gap-1 pt-0.5">
                        {o.responsibilities.split(",").map(r => r.trim()).filter(Boolean).slice(0, 4).map(r => (
                          <span key={r} className="text-[10px] rounded border bg-muted/40 px-1.5 py-0.5">{r}</span>
                        ))}
                        {o.responsibilities.split(",").length > 4 && (
                          <span className="text-[10px] text-muted-foreground">+{o.responsibilities.split(",").length - 4} more</span>
                        )}
                      </div>
                    )}
                    {o.reviewNotes && (
                      <p className="text-muted-foreground italic border-l-2 pl-2 ml-1 mt-1">{o.reviewNotes}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 0 — HIPAA-02 Risk Management
// ═══════════════════════════════════════════════════════════════════════════════

const EMPTY_RISK = {
  riskTitle: "", riskDescription: "", riskCategory: "Technical",
  assetOrSystem: "", threat: "", vulnerability: "",
  likelihood: 3, impact: 3,
  existingControls: "", controlReferences: "",
  treatmentPlan: "", mitigationActions: "",
  riskOwner: "", targetRemediationDate: "",
  residualLikelihood: "", residualImpact: "",
  remediationEvidence: "", reviewDate: "",
};

function HipaaRiskManagementSection({ canManage }: { canManage: boolean }) {
  const { toast } = useToast();
  const [tab, setTab] = useState<"register"|"create"|"detail">("register");
  const [filterLevel, setFilterLevel]   = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [form, setForm]     = useState({ ...EMPTY_RISK });
  const [editing, setEditing] = useState<RiskRegister | null>(null);
  const [fieldErrs, setFieldErrs] = useState<Record<string,string>>({});
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [statusTarget, setStatusTarget] = useState<RiskRegister | null>(null);
  const [statusForm, setStatusForm] = useState({ status: "InProgress", remediationEvidence: "" });
  const [showStatusDlg, setShowStatusDlg] = useState(false);

  const RQ  = ["/api/organization/compliance/hipaa/risks", filterLevel, filterStatus];
  const DQ  = (id: string) => ["/api/organization/compliance/hipaa/risks", id];

  const { data, isLoading } = useQuery<{ total: number; items: RiskRegister[] }>({
    queryKey: RQ,
    queryFn: (): Promise<{ total: number; items: RiskRegister[] }> => {
      const p = new URLSearchParams({ pageSize: "100" });
      if (filterLevel)  p.set("riskLevel", filterLevel);
      if (filterStatus) p.set("status", filterStatus);
      return apiRequest("GET", `/api/organization/compliance/hipaa/risks?${p}`).then(r => r.json());
    },
  });

  const { data: detail } = useQuery<RiskDetail>({
    queryKey: selectedId ? DQ(selectedId) : [],
    queryFn:  (): Promise<RiskDetail> => apiRequest("GET", `/api/organization/compliance/hipaa/risks/${selectedId}`).then(r => r.json()),
    enabled:  !!selectedId && tab === "detail",
  });

  const save = useMutation({
    mutationFn: () => {
      const body = {
        riskTitle:             form.riskTitle,
        riskDescription:       form.riskDescription,
        riskCategory:          form.riskCategory,
        assetOrSystem:         form.assetOrSystem   || null,
        threat:                form.threat,
        vulnerability:         form.vulnerability,
        likelihood:            Number(form.likelihood),
        impact:                Number(form.impact),
        existingControls:      form.existingControls      || null,
        controlReferences:     form.controlReferences     || null,
        treatmentPlan:         form.treatmentPlan         || null,
        mitigationActions:     form.mitigationActions     || null,
        riskOwner:             form.riskOwner             || null,
        targetRemediationDate: form.targetRemediationDate
          ? new Date(form.targetRemediationDate).toISOString() : null,
        residualLikelihood:    form.residualLikelihood !== "" ? Number(form.residualLikelihood) : null,
        residualImpact:        form.residualImpact     !== "" ? Number(form.residualImpact)     : null,
        remediationEvidence:   form.remediationEvidence  || null,
        reviewDate:            form.reviewDate ? new Date(form.reviewDate).toISOString() : null,
      };
      return editing
        ? apiRequest("PUT",  `/api/organization/compliance/hipaa/risks/${editing.id}`, body)
        : apiRequest("POST", "/api/organization/compliance/hipaa/risks", body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/hipaa/risks"] });
      toast({ title: editing ? "Risk updated" : "Risk registered" });
      setEditing(null); setForm({ ...EMPTY_RISK }); setFieldErrs({});
      setTab("register");
    },
    onError: (e: any) => {
      const errs: Record<string,string> = {};
      if (e?.fieldErrors) Object.assign(errs, e.fieldErrors);
      else if (e?.message) errs["_"] = e.message;
      setFieldErrs(errs);
    },
  });

  const patchStatus = useMutation({
    mutationFn: () =>
      apiRequest("PATCH", `/api/organization/compliance/hipaa/risks/${statusTarget!.id}/status`, {
        status:               statusForm.status,
        remediationEvidence:  statusForm.remediationEvidence || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/hipaa/risks"] });
      if (selectedId) queryClient.invalidateQueries({ queryKey: DQ(selectedId) });
      toast({ title: `Risk marked ${statusForm.status}` });
      setShowStatusDlg(false); setStatusTarget(null);
    },
    onError: (e: any) => {
      const msg = fieldError(e, "remediationEvidence") ?? fieldError(e, "_") ?? e.message;
      toast({ title: "Unable to update status", description: msg, variant: "destructive" });
    },
  });

  const del = useMutation({
    mutationFn: (id: string) =>
      apiRequest("DELETE", `/api/organization/compliance/hipaa/risks/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/hipaa/risks"] });
      toast({ title: "Risk deleted" });
      if (tab === "detail") { setTab("register"); setSelectedId(null); }
    },
    onError: (e: any) =>
      toast({ title: "Unable to delete", description: e.message, variant: "destructive" }),
  });

  const openCreate = () => {
    setEditing(null); setForm({ ...EMPTY_RISK }); setFieldErrs({}); setTab("create");
  };
  const openEdit = (r: RiskRegister) => {
    setEditing(r);
    setForm({
      riskTitle:            r.riskTitle,
      riskDescription:      r.riskDescription,
      riskCategory:         r.riskCategory,
      assetOrSystem:        r.assetOrSystem ?? "",
      threat:               r.threat,
      vulnerability:        r.vulnerability,
      likelihood:           r.likelihood as any,
      impact:               r.impact as any,
      existingControls:     r.existingControls     ?? "",
      controlReferences:    r.controlReferences    ?? "",
      treatmentPlan:        r.treatmentPlan        ?? "",
      mitigationActions:    r.mitigationActions    ?? "",
      riskOwner:            r.riskOwner            ?? "",
      targetRemediationDate:r.targetRemediationDate?.slice(0,10) ?? "",
      residualLikelihood:   r.residualLikelihood?.toString() ?? "",
      residualImpact:       r.residualImpact?.toString()     ?? "",
      remediationEvidence:  r.remediationEvidence  ?? "",
      reviewDate:           r.reviewDate?.slice(0,10)        ?? "",
    });
    setFieldErrs({}); setTab("create");
  };
  const openDetail = (r: RiskRegister) => {
    setSelectedId(r.id); setTab("detail");
  };

  const iScore = previewScore(Number(form.likelihood)||3, Number(form.impact)||3);
  const iLevel = previewLevel(iScore);
  const rScore = (form.residualLikelihood !== "" && form.residualImpact !== "")
    ? previewScore(Number(form.residualLikelihood), Number(form.residualImpact)) : null;
  const rLevel = rScore !== null ? previewLevel(rScore) : null;

  const handleSave = () => {
    const errs: Record<string,string> = {};
    if (!form.riskTitle.trim())    errs.riskTitle    = "Risk title is required.";
    if (!form.threat.trim())       errs.threat       = "Threat is required.";
    if (!form.vulnerability.trim())errs.vulnerability= "Vulnerability is required.";
    if (iLevel === "High" || iLevel === "Critical") {
      if (!form.treatmentPlan.trim()) errs.treatmentPlan = `${iLevel} risks require a treatment plan.`;
      if (!form.riskOwner.trim())     errs.riskOwner = `${iLevel} risks require a risk owner.`;
      if (!form.targetRemediationDate) errs.targetRemediationDate = `${iLevel} risks require a target remediation date.`;
    }
    if (Object.keys(errs).length) { setFieldErrs(errs); return; }
    setFieldErrs({});
    save.mutate();
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldAlert className="w-4 h-4" /> Risk Management
            </CardTitle>
            <CardDescription className="text-xs">
              45 CFR §164.308(a)(1)(ii)(B) — risk register, controls, residual risk, and treatment plans
            </CardDescription>
          </div>
          {canManage && tab !== "create" && (
            <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2" onClick={openCreate}>
              <Plus className="w-4 h-4" /> Add Risk
            </Button>
          )}
        </div>
        <div className="flex gap-1 pt-2 border-b">
          {([["register","Risk Register"],["create", editing ? "Edit Risk" : "Add Risk"],["detail","Risk Detail"]] as const).map(([t,label]) => (
            (t === "detail" && !selectedId) ? null :
            <button key={t} onClick={() => setTab(t as any)}
              className={`px-3 py-1.5 text-xs rounded-t font-medium transition-colors ${tab === t ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              {label}
            </button>
          ))}
        </div>
      </CardHeader>

      <CardContent className="pt-4">

        {/* ── TAB: Risk Register ─────────────────────────── */}
        {tab === "register" && (
          <>
            {/* Filters */}
            <div className="flex gap-2 mb-4 flex-wrap">
              <Sel value={filterLevel}  onChange={setFilterLevel}  options={[...RISK_LEVELS]}  placeholder="All levels" />
              <Sel value={filterStatus} onChange={setFilterStatus} options={[...RISK_STATUSES]} placeholder="All statuses" />
            </div>

            {isLoading ? (
              <div className="space-y-2">{[...Array(4)].map((_,i)=><Skeleton key={i} className="h-14 w-full"/>)}</div>
            ) : !data?.items?.length ? (
              <EmptyState icon={ShieldAlert} message="No risks recorded" sub="Click 'Add Risk' to log a risk to the register." />
            ) : (
              <div className="divide-y text-sm">
                {data.items.map(r => (
                  <div key={r.id} className="py-3 flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <button className="font-medium hover:underline text-left"
                          onClick={() => openDetail(r)}>{r.riskTitle}</button>
                        {riskLevelBadge(r.riskLevel)}
                        {r.residualRiskLevel && riskLevelBadge(r.residualRiskLevel)}
                        {riskStatusBadge(r.status)}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5 flex gap-3 flex-wrap">
                        <span>{r.riskCategory}</span>
                        <span>Inherent: {r.inherentRiskScore} ({r.likelihood}×{r.impact})</span>
                        {r.residualRiskScore != null && <span>Residual: {r.residualRiskScore}</span>}
                        {r.riskOwner && <span>Owner: {r.riskOwner}</span>}
                        {r.targetRemediationDate && <span>Due: {r.targetRemediationDate.slice(0,10)}</span>}
                      </div>
                    </div>
                    {canManage && (
                      <div className="flex items-center gap-1 shrink-0">
                        <Button variant="ghost" size="sm" className="h-6 text-xs px-2"
                          onClick={()=>{setStatusTarget(r);setStatusForm({status:"InProgress",remediationEvidence:""});setShowStatusDlg(true);}}>
                          Status
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Edit" onClick={()=>openEdit(r)}>
                          <Pencil className="w-3.5 h-3.5"/>
                        </Button>
                        {r.status !== "Mitigated" && r.status !== "Closed" && (
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" aria-label="Delete"
                            onClick={()=>del.mutate(r.id)}>
                            <Trash2 className="w-3.5 h-3.5"/>
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── TAB: Create / Edit ─────────────────────────── */}
        {tab === "create" && (
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            {/* Calculated risk preview */}
            <div className="flex gap-4 p-3 bg-muted/40 rounded-lg text-sm">
              <div>
                <span className="text-muted-foreground text-xs">Inherent Risk</span>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="font-mono font-bold">{iScore}</span>
                  {riskLevelBadge(iLevel)}
                </div>
              </div>
              {rScore !== null && (
                <div>
                  <span className="text-muted-foreground text-xs">Residual Risk</span>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="font-mono font-bold">{rScore}</span>
                    {rLevel && riskLevelBadge(rLevel)}
                  </div>
                </div>
              )}
              <div className="text-xs text-muted-foreground self-end ml-auto">
                Score = Likelihood × Impact (1–5)
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label htmlFor="r-title">Risk Title *</Label>
                <Input id="r-title" value={form.riskTitle}
                  onChange={e=>{setForm(f=>({...f,riskTitle:e.target.value}));setFieldErrs(fe=>({...fe,riskTitle:""}));}}
                  aria-invalid={!!fieldErrs.riskTitle||undefined}/>
                {fieldErrs.riskTitle && <p className="text-xs text-destructive mt-0.5">{fieldErrs.riskTitle}</p>}
              </div>
              <div>
                <Label htmlFor="r-cat">Category</Label>
                <Sel id="r-cat" value={form.riskCategory}
                  onChange={v=>setForm(f=>({...f,riskCategory:v}))} options={RISK_CATS}/>
              </div>
              <div>
                <Label htmlFor="r-asset">Asset / System</Label>
                <Input id="r-asset" value={form.assetOrSystem}
                  onChange={e=>setForm(f=>({...f,assetOrSystem:e.target.value}))}
                  placeholder="e.g. EHR, Billing system"/>
              </div>
            </div>

            <div>
              <Label htmlFor="r-desc">Risk Description</Label>
              <Textarea id="r-desc" rows={2} value={form.riskDescription}
                onChange={e=>setForm(f=>({...f,riskDescription:e.target.value}))}/>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="r-threat">Threat *</Label>
                <Input id="r-threat" value={form.threat}
                  onChange={e=>{setForm(f=>({...f,threat:e.target.value}));setFieldErrs(fe=>({...fe,threat:""}));}}
                  placeholder="e.g. Malicious insider"
                  aria-invalid={!!fieldErrs.threat||undefined}/>
                {fieldErrs.threat && <p className="text-xs text-destructive mt-0.5">{fieldErrs.threat}</p>}
              </div>
              <div>
                <Label htmlFor="r-vuln">Vulnerability *</Label>
                <Input id="r-vuln" value={form.vulnerability}
                  onChange={e=>{setForm(f=>({...f,vulnerability:e.target.value}));setFieldErrs(fe=>({...fe,vulnerability:""}));}}
                  placeholder="e.g. Excessive access privileges"
                  aria-invalid={!!fieldErrs.vulnerability||undefined}/>
                {fieldErrs.vulnerability && <p className="text-xs text-destructive mt-0.5">{fieldErrs.vulnerability}</p>}
              </div>
            </div>

            <div className="grid grid-cols-4 gap-3">
              <div>
                <Label htmlFor="r-like">Likelihood (1–5)</Label>
                <Input id="r-like" type="number" min={1} max={5} value={form.likelihood}
                  onChange={e=>setForm(f=>({...f,likelihood:e.target.value as any}))}/>
              </div>
              <div>
                <Label htmlFor="r-impact">Impact (1–5)</Label>
                <Input id="r-impact" type="number" min={1} max={5} value={form.impact}
                  onChange={e=>setForm(f=>({...f,impact:e.target.value as any}))}/>
              </div>
              <div>
                <Label htmlFor="r-rlike">Residual Likelihood</Label>
                <Input id="r-rlike" type="number" min={1} max={5} value={form.residualLikelihood}
                  onChange={e=>setForm(f=>({...f,residualLikelihood:e.target.value as any}))}
                  placeholder="—"/>
              </div>
              <div>
                <Label htmlFor="r-rimpact">Residual Impact</Label>
                <Input id="r-rimpact" type="number" min={1} max={5} value={form.residualImpact}
                  onChange={e=>setForm(f=>({...f,residualImpact:e.target.value as any}))}
                  placeholder="—"/>
              </div>
            </div>

            <div>
              <Label htmlFor="r-controls">Existing Controls</Label>
              <Textarea id="r-controls" rows={2} value={form.existingControls}
                onChange={e=>setForm(f=>({...f,existingControls:e.target.value}))}
                placeholder="RBAC, MFA, audit logging, encryption at rest…"/>
            </div>

            <div>
              <Label htmlFor="r-ctlref">Control References</Label>
              <Input id="r-ctlref" value={form.controlReferences}
                onChange={e=>setForm(f=>({...f,controlReferences:e.target.value}))}
                placeholder="e.g. NIST CSF PR.AC-1, HIPAA §164.312(a)"/>
            </div>

            <div>
              <Label htmlFor="r-plan">
                Treatment Plan
                {(iLevel === "High" || iLevel === "Critical") && <span className="text-destructive ml-1">*</span>}
              </Label>
              <Textarea id="r-plan" rows={2} value={form.treatmentPlan}
                onChange={e=>{setForm(f=>({...f,treatmentPlan:e.target.value}));setFieldErrs(fe=>({...fe,treatmentPlan:""}));}}
                aria-invalid={!!fieldErrs.treatmentPlan||undefined}
                placeholder="Describe the treatment strategy…"/>
              {fieldErrs.treatmentPlan && <p className="text-xs text-destructive mt-0.5">{fieldErrs.treatmentPlan}</p>}
            </div>

            <div>
              <Label htmlFor="r-mit">Mitigation Actions</Label>
              <Textarea id="r-mit" rows={2} value={form.mitigationActions}
                onChange={e=>setForm(f=>({...f,mitigationActions:e.target.value}))}
                placeholder="Specific actions taken or planned…"/>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="r-owner">
                  Risk Owner
                  {(iLevel === "High" || iLevel === "Critical") && <span className="text-destructive ml-1">*</span>}
                </Label>
                <Input id="r-owner" value={form.riskOwner}
                  onChange={e=>{setForm(f=>({...f,riskOwner:e.target.value}));setFieldErrs(fe=>({...fe,riskOwner:""}));}}
                  placeholder="Name or role"
                  aria-invalid={!!fieldErrs.riskOwner||undefined}/>
                {fieldErrs.riskOwner && <p className="text-xs text-destructive mt-0.5">{fieldErrs.riskOwner}</p>}
              </div>
              <div>
                <Label htmlFor="r-target">
                  Target Remediation Date
                  {(iLevel === "High" || iLevel === "Critical") && <span className="text-destructive ml-1">*</span>}
                </Label>
                <Input id="r-target" type="date" value={form.targetRemediationDate}
                  onChange={e=>{setForm(f=>({...f,targetRemediationDate:e.target.value}));setFieldErrs(fe=>({...fe,targetRemediationDate:""}));}}
                  aria-invalid={!!fieldErrs.targetRemediationDate||undefined}/>
                {fieldErrs.targetRemediationDate && <p className="text-xs text-destructive mt-0.5">{fieldErrs.targetRemediationDate}</p>}
              </div>
            </div>

            <div>
              <Label htmlFor="r-evidence">Remediation Evidence</Label>
              <Textarea id="r-evidence" rows={2} value={form.remediationEvidence}
                onChange={e=>setForm(f=>({...f,remediationEvidence:e.target.value}))}
                placeholder="Links, documents, or descriptions of completed mitigations…"/>
            </div>

            <div>
              <Label htmlFor="r-review">Review Date</Label>
              <Input id="r-review" type="date" value={form.reviewDate}
                onChange={e=>setForm(f=>({...f,reviewDate:e.target.value}))}/>
            </div>

            {fieldErrs["_"] && <p className="text-xs text-destructive">{fieldErrs["_"]}</p>}

            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={()=>{setTab("register");setEditing(null);}}>Cancel</Button>
              <Button onClick={handleSave} disabled={save.isPending}>
                {save.isPending ? "Saving…" : editing ? "Save Changes" : "Register Risk"}
              </Button>
            </div>
          </div>
        )}

        {/* ── TAB: Risk Detail ───────────────────────────── */}
        {tab === "detail" && detail && (
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-semibold text-base">{detail.risk.riskTitle}</h3>
                <p className="text-xs text-muted-foreground">{detail.risk.riskCategory} · {detail.risk.assetOrSystem}</p>
              </div>
              <div className="flex items-center gap-1.5">
                {riskLevelBadge(detail.risk.riskLevel)}
                {riskStatusBadge(detail.risk.status)}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Identified Risk</p>
                <p>{detail.risk.riskDescription}</p>
                <div className="mt-2 space-y-1">
                  <p><span className="text-muted-foreground text-xs">Threat:</span> {detail.risk.threat}</p>
                  <p><span className="text-muted-foreground text-xs">Vulnerability:</span> {detail.risk.vulnerability}</p>
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Risk Scores</p>
                <div className="space-y-1 text-xs">
                  <p>Inherent: {detail.risk.likelihood} × {detail.risk.impact} = <strong>{detail.risk.inherentRiskScore}</strong> {riskLevelBadge(detail.risk.riskLevel)}</p>
                  {detail.risk.residualRiskScore != null && (
                    <p>Residual: {detail.risk.residualLikelihood} × {detail.risk.residualImpact} = <strong>{detail.risk.residualRiskScore}</strong> {detail.risk.residualRiskLevel && riskLevelBadge(detail.risk.residualRiskLevel)}</p>
                  )}
                </div>
              </div>
            </div>

            {detail.risk.existingControls && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Existing Controls</p>
                <p className="text-sm">{detail.risk.existingControls}</p>
                {detail.risk.controlReferences && <p className="text-xs text-muted-foreground mt-0.5">{detail.risk.controlReferences}</p>}
              </div>
            )}
            {detail.risk.treatmentPlan && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Treatment Plan / Mitigation</p>
                <p className="text-sm">{detail.risk.treatmentPlan}</p>
                {detail.risk.mitigationActions && <p className="text-xs text-muted-foreground mt-1">{detail.risk.mitigationActions}</p>}
              </div>
            )}
            <div className="flex gap-6 text-sm flex-wrap">
              {detail.risk.riskOwner         && <div><span className="text-muted-foreground text-xs">Owner</span><p>{detail.risk.riskOwner}</p></div>}
              {detail.risk.targetRemediationDate && <div><span className="text-muted-foreground text-xs">Target date</span><p>{detail.risk.targetRemediationDate.slice(0,10)}</p></div>}
              {detail.risk.reviewDate         && <div><span className="text-muted-foreground text-xs">Review date</span><p>{detail.risk.reviewDate.slice(0,10)}</p></div>}
            </div>
            {detail.risk.remediationEvidence && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Remediation Evidence</p>
                <p className="text-sm">{detail.risk.remediationEvidence}</p>
              </div>
            )}

            {(detail.auditHistory ?? []).length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Audit History</p>
                <div className="divide-y text-xs">
                  {(detail.auditHistory ?? []).map((a,i) => (
                    <div key={i} className="py-1.5 flex gap-3">
                      <span className="text-muted-foreground shrink-0">{fmtDateTime(a.timestamp)}</span>
                      <span className="font-mono">{a.action}</span>
                      {a.changes && <span className="text-muted-foreground">{a.changes}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {canManage && (
              <div className="flex gap-2 pt-2">
                <Button size="sm" variant="outline" onClick={()=>openEdit(detail.risk)}>Edit</Button>
                <Button size="sm" variant="outline"
                  onClick={()=>{setStatusTarget(detail.risk);setStatusForm({status:"InProgress",remediationEvidence:""});setShowStatusDlg(true);}}>
                  Update Status
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>

      {/* ── Status Dialog ─────────────────────────────────── */}
      {showStatusDlg && statusTarget && (
        <Dialog open={showStatusDlg} onOpenChange={v=>{setShowStatusDlg(v);if(!v)setStatusTarget(null);}}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Update Risk Status</DialogTitle>
              <DialogDescription className="truncate">{statusTarget.riskTitle}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>New Status</Label>
                <Sel value={statusForm.status}
                  onChange={v=>setStatusForm(f=>({...f,status:v}))}
                  options={["Open","InProgress","Mitigated","Accepted","Closed"]}/>
              </div>
              {(statusForm.status === "Mitigated" || statusForm.status === "Closed") && (
                <div>
                  <Label>Remediation Evidence *</Label>
                  <Textarea rows={3} value={statusForm.remediationEvidence}
                    onChange={e=>setStatusForm(f=>({...f,remediationEvidence:e.target.value}))}
                    placeholder="Describe or link to the evidence of remediation…"/>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={()=>{setShowStatusDlg(false);setStatusTarget(null);}}>Cancel</Button>
              <Button onClick={()=>patchStatus.mutate()} disabled={patchStatus.isPending}>
                {patchStatus.isPending?"Saving…":"Update"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 1 — HIPAA Summary cards
// ═══════════════════════════════════════════════════════════════════════════════

function HipaaSummaryCards({ summary }: { summary: HipaaSummary }) {
  const cards = [
    {
      label: "Open incidents",
      value: summary.incidents.open,
      sub: summary.incidents.confirmedBreachesAwaitingHhs > 0
        ? `${summary.incidents.confirmedBreachesAwaitingHhs} breach awaiting HHS`
        : "None awaiting HHS",
      icon: AlertTriangle,
      color: summary.incidents.open > 0 ? "text-red-500" : "text-muted-foreground",
    },
    {
      label: "Active BAAs",
      value: summary.baa.active,
      sub: summary.baa.expiringSoon > 0
        ? `${summary.baa.expiringSoon} expiring in 30d`
        : summary.baa.expired > 0
        ? `${summary.baa.expired} expired`
        : "All current",
      icon: Building2,
      color: summary.baa.expiringSoon > 0 ? "text-amber-500" : "text-muted-foreground",
    },
    {
      label: "Training coverage",
      value: `${summary.training.coveragePct}%`,
      sub: `${summary.training.trainedUsers} / ${summary.training.totalUsers} users`,
      icon: Users,
      color: summary.training.coveragePct < 80 ? "text-amber-500" : "text-emerald-500",
    },
    {
      label: "PHI events (30d)",
      value: summary.phiAccess.eventsLast30Days,
      sub: "Access log entries",
      icon: Activity,
      color: "text-muted-foreground",
    },
  ];

  const sv = summary.sourceVerifications;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {cards.map(({ label, value, sub, icon: Icon, color }) => (
          <div key={label} className="border rounded-md p-3 flex items-center gap-3">
            <Icon className={`w-5 h-5 shrink-0 ${color}`} aria-hidden="true" />
            <div className="min-w-0">
              <p className={`text-xl font-bold leading-none ${color}`}>{value}</p>
              <p className="text-xs text-muted-foreground mt-0.5 truncate">{label}</p>
              <p className="text-[10px] text-muted-foreground/70 mt-0.5 truncate">{sub}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Source Verification Status strip */}
      {sv && (
        <div className="border rounded-md px-4 py-2.5 flex flex-wrap gap-x-6 gap-y-1 text-xs">
          <span className="font-semibold text-muted-foreground mr-1">Knowledge Sources:</span>
          <span className="text-muted-foreground">{sv.total} total</span>
          {sv.blocked > 0 && (
            <span className="text-red-600 font-medium">{sv.blocked} blocked</span>
          )}
          {sv.allowed > 0 && (
            <span className="text-amber-600 font-medium">{sv.allowed} risk&nbsp;accepted</span>
          )}
          {sv.activeExceptions > 0 && (
            <span className="text-blue-600">{sv.activeExceptions} active&nbsp;exception{sv.activeExceptions !== 1 ? "s" : ""}</span>
          )}
          {sv.expiredExceptions > 0 && (
            <span className="text-red-500">{sv.expiredExceptions} expired&nbsp;exception{sv.expiredExceptions !== 1 ? "s" : ""}</span>
          )}
          {sv.openAlerts > 0 && (
            <span className="text-orange-600 font-semibold">{sv.openAlerts} re-verification&nbsp;alert{sv.openAlerts !== 1 ? "s" : ""}</span>
          )}
          {sv.blocked === 0 && sv.allowed === 0 && sv.openAlerts === 0 && (
            <span className="text-emerald-600">All verified</span>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 1B — Compliance Alerts (re-verification failures on approved sources)
// ═══════════════════════════════════════════════════════════════════════════════

type ComplianceAlert = {
  id: string;
  verificationId: string;
  knowledgeSourceId: string;
  sourceName: string;
  previousDecisionId: string;
  previousDecisionType: string;
  failedControlsJson: string;
  createdAt: string;
};

type FailedControl = { controlKey: string; controlName: string; findings: string | null };

function decisionTypeLabel(t: string) {
  if (t === "accept_risk")        return "Risk Accepted";
  if (t === "exception_approved") return "Exception Approved";
  return t;
}

function HipaaComplianceAlertsPanel({ canManage }: { canManage: boolean }) {
  const { data: alerts, isLoading, refetch } = useQuery<ComplianceAlert[]>({
    queryKey: ["/api/organization/compliance/hipaa/alerts"],
    queryFn: () => apiRequest("GET", "/api/organization/compliance/hipaa/alerts").then(r => r.json()),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
  const { toast } = useToast();

  const dismiss = useMutation({
    mutationFn: (id: string) =>
      apiRequest("PATCH", `/api/organization/compliance/hipaa/alerts/${id}/dismiss`, {}),
    onSuccess: () => {
      toast({ title: "Alert dismissed." });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/hipaa/alerts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/hipaa/summary"] });
      refetch();
    },
    onError: () => toast({ title: "Unable to dismiss alert.", variant: "destructive" }),
  });

  if (isLoading || !alerts || alerts.length === 0) return null;

  return (
    <div className="space-y-2" role="region" aria-label="Compliance Alerts">
      {alerts.map(alert => {
        let failedControls: FailedControl[] = [];
        try { failedControls = JSON.parse(alert.failedControlsJson); } catch { /* ignore */ }

        return (
          <div
            key={alert.id}
            className="border border-orange-300 rounded-md bg-orange-50 px-4 py-3 text-sm"
            role="alert"
          >
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="space-y-1 min-w-0">
                <p className="font-semibold text-orange-800 flex items-center gap-1.5">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                  Re-verification failed after compliance approval
                </p>
                <p className="text-orange-700 text-xs">
                  <span className="font-medium">{alert.sourceName}</span>
                  {" "}was previously allowed via <span className="font-medium">{decisionTypeLabel(alert.previousDecisionType)}</span>,
                  then re-verified and found non-compliant.
                </p>
                {failedControls.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {failedControls.map(fc => (
                      <span
                        key={fc.controlKey}
                        className="inline-block rounded bg-orange-200 px-1.5 py-0.5 text-[10px] font-medium text-orange-900"
                        title={fc.findings ?? undefined}
                      >
                        {fc.controlName}
                      </span>
                    ))}
                  </div>
                )}
                <p className="text-[10px] text-orange-600 mt-1">
                  Detected {new Date(alert.createdAt).toLocaleString()}
                </p>
              </div>
              {canManage && (
                <button
                  type="button"
                  className="flex-shrink-0 text-xs text-orange-700 border border-orange-400 rounded px-2 py-1 hover:bg-orange-100 transition-colors disabled:opacity-50"
                  onClick={() => dismiss.mutate(alert.id)}
                  disabled={dismiss.isPending}
                  aria-label={`Dismiss alert for ${alert.sourceName}`}
                >
                  Dismiss
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 2 — Incident Management
// ═══════════════════════════════════════════════════════════════════════════════

function CreateIncidentModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { toast } = useToast();
  const [incidentType, setIncidentType] = useState("UnauthorizedAccess");
  const [severity,     setSeverity]     = useState("Medium");
  const [phiInvolved,  setPhiInvolved]  = useState(true);
  const [description,  setDescription]  = useState("");
  const [discoveredAt, setDiscoveredAt] = useState(new Date().toISOString().slice(0, 10));
  const [affectedCount,setAffectedCount]= useState("");
  const [notes,        setNotes]        = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const create = useMutation({
    mutationFn: () =>
      apiRequest("POST", "/api/organization/compliance/hipaa/incidents", {
        incidentType, severity, phiInvolved, description: description.trim(),
        discoveredAt: new Date(discoveredAt).toISOString(),
        affectedCount: affectedCount ? Number(affectedCount) : null,
        notes: notes.trim() || null,
      }),
    onSuccess: () => {
      toast({ title: "Incident logged", description: "Auto-evidence created in HIPAA evidence timeline." });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/hipaa/incidents"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/hipaa/summary"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/evidence"] });
      setErrors({});
      onCreated();
    },
    onError: (e: any) => {
      const serverErrs = extractServerErrors(e);
      if (serverErrs && Object.keys(serverErrs).length > 0) {
        setErrors(serverErrs);
        toast({ title: "Please correct the highlighted fields.", variant: "destructive" });
      } else {
        toast({ title: "Unable to save", description: e?.message ?? "An unexpected error occurred.", variant: "destructive" });
      }
    },
  });

  return (
    <Dialog open onOpenChange={(o) => { if (!o) { setErrors({}); onClose(); } }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Log HIPAA Incident</DialogTitle>
          <DialogDescription>
            Creates a permanent incident record and auto-generates a HIPAA evidence entry.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs" htmlFor="inc-type">Incident type *</Label>
              <Sel id="inc-type" value={incidentType} onChange={setIncidentType}
                options={INCIDENT_TYPES} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs" htmlFor="inc-sev">Severity *</Label>
              <Sel id="inc-sev" value={severity} onChange={setSeverity} options={SEVERITIES} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs" htmlFor="inc-disc">Discovered on *</Label>
              <Input id="inc-disc" type="date" value={discoveredAt}
                onChange={(e) => { setDiscoveredAt(e.target.value); clearErr(setErrors, "discoveredAt"); }} className={fieldCls("text-sm", errors, "discoveredAt")} aria-invalid={!!errors.discoveredAt} />
              {fErr(errors, "discoveredAt")}
            </div>
            <div className="space-y-1">
              <Label className="text-xs" htmlFor="inc-cnt">Affected records</Label>
              <Input id="inc-cnt" type="number" min={0} value={affectedCount}
                onChange={(e) => setAffectedCount(e.target.value)}
                placeholder="e.g. 50" className="text-sm" />
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs" htmlFor="inc-desc">Description *</Label>
            <Textarea id="inc-desc" value={description} onChange={(e) => { setDescription(e.target.value); clearErr(setErrors, "description"); }}
              placeholder="Describe what happened and initial scope assessment."
              className={fieldCls("text-sm min-h-[80px]", errors, "description")} aria-invalid={!!errors.description} maxLength={2000} />
            {fErr(errors, "description")}
          </div>

          <div className="flex items-center gap-2">
            <input id="inc-phi" type="checkbox" checked={phiInvolved}
              onChange={(e) => setPhiInvolved(e.target.checked)}
              className="h-3.5 w-3.5 rounded border" />
            <Label htmlFor="inc-phi" className="text-xs cursor-pointer">PHI involved</Label>
          </div>

          <div className="space-y-1">
            <Label className="text-xs" htmlFor="inc-notes">Notes (optional)</Label>
            <Textarea id="inc-notes" value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Initial response steps, assigned investigator, etc."
              className="text-sm min-h-[60px]" maxLength={2000} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => {
            const errs: Record<string, string> = {};
            required(errs, "description", description, "Description");
            required(errs, "discoveredAt", discoveredAt, "Discovered on");
            if (Object.keys(errs).length > 0) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; }
            setErrors({});
            create.mutate();
          }} disabled={create.isPending}>
            {create.isPending ? "Logging…" : "Log incident"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UpdateIncidentModal({ incident, onClose, onUpdated }: {
  incident: Incident; onClose: () => void; onUpdated: () => void;
}) {
  const { toast } = useToast();
  const [status,      setStatus]      = useState(incident.status);
  const [breachStatus,setBreachStatus]= useState(incident.breachStatus);
  const [severity,    setSeverity]    = useState(incident.severity);
  const [notes,       setNotes]       = useState(incident.notes ?? "");
  const [hhsDate,     setHhsDate]     = useState(incident.hhsReportedAt?.slice(0,10) ?? "");
  const [resolvedDate,setResolvedDate]= useState(incident.resolvedAt?.slice(0,10) ?? "");

  const update = useMutation({
    mutationFn: () =>
      apiRequest("PUT", `/api/organization/compliance/hipaa/incidents/${incident.id}`, {
        status, breachStatus, severity,
        notes: notes.trim() || null,
        hhsReportedAt: hhsDate     ? new Date(hhsDate).toISOString()     : null,
        resolvedAt:    resolvedDate ? new Date(resolvedDate).toISOString() : null,
      }),
    onSuccess: () => {
      toast({ title: "Incident updated" });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/hipaa/incidents"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/hipaa/summary"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/evidence"] });
      onUpdated();
    },
    onError: (e: any) => toast({ title: "Unable to update", description: e.message, variant: "destructive" }),
  });

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Update Incident</DialogTitle>
          <DialogDescription>
            <span className="font-medium">{INCIDENT_TYPE_LABELS[incident.incidentType] ?? incident.incidentType}</span>
            {" — logged "}{fmtDate(incident.discoveredAt)}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-xs" htmlFor="upd-status">Status</Label>
              <Sel id="upd-status" value={status} onChange={setStatus} options={INCIDENT_STATUSES} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs" htmlFor="upd-breach">Breach status</Label>
              <Sel id="upd-breach" value={breachStatus} onChange={setBreachStatus} options={BREACH_STATUSES} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs" htmlFor="upd-sev">Severity</Label>
              <Sel id="upd-sev" value={severity} onChange={setSeverity} options={SEVERITIES} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs" htmlFor="upd-hhs">HHS reported date</Label>
              <Input id="upd-hhs" type="date" value={hhsDate}
                onChange={(e) => setHhsDate(e.target.value)} className="text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs" htmlFor="upd-res">Resolved date</Label>
              <Input id="upd-res" type="date" value={resolvedDate}
                onChange={(e) => setResolvedDate(e.target.value)} className="text-sm" />
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs" htmlFor="upd-notes">Notes</Label>
            <Textarea id="upd-notes" value={notes} onChange={(e) => setNotes(e.target.value)}
              className="text-sm min-h-[70px]" maxLength={2000} />
          </div>

          <div className="rounded-md border bg-muted/30 p-2.5 text-xs text-muted-foreground space-y-0.5">
            <p><span className="font-medium">Discovered:</span> {fmtDate(incident.discoveredAt)}</p>
            {incident.affectedCount != null && (
              <p><span className="font-medium">Affected records:</span> {incident.affectedCount.toLocaleString()}</p>
            )}
            <p><span className="font-medium">PHI involved:</span> {incident.phiInvolved ? "Yes" : "No"}</p>
            <p className="break-words"><span className="font-medium">Description:</span> {incident.description}</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => update.mutate()} disabled={update.isPending}>
            {update.isPending ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function IncidentSection({ canManage }: { canManage: boolean }) {
  const { toast } = useToast();
  const [showCreate,  setShowCreate]  = useState(false);
  const [editTarget,  setEditTarget]  = useState<Incident | null>(null);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterSev,    setFilterSev]    = useState("");

  const qk = ["/api/organization/compliance/hipaa/incidents", filterStatus, filterSev];
  const { data, isLoading } = useQuery<PagedResult<Incident>>({
    queryKey: qk,
    queryFn: async () => {
      const params = new URLSearchParams({ page: "1", pageSize: "50" });
      if (filterStatus) params.set("status", filterStatus);
      if (filterSev)    params.set("severity", filterSev);
      const res = await fetch(`/api/organization/compliance/hipaa/incidents?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("dotnetbot.jwt") ?? sessionStorage.getItem("dotnetbot.jwt.session") ?? ""}` },
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    staleTime: 0,
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-red-500" aria-hidden="true" />
              Incident Management
            </CardTitle>
            <CardDescription>HIPAA breach incidents and investigation workflow.</CardDescription>
          </div>
          {canManage && (
            <Button size="sm" variant="outline" className="h-7 text-xs shrink-0"
              onClick={() => setShowCreate(true)} data-testid="button-add-incident">
              <Plus className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Log incident
            </Button>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 pt-1">
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
            className="text-xs border rounded px-2 py-1 bg-background">
            <option value="">All statuses</option>
            {INCIDENT_STATUSES.map((s) => <option key={s} value={s}>{s === "UnderInvestigation" ? "Investigating" : s}</option>)}
          </select>
          <select value={filterSev} onChange={(e) => setFilterSev(e.target.value)}
            className="text-xs border rounded px-2 py-1 bg-background">
            <option value="">All severities</option>
            {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </CardHeader>

      <CardContent>
        {isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : !data || data.items.length === 0 ? (
          <EmptyState icon={ShieldAlert} message="No incidents recorded."
            sub={canManage ? "Use 'Log incident' to record a breach or security event." : undefined} />
        ) : (
          <div className="space-y-2" aria-label="Incident list">
            {data.items.map((inc) => (
              <div key={inc.id}
                className="border rounded-md p-3 flex items-start gap-3"
                data-testid={`incident-row-${inc.id}`}>
                <div className="flex-1 min-w-0 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium">
                      {INCIDENT_TYPE_LABELS[inc.incidentType] ?? inc.incidentType}
                    </span>
                    {severityBadge(inc.severity)}
                    {statusBadge(inc.status)}
                    {breachBadge(inc.breachStatus)}
                    {inc.phiInvolved && (
                      <Badge variant="outline" className="text-[10px] h-4 px-1.5 border-red-400/40 bg-red-500/8 text-red-600 dark:text-red-400">
                        PHI
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-2">{inc.description}</p>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                    <span><Calendar className="inline w-3 h-3 mr-0.5" aria-hidden="true" />
                      Discovered: {fmtDate(inc.discoveredAt)}
                    </span>
                    {inc.affectedCount != null && (
                      <span>Affected: {inc.affectedCount.toLocaleString()}</span>
                    )}
                    {inc.hhsReportedAt && (
                      <span>HHS: {fmtDate(inc.hhsReportedAt)}</span>
                    )}
                    {inc.resolvedAt && (
                      <span>Resolved: {fmtDate(inc.resolvedAt)}</span>
                    )}
                  </div>
                </div>
                {canManage && (
                  <Button size="sm" variant="ghost"
                    className="shrink-0 h-7 text-xs"
                    onClick={() => setEditTarget(inc)}
                    aria-label={`Update incident: ${inc.incidentType}`}>
                    <Pencil className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Update
                  </Button>
                )}
              </div>
            ))}
            {data.total > data.items.length && (
              <p className="text-xs text-muted-foreground text-center pt-1">
                Showing {data.items.length} of {data.total} incidents.
              </p>
            )}
          </div>
        )}
      </CardContent>

      {showCreate && (
        <CreateIncidentModal
          onClose={() => setShowCreate(false)}
          onCreated={() => setShowCreate(false)}
        />
      )}
      {editTarget && (
        <UpdateIncidentModal
          incident={editTarget}
          onClose={() => setEditTarget(null)}
          onUpdated={() => setEditTarget(null)}
        />
      )}
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 3 — Business Associate Agreements
// ═══════════════════════════════════════════════════════════════════════════════

function AddBaaModal({ onClose, onAdded }: { onClose: () => void; onAdded: () => void }) {
  const { toast } = useToast();
  const [name,      setName]      = useState("");
  const [type,      setType]      = useState("Other");
  const [desc,      setDesc]      = useState("");
  const [email,     setEmail]     = useState("");
  const [aggDate,   setAggDate]   = useState("");
  const [expDate,   setExpDate]   = useState("");
  const [docUrl,    setDocUrl]    = useState("");
  const [notes,     setNotes]     = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const add = useMutation({
    mutationFn: () =>
      apiRequest("POST", "/api/organization/compliance/hipaa/baa", {
        associateName: name.trim(), associateType: type,
        serviceDescription: desc.trim(),
        contactEmail: email.trim() || null,
        agreementDate: aggDate ? new Date(aggDate).toISOString() : null,
        expiresAt: expDate ? new Date(expDate).toISOString() : null,
        documentUrl: docUrl.trim() || null,
        notes: notes.trim() || null,
      }),
    onSuccess: () => {
      toast({ title: "BAA registered", description: "Auto-evidence created in HIPAA evidence timeline." });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/hipaa/baa"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/hipaa/summary"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/evidence"] });
      setErrors({});
      onAdded();
    },
    onError: (e: any) => {
      const serverErrs = extractServerErrors(e);
      if (serverErrs && Object.keys(serverErrs).length > 0) {
        setErrors(serverErrs);
        toast({ title: "Please correct the highlighted fields.", variant: "destructive" });
      } else {
        toast({ title: "Unable to save", description: e?.message ?? "An unexpected error occurred.", variant: "destructive" });
      }
    },
  });

  return (
    <Dialog open onOpenChange={(o) => { if (!o) { setErrors({}); onClose(); } }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Register Business Associate Agreement</DialogTitle>
          <DialogDescription>
            Add a BAA to your HIPAA register. Auto-generates a HIPAA evidence record.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1 col-span-2 sm:col-span-1">
              <Label className="text-xs" htmlFor="baa-name">Associate name *</Label>
              <Input id="baa-name" value={name} onChange={(e) => { setName(e.target.value); clearErr(setErrors, "associateName"); }}
                placeholder="e.g. AWS Healthcare LLC" maxLength={200} className={fieldCls("", errors, "associateName")} aria-invalid={!!errors.associateName} />
              {fErr(errors, "associateName")}
            </div>
            <div className="space-y-1">
              <Label className="text-xs" htmlFor="baa-type">Associate type *</Label>
              <Sel id="baa-type" value={type} onChange={setType} options={ASSOCIATE_TYPES} />
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs" htmlFor="baa-desc">Service description *</Label>
            <Textarea id="baa-desc" value={desc} onChange={(e) => { setDesc(e.target.value); clearErr(setErrors, "serviceDescription"); }}
              placeholder="Describe the service and what PHI is shared."
              className={fieldCls("text-sm min-h-[70px]", errors, "serviceDescription")} aria-invalid={!!errors.serviceDescription} maxLength={1000} />
            {fErr(errors, "serviceDescription")}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs" htmlFor="baa-agg">Agreement date</Label>
              <Input id="baa-agg" type="date" value={aggDate}
                onChange={(e) => setAggDate(e.target.value)} className="text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs" htmlFor="baa-exp">Expires</Label>
              <Input id="baa-exp" type="date" value={expDate}
                onChange={(e) => setExpDate(e.target.value)} className="text-sm" />
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs" htmlFor="baa-email">Contact email</Label>
            <Input id="baa-email" type="email" value={email}
              onChange={(e) => setEmail(e.target.value)} placeholder="dpo@vendor.com" maxLength={200} />
          </div>

          <div className="space-y-1">
            <Label className="text-xs" htmlFor="baa-url">Document URL</Label>
            <Input id="baa-url" type="url" value={docUrl}
              onChange={(e) => setDocUrl(e.target.value)} placeholder="https://…" maxLength={500} />
          </div>

          <div className="space-y-1">
            <Label className="text-xs" htmlFor="baa-notes">Notes</Label>
            <Textarea id="baa-notes" value={notes} onChange={(e) => setNotes(e.target.value)}
              className="text-sm min-h-[50px]" maxLength={1000} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => {
            const errs: Record<string, string> = {};
            required(errs, "associateName", name, "Business associate name");
            required(errs, "serviceDescription", desc, "Services description");
            if (Object.keys(errs).length > 0) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; }
            setErrors({});
            add.mutate();
          }} disabled={add.isPending}>
            {add.isPending ? "Registering…" : "Register BAA"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BaaSection({ canManage }: { canManage: boolean }) {
  const { toast } = useToast();
  const [showAdd,     setShowAdd]     = useState(false);
  const [filterStatus,setFilterStatus]= useState("");
  const [confirmDel,  setConfirmDel]  = useState<Baa | null>(null);

  const qk = ["/api/organization/compliance/hipaa/baa", filterStatus];
  const { data, isLoading } = useQuery<PagedResult<Baa>>({
    queryKey: qk,
    queryFn: async () => {
      const params = new URLSearchParams({ page: "1", pageSize: "50" });
      if (filterStatus) params.set("status", filterStatus);
      const res = await fetch(`/api/organization/compliance/hipaa/baa?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("dotnetbot.jwt") ?? sessionStorage.getItem("dotnetbot.jwt.session") ?? ""}` },
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    staleTime: 0,
  });

  const terminate = useMutation({
    mutationFn: (baa: Baa) =>
      apiRequest("PUT", `/api/organization/compliance/hipaa/baa/${baa.id}`, { status: "Terminated" }),
    onSuccess: () => {
      toast({ title: "BAA terminated" });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/hipaa/baa"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/hipaa/summary"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/evidence"] });
    },
    onError: (e: any) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  const remove = useMutation({
    mutationFn: (id: string) =>
      apiRequest("DELETE", `/api/organization/compliance/hipaa/baa/${id}`),
    onSuccess: () => {
      toast({ title: "BAA removed" });
      setConfirmDel(null);
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/hipaa/baa"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/hipaa/summary"] });
    },
    onError: (e: any) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 className="w-4 h-4 text-blue-500" aria-hidden="true" />
                Business Associate Agreements
              </CardTitle>
              <CardDescription>Register and track HIPAA BAAs with all business associates handling PHI.</CardDescription>
            </div>
            {canManage && (
              <Button size="sm" variant="outline" className="h-7 text-xs shrink-0"
                onClick={() => setShowAdd(true)} data-testid="button-add-baa">
                <Plus className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Add BAA
              </Button>
            )}
          </div>
          <div className="pt-1">
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
              className="text-xs border rounded px-2 py-1 bg-background">
              <option value="">All statuses</option>
              {BAA_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </CardHeader>

        <CardContent>
          {isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : !data || data.items.length === 0 ? (
            <EmptyState icon={Building2} message="No BAAs registered."
              sub={canManage ? "Use 'Add BAA' to register a business associate agreement." : undefined} />
          ) : (
            <div className="space-y-2" aria-label="BAA list">
              {data.items.map((baa) => {
                const expired     = isExpired(baa.expiresAt);
                const expiringSoon = isExpiringSoon(baa.expiresAt);
                return (
                  <div key={baa.id}
                    className="border rounded-md p-3 flex items-start gap-3"
                    data-testid={`baa-row-${baa.id}`}>
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium">{baa.associateName}</span>
                        {statusBadge(baa.status)}
                        <Badge variant="outline" className="text-[10px] h-4 px-1.5 border-muted text-muted-foreground">
                          {baa.associateType}
                        </Badge>
                        {baa.expiresAt && expired && baa.status === "Active" && (
                          <Badge variant="outline" className="text-[10px] h-4 px-1.5 border-red-500/40 bg-red-500/10 text-red-600 dark:text-red-400">
                            Expired
                          </Badge>
                        )}
                        {baa.expiresAt && expiringSoon && !expired && (
                          <Badge variant="outline" className="text-[10px] h-4 px-1.5 border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400">
                            <Clock className="inline w-2.5 h-2.5 mr-0.5" aria-hidden="true" />
                            Expiring soon
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-1">{baa.serviceDescription}</p>
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                        {baa.agreementDate && <span>Signed: {fmtDate(baa.agreementDate)}</span>}
                        {baa.expiresAt     && <span>Expires: {fmtDate(baa.expiresAt)}</span>}
                        {baa.contactEmail  && <span>{baa.contactEmail}</span>}
                        {baa.documentUrl   && (
                          <a href={baa.documentUrl} target="_blank" rel="noopener noreferrer"
                            className="text-primary underline-offset-2 hover:underline">
                            Document ↗
                          </a>
                        )}
                      </div>
                    </div>
                    {canManage && (
                      <div className="flex items-center gap-1 shrink-0">
                        {baa.status === "Active" && (
                          <Button size="sm" variant="ghost" className="h-7 text-xs text-amber-600 hover:text-amber-700"
                            onClick={() => terminate.mutate(baa)}
                            disabled={terminate.isPending}
                            aria-label={`Terminate BAA: ${baa.associateName}`}>
                            Terminate
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                          onClick={() => setConfirmDel(baa)}
                          aria-label={`Delete BAA: ${baa.associateName}`}>
                          <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
              {data.total > data.items.length && (
                <p className="text-xs text-muted-foreground text-center pt-1">
                  Showing {data.items.length} of {data.total} BAAs.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {showAdd && <AddBaaModal onClose={() => setShowAdd(false)} onAdded={() => setShowAdd(false)} />}

      {/* Delete confirmation */}
      {confirmDel && (
        <Dialog open onOpenChange={(o) => { if (!o) setConfirmDel(null); }}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Remove BAA?</DialogTitle>
              <DialogDescription>
                This permanently removes the BAA record for <strong>{confirmDel.associateName}</strong>.
                The action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmDel(null)}>Cancel</Button>
              <Button variant="destructive"
                onClick={() => remove.mutate(confirmDel.id)}
                disabled={remove.isPending}>
                {remove.isPending ? "Removing…" : "Remove BAA"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 4 — Workforce Training
// ═══════════════════════════════════════════════════════════════════════════════

// UUID v1–v5 pattern — catches "Stu-001", integers, and other non-UUID strings client-side.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function AddTrainingModal({ onClose, onAdded }: { onClose: () => void; onAdded: () => void }) {
  const { toast } = useToast();
  const [userId,      setUserId]      = useState("");
  const [trainType,   setTrainType]   = useState("HipaaPrivacy");
  const [title,       setTitle]       = useState("");
  const [completedAt, setCompletedAt] = useState(new Date().toISOString().slice(0, 10));
  const [expiresAt,   setExpiresAt]   = useState("");
  const [evidenceUrl, setEvidenceUrl] = useState("");
  const [notes,       setNotes]       = useState("");
  // Per-field errors — shown directly under each input rather than a single banner.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const clearFieldError = (field: string) =>
    setFieldErrors(prev => { const n = { ...prev }; delete n[field]; return n; });

  const add = useMutation({
    mutationFn: () =>
      apiRequest("POST", "/api/organization/compliance/hipaa/training", {
        userId: userId.trim(),
        trainingType: trainType,
        trainingTitle: title.trim(),
        completedAt: new Date(completedAt).toISOString(),
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
        evidenceUrl: evidenceUrl.trim() || null,
        notes: notes.trim() || null,
      }),
    onSuccess: () => {
      toast({ title: "Training record logged", description: "Auto-evidence created in HIPAA evidence timeline." });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/hipaa/training"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/hipaa/summary"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/evidence"] });
      setFieldErrors({});
      onAdded();
    },
    onError: (e: any) => {
      // Map server field errors back to per-field state so they appear directly under each input.
      const uidMsg   = fieldError(e, "userId");
      const titleMsg = fieldError(e, "trainingTitle");
      const typeMsg  = fieldError(e, "trainingType");
      const bodyMsg  = fieldError(e, "_body");
      const genMsg   = fieldError(e, "_");

      const errs: Record<string, string> = {};
      if (uidMsg)   errs.userId        = uidMsg;
      if (titleMsg) errs.trainingTitle = titleMsg;
      if (typeMsg)  errs.trainingType  = typeMsg;

      if (Object.keys(errs).length > 0) {
        setFieldErrors(errs);
        // When field-specific errors exist inline, the toast is generic to avoid duplication.
        toast({ title: "Unable to log training", description: "Please correct the highlighted fields.", variant: "destructive" });
      } else {
        const msg = genMsg ?? bodyMsg ?? e.message ?? "An unexpected error occurred.";
        setFieldErrors({ _: msg });
        toast({ title: "Unable to log training", description: msg, variant: "destructive" });
      }
    },
  });

  // Client-side validation runs before the API call — catches format errors without a round-trip.
  const handleSubmit = () => {
    const errs: Record<string, string> = {};
    const uid = userId.trim();
    if (!uid)                        errs.userId = "User ID is required.";
    else if (!UUID_RE.test(uid))     errs.userId = "Enter a valid User ID (UUID).";
    if (!title.trim())               errs.trainingTitle = "Training title is required.";
    if (!completedAt)                errs.completedAt = "Completion date is required.";
    if (Object.keys(errs).length > 0) { setFieldErrors(errs); return; }
    setFieldErrors({});
    add.mutate();
  };

  const canSubmit = !add.isPending;

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Log Training Completion</DialogTitle>
          <DialogDescription>
            Record that a workforce member completed required HIPAA training. Auto-generates a HIPAA evidence entry.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          <div className="space-y-1">
            <Label className="text-xs" htmlFor="tr-uid">User ID (UUID) *</Label>
            <Input
              id="tr-uid"
              value={userId}
              onChange={(e) => { setUserId(e.target.value); clearFieldError("userId"); }}
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              className={`font-mono text-xs${fieldErrors.userId ? " border-destructive" : ""}`}
              maxLength={36}
              aria-invalid={!!fieldErrors.userId}
              aria-describedby={fieldErrors.userId ? "tr-uid-err" : undefined}
            />
            <p className="text-[10px] text-muted-foreground">
              Find the user's ID in Settings → Users, then copy their Account ID.
            </p>
            {fieldErrors.userId && (
              <p id="tr-uid-err" role="alert" className="text-xs text-destructive">{fieldErrors.userId}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs" htmlFor="tr-type">Training type *</Label>
              <Sel id="tr-type" value={trainType} onChange={(v) => { setTrainType(v); clearFieldError("trainingType"); }} options={TRAINING_TYPES} />
              {fieldErrors.trainingType && (
                <p role="alert" className="text-xs text-destructive">{fieldErrors.trainingType}</p>
              )}
            </div>
            <div className="space-y-1">
              <Label className="text-xs" htmlFor="tr-comp">Completed on *</Label>
              <Input id="tr-comp" type="date" value={completedAt}
                onChange={(e) => { setCompletedAt(e.target.value); clearFieldError("completedAt"); }}
                className={`text-sm${fieldErrors.completedAt ? " border-destructive" : ""}`} />
              {fieldErrors.completedAt && (
                <p role="alert" className="text-xs text-destructive">{fieldErrors.completedAt}</p>
              )}
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs" htmlFor="tr-title">Training title *</Label>
            <Input
              id="tr-title"
              value={title}
              onChange={(e) => { setTitle(e.target.value); clearFieldError("trainingTitle"); }}
              placeholder="e.g. Annual HIPAA Privacy & Security Training 2026"
              className={fieldErrors.trainingTitle ? "border-destructive" : ""}
              maxLength={200}
              aria-invalid={!!fieldErrors.trainingTitle}
              aria-describedby={fieldErrors.trainingTitle ? "tr-title-err" : undefined}
            />
            {fieldErrors.trainingTitle && (
              <p id="tr-title-err" role="alert" className="text-xs text-destructive">{fieldErrors.trainingTitle}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs" htmlFor="tr-exp">Expires</Label>
              <Input id="tr-exp" type="date" value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)} className="text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs" htmlFor="tr-url">Certificate URL</Label>
              <Input id="tr-url" type="url" value={evidenceUrl}
                onChange={(e) => setEvidenceUrl(e.target.value)}
                placeholder="https://lms.example.com/cert/…" maxLength={500} />
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs" htmlFor="tr-notes">Notes</Label>
            <Textarea id="tr-notes" value={notes} onChange={(e) => setNotes(e.target.value)}
              className="text-sm min-h-[50px]" maxLength={1000} />
          </div>
        </div>

        {fieldErrors._ && (
          <p role="alert" className="text-xs text-destructive px-1 -mb-1">{fieldErrors._}</p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {add.isPending ? "Logging…" : "Log training"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TrainingSection({ canManage }: { canManage: boolean }) {
  const { toast } = useToast();
  const [showAdd,      setShowAdd]      = useState(false);
  const [currentOnly,  setCurrentOnly]  = useState(false);
  const [filterType,   setFilterType]   = useState("");
  const [confirmDel,   setConfirmDel]   = useState<TrainingRecord | null>(null);

  const qk = ["/api/organization/compliance/hipaa/training", String(currentOnly), filterType];
  const { data, isLoading } = useQuery<PagedResult<TrainingRecord>>({
    queryKey: qk,
    queryFn: async () => {
      const params = new URLSearchParams({ page: "1", pageSize: "50" });
      if (currentOnly) params.set("currentOnly", "true");
      if (filterType)  params.set("trainingType", filterType);
      const res = await fetch(`/api/organization/compliance/hipaa/training?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("dotnetbot.jwt") ?? sessionStorage.getItem("dotnetbot.jwt.session") ?? ""}` },
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    staleTime: 0,
  });

  const remove = useMutation({
    mutationFn: (id: string) =>
      apiRequest("DELETE", `/api/organization/compliance/hipaa/training/${id}`),
    onSuccess: () => {
      toast({ title: "Training record removed" });
      setConfirmDel(null);
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/hipaa/training"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/hipaa/summary"] });
    },
    onError: (e: any) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="w-4 h-4 text-emerald-500" aria-hidden="true" />
                Workforce Training
              </CardTitle>
              <CardDescription>Track HIPAA training completions for each workforce member.</CardDescription>
            </div>
            {canManage && (
              <Button size="sm" variant="outline" className="h-7 text-xs shrink-0"
                onClick={() => setShowAdd(true)} data-testid="button-add-training">
                <Plus className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Log training
              </Button>
            )}
          </div>
          <div className="flex flex-wrap gap-2 pt-1">
            <select value={filterType} onChange={(e) => setFilterType(e.target.value)}
              className="text-xs border rounded px-2 py-1 bg-background">
              <option value="">All types</option>
              {TRAINING_TYPES.map((t) => (
                <option key={t} value={t}>{TRAINING_TYPE_LABELS[t] ?? t}</option>
              ))}
            </select>
            <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none">
              <input type="checkbox" checked={currentOnly}
                onChange={(e) => setCurrentOnly(e.target.checked)}
                className="h-3.5 w-3.5 rounded border" />
              Current only
            </label>
          </div>
        </CardHeader>

        <CardContent>
          {isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : !data || data.items.length === 0 ? (
            <EmptyState icon={Users} message="No training records."
              sub={canManage ? "Use 'Log training' to record a completed training." : undefined} />
          ) : (
            <div className="space-y-2" aria-label="Training records">
              {data.items.map((rec) => {
                const expired = isExpired(rec.expiresAt);
                return (
                  <div key={rec.id}
                    className={`border rounded-md p-3 flex items-start gap-3 ${expired ? "opacity-70" : ""}`}
                    data-testid={`training-row-${rec.id}`}>
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium">{rec.trainingTitle}</span>
                        <Badge variant="outline" className="text-[10px] h-4 px-1.5 border-muted text-muted-foreground">
                          {TRAINING_TYPE_LABELS[rec.trainingType] ?? rec.trainingType}
                        </Badge>
                        {rec.expiresAt && expired ? (
                          <Badge variant="outline" className="text-[10px] h-4 px-1.5 border-red-400/40 bg-red-500/8 text-red-600 dark:text-red-400">
                            <XCircle className="inline w-2.5 h-2.5 mr-0.5" aria-hidden="true" />Expired
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] h-4 px-1.5 border-emerald-500/40 bg-emerald-500/8 text-emerald-600 dark:text-emerald-400">
                            <CheckCircle2 className="inline w-2.5 h-2.5 mr-0.5" aria-hidden="true" />Current
                          </Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                        <span>User: <span className="font-mono">{rec.userId.slice(0,8)}…</span></span>
                        <span>Completed: {fmtDate(rec.completedAt)}</span>
                        {rec.expiresAt && <span>Expires: {fmtDate(rec.expiresAt)}</span>}
                        {rec.evidenceUrl && (
                          <a href={rec.evidenceUrl} target="_blank" rel="noopener noreferrer"
                            className="text-primary underline-offset-2 hover:underline">
                            Certificate ↗
                          </a>
                        )}
                      </div>
                    </div>
                    {canManage && (
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive shrink-0"
                        onClick={() => setConfirmDel(rec)}
                        aria-label={`Remove training record: ${rec.trainingTitle}`}>
                        <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                      </Button>
                    )}
                  </div>
                );
              })}
              {data.total > data.items.length && (
                <p className="text-xs text-muted-foreground text-center pt-1">
                  Showing {data.items.length} of {data.total} records.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {showAdd && <AddTrainingModal onClose={() => setShowAdd(false)} onAdded={() => setShowAdd(false)} />}

      {confirmDel && (
        <Dialog open onOpenChange={(o) => { if (!o) setConfirmDel(null); }}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Remove training record?</DialogTitle>
              <DialogDescription>
                Permanently removes the training record for <strong>{confirmDel.trainingTitle}</strong>. Cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmDel(null)}>Cancel</Button>
              <Button variant="destructive" onClick={() => remove.mutate(confirmDel.id)} disabled={remove.isPending}>
                {remove.isPending ? "Removing…" : "Remove"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 5 — PHI Access Report
// ═══════════════════════════════════════════════════════════════════════════════

function PhiAccessSection() {
  const [userId,     setUserId]     = useState("");
  const [action,     setAction]     = useState("");
  const [recordType, setRecordType] = useState("");
  const [from,       setFrom]       = useState("");
  const [to,         setTo]         = useState("");
  const [page,       setPage]       = useState(1);

  const qk = ["/api/organization/compliance/hipaa/phi-access-report", userId, action, recordType, from, to, String(page)];
  const { data, isLoading, refetch } = useQuery<PagedResult<PhiAccessItem>>({
    queryKey: qk,
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), pageSize: "50" });
      if (userId)     params.set("userId", userId.trim());
      if (action)     params.set("action", action.trim());
      if (recordType) params.set("recordType", recordType.trim());
      if (from) params.set("from", new Date(from).toISOString());
      if (to)   params.set("to",   new Date(to).toISOString());
      const res = await fetch(`/api/organization/compliance/hipaa/phi-access-report?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("dotnetbot.jwt") ?? sessionStorage.getItem("dotnetbot.jwt.session") ?? ""}` },
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    staleTime: 0,
  });

  function applyFilters() { setPage(1); refetch(); }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Eye className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
              PHI Access Report
            </CardTitle>
            <CardDescription>Read-only audit log of all PHI access events for your organization.</CardDescription>
          </div>
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => refetch()}
            aria-label="Refresh PHI access log">
            <RefreshCcw className="w-3.5 h-3.5" aria-hidden="true" />
          </Button>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-2">
          <div className="space-y-1">
            <Label className="text-[10px] text-muted-foreground">User ID</Label>
            <Input value={userId} onChange={(e) => setUserId(e.target.value)}
              placeholder="UUID…" className="text-xs h-7 font-mono" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] text-muted-foreground">Action</Label>
            <Input value={action} onChange={(e) => setAction(e.target.value)}
              placeholder="e.g. View, Export…" className="text-xs h-7" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] text-muted-foreground">Record type</Label>
            <Input value={recordType} onChange={(e) => setRecordType(e.target.value)}
              placeholder="e.g. Patient…" className="text-xs h-7" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] text-muted-foreground">From date</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
              className="text-xs h-7" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] text-muted-foreground">To date</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)}
              className="text-xs h-7" />
          </div>
          <div className="flex items-end">
            <Button size="sm" variant="outline" className="h-7 text-xs w-full"
              onClick={applyFilters}>
              Apply filters
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : !data || data.items.length === 0 ? (
          <EmptyState icon={FileText} message="No PHI access events match your filters."
            sub="Try widening the date range or clearing filters." />
        ) : (
          <>
            <div className="text-xs text-muted-foreground mb-2">
              {data.total.toLocaleString()} total event{data.total !== 1 ? "s" : ""}
              {data.total > data.items.length && `, showing ${data.items.length}`}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse" aria-label="PHI access log">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-1.5 pr-3 font-medium text-muted-foreground whitespace-nowrap">Timestamp</th>
                    <th className="text-left py-1.5 pr-3 font-medium text-muted-foreground">Action</th>
                    <th className="text-left py-1.5 pr-3 font-medium text-muted-foreground">Record type</th>
                    <th className="text-left py-1.5 pr-3 font-medium text-muted-foreground">Outcome</th>
                    <th className="text-left py-1.5 font-medium text-muted-foreground">User</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((ev) => (
                    <tr key={ev.id} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="py-1.5 pr-3 whitespace-nowrap text-muted-foreground">
                        {fmtDateTime(ev.timestamp)}
                      </td>
                      <td className="py-1.5 pr-3">{ev.action ?? "—"}</td>
                      <td className="py-1.5 pr-3">{ev.recordType ?? "—"}</td>
                      <td className="py-1.5 pr-3">
                        {ev.outcome ? (
                          <Badge variant="outline"
                            className={`text-[10px] h-4 px-1 ${
                              ev.outcome.toLowerCase().includes("fail") || ev.outcome.toLowerCase().includes("deny")
                                ? "border-red-400/40 text-red-600 dark:text-red-400"
                                : "border-emerald-400/40 text-emerald-600 dark:text-emerald-400"
                            }`}>
                            {ev.outcome}
                          </Badge>
                        ) : "—"}
                      </td>
                      <td className="py-1.5 font-mono text-muted-foreground">
                        {ev.userId ? ev.userId.slice(0, 8) + "…" : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            {data.total > data.pageSize && (
              <div className="flex items-center justify-between mt-3">
                <Button size="sm" variant="outline" className="h-7 text-xs"
                  disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>← Prev</Button>
                <span className="text-xs text-muted-foreground">
                  Page {data.page} of {Math.ceil(data.total / data.pageSize)}
                </span>
                <Button size="sm" variant="outline" className="h-7 text-xs"
                  disabled={page >= Math.ceil(data.total / data.pageSize)}
                  onClick={() => setPage((p) => p + 1)}>Next →</Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════════════════════
// HIPAA-06 — Security Awareness & Training
// ══════════════════════════════════════════════════════════════════════════════

type SecurityTrainingRecord = {
  id: string;
  organizationId: string;
  userId: string;
  workforceMemberName: string;
  workforceRole: string;
  trainingType: string;
  trainingTitle: string;
  description: string | null;
  trainingProvider: string | null;
  assignedDate: string;
  dueDate: string | null;
  completedDate: string | null;
  expirationDate: string | null;
  status: string;
  certificateReference: string | null;
  score: number | null;
  passingScore: number | null;
  policyAcknowledgement: boolean;
  acknowledgementDate: string | null;
  policyName: string | null;
  policyVersion: string | null;
  acknowledgementStatus: string | null;
  recurrenceFrequencyDays: number | null;
  notes: string | null;
  assignedBy: string | null;
  completedBy: string | null;
  requirementId: string | null;
  createdAt: string;
  updatedAt: string;
};

type TrainingRequirement = {
  id: string;
  organizationId: string;
  workforceRole: string;
  trainingType: string;
  trainingTitle: string;
  description: string | null;
  required: boolean;
  recurrenceFrequency: number | null;
  dueWithinDays: number;
  passingScore: number | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

type TrainingSummary = {
  total: number;
  completed: number;
  inProgress: number;
  dueSoon: number;
  overdue: number;
  expired: number;
  completionPercentage: number;
  byTrainingType: { trainingType: string; count: number; completedCount: number }[];
};

const SECURITY_TRAINING_TYPES = [
  { value: "HipaaSecurityAwareness",    label: "HIPAA Security Awareness" },
  { value: "PrivacySecurityPolicy",     label: "Privacy/Security Policy" },
  { value: "PhishingSocialEngineering", label: "Phishing/Social Engineering" },
  { value: "PasswordAuthSecurity",      label: "Password & Authentication Security" },
  { value: "EphiHandling",              label: "ePHI Handling" },
  { value: "IncidentBreachReporting",   label: "Incident/Breach Reporting" },
  { value: "RoleSpecificSecurity",      label: "Role-Specific Security Training" },
  { value: "PolicyAcknowledgement",     label: "Policy Acknowledgement" },
  { value: "Other",                     label: "Other" },
];

const TRAINING_STATUSES = ["Assigned", "InProgress", "Completed", "Overdue", "Expired"];

function trainingTypeLabel(t: string) {
  return SECURITY_TRAINING_TYPES.find(x => x.value === t)?.label ?? t;
}

function statusBadgeVariant(status: string): "default" | "secondary" | "destructive" | "outline" {
  if (status === "Completed")  return "default";
  if (status === "InProgress") return "secondary";
  if (status === "Overdue" || status === "Expired") return "destructive";
  return "outline";
}

// ─────────────────────────────────────────────────────────────────────────────
// HIPAA-07 — Security Incident Procedures
// ─────────────────────────────────────────────────────────────────────────────

interface SiSummary {
  total: number; open: number; critical: number; high: number; medium: number; low: number;
  ephiRelated: number; escalated: number; overdue: number; resolved: number; closed: number;
  avgContainmentHours: number | null; avgResolutionHours: number | null;
}

interface SiIncidentRow {
  id: string; incidentNumber: string; title: string; incidentType: string;
  severity: string; status: string; ephiInvolved: boolean; detectedAt: string;
  incidentOwner: string | null; escalationRequired: boolean; escalatedAt: string | null;
  assignedToUserId: string | null; createdAt: string; updatedAt: string;
}

interface SiIncidentDetail extends SiIncidentRow {
  description: string; reportedAt: string | null; acknowledgedAt: string | null;
  containmentStartedAt: string | null; containedAt: string | null;
  resolvedAt: string | null; closedAt: string | null;
  affectedSystem: string | null; affectedDataType: string | null;
  individualsAffected: number | null; initialImpactAssessment: string | null;
  rootCause: string | null; containmentActions: string | null;
  mitigationActions: string | null; correctiveActions: string | null;
  evidencePreservationNotes: string | null; evidenceReference: string | null;
  escalatedTo: string | null; escalationReason: string | null;
  postIncidentReviewRequired: boolean; postIncidentReviewDate: string | null;
  postIncidentReviewNotes: string | null; lessonsLearned: string | null;
  createdBy: string | null; updatedBy: string | null;
}

interface SiEvidence {
  id: string; description: string; evidenceReference: string;
  evidenceStatus: string; collectedAt: string;
}

interface SiHistory {
  id: string; actorUserId: string | null; action: string;
  previousStatus: string | null; newStatus: string | null;
  timestamp: string; comment: string | null;
}

const SI_TYPES = [
  "UnauthorizedAccess","UnauthorizedDisclosure","LostStolenDevice","Malware","Phishing",
  "CredentialCompromise","DataExposure","Ransomware","SystemIntrusion",
  "InsiderIncident","PrivacySecurityViolation","Other",
];
const SI_SEVERITIES = ["Low","Medium","High","Critical"];
const SI_STATUSES   = [
  "Reported","Triaged","Investigating","Contained",
  "Mitigated","Resolved","Closed","Rejected","Cancelled",
];
const SI_TERMINAL = new Set(["Rejected","Cancelled","Closed"]);

function siSeverityBadge(s: string) {
  if (s === "Critical") return "destructive";
  if (s === "High")     return "destructive";
  if (s === "Medium")   return "secondary";
  return "outline";
}
function siStatusBadge(s: string) {
  if (s === "Closed")    return "secondary";
  if (s === "Resolved")  return "secondary";
  if (s === "Rejected" || s === "Cancelled") return "outline";
  return "default";
}

function HipaaSecurityIncidentSection({ canManage }: { canManage: boolean }) {
  const [view, setView]             = useState<"list" | "detail" | "create">("list");
  const [detailId, setDetailId]     = useState<string | null>(null);
  const [filterStatus, setFilterStatus]   = useState("");
  const [filterSeverity, setFilterSeverity] = useState("");
  const [filterEphi, setFilterEphi]       = useState("");
  const [page, setPage]                   = useState(1);
  const [creating, setCreating]           = useState(false);
  const [statusModal, setStatusModal]     = useState<{ id: string; current: string } | null>(null);
  const [escalateModal, setEscalateModal] = useState<string | null>(null);
  const [containModal, setContainModal]   = useState<string | null>(null);
  const [mitigateModal, setMitigateModal] = useState<string | null>(null);
  const [resolveModal, setResolveModal]   = useState<string | null>(null);
  const [closeModal, setCloseModal]       = useState<string | null>(null);
  const [evidenceModal, setEvidenceModal] = useState<string | null>(null);
  const { toast } = useToast();

  const qc = useQueryClient();
  const BASE = "/api/organization/compliance/hipaa/security-incidents";

  const authFetch = (url: string, opts: RequestInit = {}) => {
    const token = sessionStorage.getItem("dotnetbot.jwt.session") || localStorage.getItem("dotnetbot.jwt") || "";
    return fetch(url, { ...opts, headers: { ...(opts.headers as Record<string,string> || {}), "Content-Type": "application/json", Authorization: `Bearer ${token}` } });
  };

  const summaryQ = useQuery<SiSummary>({
    queryKey: ["si-summary"],
    queryFn: async () => { const r = await authFetch(`${BASE}/summary`); if (!r.ok) throw new Error(); return r.json(); },
    staleTime: 0,
  });

  const listParams = new URLSearchParams();
  if (filterStatus)   listParams.set("status",   filterStatus);
  if (filterSeverity) listParams.set("severity", filterSeverity);
  if (filterEphi)     listParams.set("ephiInvolved", filterEphi);
  listParams.set("page", String(page)); listParams.set("pageSize", "20");

  const listQ = useQuery<{ total: number; page: number; pageSize: number; items: SiIncidentRow[] }>({
    queryKey: ["si-list", filterStatus, filterSeverity, filterEphi, page],
    queryFn: async () => { const r = await authFetch(`${BASE}?${listParams}`); if (!r.ok) throw new Error(); return r.json(); },
    staleTime: 0,
  });

  const detailQ = useQuery<{ incident: SiIncidentDetail; evidence: SiEvidence[] }>({
    queryKey: ["si-detail", detailId],
    queryFn: async () => { const r = await authFetch(`${BASE}/${detailId}`); if (!r.ok) throw new Error(); return r.json(); },
    enabled: !!detailId,
    staleTime: 0,
  });

  const historyQ = useQuery<{ incidentId: string; history: SiHistory[] }>({
    queryKey: ["si-history", detailId],
    queryFn: async () => { const r = await authFetch(`${BASE}/${detailId}/history`); if (!r.ok) throw new Error(); return r.json(); },
    enabled: !!detailId,
    staleTime: 0,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["si-summary"] });
    qc.invalidateQueries({ queryKey: ["si-list"] });
    if (detailId) {
      qc.invalidateQueries({ queryKey: ["si-detail", detailId] });
      qc.invalidateQueries({ queryKey: ["si-history", detailId] });
    }
  };

  const post = async (url: string, body: object) => {
    const r = await authFetch(url, { method: "POST", body: JSON.stringify(body) });
    if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error((e as any).message || "Request failed"); }
    return r.json();
  };
  const patch = async (url: string, body: object) => {
    const r = await authFetch(url, { method: "PATCH", body: JSON.stringify(body) });
    if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error((e as any).message || "Request failed"); }
    return r.json();
  };
  const put = async (url: string, body: object) => {
    const r = await authFetch(url, { method: "PUT", body: JSON.stringify(body) });
    if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error((e as any).message || "Request failed"); }
    return r.json();
  };

  // ── Create form ─────────────────────────────────────────────────────────────
  const CreateForm = () => {
    const [f, setF] = useState({
      title: "", description: "", incidentType: "UnauthorizedAccess",
      severity: "Medium", detectedAt: new Date().toISOString().slice(0,16),
      incidentOwner: "", affectedSystem: "", affectedDataType: "",
      ephiInvolved: false, individualsAffected: "",
      initialImpactAssessment: "",
    });
    const [busy, setBusy] = useState(false);
    const [errors, setErrors] = useState<Record<string,string>>({});

    const submit = async () => {
      const errs: Record<string,string> = {};
      required(errs, "title",       f.title,       "Title");
      required(errs, "description", f.description, "Description");
      if (Object.keys(errs).length) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; }
      setErrors({});
      setBusy(true);
      try {
        await post(BASE, {
          ...f,
          detectedAt: new Date(f.detectedAt).toISOString(),
          individualsAffected: f.individualsAffected ? Number(f.individualsAffected) : null,
          incidentOwner: f.incidentOwner || null,
          affectedSystem: f.affectedSystem || null,
          affectedDataType: f.affectedDataType || null,
          initialImpactAssessment: f.initialImpactAssessment || null,
        });
        toast({ title: "Incident reported successfully." });
        setView("list"); invalidate();
      } catch (e: any) {
        toast({ title: e.message, variant: "destructive" });
      } finally { setBusy(false); }
    };

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setView("list")} className="mr-1">←</Button>
            Report Security Incident
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1">Title *</label>
              <Input placeholder="Brief incident title" className={fieldCls("", errors, "title")} aria-invalid={!!errors.title} value={f.title} onChange={e => { setF(p => ({ ...p, title: e.target.value })); clearErr(setErrors, "title"); }} />
              {fErr(errors, "title")}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Incident Type *</label>
              <select className="w-full border rounded-md px-3 py-2 text-sm" value={f.incidentType} onChange={e => setF(p => ({ ...p, incidentType: e.target.value }))}>
                {SI_TYPES.map(t => <option key={t} value={t}>{t.replace(/([A-Z])/g,' $1').trim()}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Severity *</label>
              <select className="w-full border rounded-md px-3 py-2 text-sm" value={f.severity} onChange={e => setF(p => ({ ...p, severity: e.target.value }))}>
                {SI_SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Detected At *</label>
              <Input type="datetime-local" value={f.detectedAt} onChange={e => setF(p => ({ ...p, detectedAt: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Incident Owner</label>
              <Input placeholder="Name or team" value={f.incidentOwner} onChange={e => setF(p => ({ ...p, incidentOwner: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Affected System</label>
              <Input placeholder="e.g. EHR system" value={f.affectedSystem} onChange={e => setF(p => ({ ...p, affectedSystem: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Affected Data Type</label>
              <Input placeholder="e.g. Patient records" value={f.affectedDataType} onChange={e => setF(p => ({ ...p, affectedDataType: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Individuals Affected</label>
              <Input type="number" placeholder="0" value={f.individualsAffected} onChange={e => setF(p => ({ ...p, individualsAffected: e.target.value }))} />
            </div>
            <div className="flex items-center gap-2 pt-5">
              <input type="checkbox" id="ephi-create" checked={f.ephiInvolved} onChange={e => setF(p => ({ ...p, ephiInvolved: e.target.checked }))} />
              <label htmlFor="ephi-create" className="text-sm">ePHI Involved</label>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1">Description *</label>
              <textarea className={fieldCls("w-full border rounded-md px-3 py-2 text-sm h-24 resize-y", errors, "description")} aria-invalid={!!errors.description} placeholder="Describe what happened..." value={f.description} onChange={e => { setF(p => ({ ...p, description: e.target.value })); clearErr(setErrors, "description"); }} />
              {fErr(errors, "description")}
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1">Initial Impact Assessment</label>
              <textarea className="w-full border rounded-md px-3 py-2 text-sm h-20 resize-y" placeholder="Preliminary assessment of impact..." value={f.initialImpactAssessment} onChange={e => setF(p => ({ ...p, initialImpactAssessment: e.target.value }))} />
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button onClick={submit} disabled={busy}>{busy ? "Submitting…" : "Report Incident"}</Button>
            <Button variant="outline" onClick={() => setView("list")}>Cancel</Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  // ── Status transition modal ──────────────────────────────────────────────────
  const StatusModal = ({ id, current }: { id: string; current: string }) => {
    const TRANSITIONS: Record<string, string[]> = {
      "Reported":     ["Triaged","Rejected","Cancelled"],
      "Triaged":      ["Investigating","Rejected"],
      "Investigating": ["Contained","Mitigated","Resolved"],
      "Contained":    ["Mitigated","Resolved"],
      "Mitigated":    ["Resolved"],
      "Resolved":     ["Closed"],
    };
    const allowed = TRANSITIONS[current] || [];
    const [status,  setStatus]  = useState(allowed[0] || "");
    const [comment, setComment] = useState("");
    const [busy,    setBusy]    = useState(false);

    const submit = async () => {
      if (!status) return;
      setBusy(true);
      try {
        await patch(`${BASE}/${id}/status`, { status, comment: comment || null });
        toast({ title: `Status changed to ${status}.` });
        setStatusModal(null); invalidate();
      } catch (e: any) {
        toast({ title: e.message, variant: "destructive" });
      } finally { setBusy(false); }
    };

    return (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
        <Card className="w-full max-w-md">
          <CardHeader><CardTitle>Change Status</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">Current: <Badge>{current}</Badge></p>
            {allowed.length === 0 ? <p className="text-sm">No transitions available from this status.</p> : (
              <>
                <div>
                  <label className="text-sm font-medium">New Status</label>
                  <select className="w-full border rounded-md px-3 py-2 text-sm mt-1" value={status} onChange={e => setStatus(e.target.value)}>
                    {allowed.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium">Comment</label>
                  <textarea className="w-full border rounded-md px-3 py-2 text-sm h-20 mt-1" value={comment} onChange={e => setComment(e.target.value)} />
                </div>
              </>
            )}
            <div className="flex gap-2">
              {allowed.length > 0 && <Button onClick={submit} disabled={busy}>{busy ? "Saving…" : "Update"}</Button>}
              <Button variant="outline" onClick={() => setStatusModal(null)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // ── Escalation modal ─────────────────────────────────────────────────────────
  const EscalateModal = ({ id }: { id: string }) => {
    const [f, setF] = useState({ escalatedTo: "", escalationReason: "", comment: "" });
    const [busy, setBusy] = useState(false);
    const [errors, setErrors] = useState<Record<string,string>>({});
    const submit = async () => {
      const errs: Record<string,string> = {};
      required(errs, "escalatedTo",      f.escalatedTo,      "Escalate To");
      required(errs, "escalationReason", f.escalationReason, "Reason");
      if (Object.keys(errs).length) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; }
      setErrors({});
      setBusy(true);
      try {
        await patch(`${BASE}/${id}/escalate`, f);
        toast({ title: "Incident escalated." }); setEscalateModal(null); invalidate();
      } catch (e: any) { toast({ title: e.message, variant: "destructive" }); }
      finally { setBusy(false); }
    };
    return (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
        <Card className="w-full max-w-md">
          <CardHeader><CardTitle>Escalate Incident</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div>
              <label className="text-sm font-medium">Escalate To *</label>
              <Input className={fieldCls("mt-1", errors, "escalatedTo")} aria-invalid={!!errors.escalatedTo} placeholder="CISO, Security Team…" value={f.escalatedTo} onChange={e => { setF(p => ({...p, escalatedTo: e.target.value})); clearErr(setErrors, "escalatedTo"); }} />
              {fErr(errors, "escalatedTo")}
            </div>
            <div>
              <label className="text-sm font-medium">Reason *</label>
              <textarea className={fieldCls("w-full border rounded-md px-3 py-2 text-sm h-20 mt-1", errors, "escalationReason")} aria-invalid={!!errors.escalationReason} value={f.escalationReason} onChange={e => { setF(p => ({...p, escalationReason: e.target.value})); clearErr(setErrors, "escalationReason"); }} />
              {fErr(errors, "escalationReason")}
            </div>
            <div>
              <label className="text-sm font-medium">Comment</label>
              <textarea className="w-full border rounded-md px-3 py-2 text-sm h-16 mt-1" value={f.comment} onChange={e => setF(p => ({...p, comment: e.target.value}))} />
            </div>
            <div className="flex gap-2">
              <Button onClick={submit} disabled={busy}>{busy ? "Saving…" : "Escalate"}</Button>
              <Button variant="outline" onClick={() => setEscalateModal(null)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // ── Contain modal ─────────────────────────────────────────────────────────────
  const ContainModal = ({ id }: { id: string }) => {
    const [f, setF] = useState({ containmentActions: "", comment: "" });
    const [busy, setBusy] = useState(false);
    const [errors, setErrors] = useState<Record<string,string>>({});
    const submit = async () => {
      const errs: Record<string,string> = {};
      required(errs, "containmentActions", f.containmentActions, "Containment Actions");
      if (Object.keys(errs).length) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; }
      setErrors({});
      setBusy(true);
      try {
        await patch(`${BASE}/${id}/contain`, f);
        toast({ title: "Incident contained." }); setContainModal(null); invalidate();
      } catch (e: any) { toast({ title: e.message, variant: "destructive" }); }
      finally { setBusy(false); }
    };
    return (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
        <Card className="w-full max-w-md">
          <CardHeader><CardTitle>Record Containment</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div>
              <label className="text-sm font-medium">Containment Actions *</label>
              <textarea className={fieldCls("w-full border rounded-md px-3 py-2 text-sm h-24 mt-1", errors, "containmentActions")} aria-invalid={!!errors.containmentActions} placeholder="Steps taken to contain…" value={f.containmentActions} onChange={e => { setF(p => ({...p, containmentActions: e.target.value})); clearErr(setErrors, "containmentActions"); }} />
              {fErr(errors, "containmentActions")}
            </div>
            <div>
              <label className="text-sm font-medium">Comment</label>
              <Input className="mt-1" value={f.comment} onChange={e => setF(p => ({...p, comment: e.target.value}))} />
            </div>
            <div className="flex gap-2">
              <Button onClick={submit} disabled={busy}>{busy ? "Saving…" : "Mark Contained"}</Button>
              <Button variant="outline" onClick={() => setContainModal(null)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // ── Mitigate modal ────────────────────────────────────────────────────────────
  const MitigateModal = ({ id }: { id: string }) => {
    const [f, setF] = useState({ mitigationActions: "", rootCause: "", correctiveActions: "", comment: "" });
    const [busy, setBusy] = useState(false);
    const [errors, setErrors] = useState<Record<string,string>>({});
    const submit = async () => {
      const errs: Record<string,string> = {};
      required(errs, "mitigationActions", f.mitigationActions, "Mitigation Actions");
      if (Object.keys(errs).length) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; }
      setErrors({});
      setBusy(true);
      try {
        await patch(`${BASE}/${id}/mitigate`, { ...f, rootCause: f.rootCause || null, correctiveActions: f.correctiveActions || null, comment: f.comment || null });
        toast({ title: "Incident mitigated." }); setMitigateModal(null); invalidate();
      } catch (e: any) { toast({ title: e.message, variant: "destructive" }); }
      finally { setBusy(false); }
    };
    return (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
        <Card className="w-full max-w-md">
          <CardHeader><CardTitle>Record Mitigation</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div>
              <label className="text-sm font-medium">Mitigation Actions *</label>
              <textarea className={fieldCls("w-full border rounded-md px-3 py-2 text-sm h-20 mt-1", errors, "mitigationActions")} aria-invalid={!!errors.mitigationActions} value={f.mitigationActions} onChange={e => { setF(p => ({...p, mitigationActions: e.target.value})); clearErr(setErrors, "mitigationActions"); }} />
              {fErr(errors, "mitigationActions")}
            </div>
            <div>
              <label className="text-sm font-medium">Root Cause</label>
              <textarea className="w-full border rounded-md px-3 py-2 text-sm h-16 mt-1" value={f.rootCause} onChange={e => setF(p => ({...p, rootCause: e.target.value}))} />
            </div>
            <div>
              <label className="text-sm font-medium">Corrective Actions</label>
              <textarea className="w-full border rounded-md px-3 py-2 text-sm h-16 mt-1" value={f.correctiveActions} onChange={e => setF(p => ({...p, correctiveActions: e.target.value}))} />
            </div>
            <div className="flex gap-2">
              <Button onClick={submit} disabled={busy}>{busy ? "Saving…" : "Mark Mitigated"}</Button>
              <Button variant="outline" onClick={() => setMitigateModal(null)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // ── Resolve modal ─────────────────────────────────────────────────────────────
  const ResolveModal = ({ id }: { id: string }) => {
    const [f, setF] = useState({ rootCause: "", mitigationActions: "", lessonsLearned: "", comment: "" });
    const [busy, setBusy] = useState(false);
    const [errors, setErrors] = useState<Record<string,string>>({});
    const submit = async () => {
      const errs: Record<string,string> = {};
      required(errs, "rootCause",         f.rootCause,         "Root Cause");
      required(errs, "mitigationActions", f.mitigationActions, "Mitigation Actions");
      if (Object.keys(errs).length) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; }
      setErrors({});
      setBusy(true);
      try {
        await patch(`${BASE}/${id}/resolve`, { ...f, lessonsLearned: f.lessonsLearned || null, comment: f.comment || null });
        toast({ title: "Incident resolved." }); setResolveModal(null); invalidate();
      } catch (e: any) { toast({ title: e.message, variant: "destructive" }); }
      finally { setBusy(false); }
    };
    return (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
        <Card className="w-full max-w-md">
          <CardHeader><CardTitle>Resolve Incident</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div>
              <label className="text-sm font-medium">Root Cause *</label>
              <textarea className={fieldCls("w-full border rounded-md px-3 py-2 text-sm h-20 mt-1", errors, "rootCause")} aria-invalid={!!errors.rootCause} value={f.rootCause} onChange={e => { setF(p => ({...p, rootCause: e.target.value})); clearErr(setErrors, "rootCause"); }} />
              {fErr(errors, "rootCause")}
            </div>
            <div>
              <label className="text-sm font-medium">Mitigation Actions *</label>
              <textarea className={fieldCls("w-full border rounded-md px-3 py-2 text-sm h-20 mt-1", errors, "mitigationActions")} aria-invalid={!!errors.mitigationActions} value={f.mitigationActions} onChange={e => { setF(p => ({...p, mitigationActions: e.target.value})); clearErr(setErrors, "mitigationActions"); }} />
              {fErr(errors, "mitigationActions")}
            </div>
            <div>
              <label className="text-sm font-medium">Lessons Learned</label>
              <textarea className="w-full border rounded-md px-3 py-2 text-sm h-16 mt-1" value={f.lessonsLearned} onChange={e => setF(p => ({...p, lessonsLearned: e.target.value}))} />
            </div>
            <div className="flex gap-2">
              <Button onClick={submit} disabled={busy}>{busy ? "Saving…" : "Mark Resolved"}</Button>
              <Button variant="outline" onClick={() => setResolveModal(null)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // ── Close modal ───────────────────────────────────────────────────────────────
  const CloseModal = ({ id }: { id: string }) => {
    const inc = detailQ.data?.incident;
    const [f, setF] = useState({
      postIncidentReviewNotes: "", postIncidentReviewDate: new Date().toISOString().slice(0,10),
      lessonsLearned: "", comment: "",
    });
    const [busy, setBusy] = useState(false);
    const submit = async () => {
      setBusy(true);
      try {
        await patch(`${BASE}/${id}/close`, {
          postIncidentReviewNotes: f.postIncidentReviewNotes || null,
          postIncidentReviewDate: f.postIncidentReviewDate ? new Date(f.postIncidentReviewDate).toISOString() : null,
          lessonsLearned: f.lessonsLearned || null,
          comment: f.comment || null,
        });
        toast({ title: "Incident closed." }); setCloseModal(null); invalidate();
      } catch (e: any) { toast({ title: e.message, variant: "destructive" }); }
      finally { setBusy(false); }
    };
    return (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
        <Card className="w-full max-w-md">
          <CardHeader><CardTitle>Close Incident</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {inc && ["High","Critical"].includes(inc.severity) && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                {inc.severity} incidents require: escalation + post-incident review date.
                {inc.severity === "Critical" && " Critical also requires root cause and mitigation actions."}
              </p>
            )}
            <div>
              <label className="text-sm font-medium">Post-Incident Review Date</label>
              <Input type="date" className="mt-1" value={f.postIncidentReviewDate} onChange={e => setF(p => ({...p, postIncidentReviewDate: e.target.value}))} />
            </div>
            <div>
              <label className="text-sm font-medium">Post-Incident Review Notes</label>
              <textarea className="w-full border rounded-md px-3 py-2 text-sm h-20 mt-1" value={f.postIncidentReviewNotes} onChange={e => setF(p => ({...p, postIncidentReviewNotes: e.target.value}))} />
            </div>
            <div>
              <label className="text-sm font-medium">Lessons Learned</label>
              <textarea className="w-full border rounded-md px-3 py-2 text-sm h-16 mt-1" value={f.lessonsLearned} onChange={e => setF(p => ({...p, lessonsLearned: e.target.value}))} />
            </div>
            <div className="flex gap-2">
              <Button onClick={submit} disabled={busy}>{busy ? "Saving…" : "Close Incident"}</Button>
              <Button variant="outline" onClick={() => setCloseModal(null)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // ── Evidence modal ────────────────────────────────────────────────────────────
  const EvidenceModal = ({ id }: { id: string }) => {
    const [f, setF] = useState({ description: "", evidenceReference: "", preservationNotes: "", evidenceStatus: "Preserved" });
    const [busy, setBusy] = useState(false);
    const [errors, setErrors] = useState<Record<string,string>>({});
    const submit = async () => {
      const errs: Record<string,string> = {};
      required(errs, "description",       f.description,       "Description");
      required(errs, "evidenceReference", f.evidenceReference, "Evidence Reference");
      if (Object.keys(errs).length) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; }
      setErrors({});
      setBusy(true);
      try {
        await post(`${BASE}/${id}/evidence`, { ...f, preservationNotes: f.preservationNotes || null });
        toast({ title: "Evidence added." }); setEvidenceModal(null); invalidate();
      } catch (e: any) { toast({ title: e.message, variant: "destructive" }); }
      finally { setBusy(false); }
    };
    return (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
        <Card className="w-full max-w-md">
          <CardHeader><CardTitle>Add Evidence</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div>
              <label className="text-sm font-medium">Description *</label>
              <Input className={fieldCls("mt-1", errors, "description")} aria-invalid={!!errors.description} placeholder="e.g. Server access logs" value={f.description} onChange={e => { setF(p => ({...p, description: e.target.value})); clearErr(setErrors, "description"); }} />
              {fErr(errors, "description")}
            </div>
            <div>
              <label className="text-sm font-medium">Evidence Reference *</label>
              <Input className={fieldCls("mt-1", errors, "evidenceReference")} aria-invalid={!!errors.evidenceReference} placeholder="e.g. SIEM-2026-0042 or https://…" value={f.evidenceReference} onChange={e => { setF(p => ({...p, evidenceReference: e.target.value})); clearErr(setErrors, "evidenceReference"); }} />
              {fErr(errors, "evidenceReference")}
            </div>
            <div>
              <label className="text-sm font-medium">Preservation Notes</label>
              <textarea className="w-full border rounded-md px-3 py-2 text-sm h-16 mt-1" value={f.preservationNotes} onChange={e => setF(p => ({...p, preservationNotes: e.target.value}))} />
            </div>
            <div>
              <label className="text-sm font-medium">Status</label>
              <select className="w-full border rounded-md px-3 py-2 text-sm mt-1" value={f.evidenceStatus} onChange={e => setF(p => ({...p, evidenceStatus: e.target.value}))}>
                {["Preserved","Reviewed","Archived"].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="flex gap-2">
              <Button onClick={submit} disabled={busy}>{busy ? "Saving…" : "Add Evidence"}</Button>
              <Button variant="outline" onClick={() => setEvidenceModal(null)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // ── Detail view ───────────────────────────────────────────────────────────────
  const DetailView = () => {
    const inc = detailQ.data?.incident;
    const evidence = detailQ.data?.evidence || [];
    const history  = historyQ.data?.history || [];

    if (!inc) return <Skeleton className="h-40 w-full" />;

    const isTerminal = SI_TERMINAL.has(inc.status);

    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="ghost" size="sm" onClick={() => { setView("list"); setDetailId(null); }}>← List</Button>
          <h3 className="font-semibold text-base">{inc.incidentNumber}: {inc.title}</h3>
          <Badge variant={siSeverityBadge(inc.severity)}>{inc.severity}</Badge>
          <Badge variant={siStatusBadge(inc.status)}>{inc.status}</Badge>
          {inc.ephiInvolved && <Badge variant="destructive">ePHI</Badge>}
          {inc.escalationRequired && <Badge variant="secondary">Escalated</Badge>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          <div><span className="text-muted-foreground">Type: </span>{inc.incidentType.replace(/([A-Z])/g,' $1').trim()}</div>
          <div><span className="text-muted-foreground">Owner: </span>{inc.incidentOwner || "—"}</div>
          <div><span className="text-muted-foreground">Detected: </span>{new Date(inc.detectedAt).toLocaleString()}</div>
          {inc.affectedSystem    && <div><span className="text-muted-foreground">System: </span>{inc.affectedSystem}</div>}
          {inc.affectedDataType  && <div><span className="text-muted-foreground">Data Type: </span>{inc.affectedDataType}</div>}
          {inc.individualsAffected != null && <div><span className="text-muted-foreground">Individuals: </span>{inc.individualsAffected}</div>}
          {inc.containedAt  && <div><span className="text-muted-foreground">Contained: </span>{new Date(inc.containedAt).toLocaleString()}</div>}
          {inc.resolvedAt   && <div><span className="text-muted-foreground">Resolved: </span>{new Date(inc.resolvedAt).toLocaleString()}</div>}
          {inc.closedAt     && <div><span className="text-muted-foreground">Closed: </span>{new Date(inc.closedAt).toLocaleString()}</div>}
          {inc.escalatedTo  && <div><span className="text-muted-foreground">Escalated To: </span>{inc.escalatedTo}</div>}
          {inc.postIncidentReviewDate && <div><span className="text-muted-foreground">PIR Date: </span>{new Date(inc.postIncidentReviewDate).toLocaleDateString()}</div>}
        </div>

        <div>
          <p className="text-sm font-medium mb-1">Description</p>
          <p className="text-sm text-muted-foreground whitespace-pre-wrap border rounded-md p-3">{inc.description}</p>
        </div>

        {inc.rootCause && (
          <div>
            <p className="text-sm font-medium mb-1">Root Cause</p>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap border rounded-md p-3">{inc.rootCause}</p>
          </div>
        )}
        {inc.containmentActions && (
          <div>
            <p className="text-sm font-medium mb-1">Containment Actions</p>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap border rounded-md p-3">{inc.containmentActions}</p>
          </div>
        )}
        {inc.mitigationActions && (
          <div>
            <p className="text-sm font-medium mb-1">Mitigation Actions</p>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap border rounded-md p-3">{inc.mitigationActions}</p>
          </div>
        )}
        {inc.lessonsLearned && (
          <div>
            <p className="text-sm font-medium mb-1">Lessons Learned</p>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap border rounded-md p-3">{inc.lessonsLearned}</p>
          </div>
        )}

        {canManage && !isTerminal && (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2" onClick={() => setStatusModal({ id: inc.id, current: inc.status })}>Change Status</Button>
            <Button size="sm" variant="outline" onClick={() => setEscalateModal(inc.id)}>Escalate</Button>
            {["Investigating","Triaged"].includes(inc.status) && (
              <Button size="sm" variant="outline" onClick={() => setContainModal(inc.id)}>Mark Contained</Button>
            )}
            {["Contained","Investigating"].includes(inc.status) && (
              <Button size="sm" variant="outline" onClick={() => setMitigateModal(inc.id)}>Mitigate</Button>
            )}
            {["Mitigated","Contained"].includes(inc.status) && (
              <Button size="sm" variant="outline" onClick={() => setResolveModal(inc.id)}>Resolve</Button>
            )}
            {inc.status === "Resolved" && (
              <Button size="sm" variant="outline" onClick={() => setCloseModal(inc.id)}>Close</Button>
            )}
            <Button size="sm" variant="outline" onClick={() => setEvidenceModal(inc.id)}>Add Evidence</Button>
          </div>
        )}

        {/* Evidence */}
        <div>
          <p className="text-sm font-semibold mb-2">Evidence ({evidence.length})</p>
          {evidence.length === 0 ? (
            <p className="text-sm text-muted-foreground">No evidence recorded.</p>
          ) : (
            <div className="space-y-2">
              {evidence.map(ev => (
                <div key={ev.id} className="border rounded-md p-3 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{ev.description}</span>
                    <Badge variant="outline">{ev.evidenceStatus}</Badge>
                  </div>
                  <div className="text-muted-foreground text-xs mt-1">
                    Ref: {ev.evidenceReference} · Collected: {new Date(ev.collectedAt).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Activity history */}
        <div>
          <p className="text-sm font-semibold mb-2">Activity History ({history.length})</p>
          {history.length === 0 ? (
            <p className="text-sm text-muted-foreground">No history.</p>
          ) : (
            <div className="space-y-1.5">
              {history.map(h => (
                <div key={h.id} className="flex items-start gap-2 text-xs border-l-2 border-muted pl-3">
                  <span className="text-muted-foreground min-w-[120px]">{new Date(h.timestamp).toLocaleString()}</span>
                  <span className="font-medium">{h.action}</span>
                  {h.previousStatus && h.newStatus && (
                    <span className="text-muted-foreground">{h.previousStatus} → {h.newStatus}</span>
                  )}
                  {h.comment && <span className="text-muted-foreground italic">"{h.comment}"</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── Summary cards ─────────────────────────────────────────────────────────────
  const summary = summaryQ.data;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Security Incident Procedures <span className="text-xs font-normal text-muted-foreground ml-2">§ 164.308(a)(6)</span></span>
          {canManage && view === "list" && (
            <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2" onClick={() => setView("create")}><AlertTriangle className="h-4 w-4" /> Report Incident</Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Summary metrics */}
        {summary && view === "list" && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: "Total", value: summary.total, color: "" },
              { label: "Open", value: summary.open, color: summary.open > 0 ? "text-amber-600" : "" },
              { label: "Critical", value: summary.critical, color: summary.critical > 0 ? "text-red-600 font-bold" : "" },
              { label: "ePHI Related", value: summary.ephiRelated, color: summary.ephiRelated > 0 ? "text-red-600" : "" },
              { label: "Overdue", value: summary.overdue, color: summary.overdue > 0 ? "text-red-600 font-bold" : "" },
              { label: "Escalated", value: summary.escalated, color: "" },
              { label: "Avg Containment", value: summary.avgContainmentHours != null ? `${summary.avgContainmentHours}h` : "—", color: "" },
              { label: "Avg Resolution", value: summary.avgResolutionHours  != null ? `${summary.avgResolutionHours}h`  : "—", color: "" },
            ].map(m => (
              <div key={m.label} className="border rounded-md p-3 text-center">
                <div className={`text-2xl font-bold ${m.color}`}>{m.value}</div>
                <div className="text-xs text-muted-foreground mt-1">{m.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Views */}
        {view === "create" && <CreateForm />}

        {view === "detail" && <DetailView />}

        {view === "list" && (
          <>
            {/* Filters */}
            <div className="flex flex-wrap gap-2">
              <select className="border rounded-md px-2 py-1 text-sm" value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }}>
                <option value="">All Statuses</option>
                {SI_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <select className="border rounded-md px-2 py-1 text-sm" value={filterSeverity} onChange={e => { setFilterSeverity(e.target.value); setPage(1); }}>
                <option value="">All Severities</option>
                {SI_SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <select className="border rounded-md px-2 py-1 text-sm" value={filterEphi} onChange={e => { setFilterEphi(e.target.value); setPage(1); }}>
                <option value="">All (ePHI)</option>
                <option value="true">ePHI Involved</option>
                <option value="false">No ePHI</option>
              </select>
              {(filterStatus || filterSeverity || filterEphi) && (
                <Button size="sm" variant="ghost" onClick={() => { setFilterStatus(""); setFilterSeverity(""); setFilterEphi(""); setPage(1); }}>Clear</Button>
              )}
            </div>

            {/* Incident list */}
            {listQ.isLoading ? <Skeleton className="h-32 w-full" /> : !listQ.data?.items?.length ? (
              <p className="text-sm text-muted-foreground text-center py-6">No incidents found.</p>
            ) : (
              <div className="space-y-2">
                {listQ.data.items.map(inc => (
                  <div key={inc.id} className="border rounded-md p-3 text-sm cursor-pointer hover:bg-muted/40 transition"
                       onClick={() => { setDetailId(inc.id); setView("detail"); }}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs text-muted-foreground">{inc.incidentNumber}</span>
                      <span className="font-medium">{inc.title}</span>
                      <Badge variant={siSeverityBadge(inc.severity)}>{inc.severity}</Badge>
                      <Badge variant={siStatusBadge(inc.status)}>{inc.status}</Badge>
                      {inc.ephiInvolved && <Badge variant="destructive">ePHI</Badge>}
                      {inc.escalationRequired && <Badge variant="secondary">↑ Escalated</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {inc.incidentType.replace(/([A-Z])/g,' $1').trim()} · Detected: {new Date(inc.detectedAt).toLocaleDateString()}
                      {inc.incidentOwner && ` · Owner: ${inc.incidentOwner}`}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {(listQ.data?.total || 0) > 20 && (
              <div className="flex justify-center gap-2">
                <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
                <span className="text-sm self-center">Page {page}</span>
                <Button size="sm" variant="outline" disabled={(listQ.data?.items?.length || 0) < 20} onClick={() => setPage(p => p + 1)}>Next</Button>
              </div>
            )}
          </>
        )}
      </CardContent>

      {/* Modals */}
      {statusModal   && <StatusModal   id={statusModal.id} current={statusModal.current} />}
      {escalateModal && <EscalateModal id={escalateModal} />}
      {containModal  && <ContainModal  id={containModal}  />}
      {mitigateModal && <MitigateModal id={mitigateModal} />}
      {resolveModal  && <ResolveModal  id={resolveModal}  />}
      {closeModal    && <CloseModal    id={closeModal}    />}
      {evidenceModal && <EvidenceModal id={evidenceModal} />}
    </Card>
  );
}

function HipaaSecurityAwarenessSection({ canManage }: { canManage: boolean }) {
  const [tab, setTab] = useState<"dashboard" | "records" | "assign" | "overdue" | "requirements">("dashboard");
  const { toast } = useToast();

  // ── Queries ────────────────────────────────────────────────────────────────
  const { data: summary, isLoading: summaryLoading } = useQuery<TrainingSummary>({
    queryKey: ["/api/organization/compliance/hipaa/security-training/summary"],
    staleTime: 0,
  });

  const { data: records, isLoading: recordsLoading } = useQuery<{ total: number; items: SecurityTrainingRecord[] }>({
    queryKey: ["/api/organization/compliance/hipaa/security-training"],
    staleTime: 0,
  });

  const { data: overdue, isLoading: overdueLoading } = useQuery<{
    overdue: { id: string; workforceMemberName: string; workforceRole: string; trainingTitle: string; trainingType: string; dueDate: string | null; daysOverdue: number }[];
    expired: { id: string; workforceMemberName: string; workforceRole: string; trainingTitle: string; expirationDate: string | null }[];
    dueSoon: { id: string; workforceMemberName: string; workforceRole: string; trainingTitle: string; dueDate: string | null; daysUntilDue: number }[];
  }>({
    queryKey: ["/api/organization/compliance/hipaa/security-training/overdue"],
    staleTime: 0,
  });

  const { data: requirements, isLoading: reqLoading } = useQuery<{ total: number; items: TrainingRequirement[] }>({
    queryKey: ["/api/organization/compliance/hipaa/security-training/requirements"],
    staleTime: 0,
  });

  // ── Assign training form ───────────────────────────────────────────────────
  const [assignForm, setAssignForm] = useState({
    userId: "", workforceMemberName: "", workforceRole: "", trainingType: "",
    trainingTitle: "", trainingProvider: "", dueDate: "", passingScore: "",
    recurrenceFrequencyDays: "", policyName: "", policyVersion: "", notes: "",
  });

  const assignMutation = useMutation({
    mutationFn: (data: object) =>
      apiRequest("POST", "/api/organization/compliance/hipaa/security-training", data),
    onSuccess: () => {
      toast({ title: "Training assigned" });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/hipaa/security-training"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/hipaa/security-training/summary"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/hipaa/security-training/overdue"] });
      setAssignForm({ userId: "", workforceMemberName: "", workforceRole: "", trainingType: "",
        trainingTitle: "", trainingProvider: "", dueDate: "", passingScore: "",
        recurrenceFrequencyDays: "", policyName: "", policyVersion: "", notes: "" });
      setTab("records");
    },
    onError: (e: Error) => toast({ title: "Unable to assign training", description: e.message, variant: "destructive" }),
  });

  // ── Complete training ──────────────────────────────────────────────────────
  const [completeDialog, setCompleteDialog] = useState<SecurityTrainingRecord | null>(null);
  const [completeForm, setCompleteForm] = useState({
    completedDate: "", certificateReference: "", score: "", notes: "",
  });

  const completeMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: object }) =>
      apiRequest("PATCH", `/api/organization/compliance/hipaa/security-training/${id}/complete`, data),
    onSuccess: () => {
      toast({ title: "Training marked complete" });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/hipaa/security-training"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/hipaa/security-training/summary"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/hipaa/security-training/overdue"] });
      setCompleteDialog(null);
    },
    onError: (e: Error) => toast({ title: "Cannot complete training", description: e.message, variant: "destructive" }),
  });

  // ── Acknowledge policy ─────────────────────────────────────────────────────
  const [ackDialog, setAckDialog] = useState<SecurityTrainingRecord | null>(null);
  const [ackStatus, setAckStatus] = useState<"Acknowledged" | "Declined">("Acknowledged");

  const ackMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: object }) =>
      apiRequest("PATCH", `/api/organization/compliance/hipaa/security-training/${id}/acknowledge`, data),
    onSuccess: () => {
      toast({ title: "Policy acknowledgement recorded" });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/hipaa/security-training"] });
      setAckDialog(null);
    },
    onError: (e: Error) => toast({ title: "Cannot record acknowledgement", description: e.message, variant: "destructive" }),
  });

  // ── Delete training record ─────────────────────────────────────────────────
  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      apiRequest("DELETE", `/api/organization/compliance/hipaa/security-training/${id}`),
    onSuccess: () => {
      toast({ title: "Training record deleted" });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/hipaa/security-training"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/hipaa/security-training/summary"] });
    },
    onError: (e: Error) => toast({ title: "Unable to delete", description: e.message, variant: "destructive" }),
  });

  // ── Requirement form ───────────────────────────────────────────────────────
  const [showReqForm, setShowReqForm] = useState(false);
  const [editingReq, setEditingReq] = useState<TrainingRequirement | null>(null);
  const [reqForm, setReqForm] = useState({
    workforceRole: "", trainingType: "", trainingTitle: "", description: "",
    required: true, recurrenceFrequency: "", dueWithinDays: "30", passingScore: "",
  });

  const createReqMutation = useMutation({
    mutationFn: (data: object) =>
      apiRequest("POST", "/api/organization/compliance/hipaa/security-training/requirements", data),
    onSuccess: () => {
      toast({ title: "Requirement created" });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/hipaa/security-training/requirements"] });
      setShowReqForm(false);
      setReqForm({ workforceRole: "", trainingType: "", trainingTitle: "", description: "",
        required: true, recurrenceFrequency: "", dueWithinDays: "30", passingScore: "" });
    },
    onError: (e: Error) => toast({ title: "Unable to create requirement", description: e.message, variant: "destructive" }),
  });

  const updateReqMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: object }) =>
      apiRequest("PUT", `/api/organization/compliance/hipaa/security-training/requirements/${id}`, data),
    onSuccess: () => {
      toast({ title: "Requirement updated" });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/hipaa/security-training/requirements"] });
      setEditingReq(null);
    },
    onError: (e: Error) => toast({ title: "Unable to update", description: e.message, variant: "destructive" }),
  });

  const deactivateReqMutation = useMutation({
    mutationFn: (id: string) =>
      apiRequest("DELETE", `/api/organization/compliance/hipaa/security-training/requirements/${id}`),
    onSuccess: () => {
      toast({ title: "Requirement deactivated" });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/hipaa/security-training/requirements"] });
    },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  // ── Dashboard tab ──────────────────────────────────────────────────────────
  const renderDashboard = () => (
    <div className="space-y-4">
      {summaryLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : summary ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: "Total Assigned",   value: summary.total },
              { label: "Completed",        value: summary.completed },
              { label: "In Progress",      value: summary.inProgress },
              { label: "Due Soon",         value: summary.dueSoon },
              { label: "Overdue",          value: summary.overdue,  alert: summary.overdue > 0 },
              { label: "Expired",          value: summary.expired,  alert: summary.expired > 0 },
              { label: "Completion %",     value: `${summary.completionPercentage}%` },
            ].map(c => (
              <Card key={c.label} className={c.alert ? "border-red-300 bg-red-50" : ""}>
                <CardContent className="pt-4 pb-3">
                  <div className={`text-2xl font-bold ${c.alert ? "text-red-600" : ""}`}>{c.value}</div>
                  <div className="text-sm text-muted-foreground">{c.label}</div>
                </CardContent>
              </Card>
            ))}
          </div>
          {(summary.byTrainingType ?? []).length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">By Training Type</CardTitle></CardHeader>
              <CardContent>
                <table className="w-full text-sm">
                  <thead><tr className="text-muted-foreground border-b">
                    <th className="text-left py-1">Type</th>
                    <th className="text-right py-1">Total</th>
                    <th className="text-right py-1">Completed</th>
                    <th className="text-right py-1">%</th>
                  </tr></thead>
                  <tbody>{(summary.byTrainingType ?? []).map(r => (
                    <tr key={r.trainingType} className="border-b last:border-0">
                      <td className="py-1">{trainingTypeLabel(r.trainingType)}</td>
                      <td className="py-1 text-right">{r.count}</td>
                      <td className="py-1 text-right">{r.completedCount}</td>
                      <td className="py-1 text-right">{r.count > 0 ? `${Math.round(100 * r.completedCount / r.count)}%` : "—"}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </>
      ) : <p className="text-sm text-muted-foreground">No data yet.</p>}
    </div>
  );

  // ── Records tab ────────────────────────────────────────────────────────────
  const renderRecords = () => (
    <div className="space-y-3">
      {recordsLoading ? <Skeleton className="h-40 w-full" /> : !records?.items.length
        ? <p className="text-sm text-muted-foreground">No training records yet.</p>
        : <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-muted-foreground border-b">
                <th className="text-left py-2 pr-3">Member</th>
                <th className="text-left py-2 pr-3">Role</th>
                <th className="text-left py-2 pr-3">Training</th>
                <th className="text-left py-2 pr-3">Due</th>
                <th className="text-left py-2 pr-3">Status</th>
                {canManage && <th className="text-right py-2">Actions</th>}
              </tr></thead>
              <tbody>{records.items.map(r => (
                <tr key={r.id} className="border-b last:border-0 align-top">
                  <td className="py-2 pr-3 font-medium">{r.workforceMemberName}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{r.workforceRole}</td>
                  <td className="py-2 pr-3">
                    <div className="font-medium">{r.trainingTitle}</div>
                    <div className="text-xs text-muted-foreground">{trainingTypeLabel(r.trainingType)}</div>
                    {r.completedDate && <div className="text-xs text-green-600">✓ {r.completedDate.slice(0,10)}</div>}
                    {r.expirationDate && <div className="text-xs text-muted-foreground">Exp: {r.expirationDate.slice(0,10)}</div>}
                    {r.acknowledgementStatus && (
                      <div className="text-xs text-blue-600">Policy: {r.acknowledgementStatus}</div>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-muted-foreground">{r.dueDate ? r.dueDate.slice(0,10) : "—"}</td>
                  <td className="py-2 pr-3">
                    <Badge variant={statusBadgeVariant(r.status)} className="text-xs">{r.status}</Badge>
                  </td>
                  {canManage && (
                    <td className="py-2 text-right space-x-1 whitespace-nowrap">
                      {r.status !== "Completed" && (
                        <Button size="sm" variant="outline" className="text-xs h-7 px-2"
                          onClick={() => { setCompleteDialog(r); setCompleteForm({ completedDate: new Date().toISOString().slice(0,10), certificateReference: "", score: "", notes: "" }); }}>
                          Complete
                        </Button>
                      )}
                      {r.policyName && r.acknowledgementStatus !== "Acknowledged" && (
                        <Button size="sm" variant="outline" className="text-xs h-7 px-2"
                          onClick={() => { setAckDialog(r); setAckStatus("Acknowledged"); }}>
                          Acknowledge
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" className="text-xs h-7 px-2 text-red-600"
                        onClick={() => { if (confirm("Delete this training record?")) deleteMutation.mutate(r.id); }}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </td>
                  )}
                </tr>
              ))}</tbody>
            </table>
          </div>}
    </div>
  );

  // ── Assign tab ─────────────────────────────────────────────────────────────
  const renderAssign = () => (
    <div className="max-w-xl space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>User ID *</Label>
          <Input placeholder="UUID" value={assignForm.userId}
            onChange={e => setAssignForm(f => ({ ...f, userId: e.target.value }))} />
        </div>
        <div>
          <Label>Workforce Member Name *</Label>
          <Input placeholder="Full name" value={assignForm.workforceMemberName}
            onChange={e => setAssignForm(f => ({ ...f, workforceMemberName: e.target.value }))} />
        </div>
        <div>
          <Label>Workforce Role *</Label>
          <Input placeholder="e.g. Nurse, IT Security" value={assignForm.workforceRole}
            onChange={e => setAssignForm(f => ({ ...f, workforceRole: e.target.value }))} />
        </div>
        <div>
          <Label>Training Type *</Label>
          <select className="w-full border rounded-md px-3 py-2 text-sm bg-background"
            value={assignForm.trainingType}
            onChange={e => setAssignForm(f => ({ ...f, trainingType: e.target.value }))}>
            <option value="">Select type…</option>
            {SECURITY_TRAINING_TYPES.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
        <div className="col-span-2">
          <Label>Training Title *</Label>
          <Input placeholder="e.g. Annual HIPAA Security Awareness Training" value={assignForm.trainingTitle}
            onChange={e => setAssignForm(f => ({ ...f, trainingTitle: e.target.value }))} />
        </div>
        <div>
          <Label>Training Provider</Label>
          <Input placeholder="Provider name" value={assignForm.trainingProvider}
            onChange={e => setAssignForm(f => ({ ...f, trainingProvider: e.target.value }))} />
        </div>
        <div>
          <Label>Due Date</Label>
          <Input type="date" value={assignForm.dueDate}
            onChange={e => setAssignForm(f => ({ ...f, dueDate: e.target.value }))} />
        </div>
        <div>
          <Label>Passing Score (0–100)</Label>
          <Input type="number" placeholder="80" value={assignForm.passingScore}
            onChange={e => setAssignForm(f => ({ ...f, passingScore: e.target.value }))} />
        </div>
        <div>
          <Label>Recurrence (days)</Label>
          <Input type="number" placeholder="365" value={assignForm.recurrenceFrequencyDays}
            onChange={e => setAssignForm(f => ({ ...f, recurrenceFrequencyDays: e.target.value }))} />
        </div>
        <div>
          <Label>Policy Name</Label>
          <Input placeholder="HIPAA Security Policy" value={assignForm.policyName}
            onChange={e => setAssignForm(f => ({ ...f, policyName: e.target.value }))} />
        </div>
        <div>
          <Label>Policy Version</Label>
          <Input placeholder="v2.0" value={assignForm.policyVersion}
            onChange={e => setAssignForm(f => ({ ...f, policyVersion: e.target.value }))} />
        </div>
        <div className="col-span-2">
          <Label>Notes</Label>
          <Textarea rows={2} value={assignForm.notes}
            onChange={e => setAssignForm(f => ({ ...f, notes: e.target.value }))} />
        </div>
      </div>
      <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2" onClick={() => assignMutation.mutate({
        userId:                 assignForm.userId.trim(),
        workforceMemberName:    assignForm.workforceMemberName.trim(),
        workforceRole:          assignForm.workforceRole.trim(),
        trainingType:           assignForm.trainingType,
        trainingTitle:          assignForm.trainingTitle.trim(),
        trainingProvider:       assignForm.trainingProvider.trim() || null,
        dueDate:                assignForm.dueDate || null,
        passingScore:           assignForm.passingScore ? parseFloat(assignForm.passingScore) : null,
        recurrenceFrequencyDays:assignForm.recurrenceFrequencyDays ? parseInt(assignForm.recurrenceFrequencyDays) : null,
        policyName:             assignForm.policyName.trim() || null,
        policyVersion:          assignForm.policyVersion.trim() || null,
        notes:                  assignForm.notes.trim() || null,
      })} disabled={assignMutation.isPending}>
        {assignMutation.isPending ? "Assigning…" : "Assign Training"}
      </Button>
    </div>
  );

  // ── Overdue tab ────────────────────────────────────────────────────────────
  const renderOverdue = () => (
    <div className="space-y-6">
      {overdueLoading ? <Skeleton className="h-40 w-full" /> : overdue ? (
        <>
          {/* Overdue */}
          <div>
            <h4 className="font-medium text-red-700 mb-2 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" /> Overdue ({(overdue.overdue ?? []).length})
            </h4>
            {(overdue.overdue ?? []).length === 0
              ? <p className="text-sm text-muted-foreground">No overdue training. ✓</p>
              : <table className="w-full text-sm">
                  <thead><tr className="text-muted-foreground border-b">
                    <th className="text-left py-1 pr-3">Member</th>
                    <th className="text-left py-1 pr-3">Role</th>
                    <th className="text-left py-1 pr-3">Training</th>
                    <th className="text-left py-1 pr-3">Due Date</th>
                    <th className="text-left py-1">Days Overdue</th>
                  </tr></thead>
                  <tbody>{(overdue.overdue ?? []).map(r => (
                    <tr key={r.id} className="border-b last:border-0">
                      <td className="py-1 pr-3 font-medium">{r.workforceMemberName}</td>
                      <td className="py-1 pr-3 text-muted-foreground">{r.workforceRole}</td>
                      <td className="py-1 pr-3">{r.trainingTitle}</td>
                      <td className="py-1 pr-3 text-red-600">{r.dueDate ? r.dueDate.slice(0,10) : "—"}</td>
                      <td className="py-1 text-red-700 font-medium">{r.daysOverdue}</td>
                    </tr>
                  ))}</tbody>
                </table>}
          </div>
          {/* Expired */}
          <div>
            <h4 className="font-medium text-orange-700 mb-2 flex items-center gap-2">
              <Clock className="h-4 w-4" /> Expired ({(overdue.expired ?? []).length})
            </h4>
            {(overdue.expired ?? []).length === 0
              ? <p className="text-sm text-muted-foreground">No expired training. ✓</p>
              : <table className="w-full text-sm">
                  <thead><tr className="text-muted-foreground border-b">
                    <th className="text-left py-1 pr-3">Member</th>
                    <th className="text-left py-1 pr-3">Role</th>
                    <th className="text-left py-1 pr-3">Training</th>
                    <th className="text-left py-1">Expired On</th>
                  </tr></thead>
                  <tbody>{(overdue.expired ?? []).map(r => (
                    <tr key={r.id} className="border-b last:border-0">
                      <td className="py-1 pr-3 font-medium">{r.workforceMemberName}</td>
                      <td className="py-1 pr-3 text-muted-foreground">{r.workforceRole}</td>
                      <td className="py-1 pr-3">{r.trainingTitle}</td>
                      <td className="py-1 text-orange-700">{r.expirationDate ? r.expirationDate.slice(0,10) : "—"}</td>
                    </tr>
                  ))}</tbody>
                </table>}
          </div>
          {/* Due Soon */}
          <div>
            <h4 className="font-medium text-yellow-700 mb-2 flex items-center gap-2">
              <Calendar className="h-4 w-4" /> Due Within 30 Days ({(overdue.dueSoon ?? []).length})
            </h4>
            {(overdue.dueSoon ?? []).length === 0
              ? <p className="text-sm text-muted-foreground">Nothing due in the next 30 days. ✓</p>
              : <table className="w-full text-sm">
                  <thead><tr className="text-muted-foreground border-b">
                    <th className="text-left py-1 pr-3">Member</th>
                    <th className="text-left py-1 pr-3">Role</th>
                    <th className="text-left py-1 pr-3">Training</th>
                    <th className="text-left py-1 pr-3">Due Date</th>
                    <th className="text-left py-1">Days Left</th>
                  </tr></thead>
                  <tbody>{(overdue.dueSoon ?? []).map(r => (
                    <tr key={r.id} className="border-b last:border-0">
                      <td className="py-1 pr-3 font-medium">{r.workforceMemberName}</td>
                      <td className="py-1 pr-3 text-muted-foreground">{r.workforceRole}</td>
                      <td className="py-1 pr-3">{r.trainingTitle}</td>
                      <td className="py-1 pr-3">{r.dueDate ? r.dueDate.slice(0,10) : "—"}</td>
                      <td className="py-1 text-yellow-700">{r.daysUntilDue}</td>
                    </tr>
                  ))}</tbody>
                </table>}
          </div>
        </>
      ) : null}
    </div>
  );

  // ── Requirements tab ───────────────────────────────────────────────────────
  const renderRequirements = () => (
    <div className="space-y-4">
      {canManage && !showReqForm && (
        <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2" onClick={() => setShowReqForm(true)}>
          <Plus className="h-4 w-4" /> Add Requirement
        </Button>
      )}
      {(showReqForm || editingReq) && canManage && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{editingReq ? "Edit Requirement" : "New Training Requirement"}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Workforce Role *</Label>
                <Input placeholder="e.g. Nurse" value={reqForm.workforceRole}
                  onChange={e => setReqForm(f => ({ ...f, workforceRole: e.target.value }))} />
              </div>
              <div>
                <Label>Training Type *</Label>
                <select className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                  value={reqForm.trainingType}
                  onChange={e => setReqForm(f => ({ ...f, trainingType: e.target.value }))}>
                  <option value="">Select type…</option>
                  {SECURITY_TRAINING_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div className="col-span-2">
                <Label>Training Title *</Label>
                <Input placeholder="e.g. Annual Security Awareness" value={reqForm.trainingTitle}
                  onChange={e => setReqForm(f => ({ ...f, trainingTitle: e.target.value }))} />
              </div>
              <div>
                <Label>Due Within (days)</Label>
                <Input type="number" value={reqForm.dueWithinDays}
                  onChange={e => setReqForm(f => ({ ...f, dueWithinDays: e.target.value }))} />
              </div>
              <div>
                <Label>Recurrence (days, blank = one-time)</Label>
                <Input type="number" placeholder="365" value={reqForm.recurrenceFrequency}
                  onChange={e => setReqForm(f => ({ ...f, recurrenceFrequency: e.target.value }))} />
              </div>
              <div>
                <Label>Passing Score (0–100)</Label>
                <Input type="number" placeholder="optional" value={reqForm.passingScore}
                  onChange={e => setReqForm(f => ({ ...f, passingScore: e.target.value }))} />
              </div>
              <div className="flex items-center gap-2 pt-5">
                <input type="checkbox" id="req-required" checked={reqForm.required}
                  onChange={e => setReqForm(f => ({ ...f, required: e.target.checked }))} />
                <label htmlFor="req-required" className="text-sm">Required (mandatory)</label>
              </div>
              <div className="col-span-2">
                <Label>Description</Label>
                <Textarea rows={2} value={reqForm.description}
                  onChange={e => setReqForm(f => ({ ...f, description: e.target.value }))} />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button size="sm" onClick={() => {
                const payload = {
                  workforceRole:       reqForm.workforceRole.trim(),
                  trainingType:        reqForm.trainingType,
                  trainingTitle:       reqForm.trainingTitle.trim(),
                  description:         reqForm.description.trim() || null,
                  required:            reqForm.required,
                  dueWithinDays:       parseInt(reqForm.dueWithinDays) || 30,
                  recurrenceFrequency: reqForm.recurrenceFrequency ? parseInt(reqForm.recurrenceFrequency) : null,
                  passingScore:        reqForm.passingScore ? parseFloat(reqForm.passingScore) : null,
                };
                if (editingReq) {
                  updateReqMutation.mutate({ id: editingReq.id, data: payload });
                } else {
                  createReqMutation.mutate(payload);
                }
              }} disabled={createReqMutation.isPending || updateReqMutation.isPending}>
                {editingReq ? "Save Changes" : "Create Requirement"}
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setShowReqForm(false); setEditingReq(null); }}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      {reqLoading ? <Skeleton className="h-32 w-full" /> : !requirements?.items.length
        ? <p className="text-sm text-muted-foreground">No requirements configured yet.</p>
        : <table className="w-full text-sm">
            <thead><tr className="text-muted-foreground border-b">
              <th className="text-left py-2 pr-3">Role</th>
              <th className="text-left py-2 pr-3">Training</th>
              <th className="text-left py-2 pr-3">Type</th>
              <th className="text-left py-2 pr-3">Due Within</th>
              <th className="text-left py-2 pr-3">Recurrence</th>
              <th className="text-left py-2 pr-3">Status</th>
              {canManage && <th className="text-right py-2">Actions</th>}
            </tr></thead>
            <tbody>{requirements.items.map(r => (
              <tr key={r.id} className="border-b last:border-0 align-middle">
                <td className="py-2 pr-3 font-medium">{r.workforceRole}</td>
                <td className="py-2 pr-3">{r.trainingTitle}</td>
                <td className="py-2 pr-3 text-muted-foreground text-xs">{trainingTypeLabel(r.trainingType)}</td>
                <td className="py-2 pr-3">{r.dueWithinDays}d</td>
                <td className="py-2 pr-3">{r.recurrenceFrequency ? `Every ${r.recurrenceFrequency}d` : "One-time"}</td>
                <td className="py-2 pr-3">
                  <Badge variant={r.isActive ? "default" : "secondary"} className="text-xs">
                    {r.isActive ? "Active" : "Inactive"}
                  </Badge>
                  {r.required && <Badge variant="outline" className="text-xs ml-1">Required</Badge>}
                </td>
                {canManage && (
                  <td className="py-2 text-right space-x-1 whitespace-nowrap">
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-xs"
                      onClick={() => {
                        setEditingReq(r);
                        setReqForm({
                          workforceRole:       r.workforceRole,
                          trainingType:        r.trainingType,
                          trainingTitle:       r.trainingTitle,
                          description:         r.description ?? "",
                          required:            r.required,
                          recurrenceFrequency: r.recurrenceFrequency?.toString() ?? "",
                          dueWithinDays:       r.dueWithinDays.toString(),
                          passingScore:        r.passingScore?.toString() ?? "",
                        });
                        setShowReqForm(false);
                      }}>
                      <Pencil className="h-3 w-3" />
                    </Button>
                    {r.isActive && (
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-red-600"
                        onClick={() => { if (confirm("Deactivate this requirement?")) deactivateReqMutation.mutate(r.id); }}>
                        <XCircle className="h-3 w-3" />
                      </Button>
                    )}
                  </td>
                )}
              </tr>
            ))}</tbody>
          </table>}
    </div>
  );

  const tabs = [
    { key: "dashboard",    label: "Dashboard" },
    { key: "records",      label: "Training Records" },
    ...(canManage ? [{ key: "assign", label: "Assign Training" }] : []),
    { key: "overdue",      label: "Overdue Report" },
    { key: "requirements", label: "Requirements" },
  ] as const;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Users className="h-5 w-5 text-blue-600" />
          Security Awareness &amp; Training
        </CardTitle>
        <CardDescription>
          Workforce security training assignments, completion tracking, and policy acknowledgements.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Tab nav */}
        <div className="flex gap-1 border-b mb-4 overflow-x-auto">
          {tabs.map(t => (
            <button key={t.key}
              className={`px-3 py-2 text-sm whitespace-nowrap border-b-2 transition-colors ${
                tab === t.key
                  ? "border-primary font-medium"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
              onClick={() => setTab(t.key as typeof tab)}>
              {t.label}
            </button>
          ))}
        </div>
        {tab === "dashboard"    && renderDashboard()}
        {tab === "records"      && renderRecords()}
        {tab === "assign"       && canManage && renderAssign()}
        {tab === "overdue"      && renderOverdue()}
        {tab === "requirements" && renderRequirements()}
      </CardContent>

      {/* Complete training dialog */}
      {completeDialog && (
        <Dialog open onOpenChange={() => setCompleteDialog(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Mark Training Complete</DialogTitle>
              <DialogDescription>
                {completeDialog.trainingTitle} — {completeDialog.workforceMemberName}
                {completeDialog.passingScore ? ` (Passing score: ${completeDialog.passingScore})` : ""}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Completion Date *</Label>
                <Input type="date" value={completeForm.completedDate}
                  onChange={e => setCompleteForm(f => ({ ...f, completedDate: e.target.value }))} />
              </div>
              <div>
                <Label>Certificate / Evidence Reference {(completeDialog.passingScore || completeDialog.recurrenceFrequencyDays) ? "*" : ""}</Label>
                <Input placeholder="CERT-001, LMS record ID, or URL" value={completeForm.certificateReference}
                  onChange={e => setCompleteForm(f => ({ ...f, certificateReference: e.target.value }))} />
              </div>
              {completeDialog.passingScore && (
                <div>
                  <Label>Score * (passing: {completeDialog.passingScore})</Label>
                  <Input type="number" placeholder="0–100" value={completeForm.score}
                    onChange={e => setCompleteForm(f => ({ ...f, score: e.target.value }))} />
                </div>
              )}
              <div>
                <Label>Notes</Label>
                <Textarea rows={2} value={completeForm.notes}
                  onChange={e => setCompleteForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCompleteDialog(null)}>Cancel</Button>
              <Button onClick={() => completeMutation.mutate({
                id: completeDialog.id,
                data: {
                  completedDate:        completeForm.completedDate,
                  certificateReference: completeForm.certificateReference.trim() || null,
                  score:                completeForm.score ? parseFloat(completeForm.score) : null,
                  notes:                completeForm.notes.trim() || null,
                },
              })} disabled={completeMutation.isPending}>
                {completeMutation.isPending ? "Saving…" : "Mark Complete"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Acknowledge policy dialog */}
      {ackDialog && (
        <Dialog open onOpenChange={() => setAckDialog(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Policy Acknowledgement</DialogTitle>
              <DialogDescription>
                {ackDialog.policyName} {ackDialog.policyVersion ? `(${ackDialog.policyVersion})` : ""} —{" "}
                {ackDialog.workforceMemberName}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <Label>Acknowledgement Status</Label>
              <div className="flex gap-4">
                {(["Acknowledged", "Declined"] as const).map(s => (
                  <label key={s} className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="ackStatus" value={s}
                      checked={ackStatus === s}
                      onChange={() => setAckStatus(s)} />
                    <span className="text-sm">{s}</span>
                  </label>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAckDialog(null)}>Cancel</Button>
              <Button onClick={() => ackMutation.mutate({
                id: ackDialog.id,
                data: { acknowledgementStatus: ackStatus },
              })} disabled={ackMutation.isPending}>
                {ackMutation.isPending ? "Saving…" : "Record Acknowledgement"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
}

// ── HIPAA-08 — Contingency Planning ──────────────────────────────────────────

type ContingencyPlan = {
  id: string; organizationId: string;
  planName: string; description: string | null; planType: string; scope: string | null;
  criticalSystems: string | null; ephiSystems: string | null;
  backupStrategy: string | null; backupFrequency: string | null;
  backupRetention: string | null; backupLocation: string | null; backupEncryption: boolean;
  recoveryStrategy: string | null; rtoHours: number | null; rpoHours: number | null;
  emergencyModeProcedure: string | null; emergencyContact: string | null;
  escalationProcedure: string | null;
  lastReviewedAt: string | null; nextReviewDate: string | null;
  status: string; ownerUserId: string | null;
  createdAt: string; updatedAt: string;
};

type BackupRestoreTest = {
  id: string; testType: string; systemName: string;
  backupDate: string; testDate: string;
  result: string; dataIntegrityVerified: boolean; ephiRecoveryVerified: boolean;
  recoveryDurationMinutes: number | null;
  targetRTOHours: number | null; targetRPOHours: number | null;
  rtoMet: boolean | null; rpoMet: boolean | null;
  evidenceReference: string | null; findings: string | null; correctiveActions: string | null;
  nextTestDate: string | null; createdAt: string;
};

type ContingencyTest = {
  id: string; testName: string; testType: string; scope: string | null;
  plannedDate: string; actualDate: string | null;
  participants: string | null; systemsTested: string | null;
  result: string; findings: string | null; correctiveActions: string | null;
  evidenceReference: string | null;
  actualRTOHours: number | null; actualRPOHours: number | null;
  rtoMet: boolean | null; rpoMet: boolean | null;
  nextTestDate: string | null; reviewer: string | null;
  createdAt: string;
};

type EmergencyMode = {
  id: string; systemName: string; emergencyModeEnabled: boolean;
  activatedAt: string | null; activatedBy: string | null;
  reason: string | null; authorizedRoles: string | null;
  permittedOperations: string | null; restrictedOperations: string | null;
  emergencyContact: string | null; reviewRequired: boolean;
  deactivatedAt: string | null; deactivatedBy: string | null;
  deactivationReason: string | null; createdAt: string;
};

type ContingencySummary = {
  totalPlans: number; activePlans: number; plansUnderReview: number; plansOverdueReview: number;
  totalTests: number; passedTests: number; failedTests: number; partialTests: number;
  testsDueSoon: number; testsOverdue: number;
  systemsWithRto: number; systemsRtoMet: number;
  systemsWithRpo: number; systemsRpoMet: number;
  activeEmergencyModes: number;
  lastSuccessfulRestoreTest: string | null; lastSuccessfulDrTest: string | null;
};

function planTypeBadge(t: string) {
  if (t === "DisasterRecovery")     return "destructive";
  if (t === "BusinessContinuity")   return "default";
  if (t === "EmergencyModeOperation") return "secondary";
  return "outline";
}
function planStatusBadge(s: string) {
  if (s === "Active")      return "default";
  if (s === "UnderReview") return "secondary";
  if (s === "Retired")     return "outline";
  return "outline";
}
function brtResultBadge(r: string) {
  if (r === "Passed")  return "default";
  if (r === "Unable to complete action")  return "destructive";
  return "secondary";
}
function ctResultBadge(r: string) {
  if (r === "Passed")         return "default";
  if (r === "Unable to complete action")         return "destructive";
  if (r === "PartiallyPassed") return "secondary";
  if (r === "InProgress")     return "secondary";
  return "outline";
}

function useContingencyAuth() {
  const token = sessionStorage.getItem("dotnetbot.jwt.session") || localStorage.getItem("dotnetbot.jwt") || "";
  return (url: string, opts: RequestInit = {}) =>
    fetch(url, { ...opts, headers: { ...(opts.headers as Record<string,string> || {}), "Content-Type": "application/json", Authorization: `Bearer ${token}` } });
}

function HipaaContingencyPlanningSection({ canManage }: { canManage: boolean }) {
  const { toast } = useToast();
  const authFetch = useContingencyAuth();
  const BASE = "/api/organization/compliance/hipaa";

  // Summary
  const summaryQ = useQuery<ContingencySummary>({
    queryKey: ["hipaa08-summary"],
    queryFn: async () => { const r = await authFetch(`${BASE}/contingency-summary`); if (!r.ok) throw new Error(); return r.json(); },
    staleTime: 0,
  });

  // Plans
  const [planPage, setPlanPage] = useState(1);
  const [planTypeFilter, setPlanTypeFilter] = useState("");
  const [planStatusFilter, setPlanStatusFilter] = useState("");
  const plansQ = useQuery<{ items: ContingencyPlan[]; total: number }>({
    queryKey: ["hipaa08-plans", planPage, planTypeFilter, planStatusFilter],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(planPage), pageSize: "10" });
      if (planTypeFilter)   params.set("planType", planTypeFilter);
      if (planStatusFilter) params.set("status",   planStatusFilter);
      const r = await authFetch(`${BASE}/contingency-plans?${params}`);
      if (!r.ok) throw new Error(); return r.json();
    },
    staleTime: 0,
  });

  // Backup/restore tests
  const brtsQ = useQuery<{ items: BackupRestoreTest[]; total: number }>({
    queryKey: ["hipaa08-brts"],
    queryFn: async () => { const r = await authFetch(`${BASE}/backup-restore-tests?pageSize=20`); if (!r.ok) throw new Error(); return r.json(); },
    staleTime: 0,
  });

  // Contingency tests
  const ctsQ = useQuery<{ items: ContingencyTest[]; total: number }>({
    queryKey: ["hipaa08-cts"],
    queryFn: async () => { const r = await authFetch(`${BASE}/contingency-tests?pageSize=20`); if (!r.ok) throw new Error(); return r.json(); },
    staleTime: 0,
  });

  // Emergency modes
  const emQ = useQuery<EmergencyMode[]>({
    queryKey: ["hipaa08-em"],
    queryFn: async () => { const r = await authFetch(`${BASE}/emergency-mode`); if (!r.ok) throw new Error(); return r.json(); },
    staleTime: 0,
  });

  // Create plan form
  const [showCreatePlan, setShowCreatePlan] = useState(false);
  const [planForm, setPlanForm] = useState({
    planName: "", description: "", planType: "Backup", scope: "",
    criticalSystems: "", ephiSystems: "",
    backupStrategy: "", backupFrequency: "Daily", backupRetention: "90 days",
    backupLocation: "", backupEncryption: true,
    recoveryStrategy: "", rtoHours: "", rpoHours: "",
    emergencyModeProcedure: "", emergencyContact: "", escalationProcedure: "",
    status: "Draft",
  });
  const [planErrors, setPlanErrors] = useState<Record<string, string>>({});

  const createPlanMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/contingency-plans`, {
        method: "POST",
        body: JSON.stringify({
          ...planForm,
          rtoHours: planForm.rtoHours ? parseFloat(planForm.rtoHours) : null,
          rpoHours: planForm.rpoHours ? parseFloat(planForm.rpoHours) : null,
        }),
      });
      if (!r.ok) { const e = await r.json(); throw new Error(e.message || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => {
      toast({ title: "Contingency plan created." });
      setShowCreatePlan(false); setPlanErrors({});
      queryClient.invalidateQueries({ queryKey: ["hipaa08-plans"] });
      queryClient.invalidateQueries({ queryKey: ["hipaa08-summary"] });
    },
    onError: (e: any) => {
      const serverErrs = extractServerErrors(e);
      if (serverErrs && Object.keys(serverErrs).length) { setPlanErrors(serverErrs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); }
      else toast({ title: "Unable to save", description: e?.message ?? "An unexpected error occurred.", variant: "destructive" });
    },
  });

  // Create BRT form
  const [showCreateBrt, setShowCreateBrt] = useState(false);
  const [brtForm, setBrtForm] = useState({
    testType: "RestoreTest", systemName: "", result: "Passed",
    backupDate: "", testDate: "", backupReference: "", restoreReference: "",
    dataIntegrityVerified: true, ephiRecoveryVerified: true,
    recoveryDurationMinutes: "", targetRTOHours: "", targetRPOHours: "",
    rtoMet: "true", rpoMet: "true", evidenceReference: "", findings: "", correctiveActions: "",
  });
  const [brtErrors, setBrtErrors] = useState<Record<string, string>>({});

  const createBrtMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/backup-restore-tests`, {
        method: "POST",
        body: JSON.stringify({
          ...brtForm,
          recoveryDurationMinutes: brtForm.recoveryDurationMinutes ? parseInt(brtForm.recoveryDurationMinutes) : null,
          targetRTOHours: brtForm.targetRTOHours ? parseFloat(brtForm.targetRTOHours) : null,
          targetRPOHours: brtForm.targetRPOHours ? parseFloat(brtForm.targetRPOHours) : null,
          rtoMet: brtForm.rtoMet === "true" ? true : brtForm.rtoMet === "false" ? false : null,
          rpoMet: brtForm.rpoMet === "true" ? true : brtForm.rpoMet === "false" ? false : null,
          backupDate: brtForm.backupDate || new Date().toISOString(),
          testDate: brtForm.testDate || new Date().toISOString(),
        }),
      });
      if (!r.ok) { const e = await r.json(); throw new Error(e.message || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => {
      toast({ title: "Backup/restore test recorded." });
      setShowCreateBrt(false); setBrtErrors({});
      queryClient.invalidateQueries({ queryKey: ["hipaa08-brts"] });
      queryClient.invalidateQueries({ queryKey: ["hipaa08-summary"] });
    },
    onError: (e: any) => {
      const serverErrs = extractServerErrors(e);
      if (serverErrs && Object.keys(serverErrs).length) { setBrtErrors(serverErrs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); }
      else toast({ title: "Unable to save", description: e?.message ?? "An unexpected error occurred.", variant: "destructive" });
    },
  });

  // Create contingency test form
  const [showCreateCt, setShowCreateCt] = useState(false);
  const [ctForm, setCtForm] = useState({
    testName: "", testType: "TabletopExercise", scope: "",
    plannedDate: "", participants: "", systemsTested: "",
    result: "Planned", findings: "", correctiveActions: "", evidenceReference: "",
  });
  const [ctErrors, setCtErrors] = useState<Record<string, string>>({});

  const createCtMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/contingency-tests`, {
        method: "POST",
        body: JSON.stringify({
          ...ctForm,
          plannedDate: ctForm.plannedDate || new Date().toISOString(),
        }),
      });
      if (!r.ok) { const e = await r.json(); throw new Error(e.message || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => {
      toast({ title: "Contingency test created." });
      setShowCreateCt(false); setCtErrors({});
      queryClient.invalidateQueries({ queryKey: ["hipaa08-cts"] });
      queryClient.invalidateQueries({ queryKey: ["hipaa08-summary"] });
    },
    onError: (e: any) => {
      const serverErrs = extractServerErrors(e);
      if (serverErrs && Object.keys(serverErrs).length) { setCtErrors(serverErrs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); }
      else toast({ title: "Unable to save", description: e?.message ?? "An unexpected error occurred.", variant: "destructive" });
    },
  });

  // Emergency mode activation
  const [showActivateEm, setShowActivateEm] = useState(false);
  const [emForm, setEmForm] = useState({
    systemName: "", reason: "", authorizedRoles: "",
    permittedOperations: "", restrictedOperations: "", emergencyContact: "",
  });
  const [emErrors, setEmErrors] = useState<Record<string,string>>({});

  const activateEmMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/emergency-mode/activate`, {
        method: "POST", body: JSON.stringify({ ...emForm, reviewRequired: true }),
      });
      if (!r.ok) { const e = await r.json(); throw new Error(e.message || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => {
      toast({ title: "Emergency mode activated.", variant: "destructive" });
      setShowActivateEm(false); setEmErrors({});
      queryClient.invalidateQueries({ queryKey: ["hipaa08-em"] });
      queryClient.invalidateQueries({ queryKey: ["hipaa08-summary"] });
    },
    onError: (e: any) => { const s = extractServerErrors(e); if (s && Object.keys(s).length) { setEmErrors(s); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); } else toast({ title: "Unable to complete action", description: (e as Error)?.message, variant: "destructive" }); },
  });

  const deactivateEmMut = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const r = await authFetch(`${BASE}/emergency-mode/deactivate`, {
        method: "POST", body: JSON.stringify({ emergencyModeId: id, deactivationReason: reason }),
      });
      if (!r.ok) { const e = await r.json(); throw new Error(e.message || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => {
      toast({ title: "Emergency mode deactivated." });
      queryClient.invalidateQueries({ queryKey: ["hipaa08-em"] });
      queryClient.invalidateQueries({ queryKey: ["hipaa08-summary"] });
    },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  const summary = summaryQ.data;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-blue-600" />
          Contingency Planning
        </CardTitle>
        <CardDescription>
          Backup, disaster recovery, emergency-mode operation, and contingency testing for ePHI systems.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">

        {/* ── Dashboard ── */}
        {summaryQ.isLoading ? <Skeleton className="h-20 w-full" /> : summary && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {[
              { label: "Active Plans",     value: summary.activePlans,        warn: false },
              { label: "Plans Due Review", value: summary.plansOverdueReview, warn: summary.plansOverdueReview > 0 },
              { label: "Passed Tests",     value: summary.passedTests,        warn: false },
              { label: "Failed Tests",     value: summary.failedTests,        warn: summary.failedTests > 0 },
              { label: "Tests Due Soon",   value: summary.testsDueSoon,       warn: summary.testsDueSoon > 0 },
              { label: "Tests Overdue",    value: summary.testsOverdue,       warn: summary.testsOverdue > 0 },
              { label: "RTO Compliant",    value: `${summary.systemsRtoMet}/${summary.systemsWithRto}`, warn: summary.systemsRtoMet < summary.systemsWithRto },
              { label: "RPO Compliant",    value: `${summary.systemsRpoMet}/${summary.systemsWithRpo}`, warn: summary.systemsRpoMet < summary.systemsWithRpo },
            ].map(m => (
              <div key={m.label} className={`rounded-lg border p-3 text-center ${m.warn ? "border-amber-300 bg-amber-50" : "border-muted bg-muted/30"}`}>
                <p className={`text-2xl font-bold ${m.warn ? "text-amber-700" : "text-foreground"}`}>{m.value}</p>
                <p className="text-xs text-muted-foreground mt-1">{m.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Active emergency mode warning */}
        {summary && summary.activeEmergencyModes > 0 && (
          <div className="rounded-lg border border-red-400 bg-red-50 p-3 flex items-center gap-2 text-red-700">
            <AlertTriangle className="h-5 w-5 shrink-0" />
            <span className="font-semibold">Emergency mode is currently active for {summary.activeEmergencyModes} system(s).</span>
          </div>
        )}

        {/* ── Contingency Plans ── */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-sm">Contingency Plans</h3>
            {canManage && (
              <Button size="sm" variant="outline" onClick={() => setShowCreatePlan(true)}>
                <Plus className="h-3 w-3 mr-1" /> Add Plan
              </Button>
            )}
          </div>
          <div className="flex gap-2 mb-2">
            <select className="border rounded px-2 py-1 text-xs" value={planTypeFilter} onChange={e => { setPlanTypeFilter(e.target.value); setPlanPage(1); }}>
              <option value="">All Types</option>
              {["Backup","DisasterRecovery","BusinessContinuity","EmergencyModeOperation","SystemRecovery","Other"].map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select className="border rounded px-2 py-1 text-xs" value={planStatusFilter} onChange={e => { setPlanStatusFilter(e.target.value); setPlanPage(1); }}>
              <option value="">All Statuses</option>
              {["Draft","Active","UnderReview","Retired"].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          {plansQ.isLoading ? <Skeleton className="h-20 w-full" /> : (
            <div className="space-y-2">
              {(plansQ.data?.items ?? []).length === 0 && <p className="text-xs text-muted-foreground">No contingency plans found.</p>}
              {(plansQ.data?.items ?? []).map(p => (
                <div key={p.id} className="rounded border p-3 flex flex-col gap-1 text-sm">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <span className="font-medium">{p.planName}</span>
                    <div className="flex gap-1">
                      <Badge variant={planTypeBadge(p.planType) as any}>{p.planType}</Badge>
                      <Badge variant={planStatusBadge(p.status) as any}>{p.status}</Badge>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground flex flex-wrap gap-3">
                    {p.rtoHours != null && <span>RTO: {p.rtoHours}h</span>}
                    {p.rpoHours != null && <span>RPO: {p.rpoHours}h</span>}
                    {p.nextReviewDate && <span>Review due: {p.nextReviewDate.slice(0,10)}</span>}
                    {p.criticalSystems && <span>Systems: {p.criticalSystems}</span>}
                  </div>
                </div>
              ))}
              {(plansQ.data?.total ?? 0) > 10 && (
                <div className="flex items-center justify-center gap-2 mt-2">
                  <Button size="sm" variant="ghost" disabled={planPage <= 1} onClick={() => setPlanPage(p => p - 1)}>Prev</Button>
                  <span className="text-xs text-muted-foreground">Page {planPage}</span>
                  <Button size="sm" variant="ghost" disabled={(plansQ.data?.items?.length ?? 0) < 10} onClick={() => setPlanPage(p => p + 1)}>Next</Button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Backup / Restore Tests ── */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-sm">Backup &amp; Restore Evidence</h3>
            {canManage && (
              <Button size="sm" variant="outline" onClick={() => setShowCreateBrt(true)}>
                <Plus className="h-3 w-3 mr-1" /> Record Test
              </Button>
            )}
          </div>
          {brtsQ.isLoading ? <Skeleton className="h-20 w-full" /> : (
            <div className="space-y-2">
              {(brtsQ.data?.items ?? []).length === 0 && <p className="text-xs text-muted-foreground">No backup/restore tests recorded.</p>}
              {(brtsQ.data?.items ?? []).map(t => (
                <div key={t.id} className="rounded border p-3 text-sm space-y-1">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <span className="font-medium">{t.systemName}</span>
                    <div className="flex gap-1">
                      <Badge variant="outline">{t.testType}</Badge>
                      <Badge variant={brtResultBadge(t.result) as any}>{t.result}</Badge>
                      {t.rtoMet === false && <Badge variant="destructive">RTO Failed</Badge>}
                      {t.rpoMet === false && <Badge variant="destructive">RPO Failed</Badge>}
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground flex flex-wrap gap-3">
                    <span>Test date: {t.testDate.slice(0,10)}</span>
                    {t.recoveryDurationMinutes != null && <span>Duration: {t.recoveryDurationMinutes}min</span>}
                    {t.targetRTOHours != null && <span>Target RTO: {t.targetRTOHours}h</span>}
                    {t.targetRPOHours != null && <span>Target RPO: {t.targetRPOHours}h</span>}
                    {t.evidenceReference && <span>Evidence: {t.evidenceReference}</span>}
                  </div>
                  {t.findings && <p className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-1">Findings: {t.findings}</p>}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Contingency Tests ── */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-sm">Contingency Tests</h3>
            {canManage && (
              <Button size="sm" variant="outline" onClick={() => setShowCreateCt(true)}>
                <Plus className="h-3 w-3 mr-1" /> Schedule Test
              </Button>
            )}
          </div>
          {ctsQ.isLoading ? <Skeleton className="h-20 w-full" /> : (
            <div className="space-y-2">
              {(ctsQ.data?.items ?? []).length === 0 && <p className="text-xs text-muted-foreground">No contingency tests recorded.</p>}
              {(ctsQ.data?.items ?? []).map(t => (
                <div key={t.id} className="rounded border p-3 text-sm space-y-1">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <span className="font-medium">{t.testName}</span>
                    <div className="flex gap-1">
                      <Badge variant="outline">{t.testType}</Badge>
                      <Badge variant={ctResultBadge(t.result) as any}>{t.result}</Badge>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground flex flex-wrap gap-3">
                    <span>Planned: {t.plannedDate.slice(0,10)}</span>
                    {t.actualDate && <span>Actual: {t.actualDate.slice(0,10)}</span>}
                    {t.systemsTested && <span>Systems: {t.systemsTested}</span>}
                    {t.nextTestDate && <span>Next test: {t.nextTestDate.slice(0,10)}</span>}
                  </div>
                  {t.findings && <p className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-1">Findings: {t.findings}</p>}
                  {t.correctiveActions && <p className="text-xs text-blue-700 bg-blue-50 rounded px-2 py-1">Corrective actions: {t.correctiveActions}</p>}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Emergency Mode ── */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-sm flex items-center gap-1">
              <ShieldAlert className="h-4 w-4 text-red-600" /> Emergency Mode
            </h3>
            {canManage && (
              <Button size="sm" variant="destructive" onClick={() => setShowActivateEm(true)}>
                Activate
              </Button>
            )}
          </div>
          {emQ.isLoading ? <Skeleton className="h-16 w-full" /> : (
            <div className="space-y-2">
              {(emQ.data ?? []).length === 0 && <p className="text-xs text-muted-foreground">No emergency mode procedures registered.</p>}
              {(emQ.data ?? []).map(em => (
                <div key={em.id} className={`rounded border p-3 text-sm space-y-1 ${em.emergencyModeEnabled ? "border-red-400 bg-red-50" : ""}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <span className="font-medium">{em.systemName}</span>
                    <div className="flex gap-1 items-center">
                      <Badge variant={em.emergencyModeEnabled ? "destructive" : "secondary"}>
                        {em.emergencyModeEnabled ? "ACTIVE" : "Inactive"}
                      </Badge>
                      {canManage && em.emergencyModeEnabled && (
                        <Button size="sm" variant="outline" className="h-6 text-xs"
                          onClick={() => {
                            const reason = prompt("Deactivation reason:");
                            if (reason) deactivateEmMut.mutate({ id: em.id, reason });
                          }}>
                          Deactivate
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground flex flex-wrap gap-3">
                    {em.activatedAt && <span>Activated: {em.activatedAt.slice(0,16).replace("T"," ")}</span>}
                    {em.reason && <span>Reason: {em.reason}</span>}
                    {em.authorizedRoles && <span>Authorized roles: {em.authorizedRoles}</span>}
                  </div>
                  {em.deactivatedAt && (
                    <p className="text-xs text-muted-foreground">Deactivated: {em.deactivatedAt.slice(0,16).replace("T"," ")} — {em.deactivationReason}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

      </CardContent>

      {/* Create Plan Dialog */}
      {showCreatePlan && (
        <Dialog open onOpenChange={v => { setShowCreatePlan(v); if (!v) setPlanErrors({}); }}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Contingency Plan</DialogTitle>
              <DialogDescription>Document a contingency plan for ePHI systems.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              {[
                { label: "Plan Name *", key: "planName" },
                { label: "Scope", key: "scope" },
                { label: "Critical Systems", key: "criticalSystems" },
                { label: "ePHI Systems", key: "ephiSystems" },
                { label: "Recovery Strategy", key: "recoveryStrategy" },
                { label: "Emergency Contact", key: "emergencyContact" },
              ].map(f => (
                <div key={f.key}>
                  <Label>{f.label}</Label>
                  <Input value={(planForm as any)[f.key]} onChange={e => { setPlanForm(p => ({ ...p, [f.key]: e.target.value })); clearErr(setPlanErrors, f.key); }} className={f.key === "planName" ? fieldCls("", planErrors, "planName") : undefined} aria-invalid={f.key === "planName" ? !!planErrors.planName : undefined} />
                  {f.key === "planName" && fErr(planErrors, "planName")}
                </div>
              ))}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Plan Type *</Label>
                  <select className={fieldCls("border rounded px-2 py-1 text-sm w-full", planErrors, "planType")} value={planForm.planType} onChange={e => { setPlanForm(p => ({ ...p, planType: e.target.value })); clearErr(setPlanErrors, "planType"); }} aria-invalid={!!planErrors.planType}>
                    {["Backup","DisasterRecovery","BusinessContinuity","EmergencyModeOperation","SystemRecovery","Other"].map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  {fErr(planErrors, "planType")}
                </div>
                <div>
                  <Label>Status *</Label>
                  <select className="border rounded px-2 py-1 text-sm w-full" value={planForm.status} onChange={e => setPlanForm(p => ({ ...p, status: e.target.value }))}>
                    {["Draft","Active","UnderReview","Retired"].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <Label>RTO (hours)</Label>
                  <Input type="number" step="0.5" value={planForm.rtoHours} onChange={e => setPlanForm(p => ({ ...p, rtoHours: e.target.value }))} />
                </div>
                <div>
                  <Label>RPO (hours)</Label>
                  <Input type="number" step="0.25" value={planForm.rpoHours} onChange={e => setPlanForm(p => ({ ...p, rpoHours: e.target.value }))} />
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={planForm.description} onChange={e => setPlanForm(p => ({ ...p, description: e.target.value }))} rows={2} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowCreatePlan(false); setPlanErrors({}); }}>Cancel</Button>
              <Button onClick={() => { const errs: Record<string, string> = {}; required(errs, "planName", planForm.planName, "Plan Name"); required(errs, "planType", planForm.planType, "Plan Type"); if (Object.keys(errs).length) { setPlanErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } setPlanErrors({}); createPlanMut.mutate(); }} disabled={createPlanMut.isPending}>
                {createPlanMut.isPending ? "Saving…" : "Create Plan"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Record BRT Dialog */}
      {showCreateBrt && (
        <Dialog open onOpenChange={setShowCreateBrt}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Record Backup/Restore Test</DialogTitle>
              <DialogDescription>Record evidence of a backup verification or restore test.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Test Type *</Label>
                  <select className="border rounded px-2 py-1 text-sm w-full" value={brtForm.testType} onChange={e => setBrtForm(p => ({ ...p, testType: e.target.value }))}>
                    {["BackupVerification","RestoreTest"].map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Result *</Label>
                  <select className="border rounded px-2 py-1 text-sm w-full" value={brtForm.result} onChange={e => setBrtForm(p => ({ ...p, result: e.target.value }))}>
                    {["Passed","Unable to complete action","Partial"].map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <Label>System Name *</Label>
                <Input className={fieldCls("", brtErrors, "systemName")} aria-invalid={!!brtErrors.systemName} value={brtForm.systemName} onChange={e => { setBrtForm(p => ({ ...p, systemName: e.target.value })); clearErr(setBrtErrors, "systemName"); }} placeholder="e.g. EHR-Primary" />
                {fErr(brtErrors, "systemName")}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Backup Date</Label><Input type="date" value={brtForm.backupDate} onChange={e => setBrtForm(p => ({ ...p, backupDate: e.target.value }))} /></div>
                <div><Label>Test Date</Label><Input type="date" value={brtForm.testDate} onChange={e => setBrtForm(p => ({ ...p, testDate: e.target.value }))} /></div>
                <div><Label>Target RTO (h)</Label><Input type="number" step="0.5" value={brtForm.targetRTOHours} onChange={e => setBrtForm(p => ({ ...p, targetRTOHours: e.target.value }))} /></div>
                <div><Label>Target RPO (h)</Label><Input type="number" step="0.25" value={brtForm.targetRPOHours} onChange={e => setBrtForm(p => ({ ...p, targetRPOHours: e.target.value }))} /></div>
                <div><Label>Recovery Duration (min)</Label><Input type="number" value={brtForm.recoveryDurationMinutes} onChange={e => setBrtForm(p => ({ ...p, recoveryDurationMinutes: e.target.value }))} /></div>
                <div>
                  <Label>RTO Met?</Label>
                  <select className="border rounded px-2 py-1 text-sm w-full" value={brtForm.rtoMet} onChange={e => setBrtForm(p => ({ ...p, rtoMet: e.target.value }))}>
                    <option value="true">Yes</option><option value="false">No</option><option value="">N/A</option>
                  </select>
                </div>
              </div>
              <div>
                <Label>Evidence Reference</Label>
                <Input value={brtForm.evidenceReference} onChange={e => setBrtForm(p => ({ ...p, evidenceReference: e.target.value }))} />
              </div>
              <div>
                <Label>Findings</Label>
                <Textarea value={brtForm.findings} onChange={e => setBrtForm(p => ({ ...p, findings: e.target.value }))} rows={2} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateBrt(false)}>Cancel</Button>
              <Button onClick={() => { const errs: Record<string,string> = {}; required(errs, "systemName", brtForm.systemName, "System Name"); if (Object.keys(errs).length) { setBrtErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } setBrtErrors({}); createBrtMut.mutate(); }} disabled={createBrtMut.isPending}>
                {createBrtMut.isPending ? "Saving…" : "Record Test"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Schedule Contingency Test Dialog */}
      {showCreateCt && (
        <Dialog open onOpenChange={setShowCreateCt}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Schedule Contingency Test</DialogTitle>
              <DialogDescription>Create a contingency test exercise.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <div><Label>Test Name *</Label><Input className={fieldCls("", ctErrors, "testName")} aria-invalid={!!ctErrors.testName} value={ctForm.testName} onChange={e => { setCtForm(p => ({ ...p, testName: e.target.value })); clearErr(setCtErrors, "testName"); }} />{fErr(ctErrors, "testName")}</div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Test Type *</Label>
                  <select className="border rounded px-2 py-1 text-sm w-full" value={ctForm.testType} onChange={e => setCtForm(p => ({ ...p, testType: e.target.value }))}>
                    {["BackupVerification","RestoreTest","DisasterRecoveryTest","BusinessContinuityTest","EmergencyModeTest","TabletopExercise"].map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Result</Label>
                  <select className="border rounded px-2 py-1 text-sm w-full" value={ctForm.result} onChange={e => setCtForm(p => ({ ...p, result: e.target.value }))}>
                    {["Planned","InProgress","Passed","Unable to complete action","PartiallyPassed"].map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              </div>
              <div><Label>Planned Date</Label><Input type="date" value={ctForm.plannedDate} onChange={e => setCtForm(p => ({ ...p, plannedDate: e.target.value }))} /></div>
              <div><Label>Systems Tested</Label><Input value={ctForm.systemsTested} onChange={e => setCtForm(p => ({ ...p, systemsTested: e.target.value }))} /></div>
              <div><Label>Participants</Label><Input value={ctForm.participants} onChange={e => setCtForm(p => ({ ...p, participants: e.target.value }))} /></div>
              {(ctForm.result === "Unable to complete action" || ctForm.result === "PartiallyPassed") && (
                <div><Label>Findings *</Label><Textarea value={ctForm.findings} onChange={e => setCtForm(p => ({ ...p, findings: e.target.value }))} rows={2} /></div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateCt(false)}>Cancel</Button>
              <Button onClick={() => { const errs: Record<string,string> = {}; required(errs, "testName", ctForm.testName, "Test Name"); if (Object.keys(errs).length) { setCtErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } setCtErrors({}); createCtMut.mutate(); }} disabled={createCtMut.isPending}>
                {createCtMut.isPending ? "Saving…" : "Create Test"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Activate Emergency Mode Dialog */}
      {showActivateEm && (
        <Dialog open onOpenChange={v => { setShowActivateEm(v); if (!v) setEmErrors({}); }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-red-700 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" /> Activate Emergency Mode
              </DialogTitle>
              <DialogDescription>
                Emergency mode is fully audited and does not bypass authentication or authorization.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <div><Label>System Name *</Label><Input className={fieldCls("", emErrors, "systemName")} aria-invalid={!!emErrors.systemName} value={emForm.systemName} onChange={e => { setEmForm(p => ({ ...p, systemName: e.target.value })); clearErr(setEmErrors, "systemName"); }} placeholder="e.g. EHR-Primary" />{fErr(emErrors, "systemName")}</div>
              <div><Label>Reason *</Label><Textarea className={fieldCls("", emErrors, "reason")} aria-invalid={!!emErrors.reason} value={emForm.reason} onChange={e => { setEmForm(p => ({ ...p, reason: e.target.value })); clearErr(setEmErrors, "reason"); }} rows={2} />{fErr(emErrors, "reason")}</div>
              <div><Label>Authorized Roles</Label><Input value={emForm.authorizedRoles} onChange={e => setEmForm(p => ({ ...p, authorizedRoles: e.target.value }))} placeholder="e.g. IncidentResponseTeam, ClinicalStaff" /></div>
              <div><Label>Permitted Operations</Label><Input value={emForm.permittedOperations} onChange={e => setEmForm(p => ({ ...p, permittedOperations: e.target.value }))} /></div>
              <div><Label>Restricted Operations</Label><Input value={emForm.restrictedOperations} onChange={e => setEmForm(p => ({ ...p, restrictedOperations: e.target.value }))} /></div>
              <div><Label>Emergency Contact</Label><Input value={emForm.emergencyContact} onChange={e => setEmForm(p => ({ ...p, emergencyContact: e.target.value }))} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowActivateEm(false)}>Cancel</Button>
              <Button variant="destructive" onClick={() => { const errs: Record<string,string> = {}; required(errs, "systemName", emForm.systemName, "System Name"); required(errs, "reason", emForm.reason, "Reason"); if (Object.keys(errs).length) { setEmErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } setEmErrors({}); activateEmMut.mutate(); }} disabled={activateEmMut.isPending}>
                {activateEmMut.isPending ? "Activating…" : "Activate Emergency Mode"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
}

// ── HIPAA-09 — Security Evaluation ───────────────────────────────────────────

type SecurityEvaluation = {
  id: string; organizationId: string;
  evaluationTitle: string; evaluationType: string;
  description: string | null; scope: string | null; evaluatedControls: string | null;
  evaluationDate: string | null; nextEvaluationDate: string | null; frequency: string | null;
  evaluatorName: string | null; evaluatorRole: string | null;
  methodology: string | null; findingsSummary: string | null;
  overallResult: string | null; riskLevel: string | null;
  status: string; startedAt: string | null; completedDate: string | null;
  materialChangeTriggered: boolean; materialChangeDescription: string | null;
  relatedSystemOrPolicy: string | null; materialChangeTriggerDate: string | null;
  remediationRequired: boolean; remediationSummary: string | null;
  remediationStatus: string | null; remediationDueDate: string | null;
  remediationCompletedDate: string | null; remediationEvidenceReference: string | null;
  evidenceReference: string | null; notes: string | null;
  createdAt: string; updatedAt: string;
};

type EvalSummary = {
  total: number; planned: number; inProgress: number; completed: number;
  overdue: number; dueSoon: number; materialChange: number;
  remediationRequired: number; openRemediation: number; completionPct: number;
};

function evalStatusBadge(s: string) {
  if (s === "Completed")  return "default";
  if (s === "InProgress") return "secondary";
  if (s === "Overdue")    return "destructive";
  return "outline";
}
function evalRiskBadge(r: string | null) {
  if (!r) return "outline";
  if (r === "Critical") return "destructive";
  if (r === "High")     return "destructive";
  if (r === "Medium")   return "secondary";
  return "outline";
}
function remStatusBadge(s: string | null) {
  if (!s || s === "NotRequired") return "outline";
  if (s === "Completed")         return "default";
  if (s === "Overdue")           return "destructive";
  return "secondary";
}

function useEvalAuth() {
  const token = sessionStorage.getItem("dotnetbot.jwt.session") || localStorage.getItem("dotnetbot.jwt") || "";
  return (url: string, opts: RequestInit = {}) =>
    fetch(url, { ...opts, headers: { ...(opts.headers as Record<string,string> || {}), "Content-Type": "application/json", Authorization: `Bearer ${token}` } });
}

// ── HIPAA-12 — Device & Media Controls ────────────────────────────────────
const DMC_DEVICE_TYPES = ["Desktop","Laptop","Server","Tablet","MobileDevice","UsbStorage","ExternalDrive","BackupMedia","RemovableMedia","HardDrive","SSD","Tape","Other"];
const DMC_OWNERSHIP    = ["OrganizationOwned","EmployeeOwned","ThirdPartyOwned","Leased","Other"];
const DMC_STAGES       = ["Received","InInventory","Assigned","Removed","PendingReuse","Sanitized","Disposed","Lost","Stolen","Retired"];
const DMC_SANITIZE_METHODS = ["SecureErase","CryptographicErase","PhysicalDestruction","Degaussing","ApprovedSanitizationProcedure","Other"];

type DmcSummary = {
  total:number; ephiAssets:number; inInventory:number; assigned:number; removed:number;
  pendingReuse:number; sanitized:number; disposed:number; lost:number; stolen:number;
  disposalPending:number; sanitizationPending:number; reviewDueSoon:number; reviewOverdue:number;
};
type DmcRecord = {
  id:string; assetTag:string; deviceName:string; deviceType:string; mediaType?:string;
  manufacturer?:string; model?:string; serialNumber?:string; description?:string;
  ownershipType:string; assignedUserId?:string; assignedUserName?:string;
  workforceRole?:string; department?:string; physicalLocation?:string;
  containsEphi:boolean; ephiDataDescription?:string; dataClassification?:string;
  status:string; lifecycleStage:string;
  receiptDate?:string; receivedBy?:string; receiptCondition?:string; assignedDate?:string;
  removalDate?:string; removedBy?:string; removalReason?:string;
  destinationType?:string; destinationDetails?:string;
  reuseAllowed:boolean; reuseStatus?:string;
  sanitizationRequired:boolean; sanitizationMethod?:string; sanitizationDate?:string;
  sanitizedBy?:string; sanitizationEvidenceReference?:string;
  disposalRequired:boolean; disposalDate?:string; disposedBy?:string; disposalMethod?:string;
  disposalCertificateReference?:string; disposalVendor?:string; disposalEvidenceDate?:string;
  chainOfCustodyReference?:string;
  lastInventoryReviewDate?:string; nextInventoryReviewDate?:string;
  notes?:string; createdAt:string; updatedAt:string;
};

function dmcStageVariant(stage:string): "default"|"secondary"|"destructive"|"outline" {
  if (stage==="Assigned") return "default";
  if (stage==="Disposed") return "secondary";
  if (stage==="Lost"||stage==="Stolen") return "destructive";
  return "outline";
}
function dmcReviewStatus(d:DmcRecord) {
  if (!d.nextInventoryReviewDate) return {label:"Not Scheduled",variant:"secondary" as const};
  const nd=new Date(d.nextInventoryReviewDate),now=new Date();
  if (nd<now) return {label:"Overdue",variant:"destructive" as const};
  if (nd<=new Date(now.getTime()+30*86400000)) return {label:"Due Soon",variant:"outline" as const};
  return {label:"Current",variant:"default" as const};
}

function HipaaDeviceMediaControlsSection({canManage}:{canManage:boolean}) {
  const {toast} = useToast();
  const authFetch = useEvalAuth();
  const BASE = "/api/organization/compliance/hipaa/device-media";
  const qc = useQueryClient();

  const summaryQ = useQuery<DmcSummary>({
    queryKey:["hipaa12-summary"],
    queryFn:async()=>{const r=await authFetch(`${BASE}/summary`);if(!r.ok)throw new Error();return r.json();},
    staleTime:0,
  });
  const overdueQ = useQuery<{items:DmcRecord[];total:number}>({
    queryKey:["hipaa12-overdue"],
    queryFn:async()=>{const r=await authFetch(`${BASE}/overdue`);if(!r.ok)throw new Error();return r.json();},
    staleTime:0,
  });
  const disposalQ = useQuery<{items:any[];total:number}>({
    queryKey:["hipaa12-disposal"],
    queryFn:async()=>{const r=await authFetch(`${BASE}/disposal-evidence`);if(!r.ok)throw new Error();return r.json();},
    staleTime:0,
  });

  const [page,setPage]=useState(1);
  const [stageFilter,setStageFilter]=useState("");
  const [typeFilter,setTypeFilter]=useState("");
  const [ephiFilter,setEphiFilter]=useState("");
  const [reviewFilter,setReviewFilter]=useState("");
  const [tab,setTab]=useState<"list"|"disposal">("list");

  const listQ = useQuery<{items:DmcRecord[];total:number}>({
    queryKey:["hipaa12-list",page,stageFilter,typeFilter,ephiFilter,reviewFilter],
    queryFn:async()=>{
      const p=new URLSearchParams({page:String(page),pageSize:"10"});
      if(stageFilter) p.set("lifecycleStage",stageFilter);
      if(typeFilter)  p.set("deviceType",typeFilter);
      if(ephiFilter)  p.set("containsEphi",ephiFilter);
      if(reviewFilter) p.set("reviewStatus",reviewFilter);
      const r=await authFetch(`${BASE}?${p}`);
      if(!r.ok) throw new Error(); return r.json();
    },
    staleTime:0,
  });

  const [detailId,setDetailId]=useState<string|null>(null);
  const detailQ = useQuery<DmcRecord>({
    queryKey:["hipaa12-detail",detailId],
    queryFn:async()=>{const r=await authFetch(`${BASE}/${detailId}`);if(!r.ok)throw new Error();return r.json();},
    enabled:!!detailId, staleTime:0,
  });

  const invalidate=()=>{
    qc.invalidateQueries({queryKey:["hipaa12-list"]});
    qc.invalidateQueries({queryKey:["hipaa12-summary"]});
    qc.invalidateQueries({queryKey:["hipaa12-overdue"]});
    qc.invalidateQueries({queryKey:["hipaa12-disposal"]});
    if(detailId) qc.invalidateQueries({queryKey:["hipaa12-detail",detailId]});
  };

  // Create form
  const defForm=()=>({
    assetTag:"",deviceName:"",deviceType:"Laptop",mediaType:"",manufacturer:"",model:"",
    serialNumber:"",description:"",ownershipType:"OrganizationOwned",physicalLocation:"",
    containsEphi:false,ephiDataDescription:"",dataClassification:"",lifecycleStage:"Received",
    receiptDate:"",receivedBy:"",receiptCondition:"",
    sanitizationRequired:false,disposalRequired:false,reuseAllowed:false,
    nextInventoryReviewDate:"",notes:"",
  });
  const [showCreate,setShowCreate]=useState(false);
  const [form,setForm]=useState(defForm());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const submitCreate = () => {
    const errs: Record<string, string> = {};
    required(errs, "assetTag", form.assetTag, "Asset Tag");
    required(errs, "deviceName", form.deviceName, "Device Name");
    required(errs, "deviceType", form.deviceType, "Device Type");
    if (Object.keys(errs).length) {
      setErrors(errs);
      toast({ title: "Please correct the highlighted fields.", variant: "destructive" });
      return;
    }
    setErrors({});
    createMut.mutate();
  };
  const createMut=useMutation({
    mutationFn:async()=>{
      const r=await authFetch(BASE,{method:"POST",body:JSON.stringify({
        ...form,
        receiptDate:form.receiptDate||null,
        nextInventoryReviewDate:form.nextInventoryReviewDate||null,
      })});
      if(!r.ok){const e=await r.json();throw new Error(e.message||"Unable to complete action");}
      return r.json();
    },
    onSuccess:()=>{toast({title:"Asset registered."});setShowCreate(false);setForm(defForm());setErrors({});invalidate();},
    onError:(e:Error)=>{const serverErrs=extractServerErrors(e);if(serverErrs){setErrors(serverErrs);toast({title:"Please correct the highlighted fields.",variant:"destructive"});}else toast({title:"Unable to save",description:e.message,variant:"destructive"});},
  });

  // Assign modal
  const [assignId,setAssignId]=useState<string|null>(null);
  const [assignForm,setAssignForm]=useState({assignedUserName:"",workforceRole:"",department:"",assignedDate:""});
  const assignMut=useMutation({
    mutationFn:async()=>{
      const r=await authFetch(`${BASE}/${assignId}/assign`,{method:"PATCH",body:JSON.stringify({...assignForm,assignedDate:assignForm.assignedDate||null})});
      if(!r.ok){const e=await r.json();throw new Error(e.message||"Unable to complete action");}return r.json();
    },
    onSuccess:()=>{toast({title:"Asset assigned."});setAssignId(null);invalidate();},
    onError:(e:Error)=>toast({title:"Unable to complete action",description:e.message,variant:"destructive"}),
  });

  // Remove modal
  const [removeId,setRemoveId]=useState<string|null>(null);
  const [removeForm,setRemoveForm]=useState({removedBy:"",removalReason:"",destinationType:"",chainOfCustodyReference:""});
  const removeMut=useMutation({
    mutationFn:async()=>{
      const r=await authFetch(`${BASE}/${removeId}/remove`,{method:"PATCH",body:JSON.stringify(removeForm)});
      if(!r.ok){const e=await r.json();throw new Error(e.message||"Unable to complete action");}return r.json();
    },
    onSuccess:()=>{toast({title:"Asset removed."});setRemoveId(null);invalidate();},
    onError:(e:Error)=>toast({title:"Unable to complete action",description:e.message,variant:"destructive"}),
  });

  // Sanitize modal
  const [sanitizeId,setSanitizeId]=useState<string|null>(null);
  const [sanForm,setSanForm]=useState({sanitizationMethod:"SecureErase",sanitizationDate:"",sanitizedBy:"",sanitizationEvidenceReference:"",notes:""});
  const sanitizeMut=useMutation({
    mutationFn:async()=>{
      const r=await authFetch(`${BASE}/${sanitizeId}/sanitize`,{method:"PATCH",body:JSON.stringify({...sanForm,sanitizationDate:sanForm.sanitizationDate||null})});
      if(!r.ok){const e=await r.json();throw new Error(e.message||"Unable to complete action");}return r.json();
    },
    onSuccess:()=>{toast({title:"Sanitization recorded."});setSanitizeId(null);invalidate();},
    onError:(e:Error)=>toast({title:"Unable to complete action",description:e.message,variant:"destructive"}),
  });

  // Dispose modal
  const [disposeId,setDisposeId]=useState<string|null>(null);
  const [dispForm,setDispForm]=useState({disposalDate:"",disposedBy:"",disposalMethod:"PhysicalDestruction",disposalCertificateReference:"",disposalVendor:"",chainOfCustodyReference:"",notes:""});
  const disposeMut=useMutation({
    mutationFn:async()=>{
      const r=await authFetch(`${BASE}/${disposeId}/dispose`,{method:"PATCH",body:JSON.stringify({...dispForm,disposalDate:dispForm.disposalDate||null,disposalEvidenceDate:dispForm.disposalDate||null})});
      if(!r.ok){const e=await r.json();throw new Error(e.message||"Unable to complete action");}return r.json();
    },
    onSuccess:()=>{toast({title:"Disposal recorded."});setDisposeId(null);invalidate();},
    onError:(e:Error)=>toast({title:"Unable to complete action",description:e.message,variant:"destructive"}),
  });

  // Status patch modal
  const [statusId,setStatusId]=useState<string|null>(null);
  const [newStage,setNewStage]=useState("InInventory");
  const [statusNotes,setStatusNotes]=useState("");
  const statusMut=useMutation({
    mutationFn:async()=>{
      const r=await authFetch(`${BASE}/${statusId}/status`,{method:"PATCH",body:JSON.stringify({lifecycleStage:newStage,notes:statusNotes})});
      if(!r.ok){const e=await r.json();throw new Error(e.message||"Unable to complete action");}return r.json();
    },
    onSuccess:()=>{toast({title:"Status updated."});setStatusId(null);invalidate();},
    onError:(e:Error)=>toast({title:"Unable to complete action",description:e.message,variant:"destructive"}),
  });

  // Review modal
  const [reviewId,setReviewId]=useState<string|null>(null);
  const [revForm,setRevForm]=useState({reviewDate:"",nextReviewDate:"",reviewer:"",notes:""});
  const reviewMut=useMutation({
    mutationFn:async()=>{
      const r=await authFetch(`${BASE}/${reviewId}/review`,{method:"PATCH",body:JSON.stringify({...revForm,reviewDate:revForm.reviewDate||null,nextReviewDate:revForm.nextReviewDate||null})});
      if(!r.ok){const e=await r.json();throw new Error(e.message||"Unable to complete action");}return r.json();
    },
    onSuccess:()=>{toast({title:"Review recorded."});setReviewId(null);invalidate();},
    onError:(e:Error)=>toast({title:"Unable to complete action",description:e.message,variant:"destructive"}),
  });

  // Reuse
  const reuseMut=useMutation({
    mutationFn:async(id:string)=>{
      const r=await authFetch(`${BASE}/${id}/reuse`,{method:"PATCH",body:JSON.stringify({notes:"Approved for reuse"})});
      if(!r.ok){const e=await r.json();throw new Error(e.message||"Unable to complete action");}return r.json();
    },
    onSuccess:()=>{toast({title:"Asset approved for reuse."});invalidate();},
    onError:(e:Error)=>toast({title:"Reuse blocked",description:e.message,variant:"destructive"}),
  });

  // Delete
  const deleteMut=useMutation({
    mutationFn:async(id:string)=>{
      const r=await authFetch(`${BASE}/${id}`,{method:"DELETE"});
      if(!r.ok) throw new Error("Unable to delete");
    },
    onSuccess:()=>{toast({title:"Asset deleted."});invalidate();},
    onError:(e:Error)=>toast({title:"Unable to complete action",description:e.message,variant:"destructive"}),
  });

  // Compliance check
  const [showCheck,setShowCheck]=useState(false);
  const [checkId,setCheckId]=useState("");
  const [checkAction,setCheckAction]=useState("Assign");
  const [checkResult,setCheckResult]=useState<any>(null);
  const checkMut=useMutation({
    mutationFn:async()=>{
      const r=await authFetch(`${BASE}/check`,{method:"POST",body:JSON.stringify({deviceMediaId:checkId,requestedAction:checkAction})});
      if(!r.ok) throw new Error("Check failed");return r.json();
    },
    onSuccess:(d)=>setCheckResult(d),
    onError:(e:Error)=>toast({title:"Unable to complete action",description:e.message,variant:"destructive"}),
  });

  const s=summaryQ.data;
  const totalPages=Math.ceil((listQ.data?.total??0)/10);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Device &amp; Media Controls (§ 164.310(d))</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
      {/* Dashboard */}
      {s && (
        <div className="grid grid-cols-2 md:grid-cols-7 gap-2">
          {[
            {label:"Total Assets",value:s.total},
            {label:"ePHI Assets",value:s.ephiAssets},
            {label:"In Inventory",value:s.inInventory},
            {label:"Assigned",value:s.assigned},
            {label:"Pending Reuse",value:s.pendingReuse},
            {label:"Sanitized",value:s.sanitized},
            {label:"Disposed",value:s.disposed},
            {label:"Lost",value:s.lost},
            {label:"Stolen",value:s.stolen},
            {label:"Disposal Pending",value:s.disposalPending},
            {label:"Sanitize Pending",value:s.sanitizationPending},
            {label:"Review Due Soon",value:s.reviewDueSoon},
            {label:"Review Overdue",value:s.reviewOverdue},
          ].map(c=>(
            <div key={c.label} className="border rounded p-2 text-center">
              <div className="text-xl font-bold">{c.value}</div>
              <div className="text-xs text-muted-foreground">{c.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Overdue alert */}
      {(overdueQ.data?.total??0)>0 && (
        <div className="border border-destructive/40 rounded p-3 bg-destructive/5">
          <p className="text-sm font-medium text-destructive mb-1">
            {overdueQ.data!.total} inventory review{overdueQ.data!.total!==1?"s":""} overdue or due within 30 days
          </p>
          <div className="flex flex-wrap gap-1">
            {overdueQ.data!.items.map(d=>(
              <Badge key={d.id} variant={d.nextInventoryReviewDate&&new Date(d.nextInventoryReviewDate)<new Date()?"destructive":"outline"}>
                {d.assetTag}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Tab bar */}
      <div className="flex gap-2 border-b pb-2">
        <button className={`text-sm font-medium px-3 py-1 rounded-t ${tab==="list"?"bg-primary text-primary-foreground":"text-muted-foreground"}`} onClick={()=>setTab("list")}>Registry</button>
        <button className={`text-sm font-medium px-3 py-1 rounded-t ${tab==="disposal"?"bg-primary text-primary-foreground":"text-muted-foreground"}`} onClick={()=>setTab("disposal")}>Disposal Evidence</button>
      </div>

      {tab==="list" && (
        <>
          {/* Filters + actions */}
          <div className="flex flex-wrap gap-2 items-center">
            <select className="border rounded px-2 py-1 text-sm" value={typeFilter} onChange={e=>{setTypeFilter(e.target.value);setPage(1);}}>
              <option value="">All Types</option>
              {DMC_DEVICE_TYPES.map(t=><option key={t} value={t}>{t}</option>)}
            </select>
            <select className="border rounded px-2 py-1 text-sm" value={stageFilter} onChange={e=>{setStageFilter(e.target.value);setPage(1);}}>
              <option value="">All Stages</option>
              {DMC_STAGES.map(s=><option key={s} value={s}>{s}</option>)}
            </select>
            <select className="border rounded px-2 py-1 text-sm" value={ephiFilter} onChange={e=>{setEphiFilter(e.target.value);setPage(1);}}>
              <option value="">All ePHI</option>
              <option value="true">ePHI</option>
              <option value="false">No ePHI</option>
            </select>
            <select className="border rounded px-2 py-1 text-sm" value={reviewFilter} onChange={e=>{setReviewFilter(e.target.value);setPage(1);}}>
              <option value="">All Reviews</option>
              <option value="overdue">Overdue</option>
              <option value="dueSoon">Due Soon</option>
              <option value="current">Current</option>
            </select>
            <div className="flex gap-2 ml-auto">
              {canManage&&<Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2" onClick={()=>setShowCreate(true)}><Plus className="h-4 w-4" /> Register Asset</Button>}
              <Button size="sm" variant="outline" onClick={()=>{setShowCheck(true);setCheckResult(null);}}>Run Check</Button>
            </div>
          </div>

          {/* Create form */}
          {showCreate && (
            <div className="border rounded p-4 space-y-4 bg-muted/30">
              <h4 className="font-medium">Register Device / Media Asset</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-medium">Asset Tag *</label>
                  <Input className={fieldCls("", errors, "assetTag")} aria-invalid={!!errors.assetTag} value={form.assetTag} onChange={e=>{setForm(f=>({...f,assetTag:e.target.value}));clearErr(setErrors,"assetTag");}} placeholder="AT-2026-001"/>
                  {fErr(errors, "assetTag")}
                </div>
                <div>
                  <label className="text-xs font-medium">Device Name *</label>
                  <Input className={fieldCls("", errors, "deviceName")} aria-invalid={!!errors.deviceName} value={form.deviceName} onChange={e=>{setForm(f=>({...f,deviceName:e.target.value}));clearErr(setErrors,"deviceName");}} placeholder="Clinical Laptop #5"/>
                  {fErr(errors, "deviceName")}
                </div>
                <div>
                  <label className="text-xs font-medium">Device Type *</label>
                  <select className={fieldCls("w-full border rounded px-2 py-1.5 text-sm", errors, "deviceType")} aria-invalid={!!errors.deviceType} value={form.deviceType} onChange={e=>{setForm(f=>({...f,deviceType:e.target.value}));clearErr(setErrors,"deviceType");}}>
                    {DMC_DEVICE_TYPES.map(t=><option key={t} value={t}>{t}</option>)}
                  </select>
                  {fErr(errors, "deviceType")}
                </div>
                <div>
                  <label className="text-xs font-medium">Manufacturer</label>
                  <Input value={form.manufacturer} onChange={e=>setForm(f=>({...f,manufacturer:e.target.value}))} placeholder="Dell"/>
                </div>
                <div>
                  <label className="text-xs font-medium">Model</label>
                  <Input value={form.model} onChange={e=>setForm(f=>({...f,model:e.target.value}))} placeholder="Latitude 5540"/>
                </div>
                <div>
                  <label className="text-xs font-medium">Serial Number</label>
                  <Input value={form.serialNumber} onChange={e=>setForm(f=>({...f,serialNumber:e.target.value}))} placeholder="SN-ABC123"/>
                </div>
                <div>
                  <label className="text-xs font-medium">Ownership Type</label>
                  <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.ownershipType} onChange={e=>setForm(f=>({...f,ownershipType:e.target.value}))}>
                    {DMC_OWNERSHIP.map(o=><option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium">Physical Location</label>
                  <Input value={form.physicalLocation} onChange={e=>setForm(f=>({...f,physicalLocation:e.target.value}))} placeholder="Building A, IT Room"/>
                </div>
                <div>
                  <label className="text-xs font-medium">Lifecycle Stage</label>
                  <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.lifecycleStage} onChange={e=>setForm(f=>({...f,lifecycleStage:e.target.value}))}>
                    {DMC_STAGES.map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium">Received By</label>
                  <Input value={form.receivedBy} onChange={e=>setForm(f=>({...f,receivedBy:e.target.value}))} placeholder="IT Admin"/>
                </div>
                <div>
                  <label className="text-xs font-medium">Receipt Date</label>
                  <Input type="date" value={form.receiptDate} onChange={e=>setForm(f=>({...f,receiptDate:e.target.value}))}/>
                </div>
                <div>
                  <label className="text-xs font-medium">Receipt Condition</label>
                  <Input value={form.receiptCondition} onChange={e=>setForm(f=>({...f,receiptCondition:e.target.value}))} placeholder="Good"/>
                </div>
                <div>
                  <label className="text-xs font-medium">Next Inventory Review</label>
                  <Input type="date" value={form.nextInventoryReviewDate} onChange={e=>setForm(f=>({...f,nextInventoryReviewDate:e.target.value}))}/>
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-medium">Notes</label>
                  <Input value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))}/>
                </div>
              </div>
              <div className="flex flex-wrap gap-4 text-sm">
                {([
                  ["containsEphi","Contains ePHI"],
                  ["sanitizationRequired","Sanitization Required"],
                  ["disposalRequired","Disposal Required"],
                  ["reuseAllowed","Reuse Allowed"],
                ] as [keyof typeof form,string][]).map(([k,label])=>(
                  <label key={k} className="flex items-center gap-1 cursor-pointer">
                    <input type="checkbox" checked={form[k] as boolean} onChange={e=>setForm(f=>({...f,[k]:e.target.checked}))}/>
                    {label}
                  </label>
                ))}
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={submitCreate} disabled={createMut.isPending}>{createMut.isPending?"Saving…":"Register Asset"}</Button>
                <Button size="sm" variant="outline" onClick={()=>{setShowCreate(false);setForm(defForm());setErrors({});}}>Cancel</Button>
              </div>
            </div>
          )}

          {/* Asset list */}
          {listQ.isLoading ? <Skeleton className="h-24 w-full"/> : (
            <div className="space-y-2">
              {(listQ.data?.items??[]).map(d=>{
                const rs=dmcReviewStatus(d);
                return (
                  <div key={d.id} className="border rounded p-3 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-medium">{d.assetTag} — {d.deviceName}</p>
                        <p className="text-xs text-muted-foreground">{d.deviceType}{d.manufacturer?` / ${d.manufacturer}`:""}{d.model?` ${d.model}`:""}{d.serialNumber?` | SN: ${d.serialNumber}`:""}</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          <Badge variant={dmcStageVariant(d.lifecycleStage)}>{d.lifecycleStage}</Badge>
                          {d.containsEphi&&<Badge variant="outline">ePHI</Badge>}
                          {d.sanitizationRequired&&!d.sanitizationDate&&<Badge variant="destructive">Sanitize Pending</Badge>}
                          {d.sanitizationDate&&<Badge variant="outline">Sanitized</Badge>}
                          {d.disposalRequired&&d.lifecycleStage!=="Disposed"&&<Badge variant="destructive">Disposal Pending</Badge>}
                          {d.assignedUserName&&<Badge variant="outline">→ {d.assignedUserName}</Badge>}
                          <Badge variant={rs.variant}>Review: {rs.label}</Badge>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1 flex-shrink-0">
                        <Button size="sm" variant="outline" onClick={()=>setDetailId(d.id)}>View</Button>
                        {canManage&&<>
                          {(d.lifecycleStage==="InInventory"||d.lifecycleStage==="Sanitized")&&
                            <Button size="sm" variant="outline" onClick={()=>{setAssignId(d.id);setAssignForm({assignedUserName:"",workforceRole:"",department:"",assignedDate:""});}}>Assign</Button>}
                          {d.lifecycleStage==="Assigned"&&
                            <Button size="sm" variant="outline" onClick={()=>{setRemoveId(d.id);setRemoveForm({removedBy:"",removalReason:"",destinationType:"",chainOfCustodyReference:""});}}>Remove</Button>}
                          {d.lifecycleStage!=="Disposed"&&d.lifecycleStage!=="Lost"&&d.lifecycleStage!=="Stolen"&&
                            <Button size="sm" variant="outline" onClick={()=>{setSanitizeId(d.id);setSanForm({sanitizationMethod:"SecureErase",sanitizationDate:"",sanitizedBy:"",sanitizationEvidenceReference:"",notes:""});}}>Sanitize</Button>}
                          {(d.lifecycleStage==="Sanitized"||d.lifecycleStage==="PendingReuse")&&
                            <Button size="sm" variant="outline" onClick={()=>reuseMut.mutate(d.id)}>Reuse</Button>}
                          {d.lifecycleStage!=="Disposed"&&
                            <Button size="sm" variant="outline" onClick={()=>{setDisposeId(d.id);setDispForm({disposalDate:"",disposedBy:"",disposalMethod:"PhysicalDestruction",disposalCertificateReference:"",disposalVendor:"",chainOfCustodyReference:"",notes:""});}}>Dispose</Button>}
                          <Button size="sm" variant="outline" onClick={()=>{setStatusId(d.id);setNewStage(d.lifecycleStage);setStatusNotes("");}}>Status</Button>
                          <Button size="sm" variant="outline" onClick={()=>{setReviewId(d.id);setRevForm({reviewDate:"",nextReviewDate:"",reviewer:"",notes:""});}}>Review</Button>
                          <Button size="sm" variant="destructive" onClick={()=>{if(confirm(`Delete ${d.assetTag}?`))deleteMut.mutate(d.id);}}>Del</Button>
                        </>}
                      </div>
                    </div>
                  </div>
                );
              })}
              {(listQ.data?.total??0)===0&&!listQ.isLoading&&(
                <p className="text-sm text-muted-foreground text-center py-4">No device/media assets registered.</p>
              )}
            </div>
          )}

          {/* Pagination */}
          {totalPages>1&&(
            <div className="flex gap-2 justify-center">
              <Button size="sm" variant="outline" disabled={page<=1} onClick={()=>setPage(p=>p-1)}>Prev</Button>
              <span className="text-sm py-1">{page} / {totalPages}</span>
              <Button size="sm" variant="outline" disabled={page>=totalPages} onClick={()=>setPage(p=>p+1)}>Next</Button>
            </div>
          )}
        </>
      )}

      {tab==="disposal" && (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">{disposalQ.data?.total??0} disposal record{disposalQ.data?.total!==1?"s":""}</p>
          {(disposalQ.data?.items??[]).map((d:any)=>(
            <div key={d.id} className="border rounded p-3">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-medium">{d.assetTag} — {d.deviceName}</p>
                  <p className="text-xs text-muted-foreground">{d.deviceType}</p>
                </div>
                <Badge variant={d.evidenceMissing?"destructive":"default"}>{d.evidenceMissing?"Evidence Missing":"Complete"}</Badge>
              </div>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-sm mt-2">
                {[
                  ["Disposal Date",d.disposalDate?new Date(d.disposalDate).toLocaleDateString():"—"],
                  ["Method",d.disposalMethod||"—"],
                  ["Vendor",d.disposalVendor||"—"],
                  ["Certificate",d.disposalCertificateReference||"—"],
                  ["Evidence Date",d.disposalEvidenceDate?new Date(d.disposalEvidenceDate).toLocaleDateString():"—"],
                  ["Chain of Custody",d.chainOfCustodyReference||"—"],
                  ["Disposed By",d.disposedBy||"—"],
                  ["Lifecycle",d.lifecycleStage],
                ].map(([k,v])=>(
                  <React.Fragment key={k as string}>
                    <dt className="text-muted-foreground">{k}</dt>
                    <dd className={String(v)==="—"?"text-muted-foreground":""}>{String(v)}</dd>
                  </React.Fragment>
                ))}
              </dl>
            </div>
          ))}
          {(disposalQ.data?.total??0)===0&&<p className="text-sm text-muted-foreground text-center py-4">No disposal records found.</p>}
        </div>
      )}

      {/* Detail modal */}
      {detailId&&detailQ.data&&(
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 space-y-4">
            <div className="flex justify-between items-start">
              <h4 className="font-semibold text-lg">{detailQ.data.assetTag} — {detailQ.data.deviceName}</h4>
              <Button size="sm" variant="outline" onClick={()=>setDetailId(null)}>✕</Button>
            </div>
            {(()=>{
              const d=detailQ.data!;
              const fmt=(v?:string)=>v?new Date(v).toLocaleDateString():"—";
              const yesNo=(v:boolean)=>v?"Yes":"No";
              const sections=[
                {title:"Asset Information",rows:[["Asset Tag",d.assetTag],["Device Name",d.deviceName],["Device Type",d.deviceType],["Media Type",d.mediaType],["Manufacturer",d.manufacturer],["Model",d.model],["Serial Number",d.serialNumber],["Description",d.description]]},
                {title:"Ownership",rows:[["Ownership Type",d.ownershipType],["Status",d.status],["Lifecycle Stage",d.lifecycleStage]]},
                {title:"Assignment",rows:[["Assigned User",d.assignedUserName],["Workforce Role",d.workforceRole],["Department",d.department],["Assigned Date",fmt(d.assignedDate)]]},
                {title:"ePHI Information",rows:[["Contains ePHI",yesNo(d.containsEphi)],["ePHI Description",d.ephiDataDescription],["Data Classification",d.dataClassification]]},
                {title:"Physical Location",rows:[["Location",d.physicalLocation]]},
                {title:"Receipt",rows:[["Receipt Date",fmt(d.receiptDate)],["Received By",d.receivedBy],["Condition",d.receiptCondition]]},
                {title:"Removal / Chain of Custody",rows:[["Removal Date",fmt(d.removalDate)],["Removed By",d.removedBy],["Reason",d.removalReason],["Destination",d.destinationType],["Details",d.destinationDetails],["Chain of Custody",d.chainOfCustodyReference]]},
                {title:"Sanitization Evidence",rows:[["Required",yesNo(d.sanitizationRequired)],["Method",d.sanitizationMethod],["Date",fmt(d.sanitizationDate)],["Sanitized By",d.sanitizedBy],["Evidence Reference",d.sanitizationEvidenceReference]]},
                {title:"Disposal Evidence",rows:[["Required",yesNo(d.disposalRequired)],["Disposal Date",fmt(d.disposalDate)],["Disposed By",d.disposedBy],["Method",d.disposalMethod],["Certificate",d.disposalCertificateReference],["Vendor",d.disposalVendor],["Evidence Date",fmt(d.disposalEvidenceDate)]]},
                {title:"Inventory Review",rows:[["Last Review",fmt(d.lastInventoryReviewDate)],["Next Review",fmt(d.nextInventoryReviewDate)]]},
                {title:"Notes",rows:[["Notes",d.notes]]},
              ];
              return sections.map(sec=>(
                <div key={sec.title}>
                  <h5 className="font-medium text-sm border-b pb-1 mb-2">{sec.title}</h5>
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-sm">
                    {sec.rows.filter(([,v])=>v!==undefined&&v!==null&&v!=="").map(([k,v])=>(
                      <React.Fragment key={k as string}>
                        <dt className="text-muted-foreground">{k}</dt>
                        <dd>{String(v)}</dd>
                      </React.Fragment>
                    ))}
                  </dl>
                </div>
              ));
            })()}
            <div className="pt-2">
              <LinkedSourceRow entityType="HipaaDeviceMediaControl" entityId={detailId!} canManage={canManage} />
            </div>
          </div>
        </div>
      )}

      {/* Assign modal */}
      {assignId&&(
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-lg p-6 space-y-3 w-96">
            <h4 className="font-medium">Assign Asset</h4>
            {[["assignedUserName","Assigned User *"],["workforceRole","Workforce Role"],["department","Department"]] .map(([k,label])=>(
              <div key={k}>
                <label className="text-xs font-medium">{label}</label>
                <Input value={(assignForm as any)[k]} onChange={e=>setAssignForm(f=>({...f,[k]:e.target.value}))} placeholder={label}/>
              </div>
            ))}
            <div><label className="text-xs font-medium">Assignment Date</label><Input type="date" value={assignForm.assignedDate} onChange={e=>setAssignForm(f=>({...f,assignedDate:e.target.value}))}/></div>
            <div className="flex gap-2">
              <Button size="sm" onClick={()=>assignMut.mutate()} disabled={assignMut.isPending}>{assignMut.isPending?"Saving…":"Assign"}</Button>
              <Button size="sm" variant="outline" onClick={()=>setAssignId(null)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* Remove modal */}
      {removeId&&(
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-lg p-6 space-y-3 w-96">
            <h4 className="font-medium">Remove Asset</h4>
            {[["removedBy","Removed By *"],["removalReason","Removal Reason"],["destinationType","Destination Type"],["chainOfCustodyReference","Chain of Custody Ref"]].map(([k,label])=>(
              <div key={k}>
                <label className="text-xs font-medium">{label}</label>
                <Input value={(removeForm as any)[k]} onChange={e=>setRemoveForm(f=>({...f,[k]:e.target.value}))}/>
              </div>
            ))}
            <div className="flex gap-2">
              <Button size="sm" onClick={()=>removeMut.mutate()} disabled={removeMut.isPending}>{removeMut.isPending?"Saving…":"Record Removal"}</Button>
              <Button size="sm" variant="outline" onClick={()=>setRemoveId(null)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* Sanitize modal */}
      {sanitizeId&&(
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-lg p-6 space-y-3 w-96">
            <h4 className="font-medium">Record Sanitization</h4>
            <div><label className="text-xs font-medium">Method *</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={sanForm.sanitizationMethod} onChange={e=>setSanForm(f=>({...f,sanitizationMethod:e.target.value}))}>
                {DMC_SANITIZE_METHODS.map(m=><option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div><label className="text-xs font-medium">Sanitization Date *</label><Input type="date" value={sanForm.sanitizationDate} onChange={e=>setSanForm(f=>({...f,sanitizationDate:e.target.value}))}/></div>
            <div><label className="text-xs font-medium">Sanitized By *</label><Input value={sanForm.sanitizedBy} onChange={e=>setSanForm(f=>({...f,sanitizedBy:e.target.value}))}/></div>
            <div><label className="text-xs font-medium">Evidence Reference *</label><Input value={sanForm.sanitizationEvidenceReference} onChange={e=>setSanForm(f=>({...f,sanitizationEvidenceReference:e.target.value}))} placeholder="CERT-SANIT-2026-001"/></div>
            <div><label className="text-xs font-medium">Notes</label><Input value={sanForm.notes} onChange={e=>setSanForm(f=>({...f,notes:e.target.value}))}/></div>
            <div className="flex gap-2">
              <Button size="sm" onClick={()=>sanitizeMut.mutate()} disabled={sanitizeMut.isPending}>{sanitizeMut.isPending?"Saving…":"Record Sanitization"}</Button>
              <Button size="sm" variant="outline" onClick={()=>setSanitizeId(null)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* Dispose modal */}
      {disposeId&&(
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-lg p-6 space-y-3 w-96">
            <h4 className="font-medium">Record Disposal</h4>
            <div><label className="text-xs font-medium">Disposal Date *</label><Input type="date" value={dispForm.disposalDate} onChange={e=>setDispForm(f=>({...f,disposalDate:e.target.value}))}/></div>
            <div><label className="text-xs font-medium">Disposed By *</label><Input value={dispForm.disposedBy} onChange={e=>setDispForm(f=>({...f,disposedBy:e.target.value}))}/></div>
            <div><label className="text-xs font-medium">Disposal Method *</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={dispForm.disposalMethod} onChange={e=>setDispForm(f=>({...f,disposalMethod:e.target.value}))}>
                {DMC_SANITIZE_METHODS.concat(["ShredAndRecycle","CertifiedDisposal"]).map(m=><option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div><label className="text-xs font-medium">Certificate Reference *</label><Input value={dispForm.disposalCertificateReference} onChange={e=>setDispForm(f=>({...f,disposalCertificateReference:e.target.value}))} placeholder="CERT-DISP-2026-001"/></div>
            <div><label className="text-xs font-medium">Disposal Vendor</label><Input value={dispForm.disposalVendor} onChange={e=>setDispForm(f=>({...f,disposalVendor:e.target.value}))}/></div>
            <div><label className="text-xs font-medium">Chain of Custody Ref</label><Input value={dispForm.chainOfCustodyReference} onChange={e=>setDispForm(f=>({...f,chainOfCustodyReference:e.target.value}))}/></div>
            <div><label className="text-xs font-medium">Notes</label><Input value={dispForm.notes} onChange={e=>setDispForm(f=>({...f,notes:e.target.value}))}/></div>
            <div className="flex gap-2">
              <Button size="sm" onClick={()=>disposeMut.mutate()} disabled={disposeMut.isPending}>{disposeMut.isPending?"Saving…":"Record Disposal"}</Button>
              <Button size="sm" variant="outline" onClick={()=>setDisposeId(null)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* Status modal */}
      {statusId&&(
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-lg p-6 space-y-3 w-80">
            <h4 className="font-medium">Change Lifecycle Stage</h4>
            <select className="w-full border rounded px-2 py-1.5 text-sm" value={newStage} onChange={e=>setNewStage(e.target.value)}>
              {DMC_STAGES.map(s=><option key={s} value={s}>{s}</option>)}
            </select>
            <Input placeholder="Notes (optional)" value={statusNotes} onChange={e=>setStatusNotes(e.target.value)}/>
            <div className="flex gap-2">
              <Button size="sm" onClick={()=>statusMut.mutate()} disabled={statusMut.isPending}>{statusMut.isPending?"Saving…":"Update"}</Button>
              <Button size="sm" variant="outline" onClick={()=>setStatusId(null)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* Review modal */}
      {reviewId&&(
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-lg p-6 space-y-3 w-96">
            <h4 className="font-medium">Record Inventory Review</h4>
            <div><label className="text-xs font-medium">Review Date *</label><Input type="date" value={revForm.reviewDate} onChange={e=>setRevForm(f=>({...f,reviewDate:e.target.value}))}/></div>
            <div><label className="text-xs font-medium">Next Review Date</label><Input type="date" value={revForm.nextReviewDate} onChange={e=>setRevForm(f=>({...f,nextReviewDate:e.target.value}))}/></div>
            <div><label className="text-xs font-medium">Reviewer</label><Input value={revForm.reviewer} onChange={e=>setRevForm(f=>({...f,reviewer:e.target.value}))}/></div>
            <div><label className="text-xs font-medium">Notes</label><Input value={revForm.notes} onChange={e=>setRevForm(f=>({...f,notes:e.target.value}))}/></div>
            <div className="flex gap-2">
              <Button size="sm" onClick={()=>reviewMut.mutate()} disabled={reviewMut.isPending}>{reviewMut.isPending?"Saving…":"Record Review"}</Button>
              <Button size="sm" variant="outline" onClick={()=>setReviewId(null)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* Compliance check modal */}
      {showCheck&&(
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-lg p-6 space-y-4 max-w-lg w-full">
            <div className="flex justify-between items-center">
              <h4 className="font-medium">Device/Media Compliance Check</h4>
              <Button size="sm" variant="outline" onClick={()=>{setShowCheck(false);setCheckResult(null);}}>✕</Button>
            </div>
            {!checkResult?(
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium">Asset *</label>
                  <select className="w-full border rounded px-2 py-1.5 text-sm" value={checkId} onChange={e=>setCheckId(e.target.value)}>
                    <option value="">-- Select Asset --</option>
                    {(listQ.data?.items??[]).map(d=><option key={d.id} value={d.id}>{d.assetTag} — {d.deviceName}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium">Requested Action</label>
                  <select className="w-full border rounded px-2 py-1.5 text-sm" value={checkAction} onChange={e=>setCheckAction(e.target.value)}>
                    {["Assign","Remove","Reuse","Sanitize","Dispose"].map(a=><option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
                <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2" onClick={()=>checkMut.mutate()} disabled={checkMut.isPending||!checkId}>{checkMut.isPending?"Checking…":"Run Check"}</Button>
              </div>
            ):(
              <div className="space-y-3">
                <div className={`text-xl font-bold ${checkResult.allowed?"text-green-600":"text-red-600"}`}>
                  {checkResult.allowed?"✓ Allowed":"✗ Blocked"}
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-muted-foreground">Asset:</span> {checkResult.assetTag}</div>
                  <div><span className="text-muted-foreground">Action:</span> {checkResult.requestedAction}</div>
                  <div><span className="text-muted-foreground">Stage:</span> {checkResult.currentLifecycleStage}</div>
                  <div><span className="text-muted-foreground">ePHI:</span> {checkResult.containsEphi?"Yes":"No"}</div>
                </div>
                {checkResult.failedControls?.length>0&&(
                  <div>
                    <p className="text-sm font-medium text-destructive mb-1">Failed / Reason Codes</p>
                    <ul className="list-disc list-inside text-sm text-destructive space-y-0.5">
                      {checkResult.failedControls.map((c:string)=><li key={c}>{c}</li>)}
                    </ul>
                  </div>
                )}
                {checkResult.requiredControls?.length>0&&(
                  <div>
                    <p className="text-sm font-medium mb-1">Required</p>
                    <div className="flex flex-wrap gap-1">{checkResult.requiredControls.map((c:string)=><Badge key={c} variant="outline">{c}</Badge>)}</div>
                  </div>
                )}
                <Button size="sm" variant="outline" onClick={()=>setCheckResult(null)}>Run Another Check</Button>
              </div>
            )}
          </div>
        </div>
      )}
      </CardContent>
    </Card>
  );
}

// ── HIPAA-11 — Workstation Security ───────────────────────────────────────
type WorkstationSummary = {
  total: number; active: number; draft: number; underReview: number; inactive: number;
  withEphi: number; mfaRequired: number; encryptionRequired: number; remoteAccess: number;
  reviewDueSoon: number; reviewOverdue: number; needsAttention: number;
};

type WorkstationPolicy = {
  id: string; policyName: string; workstationType: string;
  description?: string; purpose?: string;
  ephiAccessAllowed: boolean; authorizedWorkforceRoles?: string; authorizedUsers?: string;
  physicalLocation?: string; physicalAccessRestrictions?: string;
  cleanDeskRequired: boolean; privacyScreenRequired: boolean;
  publicWorkstationAllowed: boolean; sharedWorkstationAllowed: boolean;
  screenLockRequired: boolean; screenLockTimeoutMinutes?: number;
  passwordRequired: boolean; passwordPolicyReference?: string;
  multiFactorAuthenticationRequired: boolean; autoLogoutEnabled: boolean; autoLogoutMinutes?: number;
  encryptionRequired: boolean; encryptionType?: string;
  antivirusRequired: boolean; endpointProtectionRequired: boolean;
  firewallRequired: boolean; patchManagementRequired: boolean; minimumSupportedOsVersion?: string;
  usbStorageRestricted: boolean; localStorageRestricted: boolean;
  remoteAccessAllowed: boolean; remoteAccessControls?: string;
  sessionMonitoringEnabled: boolean; accessLoggingEnabled: boolean;
  policyReviewFrequency?: string; lastReviewDate?: string; nextReviewDate?: string;
  status: string; complianceNotes?: string; evidenceReference?: string;
  createdBy?: string; updatedBy?: string; createdAt: string; updatedAt: string;
};

const WS_TYPES = ["Desktop","Laptop","ClinicalWorkstation","SharedWorkstation","RemoteWorkstation","MobileWorkstation","Kiosk","Other"];
const WS_STATUSES = ["Draft","Active","UnderReview","Inactive"];

function wsReviewStatus(p: WorkstationPolicy): { label: string; variant: "default"|"secondary"|"destructive"|"outline" } {
  if (!p.nextReviewDate) return { label: "Not Scheduled", variant: "secondary" };
  const d = new Date(p.nextReviewDate), now = new Date();
  if (d < now) return { label: "Overdue", variant: "destructive" };
  if (d <= new Date(now.getTime() + 30*86400000)) return { label: "Due Soon", variant: "outline" };
  return { label: "Current", variant: "default" };
}

function HipaaWorkstationSecuritySection({ canManage }: { canManage: boolean }) {
  const { toast } = useToast();
  const authFetch = useEvalAuth();
  const BASE = "/api/organization/compliance/hipaa/workstation-security";
  const qc = useQueryClient();

  const summaryQ = useQuery<WorkstationSummary>({
    queryKey: ["hipaa11-summary"],
    queryFn: async () => { const r = await authFetch(`${BASE}/summary`); if (!r.ok) throw new Error(); return r.json(); },
    staleTime: 0,
  });

  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter]     = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [reviewFilter, setReviewFilter] = useState("");
  const [ephiFilter, setEphiFilter]     = useState("");

  const listQ = useQuery<{ items: WorkstationPolicy[]; total: number }>({
    queryKey: ["hipaa11-list", page, typeFilter, statusFilter, reviewFilter, ephiFilter],
    queryFn: async () => {
      const p = new URLSearchParams({ page: String(page), pageSize: "10" });
      if (typeFilter)   p.set("workstationType", typeFilter);
      if (statusFilter) p.set("status", statusFilter);
      if (reviewFilter) p.set("reviewStatus", reviewFilter);
      if (ephiFilter)   p.set("ephiAccess", ephiFilter);
      const r = await authFetch(`${BASE}?${p}`);
      if (!r.ok) throw new Error(); return r.json();
    },
    staleTime: 0,
  });

  const overdueQ = useQuery<{ items: WorkstationPolicy[]; total: number }>({
    queryKey: ["hipaa11-overdue"],
    queryFn: async () => { const r = await authFetch(`${BASE}/overdue`); if (!r.ok) throw new Error(); return r.json(); },
    staleTime: 0,
  });

  const [detailId, setDetailId] = useState<string | null>(null);
  const detailQ = useQuery<WorkstationPolicy>({
    queryKey: ["hipaa11-detail", detailId],
    queryFn: async () => { const r = await authFetch(`${BASE}/${detailId}`); if (!r.ok) throw new Error(); return r.json(); },
    enabled: !!detailId,
    staleTime: 0,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["hipaa11-list"] });
    qc.invalidateQueries({ queryKey: ["hipaa11-summary"] });
    qc.invalidateQueries({ queryKey: ["hipaa11-overdue"] });
    if (detailId) qc.invalidateQueries({ queryKey: ["hipaa11-detail", detailId] });
  };

  const defaultForm = () => ({
    policyName: "", workstationType: "Desktop", description: "", purpose: "",
    ephiAccessAllowed: false, authorizedWorkforceRoles: "", authorizedUsers: "",
    physicalLocation: "", physicalAccessRestrictions: "",
    cleanDeskRequired: false, privacyScreenRequired: false,
    publicWorkstationAllowed: false, sharedWorkstationAllowed: false,
    screenLockRequired: false, screenLockTimeoutMinutes: 10,
    passwordRequired: false, passwordPolicyReference: "",
    multiFactorAuthenticationRequired: false, autoLogoutEnabled: false, autoLogoutMinutes: 15,
    encryptionRequired: false, encryptionType: "", antivirusRequired: false,
    endpointProtectionRequired: false, firewallRequired: false, patchManagementRequired: false,
    minimumSupportedOsVersion: "", usbStorageRestricted: false, localStorageRestricted: false,
    remoteAccessAllowed: false, remoteAccessControls: "",
    sessionMonitoringEnabled: false, accessLoggingEnabled: false,
    policyReviewFrequency: "Annual", nextReviewDate: "",
    status: "Draft", complianceNotes: "", evidenceReference: "",
  });
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(defaultForm());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const submitCreate = () => {
    const errs: Record<string, string> = {};
    required(errs, "policyName", form.policyName, "Policy Name");
    required(errs, "workstationType", form.workstationType, "Workstation Type");
    if (Object.keys(errs).length) {
      setErrors(errs);
      toast({ title: "Please correct the highlighted fields.", variant: "destructive" });
      return;
    }
    setErrors({});
    createMut.mutate();
  };

  const createMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(BASE, {
        method: "POST",
        body: JSON.stringify({ ...form, nextReviewDate: form.nextReviewDate || null }),
      });
      if (!r.ok) { const e = await r.json(); throw new Error(e.message || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Policy created." }); setShowCreate(false); setForm(defaultForm()); setErrors({}); invalidate(); },
    onError: (e: Error) => { const serverErrs = extractServerErrors(e); if (serverErrs) { setErrors(serverErrs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); } else toast({ title: "Unable to save", description: e.message, variant: "destructive" }); },
  });

  const [statusId, setStatusId] = useState<string | null>(null);
  const [newStatus, setNewStatus] = useState("Active");
  const statusMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/${statusId}/status`, { method: "PATCH", body: JSON.stringify({ status: newStatus }) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.message || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Status updated." }); setStatusId(null); invalidate(); },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  const [reviewId, setReviewId] = useState<string | null>(null);
  const [reviewForm, setReviewForm] = useState({ reviewDate: "", nextReviewDate: "", frequency: "Annual", notes: "", evidenceReference: "" });
  const reviewMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/${reviewId}/review`, {
        method: "PATCH",
        body: JSON.stringify({
          reviewDate: reviewForm.reviewDate || new Date().toISOString(),
          nextReviewDate: reviewForm.nextReviewDate || null,
          frequency: reviewForm.frequency, notes: reviewForm.notes,
          evidenceReference: reviewForm.evidenceReference,
        }),
      });
      if (!r.ok) { const e = await r.json(); throw new Error(e.message || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Review completed." }); setReviewId(null); invalidate(); },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      const r = await authFetch(`${BASE}/${id}`, { method: "DELETE" });
      if (!r.ok) throw new Error("Unable to delete");
    },
    onSuccess: () => { toast({ title: "Policy deleted." }); invalidate(); },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  // Compliance check form
  const [showCheck, setShowCheck] = useState(false);
  const [checkPolicyId, setCheckPolicyId] = useState("");
  const [checkForm, setCheckForm] = useState({
    workstationType: "Desktop", workforceRole: "", userId: "",
    ephiAccessRequested: false, remoteAccess: false,
    sharedWorkstation: false, publicWorkstation: false,
    requestedLocation: "", requestedAction: "",
    deviceHasScreenLock: true, deviceHasMfa: true,
    deviceHasEncryption: true, deviceHasEndpointProtection: true,
  });
  const [checkResult, setCheckResult] = useState<any>(null);
  const checkMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/check`, {
        method: "POST",
        body: JSON.stringify({ ...checkForm, policyId: checkPolicyId }),
      });
      if (!r.ok) throw new Error("Check failed");
      return r.json();
    },
    onSuccess: (data) => { setCheckResult(data); },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  const s = summaryQ.data;
  const totalPages = Math.ceil((listQ.data?.total ?? 0) / 10);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Workstation Security (§ 164.310(b)/(c))</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
      {/* Dashboard */}
      {s && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {[
            { label: "Total Policies",    value: s.total },
            { label: "Active",            value: s.active },
            { label: "Draft",             value: s.draft },
            { label: "Under Review",      value: s.underReview },
            { label: "ePHI Policies",     value: s.withEphi },
            { label: "MFA Required",      value: s.mfaRequired },
            { label: "Encryption Req'd",  value: s.encryptionRequired },
            { label: "Remote Access",     value: s.remoteAccess },
            { label: "Review Due Soon",   value: s.reviewDueSoon },
            { label: "Review Overdue",    value: s.reviewOverdue },
            { label: "Needs Attention",   value: s.needsAttention },
          ].map(c => (
            <div key={c.label} className="border rounded p-3 text-center">
              <div className="text-2xl font-bold">{c.value}</div>
              <div className="text-xs text-muted-foreground">{c.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Overdue alert */}
      {(overdueQ.data?.total ?? 0) > 0 && (
        <div className="border border-destructive/40 rounded p-3 bg-destructive/5">
          <p className="text-sm font-medium text-destructive mb-2">
            {overdueQ.data!.total} policy review{overdueQ.data!.total !== 1 ? "s" : ""} overdue or due within 30 days
          </p>
          <div className="flex flex-wrap gap-2">
            {overdueQ.data!.items.map(p => (
              <Badge key={p.id} variant={p.nextReviewDate && new Date(p.nextReviewDate) < new Date() ? "destructive" : "outline"}>
                {p.policyName}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Filters + actions */}
      <div className="flex flex-wrap gap-2 items-center">
        <select className="border rounded px-2 py-1 text-sm" value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1); }}>
          <option value="">All Types</option>
          {WS_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select className="border rounded px-2 py-1 text-sm" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="">All Statuses</option>
          {WS_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="border rounded px-2 py-1 text-sm" value={reviewFilter} onChange={e => { setReviewFilter(e.target.value); setPage(1); }}>
          <option value="">All Reviews</option>
          <option value="overdue">Overdue</option>
          <option value="dueSoon">Due Soon</option>
          <option value="current">Current</option>
        </select>
        <select className="border rounded px-2 py-1 text-sm" value={ephiFilter} onChange={e => { setEphiFilter(e.target.value); setPage(1); }}>
          <option value="">All ePHI</option>
          <option value="true">ePHI Access</option>
          <option value="false">No ePHI</option>
        </select>
        <div className="flex gap-2 ml-auto">
          {canManage && <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2" onClick={() => setShowCreate(true)}><Plus className="h-4 w-4" /> Add Policy</Button>}
          <Button size="sm" variant="outline" onClick={() => setShowCheck(true)}>Run Check</Button>
        </div>
      </div>

      {/* Create form */}
      {showCreate && (
        <div className="border rounded p-4 space-y-4 bg-muted/30">
          <h4 className="font-medium">Add Workstation Security Policy</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium">Policy Name *</label>
              <Input className={fieldCls("", errors, "policyName")} aria-invalid={!!errors.policyName} value={form.policyName} onChange={e => { setForm(f => ({ ...f, policyName: e.target.value })); clearErr(setErrors, "policyName"); }} placeholder="e.g. Clinical Laptop Policy" />
              {fErr(errors, "policyName")}
            </div>
            <div>
              <label className="text-xs font-medium">Workstation Type *</label>
              <select className={fieldCls("w-full border rounded px-2 py-1.5 text-sm", errors, "workstationType")} aria-invalid={!!errors.workstationType} value={form.workstationType}
                onChange={e => { setForm(f => ({ ...f, workstationType: e.target.value })); clearErr(setErrors, "workstationType"); }}>
                {WS_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              {fErr(errors, "workstationType")}
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-medium">Description</label>
              <textarea className="w-full border rounded px-2 py-1.5 text-sm min-h-[50px]" value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs font-medium">Authorized Workforce Roles (comma-separated)</label>
              <Input value={form.authorizedWorkforceRoles} onChange={e => setForm(f => ({ ...f, authorizedWorkforceRoles: e.target.value }))} placeholder="Clinician,Nurse,Admin" />
            </div>
            <div>
              <label className="text-xs font-medium">Physical Location</label>
              <Input value={form.physicalLocation} onChange={e => setForm(f => ({ ...f, physicalLocation: e.target.value }))} placeholder="Building A, Floor 2" />
            </div>
            <div>
              <label className="text-xs font-medium">Screen Lock Timeout (min)</label>
              <Input type="number" min={1} value={form.screenLockTimeoutMinutes}
                onChange={e => setForm(f => ({ ...f, screenLockTimeoutMinutes: parseInt(e.target.value) || 10 }))} />
            </div>
            <div>
              <label className="text-xs font-medium">Auto Logout (min)</label>
              <Input type="number" min={1} value={form.autoLogoutMinutes}
                onChange={e => setForm(f => ({ ...f, autoLogoutMinutes: parseInt(e.target.value) || 15 }))} />
            </div>
            <div>
              <label className="text-xs font-medium">Encryption Type</label>
              <Input value={form.encryptionType} onChange={e => setForm(f => ({ ...f, encryptionType: e.target.value }))} placeholder="AES-256" />
            </div>
            <div>
              <label className="text-xs font-medium">Min OS Version</label>
              <Input value={form.minimumSupportedOsVersion} onChange={e => setForm(f => ({ ...f, minimumSupportedOsVersion: e.target.value }))} placeholder="Windows 11, macOS 14" />
            </div>
            <div>
              <label className="text-xs font-medium">Policy Review Frequency</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.policyReviewFrequency}
                onChange={e => setForm(f => ({ ...f, policyReviewFrequency: e.target.value }))}>
                {["Monthly","Quarterly","SemiAnnual","Annual","AsNeeded"].map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium">Next Review Date</label>
              <Input type="date" value={form.nextReviewDate} onChange={e => setForm(f => ({ ...f, nextReviewDate: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs font-medium">Status</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.status}
                onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                {WS_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          {/* Boolean toggles */}
          <div className="flex flex-wrap gap-4 text-sm">
            {([
              ["ephiAccessAllowed","ePHI Access Allowed"],
              ["screenLockRequired","Screen Lock Required"],
              ["passwordRequired","Password Required"],
              ["multiFactorAuthenticationRequired","MFA Required"],
              ["autoLogoutEnabled","Auto Logout"],
              ["encryptionRequired","Encryption Required"],
              ["antivirusRequired","Antivirus Required"],
              ["endpointProtectionRequired","Endpoint Protection"],
              ["firewallRequired","Firewall Required"],
              ["patchManagementRequired","Patch Management"],
              ["usbStorageRestricted","USB Restricted"],
              ["localStorageRestricted","Local Storage Restricted"],
              ["remoteAccessAllowed","Remote Access Allowed"],
              ["sessionMonitoringEnabled","Session Monitoring"],
              ["accessLoggingEnabled","Access Logging"],
              ["cleanDeskRequired","Clean Desk Required"],
              ["privacyScreenRequired","Privacy Screen Required"],
              ["publicWorkstationAllowed","Public Workstation Allowed"],
              ["sharedWorkstationAllowed","Shared Workstation Allowed"],
            ] as [keyof typeof form, string][]).map(([k, label]) => (
              <label key={k} className="flex items-center gap-1 cursor-pointer">
                <input type="checkbox" checked={form[k] as boolean}
                  onChange={e => setForm(f => ({ ...f, [k]: e.target.checked }))} />
                {label}
              </label>
            ))}
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={submitCreate} disabled={createMut.isPending}>
              {createMut.isPending ? "Saving…" : "Save Policy"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setShowCreate(false); setForm(defaultForm()); setErrors({}); }}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Policy list */}
      {listQ.isLoading ? <Skeleton className="h-24 w-full" /> : (
        <div className="space-y-2">
          {(listQ.data?.items ?? []).map(p => {
            const rs = wsReviewStatus(p);
            return (
              <div key={p.id} className="border rounded p-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-medium">{p.policyName}</p>
                    <p className="text-xs text-muted-foreground">{p.workstationType}</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      <Badge variant={p.status === "Active" ? "default" : p.status === "Draft" ? "secondary" : p.status === "Inactive" ? "secondary" : "outline"}>
                        {p.status}
                      </Badge>
                      {p.ephiAccessAllowed && <Badge variant="outline">ePHI</Badge>}
                      {p.multiFactorAuthenticationRequired && <Badge variant="outline">MFA</Badge>}
                      {p.encryptionRequired && <Badge variant="outline">Encrypted</Badge>}
                      {p.screenLockRequired && <Badge variant="outline">ScreenLock</Badge>}
                      {p.endpointProtectionRequired && <Badge variant="outline">EDR</Badge>}
                      {p.remoteAccessAllowed && <Badge variant="outline">Remote</Badge>}
                      <Badge variant={rs.variant}>Review: {rs.label}</Badge>
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button size="sm" variant="outline" onClick={() => setDetailId(p.id)}>View</Button>
                    {canManage && <>
                      <Button size="sm" variant="outline" onClick={() => { setStatusId(p.id); setNewStatus(p.status); }}>Status</Button>
                      <Button size="sm" variant="outline" onClick={() => { setReviewId(p.id); setReviewForm({ reviewDate: "", nextReviewDate: "", frequency: p.policyReviewFrequency || "Annual", notes: "", evidenceReference: "" }); }}>Review</Button>
                      <Button size="sm" variant="outline" onClick={() => { setCheckPolicyId(p.id); setShowCheck(true); }}>Check</Button>
                      <Button size="sm" variant="destructive" onClick={() => { if (confirm(`Delete "${p.policyName}"?`)) deleteMut.mutate(p.id); }}>Del</Button>
                    </>}
                  </div>
                </div>
              </div>
            );
          })}
          {(listQ.data?.total ?? 0) === 0 && !listQ.isLoading && (
            <p className="text-sm text-muted-foreground text-center py-4">No workstation security policies recorded.</p>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex gap-2 justify-center">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
          <span className="text-sm py-1">{page} / {totalPages}</span>
          <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
        </div>
      )}

      {/* Detail modal */}
      {detailId && detailQ.data && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 space-y-4">
            <div className="flex justify-between items-start">
              <h4 className="font-semibold text-lg">{detailQ.data.policyName}</h4>
              <Button size="sm" variant="outline" onClick={() => setDetailId(null)}>✕</Button>
            </div>
            {(() => {
              const d = detailQ.data!;
              const yesNo = (v: boolean) => v ? "Yes" : "No";
              const sections = [
                { title: "Policy Information", rows: [
                  ["Type", d.workstationType], ["Description", d.description], ["Purpose", d.purpose], ["Status", d.status],
                ]},
                { title: "Authorized Roles / Users", rows: [
                  ["ePHI Access Allowed", yesNo(d.ephiAccessAllowed)],
                  ["Authorized Roles", d.authorizedWorkforceRoles || "All roles"],
                  ["Authorized Users", d.authorizedUsers || "All users"],
                ]},
                { title: "Authentication Controls", rows: [
                  ["Password Required", yesNo(d.passwordRequired)],
                  ["MFA Required", yesNo(d.multiFactorAuthenticationRequired)],
                  ["Screen Lock", d.screenLockRequired ? `Yes (${d.screenLockTimeoutMinutes}min)` : "No"],
                  ["Auto Logout", d.autoLogoutEnabled ? `Yes (${d.autoLogoutMinutes}min)` : "No"],
                  ["Password Policy", d.passwordPolicyReference],
                ]},
                { title: "Physical Safeguards", rows: [
                  ["Location", d.physicalLocation], ["Access Restrictions", d.physicalAccessRestrictions],
                  ["Clean Desk", yesNo(d.cleanDeskRequired)], ["Privacy Screen", yesNo(d.privacyScreenRequired)],
                  ["Public Workstation Allowed", yesNo(d.publicWorkstationAllowed)],
                  ["Shared Workstation Allowed", yesNo(d.sharedWorkstationAllowed)],
                ]},
                { title: "Device Security", rows: [
                  ["Encryption", d.encryptionRequired ? `Required (${d.encryptionType || "unspecified"})` : "Not Required"],
                  ["Antivirus", yesNo(d.antivirusRequired)], ["Endpoint Protection", yesNo(d.endpointProtectionRequired)],
                  ["Firewall", yesNo(d.firewallRequired)], ["Patch Management", yesNo(d.patchManagementRequired)],
                  ["Min OS Version", d.minimumSupportedOsVersion],
                ]},
                { title: "Data Handling", rows: [
                  ["USB Restricted", yesNo(d.usbStorageRestricted)],
                  ["Local Storage Restricted", yesNo(d.localStorageRestricted)],
                ]},
                { title: "Remote Access", rows: [
                  ["Remote Access Allowed", yesNo(d.remoteAccessAllowed)],
                  ["Controls", d.remoteAccessControls],
                ]},
                { title: "Monitoring", rows: [
                  ["Session Monitoring", yesNo(d.sessionMonitoringEnabled)],
                  ["Access Logging", yesNo(d.accessLoggingEnabled)],
                ]},
                { title: "Review Information", rows: [
                  ["Frequency", d.policyReviewFrequency],
                  ["Last Review", d.lastReviewDate ? new Date(d.lastReviewDate).toLocaleDateString() : "—"],
                  ["Next Review", d.nextReviewDate ? new Date(d.nextReviewDate).toLocaleDateString() : "—"],
                ]},
                { title: "Evidence", rows: [
                  ["Evidence Reference", d.evidenceReference], ["Compliance Notes", d.complianceNotes],
                ]},
              ];
              return sections.map(sec => (
                <div key={sec.title}>
                  <h5 className="font-medium text-sm border-b pb-1 mb-2">{sec.title}</h5>
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                    {sec.rows.filter(([, v]) => v !== undefined && v !== null && v !== "").map(([k, v]) => (
                      <React.Fragment key={k as string}>
                        <dt className="text-muted-foreground">{k}</dt>
                        <dd>{String(v)}</dd>
                      </React.Fragment>
                    ))}
                  </dl>
                </div>
              ));
            })()}
          </div>
        </div>
      )}

      {/* Status modal */}
      {statusId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-lg p-6 space-y-4 w-80">
            <h4 className="font-medium">Change Status</h4>
            <select className="w-full border rounded px-2 py-1.5 text-sm" value={newStatus}
              onChange={e => setNewStatus(e.target.value)}>
              {WS_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => statusMut.mutate()} disabled={statusMut.isPending}>
                {statusMut.isPending ? "Saving…" : "Update"}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setStatusId(null)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* Review modal */}
      {reviewId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-lg p-6 space-y-3 w-96">
            <h4 className="font-medium">Complete Policy Review</h4>
            <div>
              <label className="text-xs font-medium">Review Date *</label>
              <Input type="date" value={reviewForm.reviewDate} onChange={e => setReviewForm(f => ({ ...f, reviewDate: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs font-medium">Next Review Date</label>
              <Input type="date" value={reviewForm.nextReviewDate} onChange={e => setReviewForm(f => ({ ...f, nextReviewDate: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs font-medium">Frequency</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={reviewForm.frequency}
                onChange={e => setReviewForm(f => ({ ...f, frequency: e.target.value }))}>
                {["Monthly","Quarterly","SemiAnnual","Annual","AsNeeded"].map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium">Evidence Reference</label>
              <Input value={reviewForm.evidenceReference} onChange={e => setReviewForm(f => ({ ...f, evidenceReference: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs font-medium">Notes</label>
              <textarea className="w-full border rounded px-2 py-1.5 text-sm min-h-[50px]" value={reviewForm.notes}
                onChange={e => setReviewForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => reviewMut.mutate()} disabled={reviewMut.isPending}>
                {reviewMut.isPending ? "Saving…" : "Complete Review"}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setReviewId(null)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* Compliance check modal */}
      {showCheck && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto p-6 space-y-4">
            <div className="flex justify-between items-center">
              <h4 className="font-medium">Workstation Compliance Check</h4>
              <Button size="sm" variant="outline" onClick={() => { setShowCheck(false); setCheckResult(null); }}>✕</Button>
            </div>
            {!checkResult ? (
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium">Policy *</label>
                  <select className="w-full border rounded px-2 py-1.5 text-sm" value={checkPolicyId}
                    onChange={e => setCheckPolicyId(e.target.value)}>
                    <option value="">-- Select Policy --</option>
                    {(listQ.data?.items ?? []).map(p => <option key={p.id} value={p.id}>{p.policyName}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium">Workstation Type</label>
                    <select className="w-full border rounded px-2 py-1.5 text-sm" value={checkForm.workstationType}
                      onChange={e => setCheckForm(f => ({ ...f, workstationType: e.target.value }))}>
                      {WS_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium">Workforce Role</label>
                    <Input value={checkForm.workforceRole} onChange={e => setCheckForm(f => ({ ...f, workforceRole: e.target.value }))} placeholder="Clinician" />
                  </div>
                  <div>
                    <label className="text-xs font-medium">User ID (optional)</label>
                    <Input value={checkForm.userId} onChange={e => setCheckForm(f => ({ ...f, userId: e.target.value }))} placeholder="user@hospital.org" />
                  </div>
                  <div>
                    <label className="text-xs font-medium">Requested Location</label>
                    <Input value={checkForm.requestedLocation} onChange={e => setCheckForm(f => ({ ...f, requestedLocation: e.target.value }))} placeholder="Building A" />
                  </div>
                  <div>
                    <label className="text-xs font-medium">Requested Action</label>
                    <Input value={checkForm.requestedAction} onChange={e => setCheckForm(f => ({ ...f, requestedAction: e.target.value }))} placeholder="View patient record" />
                  </div>
                </div>
                <div className="flex flex-wrap gap-4 text-sm">
                  {([
                    ["ephiAccessRequested","ePHI Access Requested"],
                    ["remoteAccess","Remote Access"],
                    ["sharedWorkstation","Shared Workstation"],
                    ["publicWorkstation","Public Workstation"],
                    ["deviceHasScreenLock","Device Has Screen Lock"],
                    ["deviceHasMfa","Device Has MFA"],
                    ["deviceHasEncryption","Device Has Encryption"],
                    ["deviceHasEndpointProtection","Device Has Endpoint Protection"],
                  ] as [keyof typeof checkForm, string][]).map(([k, label]) => (
                    <label key={k} className="flex items-center gap-1 cursor-pointer">
                      <input type="checkbox" checked={checkForm[k] as boolean}
                        onChange={e => setCheckForm(f => ({ ...f, [k]: e.target.checked }))} />
                      {label}
                    </label>
                  ))}
                </div>
                <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2" onClick={() => checkMut.mutate()} disabled={checkMut.isPending || !checkPolicyId}>
                  {checkMut.isPending ? "Checking…" : "Run Check"}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className={`text-xl font-bold ${checkResult.allowed ? "text-green-600" : "text-red-600"}`}>
                  {checkResult.allowed ? "✓ Allowed" : "✗ Blocked"}
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-muted-foreground">Policy:</span> {checkResult.policyName}</div>
                  <div><span className="text-muted-foreground">Type:</span> {checkResult.workstationType}</div>
                  <div><span className="text-muted-foreground">Role:</span> {checkResult.workforceRole || "—"}</div>
                  <div><span className="text-muted-foreground">Review:</span> {checkResult.reviewStatus}</div>
                </div>
                {checkResult.failedControls?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-destructive mb-1">Failed Controls / Reason Codes</p>
                    <ul className="list-disc list-inside text-sm text-destructive space-y-1">
                      {checkResult.failedControls.map((c: string) => <li key={c}>{c}</li>)}
                    </ul>
                  </div>
                )}
                {checkResult.requiredControls?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium mb-1">Required Controls</p>
                    <div className="flex flex-wrap gap-1">
                      {checkResult.requiredControls.map((c: string) => <Badge key={c} variant="outline">{c}</Badge>)}
                    </div>
                  </div>
                )}
                {checkResult.warningControls?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-orange-600 mb-1">Warnings</p>
                    <ul className="list-disc list-inside text-sm text-orange-600 space-y-1">
                      {checkResult.warningControls.map((c: string) => <li key={c}>{c}</li>)}
                    </ul>
                  </div>
                )}
                <Button size="sm" variant="outline" onClick={() => setCheckResult(null)}>Run Another Check</Button>
              </div>
            )}
          </div>
        </div>
      )}
      </CardContent>
    </Card>
  );
}

// ── HIPAA-10 — Facility Access ─────────────────────────────────────────────
type FacilitySummary = {
  total: number; active: number; underReview: number; inactive: number;
  withEphi: number; reviewDueSoon: number; reviewOverdue: number;
  hostingDue: number; visitorMissing: number; compliant: number; needsAttention: number;
};

type FacilityAccess = {
  id: string; facilityName: string; facilityType: string;
  facilityAddress?: string; description?: string; ephiSystemsPresent: boolean;
  physicalAccessControlType?: string; accessControlDescription?: string;
  authorizedAccessRoles?: string; restrictedAreas?: string;
  badgeOrKeyControl: boolean; biometricControl: boolean; accessLoggingEnabled: boolean;
  accessReviewFrequency?: string; lastAccessReviewDate?: string;
  nextAccessReviewDate?: string; accessReviewNotes?: string;
  visitorControlEnabled: boolean; visitorRegistrationRequired: boolean;
  visitorEscortRequired: boolean; visitorLogRetention?: string;
  environmentalSafeguards?: string; powerProtection: boolean;
  fireProtection: boolean; temperatureMonitoring: boolean; waterLeakProtection: boolean;
  emergencyProcedures?: string;
  hostingProvider?: string; hostingProviderFacility?: string;
  hostingProviderControlsReviewed: boolean; hostingProviderReviewDate?: string;
  hostingProviderEvidenceReference?: string;
  status: string; notes?: string;
  createdBy?: string; updatedBy?: string; createdAt: string; updatedAt: string;
};

const FACILITY_TYPES = ["DataCenter","ServerRoom","Office","Cloud","CoLocation","Other"];
const FACILITY_STATUSES = ["Active","UnderReview","Inactive"];

function facilityReviewStatus(f: FacilityAccess): { label: string; variant: "default"|"secondary"|"destructive"|"outline" } {
  if (!f.nextAccessReviewDate) return { label: "Not Scheduled", variant: "secondary" };
  const d = new Date(f.nextAccessReviewDate);
  const now = new Date();
  if (d < now) return { label: "Overdue", variant: "destructive" };
  if (d <= new Date(now.getTime() + 30 * 86400000)) return { label: "Due Soon", variant: "outline" };
  return { label: "Current", variant: "default" };
}

function facilityHostingStatus(f: FacilityAccess): string {
  if (!f.hostingProvider) return "N/A";
  if (!f.hostingProviderControlsReviewed) return "Not Reviewed";
  if (f.hostingProviderReviewDate) {
    const d = new Date(f.hostingProviderReviewDate);
    const threshold = new Date();
    threshold.setFullYear(threshold.getFullYear() - 1);
    if (d < threshold) return "Overdue";
  }
  return "Reviewed";
}

function HipaaFacilityAccessSection({ canManage }: { canManage: boolean }) {
  const { toast } = useToast();
  const authFetch = useEvalAuth();
  const BASE = "/api/organization/compliance/hipaa/facility-access";

  const summaryQ = useQuery<FacilitySummary>({
    queryKey: ["hipaa10-summary"],
    queryFn: async () => { const r = await authFetch(`${BASE}/summary`); if (!r.ok) throw new Error(); return r.json(); },
    staleTime: 0,
  });

  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter]     = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [reviewFilter, setReviewFilter] = useState("");
  const [ephiFilter, setEphiFilter]     = useState("");

  const listQ = useQuery<{ items: FacilityAccess[]; total: number }>({
    queryKey: ["hipaa10-list", page, typeFilter, statusFilter, reviewFilter, ephiFilter],
    queryFn: async () => {
      const p = new URLSearchParams({ page: String(page), pageSize: "10" });
      if (typeFilter)   p.set("facilityType", typeFilter);
      if (statusFilter) p.set("status", statusFilter);
      if (reviewFilter) p.set("reviewStatus", reviewFilter);
      if (ephiFilter)   p.set("ephiSystemsPresent", ephiFilter);
      const r = await authFetch(`${BASE}?${p}`);
      if (!r.ok) throw new Error(); return r.json();
    },
    staleTime: 0,
  });

  const overdueQ = useQuery<{ items: FacilityAccess[]; total: number }>({
    queryKey: ["hipaa10-overdue"],
    queryFn: async () => { const r = await authFetch(`${BASE}/overdue`); if (!r.ok) throw new Error(); return r.json(); },
    staleTime: 0,
  });

  const [detailId, setDetailId] = useState<string | null>(null);
  const detailQ = useQuery<FacilityAccess>({
    queryKey: ["hipaa10-detail", detailId],
    queryFn: async () => { const r = await authFetch(`${BASE}/${detailId}`); if (!r.ok) throw new Error(); return r.json(); },
    enabled: !!detailId,
    staleTime: 0,
  });

  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["hipaa10-list"] });
    qc.invalidateQueries({ queryKey: ["hipaa10-summary"] });
    qc.invalidateQueries({ queryKey: ["hipaa10-overdue"] });
    if (detailId) qc.invalidateQueries({ queryKey: ["hipaa10-detail", detailId] });
  };

  const defaultFacilityForm = () => ({
    facilityName: "", facilityType: "Office", facilityAddress: "",
    description: "", ephiSystemsPresent: false,
    physicalAccessControlType: "", accessControlDescription: "",
    authorizedAccessRoles: "", restrictedAreas: "",
    badgeOrKeyControl: false, biometricControl: false, accessLoggingEnabled: false,
    accessReviewFrequency: "Annual", nextAccessReviewDate: "",
    visitorControlEnabled: false, visitorRegistrationRequired: false,
    visitorEscortRequired: false, visitorLogRetention: "",
    powerProtection: false, fireProtection: false,
    temperatureMonitoring: false, waterLeakProtection: false,
    environmentalSafeguards: "", emergencyProcedures: "",
    hostingProvider: "", hostingProviderFacility: "",
    hostingProviderControlsReviewed: false,
    hostingProviderReviewDate: "", hostingProviderEvidenceReference: "",
    status: "Active", notes: "",
  });
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(defaultFacilityForm());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const submitCreate = () => {
    const errs: Record<string, string> = {};
    required(errs, "facilityName", form.facilityName, "Facility Name");
    required(errs, "facilityType", form.facilityType, "Facility Type");
    if (Object.keys(errs).length) {
      setErrors(errs);
      toast({ title: "Please correct the highlighted fields.", variant: "destructive" });
      return;
    }
    setErrors({});
    createMut.mutate();
  };

  const createMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(BASE, {
        method: "POST",
        body: JSON.stringify({
          ...form,
          nextAccessReviewDate: form.nextAccessReviewDate || null,
          hostingProviderReviewDate: form.hostingProviderReviewDate || null,
        }),
      });
      if (!r.ok) { const e = await r.json(); throw new Error(e.message || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => {
      toast({ title: "Facility created." });
      setShowCreate(false); setForm(defaultFacilityForm()); setErrors({}); invalidate();
    },
    onError: (e: Error) => { const serverErrs = extractServerErrors(e); if (serverErrs) { setErrors(serverErrs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); } else toast({ title: "Unable to save", description: e.message, variant: "destructive" }); },
  });

  const [statusId, setStatusId] = useState<string | null>(null);
  const [newStatus, setNewStatus] = useState("Active");
  const statusMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/${statusId}/status`, { method: "PATCH", body: JSON.stringify({ status: newStatus }) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.message || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Status updated." }); setStatusId(null); invalidate(); },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  const [reviewId, setReviewId] = useState<string | null>(null);
  const [reviewForm, setReviewForm] = useState({ reviewDate: "", nextReviewDate: "", frequency: "Annual", notes: "" });
  const reviewMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/${reviewId}/review`, {
        method: "PATCH",
        body: JSON.stringify({
          reviewDate: reviewForm.reviewDate || new Date().toISOString(),
          nextReviewDate: reviewForm.nextReviewDate || null,
          frequency: reviewForm.frequency, notes: reviewForm.notes,
        }),
      });
      if (!r.ok) { const e = await r.json(); throw new Error(e.message || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Review completed." }); setReviewId(null); invalidate(); },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  const [checkId, setCheckId] = useState<string | null>(null);
  const [checkResult, setCheckResult] = useState<any>(null);
  const checkMut = useMutation({
    mutationFn: async (fid: string) => {
      const r = await authFetch(`${BASE}/check`, { method: "POST", body: JSON.stringify({ facilityId: fid }) });
      if (!r.ok) throw new Error("Check failed");
      return r.json();
    },
    onSuccess: (data) => { setCheckResult(data); },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      const r = await authFetch(`${BASE}/${id}`, { method: "DELETE" });
      if (!r.ok) throw new Error("Unable to delete");
    },
    onSuccess: () => { toast({ title: "Facility deleted." }); invalidate(); },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  const s = summaryQ.data;
  const totalPages = Math.ceil((listQ.data?.total ?? 0) / 10);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Facility Access Controls (§ 164.310)</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
      {/* Dashboard */}
      {s && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: "Total Facilities",      value: s.total },
            { label: "Active",                value: s.active },
            { label: "Under Review",          value: s.underReview },
            { label: "With ePHI Systems",     value: s.withEphi },
            { label: "Review Due Soon",       value: s.reviewDueSoon },
            { label: "Review Overdue",        value: s.reviewOverdue },
            { label: "Hosting Reviews Due",   value: s.hostingDue },
            { label: "Visitor Controls Missing", value: s.visitorMissing },
            { label: "Compliant",             value: s.compliant },
            { label: "Needs Attention",       value: s.needsAttention },
          ].map(c => (
            <div key={c.label} className="border rounded p-3 text-center">
              <div className="text-2xl font-bold">{c.value}</div>
              <div className="text-xs text-muted-foreground">{c.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Overdue reviews */}
      {(overdueQ.data?.total ?? 0) > 0 && (
        <div className="border border-destructive/40 rounded p-3 bg-destructive/5">
          <p className="text-sm font-medium text-destructive mb-2">
            {overdueQ.data!.total} facility review{overdueQ.data!.total !== 1 ? "s" : ""} overdue or due within 30 days
          </p>
          <div className="flex flex-wrap gap-2">
            {overdueQ.data!.items.map(f => (
              <Badge key={f.id} variant={f.nextAccessReviewDate && new Date(f.nextAccessReviewDate) < new Date() ? "destructive" : "outline"}>
                {f.facilityName}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Filters + Create */}
      <div className="flex flex-wrap gap-2 items-center">
        <select className="border rounded px-2 py-1 text-sm" value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1); }}>
          <option value="">All Types</option>
          {FACILITY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select className="border rounded px-2 py-1 text-sm" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="">All Statuses</option>
          {FACILITY_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="border rounded px-2 py-1 text-sm" value={reviewFilter} onChange={e => { setReviewFilter(e.target.value); setPage(1); }}>
          <option value="">All Reviews</option>
          <option value="overdue">Overdue</option>
          <option value="dueSoon">Due Soon</option>
          <option value="current">Current</option>
        </select>
        <select className="border rounded px-2 py-1 text-sm" value={ephiFilter} onChange={e => { setEphiFilter(e.target.value); setPage(1); }}>
          <option value="">All ePHI</option>
          <option value="true">ePHI Present</option>
          <option value="false">No ePHI</option>
        </select>
        {canManage && (
          <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2" onClick={() => setShowCreate(true)}><Plus className="h-4 w-4" /> Add Facility</Button>
        )}
      </div>

      {/* Create form */}
      {showCreate && (
        <div className="border rounded p-4 space-y-3 bg-muted/30">
          <h4 className="font-medium">Add Facility</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium">Facility Name *</label>
              <Input className={fieldCls("", errors, "facilityName")} aria-invalid={!!errors.facilityName} placeholder="e.g. Main Data Center" value={form.facilityName}
                onChange={e => { setForm(f => ({ ...f, facilityName: e.target.value })); clearErr(setErrors, "facilityName"); }} />
              {fErr(errors, "facilityName")}
            </div>
            <div>
              <label className="text-xs font-medium">Facility Type *</label>
              <select className={fieldCls("w-full border rounded px-2 py-1.5 text-sm", errors, "facilityType")} aria-invalid={!!errors.facilityType} value={form.facilityType}
                onChange={e => { setForm(f => ({ ...f, facilityType: e.target.value })); clearErr(setErrors, "facilityType"); }}>
                {FACILITY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              {fErr(errors, "facilityType")}
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-medium">Address</label>
              <Input placeholder="Physical address" value={form.facilityAddress}
                onChange={e => setForm(f => ({ ...f, facilityAddress: e.target.value }))} />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-medium">Description</label>
              <textarea className="w-full border rounded px-2 py-1.5 text-sm min-h-[60px]" value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs font-medium">Physical Access Control Type</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.physicalAccessControlType}
                onChange={e => setForm(f => ({ ...f, physicalAccessControlType: e.target.value }))}>
                <option value="">-- Select --</option>
                {["Keycard","PIN","Biometric","Key","None","Mixed"].map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium">Access Review Frequency</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.accessReviewFrequency}
                onChange={e => setForm(f => ({ ...f, accessReviewFrequency: e.target.value }))}>
                {["Monthly","Quarterly","SemiAnnual","Annual","AsNeeded"].map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium">Authorized Access Roles</label>
              <Input placeholder="e.g. IT Admins, Security" value={form.authorizedAccessRoles}
                onChange={e => setForm(f => ({ ...f, authorizedAccessRoles: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs font-medium">Next Access Review Date</label>
              <Input type="date" value={form.nextAccessReviewDate}
                onChange={e => setForm(f => ({ ...f, nextAccessReviewDate: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs font-medium">Visitor Log Retention</label>
              <Input placeholder="e.g. 12 months" value={form.visitorLogRetention}
                onChange={e => setForm(f => ({ ...f, visitorLogRetention: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs font-medium">Hosting Provider</label>
              <Input placeholder="e.g. AWS, Azure, GCP" value={form.hostingProvider}
                onChange={e => setForm(f => ({ ...f, hostingProvider: e.target.value }))} />
            </div>
            {form.hostingProvider && (
              <>
                <div>
                  <label className="text-xs font-medium">Hosting Provider Facility</label>
                  <Input placeholder="e.g. us-east-1" value={form.hostingProviderFacility}
                    onChange={e => setForm(f => ({ ...f, hostingProviderFacility: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs font-medium">Hosting Evidence Reference</label>
                  <Input placeholder="e.g. SOC2-2026" value={form.hostingProviderEvidenceReference}
                    onChange={e => setForm(f => ({ ...f, hostingProviderEvidenceReference: e.target.value }))} />
                </div>
              </>
            )}
            <div>
              <label className="text-xs font-medium">Status</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.status}
                onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                {FACILITY_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          {/* Boolean controls */}
          <div className="flex flex-wrap gap-4 text-sm">
            {([
              ["ephiSystemsPresent","ePHI Systems Present"],
              ["badgeOrKeyControl","Badge/Key Control"],
              ["biometricControl","Biometric Control"],
              ["accessLoggingEnabled","Access Logging"],
              ["visitorControlEnabled","Visitor Controls"],
              ["visitorRegistrationRequired","Visitor Registration Required"],
              ["visitorEscortRequired","Escort Required"],
              ["powerProtection","Power Protection"],
              ["fireProtection","Fire Protection"],
              ["temperatureMonitoring","Temperature Monitoring"],
              ["waterLeakProtection","Water Leak Protection"],
              ["hostingProviderControlsReviewed","Hosting Controls Reviewed"],
            ] as [keyof typeof form, string][]).map(([k, label]) => (
              <label key={k} className="flex items-center gap-1 cursor-pointer">
                <input type="checkbox" checked={form[k] as boolean}
                  onChange={e => setForm(f => ({ ...f, [k]: e.target.checked }))} />
                {label}
              </label>
            ))}
          </div>
          <div>
            <label className="text-xs font-medium">Environmental Safeguards</label>
            <textarea className="w-full border rounded px-2 py-1.5 text-sm min-h-[50px]" value={form.environmentalSafeguards}
              onChange={e => setForm(f => ({ ...f, environmentalSafeguards: e.target.value }))} />
          </div>
          <div>
            <label className="text-xs font-medium">Emergency Procedures</label>
            <textarea className="w-full border rounded px-2 py-1.5 text-sm min-h-[50px]" value={form.emergencyProcedures}
              onChange={e => setForm(f => ({ ...f, emergencyProcedures: e.target.value }))} />
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={submitCreate} disabled={createMut.isPending}>
              {createMut.isPending ? "Saving…" : "Save Facility"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setShowCreate(false); setForm(defaultFacilityForm()); setErrors({}); }}>Cancel</Button>
          </div>
        </div>
      )}

      {/* List */}
      {listQ.isLoading ? <Skeleton className="h-24 w-full" /> : (
        <div className="space-y-2">
          {(listQ.data?.items ?? []).map(f => {
            const rs = facilityReviewStatus(f);
            const hs = facilityHostingStatus(f);
            return (
              <div key={f.id} className="border rounded p-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-medium">{f.facilityName}</p>
                    <p className="text-xs text-muted-foreground">
                      {f.facilityType} {f.facilityAddress ? `· ${f.facilityAddress}` : ""}
                    </p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      <Badge variant={f.status === "Active" ? "default" : f.status === "Inactive" ? "secondary" : "outline"}>
                        {f.status}
                      </Badge>
                      {f.ephiSystemsPresent && <Badge variant="outline">ePHI</Badge>}
                      <Badge variant={rs.variant}>Review: {rs.label}</Badge>
                      {f.hostingProvider && <Badge variant={hs === "Reviewed" ? "default" : hs === "Overdue" ? "destructive" : "outline"}>
                        {f.hostingProvider}: {hs}
                      </Badge>}
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button size="sm" variant="outline" onClick={() => setDetailId(f.id)}>View</Button>
                    {canManage && <>
                      <Button size="sm" variant="outline" onClick={() => { setStatusId(f.id); setNewStatus(f.status); }}>Status</Button>
                      <Button size="sm" variant="outline" onClick={() => { setReviewId(f.id); }}>Review</Button>
                      <Button size="sm" variant="outline" onClick={() => { setCheckId(f.id); checkMut.mutate(f.id); }}>Check</Button>
                      <Button size="sm" variant="destructive" onClick={() => { if (confirm(`Delete "${f.facilityName}"?`)) deleteMut.mutate(f.id); }}>Del</Button>
                    </>}
                  </div>
                </div>
              </div>
            );
          })}
          {(listQ.data?.total ?? 0) === 0 && !listQ.isLoading && (
            <p className="text-sm text-muted-foreground text-center py-4">No facilities recorded.</p>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex gap-2 justify-center">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
          <span className="text-sm py-1">{page} / {totalPages}</span>
          <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
        </div>
      )}

      {/* Detail modal */}
      {detailId && detailQ.data && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 space-y-4">
            <div className="flex justify-between items-start">
              <h4 className="font-semibold text-lg">{detailQ.data.facilityName}</h4>
              <Button size="sm" variant="outline" onClick={() => setDetailId(null)}>✕</Button>
            </div>
            {(() => {
              const d = detailQ.data!;
              const sections = [
                { title: "Facility Information", rows: [
                  ["Type", d.facilityType], ["Address", d.facilityAddress], ["Description", d.description],
                  ["ePHI Systems Present", d.ephiSystemsPresent ? "Yes" : "No"],
                ]},
                { title: "Physical Access Controls", rows: [
                  ["Control Type", d.physicalAccessControlType], ["Description", d.accessControlDescription],
                  ["Badge/Key Control", d.badgeOrKeyControl ? "Yes" : "No"],
                  ["Biometric Control", d.biometricControl ? "Yes" : "No"],
                  ["Access Logging", d.accessLoggingEnabled ? "Enabled" : "Disabled"],
                ]},
                { title: "Authorized Roles & Restricted Areas", rows: [
                  ["Authorized Roles", d.authorizedAccessRoles], ["Restricted Areas", d.restrictedAreas],
                ]},
                { title: "Access Review", rows: [
                  ["Frequency", d.accessReviewFrequency],
                  ["Last Review", d.lastAccessReviewDate ? new Date(d.lastAccessReviewDate).toLocaleDateString() : "—"],
                  ["Next Review", d.nextAccessReviewDate ? new Date(d.nextAccessReviewDate).toLocaleDateString() : "—"],
                  ["Review Notes", d.accessReviewNotes],
                ]},
                { title: "Visitor Controls", rows: [
                  ["Controls Enabled", d.visitorControlEnabled ? "Yes" : "No"],
                  ["Registration Required", d.visitorRegistrationRequired ? "Yes" : "No"],
                  ["Escort Required", d.visitorEscortRequired ? "Yes" : "No"],
                  ["Log Retention", d.visitorLogRetention],
                ]},
                { title: "Environmental Safeguards", rows: [
                  ["Power Protection", d.powerProtection ? "Yes" : "No"],
                  ["Fire Protection", d.fireProtection ? "Yes" : "No"],
                  ["Temperature Monitoring", d.temperatureMonitoring ? "Yes" : "No"],
                  ["Water Leak Protection", d.waterLeakProtection ? "Yes" : "No"],
                  ["Safeguards Description", d.environmentalSafeguards],
                  ["Emergency Procedures", d.emergencyProcedures],
                ]},
                { title: "Hosting Provider Controls", rows: [
                  ["Provider", d.hostingProvider],
                  ["Facility", d.hostingProviderFacility],
                  ["Controls Reviewed", d.hostingProviderControlsReviewed ? "Yes" : "No"],
                  ["Review Date", d.hostingProviderReviewDate ? new Date(d.hostingProviderReviewDate).toLocaleDateString() : "—"],
                  ["Evidence Reference", d.hostingProviderEvidenceReference],
                ]},
              ];
              return sections.map(sec => (
                <div key={sec.title}>
                  <h5 className="font-medium text-sm border-b pb-1 mb-2">{sec.title}</h5>
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                    {sec.rows.filter(([, v]) => v !== undefined && v !== null && v !== "").map(([k, v]) => (
                      <React.Fragment key={k as string}>
                        <dt className="text-muted-foreground">{k}</dt>
                        <dd>{String(v)}</dd>
                      </React.Fragment>
                    ))}
                  </dl>
                </div>
              ));
            })()}
            {detailQ.data && (
              <div className="text-xs text-muted-foreground">
                Created: {new Date(detailQ.data.createdAt).toLocaleDateString()} · Updated: {new Date(detailQ.data.updatedAt).toLocaleDateString()}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Status modal */}
      {statusId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-lg p-6 space-y-4 w-80">
            <h4 className="font-medium">Change Status</h4>
            <select className="w-full border rounded px-2 py-1.5 text-sm" value={newStatus}
              onChange={e => setNewStatus(e.target.value)}>
              {FACILITY_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => statusMut.mutate()} disabled={statusMut.isPending}>
                {statusMut.isPending ? "Saving…" : "Update Status"}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setStatusId(null)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* Review modal */}
      {reviewId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-lg p-6 space-y-3 w-96">
            <h4 className="font-medium">Complete Access Review</h4>
            <div>
              <label className="text-xs font-medium">Review Date *</label>
              <Input type="date" value={reviewForm.reviewDate}
                onChange={e => setReviewForm(f => ({ ...f, reviewDate: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs font-medium">Next Review Date</label>
              <Input type="date" value={reviewForm.nextReviewDate}
                onChange={e => setReviewForm(f => ({ ...f, nextReviewDate: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs font-medium">Frequency</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={reviewForm.frequency}
                onChange={e => setReviewForm(f => ({ ...f, frequency: e.target.value }))}>
                {["Monthly","Quarterly","SemiAnnual","Annual","AsNeeded"].map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium">Notes</label>
              <textarea className="w-full border rounded px-2 py-1.5 text-sm min-h-[60px]" value={reviewForm.notes}
                onChange={e => setReviewForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => reviewMut.mutate()} disabled={reviewMut.isPending}>
                {reviewMut.isPending ? "Saving…" : "Complete Review"}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setReviewId(null)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* Compliance check result */}
      {checkResult && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-lg p-6 space-y-4 max-w-md w-full">
            <div className="flex justify-between items-center">
              <h4 className="font-medium">Compliance Check: {checkResult.facilityName}</h4>
              <Button size="sm" variant="outline" onClick={() => setCheckResult(null)}>✕</Button>
            </div>
            <div className={`text-lg font-bold ${checkResult.compliant ? "text-green-600" : "text-red-600"}`}>
              {checkResult.compliant ? "✓ Compliant" : "✗ Needs Attention"}
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div><span className="text-muted-foreground">Review Status:</span> {checkResult.reviewStatus}</div>
              <div><span className="text-muted-foreground">Hosting Review:</span> {checkResult.hostingReviewStatus}</div>
            </div>
            {checkResult.failedControls?.length > 0 && (
              <div>
                <p className="text-sm font-medium text-destructive mb-1">Failed Controls</p>
                <ul className="list-disc list-inside text-sm space-y-1">
                  {checkResult.failedControls.map((c: string) => <li key={c} className="text-destructive">{c}</li>)}
                </ul>
              </div>
            )}
            {checkResult.warningControls?.length > 0 && (
              <div>
                <p className="text-sm font-medium text-orange-600 mb-1">Warnings</p>
                <ul className="list-disc list-inside text-sm space-y-1">
                  {checkResult.warningControls.map((c: string) => <li key={c} className="text-orange-600">{c}</li>)}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
      </CardContent>
    </Card>
  );
}

// ── HIPAA-23 — Breach Notification (§ 164.400–414) ───────────────────────────

type BreachSummary23 = {
  total: number; openAssessments: number; highRisk: number;
  notificationRequired: number; notificationsPending: number;
  notificationsCompleted: number; overdueNotifications: number;
  totalIndividualsAffected: number; averageResolutionDays: number;
  compliancePercentage: number;
};

type BreachRecord23 = {
  id: string; organizationId: string;
  breachNumber: string; incidentId?: string; incidentReference?: string;
  discoveryDate: string; reportedDate?: string; discoverySource?: string;
  breachType?: string; affectedIndividualsCount: number;
  phiDataCategories?: string; individualsAffectedDescription?: string;
  riskAssessmentStatus: string; riskAssessmentDate?: string;
  riskLevel?: string; riskAssessmentSummary?: string;
  probabilityOfCompromise?: string; mitigationActionsTaken?: string;
  notificationRequired: boolean; notificationDecisionReason?: string;
  notificationStatus: string; individualNotificationDate?: string;
  hhsNotificationRequired: boolean; hhsNotificationDate?: string;
  mediaNotificationRequired: boolean; mediaNotificationDate?: string;
  businessAssociateInvolved: boolean; businessAssociateName?: string;
  businessAssociateNotificationDate?: string;
  correctiveActions?: string; resolutionDate?: string;
  status: string; assignedReviewer?: string; reviewerNotes?: string;
  createdAt: string; updatedAt: string;
  assessmentOverdue: boolean; notificationOverdue: boolean;
  daysSinceDiscovery: number; daysUntilDeadline?: number;
};

const BREACH23_TYPES       = ["Unauthorized Access","Unauthorized Disclosure","Data Loss","Theft","System Compromise","Other"];
const BREACH23_STATUSES    = ["Open","Assessing","PendingNotification","NotificationInProgress","Completed","Closed"];
const BREACH23_RISK_LEVELS = ["Low","Medium","High","Critical"];
const BREACH23_NOTIF_STATUSES = ["Pending","Required","Prepared","Sent","Completed","NotRequired"];
const BREACH23_PROBABILITY = ["Low","Moderate","High","VeryHigh"];
const BREACH23_SOURCES     = ["Employee Report","IT Monitoring","Patient Complaint","External Report","Other"];

function breach23StatusVariant(s: string): "default"|"secondary"|"outline"|"destructive" {
  if (s === "Completed" || s === "Closed") return "default";
  if (s === "PendingNotification" || s === "NotificationInProgress") return "secondary";
  if (s === "Open" || s === "Assessing") return "outline";
  return "destructive";
}

function breach23RiskColor(r?: string) {
  if (r === "Critical") return "text-red-700 font-bold";
  if (r === "High")     return "text-red-600 font-semibold";
  if (r === "Medium")   return "text-amber-700";
  return "text-muted-foreground";
}

function breach23NotifVariant(s: string): "default"|"secondary"|"outline"|"destructive" {
  if (s === "Completed") return "default";
  if (s === "NotRequired") return "secondary";
  if (s === "Sent" || s === "Prepared") return "outline";
  if (s === "Required") return "destructive";
  return "outline";
}

// ══════════════════════════════════════════════════════════════════════════════
// HIPAA-41 — Secure Coding: Content Security Policy (CSP) Headers
// ══════════════════════════════════════════════════════════════════════════════

interface Hipaa41Summary {
  total: number; cspEnabled: number; enforcedPolicies: number; nonCompliant: number;
  unsafeInlineRisks: number; unsafeEvalRisks: number; missingHeaders: number;
  pendingReviews: number; compliancePercentage: number;
}

interface Hipaa41Control {
  id: string; organizationId: string;
  applicationName: string; moduleName?: string; environment: string;
  cspEnabled: boolean; enforcementMode?: string; policyVersion?: string;
  defaultSrcConfigured: boolean; scriptSrcConfigured: boolean; styleSrcConfigured: boolean;
  imageSrcConfigured: boolean; connectSrcConfigured: boolean; frameProtectionConfigured: boolean;
  unsafeInlineAllowed: boolean; unsafeEvalAllowed: boolean; nonceHashProtectionEnabled: boolean;
  securityHeadersConfigured: boolean; reportUriConfigured: boolean; violationMonitoringEnabled: boolean;
  lastPolicyReviewDate?: string; nextReviewDate?: string;
  reviewerName?: string; reviewerTitle?: string; reviewNotes?: string;
  evidenceReference?: string; complianceStatus: string; riskLevel?: string;
  remediationRequired: boolean; remediationNotes?: string; lastValidationDate?: string;
  createdAt: string; updatedAt: string;
  reviewOverdue: boolean; validationCodes: string[];
}

function useAuth41() {
  return (url: string, opts: RequestInit = {}) => {
    const token = sessionStorage.getItem("dotnetbot.jwt.session") || localStorage.getItem("dotnetbot.jwt") || "";
    return fetch(url, { ...opts, headers: { ...(opts.headers as Record<string,string>||{}), "Content-Type": "application/json", Authorization: `Bearer ${token}` } });
  };
}

const H41_ENVIRONMENTS  = ["Development","Test","Production"];
const H41_CSP_MODES     = ["ReportOnly","Enforced"];
const H41_RISK_LEVELS   = ["Low","Medium","High","Critical"];
const H41_STATUSES      = ["Compliant","NonCompliant","Pending","UnderReview"];

const H41_CODE_LABELS: Record<string, string> = {
  CSP_REQUIRED:              "Content Security Policy not enabled",
  CSP_ENFORCEMENT_REQUIRED:  "CSP in report-only mode in Production (must be enforced)",
  DEFAULT_SRC_REQUIRED:      "default-src directive not configured",
  UNSAFE_INLINE_RISK:        "unsafe-inline allowed (XSS risk)",
  UNSAFE_EVAL_RISK:          "unsafe-eval allowed (code injection risk)",
  SECURITY_HEADERS_REQUIRED: "Security headers not configured",
  CSP_EVIDENCE_REQUIRED:     "No evidence reference",
  CSP_REVIEW_OVERDUE:        "CSP policy review overdue",
};

function h41StatusBadge(s: string): "default"|"secondary"|"outline"|"destructive" {
  if (s === "Compliant")    return "default";
  if (s === "NonCompliant") return "destructive";
  if (s === "UnderReview")  return "secondary";
  return "outline";
}

const H41_BLANK = {
  applicationName: "", moduleName: "", environment: "Production",
  cspEnabled: false, enforcementMode: "ReportOnly", policyVersion: "",
  defaultSrcConfigured: false, scriptSrcConfigured: false, styleSrcConfigured: false,
  imageSrcConfigured: false, connectSrcConfigured: false, frameProtectionConfigured: false,
  unsafeInlineAllowed: false, unsafeEvalAllowed: false, nonceHashProtectionEnabled: false,
  securityHeadersConfigured: false, reportUriConfigured: false, violationMonitoringEnabled: false,
  lastPolicyReviewDate: "", nextReviewDate: "",
  reviewerName: "", reviewerTitle: "", evidenceReference: "", riskLevel: "Medium",
  // optional knowledge-source link
  createSourceId: "", createSourceNotes: "",
};

function HipaaCspSecuritySection({ canManage }: { canManage: boolean }) {
  const authFetch = useAuth41();
  const qc = useQueryClient();
  const { toast } = useToast();
  const BASE = "/api/organization/compliance/hipaa/csp-security-controls";

  const summaryQ = useQuery<Hipaa41Summary>({
    queryKey: ["hipaa41-summary"],
    queryFn: () => authFetch(`${BASE}/summary`).then(r => r.json()),
  });

  const [fApp,  setFApp]  = useState("");
  const [fEnv,  setFEnv]  = useState("");
  const [fStatus,setFStatus]=useState("");
  const [fRisk, setFRisk] = useState("");
  const [fMode, setFMode] = useState("");

  const listQ = useQuery<Hipaa41Control[]>({
    queryKey: ["hipaa41-list", fApp, fEnv, fStatus, fRisk, fMode],
    queryFn: () => {
      const p = new URLSearchParams();
      if (fApp)    p.set("application", fApp);
      if (fEnv)    p.set("environment", fEnv);
      if (fStatus) p.set("status", fStatus);
      if (fRisk)   p.set("riskLevel", fRisk);
      if (fMode)   p.set("cspMode", fMode);
      return authFetch(`${BASE}?${p}`).then(r => r.json());
    },
  });

  const [showCreate,    setShowCreate]    = useState(false);
  const [showDetail,    setShowDetail]    = useState(false);
  const [showReview,    setShowReview]    = useState(false);
  const [showRemediate, setShowRemediate] = useState(false);
  const [showDelete,    setShowDelete]    = useState(false);
  const [selected,      setSelected]      = useState<Hipaa41Control|null>(null);

  const [createForm, setCreateForm] = useState({ ...H41_BLANK });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const setCF = (k: string, v: unknown) => setCreateForm(f => ({ ...f, [k]: v }));

  const [reviewForm,    setReviewForm]    = useState({ reviewerName:"", reviewerTitle:"", reviewNotes:"", nextReviewDate:"", evidenceReference:"" });
  const [reviewErrors,  setReviewErrors]  = useState<Record<string,string>>({});
  const [remediateForm, setRemediateForm] = useState({ remediationNotes:"" });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["hipaa41-summary"] });
    qc.invalidateQueries({ queryKey: ["hipaa41-list"] });
  };

  const openDetail = (c: Hipaa41Control) => { setSelected(c); setShowDetail(true); };

  const createM = useMutation({
    mutationFn: () => authFetch(BASE, { method: "POST", body: JSON.stringify({
      applicationName: createForm.applicationName,
      moduleName: createForm.moduleName || null,
      environment: createForm.environment,
      cspEnabled: createForm.cspEnabled,
      enforcementMode: createForm.enforcementMode || null,
      policyVersion: createForm.policyVersion || null,
      defaultSrcConfigured: createForm.defaultSrcConfigured,
      scriptSrcConfigured: createForm.scriptSrcConfigured,
      styleSrcConfigured: createForm.styleSrcConfigured,
      imageSrcConfigured: createForm.imageSrcConfigured,
      connectSrcConfigured: createForm.connectSrcConfigured,
      frameProtectionConfigured: createForm.frameProtectionConfigured,
      unsafeInlineAllowed: createForm.unsafeInlineAllowed,
      unsafeEvalAllowed: createForm.unsafeEvalAllowed,
      nonceHashProtectionEnabled: createForm.nonceHashProtectionEnabled,
      securityHeadersConfigured: createForm.securityHeadersConfigured,
      reportUriConfigured: createForm.reportUriConfigured,
      violationMonitoringEnabled: createForm.violationMonitoringEnabled,
      lastPolicyReviewDate: createForm.lastPolicyReviewDate || null,
      nextReviewDate: createForm.nextReviewDate || null,
      reviewerName: createForm.reviewerName || null,
      reviewerTitle: createForm.reviewerTitle || null,
      evidenceReference: createForm.evidenceReference || null,
      riskLevel: createForm.riskLevel || null,
    })}).then(async r => { const d = await r.json(); if (!r.ok) throw d; return d; }),
    onSuccess: async (d: any) => {
      // Optionally link the new control to a knowledge source.
      if (createForm.createSourceId) {
        try {
          await authFetch("/api/organization/compliance/hipaa/control-source-links", {
            method: "PUT",
            body: JSON.stringify({ entityType: "CspSecurityControl", entityId: d.id, knowledgeSourceId: createForm.createSourceId, notes: createForm.createSourceNotes || null }),
          });
        } catch { /* non-fatal — control was created; link can be added from detail view */ }
      }
      invalidate(); setShowCreate(false); setErrors({}); setCreateForm({ ...H41_BLANK }); toast({ title: "CSP control added" });
    },
    onError: (e: any) => {
      const serverErrs = extractServerErrors(e);
      if (serverErrs && Object.keys(serverErrs).length > 0) {
        setErrors(serverErrs);
        toast({ title: "Please correct the highlighted fields.", variant: "destructive" });
      } else {
        toast({ title: "Unable to save", description: e?.message ?? "An unexpected error occurred.", variant: "destructive" });
      }
    },
  });

  const validateM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}/validate`, { method: "POST", body: "{}" }).then(async r => { const d = await r.json(); if (!r.ok) throw d; return d; }),
    onSuccess: (d: any) => { invalidate(); toast({ title: d.isCompliant ? "✓ Compliant" : "Issues detected", description: d.validationCodes?.join(", ") || undefined }); },
    onError: (e: any) => toast({ title: "Validation error", description: e?.error, variant: "destructive" }),
  });

  const reviewM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}/review`, { method: "POST", body: JSON.stringify({
      reviewerName: reviewForm.reviewerName,
      reviewerTitle: reviewForm.reviewerTitle || null,
      reviewNotes: reviewForm.reviewNotes || null,
      nextReviewDate: reviewForm.nextReviewDate || null,
      evidenceReference: reviewForm.evidenceReference || null,
    })}).then(async r => { const d = await r.json(); if (!r.ok) throw d; return d; }),
    onSuccess: (d: any) => { invalidate(); setSelected(d); setShowReview(false); setReviewErrors({}); toast({ title: "Review completed" }); },
    onError: (e: any) => { const s = extractServerErrors(e); if (s && Object.keys(s).length) { setReviewErrors(s); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); } else toast({ title: "Unable to complete action", description: e?.message, variant: "destructive" }); },
  });

  const remediateM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}/remediation`, { method: "POST", body: JSON.stringify({ remediationNotes: remediateForm.remediationNotes }) }).then(async r => { const d = await r.json(); if (!r.ok) throw d; return d; }),
    onSuccess: (d: any) => { invalidate(); setSelected(d); setShowRemediate(false); toast({ title: "Remediation recorded" }); },
    onError: (e: any) => toast({ title: "Unable to complete action", description: e?.message, variant: "destructive" }),
  });

  const deleteM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}`, { method: "DELETE" }).then(async r => { if (!r.ok) { const d = await r.json(); throw d; } }),
    onSuccess: () => { invalidate(); setShowDelete(false); setShowDetail(false); setSelected(null); toast({ title: "Control deleted" }); },
    onError: (e: any) => toast({ title: "Unable to delete", description: e?.message, variant: "destructive" }),
  });

  const sum   = summaryQ.data;
  const items = listQ.data ?? [];
  const issueItems = items.filter(c => c.validationCodes.length > 0);

  const CspCheck = ({ label, ok, warn }: { label: string; ok: boolean; warn?: boolean }) => (
    <div className="flex items-center justify-between py-0.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={`text-xs font-medium ${ok ? "text-green-600" : warn ? "text-amber-600" : "text-red-600"}`}>{ok ? "✓" : "✗"}</span>
    </div>
  );

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base font-semibold">Content Security Policy (CSP) Controls</CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">Track CSP enforcement mode, directive configuration, unsafe allowances, and security headers across application environments to reduce XSS and unauthorized script execution risks.</p>
            </div>
            {canManage && (
              <Button size="sm" onClick={() => { setCreateForm({ ...H41_BLANK }); setShowCreate(true); }}>+ Add Control</Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">

          {/* Summary metrics */}
          {sum && (
            <>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {[
                  { label: "Total Applications",  value: sum.total,             color: "" },
                  { label: "CSP Enabled",         value: sum.cspEnabled,        color: sum.cspEnabled === sum.total && sum.total > 0 ? "text-green-600" : "" },
                  { label: "Enforced Policies",   value: sum.enforcedPolicies,  color: "" },
                  { label: "Non-Compliant",       value: sum.nonCompliant,      color: sum.nonCompliant > 0 ? "text-red-600" : "" },
                  { label: "Unsafe Inline Risks", value: sum.unsafeInlineRisks, color: sum.unsafeInlineRisks > 0 ? "text-red-600" : "" },
                  { label: "Unsafe Eval Risks",   value: sum.unsafeEvalRisks,   color: sum.unsafeEvalRisks > 0 ? "text-red-600" : "" },
                  { label: "Missing Headers",     value: sum.missingHeaders,    color: sum.missingHeaders > 0 ? "text-amber-600" : "" },
                  { label: "Pending Reviews",     value: sum.pendingReviews,    color: sum.pendingReviews > 0 ? "text-amber-600" : "" },
                ].map(m => (
                  <div key={m.label} className="rounded border p-2 text-center">
                    <div className={`text-xl font-bold ${m.color}`}>{m.value}</div>
                    <div className="text-xs text-muted-foreground leading-tight">{m.label}</div>
                  </div>
                ))}
              </div>
              {sum.total > 0 && (
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span>Compliance</span>
                    <span className={sum.compliancePercentage === 100 ? "text-green-600 font-semibold" : "text-amber-600"}>{sum.compliancePercentage}%</span>
                  </div>
                  <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${sum.compliancePercentage === 100 ? "bg-green-500" : "bg-amber-500"}`} style={{ width: `${sum.compliancePercentage}%` }} />
                  </div>
                </div>
              )}
            </>
          )}

          {/* Issues panel */}
          {issueItems.length > 0 && (
            <div className="rounded border border-red-200 bg-red-50 p-3">
              <p className="text-xs font-semibold text-red-800 mb-1">Applications with CSP security issues ({issueItems.length})</p>
              <ul className="space-y-1">
                {issueItems.slice(0, 5).map(c => (
                  <li key={c.id} className="text-xs text-red-700 flex justify-between items-start gap-2">
                    <button className="underline hover:no-underline text-left" onClick={() => openDetail(c)}>
                      {c.applicationName}{c.moduleName ? ` / ${c.moduleName}` : ""} <span className="text-muted-foreground">({c.environment})</span>
                    </button>
                    <div className="flex flex-wrap gap-1 justify-end shrink-0">
                      {c.validationCodes.slice(0, 2).map(code => (
                        <span key={code} className="font-mono bg-red-100 rounded px-1 text-xs">{code.replace(/_/g," ")}</span>
                      ))}
                      {c.validationCodes.length > 2 && <span>+{c.validationCodes.length-2}</span>}
                    </div>
                  </li>
                ))}
                {issueItems.length > 5 && <li className="text-xs text-red-600">+{issueItems.length - 5} more…</li>}
              </ul>
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-wrap gap-2">
            <input className="border rounded px-2 py-1 text-xs w-40" placeholder="Filter by application…" value={fApp} onChange={e => setFApp(e.target.value)} />
            <select className="border rounded px-2 py-1 text-xs" value={fEnv} onChange={e => setFEnv(e.target.value)}>
              <option value="">All environments</option>
              {H41_ENVIRONMENTS.map(e => <option key={e} value={e}>{e}</option>)}
            </select>
            <select className="border rounded px-2 py-1 text-xs" value={fStatus} onChange={e => setFStatus(e.target.value)}>
              <option value="">All statuses</option>
              {H41_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select className="border rounded px-2 py-1 text-xs" value={fRisk} onChange={e => setFRisk(e.target.value)}>
              <option value="">All risk levels</option>
              {H41_RISK_LEVELS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <select className="border rounded px-2 py-1 text-xs" value={fMode} onChange={e => setFMode(e.target.value)}>
              <option value="">All CSP modes</option>
              {H41_CSP_MODES.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            {(fApp||fEnv||fStatus||fRisk||fMode) && (
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setFApp(""); setFEnv(""); setFStatus(""); setFRisk(""); setFMode(""); }}>Clear</Button>
            )}
          </div>

          {/* Control list */}
          {listQ.isLoading ? (
            <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-14 w-full" />)}</div>
          ) : items.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-4">No CSP controls defined. {canManage && "Add a control to start tracking."}</p>
          ) : (
            <div className="divide-y rounded border">
              {items.map(c => (
                <div key={c.id} className="flex items-start justify-between p-3 hover:bg-muted/30 cursor-pointer" onClick={() => openDetail(c)}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">{c.applicationName}</span>
                      {c.moduleName && <span className="text-xs text-muted-foreground">/ {c.moduleName}</span>}
                      <Badge variant="outline" className="text-xs">{c.environment}</Badge>
                      <Badge variant={h41StatusBadge(c.complianceStatus)} className="text-xs">{c.complianceStatus}</Badge>
                      {c.enforcementMode && <Badge variant={c.enforcementMode === "Enforced" ? "default" : "secondary"} className="text-xs">{c.enforcementMode}</Badge>}
                      {c.reviewOverdue && <Badge variant="outline" className="text-xs text-red-600">Review Overdue</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 flex gap-3 flex-wrap">
                      {!c.cspEnabled && <span className="text-red-600">⚠ CSP disabled</span>}
                      {c.cspEnabled && c.enforcementMode === "ReportOnly" && c.environment === "Production" && <span className="text-amber-600">⚠ report-only in prod</span>}
                      {c.unsafeInlineAllowed && <span className="text-red-600">⚠ unsafe-inline</span>}
                      {c.unsafeEvalAllowed && <span className="text-red-600">⚠ unsafe-eval</span>}
                      {!c.securityHeadersConfigured && <span className="text-amber-600">⚠ missing headers</span>}
                      {c.policyVersion && <span>v{c.policyVersion}</span>}
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground shrink-0 ml-2 text-right">
                    {c.validationCodes.length > 0 && <div className="text-red-500">{c.validationCodes.length} issue{c.validationCodes.length>1?"s":""}</div>}
                    {c.riskLevel && <div>{c.riskLevel} risk</div>}
                    <div>{c.updatedAt.slice(0,10)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Add CSP Control ───────────────────────────────────────────────── */}
      <Dialog open={showCreate} onOpenChange={v => { setShowCreate(v); if (!v) setErrors({}); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add CSP Security Control</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Application Name *</label>
                <input className={fieldCls("w-full border rounded px-2 py-1 text-sm", errors, "applicationName")} aria-invalid={!!errors.applicationName} value={createForm.applicationName} onChange={e => { setCF("applicationName", e.target.value); clearErr(setErrors, "applicationName"); }} placeholder="e.g. Patient Portal" />
                {fErr(errors, "applicationName")}
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Module / Component</label>
                <input className={fieldCls("w-full border rounded px-2 py-1 text-sm", errors, "moduleName")} aria-invalid={!!errors.moduleName} value={createForm.moduleName} onChange={e => { setCF("moduleName", e.target.value); clearErr(setErrors, "moduleName"); }} placeholder="e.g. API Gateway" />
                {fErr(errors, "moduleName")}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Environment</label>
                <select className="w-full border rounded px-2 py-1 text-sm" value={createForm.environment} onChange={e => setCF("environment", e.target.value)}>
                  {H41_ENVIRONMENTS.map(e => <option key={e} value={e}>{e}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Enforcement Mode</label>
                <select className="w-full border rounded px-2 py-1 text-sm" value={createForm.enforcementMode} onChange={e => setCF("enforcementMode", e.target.value)}>
                  <option value="">— select —</option>
                  {H41_CSP_MODES.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Policy Version</label>
                <input className="w-full border rounded px-2 py-1 text-sm" value={createForm.policyVersion} onChange={e => setCF("policyVersion", e.target.value)} placeholder="v2.1" />
              </div>
            </div>

            <fieldset className="border rounded p-3 space-y-2">
              <legend className="text-xs font-semibold px-1">CSP Status</legend>
              <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={createForm.cspEnabled} onChange={e => setCF("cspEnabled", e.target.checked)} /> CSP header is enabled and delivered to browsers</label>
            </fieldset>

            <fieldset className="border rounded p-3 space-y-2">
              <legend className="text-xs font-semibold px-1">Policy Directives Configured</legend>
              <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={createForm.defaultSrcConfigured} onChange={e => setCF("defaultSrcConfigured", e.target.checked)} /> <code className="text-xs">default-src</code> configured</label>
              <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={createForm.scriptSrcConfigured} onChange={e => setCF("scriptSrcConfigured", e.target.checked)} /> <code className="text-xs">script-src</code> configured</label>
              <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={createForm.styleSrcConfigured} onChange={e => setCF("styleSrcConfigured", e.target.checked)} /> <code className="text-xs">style-src</code> configured</label>
              <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={createForm.imageSrcConfigured} onChange={e => setCF("imageSrcConfigured", e.target.checked)} /> <code className="text-xs">img-src</code> configured</label>
              <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={createForm.connectSrcConfigured} onChange={e => setCF("connectSrcConfigured", e.target.checked)} /> <code className="text-xs">connect-src</code> configured</label>
              <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={createForm.frameProtectionConfigured} onChange={e => setCF("frameProtectionConfigured", e.target.checked)} /> Frame protection (<code className="text-xs">frame-ancestors</code> / <code className="text-xs">X-Frame-Options</code>) configured</label>
            </fieldset>

            <fieldset className="border rounded p-3 space-y-2">
              <legend className="text-xs font-semibold px-1">Risk Flags</legend>
              <label className="flex items-center gap-2 text-xs cursor-pointer text-amber-700"><input type="checkbox" checked={createForm.unsafeInlineAllowed} onChange={e => setCF("unsafeInlineAllowed", e.target.checked)} /> <code className="text-xs">'unsafe-inline'</code> allowed (security risk)</label>
              <label className="flex items-center gap-2 text-xs cursor-pointer text-amber-700"><input type="checkbox" checked={createForm.unsafeEvalAllowed} onChange={e => setCF("unsafeEvalAllowed", e.target.checked)} /> <code className="text-xs">'unsafe-eval'</code> allowed (security risk)</label>
              <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={createForm.nonceHashProtectionEnabled} onChange={e => setCF("nonceHashProtectionEnabled", e.target.checked)} /> Nonce / hash-based protection enabled (mitigates unsafe-inline risk)</label>
            </fieldset>

            <fieldset className="border rounded p-3 space-y-2">
              <legend className="text-xs font-semibold px-1">Security Headers &amp; Monitoring</legend>
              <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={createForm.securityHeadersConfigured} onChange={e => setCF("securityHeadersConfigured", e.target.checked)} /> Security headers configured (HSTS, X-Content-Type-Options, Referrer-Policy)</label>
              <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={createForm.reportUriConfigured} onChange={e => setCF("reportUriConfigured", e.target.checked)} /> <code className="text-xs">report-uri</code> / <code className="text-xs">report-to</code> endpoint configured</label>
              <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={createForm.violationMonitoringEnabled} onChange={e => setCF("violationMonitoringEnabled", e.target.checked)} /> CSP violation monitoring and alerting enabled</label>
            </fieldset>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Last Policy Review Date</label>
                <input type="date" className="w-full border rounded px-2 py-1 text-sm" value={createForm.lastPolicyReviewDate} onChange={e => setCF("lastPolicyReviewDate", e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Next Review Date</label>
                <input type="date" className="w-full border rounded px-2 py-1 text-sm" value={createForm.nextReviewDate} onChange={e => setCF("nextReviewDate", e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Reviewer Name</label>
                <input className="w-full border rounded px-2 py-1 text-sm" value={createForm.reviewerName} onChange={e => setCF("reviewerName", e.target.value)} placeholder="Security Engineer" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Reviewer Title</label>
                <input className="w-full border rounded px-2 py-1 text-sm" value={createForm.reviewerTitle} onChange={e => setCF("reviewerTitle", e.target.value)} placeholder="AppSec Lead" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Risk Level</label>
                <select className="w-full border rounded px-2 py-1 text-sm" value={createForm.riskLevel} onChange={e => setCF("riskLevel", e.target.value)}>
                  {H41_RISK_LEVELS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Evidence Reference</label>
                <input className="w-full border rounded px-2 py-1 text-sm" value={createForm.evidenceReference} onChange={e => setCF("evidenceReference", e.target.value)} placeholder="evidence://csp-audit-2026.pdf" />
              </div>
            </div>
            {/* Optional knowledge-source association */}
            <SourceReferenceField
              value={createForm.createSourceId}
              notes={createForm.createSourceNotes}
              onChangeSourceId={id => setCF("createSourceId", id)}
              onChangeNotes={n => setCF("createSourceNotes", n)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreate(false); setErrors({}); }}>Cancel</Button>
            <Button onClick={() => {
              const errs: Record<string, string> = {};
              required(errs, "applicationName", createForm.applicationName, "Application Name");
              required(errs, "moduleName", createForm.moduleName, "Module / Component");
              if (Object.keys(errs).length > 0) {
                setErrors(errs);
                toast({ title: "Please correct the highlighted fields.", variant: "destructive" });
                return;
              }
              setErrors({});
              createM.mutate();
            }} disabled={createM.isPending}>{createM.isPending ? "Saving…" : "Add Control"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Detail ────────────────────────────────────────────────────────── */}
      {selected && showDetail && (
        <Dialog open={showDetail} onOpenChange={v => { setShowDetail(v); if (!v) setSelected(null); }}>
          <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 flex-wrap">
                {selected.applicationName}
                {selected.moduleName && <span className="text-xs font-normal text-muted-foreground">/ {selected.moduleName}</span>}
                <Badge variant="outline" className="text-xs">{selected.environment}</Badge>
                <Badge variant={h41StatusBadge(selected.complianceStatus)}>{selected.complianceStatus}</Badge>
                {selected.enforcementMode && <Badge variant={selected.enforcementMode === "Enforced" ? "default" : "secondary"} className="text-xs">{selected.enforcementMode}</Badge>}
                {selected.reviewOverdue && <Badge variant="outline" className="text-xs text-red-600">Review Overdue</Badge>}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2 text-xs">
                {selected.policyVersion && <div><p className="font-medium">Policy Version</p><p className="text-muted-foreground">{selected.policyVersion}</p></div>}
                {selected.riskLevel && <div><p className="font-medium">Risk Level</p><p className="text-muted-foreground">{selected.riskLevel}</p></div>}
              </div>

              {/* CSP Status */}
              <div className="rounded border p-2">
                <p className="text-xs font-semibold mb-1">CSP Status</p>
                <CspCheck label="CSP enabled" ok={selected.cspEnabled} />
                <CspCheck label="Enforced (not report-only in Production)" ok={!(selected.cspEnabled && selected.enforcementMode === "ReportOnly" && selected.environment === "Production")} warn={selected.cspEnabled && selected.enforcementMode === "ReportOnly"} />
                <CspCheck label="Nonce / hash protection enabled" ok={selected.nonceHashProtectionEnabled} warn />
              </div>

              {/* Directives */}
              <div className="rounded border p-2">
                <p className="text-xs font-semibold mb-1">Policy Directives</p>
                <CspCheck label="default-src" ok={selected.defaultSrcConfigured} />
                <CspCheck label="script-src"  ok={selected.scriptSrcConfigured} />
                <CspCheck label="style-src"   ok={selected.styleSrcConfigured} />
                <CspCheck label="img-src"     ok={selected.imageSrcConfigured} />
                <CspCheck label="connect-src" ok={selected.connectSrcConfigured} />
                <CspCheck label="Frame protection (frame-ancestors)" ok={selected.frameProtectionConfigured} />
              </div>

              {/* Risk flags */}
              <div className="rounded border p-2">
                <p className="text-xs font-semibold mb-1">Risk Configuration</p>
                <div className="flex items-center justify-between py-0.5">
                  <span className="text-xs text-muted-foreground">unsafe-inline</span>
                  <span className={`text-xs font-medium ${selected.unsafeInlineAllowed ? "text-red-600" : "text-green-600"}`}>{selected.unsafeInlineAllowed ? "⚠ Allowed" : "✓ Blocked"}</span>
                </div>
                <div className="flex items-center justify-between py-0.5">
                  <span className="text-xs text-muted-foreground">unsafe-eval</span>
                  <span className={`text-xs font-medium ${selected.unsafeEvalAllowed ? "text-red-600" : "text-green-600"}`}>{selected.unsafeEvalAllowed ? "⚠ Allowed" : "✓ Blocked"}</span>
                </div>
                <CspCheck label="Security headers configured" ok={selected.securityHeadersConfigured} />
                <CspCheck label="report-uri / report-to configured" ok={selected.reportUriConfigured} />
                <CspCheck label="Violation monitoring enabled" ok={selected.violationMonitoringEnabled} />
              </div>

              {/* Review info */}
              <div className="rounded border p-2 text-xs space-y-0.5">
                <p className="font-semibold mb-1">Review Information</p>
                {selected.lastPolicyReviewDate && <div className="flex justify-between"><span className="text-muted-foreground">Last review</span><span>{selected.lastPolicyReviewDate.slice(0,10)}</span></div>}
                {selected.nextReviewDate && <div className="flex justify-between"><span className="text-muted-foreground">Next review due</span><span className={selected.reviewOverdue ? "text-red-600 font-semibold" : ""}>{selected.nextReviewDate.slice(0,10)}</span></div>}
                {selected.reviewerName && <div className="flex justify-between"><span className="text-muted-foreground">Reviewer</span><span>{selected.reviewerName}{selected.reviewerTitle ? ` · ${selected.reviewerTitle}` : ""}</span></div>}
                {selected.lastValidationDate && <div className="flex justify-between"><span className="text-muted-foreground">Last validated</span><span>{selected.lastValidationDate.slice(0,10)}</span></div>}
              </div>

              {selected.evidenceReference && (
                <div className="rounded bg-blue-50 border border-blue-200 p-2 text-xs">
                  <p className="font-semibold text-blue-800 mb-0.5">Evidence Reference</p>
                  <p className="font-mono text-blue-700">{selected.evidenceReference}</p>
                </div>
              )}

              {selected.validationCodes.length > 0 && (
                <div className="rounded bg-red-50 border border-red-200 p-2">
                  <p className="text-xs font-semibold text-red-800 mb-1">Compliance Issues</p>
                  <div className="space-y-1">
                    {selected.validationCodes.map(c => (
                      <div key={c} className="flex items-center gap-2">
                        <span className="text-xs font-mono bg-red-100 text-red-700 rounded px-1.5 py-0.5">{c}</span>
                        <span className="text-xs text-red-700">{H41_CODE_LABELS[c] ?? c}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selected.remediationNotes && (
                <div>
                  <p className="text-xs font-medium mb-0.5">Remediation Notes</p>
                  <p className="text-xs text-muted-foreground whitespace-pre-wrap">{selected.remediationNotes}</p>
                </div>
              )}
              {/* Linked knowledge source — auto-populated when source triggered this finding */}
              <LinkedSourceRow entityType="CspSecurityControl" entityId={selected.id} canManage={canManage} />
            </div>
            {canManage && (
              <DialogFooter className="flex flex-wrap gap-2 sm:justify-start">
                <Button size="sm" onClick={() => validateM.mutate()} disabled={validateM.isPending}>{validateM.isPending ? "Validating…" : "Validate"}</Button>
                <Button size="sm" variant="outline" onClick={() => { setReviewForm({ reviewerName:"", reviewerTitle:"", reviewNotes:"", nextReviewDate:"", evidenceReference:"" }); setShowReview(true); }}>Complete Review</Button>
                <Button size="sm" variant="outline" onClick={() => { setRemediateForm({ remediationNotes:"" }); setShowRemediate(true); }}>Add Remediation</Button>
                <Button size="sm" variant="destructive" onClick={() => setShowDelete(true)}>Delete</Button>
              </DialogFooter>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* ── Complete Review ───────────────────────────────────────────────── */}
      {selected && showReview && (
        <Dialog open={showReview} onOpenChange={v => { setShowReview(v); if (!v) setReviewErrors({}); }}>
          <DialogContent>
            <DialogHeader><DialogTitle>Complete CSP Policy Review</DialogTitle></DialogHeader>
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">Reviewer Name *</label>
                  <input className={fieldCls("w-full border rounded px-2 py-1 text-sm", reviewErrors, "reviewerName")} aria-invalid={!!reviewErrors.reviewerName} value={reviewForm.reviewerName} onChange={e => { setReviewForm(f => ({ ...f, reviewerName: e.target.value })); clearErr(setReviewErrors, "reviewerName"); }} placeholder="Security Engineer" />
                  {fErr(reviewErrors, "reviewerName")}
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Reviewer Title</label>
                  <input className="w-full border rounded px-2 py-1 text-sm" value={reviewForm.reviewerTitle} onChange={e => setReviewForm(f => ({ ...f, reviewerTitle: e.target.value }))} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Next Review Date</label>
                <input type="date" className="w-full border rounded px-2 py-1 text-sm" value={reviewForm.nextReviewDate} onChange={e => setReviewForm(f => ({ ...f, nextReviewDate: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Review Notes</label>
                <textarea className="w-full border rounded px-2 py-1 text-sm" rows={3} value={reviewForm.reviewNotes} onChange={e => setReviewForm(f => ({ ...f, reviewNotes: e.target.value }))} placeholder="Policy directives reviewed, violations analysed…" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Evidence Reference</label>
                <input className="w-full border rounded px-2 py-1 text-sm" value={reviewForm.evidenceReference} onChange={e => setReviewForm(f => ({ ...f, evidenceReference: e.target.value }))} placeholder="evidence://csp-review-2026.pdf" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowReview(false)}>Cancel</Button>
              <Button onClick={() => { const errs: Record<string,string> = {}; required(errs, "reviewerName", reviewForm.reviewerName, "Reviewer Name"); if (Object.keys(errs).length) { setReviewErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } setReviewErrors({}); reviewM.mutate(); }} disabled={reviewM.isPending}>{reviewM.isPending ? "Saving…" : "Complete Review"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Add Remediation ───────────────────────────────────────────────── */}
      {selected && showRemediate && (
        <Dialog open={showRemediate} onOpenChange={setShowRemediate}>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Remediation Notes</DialogTitle></DialogHeader>
            <div>
              <label className="block text-xs font-medium mb-1">Remediation Notes *</label>
              <textarea className="w-full border rounded px-2 py-1 text-sm" rows={4} value={remediateForm.remediationNotes} onChange={e => setRemediateForm(f => ({ ...f, remediationNotes: e.target.value }))} placeholder="e.g. Switch enforcement mode to Enforced; remove unsafe-inline by migrating inline scripts to nonce-based approach…" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRemediate(false)}>Cancel</Button>
              <Button onClick={() => remediateM.mutate()} disabled={remediateM.isPending}>{remediateM.isPending ? "Saving…" : "Add Remediation"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Delete ────────────────────────────────────────────────────────── */}
      {selected && showDelete && (
        <Dialog open={showDelete} onOpenChange={setShowDelete}>
          <DialogContent>
            <DialogHeader><DialogTitle>Delete CSP Security Control</DialogTitle></DialogHeader>
            <p className="text-sm">Permanently delete the CSP control for <strong>{selected?.applicationName}{selected?.moduleName ? ` / ${selected.moduleName}` : ""}</strong> ({selected?.environment})? This cannot be undone.</p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDelete(false)}>Cancel</Button>
              <Button variant="destructive" onClick={() => deleteM.mutate()} disabled={deleteM.isPending}>{deleteM.isPending ? "Deleting…" : "Delete"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// HIPAA-40 — Secure Coding: XSS Prevention
// ══════════════════════════════════════════════════════════════════════════════

interface Hipaa40Summary {
  total: number; compliant: number; nonCompliant: number; applicationsProtected: number;
  sanitizationIssues: number; cspIssues: number; headerIssues: number;
  pendingReviews: number; compliancePercentage: number;
}

interface Hipaa40Control {
  id: string; organizationId: string;
  applicationName: string; moduleName?: string;
  frontendFramework?: string; backendFramework?: string;
  inputValidationEnabled: boolean; outputEncodingEnabled: boolean;
  htmlSanitizationEnabled: boolean; richTextSanitizationEnabled: boolean;
  scriptInjectionDetectionEnabled: boolean; cspEnabled: boolean;
  securityHeadersConfigured: boolean; userGeneratedContentProtected: boolean;
  thirdPartyContentReviewed: boolean;
  securityScanPerformedDate?: string; lastPenetrationTestDate?: string;
  lastReviewDate?: string; nextReviewDate?: string;
  reviewerName?: string; reviewerTitle?: string; reviewNotes?: string;
  evidenceReference?: string; complianceStatus: string; riskLevel?: string;
  remediationRequired: boolean; remediationNotes?: string; lastValidationDate?: string;
  createdAt: string; updatedAt: string;
  reviewOverdue: boolean; validationCodes: string[];
}

function useAuth40() {
  return (url: string, opts: RequestInit = {}) => {
    const token = sessionStorage.getItem("dotnetbot.jwt.session") || localStorage.getItem("dotnetbot.jwt") || "";
    return fetch(url, { ...opts, headers: { ...(opts.headers as Record<string,string>||{}), "Content-Type": "application/json", Authorization: `Bearer ${token}` } });
  };
}

const H40_FRONTENDS  = ["React","Angular","Vue","Blazor","VanillaJS","Other"];
const H40_BACKENDS   = ["ASP.NET Core","Node.js","Django","Spring","Laravel","Rails","Other"];
const H40_RISK_LEVELS = ["Low","Medium","High","Critical"];
const H40_STATUSES    = ["Compliant","NonCompliant","Pending","UnderReview"];

const H40_CODE_LABELS: Record<string, string> = {
  INPUT_VALIDATION_REQUIRED:            "Input validation not enforced",
  OUTPUT_ENCODING_REQUIRED:             "Output encoding missing",
  HTML_SANITIZATION_REQUIRED:           "HTML sanitization not configured",
  SCRIPT_INJECTION_PROTECTION_REQUIRED: "Script injection detection disabled",
  CSP_REQUIRED:                         "Content Security Policy missing",
  SECURITY_HEADERS_REQUIRED:            "Security headers not configured",
  SECURITY_EVIDENCE_REQUIRED:           "No security evidence reference",
  SECURITY_REVIEW_OVERDUE:              "Security review overdue",
};

function h40StatusBadge(s: string): "default"|"secondary"|"outline"|"destructive" {
  if (s === "Compliant")    return "default";
  if (s === "NonCompliant") return "destructive";
  if (s === "UnderReview")  return "secondary";
  return "outline";
}

const H40_BLANK = {
  applicationName: "", moduleName: "",
  frontendFramework: "React", backendFramework: "ASP.NET Core",
  inputValidationEnabled: false, outputEncodingEnabled: false,
  htmlSanitizationEnabled: false, richTextSanitizationEnabled: false,
  scriptInjectionDetectionEnabled: false, cspEnabled: false,
  securityHeadersConfigured: false, userGeneratedContentProtected: false,
  thirdPartyContentReviewed: false,
  securityScanPerformedDate: "", lastPenetrationTestDate: "",
  lastReviewDate: "", nextReviewDate: "",
  reviewerName: "", reviewerTitle: "", evidenceReference: "", riskLevel: "Medium",
};

function HipaaXssSecuritySection({ canManage }: { canManage: boolean }) {
  const authFetch = useAuth40();
  const qc = useQueryClient();
  const { toast } = useToast();
  const BASE = "/api/organization/compliance/hipaa/xss-security-controls";

  const summaryQ = useQuery<Hipaa40Summary>({
    queryKey: ["hipaa40-summary"],
    queryFn: () => authFetch(`${BASE}/summary`).then(r => r.json()),
  });

  const [fApp,      setFApp]      = useState("");
  const [fStatus,   setFStatus]   = useState("");
  const [fRisk,     setFRisk]     = useState("");
  const [fFramework,setFFramework]= useState("");

  const listQ = useQuery<Hipaa40Control[]>({
    queryKey: ["hipaa40-list", fApp, fStatus, fRisk, fFramework],
    queryFn: () => {
      const p = new URLSearchParams();
      if (fApp)       p.set("application", fApp);
      if (fStatus)    p.set("status", fStatus);
      if (fRisk)      p.set("riskLevel", fRisk);
      if (fFramework) p.set("framework", fFramework);
      return authFetch(`${BASE}?${p}`).then(r => r.json());
    },
  });

  const [showCreate,    setShowCreate]    = useState(false);
  const [showDetail,    setShowDetail]    = useState(false);
  const [showReview,    setShowReview]    = useState(false);
  const [showRemediate, setShowRemediate] = useState(false);
  const [showDelete,    setShowDelete]    = useState(false);
  const [selected,      setSelected]      = useState<Hipaa40Control|null>(null);

  const [createForm, setCreateForm] = useState({ ...H40_BLANK });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const setCF = (k: string, v: unknown) => setCreateForm(f => ({ ...f, [k]: v }));

  const [reviewForm,    setReviewForm]    = useState({ reviewerName: "", reviewerTitle: "", reviewNotes: "", nextReviewDate: "", evidenceReference: "" });
  const [reviewErrors,  setReviewErrors]  = useState<Record<string,string>>({});
  const [remediateForm, setRemediateForm] = useState({ remediationNotes: "" });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["hipaa40-summary"] });
    qc.invalidateQueries({ queryKey: ["hipaa40-list"] });
  };

  const openDetail = (c: Hipaa40Control) => { setSelected(c); setShowDetail(true); };

  const createM = useMutation({
    mutationFn: () => authFetch(BASE, { method: "POST", body: JSON.stringify({
      applicationName: createForm.applicationName,
      moduleName: createForm.moduleName || null,
      frontendFramework: createForm.frontendFramework || null,
      backendFramework: createForm.backendFramework || null,
      inputValidationEnabled: createForm.inputValidationEnabled,
      outputEncodingEnabled: createForm.outputEncodingEnabled,
      htmlSanitizationEnabled: createForm.htmlSanitizationEnabled,
      richTextSanitizationEnabled: createForm.richTextSanitizationEnabled,
      scriptInjectionDetectionEnabled: createForm.scriptInjectionDetectionEnabled,
      cspEnabled: createForm.cspEnabled,
      securityHeadersConfigured: createForm.securityHeadersConfigured,
      userGeneratedContentProtected: createForm.userGeneratedContentProtected,
      thirdPartyContentReviewed: createForm.thirdPartyContentReviewed,
      securityScanPerformedDate: createForm.securityScanPerformedDate || null,
      lastPenetrationTestDate: createForm.lastPenetrationTestDate || null,
      lastReviewDate: createForm.lastReviewDate || null,
      nextReviewDate: createForm.nextReviewDate || null,
      reviewerName: createForm.reviewerName || null,
      reviewerTitle: createForm.reviewerTitle || null,
      evidenceReference: createForm.evidenceReference || null,
      riskLevel: createForm.riskLevel || null,
    })}).then(async r => { const d = await r.json(); if (!r.ok) throw d; return d; }),
    onSuccess: () => { invalidate(); setShowCreate(false); setErrors({}); setCreateForm({ ...H40_BLANK }); toast({ title: "XSS control added" }); },
    onError: (e: any) => {
      const serverErrs = extractServerErrors(e);
      if (serverErrs && Object.keys(serverErrs).length > 0) {
        setErrors(serverErrs);
        toast({ title: "Please correct the highlighted fields.", variant: "destructive" });
      } else {
        toast({ title: "Unable to save", description: e?.message ?? "An unexpected error occurred.", variant: "destructive" });
      }
    },
  });

  const validateM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}/validate`, { method: "POST", body: "{}" }).then(async r => { const d = await r.json(); if (!r.ok) throw d; return d; }),
    onSuccess: (d: any) => { invalidate(); toast({ title: d.isCompliant ? "✓ Compliant" : "Issues detected", description: d.validationCodes?.join(", ") || undefined }); },
    onError: (e: any) => toast({ title: "Validation error", description: e?.error, variant: "destructive" }),
  });

  const reviewM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}/review`, { method: "POST", body: JSON.stringify({
      reviewerName: reviewForm.reviewerName,
      reviewerTitle: reviewForm.reviewerTitle || null,
      reviewNotes: reviewForm.reviewNotes || null,
      nextReviewDate: reviewForm.nextReviewDate || null,
      evidenceReference: reviewForm.evidenceReference || null,
    })}).then(async r => { const d = await r.json(); if (!r.ok) throw d; return d; }),
    onSuccess: (d: any) => { invalidate(); setSelected(d); setShowReview(false); setReviewErrors({}); toast({ title: "Review completed" }); },
    onError: (e: any) => { const s = extractServerErrors(e); if (s && Object.keys(s).length) { setReviewErrors(s); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); } else toast({ title: "Unable to complete action", description: e?.message, variant: "destructive" }); },
  });

  const remediateM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}/remediation`, { method: "POST", body: JSON.stringify({ remediationNotes: remediateForm.remediationNotes }) }).then(async r => { const d = await r.json(); if (!r.ok) throw d; return d; }),
    onSuccess: (d: any) => { invalidate(); setSelected(d); setShowRemediate(false); toast({ title: "Remediation recorded" }); },
    onError: (e: any) => toast({ title: "Unable to complete action", description: e?.message, variant: "destructive" }),
  });

  const deleteM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}`, { method: "DELETE" }).then(async r => { if (!r.ok) { const d = await r.json(); throw d; } }),
    onSuccess: () => { invalidate(); setShowDelete(false); setShowDetail(false); setSelected(null); toast({ title: "Control deleted" }); },
    onError: (e: any) => toast({ title: "Unable to delete", description: e?.message, variant: "destructive" }),
  });

  const sum   = summaryQ.data;
  const items = listQ.data ?? [];
  const issueItems = items.filter(c => c.validationCodes.length > 0);

  const CheckRow40 = ({ label, ok }: { label: string; ok: boolean }) => (
    <div className="flex items-center justify-between py-0.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={`text-xs font-medium ${ok ? "text-green-600" : "text-red-600"}`}>{ok ? "✓" : "✗"}</span>
    </div>
  );

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base font-semibold">XSS Prevention &amp; Secure Coding</CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">Track input validation, output encoding, HTML sanitization, CSP enforcement, and security header configuration to prevent Cross-Site Scripting attacks.</p>
            </div>
            {canManage && (
              <Button size="sm" onClick={() => { setCreateForm({ ...H40_BLANK }); setShowCreate(true); }}>+ Add Control</Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">

          {/* Summary metrics */}
          {sum && (
            <>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {[
                  { label: "Total Applications",    value: sum.total,                color: "" },
                  { label: "Compliant",             value: sum.compliant,            color: "text-green-600" },
                  { label: "Non-Compliant",         value: sum.nonCompliant,         color: sum.nonCompliant > 0 ? "text-red-600" : "" },
                  { label: "Fully Protected",       value: sum.applicationsProtected,color: "" },
                  { label: "Sanitization Issues",   value: sum.sanitizationIssues,   color: sum.sanitizationIssues > 0 ? "text-red-600" : "" },
                  { label: "CSP Issues",            value: sum.cspIssues,            color: sum.cspIssues > 0 ? "text-red-600" : "" },
                  { label: "Header Issues",         value: sum.headerIssues,         color: sum.headerIssues > 0 ? "text-red-600" : "" },
                  { label: "Pending Reviews",       value: sum.pendingReviews,       color: sum.pendingReviews > 0 ? "text-amber-600" : "" },
                ].map(m => (
                  <div key={m.label} className="rounded border p-2 text-center">
                    <div className={`text-xl font-bold ${m.color}`}>{m.value}</div>
                    <div className="text-xs text-muted-foreground leading-tight">{m.label}</div>
                  </div>
                ))}
              </div>
              {sum.total > 0 && (
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span>Compliance</span>
                    <span className={sum.compliancePercentage === 100 ? "text-green-600 font-semibold" : "text-amber-600"}>{sum.compliancePercentage}%</span>
                  </div>
                  <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${sum.compliancePercentage === 100 ? "bg-green-500" : "bg-amber-500"}`} style={{ width: `${sum.compliancePercentage}%` }} />
                  </div>
                </div>
              )}
            </>
          )}

          {/* Issues panel */}
          {issueItems.length > 0 && (
            <div className="rounded border border-red-200 bg-red-50 p-3">
              <p className="text-xs font-semibold text-red-800 mb-1">Applications with XSS security issues ({issueItems.length})</p>
              <ul className="space-y-1">
                {issueItems.slice(0, 5).map(c => (
                  <li key={c.id} className="text-xs text-red-700 flex justify-between items-start gap-2">
                    <button className="underline hover:no-underline text-left" onClick={() => openDetail(c)}>
                      {c.applicationName}{c.moduleName ? ` / ${c.moduleName}` : ""}
                    </button>
                    <div className="flex flex-wrap gap-1 justify-end shrink-0">
                      {c.validationCodes.slice(0, 2).map(code => (
                        <span key={code} className="font-mono bg-red-100 rounded px-1 text-xs">{code.replace(/_/g," ")}</span>
                      ))}
                      {c.validationCodes.length > 2 && <span>+{c.validationCodes.length-2}</span>}
                    </div>
                  </li>
                ))}
                {issueItems.length > 5 && <li className="text-xs text-red-600">+{issueItems.length - 5} more…</li>}
              </ul>
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-wrap gap-2">
            <input className="border rounded px-2 py-1 text-xs w-40" placeholder="Filter by application…" value={fApp} onChange={e => setFApp(e.target.value)} />
            <select className="border rounded px-2 py-1 text-xs" value={fStatus} onChange={e => setFStatus(e.target.value)}>
              <option value="">All statuses</option>
              {H40_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select className="border rounded px-2 py-1 text-xs" value={fRisk} onChange={e => setFRisk(e.target.value)}>
              <option value="">All risk levels</option>
              {H40_RISK_LEVELS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <input className="border rounded px-2 py-1 text-xs w-36" placeholder="Filter by framework…" value={fFramework} onChange={e => setFFramework(e.target.value)} />
            {(fApp||fStatus||fRisk||fFramework) && (
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setFApp(""); setFStatus(""); setFRisk(""); setFFramework(""); }}>Clear</Button>
            )}
          </div>

          {/* Control list */}
          {listQ.isLoading ? (
            <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-14 w-full" />)}</div>
          ) : items.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-4">No XSS controls defined. {canManage && "Add a control to start tracking."}</p>
          ) : (
            <div className="divide-y rounded border">
              {items.map(c => (
                <div key={c.id} className="flex items-start justify-between p-3 hover:bg-muted/30 cursor-pointer" onClick={() => openDetail(c)}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">{c.applicationName}</span>
                      {c.moduleName && <span className="text-xs text-muted-foreground">/ {c.moduleName}</span>}
                      <Badge variant={h40StatusBadge(c.complianceStatus)} className="text-xs">{c.complianceStatus}</Badge>
                      {c.reviewOverdue && <Badge variant="outline" className="text-xs text-red-600">Review Overdue</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 flex gap-3 flex-wrap">
                      {!c.inputValidationEnabled && <span className="text-red-600">⚠ no input validation</span>}
                      {!c.outputEncodingEnabled && <span className="text-red-600">⚠ no output encoding</span>}
                      {!c.cspEnabled && <span className="text-red-600">⚠ no CSP</span>}
                      {!c.securityHeadersConfigured && <span className="text-amber-600">⚠ missing headers</span>}
                      {c.frontendFramework && <span>{c.frontendFramework}</span>}
                      {c.backendFramework && <span>/ {c.backendFramework}</span>}
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground shrink-0 ml-2 text-right">
                    {c.validationCodes.length > 0 && <div className="text-red-500">{c.validationCodes.length} issue{c.validationCodes.length>1?"s":""}</div>}
                    {c.riskLevel && <div>{c.riskLevel} risk</div>}
                    <div>{c.updatedAt.slice(0,10)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Add XSS Control ───────────────────────────────────────────────── */}
      <Dialog open={showCreate} onOpenChange={v => { setShowCreate(v); if (!v) setErrors({}); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add XSS Security Control</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Application Name *</label>
                <input className={fieldCls("w-full border rounded px-2 py-1 text-sm", errors, "applicationName")} aria-invalid={!!errors.applicationName} value={createForm.applicationName} onChange={e => { setCF("applicationName", e.target.value); clearErr(setErrors, "applicationName"); }} placeholder="e.g. Patient Portal" />
                {fErr(errors, "applicationName")}
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Module / Component</label>
                <input className={fieldCls("w-full border rounded px-2 py-1 text-sm", errors, "moduleName")} aria-invalid={!!errors.moduleName} value={createForm.moduleName} onChange={e => { setCF("moduleName", e.target.value); clearErr(setErrors, "moduleName"); }} placeholder="e.g. Registration Form" />
                {fErr(errors, "moduleName")}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Frontend Framework</label>
                <select className="w-full border rounded px-2 py-1 text-sm" value={createForm.frontendFramework} onChange={e => setCF("frontendFramework", e.target.value)}>
                  {H40_FRONTENDS.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Backend Framework</label>
                <select className="w-full border rounded px-2 py-1 text-sm" value={createForm.backendFramework} onChange={e => setCF("backendFramework", e.target.value)}>
                  {H40_BACKENDS.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
            </div>

            <fieldset className="border rounded p-3 space-y-2">
              <legend className="text-xs font-semibold px-1">Input &amp; Output Protection</legend>
              <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={createForm.inputValidationEnabled} onChange={e => setCF("inputValidationEnabled", e.target.checked)} /> Input validation enforced (all user-supplied data validated server-side)</label>
              <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={createForm.outputEncodingEnabled} onChange={e => setCF("outputEncodingEnabled", e.target.checked)} /> Output encoding applied (HTML/JS/URL encoding before rendering)</label>
              <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={createForm.htmlSanitizationEnabled} onChange={e => setCF("htmlSanitizationEnabled", e.target.checked)} /> HTML sanitization enabled (DOMPurify or equivalent)</label>
              <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={createForm.richTextSanitizationEnabled} onChange={e => setCF("richTextSanitizationEnabled", e.target.checked)} /> Rich text / WYSIWYG sanitization enabled</label>
              <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={createForm.scriptInjectionDetectionEnabled} onChange={e => setCF("scriptInjectionDetectionEnabled", e.target.checked)} /> Script injection detection enabled (WAF / CSP reporting)</label>
            </fieldset>

            <fieldset className="border rounded p-3 space-y-2">
              <legend className="text-xs font-semibold px-1">Security Configuration</legend>
              <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={createForm.cspEnabled} onChange={e => setCF("cspEnabled", e.target.checked)} /> Content Security Policy (CSP) header configured</label>
              <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={createForm.securityHeadersConfigured} onChange={e => setCF("securityHeadersConfigured", e.target.checked)} /> Security headers configured (X-Content-Type-Options, X-Frame-Options, HSTS)</label>
              <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={createForm.userGeneratedContentProtected} onChange={e => setCF("userGeneratedContentProtected", e.target.checked)} /> User-generated content sandboxed / protected</label>
              <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={createForm.thirdPartyContentReviewed} onChange={e => setCF("thirdPartyContentReviewed", e.target.checked)} /> Third-party scripts and embeds reviewed</label>
            </fieldset>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Security Scan Date</label>
                <input type="date" className="w-full border rounded px-2 py-1 text-sm" value={createForm.securityScanPerformedDate} onChange={e => setCF("securityScanPerformedDate", e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Last Penetration Test</label>
                <input type="date" className="w-full border rounded px-2 py-1 text-sm" value={createForm.lastPenetrationTestDate} onChange={e => setCF("lastPenetrationTestDate", e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Last Review Date</label>
                <input type="date" className="w-full border rounded px-2 py-1 text-sm" value={createForm.lastReviewDate} onChange={e => setCF("lastReviewDate", e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Next Review Date</label>
                <input type="date" className="w-full border rounded px-2 py-1 text-sm" value={createForm.nextReviewDate} onChange={e => setCF("nextReviewDate", e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Reviewer Name</label>
                <input className="w-full border rounded px-2 py-1 text-sm" value={createForm.reviewerName} onChange={e => setCF("reviewerName", e.target.value)} placeholder="Security Engineer" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Reviewer Title</label>
                <input className="w-full border rounded px-2 py-1 text-sm" value={createForm.reviewerTitle} onChange={e => setCF("reviewerTitle", e.target.value)} placeholder="AppSec Lead" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Risk Level</label>
                <select className="w-full border rounded px-2 py-1 text-sm" value={createForm.riskLevel} onChange={e => setCF("riskLevel", e.target.value)}>
                  {H40_RISK_LEVELS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Evidence Reference</label>
                <input className="w-full border rounded px-2 py-1 text-sm" value={createForm.evidenceReference} onChange={e => setCF("evidenceReference", e.target.value)} placeholder="evidence://xss-audit-2026.pdf" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreate(false); setErrors({}); }}>Cancel</Button>
            <Button onClick={() => {
              const errs: Record<string, string> = {};
              required(errs, "applicationName", createForm.applicationName, "Application Name");
              required(errs, "moduleName", createForm.moduleName, "Module / Component");
              if (Object.keys(errs).length > 0) {
                setErrors(errs);
                toast({ title: "Please correct the highlighted fields.", variant: "destructive" });
                return;
              }
              setErrors({});
              createM.mutate();
            }} disabled={createM.isPending}>{createM.isPending ? "Saving…" : "Add Control"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Detail ────────────────────────────────────────────────────────── */}
      {selected && showDetail && (
        <Dialog open={showDetail} onOpenChange={v => { setShowDetail(v); if (!v) setSelected(null); }}>
          <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 flex-wrap">
                {selected.applicationName}
                {selected.moduleName && <span className="text-xs font-normal text-muted-foreground">/ {selected.moduleName}</span>}
                <Badge variant={h40StatusBadge(selected.complianceStatus)}>{selected.complianceStatus}</Badge>
                {selected.reviewOverdue && <Badge variant="outline" className="text-xs text-red-600">Review Overdue</Badge>}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2 text-xs">
                {selected.frontendFramework && <div><p className="font-medium">Frontend</p><p className="text-muted-foreground">{selected.frontendFramework}</p></div>}
                {selected.backendFramework && <div><p className="font-medium">Backend</p><p className="text-muted-foreground">{selected.backendFramework}</p></div>}
                {selected.riskLevel && <div><p className="font-medium">Risk Level</p><p className="text-muted-foreground">{selected.riskLevel}</p></div>}
              </div>

              {/* Input / Output checks */}
              <div className="rounded border p-2">
                <p className="text-xs font-semibold mb-1">Input &amp; Output Protection</p>
                <CheckRow40 label="Input validation"            ok={selected.inputValidationEnabled} />
                <CheckRow40 label="Output encoding"             ok={selected.outputEncodingEnabled} />
                <CheckRow40 label="HTML sanitization"           ok={selected.htmlSanitizationEnabled} />
                <CheckRow40 label="Rich text sanitization"      ok={selected.richTextSanitizationEnabled} />
                <CheckRow40 label="Script injection detection"  ok={selected.scriptInjectionDetectionEnabled} />
              </div>

              {/* Security configuration */}
              <div className="rounded border p-2">
                <p className="text-xs font-semibold mb-1">Security Configuration</p>
                <CheckRow40 label="Content Security Policy (CSP)"   ok={selected.cspEnabled} />
                <CheckRow40 label="Security headers configured"      ok={selected.securityHeadersConfigured} />
                <CheckRow40 label="User-generated content protected" ok={selected.userGeneratedContentProtected} />
                <CheckRow40 label="Third-party content reviewed"     ok={selected.thirdPartyContentReviewed} />
              </div>

              {/* Review info */}
              <div className="rounded border p-2 text-xs space-y-0.5">
                <p className="font-semibold mb-1">Security Testing &amp; Review</p>
                {selected.securityScanPerformedDate && <div className="flex justify-between"><span className="text-muted-foreground">Security scan</span><span>{selected.securityScanPerformedDate.slice(0,10)}</span></div>}
                {selected.lastPenetrationTestDate && <div className="flex justify-between"><span className="text-muted-foreground">Penetration test</span><span>{selected.lastPenetrationTestDate.slice(0,10)}</span></div>}
                {selected.lastReviewDate && <div className="flex justify-between"><span className="text-muted-foreground">Last review</span><span>{selected.lastReviewDate.slice(0,10)}</span></div>}
                {selected.nextReviewDate && <div className="flex justify-between"><span className="text-muted-foreground">Next review due</span><span className={selected.reviewOverdue ? "text-red-600 font-semibold" : ""}>{selected.nextReviewDate.slice(0,10)}</span></div>}
                {selected.reviewerName && <div className="flex justify-between"><span className="text-muted-foreground">Reviewer</span><span>{selected.reviewerName}{selected.reviewerTitle ? ` · ${selected.reviewerTitle}` : ""}</span></div>}
                {selected.lastValidationDate && <div className="flex justify-between"><span className="text-muted-foreground">Last validated</span><span>{selected.lastValidationDate.slice(0,10)}</span></div>}
              </div>

              {selected.evidenceReference && (
                <div className="rounded bg-blue-50 border border-blue-200 p-2 text-xs">
                  <p className="font-semibold text-blue-800 mb-0.5">Evidence Reference</p>
                  <p className="font-mono text-blue-700">{selected.evidenceReference}</p>
                </div>
              )}

              {selected.validationCodes.length > 0 && (
                <div className="rounded bg-red-50 border border-red-200 p-2">
                  <p className="text-xs font-semibold text-red-800 mb-1">Compliance Issues</p>
                  <div className="space-y-1">
                    {selected.validationCodes.map(c => (
                      <div key={c} className="flex items-center gap-2">
                        <span className="text-xs font-mono bg-red-100 text-red-700 rounded px-1.5 py-0.5">{c}</span>
                        <span className="text-xs text-red-700">{H40_CODE_LABELS[c] ?? c}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selected.remediationNotes && (
                <div>
                  <p className="text-xs font-medium mb-0.5">Remediation Notes</p>
                  <p className="text-xs text-muted-foreground whitespace-pre-wrap">{selected.remediationNotes}</p>
                </div>
              )}

              <LinkedSourceRow entityType="HipaaXssSecurityControl" entityId={selected.id} canManage={canManage} />
            </div>
            {canManage && (
              <DialogFooter className="flex flex-wrap gap-2 sm:justify-start">
                <Button size="sm" onClick={() => validateM.mutate()} disabled={validateM.isPending}>{validateM.isPending ? "Validating…" : "Validate"}</Button>
                <Button size="sm" variant="outline" onClick={() => { setReviewForm({ reviewerName: "", reviewerTitle: "", reviewNotes: "", nextReviewDate: "", evidenceReference: "" }); setShowReview(true); }}>Complete Review</Button>
                <Button size="sm" variant="outline" onClick={() => { setRemediateForm({ remediationNotes: "" }); setShowRemediate(true); }}>Add Remediation</Button>
                <Button size="sm" variant="destructive" onClick={() => setShowDelete(true)}>Delete</Button>
              </DialogFooter>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* ── Complete Review ───────────────────────────────────────────────── */}
      {selected && showReview && (
        <Dialog open={showReview} onOpenChange={v => { setShowReview(v); if (!v) setReviewErrors({}); }}>
          <DialogContent>
            <DialogHeader><DialogTitle>Complete Security Review</DialogTitle></DialogHeader>
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">Reviewer Name *</label>
                  <input className={fieldCls("w-full border rounded px-2 py-1 text-sm", reviewErrors, "reviewerName")} aria-invalid={!!reviewErrors.reviewerName} value={reviewForm.reviewerName} onChange={e => { setReviewForm(f => ({ ...f, reviewerName: e.target.value })); clearErr(setReviewErrors, "reviewerName"); }} placeholder="Security Engineer" />
                  {fErr(reviewErrors, "reviewerName")}
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Reviewer Title</label>
                  <input className="w-full border rounded px-2 py-1 text-sm" value={reviewForm.reviewerTitle} onChange={e => setReviewForm(f => ({ ...f, reviewerTitle: e.target.value }))} placeholder="AppSec Lead" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Next Review Date</label>
                <input type="date" className="w-full border rounded px-2 py-1 text-sm" value={reviewForm.nextReviewDate} onChange={e => setReviewForm(f => ({ ...f, nextReviewDate: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Review Notes</label>
                <textarea className="w-full border rounded px-2 py-1 text-sm" rows={3} value={reviewForm.reviewNotes} onChange={e => setReviewForm(f => ({ ...f, reviewNotes: e.target.value }))} placeholder="XSS audit findings, tools used, scope…" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Evidence Reference</label>
                <input className="w-full border rounded px-2 py-1 text-sm" value={reviewForm.evidenceReference} onChange={e => setReviewForm(f => ({ ...f, evidenceReference: e.target.value }))} placeholder="evidence://xss-review-2026.pdf" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowReview(false)}>Cancel</Button>
              <Button onClick={() => { const errs: Record<string,string> = {}; required(errs, "reviewerName", reviewForm.reviewerName, "Reviewer Name"); if (Object.keys(errs).length) { setReviewErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } setReviewErrors({}); reviewM.mutate(); }} disabled={reviewM.isPending}>{reviewM.isPending ? "Saving…" : "Complete Review"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Add Remediation ───────────────────────────────────────────────── */}
      {selected && showRemediate && (
        <Dialog open={showRemediate} onOpenChange={setShowRemediate}>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Remediation Notes</DialogTitle></DialogHeader>
            <div>
              <label className="block text-xs font-medium mb-1">Remediation Notes *</label>
              <textarea className="w-full border rounded px-2 py-1 text-sm" rows={4} value={remediateForm.remediationNotes} onChange={e => setRemediateForm(f => ({ ...f, remediationNotes: e.target.value }))} placeholder="e.g. Enable DOMPurify on all rendered user content; add CSP header to all API responses…" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRemediate(false)}>Cancel</Button>
              <Button onClick={() => remediateM.mutate()} disabled={remediateM.isPending}>{remediateM.isPending ? "Saving…" : "Add Remediation"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Delete ────────────────────────────────────────────────────────── */}
      {selected && showDelete && (
        <Dialog open={showDelete} onOpenChange={setShowDelete}>
          <DialogContent>
            <DialogHeader><DialogTitle>Delete XSS Security Control</DialogTitle></DialogHeader>
            <p className="text-sm">Permanently delete the XSS control for <strong>{selected?.applicationName}{selected?.moduleName ? ` / ${selected.moduleName}` : ""}</strong>? This cannot be undone.</p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDelete(false)}>Cancel</Button>
              <Button variant="destructive" onClick={() => deleteM.mutate()} disabled={deleteM.isPending}>{deleteM.isPending ? "Deleting…" : "Delete"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// HIPAA-39 — Disable Browser Autocomplete for Sensitive Fields
// ══════════════════════════════════════════════════════════════════════════════

interface Hipaa39Summary {
  total: number; compliant: number; nonCompliant: number; sensitiveFormsReviewed: number;
  autocompleteIssues: number; passwordIssues: number; pendingReviews: number;
  browserRetentionRisks: number; compliancePercentage: number;
}

interface Hipaa39Control {
  id: string; organizationId: string;
  applicationName: string; formPageName?: string; sensitiveFieldCategory?: string;
  phiFieldInvolved: boolean; fieldCountReviewed?: number;
  autocompleteDisabled: boolean; passwordFieldProtected: boolean;
  browserRetentionPrevented: boolean; sensitiveInputMaskingEnabled: boolean;
  secureFormConfigured: boolean;
  reviewPerformedDate?: string; nextReviewDate?: string;
  reviewerName?: string; reviewerTitle?: string; reviewNotes?: string;
  evidenceReference?: string; complianceStatus: string; riskLevel?: string;
  remediationRequired: boolean; remediationNotes?: string; lastValidationDate?: string;
  createdAt: string; updatedAt: string;
  reviewOverdue: boolean; validationCodes: string[];
}

function useAuth39() {
  return (url: string, opts: RequestInit = {}) => {
    const token = sessionStorage.getItem("dotnetbot.jwt.session") || localStorage.getItem("dotnetbot.jwt") || "";
    return fetch(url, { ...opts, headers: { ...(opts.headers as Record<string,string>||{}), "Content-Type": "application/json", Authorization: `Bearer ${token}` } });
  };
}

const H39_CATEGORIES  = ["PHI","PII","Password","MedicalRecord","Insurance","Financial","Other"];
const H39_RISK_LEVELS = ["Low","Medium","High","Critical"];
const H39_STATUSES    = ["Compliant","NonCompliant","Pending","UnderReview"];

const H39_CODE_LABELS: Record<string, string> = {
  PHI_AUTOCOMPLETE_ENABLED:            "PHI fields allow autocomplete",
  PASSWORD_AUTOCOMPLETE_MISCONFIGURED: "Password autocomplete not hardened",
  SENSITIVE_FIELD_REVIEW_REQUIRED:     "Sensitive field review not performed",
  EVIDENCE_REFERENCE_REQUIRED:         "No evidence reference",
  BROWSER_RETENTION_RISK:              "Browser may retain PHI input",
  SECURITY_REVIEW_OVERDUE:             "Security review overdue",
};

function h39StatusBadge(s: string): "default"|"secondary"|"outline"|"destructive" {
  if (s === "Compliant")    return "default";
  if (s === "NonCompliant") return "destructive";
  if (s === "UnderReview")  return "secondary";
  return "outline";
}

const H39_BLANK = {
  applicationName: "", formPageName: "", sensitiveFieldCategory: "PHI",
  phiFieldInvolved: true, fieldCountReviewed: "" as string|number,
  autocompleteDisabled: false, passwordFieldProtected: false,
  browserRetentionPrevented: false, sensitiveInputMaskingEnabled: false,
  reviewPerformedDate: "", nextReviewDate: "",
  reviewerName: "", reviewerTitle: "", evidenceReference: "", riskLevel: "Medium",
};

function HipaaSensitiveFieldAutocompleteSection({ canManage }: { canManage: boolean }) {
  const authFetch = useAuth39();
  const qc = useQueryClient();
  const { toast } = useToast();
  const BASE = "/api/organization/compliance/hipaa/autocomplete-controls";

  const summaryQ = useQuery<Hipaa39Summary>({
    queryKey: ["hipaa39-summary"],
    queryFn: () => authFetch(`${BASE}/summary`).then(r => r.json()),
  });

  const [fApp,      setFApp]      = useState("");
  const [fStatus,   setFStatus]   = useState("");
  const [fRisk,     setFRisk]     = useState("");
  const [fCategory, setFCategory] = useState("");

  const listQ = useQuery<Hipaa39Control[]>({
    queryKey: ["hipaa39-list", fApp, fStatus, fRisk, fCategory],
    queryFn: () => {
      const p = new URLSearchParams();
      if (fApp)      p.set("application", fApp);
      if (fStatus)   p.set("status", fStatus);
      if (fRisk)     p.set("riskLevel", fRisk);
      if (fCategory) p.set("category", fCategory);
      return authFetch(`${BASE}?${p}`).then(r => r.json());
    },
  });

  const [showCreate,    setShowCreate]    = useState(false);
  const [showDetail,    setShowDetail]    = useState(false);
  const [showReview,    setShowReview]    = useState(false);
  const [showRemediate, setShowRemediate] = useState(false);
  const [showDelete,    setShowDelete]    = useState(false);
  const [selected,      setSelected]      = useState<Hipaa39Control|null>(null);

  const [createForm, setCreateForm] = useState({ ...H39_BLANK });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const setCF = (k: string, v: unknown) => setCreateForm(f => ({ ...f, [k]: v }));
  const submitCreate = () => {
    const errs: Record<string, string> = {};
    required(errs, "applicationName", createForm.applicationName, "Application Name");
    required(errs, "formPageName", createForm.formPageName, "Form / Page Name");
    if (Object.keys(errs).length) {
      setErrors(errs);
      toast({ title: "Please correct the highlighted fields.", variant: "destructive" });
      return;
    }
    setErrors({});
    createM.mutate();
  };

  const [reviewForm,    setReviewForm]    = useState({ reviewerName: "", reviewerTitle: "", reviewNotes: "", nextReviewDate: "", evidenceReference: "" });
  const [reviewErrors,  setReviewErrors]  = useState<Record<string,string>>({});
  const [remediateForm, setRemediateForm] = useState({ remediationNotes: "" });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["hipaa39-summary"] });
    qc.invalidateQueries({ queryKey: ["hipaa39-list"] });
  };

  const openDetail = (c: Hipaa39Control) => { setSelected(c); setShowDetail(true); };

  const createM = useMutation({
    mutationFn: () => authFetch(BASE, { method: "POST", body: JSON.stringify({
      applicationName: createForm.applicationName,
      formPageName: createForm.formPageName || null,
      sensitiveFieldCategory: createForm.sensitiveFieldCategory || null,
      phiFieldInvolved: createForm.phiFieldInvolved,
      fieldCountReviewed: createForm.fieldCountReviewed !== "" ? Number(createForm.fieldCountReviewed) : null,
      autocompleteDisabled: createForm.autocompleteDisabled,
      passwordFieldProtected: createForm.passwordFieldProtected,
      browserRetentionPrevented: createForm.browserRetentionPrevented,
      sensitiveInputMaskingEnabled: createForm.sensitiveInputMaskingEnabled,
      reviewPerformedDate: createForm.reviewPerformedDate || null,
      nextReviewDate: createForm.nextReviewDate || null,
      reviewerName: createForm.reviewerName || null,
      reviewerTitle: createForm.reviewerTitle || null,
      evidenceReference: createForm.evidenceReference || null,
      riskLevel: createForm.riskLevel || null,
    })}).then(async r => { const d = await r.json(); if (!r.ok) throw d; return d; }),
    onSuccess: () => { invalidate(); setShowCreate(false); setCreateForm({ ...H39_BLANK }); setErrors({}); toast({ title: "Autocomplete control added" }); },
    onError: (e: any) => { const serverErrs = extractServerErrors(e); if (serverErrs) { setErrors(serverErrs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); } else toast({ title: "Unable to save", description: e?.message, variant: "destructive" }); },
  });

  const validateM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}/validate`, { method: "POST", body: "{}" }).then(async r => { const d = await r.json(); if (!r.ok) throw d; return d; }),
    onSuccess: (d: any) => { invalidate(); toast({ title: d.isCompliant ? "✓ Compliant" : "Issues detected", description: d.validationCodes?.join(", ") || undefined }); },
    onError: (e: any) => toast({ title: "Validation error", description: e?.error, variant: "destructive" }),
  });

  const reviewM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}/review`, { method: "POST", body: JSON.stringify({
      reviewerName: reviewForm.reviewerName,
      reviewerTitle: reviewForm.reviewerTitle || null,
      reviewNotes: reviewForm.reviewNotes || null,
      nextReviewDate: reviewForm.nextReviewDate || null,
      evidenceReference: reviewForm.evidenceReference || null,
    })}).then(async r => { const d = await r.json(); if (!r.ok) throw d; return d; }),
    onSuccess: (d: any) => { invalidate(); setSelected(d); setShowReview(false); setReviewErrors({}); toast({ title: "Review completed" }); },
    onError: (e: any) => { const s = extractServerErrors(e); if (s && Object.keys(s).length) { setReviewErrors(s); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); } else toast({ title: "Unable to complete action", description: e?.message, variant: "destructive" }); },
  });

  const remediateM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}/remediation`, { method: "POST", body: JSON.stringify({ remediationNotes: remediateForm.remediationNotes }) }).then(async r => { const d = await r.json(); if (!r.ok) throw d; return d; }),
    onSuccess: (d: any) => { invalidate(); setSelected(d); setShowRemediate(false); toast({ title: "Remediation recorded" }); },
    onError: (e: any) => toast({ title: "Unable to complete action", description: e?.message, variant: "destructive" }),
  });

  const deleteM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}`, { method: "DELETE" }).then(async r => { if (!r.ok) { const d = await r.json(); throw d; } }),
    onSuccess: () => { invalidate(); setShowDelete(false); setShowDetail(false); setSelected(null); toast({ title: "Control deleted" }); },
    onError: (e: any) => toast({ title: "Unable to delete", description: e?.message, variant: "destructive" }),
  });

  const sum   = summaryQ.data;
  const items = listQ.data ?? [];
  const issueItems = items.filter(c => c.validationCodes.length > 0);

  const CheckRow = ({ label, ok, na }: { label: string; ok: boolean; na?: boolean }) => (
    <div className="flex items-center justify-between py-0.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={`text-xs font-medium ${na ? "text-gray-400" : ok ? "text-green-600" : "text-red-600"}`}>{na ? "N/A" : ok ? "✓" : "✗"}</span>
    </div>
  );

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base font-semibold">Sensitive Field Autocomplete Protection</CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">Ensure browsers cannot store or suggest PHI-related input values. Sensitive fields must use <code className="text-xs">autocomplete="off"</code> or secure equivalents.</p>
            </div>
            {canManage && (
              <Button size="sm" onClick={() => { setCreateForm({ ...H39_BLANK }); setShowCreate(true); }}>+ Add Control</Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">

          {/* Summary metrics */}
          {sum && (
            <>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {[
                  { label: "Total Applications",     value: sum.total,                  color: "" },
                  { label: "Compliant",              value: sum.compliant,              color: "text-green-600" },
                  { label: "Non-Compliant",          value: sum.nonCompliant,           color: sum.nonCompliant > 0 ? "text-red-600" : "" },
                  { label: "Sensitive Forms Reviewed",value: sum.sensitiveFormsReviewed,color: "" },
                  { label: "Autocomplete Issues",    value: sum.autocompleteIssues,     color: sum.autocompleteIssues > 0 ? "text-red-600" : "" },
                  { label: "Password Issues",        value: sum.passwordIssues,         color: sum.passwordIssues > 0 ? "text-red-600" : "" },
                  { label: "Browser Retention Risks",value: sum.browserRetentionRisks,  color: sum.browserRetentionRisks > 0 ? "text-red-600" : "" },
                  { label: "Pending Reviews",        value: sum.pendingReviews,         color: sum.pendingReviews > 0 ? "text-amber-600" : "" },
                ].map(m => (
                  <div key={m.label} className="rounded border p-2 text-center">
                    <div className={`text-xl font-bold ${m.color}`}>{m.value}</div>
                    <div className="text-xs text-muted-foreground leading-tight">{m.label}</div>
                  </div>
                ))}
              </div>
              {sum.total > 0 && (
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span>Compliance</span>
                    <span className={sum.compliancePercentage === 100 ? "text-green-600 font-semibold" : "text-amber-600"}>{sum.compliancePercentage}%</span>
                  </div>
                  <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${sum.compliancePercentage === 100 ? "bg-green-500" : "bg-amber-500"}`} style={{ width: `${sum.compliancePercentage}%` }} />
                  </div>
                </div>
              )}
            </>
          )}

          {/* Issues panel */}
          {issueItems.length > 0 && (
            <div className="rounded border border-red-200 bg-red-50 p-3">
              <p className="text-xs font-semibold text-red-800 mb-1">Forms with autocomplete exposure issues ({issueItems.length})</p>
              <ul className="space-y-1">
                {issueItems.slice(0, 5).map(c => (
                  <li key={c.id} className="text-xs text-red-700 flex justify-between items-start gap-2">
                    <button className="underline hover:no-underline text-left" onClick={() => openDetail(c)}>
                      {c.applicationName}{c.formPageName ? ` / ${c.formPageName}` : ""}
                    </button>
                    <div className="flex flex-wrap gap-1 justify-end shrink-0">
                      {c.validationCodes.slice(0, 2).map(code => (
                        <span key={code} className="font-mono bg-red-100 rounded px-1 text-xs">{code.replace(/_/g," ")}</span>
                      ))}
                      {c.validationCodes.length > 2 && <span>+{c.validationCodes.length-2}</span>}
                    </div>
                  </li>
                ))}
                {issueItems.length > 5 && <li className="text-xs text-red-600">+{issueItems.length - 5} more…</li>}
              </ul>
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-wrap gap-2">
            <input className="border rounded px-2 py-1 text-xs w-40" placeholder="Filter by application…" value={fApp} onChange={e => setFApp(e.target.value)} />
            <select className="border rounded px-2 py-1 text-xs" value={fStatus} onChange={e => setFStatus(e.target.value)}>
              <option value="">All statuses</option>
              {H39_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select className="border rounded px-2 py-1 text-xs" value={fRisk} onChange={e => setFRisk(e.target.value)}>
              <option value="">All risk levels</option>
              {H39_RISK_LEVELS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <select className="border rounded px-2 py-1 text-xs" value={fCategory} onChange={e => setFCategory(e.target.value)}>
              <option value="">All categories</option>
              {H39_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            {(fApp||fStatus||fRisk||fCategory) && (
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setFApp(""); setFStatus(""); setFRisk(""); setFCategory(""); }}>Clear</Button>
            )}
          </div>

          {/* Control list */}
          {listQ.isLoading ? (
            <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-14 w-full" />)}</div>
          ) : items.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-4">No autocomplete controls defined. {canManage && "Add a control to start tracking."}</p>
          ) : (
            <div className="divide-y rounded border">
              {items.map(c => (
                <div key={c.id} className="flex items-start justify-between p-3 hover:bg-muted/30 cursor-pointer" onClick={() => openDetail(c)}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">{c.applicationName}</span>
                      {c.formPageName && <span className="text-xs text-muted-foreground">/ {c.formPageName}</span>}
                      <Badge variant={h39StatusBadge(c.complianceStatus)} className="text-xs">{c.complianceStatus}</Badge>
                      {c.phiFieldInvolved && <Badge variant="outline" className="text-xs text-amber-600">PHI</Badge>}
                      {c.reviewOverdue && <Badge variant="outline" className="text-xs text-red-600">Review Overdue</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 flex gap-3 flex-wrap">
                      {c.phiFieldInvolved && !c.autocompleteDisabled && <span className="text-red-600">⚠ autocomplete on</span>}
                      {!c.passwordFieldProtected && <span className="text-red-600">⚠ password unprotected</span>}
                      {c.phiFieldInvolved && !c.browserRetentionPrevented && <span className="text-amber-600">⚠ retention risk</span>}
                      {c.sensitiveFieldCategory && <span>Category: {c.sensitiveFieldCategory}</span>}
                      {c.fieldCountReviewed && <span>{c.fieldCountReviewed} fields reviewed</span>}
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground shrink-0 ml-2 text-right">
                    {c.validationCodes.length > 0 && <div className="text-red-500">{c.validationCodes.length} issue{c.validationCodes.length>1?"s":""}</div>}
                    {c.riskLevel && <div>{c.riskLevel} risk</div>}
                    <div>{c.updatedAt.slice(0,10)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Add Control ───────────────────────────────────────────────────── */}
      <Dialog open={showCreate} onOpenChange={v => { setShowCreate(v); if (!v) setErrors({}); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add Sensitive Field Autocomplete Control</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Application Name *</label>
                <input className={fieldCls("w-full border rounded px-2 py-1 text-sm", errors, "applicationName")} aria-invalid={!!errors.applicationName} value={createForm.applicationName} onChange={e => { setCF("applicationName", e.target.value); clearErr(setErrors, "applicationName"); }} placeholder="e.g. Student Health Portal" />
                {fErr(errors, "applicationName")}
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Form / Page Name</label>
                <input className={fieldCls("w-full border rounded px-2 py-1 text-sm", errors, "formPageName")} aria-invalid={!!errors.formPageName} value={createForm.formPageName} onChange={e => { setCF("formPageName", e.target.value); clearErr(setErrors, "formPageName"); }} placeholder="Patient Registration" />
                {fErr(errors, "formPageName")}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Sensitive Field Category</label>
                <select className="w-full border rounded px-2 py-1 text-sm" value={createForm.sensitiveFieldCategory} onChange={e => setCF("sensitiveFieldCategory", e.target.value)}>
                  {H39_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Risk Level</label>
                <select className="w-full border rounded px-2 py-1 text-sm" value={createForm.riskLevel} onChange={e => setCF("riskLevel", e.target.value)}>
                  {H39_RISK_LEVELS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Field Count Reviewed</label>
                <input type="number" min={0} className="w-full border rounded px-2 py-1 text-sm" value={createForm.fieldCountReviewed} onChange={e => setCF("fieldCountReviewed", e.target.value)} placeholder="e.g. 12" />
              </div>
            </div>

            <fieldset className="border rounded p-3 space-y-2">
              <legend className="text-xs font-semibold px-1">Field Configuration</legend>
              <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={createForm.phiFieldInvolved} onChange={e => setCF("phiFieldInvolved", e.target.checked)} /> PHI fields present in this form</label>
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={createForm.autocompleteDisabled} onChange={e => setCF("autocompleteDisabled", e.target.checked)} />
                <span>Autocomplete disabled (<code className="text-xs">autocomplete="off"</code> set on sensitive fields)</span>
              </label>
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={createForm.passwordFieldProtected} onChange={e => setCF("passwordFieldProtected", e.target.checked)} />
                <span>Password fields use <code className="text-xs">autocomplete="new-password"</code> or <code className="text-xs">"current-password"</code></span>
              </label>
              <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={createForm.browserRetentionPrevented} onChange={e => setCF("browserRetentionPrevented", e.target.checked)} /> Browser retention of form data prevented (Cache-Control headers / no-store)</label>
              <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={createForm.sensitiveInputMaskingEnabled} onChange={e => setCF("sensitiveInputMaskingEnabled", e.target.checked)} /> Sensitive inputs are masked (type=password or equivalent)</label>
            </fieldset>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Review Performed Date</label>
                <input type="date" className="w-full border rounded px-2 py-1 text-sm" value={createForm.reviewPerformedDate} onChange={e => setCF("reviewPerformedDate", e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Next Review Date</label>
                <input type="date" className="w-full border rounded px-2 py-1 text-sm" value={createForm.nextReviewDate} onChange={e => setCF("nextReviewDate", e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Reviewer Name</label>
                <input className="w-full border rounded px-2 py-1 text-sm" value={createForm.reviewerName} onChange={e => setCF("reviewerName", e.target.value)} placeholder="Security Analyst" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Reviewer Title</label>
                <input className="w-full border rounded px-2 py-1 text-sm" value={createForm.reviewerTitle} onChange={e => setCF("reviewerTitle", e.target.value)} placeholder="CISO" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Evidence Reference</label>
              <input className="w-full border rounded px-2 py-1 text-sm" value={createForm.evidenceReference} onChange={e => setCF("evidenceReference", e.target.value)} placeholder="evidence://autocomplete-audit-2026.pdf" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreate(false); setErrors({}); }}>Cancel</Button>
            <Button onClick={submitCreate} disabled={createM.isPending}>{createM.isPending ? "Saving…" : "Add Control"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Detail ────────────────────────────────────────────────────────── */}
      {selected && showDetail && (
        <Dialog open={showDetail} onOpenChange={v => { setShowDetail(v); if (!v) setSelected(null); }}>
          <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 flex-wrap">
                {selected.applicationName}
                {selected.formPageName && <span className="text-xs font-normal text-muted-foreground">/ {selected.formPageName}</span>}
                <Badge variant={h39StatusBadge(selected.complianceStatus)}>{selected.complianceStatus}</Badge>
                {selected.reviewOverdue && <Badge variant="outline" className="text-xs text-red-600">Review Overdue</Badge>}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              {/* Application / form info */}
              <div className="grid grid-cols-2 gap-2 text-xs">
                {selected.sensitiveFieldCategory && <div><p className="font-medium">Field Category</p><p className="text-muted-foreground">{selected.sensitiveFieldCategory}</p></div>}
                {selected.fieldCountReviewed && <div><p className="font-medium">Fields Reviewed</p><p className="text-muted-foreground">{selected.fieldCountReviewed}</p></div>}
                {selected.riskLevel && <div><p className="font-medium">Risk Level</p><p className="text-muted-foreground">{selected.riskLevel}</p></div>}
              </div>

              {/* Sensitive field config */}
              <div className="rounded border p-2">
                <p className="text-xs font-semibold mb-1">Sensitive Field Configuration</p>
                <CheckRow label="PHI fields present"              ok={!selected.phiFieldInvolved} />
                <CheckRow label="Autocomplete disabled"           ok={selected.autocompleteDisabled} na={!selected.phiFieldInvolved} />
                <CheckRow label="Password autocomplete hardened"  ok={selected.passwordFieldProtected} />
                <CheckRow label="Browser retention prevented"     ok={selected.browserRetentionPrevented} na={!selected.phiFieldInvolved} />
                <CheckRow label="Sensitive input masking enabled" ok={selected.sensitiveInputMaskingEnabled} />
                <CheckRow label="Secure form configured"          ok={selected.secureFormConfigured} />
              </div>

              {/* Review info */}
              <div className="rounded border p-2 text-xs space-y-0.5">
                <p className="font-semibold mb-1">Review Information</p>
                {selected.reviewPerformedDate && <div className="flex justify-between"><span className="text-muted-foreground">Review performed</span><span>{selected.reviewPerformedDate.slice(0,10)}</span></div>}
                {selected.nextReviewDate && <div className="flex justify-between"><span className="text-muted-foreground">Next review due</span><span className={selected.reviewOverdue ? "text-red-600 font-semibold" : ""}>{selected.nextReviewDate.slice(0,10)}</span></div>}
                {selected.reviewerName && <div className="flex justify-between"><span className="text-muted-foreground">Reviewer</span><span>{selected.reviewerName}{selected.reviewerTitle ? ` · ${selected.reviewerTitle}` : ""}</span></div>}
                {selected.lastValidationDate && <div className="flex justify-between"><span className="text-muted-foreground">Last validated</span><span>{selected.lastValidationDate.slice(0,10)}</span></div>}
              </div>

              {selected.evidenceReference && (
                <div className="rounded bg-blue-50 border border-blue-200 p-2 text-xs">
                  <p className="font-semibold text-blue-800 mb-0.5">Evidence Reference</p>
                  <p className="font-mono text-blue-700">{selected.evidenceReference}</p>
                </div>
              )}

              {selected.validationCodes.length > 0 && (
                <div className="rounded bg-red-50 border border-red-200 p-2">
                  <p className="text-xs font-semibold text-red-800 mb-1">Compliance Issues</p>
                  <div className="space-y-1">
                    {selected.validationCodes.map(c => (
                      <div key={c} className="flex items-center gap-2">
                        <span className="text-xs font-mono bg-red-100 text-red-700 rounded px-1.5 py-0.5">{c}</span>
                        <span className="text-xs text-red-700">{H39_CODE_LABELS[c] ?? c}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selected.remediationNotes && (
                <div>
                  <p className="text-xs font-medium mb-0.5">Remediation Notes</p>
                  <p className="text-xs text-muted-foreground whitespace-pre-wrap">{selected.remediationNotes}</p>
                </div>
              )}
            </div>
            {canManage && (
              <DialogFooter className="flex flex-wrap gap-2 sm:justify-start">
                <Button size="sm" onClick={() => validateM.mutate()} disabled={validateM.isPending}>{validateM.isPending ? "Validating…" : "Validate"}</Button>
                <Button size="sm" variant="outline" onClick={() => { setReviewForm({ reviewerName: "", reviewerTitle: "", reviewNotes: "", nextReviewDate: "", evidenceReference: "" }); setShowReview(true); }}>Complete Review</Button>
                <Button size="sm" variant="outline" onClick={() => { setRemediateForm({ remediationNotes: "" }); setShowRemediate(true); }}>Add Remediation</Button>
                <Button size="sm" variant="destructive" onClick={() => setShowDelete(true)}>Delete</Button>
              </DialogFooter>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* ── Complete Review ───────────────────────────────────────────────── */}
      {selected && showReview && (
        <Dialog open={showReview} onOpenChange={v => { setShowReview(v); if (!v) setReviewErrors({}); }}>
          <DialogContent>
            <DialogHeader><DialogTitle>Complete Security Review</DialogTitle></DialogHeader>
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">Reviewer Name *</label>
                  <input className={fieldCls("w-full border rounded px-2 py-1 text-sm", reviewErrors, "reviewerName")} aria-invalid={!!reviewErrors.reviewerName} value={reviewForm.reviewerName} onChange={e => { setReviewForm(f => ({ ...f, reviewerName: e.target.value })); clearErr(setReviewErrors, "reviewerName"); }} placeholder="Security Analyst" />
                  {fErr(reviewErrors, "reviewerName")}
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Reviewer Title</label>
                  <input className="w-full border rounded px-2 py-1 text-sm" value={reviewForm.reviewerTitle} onChange={e => setReviewForm(f => ({ ...f, reviewerTitle: e.target.value }))} placeholder="CISO" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Next Review Date</label>
                <input type="date" className="w-full border rounded px-2 py-1 text-sm" value={reviewForm.nextReviewDate} onChange={e => setReviewForm(f => ({ ...f, nextReviewDate: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Review Notes</label>
                <textarea className="w-full border rounded px-2 py-1 text-sm" rows={3} value={reviewForm.reviewNotes} onChange={e => setReviewForm(f => ({ ...f, reviewNotes: e.target.value }))} placeholder="Findings from autocomplete audit…" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Evidence Reference</label>
                <input className="w-full border rounded px-2 py-1 text-sm" value={reviewForm.evidenceReference} onChange={e => setReviewForm(f => ({ ...f, evidenceReference: e.target.value }))} placeholder="evidence://review-2026.pdf" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowReview(false)}>Cancel</Button>
              <Button onClick={() => { const errs: Record<string,string> = {}; required(errs, "reviewerName", reviewForm.reviewerName, "Reviewer Name"); if (Object.keys(errs).length) { setReviewErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } setReviewErrors({}); reviewM.mutate(); }} disabled={reviewM.isPending}>{reviewM.isPending ? "Saving…" : "Complete Review"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Add Remediation ───────────────────────────────────────────────── */}
      {selected && showRemediate && (
        <Dialog open={showRemediate} onOpenChange={setShowRemediate}>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Remediation Notes</DialogTitle></DialogHeader>
            <div>
              <label className="block text-xs font-medium mb-1">Remediation Notes *</label>
              <textarea className="w-full border rounded px-2 py-1 text-sm" rows={4} value={remediateForm.remediationNotes} onChange={e => setRemediateForm(f => ({ ...f, remediationNotes: e.target.value }))} placeholder="e.g. Add autocomplete=off to all PHI fields in the patient registration form…" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRemediate(false)}>Cancel</Button>
              <Button onClick={() => remediateM.mutate()} disabled={remediateM.isPending}>{remediateM.isPending ? "Saving…" : "Add Remediation"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Delete ────────────────────────────────────────────────────────── */}
      {selected && showDelete && (
        <Dialog open={showDelete} onOpenChange={setShowDelete}>
          <DialogContent>
            <DialogHeader><DialogTitle>Delete Autocomplete Control</DialogTitle></DialogHeader>
            <p className="text-sm">Permanently delete the control for <strong>{selected?.applicationName}{selected?.formPageName ? ` / ${selected.formPageName}` : ""}</strong>? This cannot be undone.</p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDelete(false)}>Cancel</Button>
              <Button variant="destructive" onClick={() => deleteM.mutate()} disabled={deleteM.isPending}>{deleteM.isPending ? "Deleting…" : "Delete"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// HIPAA-38 — Protection Against Data Exposure: No PHI in Browser Cache
// ══════════════════════════════════════════════════════════════════════════════

interface Hipaa38Summary {
  total: number; compliant: number; nonCompliant: number; phiSystems: number;
  storageIssues: number; cacheIssues: number; pendingReviews: number;
  cookieIssues: number; compliancePercentage: number;
}

interface Hipaa38Control {
  id: string; organizationId: string;
  applicationName: string; frontendPlatform?: string; phiHandlingEnabled: boolean;
  localStorageUsageDetected: boolean; sessionStorageUsageDetected: boolean;
  browserCacheDisabled: boolean; sensitiveDataCachingDisabled: boolean;
  cookieSecurityConfigured: boolean; httpOnlyCookieEnabled: boolean;
  secureCookieEnabled: boolean; sameSitePolicy?: string; clientEncryptionEnabled: boolean;
  storageScanPerformedDate?: string; lastReviewDate?: string;
  reviewerName?: string; reviewerTitle?: string; reviewNotes?: string;
  evidenceReference?: string; complianceStatus: string; riskLevel?: string;
  remediationRequired: boolean; remediationNotes?: string;
  createdAt: string; updatedAt: string;
  reviewOverdue: boolean; validationCodes: string[];
}

function useAuth38() {
  return (url: string, opts: RequestInit = {}) => {
    const token = sessionStorage.getItem("dotnetbot.jwt.session") || localStorage.getItem("dotnetbot.jwt") || "";
    return fetch(url, { ...opts, headers: { ...(opts.headers as Record<string,string>||{}), "Content-Type": "application/json", Authorization: `Bearer ${token}` } });
  };
}

const H38_PLATFORMS     = ["React","Angular","Vue","VanillaJS","iOS","Android","Blazor","Other"];
const H38_SAMESITE      = ["Strict","Lax","None"];
const H38_RISK_LEVELS   = ["Low","Medium","High","Critical"];
const H38_STATUSES      = ["Compliant","NonCompliant","Pending","UnderReview"];

const H38_CODE_LABELS: Record<string, string> = {
  PHI_LOCAL_STORAGE_DETECTED:  "PHI in localStorage",
  PHI_SESSION_STORAGE_DETECTED:"PHI in sessionStorage",
  PHI_BROWSER_CACHE_ENABLED:   "Browser cache active for PHI",
  COOKIE_SECURITY_REQUIRED:    "Cookie security missing",
  STORAGE_REVIEW_REQUIRED:     "Storage scan not performed",
  EVIDENCE_REFERENCE_REQUIRED: "No evidence reference",
  SECURITY_REVIEW_OVERDUE:     "Security review overdue",
};

function h38StatusBadge(s: string): "default"|"secondary"|"outline"|"destructive" {
  if (s === "Compliant")   return "default";
  if (s === "NonCompliant")return "destructive";
  if (s === "UnderReview") return "secondary";
  return "outline";
}

const H38_BLANK = {
  applicationName: "", frontendPlatform: "React",
  phiHandlingEnabled: true, localStorageUsageDetected: false, sessionStorageUsageDetected: false,
  browserCacheDisabled: false, sensitiveDataCachingDisabled: false,
  httpOnlyCookieEnabled: false, secureCookieEnabled: false, sameSitePolicy: "Strict",
  clientEncryptionEnabled: false, storageScanPerformedDate: "",
  reviewerName: "", reviewerTitle: "", evidenceReference: "", riskLevel: "Medium",
};

function HipaaBrowserDataProtectionSection({ canManage }: { canManage: boolean }) {
  const authFetch = useAuth38();
  const qc = useQueryClient();
  const { toast } = useToast();
  const BASE = "/api/organization/compliance/hipaa/browser-data-protection";

  const summaryQ = useQuery<Hipaa38Summary>({
    queryKey: ["hipaa38-summary"],
    queryFn: () => authFetch(`${BASE}/summary`).then(r => r.json()),
  });

  const [fApp,    setFApp]    = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fRisk,   setFRisk]   = useState("");
  const [fPlatform, setFPlatform] = useState("");

  const listQ = useQuery<Hipaa38Control[]>({
    queryKey: ["hipaa38-list", fApp, fStatus, fRisk, fPlatform],
    queryFn: () => {
      const p = new URLSearchParams();
      if (fApp)      p.set("application", fApp);
      if (fStatus)   p.set("status", fStatus);
      if (fRisk)     p.set("riskLevel", fRisk);
      if (fPlatform) p.set("platform", fPlatform);
      return authFetch(`${BASE}?${p}`).then(r => r.json());
    },
  });

  const [showCreate,    setShowCreate]    = useState(false);
  const [showDetail,    setShowDetail]    = useState(false);
  const [showReview,    setShowReview]    = useState(false);
  const [showRemediate, setShowRemediate] = useState(false);
  const [showDelete,    setShowDelete]    = useState(false);
  const [selected,      setSelected]      = useState<Hipaa38Control|null>(null);

  const [createForm, setCreateForm] = useState({ ...H38_BLANK });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const setCF = (k: string, v: unknown) => setCreateForm(f => ({ ...f, [k]: v }));
  const submitCreate = () => {
    const errs: Record<string, string> = {};
    required(errs, "applicationName", createForm.applicationName, "Application Name");
    if (Object.keys(errs).length) {
      setErrors(errs);
      toast({ title: "Please correct the highlighted fields.", variant: "destructive" });
      return;
    }
    setErrors({});
    createM.mutate();
  };

  const [reviewForm,    setReviewForm]    = useState({ reviewerName: "", reviewerTitle: "", reviewNotes: "", evidenceReference: "" });
  const [reviewErrors,  setReviewErrors]  = useState<Record<string,string>>({});
  const [remediateForm, setRemediateForm] = useState({ remediationNotes: "" });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["hipaa38-summary"] });
    qc.invalidateQueries({ queryKey: ["hipaa38-list"] });
  };

  const openDetail = (c: Hipaa38Control) => { setSelected(c); setShowDetail(true); };

  const createM = useMutation({
    mutationFn: () => authFetch(BASE, { method: "POST", body: JSON.stringify({
      applicationName: createForm.applicationName,
      frontendPlatform: createForm.frontendPlatform || null,
      phiHandlingEnabled: createForm.phiHandlingEnabled,
      localStorageUsageDetected: createForm.localStorageUsageDetected,
      sessionStorageUsageDetected: createForm.sessionStorageUsageDetected,
      browserCacheDisabled: createForm.browserCacheDisabled,
      sensitiveDataCachingDisabled: createForm.sensitiveDataCachingDisabled,
      httpOnlyCookieEnabled: createForm.httpOnlyCookieEnabled,
      secureCookieEnabled: createForm.secureCookieEnabled,
      sameSitePolicy: createForm.sameSitePolicy || null,
      clientEncryptionEnabled: createForm.clientEncryptionEnabled,
      storageScanPerformedDate: createForm.storageScanPerformedDate || null,
      reviewerName: createForm.reviewerName || null,
      reviewerTitle: createForm.reviewerTitle || null,
      evidenceReference: createForm.evidenceReference || null,
      riskLevel: createForm.riskLevel || null,
    })}).then(async r => { const d = await r.json(); if (!r.ok) throw d; return d; }),
    onSuccess: () => { invalidate(); setShowCreate(false); setCreateForm({ ...H38_BLANK }); setErrors({}); toast({ title: "Browser protection control added" }); },
    onError: (e: any) => { const serverErrs = extractServerErrors(e); if (serverErrs) { setErrors(serverErrs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); } else toast({ title: "Unable to save", description: e?.message, variant: "destructive" }); },
  });

  const validateM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}/validate`, { method: "POST", body: "{}" }).then(async r => { const d = await r.json(); if (!r.ok) throw d; return d; }),
    onSuccess: (d: any) => { invalidate(); toast({ title: d.isCompliant ? "✓ Compliant" : "Issues detected", description: d.validationCodes?.join(", ") || undefined }); },
    onError: (e: any) => toast({ title: "Validation error", description: e?.error, variant: "destructive" }),
  });

  const reviewM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}/review`, { method: "POST", body: JSON.stringify({
      reviewerName: reviewForm.reviewerName, reviewerTitle: reviewForm.reviewerTitle || null,
      reviewNotes: reviewForm.reviewNotes || null, evidenceReference: reviewForm.evidenceReference || null,
    })}).then(async r => { const d = await r.json(); if (!r.ok) throw d; return d; }),
    onSuccess: (d: any) => { invalidate(); setSelected(d); setShowReview(false); setReviewErrors({}); toast({ title: "Review completed" }); },
    onError: (e: any) => { const s = extractServerErrors(e); if (s && Object.keys(s).length) { setReviewErrors(s); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); } else toast({ title: "Unable to complete action", description: e?.message, variant: "destructive" }); },
  });

  const remediateM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}/remediation`, { method: "POST", body: JSON.stringify({ remediationNotes: remediateForm.remediationNotes }) }).then(async r => { const d = await r.json(); if (!r.ok) throw d; return d; }),
    onSuccess: (d: any) => { invalidate(); setSelected(d); setShowRemediate(false); toast({ title: "Remediation recorded" }); },
    onError: (e: any) => toast({ title: "Unable to complete action", description: e?.message, variant: "destructive" }),
  });

  const deleteM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}`, { method: "DELETE" }).then(async r => { if (!r.ok) { const d = await r.json(); throw d; } }),
    onSuccess: () => { invalidate(); setShowDelete(false); setShowDetail(false); setSelected(null); toast({ title: "Control deleted" }); },
    onError: (e: any) => toast({ title: "Unable to delete", description: e?.message, variant: "destructive" }),
  });

  const sum   = summaryQ.data;
  const items = listQ.data ?? [];
  const issueItems = items.filter(c => c.validationCodes.length > 0);

  const CheckRow = ({ label, ok, na }: { label: string; ok: boolean; na?: boolean }) => (
    <div className="flex items-center justify-between py-0.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={`text-xs font-medium ${na ? "text-gray-400" : ok ? "text-green-600" : "text-red-600"}`}>{na ? "N/A" : ok ? "✓" : "✗"}</span>
    </div>
  );

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base font-semibold">Browser Data Protection — No PHI in Cache / Storage</CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">Verify that PHI/ePHI is never persisted in browser localStorage, sessionStorage, browser cache, or insecure cookies.</p>
            </div>
            {canManage && (
              <Button size="sm" onClick={() => { setCreateForm({ ...H38_BLANK }); setErrors({}); setShowCreate(true); }}>+ Add Control</Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Summary metrics */}
          {sum && (
            <>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {[
                  { label: "Total Applications", value: sum.total,         color: "" },
                  { label: "Compliant",          value: sum.compliant,     color: "text-green-600" },
                  { label: "Non-Compliant",      value: sum.nonCompliant,  color: sum.nonCompliant > 0 ? "text-red-600" : "" },
                  { label: "PHI Systems",        value: sum.phiSystems,    color: sum.phiSystems > 0 ? "text-amber-600" : "" },
                  { label: "Storage Issues",     value: sum.storageIssues, color: sum.storageIssues > 0 ? "text-red-600" : "" },
                  { label: "Cache Issues",       value: sum.cacheIssues,   color: sum.cacheIssues > 0 ? "text-red-600" : "" },
                  { label: "Cookie Issues",      value: sum.cookieIssues,  color: sum.cookieIssues > 0 ? "text-red-600" : "" },
                  { label: "Pending Reviews",    value: sum.pendingReviews,color: sum.pendingReviews > 0 ? "text-amber-600" : "" },
                ].map(m => (
                  <div key={m.label} className="rounded border p-2 text-center">
                    <div className={`text-xl font-bold ${m.color}`}>{m.value}</div>
                    <div className="text-xs text-muted-foreground leading-tight">{m.label}</div>
                  </div>
                ))}
              </div>
              {sum.total > 0 && (
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span>Compliance</span>
                    <span className={sum.compliancePercentage === 100 ? "text-green-600 font-semibold" : "text-amber-600"}>{sum.compliancePercentage}%</span>
                  </div>
                  <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${sum.compliancePercentage === 100 ? "bg-green-500" : "bg-amber-500"}`} style={{ width: `${sum.compliancePercentage}%` }} />
                  </div>
                </div>
              )}
            </>
          )}

          {/* Issues panel */}
          {issueItems.length > 0 && (
            <div className="rounded border border-red-200 bg-red-50 p-3">
              <p className="text-xs font-semibold text-red-800 mb-1">Applications with data exposure issues ({issueItems.length})</p>
              <ul className="space-y-1">
                {issueItems.slice(0, 5).map(c => (
                  <li key={c.id} className="text-xs text-red-700 flex justify-between items-start gap-2">
                    <button className="underline hover:no-underline text-left" onClick={() => openDetail(c)}>{c.applicationName}</button>
                    <div className="flex flex-wrap gap-1 justify-end shrink-0">
                      {c.validationCodes.slice(0, 2).map(code => (
                        <span key={code} className="font-mono bg-red-100 rounded px-1">{code.replace(/_/g," ")}</span>
                      ))}
                      {c.validationCodes.length > 2 && <span>+{c.validationCodes.length-2}</span>}
                    </div>
                  </li>
                ))}
                {issueItems.length > 5 && <li className="text-xs text-red-600">+{issueItems.length - 5} more…</li>}
              </ul>
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-wrap gap-2">
            <input className="border rounded px-2 py-1 text-xs w-40" placeholder="Filter by app name…" value={fApp} onChange={e => setFApp(e.target.value)} />
            <select className="border rounded px-2 py-1 text-xs" value={fStatus} onChange={e => setFStatus(e.target.value)}>
              <option value="">All statuses</option>
              {H38_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select className="border rounded px-2 py-1 text-xs" value={fRisk} onChange={e => setFRisk(e.target.value)}>
              <option value="">All risk levels</option>
              {H38_RISK_LEVELS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <select className="border rounded px-2 py-1 text-xs" value={fPlatform} onChange={e => setFPlatform(e.target.value)}>
              <option value="">All platforms</option>
              {H38_PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            {(fApp||fStatus||fRisk||fPlatform) && (
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setFApp(""); setFStatus(""); setFRisk(""); setFPlatform(""); }}>Clear</Button>
            )}
          </div>

          {/* Control list */}
          {listQ.isLoading ? (
            <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-14 w-full" />)}</div>
          ) : items.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-4">No browser protection controls defined. {canManage && "Add a control to start tracking."}</p>
          ) : (
            <div className="divide-y rounded border">
              {items.map(c => (
                <div key={c.id} className="flex items-start justify-between p-3 hover:bg-muted/30 cursor-pointer" onClick={() => openDetail(c)}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">{c.applicationName}</span>
                      {c.frontendPlatform && <span className="text-xs text-muted-foreground">{c.frontendPlatform}</span>}
                      <Badge variant={h38StatusBadge(c.complianceStatus)} className="text-xs">{c.complianceStatus}</Badge>
                      {c.phiHandlingEnabled && <Badge variant="outline" className="text-xs text-amber-600">PHI</Badge>}
                      {c.reviewOverdue && <Badge variant="outline" className="text-xs text-red-600">Review Overdue</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 flex gap-3 flex-wrap">
                      {c.localStorageUsageDetected  && <span className="text-red-600">⚠ localStorage</span>}
                      {c.sessionStorageUsageDetected && <span className="text-red-600">⚠ sessionStorage</span>}
                      {c.phiHandlingEnabled && !c.browserCacheDisabled && <span className="text-red-600">⚠ cache enabled</span>}
                      {c.riskLevel && <span>Risk: {c.riskLevel}</span>}
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground shrink-0 ml-2 text-right">
                    {c.validationCodes.length > 0 && <div className="text-red-500">{c.validationCodes.length} issue{c.validationCodes.length>1?"s":""}</div>}
                    <div>{c.updatedAt.slice(0,10)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Add Control ───────────────────────────────────────────────────── */}
      <Dialog open={showCreate} onOpenChange={v => { setShowCreate(v); if (!v) setErrors({}); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add Browser Data Protection Control</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <label className="block text-xs font-medium mb-1">Application Name *</label>
              <input className={fieldCls("w-full border rounded px-2 py-1 text-sm", errors, "applicationName")} aria-invalid={!!errors.applicationName} value={createForm.applicationName} onChange={e => { setCF("applicationName", e.target.value); clearErr(setErrors, "applicationName"); }} placeholder="e.g. Student Portal" />
              {fErr(errors, "applicationName")}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Frontend Platform</label>
                <select className="w-full border rounded px-2 py-1 text-sm" value={createForm.frontendPlatform} onChange={e => setCF("frontendPlatform", e.target.value)}>
                  {H38_PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Risk Level</label>
                <select className="w-full border rounded px-2 py-1 text-sm" value={createForm.riskLevel} onChange={e => setCF("riskLevel", e.target.value)}>
                  {H38_RISK_LEVELS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>

            <fieldset className="border rounded p-3 space-y-2">
              <legend className="text-xs font-semibold px-1">Storage Detection</legend>
              <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={createForm.phiHandlingEnabled} onChange={e => setCF("phiHandlingEnabled", e.target.checked)} /> PHI Handling Enabled in this application</label>
              <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={createForm.localStorageUsageDetected} onChange={e => setCF("localStorageUsageDetected", e.target.checked)} /><span className={createForm.localStorageUsageDetected ? "text-red-600 font-medium" : ""}>localStorage usage detected</span></label>
              <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={createForm.sessionStorageUsageDetected} onChange={e => setCF("sessionStorageUsageDetected", e.target.checked)} /><span className={createForm.sessionStorageUsageDetected ? "text-red-600 font-medium" : ""}>sessionStorage usage detected</span></label>
            </fieldset>

            <fieldset className="border rounded p-3 space-y-2">
              <legend className="text-xs font-semibold px-1">Cache Settings</legend>
              <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={createForm.browserCacheDisabled} onChange={e => setCF("browserCacheDisabled", e.target.checked)} /> Browser cache disabled for PHI responses</label>
              <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={createForm.sensitiveDataCachingDisabled} onChange={e => setCF("sensitiveDataCachingDisabled", e.target.checked)} /> Sensitive data caching disabled</label>
            </fieldset>

            <fieldset className="border rounded p-3 space-y-2">
              <legend className="text-xs font-semibold px-1">Cookie Security</legend>
              <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={createForm.httpOnlyCookieEnabled} onChange={e => setCF("httpOnlyCookieEnabled", e.target.checked)} /> HttpOnly cookie enabled</label>
              <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={createForm.secureCookieEnabled} onChange={e => setCF("secureCookieEnabled", e.target.checked)} /> Secure cookie enabled</label>
              <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={createForm.clientEncryptionEnabled} onChange={e => setCF("clientEncryptionEnabled", e.target.checked)} /> Client-side encryption enabled</label>
              <div>
                <label className="block text-xs mb-1">SameSite Policy</label>
                <select className="border rounded px-2 py-1 text-xs" value={createForm.sameSitePolicy} onChange={e => setCF("sameSitePolicy", e.target.value)}>
                  <option value="">— select —</option>
                  {H38_SAMESITE.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </fieldset>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Storage Scan Date</label>
                <input type="date" className="w-full border rounded px-2 py-1 text-sm" value={createForm.storageScanPerformedDate} onChange={e => setCF("storageScanPerformedDate", e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Reviewer Name</label>
                <input className="w-full border rounded px-2 py-1 text-sm" value={createForm.reviewerName} onChange={e => setCF("reviewerName", e.target.value)} placeholder="Security Team" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Evidence Reference</label>
              <input className="w-full border rounded px-2 py-1 text-sm" value={createForm.evidenceReference} onChange={e => setCF("evidenceReference", e.target.value)} placeholder="evidence://storage-scan-2026.pdf" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreate(false); setErrors({}); }}>Cancel</Button>
            <Button onClick={submitCreate} disabled={createM.isPending}>{createM.isPending ? "Saving…" : "Add Control"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Detail ────────────────────────────────────────────────────────── */}
      {selected && showDetail && (
        <Dialog open={showDetail} onOpenChange={v => { setShowDetail(v); if (!v) setSelected(null); }}>
          <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 flex-wrap">
                {selected.applicationName}
                {selected.frontendPlatform && <span className="text-xs font-normal text-muted-foreground">{selected.frontendPlatform}</span>}
                <Badge variant={h38StatusBadge(selected.complianceStatus)}>{selected.complianceStatus}</Badge>
                {selected.reviewOverdue && <Badge variant="outline" className="text-xs text-red-600">Review Overdue</Badge>}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              {/* Storage config */}
              <div className="rounded border p-2">
                <p className="text-xs font-semibold mb-1">Storage Configuration</p>
                <CheckRow label="PHI handling enabled"       ok={!selected.phiHandlingEnabled} />
                <CheckRow label="localStorage — no PHI"     ok={!selected.localStorageUsageDetected} na={!selected.phiHandlingEnabled} />
                <CheckRow label="sessionStorage — no PHI"   ok={!selected.sessionStorageUsageDetected} na={!selected.phiHandlingEnabled} />
              </div>
              <div className="rounded border p-2">
                <p className="text-xs font-semibold mb-1">Cache Settings</p>
                <CheckRow label="Browser cache disabled"    ok={selected.browserCacheDisabled} na={!selected.phiHandlingEnabled} />
                <CheckRow label="Sensitive caching off"     ok={selected.sensitiveDataCachingDisabled} na={!selected.phiHandlingEnabled} />
              </div>
              <div className="rounded border p-2">
                <p className="text-xs font-semibold mb-1">Cookie Security</p>
                <CheckRow label="HttpOnly"                  ok={selected.httpOnlyCookieEnabled} na={!selected.phiHandlingEnabled} />
                <CheckRow label="Secure"                    ok={selected.secureCookieEnabled}   na={!selected.phiHandlingEnabled} />
                <CheckRow label="Client encryption"         ok={selected.clientEncryptionEnabled} />
                {selected.sameSitePolicy && (
                  <div className="flex justify-between py-0.5 text-xs">
                    <span className="text-muted-foreground">SameSite</span>
                    <span className={selected.sameSitePolicy === "Strict" ? "text-green-600" : "text-amber-600"}>{selected.sameSitePolicy}</span>
                  </div>
                )}
              </div>
              <div className="rounded border p-2">
                <p className="text-xs font-semibold mb-1">Review Information</p>
                <div className="space-y-0.5 text-xs">
                  {selected.storageScanPerformedDate && <div className="flex justify-between"><span className="text-muted-foreground">Last scan</span><span>{selected.storageScanPerformedDate.slice(0,10)}</span></div>}
                  {selected.lastReviewDate && <div className="flex justify-between"><span className="text-muted-foreground">Last review</span><span>{selected.lastReviewDate.slice(0,10)}</span></div>}
                  {selected.reviewerName && <div className="flex justify-between"><span className="text-muted-foreground">Reviewer</span><span>{selected.reviewerName}{selected.reviewerTitle ? ` · ${selected.reviewerTitle}` : ""}</span></div>}
                  {selected.riskLevel && <div className="flex justify-between"><span className="text-muted-foreground">Risk level</span><span>{selected.riskLevel}</span></div>}
                </div>
              </div>
              {selected.evidenceReference && (
                <div className="rounded bg-blue-50 border border-blue-200 p-2 text-xs">
                  <p className="font-semibold text-blue-800 mb-0.5">Evidence Reference</p>
                  <p className="font-mono text-blue-700">{selected.evidenceReference}</p>
                </div>
              )}
              {selected.validationCodes.length > 0 && (
                <div className="rounded bg-red-50 border border-red-200 p-2">
                  <p className="text-xs font-semibold text-red-800 mb-1">Compliance Issues</p>
                  <div className="space-y-1">
                    {selected.validationCodes.map(c => (
                      <div key={c} className="flex items-center gap-2">
                        <span className="text-xs font-mono bg-red-100 text-red-700 rounded px-1.5 py-0.5">{c}</span>
                        <span className="text-xs text-red-700">{H38_CODE_LABELS[c] ?? c}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {selected.remediationNotes && (
                <div>
                  <p className="text-xs font-medium mb-0.5">Remediation Notes</p>
                  <p className="text-xs text-muted-foreground whitespace-pre-wrap">{selected.remediationNotes}</p>
                </div>
              )}

              <LinkedSourceRow entityType="HipaaBrowserDataProtectionControl" entityId={selected.id} canManage={canManage} />
            </div>
            {canManage && (
              <DialogFooter className="flex flex-wrap gap-2 sm:justify-start">
                <Button size="sm" onClick={() => validateM.mutate()} disabled={validateM.isPending}>{validateM.isPending ? "Validating…" : "Validate"}</Button>
                <Button size="sm" variant="outline" onClick={() => { setReviewForm({ reviewerName: "", reviewerTitle: "", reviewNotes: "", evidenceReference: "" }); setShowReview(true); }}>Complete Review</Button>
                <Button size="sm" variant="outline" onClick={() => { setRemediateForm({ remediationNotes: "" }); setShowRemediate(true); }}>Add Remediation</Button>
                <Button size="sm" variant="destructive" onClick={() => setShowDelete(true)}>Delete</Button>
              </DialogFooter>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* ── Complete Review ───────────────────────────────────────────────── */}
      {selected && showReview && (
        <Dialog open={showReview} onOpenChange={v => { setShowReview(v); if (!v) setReviewErrors({}); }}>
          <DialogContent>
            <DialogHeader><DialogTitle>Complete Security Review</DialogTitle></DialogHeader>
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">Reviewer Name *</label>
                  <input className={fieldCls("w-full border rounded px-2 py-1 text-sm", reviewErrors, "reviewerName")} aria-invalid={!!reviewErrors.reviewerName} value={reviewForm.reviewerName} onChange={e => { setReviewForm(f => ({ ...f, reviewerName: e.target.value })); clearErr(setReviewErrors, "reviewerName"); }} placeholder="Security Analyst" />
                  {fErr(reviewErrors, "reviewerName")}
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Reviewer Title</label>
                  <input className="w-full border rounded px-2 py-1 text-sm" value={reviewForm.reviewerTitle} onChange={e => setReviewForm(f => ({ ...f, reviewerTitle: e.target.value }))} placeholder="CISO" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Review Notes</label>
                <textarea className="w-full border rounded px-2 py-1 text-sm" rows={3} value={reviewForm.reviewNotes} onChange={e => setReviewForm(f => ({ ...f, reviewNotes: e.target.value }))} placeholder="Findings and actions taken…" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Evidence Reference</label>
                <input className="w-full border rounded px-2 py-1 text-sm" value={reviewForm.evidenceReference} onChange={e => setReviewForm(f => ({ ...f, evidenceReference: e.target.value }))} placeholder="evidence://review-2026.pdf" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowReview(false)}>Cancel</Button>
              <Button onClick={() => { const errs: Record<string,string> = {}; required(errs, "reviewerName", reviewForm.reviewerName, "Reviewer Name"); if (Object.keys(errs).length) { setReviewErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } setReviewErrors({}); reviewM.mutate(); }} disabled={reviewM.isPending}>{reviewM.isPending ? "Saving…" : "Complete Review"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Add Remediation ───────────────────────────────────────────────── */}
      {selected && showRemediate && (
        <Dialog open={showRemediate} onOpenChange={setShowRemediate}>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Remediation Notes</DialogTitle></DialogHeader>
            <div>
              <label className="block text-xs font-medium mb-1">Remediation Notes *</label>
              <textarea className="w-full border rounded px-2 py-1 text-sm" rows={4} value={remediateForm.remediationNotes} onChange={e => setRemediateForm(f => ({ ...f, remediationNotes: e.target.value }))} placeholder="Describe the remediation steps taken or planned…" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRemediate(false)}>Cancel</Button>
              <Button onClick={() => remediateM.mutate()} disabled={remediateM.isPending}>{remediateM.isPending ? "Saving…" : "Add Remediation"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Delete ────────────────────────────────────────────────────────── */}
      {selected && showDelete && (
        <Dialog open={showDelete} onOpenChange={setShowDelete}>
          <DialogContent>
            <DialogHeader><DialogTitle>Delete Browser Protection Control</DialogTitle></DialogHeader>
            <p className="text-sm">Permanently delete the control for <strong>{selected?.applicationName}</strong>? This cannot be undone.</p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDelete(false)}>Cancel</Button>
              <Button variant="destructive" onClick={() => deleteM.mutate()} disabled={deleteM.isPending}>{deleteM.isPending ? "Deleting…" : "Delete"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// HIPAA-34 — Incident Management & Breach Investigation Workflow
// ══════════════════════════════════════════════════════════════════════════════

interface Hipaa34Summary {
  total: number; openInvestigations: number; criticalIncidents: number;
  phiIncidents: number; overdueInvestigations: number; resolvedIncidents: number;
  closedIncidents: number; criticalUnresolved: number; compliancePercentage: number;
}

interface Hipaa34Incident {
  id: string; organizationId: string;
  incidentNumber: string; title: string; incidentType: string; description?: string;
  detectionSource?: string; reportedBy?: string; reportedDate?: string;
  incidentOwner?: string; assignedInvestigator?: string; investigatorAssignedAt?: string;
  severityLevel: string; riskLevel?: string;
  phiInvolved: boolean; phiCategoriesAffected?: string; individualsAffected?: number;
  systemsAffected?: string;
  investigationStatus: string; investigationStartDate?: string; riskAssessmentDate?: string;
  investigationFindings?: string; rootCause?: string;
  containmentActions?: string; correctiveActions?: string;
  evidenceReferences?: string; documentationRefs?: string;
  breachDetermination?: string; notificationRequired: boolean; notificationTrackingRef?: string;
  resolutionDate?: string; reviewDate?: string; closedAt?: string;
  complianceStatus: string; remediationRequired: boolean; remediationNotes?: string;
  createdAt: string; updatedAt: string;
  investigationOverdue: boolean; isCriticalUnresolved: boolean; validationCodes: string[];
}

function useAuth34() {
  return (url: string, opts: RequestInit = {}) => {
    const token = sessionStorage.getItem("dotnetbot.jwt.session") || localStorage.getItem("dotnetbot.jwt") || "";
    return fetch(url, { ...opts, headers: { ...(opts.headers as Record<string,string>||{}), "Content-Type": "application/json", Authorization: `Bearer ${token}` } });
  };
}

const H34_STATUSES      = ["Detected","Reported","UnderInvestigation","RiskAssessed","Contained","Resolved","Closed","FalsePositive","NoBreachDetermined"];
const H34_SEVERITIES    = ["Low","Medium","High","Critical"];
const H34_RISK_LEVELS   = ["Low","Medium","High","Critical"];
const H34_TYPES         = ["UnauthorizedAccess","DataLoss","MalwareRansomware","PhishingEmail","InsiderThreat","PhysicalBreach","SystemMisuse","PolicyViolation","Other"];
const H34_BREACH_DETS   = ["Pending","BreachConfirmed","NoBreachDetermined","FalsePositive"];
const H34_DETECTION_SRC = ["SecurityMonitoring","UserReport","AuditLog","ExternalReport","VendorAlert","Other"];

function h34SeverityBadge(s: string): "default"|"secondary"|"outline"|"destructive" {
  if (s === "Critical") return "destructive";
  if (s === "High")     return "outline";
  if (s === "Medium")   return "secondary";
  return "default";
}

function h34StatusColor(s: string) {
  if (s === "Closed" || s === "FalsePositive" || s === "NoBreachDetermined") return "text-gray-500";
  if (s === "Resolved") return "text-green-600";
  if (s === "Contained" || s === "RiskAssessed") return "text-amber-600";
  return "text-blue-600";
}

const H34_BLANK_CREATE = {
  title: "", incidentType: "Other", description: "", detectionSource: "", reportedBy: "",
  incidentOwner: "", assignedInvestigator: "", severityLevel: "Medium", riskLevel: "",
  phiInvolved: false, phiCategoriesAffected: "", individualsAffected: "" as string|number,
  systemsAffected: "", evidenceReferences: "", documentationRefs: "", notificationRequired: false,
};

function HipaaIncidentManagementSection({ canManage }: { canManage: boolean }) {
  const authFetch = useAuth34();
  const qc = useQueryClient();
  const { toast } = useToast();
  const BASE = "/api/organization/compliance/hipaa/incident-management";

  // ── Queries ───────────────────────────────────────────────────────────────
  const summaryQ = useQuery<Hipaa34Summary>({
    queryKey: ["hipaa34-summary"],
    queryFn: () => authFetch(`${BASE}/summary`).then(r => r.json()),
  });
  const openQ = useQuery<Hipaa34Incident[]>({
    queryKey: ["hipaa34-open"],
    queryFn: () => authFetch(`${BASE}/open`).then(r => r.json()),
  });

  const [fStatus,   setFStatus]   = useState("");
  const [fSeverity, setFSeverity] = useState("");
  const [fRisk,     setFRisk]     = useState("");
  const [fPhi,      setFPhi]      = useState("");
  const [fType,     setFType]     = useState("");
  const [page,      setPage]      = useState(1);

  const listQ = useQuery<{ count: number; items: Hipaa34Incident[] }>({
    queryKey: ["hipaa34-list", fStatus, fSeverity, fRisk, fPhi, fType, page],
    queryFn: () => {
      const p = new URLSearchParams({ page: String(page), limit: "10" });
      if (fStatus)   p.set("status", fStatus);
      if (fSeverity) p.set("severity", fSeverity);
      if (fRisk)     p.set("riskLevel", fRisk);
      if (fPhi)      p.set("phiInvolved", fPhi);
      if (fType)     p.set("incidentType", fType);
      return authFetch(`${BASE}?${p}`).then(r => r.json());
    },
  });

  // ── UI State ──────────────────────────────────────────────────────────────
  const [showCreate,      setShowCreate]      = useState(false);
  const [showDetail,      setShowDetail]      = useState(false);
  const [showEdit,        setShowEdit]        = useState(false);
  const [showAssign,      setShowAssign]      = useState(false);
  const [showEvidence,    setShowEvidence]    = useState(false);
  const [showRisk,        setShowRisk]        = useState(false);
  const [showCorrectiveA, setShowCorrectiveA] = useState(false);
  const [showResolve,     setShowResolve]     = useState(false);
  const [showClose,       setShowClose]       = useState(false);
  const [showDelete,      setShowDelete]      = useState(false);
  const [selected,        setSelected]        = useState<Hipaa34Incident|null>(null);

  const [createForm, setCreateForm] = useState({ ...H34_BLANK_CREATE });
  const setCF = (k: string, v: unknown) => setCreateForm(f => ({ ...f, [k]: v }));
  const [createErrors, setCreateErrors] = useState<Record<string, string>>({});

  const [assignForm,    setAssignForm]    = useState({ investigatorName: "" });
  const [evidenceForm,  setEvidenceForm]  = useState({ evidenceRef: "" });
  const [riskForm,      setRiskForm]      = useState({ riskLevel: "High", findings: "", evidenceRef: "" });
  const [correctiveForm, setCorrectiveForm] = useState({ correctiveAction: "" });
  const [resolveForm,   setResolveForm]   = useState({ rootCause: "", correctiveActions: "", breachDetermination: "Pending", notificationRequired: false, notificationTrackingRef: "", evidenceRef: "" });
  const [closeForm,     setCloseForm]     = useState({ reviewNotes: "", evidenceRef: "" });
  const [assignErrors, setAssignErrors] = useState<Record<string, string>>({});
  const [evidenceErrors, setEvidenceErrors] = useState<Record<string, string>>({});
  const [riskErrors, setRiskErrors] = useState<Record<string, string>>({});
  const [correctiveErrors, setCorrectiveErrors] = useState<Record<string, string>>({});
  const [resolveErrors, setResolveErrors] = useState<Record<string, string>>({});

  // ── Helpers ───────────────────────────────────────────────────────────────
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["hipaa34-summary"] });
    qc.invalidateQueries({ queryKey: ["hipaa34-open"] });
    qc.invalidateQueries({ queryKey: ["hipaa34-list"] });
  };

  const openDetail = (i: Hipaa34Incident) => { setSelected(i); setShowDetail(true); };

  // ── Mutations ─────────────────────────────────────────────────────────────
  const createM = useMutation({
    mutationFn: () => authFetch(BASE, { method: "POST", body: JSON.stringify({
      title: createForm.title, incidentType: createForm.incidentType,
      description: createForm.description || null, detectionSource: createForm.detectionSource || null,
      reportedBy: createForm.reportedBy || null, incidentOwner: createForm.incidentOwner || null,
      assignedInvestigator: createForm.assignedInvestigator || null,
      severityLevel: createForm.severityLevel, riskLevel: createForm.riskLevel || null,
      phiInvolved: createForm.phiInvolved,
      phiCategoriesAffected: createForm.phiCategoriesAffected || null,
      individualsAffected: createForm.individualsAffected !== "" ? Number(createForm.individualsAffected) : null,
      systemsAffected: createForm.systemsAffected || null,
      evidenceReferences: createForm.evidenceReferences || null,
      documentationRefs: createForm.documentationRefs || null,
      notificationRequired: createForm.notificationRequired,
    })}).then(async r => { const d = await r.json(); if (!r.ok) throw d; return d; }),
    onSuccess: () => { invalidate(); setShowCreate(false); setCreateForm({ ...H34_BLANK_CREATE }); setCreateErrors({}); toast({ title: "Incident created" }); },
    onError: (e: any) => { const serverErrs = extractServerErrors(e); if (serverErrs) { setCreateErrors(serverErrs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); } else toast({ title: "Unable to save", description: e?.message, variant: "destructive" }); },
  });

  const assignM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}/assign`, { method: "POST", body: JSON.stringify({ investigatorName: assignForm.investigatorName }) }).then(async r => { const d = await r.json(); if (!r.ok) throw d; return d; }),
    onSuccess: (d) => { invalidate(); setSelected(d); setShowAssign(false); setAssignErrors({}); toast({ title: "Investigator assigned" }); },
    onError: (e: any) => { const serverErrs = extractServerErrors(e); if (serverErrs) { setAssignErrors(serverErrs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); } else toast({ title: "Unable to save", description: e?.message, variant: "destructive" }); },
  });

  const startInvestigationM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}/start-investigation`, { method: "POST", body: JSON.stringify({}) }).then(async r => { const d = await r.json(); if (!r.ok) throw d; return d; }),
    onSuccess: (d) => { invalidate(); setSelected(d); toast({ title: "Investigation started" }); },
    onError: (e: any) => toast({ title: "Unable to complete action", description: e?.message || e?.error, variant: "destructive" }),
  });

  const riskM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}/risk-assessment`, { method: "POST", body: JSON.stringify({ riskLevel: riskForm.riskLevel, findings: riskForm.findings || null, evidenceRef: riskForm.evidenceRef || null }) }).then(async r => { const d = await r.json(); if (!r.ok) throw d; return d; }),
    onSuccess: (d) => { invalidate(); setSelected(d); setShowRisk(false); setRiskErrors({}); toast({ title: "Risk assessment completed" }); },
    onError: (e: any) => { const serverErrs = extractServerErrors(e); if (serverErrs) { setRiskErrors(serverErrs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); } else toast({ title: "Unable to save", description: e?.message || e?.error, variant: "destructive" }); },
  });

  const evidenceM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}/evidence`, { method: "POST", body: JSON.stringify({ evidenceRef: evidenceForm.evidenceRef }) }).then(async r => { const d = await r.json(); if (!r.ok) throw d; return d; }),
    onSuccess: (d) => { invalidate(); setSelected(d); setShowEvidence(false); setEvidenceErrors({}); toast({ title: "Evidence added" }); },
    onError: (e: any) => { const serverErrs = extractServerErrors(e); if (serverErrs) { setEvidenceErrors(serverErrs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); } else toast({ title: "Unable to save", description: e?.message, variant: "destructive" }); },
  });

  const correctiveM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}/corrective-action`, { method: "POST", body: JSON.stringify({ correctiveAction: correctiveForm.correctiveAction }) }).then(async r => { const d = await r.json(); if (!r.ok) throw d; return d; }),
    onSuccess: (d) => { invalidate(); setSelected(d); setShowCorrectiveA(false); setCorrectiveErrors({}); toast({ title: "Corrective action added" }); },
    onError: (e: any) => { const serverErrs = extractServerErrors(e); if (serverErrs) { setCorrectiveErrors(serverErrs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); } else toast({ title: "Unable to save", description: e?.message, variant: "destructive" }); },
  });

  const resolveM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}/resolve`, { method: "POST", body: JSON.stringify({
      rootCause: resolveForm.rootCause, correctiveActions: resolveForm.correctiveActions || null,
      breachDetermination: resolveForm.breachDetermination || null,
      notificationRequired: resolveForm.notificationRequired,
      notificationTrackingRef: resolveForm.notificationTrackingRef || null,
      evidenceRef: resolveForm.evidenceRef || null,
    })}).then(async r => { const d = await r.json(); if (!r.ok) throw d; return d; }),
    onSuccess: (d) => { invalidate(); setSelected(d); setShowResolve(false); setResolveErrors({}); toast({ title: "Incident resolved" }); },
    onError: (e: any) => { const serverErrs = extractServerErrors(e); if (serverErrs) { setResolveErrors(serverErrs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); } else toast({ title: "Unable to save", description: e?.message || e?.error, variant: "destructive" }); },
  });

  const closeM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}/close`, { method: "POST", body: JSON.stringify({ reviewNotes: closeForm.reviewNotes || null, evidenceRef: closeForm.evidenceRef || null }) }).then(async r => { const d = await r.json(); if (!r.ok) throw d; return d; }),
    onSuccess: (d) => { invalidate(); setSelected(d); setShowClose(false); toast({ title: "Incident closed" }); },
    onError: (e: any) => toast({ title: "Unable to complete action", description: e?.message || e?.error, variant: "destructive" }),
  });

  const deleteM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}`, { method: "DELETE" }).then(async r => { if (!r.ok) { const d = await r.json(); throw d; } }),
    onSuccess: () => { invalidate(); setShowDelete(false); setShowDetail(false); setSelected(null); toast({ title: "Incident deleted" }); },
    onError: (e: any) => toast({ title: "Unable to delete", description: e?.message, variant: "destructive" }),
  });

  const sum    = summaryQ.data;
  const items  = listQ.data?.items ?? [];
  const count  = listQ.data?.count ?? 0;
  const alerts = openQ.data?.filter(i => i.validationCodes.length > 0 || i.isCriticalUnresolved || i.investigationOverdue) ?? [];

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base font-semibold">Incident Management & Breach Investigation</CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">§ 164.308(a)(6) — Track security incidents through detection → investigation → resolution with breach determination and notification tracking.</p>
            </div>
            {canManage && (
              <Button size="sm" onClick={() => { setCreateForm({ ...H34_BLANK_CREATE }); setShowCreate(true); }}>+ Report Incident</Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Summary metrics */}
          {sum && (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {[
                { label: "Total Incidents",       value: sum.total,                color: "" },
                { label: "Open Investigations",   value: sum.openInvestigations,   color: sum.openInvestigations > 0 ? "text-blue-600" : "" },
                { label: "Critical",              value: sum.criticalIncidents,    color: sum.criticalIncidents > 0 ? "text-red-600 font-bold" : "" },
                { label: "PHI Incidents",         value: sum.phiIncidents,         color: sum.phiIncidents > 0 ? "text-amber-600" : "" },
                { label: "Overdue Investigations",value: sum.overdueInvestigations,color: sum.overdueInvestigations > 0 ? "text-red-600" : "" },
                { label: "Resolved",              value: sum.resolvedIncidents,    color: "text-green-600" },
                { label: "Closed",                value: sum.closedIncidents,      color: "text-gray-600" },
                { label: "Critical Unresolved",   value: sum.criticalUnresolved,   color: sum.criticalUnresolved > 0 ? "text-red-600 font-bold" : "" },
              ].map(m => (
                <div key={m.label} className="rounded border p-2 text-center">
                  <div className={`text-xl font-bold ${m.color}`}>{m.value}</div>
                  <div className="text-xs text-muted-foreground leading-tight">{m.label}</div>
                </div>
              ))}
            </div>
          )}

          {/* Alert panel */}
          {alerts.length > 0 && (
            <div className="rounded border border-red-200 bg-red-50 p-3">
              <p className="text-xs font-semibold text-red-800 mb-1">Incidents requiring immediate attention ({alerts.length})</p>
              <ul className="space-y-1">
                {alerts.slice(0, 5).map(i => (
                  <li key={i.id} className="text-xs text-red-700 flex justify-between items-start gap-2">
                    <button className="underline hover:no-underline text-left" onClick={() => openDetail(i)}>
                      [{i.incidentNumber}] {i.title}
                    </button>
                    <span className="shrink-0 font-mono text-red-600">
                      {i.isCriticalUnresolved ? "CRITICAL" : i.investigationOverdue ? "OVERDUE" : i.validationCodes[0]}
                    </span>
                  </li>
                ))}
                {alerts.length > 5 && <li className="text-xs text-red-600">+{alerts.length - 5} more…</li>}
              </ul>
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-wrap gap-2">
            <select className="border rounded px-2 py-1 text-xs" value={fStatus} onChange={e => { setFStatus(e.target.value); setPage(1); }}>
              <option value="">All statuses</option>
              {H34_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select className="border rounded px-2 py-1 text-xs" value={fSeverity} onChange={e => { setFSeverity(e.target.value); setPage(1); }}>
              <option value="">All severities</option>
              {H34_SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select className="border rounded px-2 py-1 text-xs" value={fRisk} onChange={e => { setFRisk(e.target.value); setPage(1); }}>
              <option value="">All risk levels</option>
              {H34_RISK_LEVELS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <select className="border rounded px-2 py-1 text-xs" value={fPhi} onChange={e => { setFPhi(e.target.value); setPage(1); }}>
              <option value="">PHI: any</option>
              <option value="true">PHI Involved</option>
              <option value="false">No PHI</option>
            </select>
            <select className="border rounded px-2 py-1 text-xs" value={fType} onChange={e => { setFType(e.target.value); setPage(1); }}>
              <option value="">All types</option>
              {H34_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            {(fStatus||fSeverity||fRisk||fPhi||fType) && (
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setFStatus(""); setFSeverity(""); setFRisk(""); setFPhi(""); setFType(""); setPage(1); }}>Clear</Button>
            )}
          </div>

          {/* Incident list */}
          {listQ.isLoading ? (
            <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-16 w-full" />)}</div>
          ) : items.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-4">No incidents found. {canManage && "Report an incident to begin tracking."}</p>
          ) : (
            <div className="divide-y rounded border">
              {items.map(i => (
                <div key={i.id} className="flex items-start justify-between p-3 hover:bg-muted/30 cursor-pointer" onClick={() => openDetail(i)}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-mono text-muted-foreground">{i.incidentNumber}</span>
                      <span className="text-sm font-medium truncate">{i.title}</span>
                      <Badge variant={h34SeverityBadge(i.severityLevel)} className="text-xs">{i.severityLevel}</Badge>
                      <span className={`text-xs font-medium ${h34StatusColor(i.investigationStatus)}`}>{i.investigationStatus}</span>
                      {i.phiInvolved && <Badge variant="outline" className="text-xs text-amber-600">PHI</Badge>}
                      {i.isCriticalUnresolved && <Badge variant="destructive" className="text-xs">Unresolved Critical</Badge>}
                      {i.investigationOverdue && <Badge variant="outline" className="text-xs text-red-600">Overdue</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 flex gap-3 flex-wrap">
                      {i.incidentType && <span>{i.incidentType}</span>}
                      {i.assignedInvestigator && <span>Investigator: {i.assignedInvestigator}</span>}
                      {i.phiInvolved && i.individualsAffected && <span>{i.individualsAffected} individuals affected</span>}
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground shrink-0 ml-2 text-right">
                    {i.riskLevel && <div>{i.riskLevel} risk</div>}
                    {i.validationCodes.length > 0 && <div className="text-red-500">{i.validationCodes.length} issue{i.validationCodes.length > 1 ? "s" : ""}</div>}
                    <div>{i.createdAt.slice(0,10)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {count > 10 && (
            <div className="flex justify-between items-center text-xs">
              <span>{count} total</span>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" className="h-7 text-xs" disabled={page<=1} onClick={() => setPage(p => p-1)}>Prev</Button>
                <span className="px-2 py-1">{page}</span>
                <Button size="sm" variant="outline" className="h-7 text-xs" disabled={page*10>=count} onClick={() => setPage(p => p+1)}>Next</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Create Incident ───────────────────────────────────────────────── */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Report New Security Incident</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <label className="block text-xs font-medium mb-1">Incident Title *</label>
              <input className={fieldCls("w-full border rounded px-2 py-1 text-sm", createErrors, "title")} aria-invalid={!!createErrors.title} value={createForm.title} onChange={e => { setCF("title", e.target.value); clearErr(setCreateErrors, "title"); }} placeholder="e.g. Unauthorised EHR Access Detected" />
              {fErr(createErrors, "title")}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Incident Type</label>
                <select className="w-full border rounded px-2 py-1 text-sm" value={createForm.incidentType} onChange={e => setCF("incidentType", e.target.value)}>
                  {H34_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Severity Level</label>
                <select className="w-full border rounded px-2 py-1 text-sm" value={createForm.severityLevel} onChange={e => setCF("severityLevel", e.target.value)}>
                  {H34_SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Description</label>
              <textarea className="w-full border rounded px-2 py-1 text-sm" rows={3} value={createForm.description} onChange={e => setCF("description", e.target.value)} placeholder="Describe the incident (do not include actual PHI)" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Detection Source</label>
                <select className="w-full border rounded px-2 py-1 text-sm" value={createForm.detectionSource} onChange={e => setCF("detectionSource", e.target.value)}>
                  <option value="">— select —</option>
                  {H34_DETECTION_SRC.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Reported By</label>
                <input className="w-full border rounded px-2 py-1 text-sm" value={createForm.reportedBy} onChange={e => setCF("reportedBy", e.target.value)} placeholder="Name or team" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Incident Owner</label>
                <input className="w-full border rounded px-2 py-1 text-sm" value={createForm.incidentOwner} onChange={e => setCF("incidentOwner", e.target.value)} placeholder="Privacy Officer" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Assigned Investigator</label>
                <input className="w-full border rounded px-2 py-1 text-sm" value={createForm.assignedInvestigator} onChange={e => setCF("assignedInvestigator", e.target.value)} placeholder="CISO Team" />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={createForm.phiInvolved} onChange={e => setCF("phiInvolved", e.target.checked)} />
                PHI Involved
              </label>
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={createForm.notificationRequired} onChange={e => setCF("notificationRequired", e.target.checked)} />
                Notification Required
              </label>
            </div>
            {createForm.phiInvolved && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">PHI Categories Affected</label>
                  <input className="w-full border rounded px-2 py-1 text-sm" value={createForm.phiCategoriesAffected} onChange={e => setCF("phiCategoriesAffected", e.target.value)} placeholder="Demographics,Diagnoses" />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Individuals Affected</label>
                  <input type="number" min={0} className="w-full border rounded px-2 py-1 text-sm" value={createForm.individualsAffected} onChange={e => setCF("individualsAffected", e.target.value)} />
                </div>
              </div>
            )}
            <div>
              <label className="block text-xs font-medium mb-1">Systems Affected (comma-separated)</label>
              <input className="w-full border rounded px-2 py-1 text-sm" value={createForm.systemsAffected} onChange={e => setCF("systemsAffected", e.target.value)} placeholder="EHR,Lab,Pharmacy" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={() => { const errs: Record<string, string> = {}; required(errs, "title", createForm.title, "Incident title"); if (Object.keys(errs).length) { setCreateErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } setCreateErrors({}); createM.mutate(); }} disabled={createM.isPending}>{createM.isPending ? "Saving…" : "Report Incident"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Detail / Timeline ─────────────────────────────────────────────── */}
      {selected && showDetail && (
        <Dialog open={showDetail} onOpenChange={v => { setShowDetail(v); if (!v) setSelected(null); }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-sm text-muted-foreground">{selected.incidentNumber}</span>
                {selected.title}
                <Badge variant={h34SeverityBadge(selected.severityLevel)}>{selected.severityLevel}</Badge>
                <span className={`text-sm font-semibold ${h34StatusColor(selected.investigationStatus)}`}>{selected.investigationStatus}</span>
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              {selected.description && <p className="text-xs text-muted-foreground">{selected.description}</p>}

              {/* Key info grid */}
              <div className="grid grid-cols-2 gap-2 text-xs">
                {selected.incidentType         && <div><p className="font-medium">Type</p><p className="text-muted-foreground">{selected.incidentType}</p></div>}
                {selected.incidentOwner        && <div><p className="font-medium">Owner</p><p className="text-muted-foreground">{selected.incidentOwner}</p></div>}
                {selected.assignedInvestigator && <div><p className="font-medium">Investigator</p><p className="text-muted-foreground">{selected.assignedInvestigator}</p></div>}
                {selected.detectionSource      && <div><p className="font-medium">Detection Source</p><p className="text-muted-foreground">{selected.detectionSource}</p></div>}
                {selected.systemsAffected      && <div><p className="font-medium">Systems Affected</p><p className="text-muted-foreground">{selected.systemsAffected}</p></div>}
                {selected.riskLevel            && <div><p className="font-medium">Risk Level</p><p className="text-muted-foreground">{selected.riskLevel}</p></div>}
              </div>

              {/* PHI section */}
              {selected.phiInvolved && (
                <div className="rounded border border-amber-200 bg-amber-50 p-2 text-xs">
                  <p className="font-semibold text-amber-800 mb-1">⚠ PHI Involved</p>
                  {selected.phiCategoriesAffected && <p>Categories: {selected.phiCategoriesAffected}</p>}
                  {selected.individualsAffected && <p>Individuals affected: {selected.individualsAffected}</p>}
                  {selected.riskAssessmentDate && <p className="text-green-700">✓ Risk assessed: {selected.riskAssessmentDate.slice(0,10)}</p>}
                  {!selected.riskAssessmentDate && <p className="text-red-600">✗ Risk assessment required</p>}
                </div>
              )}

              {/* Investigation timeline */}
              <div className="rounded border p-2 text-xs space-y-1">
                <p className="font-semibold mb-1">Investigation Timeline</p>
                {[
                  { label: "Reported",         date: selected.reportedDate },
                  { label: "Investigation started", date: selected.investigationStartDate },
                  { label: "Risk assessed",    date: selected.riskAssessmentDate },
                  { label: "Resolved",         date: selected.resolutionDate },
                  { label: "Closed",           date: selected.closedAt },
                ].filter(t => t.date).map(t => (
                  <div key={t.label} className="flex justify-between">
                    <span className="text-muted-foreground">{t.label}</span>
                    <span>{t.date!.slice(0,10)}</span>
                  </div>
                ))}
                {selected.investigationOverdue && <p className="text-red-600 font-semibold">⚠ Investigation overdue (&gt;30 days)</p>}
              </div>

              {/* Findings / Root cause */}
              {selected.investigationFindings && <div><p className="text-xs font-medium">Investigation Findings</p><p className="text-xs text-muted-foreground whitespace-pre-wrap">{selected.investigationFindings}</p></div>}
              {selected.rootCause && <div><p className="text-xs font-medium">Root Cause</p><p className="text-xs text-muted-foreground whitespace-pre-wrap">{selected.rootCause}</p></div>}
              {selected.containmentActions && <div><p className="text-xs font-medium">Containment Actions</p><p className="text-xs text-muted-foreground whitespace-pre-wrap">{selected.containmentActions}</p></div>}
              {selected.correctiveActions && <div><p className="text-xs font-medium">Corrective Actions</p><p className="text-xs text-muted-foreground whitespace-pre-wrap">{selected.correctiveActions}</p></div>}

              {/* Evidence */}
              {selected.evidenceReferences && (
                <div className="rounded bg-blue-50 border border-blue-200 p-2 text-xs">
                  <p className="font-semibold text-blue-800 mb-1">Evidence References</p>
                  {selected.evidenceReferences.split(",").map((r, i) => <p key={i} className="font-mono text-blue-700">{r.trim()}</p>)}
                </div>
              )}

              {/* Breach / notification */}
              <div className="grid grid-cols-2 gap-2 text-xs">
                {selected.breachDetermination && <div><p className="font-medium">Breach Determination</p><p className={`font-semibold ${selected.breachDetermination === "BreachConfirmed" ? "text-red-600" : "text-muted-foreground"}`}>{selected.breachDetermination}</p></div>}
                <div><p className="font-medium">Notification Required</p><p className={selected.notificationRequired ? "text-amber-600 font-semibold" : "text-muted-foreground"}>{selected.notificationRequired ? "Yes" : "No"}</p></div>
              </div>

              {/* Validation issues */}
              {selected.validationCodes.length > 0 && (
                <div className="rounded bg-red-50 border border-red-200 p-2">
                  <p className="text-xs font-semibold text-red-800 mb-1">Compliance Issues</p>
                  <div className="flex flex-wrap gap-1">
                    {selected.validationCodes.map(c => <span key={c} className="text-xs font-mono bg-red-100 text-red-700 rounded px-1.5 py-0.5">{c}</span>)}
                  </div>
                </div>
              )}
            </div>
            {canManage && (
              <DialogFooter className="flex flex-wrap gap-2 sm:justify-start">
                {selected.investigationStatus === "Detected" && (
                  <Button size="sm" onClick={() => { setAssignForm({ investigatorName: "" }); setShowAssign(true); }}>Assign Investigator</Button>
                )}
                {(selected.investigationStatus === "Reported") && (
                  <Button size="sm" onClick={() => startInvestigationM.mutate()} disabled={startInvestigationM.isPending}>Start Investigation</Button>
                )}
                {selected.investigationStatus === "UnderInvestigation" && (
                  <Button size="sm" onClick={() => { setRiskForm({ riskLevel: "High", findings: "", evidenceRef: "" }); setShowRisk(true); }}>Risk Assessment</Button>
                )}
                {(selected.investigationStatus === "RiskAssessed" || selected.investigationStatus === "Contained") && (
                  <Button size="sm" onClick={() => { setResolveForm({ rootCause: "", correctiveActions: "", breachDetermination: "Pending", notificationRequired: false, notificationTrackingRef: "", evidenceRef: "" }); setShowResolve(true); }}>Resolve</Button>
                )}
                {selected.investigationStatus === "Resolved" && (
                  <Button size="sm" onClick={() => { setCloseForm({ reviewNotes: "", evidenceRef: "" }); setShowClose(true); }}>Close Incident</Button>
                )}
                <Button size="sm" variant="outline" onClick={() => { setEvidenceForm({ evidenceRef: "" }); setShowEvidence(true); }}>Add Evidence</Button>
                <Button size="sm" variant="outline" onClick={() => { setCorrectiveForm({ correctiveAction: "" }); setShowCorrectiveA(true); }}>Add Corrective Action</Button>
                <Button size="sm" variant="destructive" onClick={() => setShowDelete(true)}>Delete</Button>
              </DialogFooter>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* ── Assign Investigator ───────────────────────────────────────────── */}
      {selected && showAssign && (
        <Dialog open={showAssign} onOpenChange={v => { setShowAssign(v); if (!v) setAssignErrors({}); }}>
          <DialogContent>
            <DialogHeader><DialogTitle>Assign Investigator</DialogTitle></DialogHeader>
            <div>
              <label className="block text-xs font-medium mb-1">Investigator Name *</label>
              <input className={fieldCls("w-full border rounded px-2 py-1 text-sm", assignErrors, "investigatorName")} aria-invalid={!!assignErrors.investigatorName} value={assignForm.investigatorName} onChange={e => { setAssignForm(f => ({ ...f, investigatorName: e.target.value })); clearErr(setAssignErrors, "investigatorName"); }} placeholder="Full name or team" />
              {fErr(assignErrors, "investigatorName")}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAssign(false)}>Cancel</Button>
              <Button onClick={() => { const errs: Record<string, string> = {}; required(errs, "investigatorName", assignForm.investigatorName, "Investigator name"); if (Object.keys(errs).length) { setAssignErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } setAssignErrors({}); assignM.mutate(); }} disabled={assignM.isPending}>{assignM.isPending ? "Saving…" : "Assign"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Add Evidence ──────────────────────────────────────────────────── */}
      {selected && showEvidence && (
        <Dialog open={showEvidence} onOpenChange={v => { setShowEvidence(v); if (!v) setEvidenceErrors({}); }}>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Evidence Reference</DialogTitle></DialogHeader>
            <p className="text-xs text-muted-foreground">Add a reference to evidence documentation (no PHI values).</p>
            <div>
              <label className="block text-xs font-medium mb-1">Evidence Reference *</label>
              <input className={fieldCls("w-full border rounded px-2 py-1 text-sm", evidenceErrors, "evidenceRef")} aria-invalid={!!evidenceErrors.evidenceRef} value={evidenceForm.evidenceRef} onChange={e => { setEvidenceForm(f => ({ ...f, evidenceRef: e.target.value })); clearErr(setEvidenceErrors, "evidenceRef"); }} placeholder="evidence://screenshot-2026-09.pdf" />
              {fErr(evidenceErrors, "evidenceRef")}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEvidence(false)}>Cancel</Button>
              <Button onClick={() => { const errs: Record<string, string> = {}; required(errs, "evidenceRef", evidenceForm.evidenceRef, "Evidence reference"); if (Object.keys(errs).length) { setEvidenceErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } setEvidenceErrors({}); evidenceM.mutate(); }} disabled={evidenceM.isPending}>{evidenceM.isPending ? "Saving…" : "Add Evidence"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Risk Assessment ───────────────────────────────────────────────── */}
      {selected && showRisk && (
        <Dialog open={showRisk} onOpenChange={v => { setShowRisk(v); if (!v) setRiskErrors({}); }}>
          <DialogContent>
            <DialogHeader><DialogTitle>Complete Risk Assessment</DialogTitle></DialogHeader>
            <div className="space-y-3 text-sm">
              <div>
                <label className="block text-xs font-medium mb-1">Risk Level *</label>
                <select className={fieldCls("w-full border rounded px-2 py-1 text-sm", riskErrors, "riskLevel")} aria-invalid={!!riskErrors.riskLevel} value={riskForm.riskLevel} onChange={e => { setRiskForm(f => ({ ...f, riskLevel: e.target.value })); clearErr(setRiskErrors, "riskLevel"); }}>
                  {H34_RISK_LEVELS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                {fErr(riskErrors, "riskLevel")}
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Findings</label>
                <textarea className="w-full border rounded px-2 py-1 text-sm" rows={3} value={riskForm.findings} onChange={e => setRiskForm(f => ({ ...f, findings: e.target.value }))} placeholder="Risk assessment findings…" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Evidence Reference</label>
                <input className="w-full border rounded px-2 py-1 text-sm" value={riskForm.evidenceRef} onChange={e => setRiskForm(f => ({ ...f, evidenceRef: e.target.value }))} placeholder="evidence://risk-assessment.pdf" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRisk(false)}>Cancel</Button>
              <Button onClick={() => { const errs: Record<string, string> = {}; required(errs, "riskLevel", riskForm.riskLevel, "Risk level"); if (Object.keys(errs).length) { setRiskErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } setRiskErrors({}); riskM.mutate(); }} disabled={riskM.isPending}>{riskM.isPending ? "Saving…" : "Complete Assessment"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Add Corrective Action ─────────────────────────────────────────── */}
      {selected && showCorrectiveA && (
        <Dialog open={showCorrectiveA} onOpenChange={v => { setShowCorrectiveA(v); if (!v) setCorrectiveErrors({}); }}>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Corrective Action</DialogTitle></DialogHeader>
            <div>
              <label className="block text-xs font-medium mb-1">Corrective Action *</label>
              <textarea className={fieldCls("w-full border rounded px-2 py-1 text-sm", correctiveErrors, "correctiveAction")} aria-invalid={!!correctiveErrors.correctiveAction} rows={4} value={correctiveForm.correctiveAction} onChange={e => { setCorrectiveForm(f => ({ ...f, correctiveAction: e.target.value })); clearErr(setCorrectiveErrors, "correctiveAction"); }} placeholder="Describe the corrective action taken…" />
              {fErr(correctiveErrors, "correctiveAction")}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCorrectiveA(false)}>Cancel</Button>
              <Button onClick={() => { const errs: Record<string, string> = {}; required(errs, "correctiveAction", correctiveForm.correctiveAction, "Corrective action"); if (Object.keys(errs).length) { setCorrectiveErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } setCorrectiveErrors({}); correctiveM.mutate(); }} disabled={correctiveM.isPending}>{correctiveM.isPending ? "Saving…" : "Add Action"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Resolve ───────────────────────────────────────────────────────── */}
      {selected && showResolve && (
        <Dialog open={showResolve} onOpenChange={v => { setShowResolve(v); if (!v) setResolveErrors({}); }}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Resolve Incident</DialogTitle></DialogHeader>
            <div className="space-y-3 text-sm">
              <div>
                <label className="block text-xs font-medium mb-1">Root Cause *</label>
                <textarea className={fieldCls("w-full border rounded px-2 py-1 text-sm", resolveErrors, "rootCause")} aria-invalid={!!resolveErrors.rootCause} rows={3} value={resolveForm.rootCause} onChange={e => { setResolveForm(f => ({ ...f, rootCause: e.target.value })); clearErr(setResolveErrors, "rootCause"); }} placeholder="Describe the root cause…" />
                {fErr(resolveErrors, "rootCause")}
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Additional Corrective Actions</label>
                <textarea className="w-full border rounded px-2 py-1 text-sm" rows={2} value={resolveForm.correctiveActions} onChange={e => setResolveForm(f => ({ ...f, correctiveActions: e.target.value }))} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">Breach Determination</label>
                  <select className="w-full border rounded px-2 py-1 text-sm" value={resolveForm.breachDetermination} onChange={e => setResolveForm(f => ({ ...f, breachDetermination: e.target.value }))}>
                    {H34_BREACH_DETS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Evidence Reference</label>
                  <input className="w-full border rounded px-2 py-1 text-sm" value={resolveForm.evidenceRef} onChange={e => setResolveForm(f => ({ ...f, evidenceRef: e.target.value }))} placeholder="evidence://resolution.pdf" />
                </div>
              </div>
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={resolveForm.notificationRequired} onChange={e => setResolveForm(f => ({ ...f, notificationRequired: e.target.checked }))} />
                Notification Required
              </label>
              {resolveForm.notificationRequired && (
                <div>
                  <label className="block text-xs font-medium mb-1">Notification Tracking Reference</label>
                  <input className="w-full border rounded px-2 py-1 text-sm" value={resolveForm.notificationTrackingRef} onChange={e => setResolveForm(f => ({ ...f, notificationTrackingRef: e.target.value }))} placeholder="notification://hhs-breach-report-2026" />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowResolve(false)}>Cancel</Button>
              <Button onClick={() => { const errs: Record<string, string> = {}; required(errs, "rootCause", resolveForm.rootCause, "Root cause"); if (Object.keys(errs).length) { setResolveErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } setResolveErrors({}); resolveM.mutate(); }} disabled={resolveM.isPending}>{resolveM.isPending ? "Resolving…" : "Resolve Incident"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Close ─────────────────────────────────────────────────────────── */}
      {selected && showClose && (
        <Dialog open={showClose} onOpenChange={setShowClose}>
          <DialogContent>
            <DialogHeader><DialogTitle>Close Incident</DialogTitle></DialogHeader>
            <p className="text-sm">Close <strong>{selected.incidentNumber}</strong>. This is a terminal state — the incident cannot be re-opened.</p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium mb-1">Review Notes</label>
                <textarea className="w-full border rounded px-2 py-1 text-sm" rows={3} value={closeForm.reviewNotes} onChange={e => setCloseForm(f => ({ ...f, reviewNotes: e.target.value }))} placeholder="Final review notes…" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Closure Evidence Reference</label>
                <input className="w-full border rounded px-2 py-1 text-sm" value={closeForm.evidenceRef} onChange={e => setCloseForm(f => ({ ...f, evidenceRef: e.target.value }))} placeholder="evidence://closure-report.pdf" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowClose(false)}>Cancel</Button>
              <Button onClick={() => closeM.mutate()} disabled={closeM.isPending}>{closeM.isPending ? "Closing…" : "Close Incident"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Delete ────────────────────────────────────────────────────────── */}
      {selected && showDelete && (
        <Dialog open={showDelete} onOpenChange={setShowDelete}>
          <DialogContent>
            <DialogHeader><DialogTitle>Delete Incident</DialogTitle></DialogHeader>
            <p className="text-sm">Permanently delete incident <strong>{selected.incidentNumber} — {selected.title}</strong>? This cannot be undone.</p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDelete(false)}>Cancel</Button>
              <Button variant="destructive" onClick={() => deleteM.mutate()} disabled={deleteM.isPending}>{deleteM.isPending ? "Deleting…" : "Delete"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// HIPAA-33 — PHI Audit Logging & Access Monitoring (§ 164.312(b))
// ══════════════════════════════════════════════════════════════════════════════

interface Hipaa33Summary {
  total: number; compliant: number; nonCompliant: number; pendingReview: number;
  disabled: number; phiSystemsMonitored: number; accessEventsLogged: number;
  modificationEventsLogged: number; reviewOverdue: number; compliancePercentage: number;
}

interface Hipaa33Control {
  id: string; organizationId: string;
  auditPolicyName: string; policyDescription?: string;
  coveredSystems?: string; phiSystemsEnabled?: string;
  auditLoggingEnabled: boolean; accessEventLoggingEnabled: boolean;
  modificationLoggingEnabled: boolean; authEventLoggingEnabled: boolean;
  failedAccessLoggingEnabled: boolean;
  userIdentityCaptured: boolean; userRoleCaptured: boolean;
  timestampCaptured: boolean; ipDeviceCaptured: boolean;
  eventTypesLogged?: string; phiCategoriesInScope?: string;
  recordIdentifierRef?: string; accessPurposesCaptured?: string;
  statusCaptured: boolean;
  retentionPeriodDays?: number; logStorageReference?: string;
  integrityProtectionEnabled: boolean;
  reviewSchedule?: string; lastReviewDate?: string; nextReviewDate?: string;
  reviewerName?: string; reviewerTitle?: string; reviewNotes?: string;
  evidenceReference?: string;
  complianceStatus: string; riskLevel?: string;
  remediationRequired: boolean; remediationNotes?: string;
  createdAt: string; updatedAt: string;
  reviewOverdue: boolean; validationCodes: string[];
}

function useAuth33() {
  return (url: string, opts: RequestInit = {}) => {
    const token = sessionStorage.getItem("dotnetbot.jwt.session") || localStorage.getItem("dotnetbot.jwt") || "";
    return fetch(url, { ...opts, headers: { ...(opts.headers as Record<string,string> || {}), "Content-Type": "application/json", Authorization: `Bearer ${token}` } });
  };
}

const HIPAA33_COMPLIANCE_STATUSES = ["Compliant","NonCompliant","PendingReview","Disabled"];
const HIPAA33_RISK_LEVELS          = ["Low","Medium","High","Critical"];
const HIPAA33_REVIEW_SCHEDULES     = ["Weekly","Monthly","Quarterly","SemiAnnual","Annual"];
const HIPAA33_EVENT_TYPES          = ["Read","Write","Delete","Auth","FailedAccess","Admin","Other"];

function h33ComplianceBadge(s: string): "default"|"secondary"|"outline"|"destructive" {
  if (s === "Compliant")     return "default";
  if (s === "NonCompliant")  return "destructive";
  if (s === "Disabled")      return "secondary";
  return "outline";
}

const HIPAA33_BLANK = {
  auditPolicyName: "", policyDescription: "", coveredSystems: "", phiSystemsEnabled: "",
  auditLoggingEnabled: false, accessEventLoggingEnabled: false, modificationLoggingEnabled: false,
  authEventLoggingEnabled: false, failedAccessLoggingEnabled: false,
  userIdentityCaptured: false, userRoleCaptured: false, timestampCaptured: false, ipDeviceCaptured: false,
  eventTypesLogged: "", phiCategoriesInScope: "", recordIdentifierRef: "", accessPurposesCaptured: "",
  statusCaptured: false, retentionPeriodDays: "" as string|number, logStorageReference: "",
  integrityProtectionEnabled: false, reviewSchedule: "", nextReviewDate: "",
  evidenceReference: "", riskLevel: "",
};

function HipaaPhiAuditLoggingSection({ canManage }: { canManage: boolean }) {
  const authFetch = useAuth33();
  const qc = useQueryClient();
  const { toast } = useToast();
  const BASE = "/api/organization/compliance/hipaa/phi-audit-logging";

  // ── Queries ───────────────────────────────────────────────────────────────
  const summaryQ = useQuery<Hipaa33Summary>({
    queryKey: ["hipaa33-summary"],
    queryFn: () => authFetch(`${BASE}/summary`).then(r => r.json()),
  });
  const issuesQ = useQuery<Hipaa33Control[]>({
    queryKey: ["hipaa33-issues"],
    queryFn: () => authFetch(`${BASE}/issues`).then(r => r.json()),
  });

  const [fSystem,     setFSystem]     = useState("");
  const [fCompliance, setFCompliance] = useState("");
  const [fRisk,       setFRisk]       = useState("");
  const [fLogging,    setFLogging]    = useState("");
  const [page,        setPage]        = useState(1);

  const listQ = useQuery<{ count: number; items: Hipaa33Control[] }>({
    queryKey: ["hipaa33-list", fSystem, fCompliance, fRisk, fLogging, page],
    queryFn: () => {
      const p = new URLSearchParams({ page: String(page), limit: "10" });
      if (fSystem)     p.set("system", fSystem);
      if (fCompliance) p.set("complianceStatus", fCompliance);
      if (fRisk)       p.set("riskLevel", fRisk);
      if (fLogging)    p.set("loggingEnabled", fLogging);
      return authFetch(`${BASE}?${p}`).then(r => r.json());
    },
  });

  // ── UI State ──────────────────────────────────────────────────────────────
  const [showCreate,   setShowCreate]   = useState(false);
  const [showDetail,   setShowDetail]   = useState(false);
  const [showEdit,     setShowEdit]     = useState(false);
  const [showValidate, setShowValidate] = useState(false);
  const [showReview,   setShowReview]   = useState(false);
  const [showDelete,   setShowDelete]   = useState(false);
  const [selected,     setSelected]     = useState<Hipaa33Control|null>(null);
  const [validateResult, setValidateResult] = useState<{ valid: boolean; codes: string[]; complianceStatus: string }|null>(null);

  const [form, setForm] = useState({ ...HIPAA33_BLANK });
  const setF = (k: string, v: unknown) => setForm(f => ({ ...f, [k]: v }));
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [reviewForm, setReviewForm] = useState({
    reviewerName: "", reviewerTitle: "", reviewNotes: "", nextReviewDate: "", evidenceReference: "",
  });
  const [reviewErrors, setReviewErrors] = useState<Record<string,string>>({});

  // ── Helpers ───────────────────────────────────────────────────────────────
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["hipaa33-summary"] });
    qc.invalidateQueries({ queryKey: ["hipaa33-issues"] });
    qc.invalidateQueries({ queryKey: ["hipaa33-list"] });
  };

  const openDetail = (c: Hipaa33Control) => { setSelected(c); setShowDetail(true); };
  const openEdit   = (c: Hipaa33Control) => {
    setSelected(c);
    setForm({
      auditPolicyName:           c.auditPolicyName,
      policyDescription:         c.policyDescription ?? "",
      coveredSystems:            c.coveredSystems ?? "",
      phiSystemsEnabled:         c.phiSystemsEnabled ?? "",
      auditLoggingEnabled:       c.auditLoggingEnabled,
      accessEventLoggingEnabled: c.accessEventLoggingEnabled,
      modificationLoggingEnabled:c.modificationLoggingEnabled,
      authEventLoggingEnabled:   c.authEventLoggingEnabled,
      failedAccessLoggingEnabled:c.failedAccessLoggingEnabled,
      userIdentityCaptured:      c.userIdentityCaptured,
      userRoleCaptured:          c.userRoleCaptured,
      timestampCaptured:         c.timestampCaptured,
      ipDeviceCaptured:          c.ipDeviceCaptured,
      eventTypesLogged:          c.eventTypesLogged ?? "",
      phiCategoriesInScope:      c.phiCategoriesInScope ?? "",
      recordIdentifierRef:       c.recordIdentifierRef ?? "",
      accessPurposesCaptured:    c.accessPurposesCaptured ?? "",
      statusCaptured:            c.statusCaptured,
      retentionPeriodDays:       c.retentionPeriodDays ?? "",
      logStorageReference:       c.logStorageReference ?? "",
      integrityProtectionEnabled:c.integrityProtectionEnabled,
      reviewSchedule:            c.reviewSchedule ?? "",
      nextReviewDate:            c.nextReviewDate?.slice(0,10) ?? "",
      evidenceReference:         c.evidenceReference ?? "",
      riskLevel:                 c.riskLevel ?? "",
    });
    setShowEdit(true);
  };

  const buildBody = () => ({
    auditPolicyName:            form.auditPolicyName,
    policyDescription:          form.policyDescription || null,
    coveredSystems:             form.coveredSystems || null,
    phiSystemsEnabled:          form.phiSystemsEnabled || null,
    auditLoggingEnabled:        form.auditLoggingEnabled,
    accessEventLoggingEnabled:  form.accessEventLoggingEnabled,
    modificationLoggingEnabled: form.modificationLoggingEnabled,
    authEventLoggingEnabled:    form.authEventLoggingEnabled,
    failedAccessLoggingEnabled: form.failedAccessLoggingEnabled,
    userIdentityCaptured:       form.userIdentityCaptured,
    userRoleCaptured:           form.userRoleCaptured,
    timestampCaptured:          form.timestampCaptured,
    ipDeviceCaptured:           form.ipDeviceCaptured,
    eventTypesLogged:           form.eventTypesLogged || null,
    phiCategoriesInScope:       form.phiCategoriesInScope || null,
    recordIdentifierRef:        form.recordIdentifierRef || null,
    accessPurposesCaptured:     form.accessPurposesCaptured || null,
    statusCaptured:             form.statusCaptured,
    retentionPeriodDays:        form.retentionPeriodDays !== "" ? Number(form.retentionPeriodDays) : null,
    logStorageReference:        form.logStorageReference || null,
    integrityProtectionEnabled: form.integrityProtectionEnabled,
    reviewSchedule:             form.reviewSchedule || null,
    nextReviewDate:             form.nextReviewDate ? new Date(form.nextReviewDate).toISOString() : null,
    evidenceReference:          form.evidenceReference || null,
    riskLevel:                  form.riskLevel || null,
  });

  // ── Mutations ─────────────────────────────────────────────────────────────
  const createM = useMutation({
    mutationFn: () => authFetch(BASE, { method: "POST", body: JSON.stringify(buildBody()) }).then(async r => { const d = await r.json(); if (!r.ok) throw d; return d; }),
    onSuccess: () => { invalidate(); setShowCreate(false); setForm({ ...HIPAA33_BLANK }); setErrors({}); toast({ title: "Audit control created" }); },
    onError: (e: any) => { const serverErrs = extractServerErrors(e); if (serverErrs) { setErrors(serverErrs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); } else toast({ title: "Unable to save", description: e?.message, variant: "destructive" }); },
  });
  const updateM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}`, { method: "PUT", body: JSON.stringify(buildBody()) }).then(async r => { const d = await r.json(); if (!r.ok) throw d; return d; }),
    onSuccess: (d) => { invalidate(); setSelected(d); setShowEdit(false); setErrors({}); toast({ title: "Audit control updated" }); },
    onError: (e: any) => { const serverErrs = extractServerErrors(e); if (serverErrs) { setErrors(serverErrs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); } else toast({ title: "Unable to save", description: e?.message, variant: "destructive" }); },
  });
  const validateM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}/validate`, { method: "POST" }).then(async r => { const d = await r.json(); if (!r.ok) throw d; return d; }),
    onSuccess: (d) => { setValidateResult(d); invalidate(); },
    onError: (e: any) => toast({ title: "Validation error", description: e?.message, variant: "destructive" }),
  });
  const reviewM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}/review`, { method: "PATCH", body: JSON.stringify({
      reviewerName:    reviewForm.reviewerName,
      reviewerTitle:   reviewForm.reviewerTitle || null,
      reviewNotes:     reviewForm.reviewNotes || null,
      nextReviewDate:  reviewForm.nextReviewDate ? new Date(reviewForm.nextReviewDate).toISOString() : null,
      evidenceReference: reviewForm.evidenceReference || null,
    })}).then(async r => { const d = await r.json(); if (!r.ok) throw d; return d; }),
    onSuccess: (d) => { invalidate(); setSelected(d); setShowReview(false); setReviewErrors({}); toast({ title: "Review completed" }); },
    onError: (e: any) => { const s = extractServerErrors(e); if (s && Object.keys(s).length) { setReviewErrors(s); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); } else toast({ title: "Unable to save review", description: e?.message, variant: "destructive" }); },
  });
  const deleteM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}`, { method: "DELETE" }).then(async r => { if (!r.ok) { const d = await r.json(); throw d; } }),
    onSuccess: () => { invalidate(); setShowDelete(false); setShowDetail(false); setSelected(null); toast({ title: "Audit control deleted" }); },
    onError: (e: any) => toast({ title: "Unable to delete", description: e?.message, variant: "destructive" }),
  });

  const sum    = summaryQ.data;
  const items  = listQ.data?.items ?? [];
  const count  = listQ.data?.count ?? 0;
  const issues = issuesQ.data ?? [];

  // ── Check-box row helper ──────────────────────────────────────────────────
  const BoolRow = ({ label, field }: { label: string; field: string }) => (
    <label className="flex items-center gap-2 text-xs cursor-pointer">
      <input type="checkbox" checked={!!(form as any)[field]} onChange={e => setF(field, e.target.checked)} />
      {label}
    </label>
  );

  // ── Form ──────────────────────────────────────────────────────────────────
  const formSection = (
    <div className="space-y-3 text-sm">
      <div>
        <label className="block text-xs font-medium mb-1">Audit Policy Name *</label>
        <input className={fieldCls("w-full border rounded px-2 py-1 text-sm", errors, "auditPolicyName")} aria-invalid={!!errors.auditPolicyName} value={form.auditPolicyName} onChange={e => { setF("auditPolicyName", e.target.value); clearErr(setErrors, "auditPolicyName"); }} placeholder="e.g. EHR PHI Audit Policy" />
        {fErr(errors, "auditPolicyName")}
      </div>
      <div>
        <label className="block text-xs font-medium mb-1">Description</label>
        <textarea className="w-full border rounded px-2 py-1 text-sm" rows={2} value={form.policyDescription} onChange={e => setF("policyDescription", e.target.value)} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium mb-1">Covered Systems (comma-separated)</label>
          <input className="w-full border rounded px-2 py-1 text-sm" value={form.coveredSystems} onChange={e => setF("coveredSystems", e.target.value)} placeholder="EHR,Lab,Pharmacy" />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">PHI Systems Enabled</label>
          <input className="w-full border rounded px-2 py-1 text-sm" value={form.phiSystemsEnabled} onChange={e => setF("phiSystemsEnabled", e.target.value)} placeholder="EHR v7, Lab Portal 3.2" />
        </div>
      </div>
      <div className="rounded border p-3 space-y-2">
        <p className="text-xs font-semibold text-gray-700">Logging Configuration</p>
        <div className="grid grid-cols-2 gap-1">
          <BoolRow label="Audit Logging Enabled"         field="auditLoggingEnabled" />
          <BoolRow label="Access Event Logging"          field="accessEventLoggingEnabled" />
          <BoolRow label="Record Modification Logging"   field="modificationLoggingEnabled" />
          <BoolRow label="Authentication Event Logging"  field="authEventLoggingEnabled" />
          <BoolRow label="Failed Access Attempt Logging" field="failedAccessLoggingEnabled" />
        </div>
      </div>
      <div className="rounded border p-3 space-y-2">
        <p className="text-xs font-semibold text-gray-700">Attribution Capture</p>
        <div className="grid grid-cols-2 gap-1">
          <BoolRow label="User Identity Captured"   field="userIdentityCaptured" />
          <BoolRow label="User Role Captured"       field="userRoleCaptured" />
          <BoolRow label="Timestamp Captured"       field="timestampCaptured" />
          <BoolRow label="IP/Device Info Captured"  field="ipDeviceCaptured" />
          <BoolRow label="Success/Failure Status"   field="statusCaptured" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium mb-1">Event Types Logged (comma-separated)</label>
          <input className="w-full border rounded px-2 py-1 text-sm" value={form.eventTypesLogged} onChange={e => setF("eventTypesLogged", e.target.value)} placeholder="Read,Write,Delete,Auth,FailedAccess" />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">PHI Categories in Scope</label>
          <input className="w-full border rounded px-2 py-1 text-sm" value={form.phiCategoriesInScope} onChange={e => setF("phiCategoriesInScope", e.target.value)} placeholder="Demographics,Diagnoses,Medications" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium mb-1">Access Purposes Captured</label>
          <input className="w-full border rounded px-2 py-1 text-sm" value={form.accessPurposesCaptured} onChange={e => setF("accessPurposesCaptured", e.target.value)} placeholder="Treatment,Payment,Operations" />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">Record Identifier Format</label>
          <input className="w-full border rounded px-2 py-1 text-sm" value={form.recordIdentifierRef} onChange={e => setF("recordIdentifierRef", e.target.value)} placeholder="MRN:XXXXXXXX (no PHI values)" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium mb-1">Retention Period (days)</label>
          <input type="number" min={0} className="w-full border rounded px-2 py-1 text-sm" value={form.retentionPeriodDays} onChange={e => setF("retentionPeriodDays", e.target.value)} placeholder="2190 (HIPAA min = 6 years)" />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">Log Storage Reference</label>
          <input className="w-full border rounded px-2 py-1 text-sm" value={form.logStorageReference} onChange={e => setF("logStorageReference", e.target.value)} placeholder="splunk://phi-audit-logs" />
        </div>
      </div>
      <div className="flex items-center gap-4">
        <BoolRow label="Integrity Protection Enabled" field="integrityProtectionEnabled" />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-medium mb-1">Review Schedule</label>
          <select className="w-full border rounded px-2 py-1 text-sm" value={form.reviewSchedule} onChange={e => setF("reviewSchedule", e.target.value)}>
            <option value="">— select —</option>
            {HIPAA33_REVIEW_SCHEDULES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">Next Review Date</label>
          <input type="date" className="w-full border rounded px-2 py-1 text-sm" value={form.nextReviewDate} onChange={e => setF("nextReviewDate", e.target.value)} />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">Risk Level</label>
          <select className="w-full border rounded px-2 py-1 text-sm" value={form.riskLevel} onChange={e => setF("riskLevel", e.target.value)}>
            <option value="">— select —</option>
            {HIPAA33_RISK_LEVELS.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium mb-1">Evidence Reference</label>
        <input className="w-full border rounded px-2 py-1 text-sm" value={form.evidenceReference} onChange={e => setF("evidenceReference", e.target.value)} placeholder="evidence://hipaa33-audit-policy.pdf" />
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base font-semibold">PHI Audit Logging & Access Monitoring</CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">§ 164.312(b) — Track and monitor PHI access and record modification activity with HIPAA-specific audit evidence.</p>
            </div>
            {canManage && (
              <Button size="sm" onClick={() => { setForm({ ...HIPAA33_BLANK }); setShowCreate(true); }}>+ Add Audit Control</Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Summary metrics */}
          {sum && (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {[
                { label: "Total Controls",        value: sum.total,                    color: "" },
                { label: "Compliant",             value: sum.compliant,                color: sum.compliant > 0 ? "text-green-600" : "" },
                { label: "Non-Compliant",         value: sum.nonCompliant,             color: sum.nonCompliant > 0 ? "text-red-600" : "" },
                { label: "PHI Systems Monitored", value: sum.phiSystemsMonitored,      color: "" },
                { label: "Access Events Logged",  value: sum.accessEventsLogged,       color: "" },
                { label: "Mod Events Logged",     value: sum.modificationEventsLogged, color: "" },
                { label: "Pending Review",        value: sum.pendingReview,            color: sum.pendingReview > 0 ? "text-amber-600" : "" },
                { label: "Review Overdue",        value: sum.reviewOverdue,            color: sum.reviewOverdue > 0 ? "text-red-600" : "" },
              ].map(m => (
                <div key={m.label} className="rounded border p-2 text-center">
                  <div className={`text-xl font-bold ${m.color}`}>{m.value}</div>
                  <div className="text-xs text-muted-foreground leading-tight">{m.label}</div>
                </div>
              ))}
            </div>
          )}

          {/* Compliance percentage */}
          {sum && sum.total > 0 && (
            <div className="flex items-center gap-2 text-xs">
              <span className="text-muted-foreground">Compliance rate:</span>
              <span className={`font-semibold ${sum.compliancePercentage >= 80 ? "text-green-600" : sum.compliancePercentage >= 50 ? "text-amber-600" : "text-red-600"}`}>
                {sum.compliancePercentage}%
              </span>
              <div className="flex-1 rounded-full bg-gray-200 h-1.5 max-w-xs">
                <div className={`h-1.5 rounded-full ${sum.compliancePercentage >= 80 ? "bg-green-500" : sum.compliancePercentage >= 50 ? "bg-amber-500" : "bg-red-500"}`}
                     style={{ width: `${sum.compliancePercentage}%` }} />
              </div>
            </div>
          )}

          {/* Issues panel */}
          {issues.length > 0 && (
            <div className="rounded border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs font-semibold text-amber-800 mb-1">Controls requiring attention ({issues.length})</p>
              <ul className="space-y-1">
                {issues.slice(0, 5).map(c => (
                  <li key={c.id} className="text-xs text-amber-700 flex justify-between items-start gap-2">
                    <button className="underline hover:no-underline text-left" onClick={() => openDetail(c)}>{c.auditPolicyName}</button>
                    <span className="shrink-0 text-amber-600 font-mono">
                      {c.validationCodes.slice(0, 2).join(", ")}{c.validationCodes.length > 2 ? "…" : ""}
                    </span>
                  </li>
                ))}
                {issues.length > 5 && <li className="text-xs text-amber-600">+{issues.length - 5} more…</li>}
              </ul>
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-wrap gap-2">
            <input className="border rounded px-2 py-1 text-xs w-36" value={fSystem} onChange={e => { setFSystem(e.target.value); setPage(1); }} placeholder="Filter by system" />
            <select className="border rounded px-2 py-1 text-xs" value={fCompliance} onChange={e => { setFCompliance(e.target.value); setPage(1); }}>
              <option value="">All compliance statuses</option>
              {HIPAA33_COMPLIANCE_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select className="border rounded px-2 py-1 text-xs" value={fRisk} onChange={e => { setFRisk(e.target.value); setPage(1); }}>
              <option value="">All risk levels</option>
              {HIPAA33_RISK_LEVELS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <select className="border rounded px-2 py-1 text-xs" value={fLogging} onChange={e => { setFLogging(e.target.value); setPage(1); }}>
              <option value="">Any logging status</option>
              <option value="true">Logging enabled</option>
              <option value="false">Logging disabled</option>
            </select>
            {(fSystem||fCompliance||fRisk||fLogging) && (
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setFSystem(""); setFCompliance(""); setFRisk(""); setFLogging(""); setPage(1); }}>Clear</Button>
            )}
          </div>

          {/* Control list */}
          {listQ.isLoading ? (
            <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-14 w-full" />)}</div>
          ) : items.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-4">No audit controls found. {canManage && "Add one to track PHI audit logging compliance."}</p>
          ) : (
            <div className="divide-y rounded border">
              {items.map(c => (
                <div key={c.id} className="flex items-start justify-between p-3 hover:bg-muted/30 cursor-pointer" onClick={() => openDetail(c)}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium truncate">{c.auditPolicyName}</span>
                      <Badge variant={h33ComplianceBadge(c.complianceStatus)} className="text-xs">{c.complianceStatus}</Badge>
                      {c.reviewOverdue && <Badge variant="outline" className="text-xs text-amber-600">Review Overdue</Badge>}
                      {c.remediationRequired && <Badge variant="outline" className="text-xs text-red-600">Remediation</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 flex gap-3 flex-wrap">
                      {c.coveredSystems && <span>Systems: {c.coveredSystems.split(",").slice(0,3).join(", ")}</span>}
                      {c.auditLoggingEnabled && <span className="text-green-600">● Audit On</span>}
                      {!c.auditLoggingEnabled && <span className="text-red-500">● Audit Off</span>}
                      {c.retentionPeriodDays && <span>{c.retentionPeriodDays}d retention</span>}
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground shrink-0 ml-2 text-right">
                    {c.riskLevel && <div>{c.riskLevel} risk</div>}
                    {c.validationCodes.length > 0 && <div className="text-red-500">{c.validationCodes.length} issue{c.validationCodes.length > 1 ? "s" : ""}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {count > 10 && (
            <div className="flex justify-between items-center text-xs">
              <span>{count} total</span>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" className="h-7 text-xs" disabled={page<=1} onClick={() => setPage(p => p-1)}>Prev</Button>
                <span className="px-2 py-1">{page}</span>
                <Button size="sm" variant="outline" className="h-7 text-xs" disabled={page*10>=count} onClick={() => setPage(p => p+1)}>Next</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Create dialog ─────────────────────────────────────────────────── */}
      <Dialog open={showCreate} onOpenChange={v => { setShowCreate(v); if (!v) setErrors({}); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add PHI Audit Logging Control</DialogTitle></DialogHeader>
          {formSection}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={() => { const errs: Record<string, string> = {}; required(errs, "auditPolicyName", form.auditPolicyName, "Audit policy name"); if (Object.keys(errs).length) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } setErrors({}); createM.mutate(); }} disabled={createM.isPending}>{createM.isPending ? "Saving…" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Detail dialog ─────────────────────────────────────────────────── */}
      {selected && showDetail && (
        <Dialog open={showDetail} onOpenChange={v => { setShowDetail(v); if (!v) setSelected(null); }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 flex-wrap">
                {selected.auditPolicyName}
                <Badge variant={h33ComplianceBadge(selected.complianceStatus)}>{selected.complianceStatus}</Badge>
                {selected.reviewOverdue && <Badge variant="outline" className="text-amber-600">Review Overdue</Badge>}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              {selected.policyDescription && <p className="text-xs text-muted-foreground">{selected.policyDescription}</p>}

              {/* Logging coverage grid */}
              <div className="rounded border p-3">
                <p className="text-xs font-semibold mb-2">Logging Coverage</p>
                <div className="grid grid-cols-2 gap-1 text-xs">
                  {[
                    { label: "Audit Logging",          val: selected.auditLoggingEnabled },
                    { label: "Access Events",          val: selected.accessEventLoggingEnabled },
                    { label: "Modification Events",    val: selected.modificationLoggingEnabled },
                    { label: "Auth Events",            val: selected.authEventLoggingEnabled },
                    { label: "Failed Access",          val: selected.failedAccessLoggingEnabled },
                    { label: "User Identity",          val: selected.userIdentityCaptured },
                    { label: "User Role",              val: selected.userRoleCaptured },
                    { label: "Timestamp",              val: selected.timestampCaptured },
                    { label: "IP/Device",              val: selected.ipDeviceCaptured },
                    { label: "Success/Failure Status", val: selected.statusCaptured },
                    { label: "Integrity Protection",   val: selected.integrityProtectionEnabled },
                  ].map(r => (
                    <div key={r.label} className="flex items-center gap-1">
                      <span className={r.val ? "text-green-600" : "text-red-500"}>{r.val ? "✓" : "✗"}</span>
                      <span>{r.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs">
                {selected.coveredSystems    && <div><p className="font-medium">Covered Systems</p><p className="text-muted-foreground">{selected.coveredSystems}</p></div>}
                {selected.phiCategoriesInScope && <div><p className="font-medium">PHI Categories in Scope</p><p className="text-muted-foreground">{selected.phiCategoriesInScope}</p></div>}
                {selected.retentionPeriodDays && <div><p className="font-medium">Retention Period</p><p className="text-muted-foreground">{selected.retentionPeriodDays} days ({Math.round(selected.retentionPeriodDays / 365 * 10) / 10} years)</p></div>}
                {selected.logStorageReference && <div><p className="font-medium">Log Storage</p><p className="font-mono text-muted-foreground">{selected.logStorageReference}</p></div>}
                {selected.reviewSchedule && <div><p className="font-medium">Review Schedule</p><p className="text-muted-foreground">{selected.reviewSchedule}</p></div>}
                {selected.nextReviewDate && <div><p className="font-medium">Next Review</p><p className={`${selected.reviewOverdue ? "text-red-600 font-semibold" : "text-muted-foreground"}`}>{selected.nextReviewDate.slice(0,10)}</p></div>}
                {selected.reviewerName  && <div><p className="font-medium">Reviewer</p><p className="text-muted-foreground">{selected.reviewerName}{selected.reviewerTitle ? ` (${selected.reviewerTitle})` : ""}</p></div>}
              </div>

              {selected.validationCodes.length > 0 && (
                <div className="rounded bg-red-50 border border-red-200 p-2">
                  <p className="text-xs font-semibold text-red-800 mb-1">Compliance Issues</p>
                  <div className="flex flex-wrap gap-1">
                    {selected.validationCodes.map(c => (
                      <span key={c} className="text-xs font-mono bg-red-100 text-red-700 rounded px-1.5 py-0.5">{c}</span>
                    ))}
                  </div>
                </div>
              )}

              {selected.evidenceReference && <div><p className="text-xs font-medium">Evidence</p><p className="text-xs font-mono">{selected.evidenceReference}</p></div>}
            </div>
            <LinkedSourceRow entityType="HipaaPhiAuditControl" entityId={selected.id} canManage={canManage} />
            {canManage && (
              <DialogFooter className="flex flex-wrap gap-2 sm:justify-start">
                <Button size="sm" variant="outline" onClick={() => { setShowDetail(false); openEdit(selected); }}>Edit</Button>
                <Button size="sm" variant="outline" onClick={() => { setValidateResult(null); setShowValidate(true); }}>Validate</Button>
                <Button size="sm" variant="outline" onClick={() => { setReviewForm({ reviewerName: "", reviewerTitle: "", reviewNotes: "", nextReviewDate: "", evidenceReference: "" }); setShowReview(true); }}>Review</Button>
                <Button size="sm" variant="destructive" onClick={() => setShowDelete(true)}>Delete</Button>
              </DialogFooter>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* ── Edit dialog ───────────────────────────────────────────────────── */}
      {selected && showEdit && (
        <Dialog open={showEdit} onOpenChange={v => { setShowEdit(v); if (!v) setErrors({}); }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Edit Audit Control</DialogTitle></DialogHeader>
            {formSection}
            <LinkedSourceRow entityType="HipaaPhiAuditControl" entityId={selected?.id ?? ""} canManage={canManage} />
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEdit(false)}>Cancel</Button>
              <Button onClick={() => { const errs: Record<string, string> = {}; required(errs, "auditPolicyName", form.auditPolicyName, "Audit policy name"); if (Object.keys(errs).length) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } setErrors({}); updateM.mutate(); }} disabled={updateM.isPending}>{updateM.isPending ? "Saving…" : "Save"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Validate dialog ───────────────────────────────────────────────── */}
      {selected && showValidate && (
        <Dialog open={showValidate} onOpenChange={v => { setShowValidate(v); if (!v) setValidateResult(null); }}>
          <DialogContent>
            <DialogHeader><DialogTitle>Validate Audit Configuration</DialogTitle></DialogHeader>
            <p className="text-sm">Run compliance check for <strong>{selected.auditPolicyName}</strong>.</p>
            {validateResult && (
              <div className={`rounded border p-3 ${validateResult.valid ? "border-green-300 bg-green-50" : "border-red-300 bg-red-50"}`}>
                <div className={`font-bold ${validateResult.valid ? "text-green-700" : "text-red-700"}`}>
                  {validateResult.valid ? "✓ COMPLIANT" : "✗ NON-COMPLIANT"}
                </div>
                {validateResult.codes.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {validateResult.codes.map(c => (
                      <span key={c} className="text-xs font-mono bg-red-100 text-red-700 rounded px-1.5 py-0.5">{c}</span>
                    ))}
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowValidate(false)}>Close</Button>
              <Button onClick={() => validateM.mutate()} disabled={validateM.isPending}>{validateM.isPending ? "Validating…" : "Run Validation"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Review dialog ─────────────────────────────────────────────────── */}
      {selected && showReview && (
        <Dialog open={showReview} onOpenChange={v => { setShowReview(v); if (!v) setReviewErrors({}); }}>
          <DialogContent>
            <DialogHeader><DialogTitle>Complete Audit Review</DialogTitle></DialogHeader>
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">Reviewer Name *</label>
                  <input className={fieldCls("w-full border rounded px-2 py-1 text-sm", reviewErrors, "reviewerName")} aria-invalid={!!reviewErrors.reviewerName} value={reviewForm.reviewerName} onChange={e => { setReviewForm(f => ({ ...f, reviewerName: e.target.value })); clearErr(setReviewErrors, "reviewerName"); }} />
                  {fErr(reviewErrors, "reviewerName")}
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Reviewer Title</label>
                  <input className="w-full border rounded px-2 py-1 text-sm" value={reviewForm.reviewerTitle} onChange={e => setReviewForm(f => ({ ...f, reviewerTitle: e.target.value }))} placeholder="CISO, Privacy Officer" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Review Notes</label>
                <textarea className="w-full border rounded px-2 py-1 text-sm" rows={3} value={reviewForm.reviewNotes} onChange={e => setReviewForm(f => ({ ...f, reviewNotes: e.target.value }))} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">Next Review Date</label>
                  <input type="date" className="w-full border rounded px-2 py-1 text-sm" value={reviewForm.nextReviewDate} onChange={e => setReviewForm(f => ({ ...f, nextReviewDate: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Evidence Reference</label>
                  <input className="w-full border rounded px-2 py-1 text-sm" value={reviewForm.evidenceReference} onChange={e => setReviewForm(f => ({ ...f, evidenceReference: e.target.value }))} placeholder="evidence://review-2026.pdf" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowReview(false)}>Cancel</Button>
              <Button onClick={() => { const errs: Record<string,string> = {}; required(errs, "reviewerName", reviewForm.reviewerName, "Reviewer Name"); if (Object.keys(errs).length) { setReviewErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } setReviewErrors({}); reviewM.mutate(); }} disabled={reviewM.isPending}>{reviewM.isPending ? "Saving…" : "Complete Review"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Delete confirm ────────────────────────────────────────────────── */}
      {selected && showDelete && (
        <Dialog open={showDelete} onOpenChange={setShowDelete}>
          <DialogContent>
            <DialogHeader><DialogTitle>Delete Audit Control</DialogTitle></DialogHeader>
            <p className="text-sm">Permanently delete <strong>{selected.auditPolicyName}</strong>? This cannot be undone.</p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDelete(false)}>Cancel</Button>
              <Button variant="destructive" onClick={() => deleteM.mutate()} disabled={deleteM.isPending}>{deleteM.isPending ? "Deleting…" : "Delete"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// HIPAA-32 — Minimum Necessary PHI Access Control (§ 164.514(d))
// ══════════════════════════════════════════════════════════════════════════════

interface Mnap32Summary {
  total: number; active: number; draft: number; disabled: number; expired: number;
  reviewOverdue: number; approvalRequired: number; restrictedPolicies: number;
  compliancePercentage: number;
}

interface Mnap32Policy {
  id: string; organizationId: string;
  policyName: string; policyDescription?: string; systemApplication?: string;
  authorizedRoles?: string; authorizedDepartments?: string;
  allowedPurposes?: string; accessPurpose?: string;
  phiCategoriesCovered?: string; allowedDataElements?: string;
  restrictedDataElements?: string; restrictedPhiCategories?: string;
  minimumNecessaryEnforced: boolean; approvalRequired: boolean;
  authorizationLevel: string; status: string;
  effectiveDate?: string; expirationDate?: string; nextReviewDate?: string;
  reviewerName?: string; reviewerTitle?: string; reviewNotes?: string;
  lastReviewDate?: string; approverName?: string; approvalDate?: string;
  riskLevel?: string; evidenceReference?: string;
  remediationRequired: boolean; remediationNotes?: string;
  createdAt: string; updatedAt: string;
  isExpired: boolean; reviewOverdue: boolean;
}

interface Mnap32AccessResult {
  allowed: boolean; denialCode?: string; reason: string;
  appliedPolicy?: Mnap32Policy | { id: string; policyName: string };
  reviewOverdue?: boolean; reviewOverdueCode?: string;
}

function useAuth32() {
  return (url: string, opts: RequestInit = {}) => {
    const token = sessionStorage.getItem("dotnetbot.jwt.session") || localStorage.getItem("dotnetbot.jwt") || "";
    return fetch(url, { ...opts, headers: { ...(opts.headers as Record<string,string> || {}), "Content-Type": "application/json", Authorization: `Bearer ${token}` } });
  };
}

const MNAP32_STATUSES   = ["Draft","Active","Disabled","Expired"];
const MNAP32_AUTH_LVLS  = ["None","Read","Write","Admin"];
const MNAP32_RISK_LVLS  = ["Low","Medium","High","Critical"];
const MNAP32_PURPOSES   = ["Treatment","Payment","Operations","CareCoordination","Research","PublicHealth","LegalCompliance","Other"];
const MNAP32_PHI_CATS   = ["Demographics","Diagnoses","Medications","LabResults","Imaging","MentalHealth","BehavioralHealth","SubstanceAbuse","Genetics","Insurance","Billing","OperationalNotes","Other"];

function mnap32StatusBadge(s: string): "default"|"secondary"|"outline"|"destructive" {
  if (s === "Active")   return "default";
  if (s === "Draft")    return "outline";
  if (s === "Disabled") return "secondary";
  if (s === "Expired")  return "destructive";
  return "outline";
}

const MNAP32_BLANK = {
  policyName: "", policyDescription: "", systemApplication: "",
  authorizedRoles: "", authorizedDepartments: "", allowedPurposes: "", accessPurpose: "",
  phiCategoriesCovered: "", allowedDataElements: "", restrictedDataElements: "", restrictedPhiCategories: "",
  minimumNecessaryEnforced: true, approvalRequired: false, authorizationLevel: "Read",
  effectiveDate: "", expirationDate: "", nextReviewDate: "",
  riskLevel: "", evidenceReference: "",
};

function HipaaMinimumNecessaryAccessSection({ canManage }: { canManage: boolean }) {
  const authFetch = useAuth32();
  const qc = useQueryClient();
  const { toast } = useToast();
  const BASE = "/api/organization/compliance/hipaa/mn-access-control";

  // ── Queries ───────────────────────────────────────────────────────────────
  const summaryQ = useQuery<Mnap32Summary>({
    queryKey: ["hipaa32-summary"],
    queryFn: () => authFetch(`${BASE}/summary`).then(r => r.json()),
  });
  const issuesQ = useQuery<Mnap32Policy[]>({
    queryKey: ["hipaa32-issues"],
    queryFn: () => authFetch(`${BASE}/issues`).then(r => r.json()),
  });

  const [fStatus, setFStatus]   = useState("");
  const [fRole,   setFRole]     = useState("");
  const [fDept,   setFDept]     = useState("");
  const [fPhiCat, setFPhiCat]   = useState("");
  const [fRisk,   setFRisk]     = useState("");
  const [page,    setPage]      = useState(1);

  const listQ = useQuery<{ count: number; items: Mnap32Policy[] }>({
    queryKey: ["hipaa32-list", fStatus, fRole, fDept, fPhiCat, fRisk, page],
    queryFn: () => {
      const p = new URLSearchParams({ page: String(page), limit: "10" });
      if (fStatus) p.set("status", fStatus);
      if (fRole)   p.set("role", fRole);
      if (fDept)   p.set("department", fDept);
      if (fPhiCat) p.set("phiCat", fPhiCat);
      if (fRisk)   p.set("riskLevel", fRisk);
      return authFetch(`${BASE}?${p}`).then(r => r.json());
    },
  });

  // ── UI state ──────────────────────────────────────────────────────────────
  const [showCreate,   setShowCreate]   = useState(false);
  const [showDetail,   setShowDetail]   = useState(false);
  const [showEdit,     setShowEdit]     = useState(false);
  const [showActivate, setShowActivate] = useState(false);
  const [showDisable,  setShowDisable]  = useState(false);
  const [showReview,   setShowReview]   = useState(false);
  const [showCheck,    setShowCheck]    = useState(false);
  const [showDelete,   setShowDelete]   = useState(false);
  const [selected,     setSelected]     = useState<Mnap32Policy|null>(null);

  const [form, setForm] = useState({ ...MNAP32_BLANK });
  const setF = (k: string, v: unknown) => setForm(f => ({ ...f, [k]: v }));
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [activateForm, setActivateForm] = useState({ effectiveDate: "", expirationDate: "", approverName: "" });
  const [disableForm,  setDisableForm]  = useState({ reason: "" });
  const [reviewForm,    setReviewForm]   = useState({ reviewerName: "", reviewerTitle: "", reviewNotes: "", nextReviewDate: "" });
  const [reviewErrors,  setReviewErrors] = useState<Record<string,string>>({});
  const [checkForm,     setCheckForm]    = useState({ role: "", department: "", purpose: "", phiCategory: "", requestedDataElements: "", hasPreApproval: false });
  const [checkResult,  setCheckResult]  = useState<Mnap32AccessResult|null>(null);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["hipaa32-summary"] });
    qc.invalidateQueries({ queryKey: ["hipaa32-issues"] });
    qc.invalidateQueries({ queryKey: ["hipaa32-list"] });
  };

  const openDetail = (p: Mnap32Policy) => { setSelected(p); setShowDetail(true); };
  const openEdit   = (p: Mnap32Policy) => {
    setSelected(p);
    setForm({
      policyName: p.policyName, policyDescription: p.policyDescription ?? "",
      systemApplication: p.systemApplication ?? "",
      authorizedRoles: p.authorizedRoles ?? "", authorizedDepartments: p.authorizedDepartments ?? "",
      allowedPurposes: p.allowedPurposes ?? "", accessPurpose: p.accessPurpose ?? "",
      phiCategoriesCovered: p.phiCategoriesCovered ?? "",
      allowedDataElements: p.allowedDataElements ?? "",
      restrictedDataElements: p.restrictedDataElements ?? "",
      restrictedPhiCategories: p.restrictedPhiCategories ?? "",
      minimumNecessaryEnforced: p.minimumNecessaryEnforced,
      approvalRequired: p.approvalRequired, authorizationLevel: p.authorizationLevel,
      effectiveDate: p.effectiveDate?.slice(0,10) ?? "",
      expirationDate: p.expirationDate?.slice(0,10) ?? "",
      nextReviewDate: p.nextReviewDate?.slice(0,10) ?? "",
      riskLevel: p.riskLevel ?? "", evidenceReference: p.evidenceReference ?? "",
    });
    setShowEdit(true);
  };

  const buildBody = () => ({
    policyName:               form.policyName,
    policyDescription:        form.policyDescription || null,
    systemApplication:        form.systemApplication || null,
    authorizedRoles:          form.authorizedRoles || null,
    authorizedDepartments:    form.authorizedDepartments || null,
    allowedPurposes:          form.allowedPurposes || null,
    accessPurpose:            form.accessPurpose || null,
    phiCategoriesCovered:     form.phiCategoriesCovered || null,
    allowedDataElements:      form.allowedDataElements || null,
    restrictedDataElements:   form.restrictedDataElements || null,
    restrictedPhiCategories:  form.restrictedPhiCategories || null,
    minimumNecessaryEnforced: form.minimumNecessaryEnforced,
    approvalRequired:         form.approvalRequired,
    authorizationLevel:       form.authorizationLevel,
    effectiveDate:  form.effectiveDate  ? new Date(form.effectiveDate).toISOString()  : null,
    expirationDate: form.expirationDate ? new Date(form.expirationDate).toISOString() : null,
    nextReviewDate: form.nextReviewDate ? new Date(form.nextReviewDate).toISOString() : null,
    riskLevel:         form.riskLevel || null,
    evidenceReference: form.evidenceReference || null,
  });

  // ── Mutations ─────────────────────────────────────────────────────────────
  const createM = useMutation({
    mutationFn: () => authFetch(BASE, { method: "POST", body: JSON.stringify(buildBody()) }).then(async r => { const d = await r.json(); if (!r.ok) throw d; return d; }),
    onSuccess: () => { invalidate(); setShowCreate(false); setForm({ ...MNAP32_BLANK }); setErrors({}); toast({ title: "Policy created" }); },
    onError: (e: any) => { const serverErrs = extractServerErrors(e); if (serverErrs) { setErrors(serverErrs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); } else toast({ title: "Unable to save", description: e?.message, variant: "destructive" }); },
  });
  const updateM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}`, { method: "PUT", body: JSON.stringify(buildBody()) }).then(async r => { const d = await r.json(); if (!r.ok) throw d; return d; }),
    onSuccess: (d) => { invalidate(); setSelected(d); setShowEdit(false); setErrors({}); toast({ title: "Policy updated" }); },
    onError: (e: any) => { const serverErrs = extractServerErrors(e); if (serverErrs) { setErrors(serverErrs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); } else toast({ title: "Unable to save", description: e?.message, variant: "destructive" }); },
  });
  const activateM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}/activate`, { method: "POST", body: JSON.stringify({
      effectiveDate: activateForm.effectiveDate ? new Date(activateForm.effectiveDate).toISOString() : null,
      expirationDate: activateForm.expirationDate ? new Date(activateForm.expirationDate).toISOString() : null,
      approverName: activateForm.approverName || null,
    })}).then(async r => { const d = await r.json(); if (!r.ok) throw d; return d; }),
    onSuccess: (d) => { invalidate(); setSelected(d); setShowActivate(false); toast({ title: "Policy activated" }); },
    onError: (e: any) => toast({ title: "Activation failed", description: e?.message, variant: "destructive" }),
  });
  const disableM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}/disable`, { method: "POST", body: JSON.stringify({ reason: disableForm.reason || null }) }).then(async r => { const d = await r.json(); if (!r.ok) throw d; return d; }),
    onSuccess: (d) => { invalidate(); setSelected(d); setShowDisable(false); toast({ title: "Policy disabled" }); },
    onError: (e: any) => toast({ title: "Disable failed", description: e?.message, variant: "destructive" }),
  });
  const reviewM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}/review`, { method: "PATCH", body: JSON.stringify({
      reviewerName: reviewForm.reviewerName, reviewerTitle: reviewForm.reviewerTitle || null,
      reviewNotes: reviewForm.reviewNotes || null,
      nextReviewDate: reviewForm.nextReviewDate ? new Date(reviewForm.nextReviewDate).toISOString() : null,
    })}).then(async r => { const d = await r.json(); if (!r.ok) throw d; return d; }),
    onSuccess: (d) => { invalidate(); setSelected(d); setShowReview(false); setReviewErrors({}); toast({ title: "Review completed" }); },
    onError: (e: any) => { const s = extractServerErrors(e); if (s && Object.keys(s).length) { setReviewErrors(s); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); } else toast({ title: "Unable to save review", description: e?.message, variant: "destructive" }); },
  });
  const checkM = useMutation({
    mutationFn: () => authFetch(`${BASE}/access-check`, { method: "POST", body: JSON.stringify({
      role: checkForm.role, department: checkForm.department || null,
      purpose: checkForm.purpose || null, phiCategory: checkForm.phiCategory || null,
      requestedDataElements: checkForm.requestedDataElements || null,
      hasPreApproval: checkForm.hasPreApproval,
    })}).then(async r => { const d = await r.json(); if (!r.ok) throw d; return d; }),
    onSuccess: (d) => setCheckResult(d),
    onError: (e: any) => toast({ title: "Check failed", description: e?.message, variant: "destructive" }),
  });
  const deleteM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}`, { method: "DELETE" }).then(async r => { if (!r.ok) { const d = await r.json(); throw d; } }),
    onSuccess: () => { invalidate(); setShowDelete(false); setShowDetail(false); setSelected(null); toast({ title: "Policy deleted" }); },
    onError: (e: any) => toast({ title: "Unable to delete", description: e?.message, variant: "destructive" }),
  });

  const sum   = summaryQ.data;
  const items = listQ.data?.items ?? [];
  const count = listQ.data?.count ?? 0;
  const issues = issuesQ.data ?? [];

  // ── Form JSX ──────────────────────────────────────────────────────────────
  const formSection = (
    <div className="space-y-3 text-sm">
      <div>
        <label className="block text-xs font-medium mb-1">Policy Name *</label>
        <input className={fieldCls("w-full border rounded px-2 py-1 text-sm", errors, "policyName")} aria-invalid={!!errors.policyName} value={form.policyName} onChange={e => { setF("policyName", e.target.value); clearErr(setErrors, "policyName"); }} placeholder="e.g. Nurse PHI Access Policy" />
        {fErr(errors, "policyName")}
      </div>
      <div>
        <label className="block text-xs font-medium mb-1">Description</label>
        <textarea className="w-full border rounded px-2 py-1 text-sm" rows={2} value={form.policyDescription} onChange={e => setF("policyDescription", e.target.value)} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium mb-1">System / Application</label>
          <input className={fieldCls("w-full border rounded px-2 py-1 text-sm", errors, "systemApplication")} aria-invalid={!!errors.systemApplication} value={form.systemApplication} onChange={e => { setF("systemApplication", e.target.value); clearErr(setErrors, "systemApplication"); }} placeholder="EHR, Lab System, etc." />
          {fErr(errors, "systemApplication")}
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">Authorization Level</label>
          <select className="w-full border rounded px-2 py-1 text-sm" value={form.authorizationLevel} onChange={e => setF("authorizationLevel", e.target.value)}>
            {MNAP32_AUTH_LVLS.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium mb-1">Authorized Roles (comma-separated)</label>
          <input className="w-full border rounded px-2 py-1 text-sm" value={form.authorizedRoles} onChange={e => setF("authorizedRoles", e.target.value)} placeholder="Nurse,Physician,Radiologist" />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">Authorized Departments</label>
          <input className="w-full border rounded px-2 py-1 text-sm" value={form.authorizedDepartments} onChange={e => setF("authorizedDepartments", e.target.value)} placeholder="ICU,MedSurg,ED" />
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium mb-1">Allowed Purposes (comma-separated)</label>
        <input className="w-full border rounded px-2 py-1 text-sm" value={form.allowedPurposes} onChange={e => setF("allowedPurposes", e.target.value)} placeholder="Treatment,Payment,Operations" />
      </div>
      <div>
        <label className="block text-xs font-medium mb-1">PHI Categories Covered (comma-separated)</label>
        <input className="w-full border rounded px-2 py-1 text-sm" value={form.phiCategoriesCovered} onChange={e => setF("phiCategoriesCovered", e.target.value)} placeholder="Demographics,Diagnoses,Medications" />
      </div>
      <div>
        <label className="block text-xs font-medium mb-1">Allowed Data Elements (comma-separated)</label>
        <input className="w-full border rounded px-2 py-1 text-sm" value={form.allowedDataElements} onChange={e => setF("allowedDataElements", e.target.value)} placeholder="PatientName,DOB,Diagnosis" />
      </div>
      <div>
        <label className="block text-xs font-medium mb-1">Restricted Data Elements (comma-separated)</label>
        <input className="w-full border rounded px-2 py-1 text-sm" value={form.restrictedDataElements} onChange={e => setF("restrictedDataElements", e.target.value)} placeholder="SSN,FinancialInfo,MentalHealth" />
      </div>
      <div>
        <label className="block text-xs font-medium mb-1">Restricted PHI Categories (comma-separated)</label>
        <input className="w-full border rounded px-2 py-1 text-sm" value={form.restrictedPhiCategories} onChange={e => setF("restrictedPhiCategories", e.target.value)} placeholder="BehavioralHealth,SubstanceAbuse" />
      </div>
      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <input type="checkbox" checked={form.minimumNecessaryEnforced} onChange={e => setF("minimumNecessaryEnforced", e.target.checked)} />
          Minimum Necessary Enforced
        </label>
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <input type="checkbox" checked={form.approvalRequired} onChange={e => setF("approvalRequired", e.target.checked)} />
          Approval Required
        </label>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-medium mb-1">Effective Date</label>
          <input type="date" className="w-full border rounded px-2 py-1 text-sm" value={form.effectiveDate} onChange={e => setF("effectiveDate", e.target.value)} />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">Expiration Date</label>
          <input type="date" className="w-full border rounded px-2 py-1 text-sm" value={form.expirationDate} onChange={e => setF("expirationDate", e.target.value)} />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">Next Review</label>
          <input type="date" className="w-full border rounded px-2 py-1 text-sm" value={form.nextReviewDate} onChange={e => setF("nextReviewDate", e.target.value)} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium mb-1">Risk Level</label>
          <select className="w-full border rounded px-2 py-1 text-sm" value={form.riskLevel} onChange={e => setF("riskLevel", e.target.value)}>
            <option value="">— select —</option>
            {MNAP32_RISK_LVLS.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">Evidence Reference</label>
          <input className="w-full border rounded px-2 py-1 text-sm" value={form.evidenceReference} onChange={e => setF("evidenceReference", e.target.value)} placeholder="evidence://policy-doc.pdf" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base font-semibold">Minimum Necessary PHI Access Control</CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">§ 164.514(d) — Per-role access policies mapping authorized PHI categories, data elements, and permitted purposes.</p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => { setCheckResult(null); setCheckForm({ role: "", department: "", purpose: "", phiCategory: "", requestedDataElements: "", hasPreApproval: false }); setShowCheck(true); }}>
                Access Check
              </Button>
              {canManage && (
                <Button size="sm" onClick={() => { setForm({ ...MNAP32_BLANK }); setShowCreate(true); }}>+ Add Policy</Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Summary metrics */}
          {sum && (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {[
                { label: "Total Policies",   value: sum.total,            color: "" },
                { label: "Active",           value: sum.active,           color: sum.active > 0 ? "text-green-600" : "" },
                { label: "Restricted",       value: sum.restrictedPolicies,color: sum.restrictedPolicies > 0 ? "text-amber-600" : "" },
                { label: "Review Overdue",   value: sum.reviewOverdue,    color: sum.reviewOverdue > 0 ? "text-red-600" : "" },
              ].map(m => (
                <div key={m.label} className="rounded border p-3 text-center">
                  <div className={`text-2xl font-bold ${m.color}`}>{m.value}</div>
                  <div className="text-xs text-muted-foreground">{m.label}</div>
                </div>
              ))}
            </div>
          )}

          {/* Status chips */}
          {sum && (
            <div className="flex flex-wrap gap-2 text-xs">
              {[
                { label: `${sum.active} Active`,           cls: "bg-green-100 text-green-700" },
                { label: `${sum.draft} Draft`,             cls: "bg-gray-100 text-gray-600" },
                { label: `${sum.disabled} Disabled`,       cls: "bg-slate-100 text-slate-600" },
                { label: `${sum.expired} Expired`,         cls: sum.expired > 0 ? "bg-red-100 text-red-600" : "bg-gray-100 text-gray-400" },
                { label: `${sum.approvalRequired} Require Approval`, cls: "bg-blue-100 text-blue-600" },
              ].map(c => <span key={c.label} className={`rounded-full px-2 py-0.5 ${c.cls}`}>{c.label}</span>)}
            </div>
          )}

          {/* Issues */}
          {issues.length > 0 && (
            <div className="rounded border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs font-semibold text-amber-800 mb-1">Policies requiring attention ({issues.length})</p>
              <ul className="space-y-0.5">
                {issues.slice(0, 5).map(p => (
                  <li key={p.id} className="text-xs text-amber-700 flex justify-between items-start gap-2">
                    <button className="underline hover:no-underline text-left" onClick={() => openDetail(p)}>{p.policyName}</button>
                    <span className="shrink-0 text-amber-600">
                      {p.isExpired ? "Expired" : p.reviewOverdue ? "Review Overdue" : p.remediationRequired ? "Remediation" : ""}
                    </span>
                  </li>
                ))}
                {issues.length > 5 && <li className="text-xs text-amber-600">+{issues.length - 5} more…</li>}
              </ul>
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-wrap gap-2">
            <select className="border rounded px-2 py-1 text-xs" value={fStatus} onChange={e => { setFStatus(e.target.value); setPage(1); }}>
              <option value="">All statuses</option>
              {MNAP32_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <input className="border rounded px-2 py-1 text-xs w-32" value={fRole} onChange={e => { setFRole(e.target.value); setPage(1); }} placeholder="Filter by role" />
            <input className="border rounded px-2 py-1 text-xs w-32" value={fDept} onChange={e => { setFDept(e.target.value); setPage(1); }} placeholder="Filter by dept" />
            <select className="border rounded px-2 py-1 text-xs" value={fPhiCat} onChange={e => { setFPhiCat(e.target.value); setPage(1); }}>
              <option value="">All PHI categories</option>
              {MNAP32_PHI_CATS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select className="border rounded px-2 py-1 text-xs" value={fRisk} onChange={e => { setFRisk(e.target.value); setPage(1); }}>
              <option value="">All risk levels</option>
              {MNAP32_RISK_LVLS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            {(fStatus||fRole||fDept||fPhiCat||fRisk) && (
              <Button size="sm" variant="ghost" className="text-xs h-7" onClick={() => { setFStatus(""); setFRole(""); setFDept(""); setFPhiCat(""); setFRisk(""); setPage(1); }}>Clear</Button>
            )}
          </div>

          {/* Policy list */}
          {listQ.isLoading ? (
            <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-14 w-full" />)}</div>
          ) : items.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-4">No policies found. {canManage && "Add one to enforce minimum necessary PHI access."}</p>
          ) : (
            <div className="divide-y rounded border">
              {items.map(p => (
                <div key={p.id} className="flex items-start justify-between p-3 hover:bg-muted/30 cursor-pointer" onClick={() => openDetail(p)}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium truncate">{p.policyName}</span>
                      <Badge variant={mnap32StatusBadge(p.status)} className="text-xs">{p.status}</Badge>
                      {p.isExpired  && <Badge variant="destructive" className="text-xs">Expired</Badge>}
                      {p.reviewOverdue && <Badge variant="outline" className="text-xs text-amber-600">Review Overdue</Badge>}
                      {p.approvalRequired && <Badge variant="outline" className="text-xs text-blue-600">Approval Req.</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 flex gap-3 flex-wrap">
                      {p.authorizedRoles && <span>Roles: {p.authorizedRoles.split(",").slice(0,3).join(", ")}{p.authorizedRoles.split(",").length>3?"…":""}</span>}
                      {p.phiCategoriesCovered && <span>PHI: {p.phiCategoriesCovered.split(",").slice(0,2).join(", ")}{p.phiCategoriesCovered.split(",").length>2?"…":""}</span>}
                      {p.authorizationLevel && <span>{p.authorizationLevel} access</span>}
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground shrink-0 ml-2 text-right">
                    {p.riskLevel && <div>{p.riskLevel} risk</div>}
                    {p.nextReviewDate && <div>Review: {p.nextReviewDate.slice(0,10)}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {count > 10 && (
            <div className="flex justify-between items-center text-xs">
              <span>{count} total</span>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" className="h-7 text-xs" disabled={page<=1} onClick={() => setPage(p => p-1)}>Prev</Button>
                <span className="px-2 py-1">{page}</span>
                <Button size="sm" variant="outline" className="h-7 text-xs" disabled={page*10>=count} onClick={() => setPage(p => p+1)}>Next</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Create dialog ─────────────────────────────────────────────────── */}
      <Dialog open={showCreate} onOpenChange={v => { setShowCreate(v); if (!v) setErrors({}); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add Minimum Necessary Access Policy</DialogTitle></DialogHeader>
          {formSection}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={() => { const errs: Record<string, string> = {}; required(errs, "policyName", form.policyName, "Policy name"); required(errs, "systemApplication", form.systemApplication, "Application Name"); if (Object.keys(errs).length) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } setErrors({}); createM.mutate(); }} disabled={createM.isPending}>{createM.isPending ? "Saving…" : "Create Policy"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Detail dialog ─────────────────────────────────────────────────── */}
      {selected && showDetail && (
        <Dialog open={showDetail} onOpenChange={v => { setShowDetail(v); if (!v) setSelected(null); }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 flex-wrap">
                {selected.policyName}
                <Badge variant={mnap32StatusBadge(selected.status)}>{selected.status}</Badge>
                {selected.isExpired && <Badge variant="destructive">Expired</Badge>}
                {selected.reviewOverdue && <Badge variant="outline" className="text-amber-600">Review Overdue</Badge>}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              {selected.policyDescription && <p className="text-xs text-muted-foreground">{selected.policyDescription}</p>}
              <div className="grid grid-cols-3 gap-2 text-center text-xs">
                <div className="rounded border p-2"><div className="font-bold">{selected.authorizationLevel}</div><div className="text-muted-foreground">Auth Level</div></div>
                <div className="rounded border p-2"><div className={`font-bold ${selected.minimumNecessaryEnforced ? "text-green-600" : "text-amber-600"}`}>{selected.minimumNecessaryEnforced ? "Yes" : "No"}</div><div className="text-muted-foreground">Min. Nec. Enforced</div></div>
                <div className="rounded border p-2"><div className={`font-bold ${selected.approvalRequired ? "text-blue-600" : "text-gray-500"}`}>{selected.approvalRequired ? "Required" : "Not Required"}</div><div className="text-muted-foreground">Approval</div></div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {selected.authorizedRoles      && <div><p className="font-medium">Authorized Roles</p><p className="text-muted-foreground">{selected.authorizedRoles}</p></div>}
                {selected.authorizedDepartments && <div><p className="font-medium">Departments</p><p className="text-muted-foreground">{selected.authorizedDepartments}</p></div>}
                {selected.allowedPurposes      && <div><p className="font-medium">Allowed Purposes</p><p className="text-muted-foreground">{selected.allowedPurposes}</p></div>}
                {selected.phiCategoriesCovered && <div><p className="font-medium">PHI Categories</p><p className="text-muted-foreground">{selected.phiCategoriesCovered}</p></div>}
              </div>
              {selected.allowedDataElements && (
                <div className="rounded bg-green-50 border border-green-200 p-2 text-xs">
                  <p className="font-semibold text-green-800 mb-1">✓ Allowed Data Elements</p>
                  <p className="text-green-700">{selected.allowedDataElements}</p>
                </div>
              )}
              {selected.restrictedDataElements && (
                <div className="rounded bg-red-50 border border-red-200 p-2 text-xs">
                  <p className="font-semibold text-red-800 mb-1">✗ Restricted Data Elements</p>
                  <p className="text-red-700">{selected.restrictedDataElements}</p>
                </div>
              )}
              {selected.restrictedPhiCategories && (
                <div className="rounded bg-red-50 border border-red-200 p-2 text-xs">
                  <p className="font-semibold text-red-800 mb-1">✗ Restricted PHI Categories</p>
                  <p className="text-red-700">{selected.restrictedPhiCategories}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2 text-xs">
                {selected.effectiveDate  && <div><span className="font-medium">Effective: </span>{selected.effectiveDate.slice(0,10)}</div>}
                {selected.expirationDate && <div><span className="font-medium">Expires: </span>{selected.expirationDate.slice(0,10)}</div>}
                {selected.lastReviewDate && <div><span className="font-medium">Last Review: </span>{selected.lastReviewDate.slice(0,10)}</div>}
                {selected.nextReviewDate && <div><span className="font-medium">Next Review: </span>{selected.nextReviewDate.slice(0,10)}</div>}
                {selected.reviewerName   && <div><span className="font-medium">Reviewer: </span>{selected.reviewerName}{selected.reviewerTitle ? ` (${selected.reviewerTitle})` : ""}</div>}
                {selected.approverName   && <div><span className="font-medium">Approver: </span>{selected.approverName}</div>}
              </div>
              {selected.evidenceReference && <div><p className="text-xs font-medium">Evidence</p><p className="text-xs font-mono">{selected.evidenceReference}</p></div>}
            </div>
            <LinkedSourceRow entityType="HipaaMinNecessaryAccessPolicy" entityId={selected.id} canManage={canManage} />
            {canManage && (
              <DialogFooter className="flex flex-wrap gap-2 sm:justify-start">
                <Button size="sm" variant="outline" onClick={() => { setShowDetail(false); openEdit(selected); }}>Edit</Button>
                {selected.status === "Draft" && (
                  <Button size="sm" onClick={() => { setActivateForm({ effectiveDate: "", expirationDate: "", approverName: "" }); setShowActivate(true); }}>Activate</Button>
                )}
                {selected.status === "Active" && (
                  <Button size="sm" variant="outline" onClick={() => { setDisableForm({ reason: "" }); setShowDisable(true); }}>Disable</Button>
                )}
                <Button size="sm" variant="outline" onClick={() => { setReviewForm({ reviewerName: "", reviewerTitle: "", reviewNotes: "", nextReviewDate: "" }); setShowReview(true); }}>Review</Button>
                <Button size="sm" variant="destructive" onClick={() => setShowDelete(true)}>Delete</Button>
              </DialogFooter>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* ── Edit dialog ───────────────────────────────────────────────────── */}
      {selected && showEdit && (
        <Dialog open={showEdit} onOpenChange={v => { setShowEdit(v); if (!v) setErrors({}); }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Edit Policy</DialogTitle></DialogHeader>
            {formSection}
            <LinkedSourceRow entityType="HipaaMinNecessaryAccessPolicy" entityId={selected?.id ?? ""} canManage={canManage} />
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEdit(false)}>Cancel</Button>
              <Button onClick={() => { const errs: Record<string, string> = {}; required(errs, "policyName", form.policyName, "Policy name"); required(errs, "systemApplication", form.systemApplication, "Application Name"); if (Object.keys(errs).length) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } setErrors({}); updateM.mutate(); }} disabled={updateM.isPending}>{updateM.isPending ? "Saving…" : "Save"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Activate dialog ───────────────────────────────────────────────── */}
      {selected && showActivate && (
        <Dialog open={showActivate} onOpenChange={setShowActivate}>
          <DialogContent>
            <DialogHeader><DialogTitle>Activate Policy</DialogTitle></DialogHeader>
            <div className="space-y-3 text-sm">
              <p className="text-sm">Activate <strong>{selected.policyName}</strong>. Once active, this policy will be used in access checks.</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">Effective Date</label>
                  <input type="date" className="w-full border rounded px-2 py-1 text-sm" value={activateForm.effectiveDate} onChange={e => setActivateForm(f => ({ ...f, effectiveDate: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Expiration Date</label>
                  <input type="date" className="w-full border rounded px-2 py-1 text-sm" value={activateForm.expirationDate} onChange={e => setActivateForm(f => ({ ...f, expirationDate: e.target.value }))} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Approver Name</label>
                <input className="w-full border rounded px-2 py-1 text-sm" value={activateForm.approverName} onChange={e => setActivateForm(f => ({ ...f, approverName: e.target.value }))} placeholder="Privacy Officer" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowActivate(false)}>Cancel</Button>
              <Button onClick={() => activateM.mutate()} disabled={activateM.isPending}>{activateM.isPending ? "Activating…" : "Activate"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Disable dialog ────────────────────────────────────────────────── */}
      {selected && showDisable && (
        <Dialog open={showDisable} onOpenChange={setShowDisable}>
          <DialogContent>
            <DialogHeader><DialogTitle>Disable Policy</DialogTitle></DialogHeader>
            <p className="text-sm">Disable <strong>{selected.policyName}</strong>? It will no longer be used in access checks.</p>
            <div>
              <label className="block text-xs font-medium mb-1">Reason</label>
              <textarea className="w-full border rounded px-2 py-1 text-sm" rows={2} value={disableForm.reason} onChange={e => setDisableForm(f => ({ ...f, reason: e.target.value }))} placeholder="Policy superseded, role retired, etc." />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDisable(false)}>Cancel</Button>
              <Button variant="destructive" onClick={() => disableM.mutate()} disabled={disableM.isPending}>{disableM.isPending ? "Disabling…" : "Disable"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Review dialog ─────────────────────────────────────────────────── */}
      {selected && showReview && (
        <Dialog open={showReview} onOpenChange={v => { setShowReview(v); if (!v) setReviewErrors({}); }}>
          <DialogContent>
            <DialogHeader><DialogTitle>Complete Policy Review</DialogTitle></DialogHeader>
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">Reviewer Name *</label>
                  <input className={fieldCls("w-full border rounded px-2 py-1 text-sm", reviewErrors, "reviewerName")} aria-invalid={!!reviewErrors.reviewerName} value={reviewForm.reviewerName} onChange={e => { setReviewForm(f => ({ ...f, reviewerName: e.target.value })); clearErr(setReviewErrors, "reviewerName"); }} placeholder="Full name" />
                  {fErr(reviewErrors, "reviewerName")}
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Reviewer Title</label>
                  <input className="w-full border rounded px-2 py-1 text-sm" value={reviewForm.reviewerTitle} onChange={e => setReviewForm(f => ({ ...f, reviewerTitle: e.target.value }))} placeholder="Privacy Officer" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Review Notes</label>
                <textarea className="w-full border rounded px-2 py-1 text-sm" rows={3} value={reviewForm.reviewNotes} onChange={e => setReviewForm(f => ({ ...f, reviewNotes: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Next Review Date</label>
                <input type="date" className="w-full border rounded px-2 py-1 text-sm" value={reviewForm.nextReviewDate} onChange={e => setReviewForm(f => ({ ...f, nextReviewDate: e.target.value }))} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowReview(false)}>Cancel</Button>
              <Button onClick={() => { const errs: Record<string,string> = {}; required(errs, "reviewerName", reviewForm.reviewerName, "Reviewer Name"); if (Object.keys(errs).length) { setReviewErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } setReviewErrors({}); reviewM.mutate(); }} disabled={reviewM.isPending}>{reviewM.isPending ? "Saving…" : "Complete Review"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Access Check dialog ───────────────────────────────────────────── */}
      <Dialog open={showCheck} onOpenChange={v => { setShowCheck(v); if (!v) setCheckResult(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Access Check</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-xs text-muted-foreground">Evaluate whether a given role/purpose/PHI combination is allowed by active policies.</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Role *</label>
                <input className="w-full border rounded px-2 py-1 text-sm" value={checkForm.role} onChange={e => setCheckForm(f => ({ ...f, role: e.target.value }))} placeholder="Nurse, Physician..." />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Department</label>
                <input className="w-full border rounded px-2 py-1 text-sm" value={checkForm.department} onChange={e => setCheckForm(f => ({ ...f, department: e.target.value }))} placeholder="ICU, ED..." />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Purpose</label>
                <select className="w-full border rounded px-2 py-1 text-sm" value={checkForm.purpose} onChange={e => setCheckForm(f => ({ ...f, purpose: e.target.value }))}>
                  <option value="">— any —</option>
                  {MNAP32_PURPOSES.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">PHI Category</label>
                <select className="w-full border rounded px-2 py-1 text-sm" value={checkForm.phiCategory} onChange={e => setCheckForm(f => ({ ...f, phiCategory: e.target.value }))}>
                  <option value="">— any —</option>
                  {MNAP32_PHI_CATS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Requested Data Elements (comma-separated)</label>
              <input className="w-full border rounded px-2 py-1 text-sm" value={checkForm.requestedDataElements} onChange={e => setCheckForm(f => ({ ...f, requestedDataElements: e.target.value }))} placeholder="PatientName,DOB,SSN..." />
            </div>
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={checkForm.hasPreApproval} onChange={e => setCheckForm(f => ({ ...f, hasPreApproval: e.target.checked }))} />
              Has Pre-Approval
            </label>

            {/* Result */}
            {checkResult && (
              <div className={`rounded border p-3 ${checkResult.allowed ? "border-green-300 bg-green-50" : "border-red-300 bg-red-50"}`}>
                <div className="flex items-center gap-2">
                  <span className={`text-lg font-bold ${checkResult.allowed ? "text-green-700" : "text-red-700"}`}>
                    {checkResult.allowed ? "✓ ALLOW" : "✗ DENY"}
                  </span>
                  {checkResult.denialCode && (
                    <span className="text-xs font-mono bg-red-100 text-red-600 px-1.5 py-0.5 rounded">{checkResult.denialCode}</span>
                  )}
                  {checkResult.reviewOverdueCode && (
                    <span className="text-xs font-mono bg-amber-100 text-amber-600 px-1.5 py-0.5 rounded">{checkResult.reviewOverdueCode}</span>
                  )}
                </div>
                <p className="text-xs mt-1">{checkResult.reason}</p>
                {checkResult.appliedPolicy && "policyName" in checkResult.appliedPolicy && (
                  <p className="text-xs text-muted-foreground mt-0.5">Policy: {(checkResult.appliedPolicy as Mnap32Policy).policyName}</p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCheck(false)}>Close</Button>
            <Button onClick={() => checkM.mutate()} disabled={checkM.isPending || !checkForm.role}>{checkM.isPending ? "Checking…" : "Check Access"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete confirm ────────────────────────────────────────────────── */}
      {selected && showDelete && (
        <Dialog open={showDelete} onOpenChange={setShowDelete}>
          <DialogContent>
            <DialogHeader><DialogTitle>Delete Policy</DialogTitle></DialogHeader>
            <p className="text-sm">Permanently delete <strong>{selected.policyName}</strong>? This cannot be undone.</p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDelete(false)}>Cancel</Button>
              <Button variant="destructive" onClick={() => deleteM.mutate()} disabled={deleteM.isPending}>{deleteM.isPending ? "Deleting…" : "Delete"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// HIPAA-31 — PHI Encryption at Rest (AES-256 PHI Protection)
// ══════════════════════════════════════════════════════════════════════════════

interface Pear31Summary {
  total: number; compliant: number; nonCompliant: number; pendingReview: number;
  encrypted: number; unencryptedPhi: number; missingEvidence: number;
  rotationOverdue: number; reviewOverdue: number; issues: number;
  compliancePercentage: number;
}

interface Pear31Control {
  id: string; organizationId: string;
  systemName: string; storageType: string; phiDataInvolved?: string;
  encryptionEnabled: boolean; encryptionAlgorithm?: string; encryptionStrength?: string;
  keyReference?: string; keyManagementProvider?: string;
  keyRotationPolicy?: string; lastKeyRotationDate?: string; nextKeyRotationDate?: string;
  backupEncryptionEnabled: boolean; databaseEncryptionEnabled: boolean; storageEncryptionEnabled: boolean;
  evidenceReference?: string;
  reviewStatus?: string; reviewerName?: string; reviewerTitle?: string; reviewNotes?: string;
  reviewDate?: string; nextReviewDate?: string;
  complianceStatus: string; riskLevel?: string;
  remediationRequired: boolean; remediationStatus?: string; remediationNotes?: string;
  createdAt: string; updatedAt: string;
  rotationOverdue: boolean; reviewOverdue: boolean; missingEvidence: boolean; missingKeyRef: boolean;
  validationCodes: string[]; issueCount: number;
}

function useAuth31() {
  return (url: string, opts: RequestInit = {}) => {
    const token = sessionStorage.getItem("dotnetbot.jwt.session") || localStorage.getItem("dotnetbot.jwt") || "";
    return fetch(url, { ...opts, headers: { ...(opts.headers as Record<string,string> || {}), "Content-Type": "application/json", Authorization: `Bearer ${token}` } });
  };
}

const PEAR31_STORAGE_TYPES = ["Database","ObjectStorage","FileStorage","Backup","Other"];
const PEAR31_ALGORITHMS    = ["AES-256","AES-256-GCM","AES-256-CBC","Provider Managed Encryption","Customer Managed Keys","Other"];
const PEAR31_KM_PROVIDERS  = ["AWS KMS","Azure Key Vault","GCP Cloud KMS","HashiCorp Vault","HSM","Internal","Other"];
const PEAR31_ROT_POLICIES  = ["Automatic","Manual","None"];
const PEAR31_STATUSES      = ["Compliant","PendingReview","NonCompliant","Disabled"];
const PEAR31_RISK_LEVELS   = ["Low","Medium","High","Critical"];

function pear31StatusBadge(s: string): "default"|"secondary"|"outline"|"destructive" {
  if (s === "Compliant")     return "default";
  if (s === "PendingReview") return "outline";
  if (s === "NonCompliant")  return "destructive";
  if (s === "Disabled")      return "secondary";
  return "outline";
}

const PEAR31_BLANK = {
  systemName: "", storageType: "Database", phiDataInvolved: "",
  encryptionEnabled: true, encryptionAlgorithm: "AES-256-GCM", encryptionStrength: "256-bit",
  keyReference: "", keyManagementProvider: "AWS KMS",
  keyRotationPolicy: "Automatic", lastKeyRotationDate: "", nextKeyRotationDate: "",
  backupEncryptionEnabled: false, databaseEncryptionEnabled: true, storageEncryptionEnabled: true,
  evidenceReference: "", riskLevel: "", nextReviewDate: "",
};

function HipaaEncryptionAtRestSection({ canManage }: { canManage: boolean }) {
  const authFetch = useAuth31();
  const qc = useQueryClient();
  const { toast } = useToast();
  const BASE = "/api/organization/compliance/hipaa/phi-encryption-at-rest";

  // ── Queries ───────────────────────────────────────────────────────────────
  const summaryQ = useQuery<Pear31Summary>({
    queryKey: ["hipaa31-summary"],
    queryFn: () => authFetch(`${BASE}/summary`).then(r => r.json()),
  });
  const issuesQ = useQuery<Pear31Control[]>({
    queryKey: ["hipaa31-issues"],
    queryFn: () => authFetch(`${BASE}/issues`).then(r => r.json()),
  });

  const [fStatus,  setFStatus]  = useState("");
  const [fStorage, setFStorage] = useState("");
  const [fEnc,     setFEnc]     = useState("");
  const [fRisk,    setFRisk]    = useState("");
  const [page,     setPage]     = useState(1);

  const listQ = useQuery<{ count: number; items: Pear31Control[] }>({
    queryKey: ["hipaa31-list", fStatus, fStorage, fEnc, fRisk, page],
    queryFn: () => {
      const p = new URLSearchParams({ page: String(page), limit: "10" });
      if (fStatus)  p.set("status", fStatus);
      if (fStorage) p.set("storageType", fStorage);
      if (fEnc)     p.set("encEnabled", fEnc);
      if (fRisk)    p.set("riskLevel", fRisk);
      return authFetch(`${BASE}?${p}`).then(r => r.json());
    },
  });

  // ── UI state ──────────────────────────────────────────────────────────────
  const [showCreate,   setShowCreate]   = useState(false);
  const [showDetail,   setShowDetail]   = useState(false);
  const [showEdit,     setShowEdit]     = useState(false);
  const [showValidate, setShowValidate] = useState(false);
  const [showRotate,   setShowRotate]   = useState(false);
  const [showReview,   setShowReview]   = useState(false);
  const [showDelete,   setShowDelete]   = useState(false);
  const [selected,     setSelected]     = useState<Pear31Control|null>(null);

  const [form,  setForm]  = useState({ ...PEAR31_BLANK });
  const setF = (k: string, v: unknown) => setForm(f => ({ ...f, [k]: v }));
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [rotateForm, setRotateForm] = useState({ lastKeyRotationDate: "", nextKeyRotationDate: "", rotationNotes: "" });
  const [reviewForm,   setReviewForm]   = useState({ reviewerName: "", reviewerTitle: "", reviewNotes: "", complianceStatus: "Compliant", nextReviewDate: "" });
  const [reviewErrors, setReviewErrors] = useState<Record<string,string>>({});

  // ── Helpers ───────────────────────────────────────────────────────────────
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["hipaa31-summary"] });
    qc.invalidateQueries({ queryKey: ["hipaa31-issues"] });
    qc.invalidateQueries({ queryKey: ["hipaa31-list"] });
  };

  const openDetail = (c: Pear31Control) => { setSelected(c); setShowDetail(true); };
  const openEdit   = (c: Pear31Control) => {
    setSelected(c);
    setForm({
      systemName: c.systemName, storageType: c.storageType, phiDataInvolved: c.phiDataInvolved ?? "",
      encryptionEnabled: c.encryptionEnabled, encryptionAlgorithm: c.encryptionAlgorithm ?? "",
      encryptionStrength: c.encryptionStrength ?? "", keyReference: c.keyReference ?? "",
      keyManagementProvider: c.keyManagementProvider ?? "", keyRotationPolicy: c.keyRotationPolicy ?? "",
      lastKeyRotationDate: c.lastKeyRotationDate?.slice(0,10) ?? "",
      nextKeyRotationDate: c.nextKeyRotationDate?.slice(0,10) ?? "",
      backupEncryptionEnabled: c.backupEncryptionEnabled,
      databaseEncryptionEnabled: c.databaseEncryptionEnabled,
      storageEncryptionEnabled: c.storageEncryptionEnabled,
      evidenceReference: c.evidenceReference ?? "", riskLevel: c.riskLevel ?? "",
      nextReviewDate: c.nextReviewDate?.slice(0,10) ?? "",
    });
    setShowEdit(true);
  };

  const buildBody = () => ({
    systemName:              form.systemName,
    storageType:             form.storageType,
    phiDataInvolved:         form.phiDataInvolved || null,
    encryptionEnabled:       form.encryptionEnabled,
    encryptionAlgorithm:     form.encryptionAlgorithm || null,
    encryptionStrength:      form.encryptionStrength || null,
    keyReference:            form.keyReference || null,
    keyManagementProvider:   form.keyManagementProvider || null,
    keyRotationPolicy:       form.keyRotationPolicy || null,
    lastKeyRotationDate:     form.lastKeyRotationDate ? new Date(form.lastKeyRotationDate).toISOString() : null,
    nextKeyRotationDate:     form.nextKeyRotationDate ? new Date(form.nextKeyRotationDate).toISOString() : null,
    backupEncryptionEnabled:   form.backupEncryptionEnabled,
    databaseEncryptionEnabled: form.databaseEncryptionEnabled,
    storageEncryptionEnabled:  form.storageEncryptionEnabled,
    evidenceReference:       form.evidenceReference || null,
    riskLevel:               form.riskLevel || null,
    nextReviewDate:          form.nextReviewDate ? new Date(form.nextReviewDate).toISOString() : null,
  });

  // ── Mutations ─────────────────────────────────────────────────────────────
  const createM = useMutation({
    mutationFn: () => authFetch(BASE, { method: "POST", body: JSON.stringify(buildBody()) }).then(async r => { const d = await r.json(); if (!r.ok) throw d; return d; }),
    onSuccess: () => { invalidate(); setShowCreate(false); setErrors({}); setForm({ ...PEAR31_BLANK }); toast({ title: "Control added" }); },
    onError: (e: any) => {
      const serverErrs = extractServerErrors(e);
      if (serverErrs && Object.keys(serverErrs).length) { setErrors(serverErrs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); }
      else toast({ title: "Unable to save", description: e?.message ?? String(e?.codes?.join(", ") ?? ""), variant: "destructive" });
    },
  });
  const updateM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}`, { method: "PUT", body: JSON.stringify(buildBody()) }).then(async r => { const d = await r.json(); if (!r.ok) throw d; return d; }),
    onSuccess: (d) => { invalidate(); setSelected(d); setShowEdit(false); toast({ title: "Control updated" }); },
    onError: (e: any) => toast({ title: "Unable to update", description: e?.message, variant: "destructive" }),
  });
  const validateM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}/validate`, { method: "POST", body: "{}" }).then(async r => { const d = await r.json(); if (!r.ok) throw d; return d; }),
    onSuccess: (d) => { invalidate(); setSelected(d.entity); setShowValidate(false); toast({ title: d.compliant ? "Compliant ✓" : "Issues found", description: d.compliant ? undefined : d.validationCodes?.join(", "), variant: d.compliant ? "default" : "destructive" }); },
    onError: (e: any) => toast({ title: "Validate failed", description: e?.message, variant: "destructive" }),
  });
  const rotateM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}/rotate`, { method: "PATCH", body: JSON.stringify({
      lastKeyRotationDate: new Date(rotateForm.lastKeyRotationDate).toISOString(),
      nextKeyRotationDate: rotateForm.nextKeyRotationDate ? new Date(rotateForm.nextKeyRotationDate).toISOString() : null,
      rotationNotes: rotateForm.rotationNotes || null,
    })}).then(async r => { const d = await r.json(); if (!r.ok) throw d; return d; }),
    onSuccess: (d) => { invalidate(); setSelected(d); setShowRotate(false); toast({ title: "Key rotation recorded" }); },
    onError: (e: any) => toast({ title: "Rotation failed", description: e?.message, variant: "destructive" }),
  });
  const reviewM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}/review`, { method: "PATCH", body: JSON.stringify({
      reviewerName: reviewForm.reviewerName, reviewerTitle: reviewForm.reviewerTitle || null,
      reviewNotes: reviewForm.reviewNotes || null,
      complianceStatus: reviewForm.complianceStatus || null,
      nextReviewDate: reviewForm.nextReviewDate ? new Date(reviewForm.nextReviewDate).toISOString() : null,
    })}).then(async r => { const d = await r.json(); if (!r.ok) throw d; return d; }),
    onSuccess: (d) => { invalidate(); setSelected(d); setShowReview(false); setReviewErrors({}); toast({ title: "Review completed" }); },
    onError: (e: any) => { const s = extractServerErrors(e); if (s && Object.keys(s).length) { setReviewErrors(s); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); } else toast({ title: "Unable to save review", description: e?.message, variant: "destructive" }); },
  });
  const deleteM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}`, { method: "DELETE" }).then(async r => { if (!r.ok) { const d = await r.json(); throw d; } }),
    onSuccess: () => { invalidate(); setShowDelete(false); setShowDetail(false); setSelected(null); toast({ title: "Control deleted" }); },
    onError: (e: any) => toast({ title: "Unable to delete", description: e?.message, variant: "destructive" }),
  });

  const sum   = summaryQ.data;
  const items = listQ.data?.items ?? [];
  const count = listQ.data?.count ?? 0;
  const issues = issuesQ.data ?? [];

  const formSection = (
    <div className="space-y-3 text-sm">
      <div>
        <label className="block text-xs font-medium mb-1">System / Application Name *</label>
        <input className={fieldCls("w-full border rounded px-2 py-1 text-sm", errors, "systemName")} aria-invalid={!!errors.systemName} value={form.systemName} onChange={e => { setF("systemName", e.target.value); clearErr(setErrors, "systemName"); }} placeholder="e.g. Patient EHR Database" />
        {fErr(errors, "systemName")}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium mb-1">Storage Type</label>
          <select className="w-full border rounded px-2 py-1 text-sm" value={form.storageType} onChange={e => setF("storageType", e.target.value)}>
            {PEAR31_STORAGE_TYPES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">Risk Level</label>
          <select className="w-full border rounded px-2 py-1 text-sm" value={form.riskLevel} onChange={e => setF("riskLevel", e.target.value)}>
            <option value="">— select —</option>
            {PEAR31_RISK_LEVELS.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium mb-1">PHI Data Involved</label>
        <input className="w-full border rounded px-2 py-1 text-sm" value={form.phiDataInvolved} onChange={e => setF("phiDataInvolved", e.target.value)} placeholder="Patient records, medical history, diagnoses..." />
      </div>
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <input type="checkbox" checked={form.encryptionEnabled} onChange={e => setF("encryptionEnabled", e.target.checked)} />
          Encryption Enabled
        </label>
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <input type="checkbox" checked={form.backupEncryptionEnabled} onChange={e => setF("backupEncryptionEnabled", e.target.checked)} />
          Backup Enc.
        </label>
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <input type="checkbox" checked={form.databaseEncryptionEnabled} onChange={e => setF("databaseEncryptionEnabled", e.target.checked)} />
          DB Enc.
        </label>
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <input type="checkbox" checked={form.storageEncryptionEnabled} onChange={e => setF("storageEncryptionEnabled", e.target.checked)} />
          Storage Enc.
        </label>
      </div>
      {form.encryptionEnabled && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1">Encryption Algorithm *</label>
              <select className="w-full border rounded px-2 py-1 text-sm" value={form.encryptionAlgorithm} onChange={e => setF("encryptionAlgorithm", e.target.value)}>
                <option value="">— select —</option>
                {PEAR31_ALGORITHMS.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Encryption Strength</label>
              <input className="w-full border rounded px-2 py-1 text-sm" value={form.encryptionStrength} onChange={e => setF("encryptionStrength", e.target.value)} placeholder="256-bit" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Key Reference (ARN/alias — not the actual key) *</label>
            <input className="w-full border rounded px-2 py-1 text-sm" value={form.keyReference} onChange={e => setF("keyReference", e.target.value)} placeholder="arn:aws:kms:us-east-1:123456789:key/mrk-..." />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1">Key Management Provider</label>
              <select className="w-full border rounded px-2 py-1 text-sm" value={form.keyManagementProvider} onChange={e => setF("keyManagementProvider", e.target.value)}>
                <option value="">— select —</option>
                {PEAR31_KM_PROVIDERS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Key Rotation Policy</label>
              <select className="w-full border rounded px-2 py-1 text-sm" value={form.keyRotationPolicy} onChange={e => setF("keyRotationPolicy", e.target.value)}>
                <option value="">— select —</option>
                {PEAR31_ROT_POLICIES.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1">Last Key Rotation</label>
              <input type="date" className="w-full border rounded px-2 py-1 text-sm" value={form.lastKeyRotationDate} onChange={e => setF("lastKeyRotationDate", e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Next Key Rotation</label>
              <input type="date" className="w-full border rounded px-2 py-1 text-sm" value={form.nextKeyRotationDate} onChange={e => setF("nextKeyRotationDate", e.target.value)} />
            </div>
          </div>
        </>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium mb-1">Evidence Reference</label>
          <input className="w-full border rounded px-2 py-1 text-sm" value={form.evidenceReference} onChange={e => setF("evidenceReference", e.target.value)} placeholder="evidence://enc-audit-2026.pdf" />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">Next Review Date</label>
          <input type="date" className="w-full border rounded px-2 py-1 text-sm" value={form.nextReviewDate} onChange={e => setF("nextReviewDate", e.target.value)} />
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base font-semibold">PHI Encryption at Rest</CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">§ 164.312(a)(2)(iv) — Verify that all PHI stored at rest is protected with AES-256 encryption.</p>
            </div>
            {canManage && (
              <Button size="sm" onClick={() => { setForm({ ...PEAR31_BLANK }); setShowCreate(true); }}>+ Add Control</Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Summary metrics */}
          {sum && (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {[
                { label: "Total Systems",      value: sum.total,          color: "" },
                { label: "Compliant",          value: sum.compliant,      color: sum.compliant === sum.total && sum.total > 0 ? "text-green-600" : "" },
                { label: "Non-Compliant",      value: sum.nonCompliant,   color: sum.nonCompliant > 0 ? "text-red-600" : "" },
                { label: "Issues",             value: sum.issues,         color: sum.issues > 0 ? "text-amber-600" : "" },
              ].map(m => (
                <div key={m.label} className="rounded border p-3 text-center">
                  <div className={`text-2xl font-bold ${m.color}`}>{m.value}</div>
                  <div className="text-xs text-muted-foreground">{m.label}</div>
                </div>
              ))}
            </div>
          )}

          {/* Alert row */}
          {sum && (sum.unencryptedPhi > 0 || sum.rotationOverdue > 0 || sum.missingEvidence > 0) && (
            <div className="flex flex-wrap gap-2 text-xs">
              {sum.unencryptedPhi > 0   && <span className="rounded-full bg-red-100 text-red-700 px-2 py-0.5 font-medium">⚠ {sum.unencryptedPhi} PHI system{sum.unencryptedPhi>1?"s":""} unencrypted</span>}
              {sum.rotationOverdue > 0  && <span className="rounded-full bg-amber-100 text-amber-700 px-2 py-0.5">⚠ {sum.rotationOverdue} rotation overdue</span>}
              {sum.missingEvidence > 0  && <span className="rounded-full bg-yellow-100 text-yellow-700 px-2 py-0.5">⚠ {sum.missingEvidence} missing evidence</span>}
            </div>
          )}

          {/* Compliance % bar */}
          {sum && sum.total > 0 && (
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span>Compliance rate</span><span className="font-medium">{sum.compliancePercentage}%</span>
              </div>
              <div className="h-2 rounded-full bg-muted overflow-hidden">
                <div className={`h-full rounded-full transition-all ${sum.compliancePercentage >= 80 ? "bg-green-500" : sum.compliancePercentage >= 50 ? "bg-amber-500" : "bg-red-500"}`}
                  style={{ width: `${sum.compliancePercentage}%` }} />
              </div>
            </div>
          )}

          {/* Issues panel */}
          {issues.length > 0 && (
            <div className="rounded border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs font-semibold text-amber-800 mb-1">Systems with issues ({issues.length})</p>
              <ul className="space-y-1">
                {issues.slice(0, 5).map(c => (
                  <li key={c.id} className="text-xs text-amber-700 flex justify-between items-start gap-2">
                    <button className="underline hover:no-underline text-left" onClick={() => openDetail(c)}>{c.systemName}</button>
                    <span className="shrink-0">{c.validationCodes.slice(0,2).join(", ")}</span>
                  </li>
                ))}
                {issues.length > 5 && <li className="text-xs text-amber-600">+{issues.length-5} more…</li>}
              </ul>
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-wrap gap-2">
            <select className="border rounded px-2 py-1 text-xs" value={fStatus} onChange={e => { setFStatus(e.target.value); setPage(1); }}>
              <option value="">All statuses</option>
              {PEAR31_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select className="border rounded px-2 py-1 text-xs" value={fStorage} onChange={e => { setFStorage(e.target.value); setPage(1); }}>
              <option value="">All storage types</option>
              {PEAR31_STORAGE_TYPES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select className="border rounded px-2 py-1 text-xs" value={fEnc} onChange={e => { setFEnc(e.target.value); setPage(1); }}>
              <option value="">Enc. status</option>
              <option value="true">Encrypted</option>
              <option value="false">Not encrypted</option>
            </select>
            <select className="border rounded px-2 py-1 text-xs" value={fRisk} onChange={e => { setFRisk(e.target.value); setPage(1); }}>
              <option value="">All risk levels</option>
              {PEAR31_RISK_LEVELS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            {(fStatus||fStorage||fEnc||fRisk) && <Button size="sm" variant="ghost" className="text-xs h-7" onClick={() => { setFStatus(""); setFStorage(""); setFEnc(""); setFRisk(""); setPage(1); }}>Clear</Button>}
          </div>

          {/* List */}
          {listQ.isLoading ? (
            <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>
          ) : items.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-4">No controls found. {canManage && "Add one to track PHI encryption compliance."}</p>
          ) : (
            <div className="divide-y rounded border">
              {items.map(c => (
                <div key={c.id} className="flex items-start justify-between p-3 hover:bg-muted/30 cursor-pointer" onClick={() => openDetail(c)}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium truncate">{c.systemName}</span>
                      <Badge variant={pear31StatusBadge(c.complianceStatus)} className="text-xs">{c.complianceStatus}</Badge>
                      <span className="text-xs text-muted-foreground">{c.storageType}</span>
                      {c.encryptionEnabled ? <span className="text-xs text-green-600">🔒 AES-256</span> : <span className="text-xs text-red-600">⚠ Unencrypted</span>}
                      {c.rotationOverdue && <Badge variant="destructive" className="text-xs">Rotation Overdue</Badge>}
                      {c.issueCount > 0 && <Badge variant="outline" className="text-xs text-amber-600">{c.issueCount} issue{c.issueCount>1?"s":""}</Badge>}
                    </div>
                    {c.phiDataInvolved && <p className="text-xs text-muted-foreground mt-0.5 truncate">{c.phiDataInvolved}</p>}
                    {c.encryptionAlgorithm && <p className="text-xs text-muted-foreground">{c.encryptionAlgorithm}{c.keyManagementProvider ? ` · ${c.keyManagementProvider}` : ""}</p>}
                  </div>
                  <div className="text-xs text-muted-foreground shrink-0 ml-2 text-right">
                    {c.nextKeyRotationDate && <div>Rotate: {c.nextKeyRotationDate.slice(0,10)}</div>}
                    {c.riskLevel && <div>{c.riskLevel} risk</div>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {count > 10 && (
            <div className="flex justify-between items-center text-xs">
              <span>{count} total</span>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" className="h-7 text-xs" disabled={page<=1} onClick={() => setPage(p => p-1)}>Prev</Button>
                <span className="px-2 py-1">{page}</span>
                <Button size="sm" variant="outline" className="h-7 text-xs" disabled={page*10>=count} onClick={() => setPage(p => p+1)}>Next</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Create dialog ─────────────────────────────────────────────────── */}
      <Dialog open={showCreate} onOpenChange={v => { setShowCreate(v); if (!v) setErrors({}); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add PHI Encryption Control</DialogTitle></DialogHeader>
          {formSection}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreate(false); setErrors({}); }}>Cancel</Button>
            <Button onClick={() => {
              const errs: Record<string, string> = {};
              required(errs, "systemName", form.systemName, "System / Application Name");
              if (Object.keys(errs).length) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; }
              setErrors({}); createM.mutate();
            }} disabled={createM.isPending}>{createM.isPending ? "Saving…" : "Add Control"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Detail dialog ─────────────────────────────────────────────────── */}
      {selected && showDetail && (
        <Dialog open={showDetail} onOpenChange={v => { setShowDetail(v); if (!v) setSelected(null); }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 flex-wrap">
                {selected.systemName}
                <Badge variant={pear31StatusBadge(selected.complianceStatus)}>{selected.complianceStatus}</Badge>
                {selected.rotationOverdue && <Badge variant="destructive">Rotation Overdue</Badge>}
                {selected.missingEvidence && <Badge variant="outline" className="text-amber-600">Missing Evidence</Badge>}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-3 gap-2 text-center text-xs">
                <div className="rounded border p-2"><div className="font-bold">{selected.storageType}</div><div className="text-muted-foreground">Storage Type</div></div>
                <div className="rounded border p-2"><div className={`font-bold ${selected.encryptionEnabled ? "text-green-600" : "text-red-600"}`}>{selected.encryptionEnabled ? "Yes" : "No"}</div><div className="text-muted-foreground">Encrypted</div></div>
                <div className="rounded border p-2"><div className="font-bold">{selected.encryptionAlgorithm ?? "—"}</div><div className="text-muted-foreground">Algorithm</div></div>
              </div>
              {selected.phiDataInvolved && <div><p className="text-xs font-medium">PHI Data Involved</p><p className="text-xs">{selected.phiDataInvolved}</p></div>}
              <div className="grid grid-cols-2 gap-2 text-xs">
                {selected.encryptionStrength     && <div><span className="font-medium">Strength: </span>{selected.encryptionStrength}</div>}
                {selected.keyManagementProvider  && <div><span className="font-medium">KMS Provider: </span>{selected.keyManagementProvider}</div>}
                {selected.keyRotationPolicy      && <div><span className="font-medium">Rotation Policy: </span>{selected.keyRotationPolicy}</div>}
                {selected.lastKeyRotationDate    && <div><span className="font-medium">Last Rotated: </span>{selected.lastKeyRotationDate.slice(0,10)}</div>}
                {selected.nextKeyRotationDate    && <div><span className="font-medium">Next Rotation: </span>{selected.nextKeyRotationDate.slice(0,10)}</div>}
                {selected.riskLevel              && <div><span className="font-medium">Risk Level: </span>{selected.riskLevel}</div>}
              </div>
              {selected.keyReference && <div><p className="text-xs font-medium">Key Reference</p><p className="text-xs font-mono break-all">{selected.keyReference}</p></div>}
              <div className="flex gap-4 text-xs">
                <span className={`${selected.backupEncryptionEnabled ? "text-green-600" : "text-red-500"}`}>{selected.backupEncryptionEnabled ? "✓" : "✗"} Backup Enc.</span>
                <span className={`${selected.databaseEncryptionEnabled ? "text-green-600" : "text-red-500"}`}>{selected.databaseEncryptionEnabled ? "✓" : "✗"} DB Enc.</span>
                <span className={`${selected.storageEncryptionEnabled ? "text-green-600" : "text-red-500"}`}>{selected.storageEncryptionEnabled ? "✓" : "✗"} Storage Enc.</span>
              </div>
              {selected.issueCount > 0 && (
                <div className="rounded border border-red-200 bg-red-50 p-2">
                  <p className="text-xs font-semibold text-red-700 mb-1">Validation Issues</p>
                  <ul className="space-y-0.5">{selected.validationCodes.map(c => <li key={c} className="text-xs text-red-600 font-mono">{c}</li>)}</ul>
                </div>
              )}
              {selected.evidenceReference && <div><p className="text-xs font-medium">Evidence Reference</p><p className="text-xs font-mono">{selected.evidenceReference}</p></div>}
              {selected.reviewerName && <div><p className="text-xs font-medium">Reviewer</p><p className="text-xs">{selected.reviewerName}{selected.reviewerTitle ? ` — ${selected.reviewerTitle}` : ""}</p></div>}
              {selected.reviewNotes && <div><p className="text-xs font-medium">Review Notes</p><p className="text-xs">{selected.reviewNotes}</p></div>}
            </div>
            <LinkedSourceRow entityType="HipaaPhiEncryptionAtRest" entityId={selected.id} canManage={canManage} />
            {canManage && (
              <DialogFooter className="flex flex-wrap gap-2 sm:justify-start">
                <Button size="sm" variant="outline" onClick={() => { setShowDetail(false); openEdit(selected); }}>Edit</Button>
                <Button size="sm" variant="outline" onClick={() => setShowValidate(true)}>Validate</Button>
                {selected.encryptionEnabled && <Button size="sm" variant="outline" onClick={() => { setRotateForm({ lastKeyRotationDate: "", nextKeyRotationDate: "", rotationNotes: "" }); setShowRotate(true); }}>Record Rotation</Button>}
                <Button size="sm" variant="outline" onClick={() => { setReviewForm({ reviewerName: "", reviewerTitle: "", reviewNotes: "", complianceStatus: "Compliant", nextReviewDate: "" }); setShowReview(true); }}>Complete Review</Button>
                <Button size="sm" variant="destructive" onClick={() => setShowDelete(true)}>Delete</Button>
              </DialogFooter>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* ── Edit dialog ───────────────────────────────────────────────────── */}
      {selected && showEdit && (
        <Dialog open={showEdit} onOpenChange={setShowEdit}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Edit Control</DialogTitle></DialogHeader>
            {formSection}
            <LinkedSourceRow entityType="HipaaPhiEncryptionAtRest" entityId={selected?.id ?? ""} canManage={canManage} />
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEdit(false)}>Cancel</Button>
              <Button onClick={() => updateM.mutate()} disabled={updateM.isPending}>{updateM.isPending ? "Saving…" : "Save"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Validate dialog ───────────────────────────────────────────────── */}
      {selected && showValidate && (
        <Dialog open={showValidate} onOpenChange={setShowValidate}>
          <DialogContent>
            <DialogHeader><DialogTitle>Validate Encryption Configuration</DialogTitle></DialogHeader>
            <p className="text-sm">Run a compliance check on <strong>{selected.systemName}</strong>. The server will verify AES-256 requirements and flag any issues.</p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowValidate(false)}>Cancel</Button>
              <Button onClick={() => validateM.mutate()} disabled={validateM.isPending}>{validateM.isPending ? "Validating…" : "Run Validation"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Record key rotation dialog ────────────────────────────────────── */}
      {selected && showRotate && (
        <Dialog open={showRotate} onOpenChange={setShowRotate}>
          <DialogContent>
            <DialogHeader><DialogTitle>Record Key Rotation</DialogTitle></DialogHeader>
            <div className="space-y-3 text-sm">
              <div>
                <label className="block text-xs font-medium mb-1">Last Rotation Date *</label>
                <input type="date" className="w-full border rounded px-2 py-1 text-sm" value={rotateForm.lastKeyRotationDate} onChange={e => setRotateForm(f => ({ ...f, lastKeyRotationDate: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Next Rotation Date</label>
                <input type="date" className="w-full border rounded px-2 py-1 text-sm" value={rotateForm.nextKeyRotationDate} onChange={e => setRotateForm(f => ({ ...f, nextKeyRotationDate: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Notes</label>
                <textarea className="w-full border rounded px-2 py-1 text-sm" rows={2} value={rotateForm.rotationNotes} onChange={e => setRotateForm(f => ({ ...f, rotationNotes: e.target.value }))} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRotate(false)}>Cancel</Button>
              <Button onClick={() => rotateM.mutate()} disabled={rotateM.isPending}>{rotateM.isPending ? "Saving…" : "Record Rotation"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Complete review dialog ────────────────────────────────────────── */}
      {selected && showReview && (
        <Dialog open={showReview} onOpenChange={v => { setShowReview(v); if (!v) setReviewErrors({}); }}>
          <DialogContent>
            <DialogHeader><DialogTitle>Complete Review</DialogTitle></DialogHeader>
            <div className="space-y-3 text-sm">
              <div>
                <label className="block text-xs font-medium mb-1">Reviewer Name *</label>
                <input className={fieldCls("w-full border rounded px-2 py-1 text-sm", reviewErrors, "reviewerName")} aria-invalid={!!reviewErrors.reviewerName} value={reviewForm.reviewerName} onChange={e => { setReviewForm(f => ({ ...f, reviewerName: e.target.value })); clearErr(setReviewErrors, "reviewerName"); }} placeholder="Full name" />
                {fErr(reviewErrors, "reviewerName")}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">Reviewer Title</label>
                  <input className="w-full border rounded px-2 py-1 text-sm" value={reviewForm.reviewerTitle} onChange={e => setReviewForm(f => ({ ...f, reviewerTitle: e.target.value }))} placeholder="CISO, Security Officer..." />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Update Compliance Status</label>
                  <select className="w-full border rounded px-2 py-1 text-sm" value={reviewForm.complianceStatus} onChange={e => setReviewForm(f => ({ ...f, complianceStatus: e.target.value }))}>
                    {PEAR31_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Review Notes</label>
                <textarea className="w-full border rounded px-2 py-1 text-sm" rows={3} value={reviewForm.reviewNotes} onChange={e => setReviewForm(f => ({ ...f, reviewNotes: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Next Review Date</label>
                <input type="date" className="w-full border rounded px-2 py-1 text-sm" value={reviewForm.nextReviewDate} onChange={e => setReviewForm(f => ({ ...f, nextReviewDate: e.target.value }))} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowReview(false)}>Cancel</Button>
              <Button onClick={() => { const errs: Record<string,string> = {}; required(errs, "reviewerName", reviewForm.reviewerName, "Reviewer Name"); if (Object.keys(errs).length) { setReviewErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } setReviewErrors({}); reviewM.mutate(); }} disabled={reviewM.isPending}>{reviewM.isPending ? "Saving…" : "Complete Review"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Delete confirm ────────────────────────────────────────────────── */}
      {selected && showDelete && (
        <Dialog open={showDelete} onOpenChange={setShowDelete}>
          <DialogContent>
            <DialogHeader><DialogTitle>Delete Control</DialogTitle></DialogHeader>
            <p className="text-sm">Permanently delete the control for <strong>{selected.systemName}</strong>? This cannot be undone.</p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDelete(false)}>Cancel</Button>
              <Button variant="destructive" onClick={() => deleteM.mutate()} disabled={deleteM.isPending}>{deleteM.isPending ? "Deleting…" : "Delete"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// HIPAA-30 — Risk Management Assessments (§ 164.308(a)(1))
// ══════════════════════════════════════════════════════════════════════════════

interface Ra30Summary {
  total: number; open: number; draft: number; inProgress: number;
  inReview: number; approved: number; remReq: number; closed: number;
  highCritical: number; pendingApproval: number; overdue: number;
}

interface Ra30Assessment {
  id: string; organizationId: string;
  assessmentName: string; assessmentReference?: string;
  assessmentScope?: string; coveredSystems?: string; ephiSystems?: string;
  threatCategories?: string; vulnerabilitiesIdentified?: string;
  likelihood: number; impact: number; riskScore: number; riskLevel: string;
  mitigationActions?: string; assignedOwner?: string;
  targetRemediationDate?: string;
  status: string; approvalStatus?: string;
  reviewerName?: string; reviewerTitle?: string; reviewNotes?: string;
  assessmentDate?: string; nextReviewDate?: string;
  evidenceReference?: string;
  createdAt: string; updatedAt: string;
  remediationOverdue: boolean;
}

function useAuth30() {
  return (url: string, opts: RequestInit = {}) => {
    const token = sessionStorage.getItem("dotnetbot.jwt.session") || localStorage.getItem("dotnetbot.jwt") || "";
    return fetch(url, {
      ...opts,
      headers: { ...(opts.headers as Record<string, string> || {}), "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    });
  };
}

const RA30_RISK_LEVELS = ["Low", "Medium", "High", "Critical"];
const RA30_STATUSES    = ["Draft", "AssessmentInProgress", "Review", "Approved", "RemediationRequired", "Closed"];

function ra30LevelBadge(l: string): "default" | "secondary" | "outline" | "destructive" {
  if (l === "Low")      return "secondary";
  if (l === "Medium")   return "outline";
  if (l === "High")     return "default";
  if (l === "Critical") return "destructive";
  return "outline";
}

function ra30StatusBadge(s: string): "default" | "secondary" | "outline" | "destructive" {
  if (s === "Approved")           return "default";
  if (s === "Closed")             return "secondary";
  if (s === "Review")             return "outline";
  if (s === "RemediationRequired") return "destructive";
  return "outline";
}

const RA30_BLANK = {
  assessmentName: "", assessmentReference: "", assessmentScope: "",
  coveredSystems: "", ephiSystems: "",
  threatCategories: "", vulnerabilitiesIdentified: "",
  likelihood: 3, impact: 3,
  mitigationActions: "", assignedOwner: "", targetRemediationDate: "",
  evidenceReference: "", assessmentDate: "", nextReviewDate: "",
};

function HipaaRiskAssessmentSection({ canManage }: { canManage: boolean }) {
  const authFetch = useAuth30();
  const qc = useQueryClient();
  const { toast } = useToast();
  const BASE = "/api/organization/compliance/hipaa/risk-assessments";

  // ── Queries ───────────────────────────────────────────────────────────────
  const summaryQ = useQuery<Ra30Summary>({
    queryKey: ["hipaa30-summary"],
    queryFn: () => authFetch(`${BASE}/summary`).then(r => r.json()),
  });
  const [fStatus, setFStatus]     = useState("");
  const [fLevel,  setFLevel]      = useState("");
  const [page,    setPage]        = useState(1);

  const listQ = useQuery<{ count: number; items: Ra30Assessment[] }>({
    queryKey: ["hipaa30-list", fStatus, fLevel, page],
    queryFn: () => {
      const p = new URLSearchParams({ page: String(page), limit: "10" });
      if (fStatus) p.set("status", fStatus);
      if (fLevel)  p.set("riskLevel", fLevel);
      return authFetch(`${BASE}?${p}`).then(r => r.json());
    },
  });

  const overdueQ = useQuery<Ra30Assessment[]>({
    queryKey: ["hipaa30-overdue"],
    queryFn: () => authFetch(`${BASE}/overdue`).then(r => r.json()),
  });

  // ── UI state ──────────────────────────────────────────────────────────────
  const [showCreate,   setShowCreate]   = useState(false);
  const [showDetail,   setShowDetail]   = useState(false);
  const [showEdit,     setShowEdit]     = useState(false);
  const [showSubmit,   setShowSubmit]   = useState(false);
  const [showApprove,  setShowApprove]  = useState(false);
  const [showMitigate, setShowMitigate] = useState(false);
  const [showClose,    setShowClose]    = useState(false);
  const [showDelete,   setShowDelete]   = useState(false);
  const [selected,     setSelected]     = useState<Ra30Assessment | null>(null);

  const [form, setForm] = useState({ ...RA30_BLANK });
  const setF = (k: string, v: unknown) => setForm(f => ({ ...f, [k]: v }));
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [approveForm, setApproveForm]   = useState({ reviewerName: "", reviewerTitle: "", reviewNotes: "", approvalStatus: "Approved" });
  const [mitigateForm, setMitigateForm] = useState({ mitigationActions: "", assignedOwner: "", targetRemediationDate: "", evidenceReference: "" });
  const [closeForm, setCloseForm]       = useState({ evidenceReference: "", notes: "" });
  const [approveErrors, setApproveErrors] = useState<Record<string,string>>({});
  const [mitigateErrors, setMitigateErrors] = useState<Record<string,string>>({});
  const [closeErrors,   setCloseErrors]   = useState<Record<string,string>>({});

  // ── Helpers ───────────────────────────────────────────────────────────────
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["hipaa30-summary"] });
    qc.invalidateQueries({ queryKey: ["hipaa30-list"] });
    qc.invalidateQueries({ queryKey: ["hipaa30-overdue"] });
  };

  const previewScore = (l: number, i: number) => Math.max(1, Math.min(5, l)) * Math.max(1, Math.min(5, i));
  const previewLevel = (s: number) => s <= 5 ? "Low" : s <= 10 ? "Medium" : s <= 17 ? "High" : "Critical";
  const iScore = previewScore(Number(form.likelihood) || 3, Number(form.impact) || 3);
  const iLevel = previewLevel(iScore);

  const openDetail   = (a: Ra30Assessment) => { setSelected(a); setShowDetail(true); };
  const openEdit     = (a: Ra30Assessment) => {
    setSelected(a);
    setForm({
      assessmentName:          a.assessmentName,
      assessmentReference:     a.assessmentReference ?? "",
      assessmentScope:         a.assessmentScope ?? "",
      coveredSystems:          a.coveredSystems ?? "",
      ephiSystems:             a.ephiSystems ?? "",
      threatCategories:        a.threatCategories ?? "",
      vulnerabilitiesIdentified: a.vulnerabilitiesIdentified ?? "",
      likelihood:              a.likelihood,
      impact:                  a.impact,
      mitigationActions:       a.mitigationActions ?? "",
      assignedOwner:           a.assignedOwner ?? "",
      targetRemediationDate:   a.targetRemediationDate?.slice(0, 10) ?? "",
      evidenceReference:       a.evidenceReference ?? "",
      assessmentDate:          a.assessmentDate?.slice(0, 10) ?? "",
      nextReviewDate:          a.nextReviewDate?.slice(0, 10) ?? "",
    });
    setShowEdit(true);
  };

  // ── Mutations ─────────────────────────────────────────────────────────────
  const buildBody = () => ({
    assessmentName:          form.assessmentName,
    assessmentReference:     form.assessmentReference || null,
    assessmentScope:         form.assessmentScope || null,
    coveredSystems:          form.coveredSystems || null,
    ephiSystems:             form.ephiSystems || null,
    threatCategories:        form.threatCategories || null,
    vulnerabilitiesIdentified: form.vulnerabilitiesIdentified || null,
    likelihood:              Number(form.likelihood),
    impact:                  Number(form.impact),
    mitigationActions:       form.mitigationActions || null,
    assignedOwner:           form.assignedOwner || null,
    targetRemediationDate:   form.targetRemediationDate ? new Date(form.targetRemediationDate).toISOString() : null,
    evidenceReference:       form.evidenceReference || null,
    assessmentDate:          form.assessmentDate ? new Date(form.assessmentDate).toISOString() : null,
    nextReviewDate:          form.nextReviewDate ? new Date(form.nextReviewDate).toISOString() : null,
  });

  const createM = useMutation({
    mutationFn: () => authFetch(BASE, { method: "POST", body: JSON.stringify(buildBody()) }).then(async r => {
      const d = await r.json(); if (!r.ok) throw d; return d;
    }),
    onSuccess: () => { invalidate(); setShowCreate(false); setErrors({}); setForm({ ...RA30_BLANK }); toast({ title: "Assessment created" }); },
    onError:   (e: any) => {
      const serverErrs = extractServerErrors(e);
      if (serverErrs && Object.keys(serverErrs).length) { setErrors(serverErrs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); }
      else toast({ title: "Unable to save", description: e?.message ?? "Server error", variant: "destructive" });
    },
  });

  const updateM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}`, { method: "PUT", body: JSON.stringify(buildBody()) }).then(async r => {
      const d = await r.json(); if (!r.ok) throw d; return d;
    }),
    onSuccess: (d) => { invalidate(); setSelected(d); setShowEdit(false); setErrors({}); toast({ title: "Assessment updated" }); },
    onError:   (e: any) => { const s = extractServerErrors(e); if (s && Object.keys(s).length) { setErrors(s); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); } else toast({ title: "Unable to update", description: e?.message ?? "Server error", variant: "destructive" }); },
  });

  const submitM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}/submit`, { method: "POST", body: "{}" }).then(async r => {
      const d = await r.json(); if (!r.ok) throw d; return d;
    }),
    onSuccess: (d) => { invalidate(); setSelected(d); setShowSubmit(false); toast({ title: "Submitted for review" }); },
    onError:   (e: any) => toast({ title: "Submit failed", description: e?.message ?? String(e?.codes?.join(", ") ?? ""), variant: "destructive" }),
  });

  const approveM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}/approve`, { method: "POST", body: JSON.stringify(approveForm) }).then(async r => {
      const d = await r.json(); if (!r.ok) throw d; return d;
    }),
    onSuccess: (d) => { invalidate(); setSelected(d); setShowApprove(false); setApproveErrors({}); toast({ title: approveForm.approvalStatus === "Approved" ? "Assessment approved" : "Assessment rejected" }); },
    onError:   (e: any) => { const s = extractServerErrors(e); if (s && Object.keys(s).length) { setApproveErrors(s); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); } else toast({ title: "Approval failed", description: e?.message, variant: "destructive" }); },
  });

  const mitigateM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}/mitigate`, { method: "POST", body: JSON.stringify({
      mitigationActions:     mitigateForm.mitigationActions,
      assignedOwner:         mitigateForm.assignedOwner || null,
      targetRemediationDate: mitigateForm.targetRemediationDate ? new Date(mitigateForm.targetRemediationDate).toISOString() : null,
      evidenceReference:     mitigateForm.evidenceReference || null,
    })}).then(async r => { const d = await r.json(); if (!r.ok) throw d; return d; }),
    onSuccess: (d) => { invalidate(); setSelected(d); setShowMitigate(false); setMitigateErrors({}); toast({ title: "Mitigation updated" }); },
    onError:   (e: any) => { const s = extractServerErrors(e); if (s && Object.keys(s).length) { setMitigateErrors(s); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); } else toast({ title: "Mitigation failed", description: e?.message, variant: "destructive" }); },
  });

  const closeM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}/close`, { method: "POST", body: JSON.stringify(closeForm) }).then(async r => {
      const d = await r.json(); if (!r.ok) throw d; return d;
    }),
    onSuccess: (d) => { invalidate(); setSelected(d); setShowClose(false); setCloseErrors({}); toast({ title: "Assessment closed" }); },
    onError:   (e: any) => { const s = extractServerErrors(e); if (s && Object.keys(s).length) { setCloseErrors(s); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); } else toast({ title: "Close failed", description: e?.message, variant: "destructive" }); },
  });

  const deleteM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}`, { method: "DELETE" }).then(async r => {
      if (!r.ok) { const d = await r.json(); throw d; }
    }),
    onSuccess: () => { invalidate(); setShowDelete(false); setShowDetail(false); setSelected(null); toast({ title: "Assessment deleted" }); },
    onError:   (e: any) => toast({ title: "Unable to delete", description: e?.message, variant: "destructive" }),
  });

  const sum = summaryQ.data;
  const items = listQ.data?.items ?? [];
  const count = listQ.data?.count ?? 0;
  const overdue = overdueQ.data ?? [];

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base font-semibold">Risk Management Assessments</CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">§ 164.308(a)(1) — Conduct formal risk assessments, track findings, and drive remediation to closure.</p>
            </div>
            {canManage && (
              <Button size="sm" onClick={() => { setForm({ ...RA30_BLANK }); setShowCreate(true); }}>
                + New Assessment
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Summary metrics */}
          {sum && (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {[
                { label: "Total",         value: sum.total,          color: "" },
                { label: "Open",          value: sum.open,           color: sum.open > 0 ? "text-amber-600" : "" },
                { label: "High/Critical", value: sum.highCritical,   color: sum.highCritical > 0 ? "text-red-600" : "" },
                { label: "Overdue",       value: sum.overdue,        color: sum.overdue > 0 ? "text-red-700 font-bold" : "" },
              ].map(m => (
                <div key={m.label} className="rounded border p-3 text-center">
                  <div className={`text-2xl font-bold ${m.color}`}>{m.value}</div>
                  <div className="text-xs text-muted-foreground">{m.label}</div>
                </div>
              ))}
            </div>
          )}

          {/* Status chips */}
          {sum && (
            <div className="flex flex-wrap gap-2 text-xs">
              {[
                ["Draft",               sum.draft],
                ["In Progress",         sum.inProgress],
                ["In Review",           sum.inReview],
                ["Approved",            sum.approved],
                ["Remediation Req.",    sum.remReq],
                ["Closed",              sum.closed],
              ].map(([label, val]) => (
                <span key={label as string} className="rounded-full border px-2 py-0.5">
                  {label}: <strong>{val as number}</strong>
                </span>
              ))}
            </div>
          )}

          {/* Overdue alert */}
          {overdue.length > 0 && (
            <div className="rounded border border-red-200 bg-red-50 p-3">
              <p className="text-xs font-semibold text-red-700 mb-1">⚠ Overdue Remediation ({overdue.length})</p>
              <ul className="space-y-1">
                {overdue.map(a => (
                  <li key={a.id} className="text-xs text-red-600 flex justify-between">
                    <button className="underline hover:no-underline text-left" onClick={() => openDetail(a)}>{a.assessmentName}</button>
                    <span>{a.targetRemediationDate?.slice(0,10)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-wrap gap-2">
            <select className="border rounded px-2 py-1 text-xs" value={fStatus} onChange={e => { setFStatus(e.target.value); setPage(1); }}>
              <option value="">All statuses</option>
              {RA30_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select className="border rounded px-2 py-1 text-xs" value={fLevel} onChange={e => { setFLevel(e.target.value); setPage(1); }}>
              <option value="">All risk levels</option>
              {RA30_RISK_LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
            {(fStatus || fLevel) && (
              <Button size="sm" variant="ghost" className="text-xs h-7" onClick={() => { setFStatus(""); setFLevel(""); setPage(1); }}>Clear</Button>
            )}
          </div>

          {/* List */}
          {listQ.isLoading ? (
            <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-14 w-full" />)}</div>
          ) : items.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-4">No assessments found. {canManage && "Create one to get started."}</p>
          ) : (
            <div className="divide-y rounded border">
              {items.map(a => (
                <div key={a.id} className="flex items-start justify-between p-3 hover:bg-muted/30 cursor-pointer" onClick={() => openDetail(a)}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium truncate">{a.assessmentName}</span>
                      {a.assessmentReference && <span className="text-xs text-muted-foreground">{a.assessmentReference}</span>}
                      <Badge variant={ra30LevelBadge(a.riskLevel)} className="text-xs">{a.riskLevel}</Badge>
                      <Badge variant={ra30StatusBadge(a.status)} className="text-xs">{a.status}</Badge>
                      {a.remediationOverdue && <Badge variant="destructive" className="text-xs">Overdue</Badge>}
                    </div>
                    {a.coveredSystems && <p className="text-xs text-muted-foreground mt-0.5 truncate">{a.coveredSystems}</p>}
                    <p className="text-xs text-muted-foreground">Score: {a.riskScore} ({a.likelihood}L × {a.impact}I)</p>
                  </div>
                  <div className="text-xs text-muted-foreground shrink-0 ml-2 text-right">
                    {a.assignedOwner && <div>{a.assignedOwner}</div>}
                    {a.targetRemediationDate && <div>Due: {a.targetRemediationDate.slice(0,10)}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {count > 10 && (
            <div className="flex justify-between items-center text-xs">
              <span>{count} total</span>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" className="h-7 text-xs" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
                <span className="px-2 py-1">{page}</span>
                <Button size="sm" variant="outline" className="h-7 text-xs" disabled={page * 10 >= count} onClick={() => setPage(p => p + 1)}>Next</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Create dialog ─────────────────────────────────────────────────── */}
      <Dialog open={showCreate} onOpenChange={v => { setShowCreate(v); if (!v) setErrors({}); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Risk Assessment</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <label className="block text-xs font-medium mb-1">Assessment Name *</label>
              <input className={fieldCls("w-full border rounded px-2 py-1 text-sm", errors, "assessmentName")} aria-invalid={!!errors.assessmentName} value={form.assessmentName} onChange={e => { setF("assessmentName", e.target.value); clearErr(setErrors, "assessmentName"); }} placeholder="e.g. Annual HIPAA Risk Assessment 2026" />
              {fErr(errors, "assessmentName")}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Reference / Doc Number</label>
                <input className="w-full border rounded px-2 py-1 text-sm" value={form.assessmentReference} onChange={e => setF("assessmentReference", e.target.value)} placeholder="RA-2026-001" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Assessment Date</label>
                <input type="date" className="w-full border rounded px-2 py-1 text-sm" value={form.assessmentDate} onChange={e => setF("assessmentDate", e.target.value)} />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Assessment Scope</label>
              <textarea className="w-full border rounded px-2 py-1 text-sm" rows={2} value={form.assessmentScope} onChange={e => setF("assessmentScope", e.target.value)} placeholder="Describe the scope of this assessment..." />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Covered Systems</label>
                <input className="w-full border rounded px-2 py-1 text-sm" value={form.coveredSystems} onChange={e => setF("coveredSystems", e.target.value)} placeholder="EHR, Patient Portal, Billing..." />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">ePHI Systems</label>
                <input className="w-full border rounded px-2 py-1 text-sm" value={form.ephiSystems} onChange={e => setF("ephiSystems", e.target.value)} placeholder="Systems containing ePHI..." />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Threat Categories</label>
              <input className="w-full border rounded px-2 py-1 text-sm" value={form.threatCategories} onChange={e => setF("threatCategories", e.target.value)} placeholder="Technical, Administrative, Physical..." />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Vulnerabilities Identified</label>
              <textarea className="w-full border rounded px-2 py-1 text-sm" rows={2} value={form.vulnerabilitiesIdentified} onChange={e => setF("vulnerabilitiesIdentified", e.target.value)} placeholder="Unpatched OS, weak passwords..." />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Likelihood (1–5)</label>
                <input type="number" min={1} max={5} className="w-full border rounded px-2 py-1 text-sm" value={form.likelihood} onChange={e => setF("likelihood", e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Impact (1–5)</label>
                <input type="number" min={1} max={5} className="w-full border rounded px-2 py-1 text-sm" value={form.impact} onChange={e => setF("impact", e.target.value)} />
              </div>
            </div>
            <div className="flex gap-2 text-xs">
              <span>Risk Score: <strong>{iScore}</strong></span>
              <Badge variant={ra30LevelBadge(iLevel)}>{iLevel}</Badge>
              {(iLevel === "High" || iLevel === "Critical") && <span className="text-amber-600">⚠ Requires mitigation actions, owner & target date</span>}
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Mitigation Actions{(iLevel === "High" || iLevel === "Critical") && " *"}</label>
              <textarea className="w-full border rounded px-2 py-1 text-sm" rows={2} value={form.mitigationActions} onChange={e => setF("mitigationActions", e.target.value)} placeholder="Apply patches, enforce MFA, restrict access..." />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Assigned Owner{(iLevel === "High" || iLevel === "Critical") && " *"}</label>
                <input className="w-full border rounded px-2 py-1 text-sm" value={form.assignedOwner} onChange={e => setF("assignedOwner", e.target.value)} placeholder="Security Officer" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Target Remediation Date{(iLevel === "High" || iLevel === "Critical") && " *"}</label>
                <input type="date" className="w-full border rounded px-2 py-1 text-sm" value={form.targetRemediationDate} onChange={e => setF("targetRemediationDate", e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Evidence Reference</label>
                <input className="w-full border rounded px-2 py-1 text-sm" value={form.evidenceReference} onChange={e => setF("evidenceReference", e.target.value)} placeholder="evidence://risk-assessment-2026.pdf" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Next Review Date</label>
                <input type="date" className="w-full border rounded px-2 py-1 text-sm" value={form.nextReviewDate} onChange={e => setF("nextReviewDate", e.target.value)} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreate(false); setErrors({}); }}>Cancel</Button>
            <Button onClick={() => {
              const errs: Record<string, string> = {};
              required(errs, "assessmentName", form.assessmentName, "Assessment Name");
              if (Object.keys(errs).length) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; }
              setErrors({}); createM.mutate();
            }} disabled={createM.isPending}>
              {createM.isPending ? "Saving…" : "Create Assessment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Detail dialog ─────────────────────────────────────────────────── */}
      {selected && showDetail && (
        <Dialog open={showDetail} onOpenChange={v => { setShowDetail(v); if (!v) setSelected(null); }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 flex-wrap">
                {selected.assessmentName}
                <Badge variant={ra30LevelBadge(selected.riskLevel)}>{selected.riskLevel}</Badge>
                <Badge variant={ra30StatusBadge(selected.status)}>{selected.status}</Badge>
                {selected.remediationOverdue && <Badge variant="destructive">Overdue</Badge>}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded border p-2"><div className="text-lg font-bold">{selected.riskScore}</div><div className="text-xs text-muted-foreground">Risk Score</div></div>
                <div className="rounded border p-2"><div className="text-lg font-bold">{selected.likelihood}</div><div className="text-xs text-muted-foreground">Likelihood</div></div>
                <div className="rounded border p-2"><div className="text-lg font-bold">{selected.impact}</div><div className="text-xs text-muted-foreground">Impact</div></div>
              </div>
              {selected.assessmentReference && <p className="text-xs text-muted-foreground">Ref: {selected.assessmentReference}</p>}
              {selected.assessmentScope && <div><p className="text-xs font-medium">Scope</p><p className="text-xs mt-0.5">{selected.assessmentScope}</p></div>}
              {selected.coveredSystems && <div><p className="text-xs font-medium">Covered Systems</p><p className="text-xs">{selected.coveredSystems}</p></div>}
              {selected.ephiSystems && <div><p className="text-xs font-medium">ePHI Systems</p><p className="text-xs">{selected.ephiSystems}</p></div>}
              {selected.threatCategories && <div><p className="text-xs font-medium">Threat Categories</p><p className="text-xs">{selected.threatCategories}</p></div>}
              {selected.vulnerabilitiesIdentified && <div><p className="text-xs font-medium">Vulnerabilities Identified</p><p className="text-xs">{selected.vulnerabilitiesIdentified}</p></div>}
              {selected.mitigationActions && <div><p className="text-xs font-medium">Mitigation Actions</p><p className="text-xs">{selected.mitigationActions}</p></div>}
              <div className="grid grid-cols-2 gap-2 text-xs">
                {selected.assignedOwner && <div><span className="font-medium">Owner: </span>{selected.assignedOwner}</div>}
                {selected.targetRemediationDate && <div><span className="font-medium">Target Date: </span>{selected.targetRemediationDate.slice(0,10)}</div>}
                {selected.reviewerName && <div><span className="font-medium">Reviewer: </span>{selected.reviewerName}{selected.reviewerTitle ? ` (${selected.reviewerTitle})` : ""}</div>}
                {selected.approvalStatus && <div><span className="font-medium">Approval: </span>{selected.approvalStatus}</div>}
                {selected.assessmentDate && <div><span className="font-medium">Assessment Date: </span>{selected.assessmentDate.slice(0,10)}</div>}
                {selected.nextReviewDate && <div><span className="font-medium">Next Review: </span>{selected.nextReviewDate.slice(0,10)}</div>}
              </div>
              {selected.reviewNotes && <div><p className="text-xs font-medium">Review Notes</p><p className="text-xs">{selected.reviewNotes}</p></div>}
              {selected.evidenceReference && <div><p className="text-xs font-medium">Evidence Reference</p><p className="text-xs font-mono">{selected.evidenceReference}</p></div>}
            </div>
            <LinkedSourceRow entityType="HipaaRiskAssessment" entityId={selected.id} canManage={canManage} />
            {canManage && (
              <DialogFooter className="flex flex-wrap gap-2 sm:justify-start">
                {selected.status !== "Closed" && selected.status !== "Approved" && (
                  <Button size="sm" variant="outline" onClick={() => { setShowDetail(false); openEdit(selected); }}>Edit</Button>
                )}
                {(selected.status === "Draft" || selected.status === "AssessmentInProgress") && (
                  <Button size="sm" onClick={() => setShowSubmit(true)}>Submit for Review</Button>
                )}
                {selected.status === "Review" && (
                  <Button size="sm" onClick={() => { setApproveForm({ reviewerName: "", reviewerTitle: "", reviewNotes: "", approvalStatus: "Approved" }); setShowApprove(true); }}>Approve / Reject</Button>
                )}
                {(selected.status === "Approved" || selected.status === "RemediationRequired") && (
                  <Button size="sm" variant="outline" onClick={() => { setMitigateForm({ mitigationActions: selected.mitigationActions ?? "", assignedOwner: selected.assignedOwner ?? "", targetRemediationDate: selected.targetRemediationDate?.slice(0,10) ?? "", evidenceReference: selected.evidenceReference ?? "" }); setShowMitigate(true); }}>Add Mitigation</Button>
                )}
                {selected.status !== "Closed" && (
                  <Button size="sm" variant="outline" onClick={() => { setCloseForm({ evidenceReference: selected.evidenceReference ?? "", notes: "" }); setShowClose(true); }}>Close</Button>
                )}
                {selected.status !== "Approved" && selected.status !== "Closed" && (
                  <Button size="sm" variant="destructive" onClick={() => setShowDelete(true)}>Delete</Button>
                )}
              </DialogFooter>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* ── Edit dialog (reuses create form) ─────────────────────────────── */}
      {selected && showEdit && (
        <Dialog open={showEdit} onOpenChange={v => { setShowEdit(v); if (!v) setErrors({}); }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Edit Assessment</DialogTitle></DialogHeader>
            <div className="space-y-3 text-sm">
              <div>
                <label className="block text-xs font-medium mb-1">Assessment Name *</label>
                <input className={fieldCls("w-full border rounded px-2 py-1 text-sm", errors, "assessmentName")} aria-invalid={!!errors.assessmentName} value={form.assessmentName} onChange={e => { setF("assessmentName", e.target.value); clearErr(setErrors, "assessmentName"); }} />
                {fErr(errors, "assessmentName")}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">Reference</label>
                  <input className="w-full border rounded px-2 py-1 text-sm" value={form.assessmentReference} onChange={e => setF("assessmentReference", e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Assessment Date</label>
                  <input type="date" className="w-full border rounded px-2 py-1 text-sm" value={form.assessmentDate} onChange={e => setF("assessmentDate", e.target.value)} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Assessment Scope</label>
                <textarea className="w-full border rounded px-2 py-1 text-sm" rows={2} value={form.assessmentScope} onChange={e => setF("assessmentScope", e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">Covered Systems</label>
                  <input className="w-full border rounded px-2 py-1 text-sm" value={form.coveredSystems} onChange={e => setF("coveredSystems", e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">ePHI Systems</label>
                  <input className="w-full border rounded px-2 py-1 text-sm" value={form.ephiSystems} onChange={e => setF("ephiSystems", e.target.value)} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Threat Categories</label>
                <input className="w-full border rounded px-2 py-1 text-sm" value={form.threatCategories} onChange={e => setF("threatCategories", e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Vulnerabilities</label>
                <textarea className="w-full border rounded px-2 py-1 text-sm" rows={2} value={form.vulnerabilitiesIdentified} onChange={e => setF("vulnerabilitiesIdentified", e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs font-medium mb-1">Likelihood (1–5)</label><input type="number" min={1} max={5} className="w-full border rounded px-2 py-1 text-sm" value={form.likelihood} onChange={e => setF("likelihood", e.target.value)} /></div>
                <div><label className="block text-xs font-medium mb-1">Impact (1–5)</label><input type="number" min={1} max={5} className="w-full border rounded px-2 py-1 text-sm" value={form.impact} onChange={e => setF("impact", e.target.value)} /></div>
              </div>
              <div className="flex gap-2 text-xs"><span>Score: <strong>{iScore}</strong></span><Badge variant={ra30LevelBadge(iLevel)}>{iLevel}</Badge></div>
              <div>
                <label className="block text-xs font-medium mb-1">Mitigation Actions</label>
                <textarea className="w-full border rounded px-2 py-1 text-sm" rows={2} value={form.mitigationActions} onChange={e => setF("mitigationActions", e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs font-medium mb-1">Assigned Owner</label><input className="w-full border rounded px-2 py-1 text-sm" value={form.assignedOwner} onChange={e => setF("assignedOwner", e.target.value)} /></div>
                <div><label className="block text-xs font-medium mb-1">Target Remediation Date</label><input type="date" className="w-full border rounded px-2 py-1 text-sm" value={form.targetRemediationDate} onChange={e => setF("targetRemediationDate", e.target.value)} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs font-medium mb-1">Evidence Reference</label><input className="w-full border rounded px-2 py-1 text-sm" value={form.evidenceReference} onChange={e => setF("evidenceReference", e.target.value)} /></div>
                <div><label className="block text-xs font-medium mb-1">Next Review Date</label><input type="date" className="w-full border rounded px-2 py-1 text-sm" value={form.nextReviewDate} onChange={e => setF("nextReviewDate", e.target.value)} /></div>
              </div>
            </div>
            <LinkedSourceRow entityType="HipaaRiskAssessment" entityId={selected?.id ?? ""} canManage={canManage} />
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEdit(false)}>Cancel</Button>
              <Button onClick={() => { const errs: Record<string,string> = {}; required(errs, "assessmentName", form.assessmentName, "Assessment Name"); if (Object.keys(errs).length) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } setErrors({}); updateM.mutate(); }} disabled={updateM.isPending}>{updateM.isPending ? "Saving…" : "Save Changes"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Submit-for-review confirm ─────────────────────────────────────── */}
      {selected && showSubmit && (
        <Dialog open={showSubmit} onOpenChange={setShowSubmit}>
          <DialogContent>
            <DialogHeader><DialogTitle>Submit for Review</DialogTitle></DialogHeader>
            <p className="text-sm">Submit <strong>{selected.assessmentName}</strong> for management review and approval? The system will validate completeness before submitting.</p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSubmit(false)}>Cancel</Button>
              <Button onClick={() => submitM.mutate()} disabled={submitM.isPending}>{submitM.isPending ? "Submitting…" : "Submit"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Approve / Reject dialog ────────────────────────────────────────── */}
      {selected && showApprove && (
        <Dialog open={showApprove} onOpenChange={v => { setShowApprove(v); if (!v) setApproveErrors({}); }}>
          <DialogContent>
            <DialogHeader><DialogTitle>Approve / Reject Assessment</DialogTitle></DialogHeader>
            <div className="space-y-3 text-sm">
              <div>
                <label className="block text-xs font-medium mb-1">Decision *</label>
                <select className="w-full border rounded px-2 py-1 text-sm" value={approveForm.approvalStatus} onChange={e => setApproveForm(f => ({ ...f, approvalStatus: e.target.value }))}>
                  <option value="Approved">Approved</option>
                  <option value="Rejected">Rejected (return to Draft)</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Reviewer Name *</label>
                <input className={fieldCls("w-full border rounded px-2 py-1 text-sm", approveErrors, "reviewerName")} aria-invalid={!!approveErrors.reviewerName} value={approveForm.reviewerName} onChange={e => { setApproveForm(f => ({ ...f, reviewerName: e.target.value })); clearErr(setApproveErrors, "reviewerName"); }} placeholder="Full name" />
                {fErr(approveErrors, "reviewerName")}
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Title / Role</label>
                <input className="w-full border rounded px-2 py-1 text-sm" value={approveForm.reviewerTitle} onChange={e => setApproveForm(f => ({ ...f, reviewerTitle: e.target.value }))} placeholder="CISO, Security Officer..." />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Review Notes</label>
                <textarea className="w-full border rounded px-2 py-1 text-sm" rows={3} value={approveForm.reviewNotes} onChange={e => setApproveForm(f => ({ ...f, reviewNotes: e.target.value }))} placeholder="Notes on the decision..." />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowApprove(false)}>Cancel</Button>
              <Button onClick={() => { const errs: Record<string,string> = {}; required(errs, "reviewerName", approveForm.reviewerName, "Reviewer Name"); if (Object.keys(errs).length) { setApproveErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } setApproveErrors({}); approveM.mutate(); }} disabled={approveM.isPending}>{approveM.isPending ? "Saving…" : "Submit Decision"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Add Mitigation dialog ──────────────────────────────────────────── */}
      {selected && showMitigate && (
        <Dialog open={showMitigate} onOpenChange={v => { setShowMitigate(v); if (!v) setMitigateErrors({}); }}>
          <DialogContent>
            <DialogHeader><DialogTitle>Add / Update Mitigation</DialogTitle></DialogHeader>
            <div className="space-y-3 text-sm">
              <div>
                <label className="block text-xs font-medium mb-1">Mitigation Actions *</label>
                <textarea className={fieldCls("w-full border rounded px-2 py-1 text-sm", mitigateErrors, "mitigationActions")} aria-invalid={!!mitigateErrors.mitigationActions} rows={3} value={mitigateForm.mitigationActions} onChange={e => { setMitigateForm(f => ({ ...f, mitigationActions: e.target.value })); clearErr(setMitigateErrors, "mitigationActions"); }} />
                {fErr(mitigateErrors, "mitigationActions")}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">Assigned Owner</label>
                  <input className="w-full border rounded px-2 py-1 text-sm" value={mitigateForm.assignedOwner} onChange={e => setMitigateForm(f => ({ ...f, assignedOwner: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Target Date</label>
                  <input type="date" className="w-full border rounded px-2 py-1 text-sm" value={mitigateForm.targetRemediationDate} onChange={e => setMitigateForm(f => ({ ...f, targetRemediationDate: e.target.value }))} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Evidence Reference</label>
                <input className="w-full border rounded px-2 py-1 text-sm" value={mitigateForm.evidenceReference} onChange={e => setMitigateForm(f => ({ ...f, evidenceReference: e.target.value }))} placeholder="Document reference / URL" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowMitigate(false)}>Cancel</Button>
              <Button onClick={() => { const errs: Record<string,string> = {}; required(errs, "mitigationActions", mitigateForm.mitigationActions, "Mitigation Actions"); if (Object.keys(errs).length) { setMitigateErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } setMitigateErrors({}); mitigateM.mutate(); }} disabled={mitigateM.isPending}>{mitigateM.isPending ? "Saving…" : "Save Mitigation"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Close dialog ──────────────────────────────────────────────────── */}
      {selected && showClose && (
        <Dialog open={showClose} onOpenChange={v => { setShowClose(v); if (!v) setCloseErrors({}); }}>
          <DialogContent>
            <DialogHeader><DialogTitle>Close Assessment</DialogTitle></DialogHeader>
            <div className="space-y-3 text-sm">
              <p className="text-xs text-muted-foreground">Closing marks this assessment as complete. An evidence reference is required.</p>
              <div>
                <label className="block text-xs font-medium mb-1">Evidence Reference *</label>
                <input className={fieldCls("w-full border rounded px-2 py-1 text-sm", closeErrors, "evidenceReference")} aria-invalid={!!closeErrors.evidenceReference} value={closeForm.evidenceReference} onChange={e => { setCloseForm(f => ({ ...f, evidenceReference: e.target.value })); clearErr(setCloseErrors, "evidenceReference"); }} placeholder="closure-report-2026.pdf" />
                {fErr(closeErrors, "evidenceReference")}
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Closure Notes</label>
                <textarea className="w-full border rounded px-2 py-1 text-sm" rows={2} value={closeForm.notes} onChange={e => setCloseForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowClose(false)}>Cancel</Button>
              <Button onClick={() => { const errs: Record<string,string> = {}; required(errs, "evidenceReference", closeForm.evidenceReference, "Evidence Reference"); if (Object.keys(errs).length) { setCloseErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } setCloseErrors({}); closeM.mutate(); }} disabled={closeM.isPending}>{closeM.isPending ? "Closing…" : "Close Assessment"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Delete confirm ────────────────────────────────────────────────── */}
      {selected && showDelete && (
        <Dialog open={showDelete} onOpenChange={setShowDelete}>
          <DialogContent>
            <DialogHeader><DialogTitle>Delete Assessment</DialogTitle></DialogHeader>
            <p className="text-sm">Permanently delete <strong>{selected.assessmentName}</strong>? This cannot be undone.</p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDelete(false)}>Cancel</Button>
              <Button variant="destructive" onClick={() => deleteM.mutate()} disabled={deleteM.isPending}>{deleteM.isPending ? "Deleting…" : "Delete"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// HIPAA-29 — Key Management
// ══════════════════════════════════════════════════════════════════════════════

interface KeyMgmtSummary29 {
  total: number; compliant: number; nonCompliant: number; pendingReview: number;
  hardcoded: number; rotationOverdue: number; reviewOverdue: number;
  missingPolicy: number; issues: number; compliancePercentage: number;
}
interface KeyMgmtControl29 {
  id: string; organizationId: string;
  systemName: string; serviceContext?: string; description?: string;
  keyType?: string; encryptionAlgorithm?: string; keyReference?: string;
  storageProvider: string; secretManagementMethod: string;
  isHardcoded: boolean;
  accessRestrictions?: string; accessControlled: boolean; auditLoggingEnabled: boolean;
  rotationPolicy?: string; rotationIntervalDays?: number;
  lastRotationDate?: string; nextRotationDate?: string;
  complianceStatus: string; riskLevel?: string;
  lastReviewDate?: string; nextReviewDate?: string; reviewerName?: string; reviewNotes?: string;
  evidenceReference?: string;
  remediationRequired: boolean; remediationStatus?: string; remediationNotes?: string;
  createdAt: string; updatedAt: string;
  rotationOverdue: boolean; reviewOverdue: boolean; missingPolicy: boolean; missingEvidence: boolean;
  daysUntilRotation?: number;
}

function useAuth29() {
  return (url: string, opts: RequestInit = {}) => {
    const token = sessionStorage.getItem("dotnetbot.jwt.session") || localStorage.getItem("dotnetbot.jwt") || "";
    return fetch(url, { ...opts, headers: { ...(opts.headers as Record<string,string> || {}), "Content-Type": "application/json", Authorization: `Bearer ${token}` } });
  };
}

function km29StatusVariant(s: string): "default"|"secondary"|"outline"|"destructive" {
  if (s === "Compliant")     return "default";
  if (s === "PendingReview") return "outline";
  if (s === "NonCompliant")  return "destructive";
  if (s === "Disabled")      return "secondary";
  return "outline";
}

const KM29_KEY_TYPES        = ["Symmetric","Asymmetric","HMAC","API Key","Certificate","Secret","Other"];
const KM29_ALGORITHMS       = ["AES-256","AES-128","RSA-4096","RSA-2048","ECDSA-P256","ChaCha20","HMAC-SHA256","Other"];
const KM29_PROVIDERS        = ["AWS KMS","Azure Key Vault","HashiCorp Vault","GCP KMS","Local HSM","Environment Variable","Application Config","Hardcoded","Other"];
const KM29_SECRET_METHODS   = ["Key Vault","HSM","Environment Variable","Config File","Hardcoded","Other"];
const KM29_ROTATION_POLICIES = ["Automatic","Manual","Never","Disabled"];
const KM29_RISK_LEVELS      = ["Low","Medium","High","Critical"];
const KM29_STATUSES         = ["Compliant","PendingReview","NonCompliant","Disabled"];
const KM29_SERVICE_CONTEXTS = ["Database","API","Application","Infrastructure","Authentication","Integration","Other"];

function HipaaKeyManagementSection({ canManage }: { canManage: boolean }) {
  const authFetch = useAuth29();
  const qc = useQueryClient();
  const { toast } = useToast();
  const BASE = "/api/organization/compliance/hipaa/key-management";

  // ── summary & issues ────────────────────────────────────────────────────────
  const summaryQ = useQuery<KeyMgmtSummary29>({
    queryKey: ["hipaa29-summary"],
    queryFn: () => authFetch(`${BASE}/summary`).then(r => r.json()),
  });
  const issuesQ = useQuery<KeyMgmtControl29[]>({
    queryKey: ["hipaa29-issues"],
    queryFn: () => authFetch(`${BASE}/issues`).then(r => r.json()),
  });

  // ── list filters ────────────────────────────────────────────────────────────
  const [fStatus,   setFStatus]   = useState("");
  const [fKeyType,  setFKeyType]  = useState("");
  const [fProvider, setFProvider] = useState("");
  const [fRisk,     setFRisk]     = useState("");
  const [page, setPage]           = useState(1);

  const listQ = useQuery<{ count: number; items: KeyMgmtControl29[] }>({
    queryKey: ["hipaa29-list", fStatus, fKeyType, fProvider, fRisk, page],
    queryFn: () => {
      const p = new URLSearchParams({ page: String(page), limit: "10" });
      if (fStatus)   p.set("status", fStatus);
      if (fKeyType)  p.set("keyType", fKeyType);
      if (fProvider) p.set("storageProvider", fProvider);
      if (fRisk)     p.set("riskLevel", fRisk);
      return authFetch(`${BASE}?${p}`).then(r => r.json());
    },
  });

  // ── dialogs ──────────────────────────────────────────────────────────────────
  const [showCreate,   setShowCreate]   = useState(false);
  const [showDetail,   setShowDetail]   = useState(false);
  const [showEdit,     setShowEdit]     = useState(false);
  const [showValidate, setShowValidate] = useState(false);
  const [showRotate,   setShowRotate]   = useState(false);
  const [showReview,   setShowReview]   = useState(false);
  const [showDelete,   setShowDelete]   = useState(false);
  const [selected,     setSelected]     = useState<KeyMgmtControl29|null>(null);

  // ── form state (create / edit) ───────────────────────────────────────────────
  const blankForm = () => ({
    systemName: "", serviceContext: "", description: "",
    keyType: "", encryptionAlgorithm: "", keyReference: "",
    storageProvider: "AWS KMS", secretManagementMethod: "Key Vault",
    isHardcoded: false, accessRestrictions: "", accessControlled: true, auditLoggingEnabled: true,
    rotationPolicy: "Automatic", rotationIntervalDays: 90,
    lastRotationDate: "", nextRotationDate: "",
    complianceStatus: "PendingReview", riskLevel: "",
    nextReviewDate: "", evidenceReference: "",
    remediationRequired: false, remediationStatus: "", remediationNotes: "",
  });
  const [form, setForm] = useState(blankForm());
  const setF = (k: string, v: unknown) => setForm(f => ({ ...f, [k]: v }));
  const [errors, setErrors] = useState<Record<string,string>>({});

  // ── action form state ────────────────────────────────────────────────────────
  const [validateNotes, setValidateNotes]   = useState("");
  const [rotateNotes,   setRotateNotes]     = useState("");
  const [rotateNext,    setRotateNext]      = useState("");
  const [reviewerName,  setReviewerName]    = useState("");
  const [reviewNotes,   setReviewNotes]     = useState("");
  const [reviewStatus,  setReviewStatus]    = useState("");
  const [reviewNext,    setReviewNext]      = useState("");

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["hipaa29-summary"] });
    qc.invalidateQueries({ queryKey: ["hipaa29-issues"] });
    qc.invalidateQueries({ queryKey: ["hipaa29-list"] });
  };

  // ── mutations ─────────────────────────────────────────────────────────────────
  const createM = useMutation({
    mutationFn: (body: object) => authFetch(BASE, { method: "POST", body: JSON.stringify(body) }).then(r => r.json()),
    onSuccess: () => { invalidate(); setShowCreate(false); setErrors({}); setForm(blankForm()); },
    onError: (e: any) => { const s = extractServerErrors(e); if (s && Object.keys(s).length) { setErrors(s); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); } else toast({ title: "Unable to save", description: (e as Error)?.message, variant: "destructive" }); },
  });
  const updateM = useMutation({
    mutationFn: (body: object) => authFetch(`${BASE}/${selected?.id}`, { method: "PUT", body: JSON.stringify(body) }).then(r => r.json()),
    onSuccess: (d) => { invalidate(); setSelected(d); setShowEdit(false); setErrors({}); },
  });
  const validateM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}/validate`, { method: "PATCH", body: JSON.stringify({ notes: validateNotes }) }).then(r => r.json()),
    onSuccess: (d) => { invalidate(); setSelected(d.entity); setShowValidate(false); setValidateNotes(""); },
  });
  const rotateM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}/rotate`, { method: "PATCH", body: JSON.stringify({ notes: rotateNotes, nextRotationDate: rotateNext || null }) }).then(r => r.json()),
    onSuccess: (d) => { invalidate(); setSelected(d); setShowRotate(false); setRotateNotes(""); setRotateNext(""); },
  });
  const reviewM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}/review`, { method: "PATCH", body: JSON.stringify({ reviewerName, reviewNotes, complianceStatus: reviewStatus || undefined, nextReviewDate: reviewNext || null }) }).then(r => r.json()),
    onSuccess: (d) => { invalidate(); setSelected(d); setShowReview(false); setReviewerName(""); setReviewNotes(""); setReviewStatus(""); setReviewNext(""); },
  });
  const deleteM = useMutation({
    mutationFn: () => authFetch(`${BASE}/${selected?.id}`, { method: "DELETE" }),
    onSuccess: () => { invalidate(); setShowDelete(false); setSelected(null); setShowDetail(false); },
  });

  const openDetail = (c: KeyMgmtControl29) => { setSelected(c); setShowDetail(true); };
  const openEdit   = (c: KeyMgmtControl29) => {
    setSelected(c);
    setForm({
      systemName: c.systemName, serviceContext: c.serviceContext ?? "", description: c.description ?? "",
      keyType: c.keyType ?? "", encryptionAlgorithm: c.encryptionAlgorithm ?? "", keyReference: c.keyReference ?? "",
      storageProvider: c.storageProvider, secretManagementMethod: c.secretManagementMethod,
      isHardcoded: c.isHardcoded, accessRestrictions: c.accessRestrictions ?? "",
      accessControlled: c.accessControlled, auditLoggingEnabled: c.auditLoggingEnabled,
      rotationPolicy: c.rotationPolicy ?? "", rotationIntervalDays: c.rotationIntervalDays ?? 90,
      lastRotationDate: c.lastRotationDate?.slice(0,10) ?? "", nextRotationDate: c.nextRotationDate?.slice(0,10) ?? "",
      complianceStatus: c.complianceStatus, riskLevel: c.riskLevel ?? "",
      nextReviewDate: c.nextReviewDate?.slice(0,10) ?? "", evidenceReference: c.evidenceReference ?? "",
      remediationRequired: c.remediationRequired, remediationStatus: c.remediationStatus ?? "", remediationNotes: c.remediationNotes ?? "",
    });
    setShowEdit(true);
  };

  const summary = summaryQ.data;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base font-semibold">Key Management</CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">§ 164.312 — Secure encryption keys and secrets. Keys are not hardcoded and are securely managed.</p>
            </div>
            {canManage && (
              <Button size="sm" onClick={() => { setForm(blankForm()); setShowCreate(true); }}>
                + Add Control
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Summary metrics */}
          {summary && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="rounded-lg border p-3 text-center">
                <div className="text-2xl font-bold">{summary.total}</div>
                <div className="text-xs text-muted-foreground">Total Controls</div>
              </div>
              <div className="rounded-lg border p-3 text-center">
                <div className="text-2xl font-bold text-green-600">{summary.compliant}</div>
                <div className="text-xs text-muted-foreground">Compliant</div>
              </div>
              <div className="rounded-lg border p-3 text-center">
                <div className={`text-2xl font-bold ${summary.nonCompliant > 0 ? "text-red-600" : ""}`}>{summary.nonCompliant}</div>
                <div className="text-xs text-muted-foreground">Non-Compliant</div>
              </div>
              <div className="rounded-lg border p-3 text-center">
                <div className={`text-2xl font-bold ${summary.hardcoded > 0 ? "text-red-600" : ""}`}>{summary.hardcoded}</div>
                <div className="text-xs text-muted-foreground">Hardcoded Keys</div>
              </div>
              <div className="rounded-lg border p-3 text-center">
                <div className={`text-2xl font-bold ${summary.rotationOverdue > 0 ? "text-amber-600" : ""}`}>{summary.rotationOverdue}</div>
                <div className="text-xs text-muted-foreground">Rotation Overdue</div>
              </div>
              <div className="rounded-lg border p-3 text-center">
                <div className={`text-2xl font-bold ${summary.missingPolicy > 0 ? "text-amber-600" : ""}`}>{summary.missingPolicy}</div>
                <div className="text-xs text-muted-foreground">Missing Policy</div>
              </div>
              <div className="rounded-lg border p-3 text-center">
                <div className={`text-2xl font-bold ${summary.reviewOverdue > 0 ? "text-amber-600" : ""}`}>{summary.reviewOverdue}</div>
                <div className="text-xs text-muted-foreground">Review Overdue</div>
              </div>
              <div className="rounded-lg border p-3 text-center">
                <div className={`text-2xl font-bold ${summary.compliancePercentage < 80 ? "text-red-600" : summary.compliancePercentage < 100 ? "text-amber-600" : "text-green-600"}`}>{summary.compliancePercentage}%</div>
                <div className="text-xs text-muted-foreground">Compliance Rate</div>
              </div>
            </div>
          )}

          {/* Issues */}
          {issuesQ.data && issuesQ.data.length > 0 && (
            <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-900 p-3 space-y-1">
              <p className="text-xs font-medium text-red-700 dark:text-red-400">Compliance Issues ({issuesQ.data.length})</p>
              {issuesQ.data.slice(0, 5).map(c => (
                <div key={c.id} className="flex items-center gap-2 text-xs text-red-600 dark:text-red-400">
                  <span>•</span>
                  <span className="font-medium">{c.systemName}</span>
                  {c.isHardcoded        && <Badge variant="destructive" className="text-[10px] py-0 h-4">Hardcoded</Badge>}
                  {c.rotationOverdue    && <Badge variant="outline"     className="text-[10px] py-0 h-4 border-amber-400 text-amber-600">Rotation Overdue</Badge>}
                  {c.missingPolicy      && <Badge variant="outline"     className="text-[10px] py-0 h-4">No Policy</Badge>}
                  {c.missingEvidence    && <Badge variant="outline"     className="text-[10px] py-0 h-4">No Evidence</Badge>}
                </div>
              ))}
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-wrap gap-2">
            <select className="text-xs border rounded px-2 py-1 bg-background" value={fStatus} onChange={e => { setFStatus(e.target.value); setPage(1); }}>
              <option value="">All Statuses</option>
              {KM29_STATUSES.map(s => <option key={s}>{s}</option>)}
            </select>
            <select className="text-xs border rounded px-2 py-1 bg-background" value={fKeyType} onChange={e => { setFKeyType(e.target.value); setPage(1); }}>
              <option value="">All Key Types</option>
              {KM29_KEY_TYPES.map(s => <option key={s}>{s}</option>)}
            </select>
            <select className="text-xs border rounded px-2 py-1 bg-background" value={fProvider} onChange={e => { setFProvider(e.target.value); setPage(1); }}>
              <option value="">All Providers</option>
              {KM29_PROVIDERS.map(s => <option key={s}>{s}</option>)}
            </select>
            <select className="text-xs border rounded px-2 py-1 bg-background" value={fRisk} onChange={e => { setFRisk(e.target.value); setPage(1); }}>
              <option value="">All Risk Levels</option>
              {KM29_RISK_LEVELS.map(s => <option key={s}>{s}</option>)}
            </select>
            {(fStatus || fKeyType || fProvider || fRisk) && (
              <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => { setFStatus(""); setFKeyType(""); setFProvider(""); setFRisk(""); setPage(1); }}>Clear</Button>
            )}
          </div>

          {/* List */}
          {listQ.isLoading ? (
            <p className="text-xs text-muted-foreground">Loading…</p>
          ) : listQ.data?.items.length === 0 ? (
            <p className="text-xs text-muted-foreground py-4 text-center">No key management controls found. Add one to begin tracking.</p>
          ) : (
            <div className="space-y-2">
              {listQ.data?.items.map(c => (
                <div key={c.id} className="border rounded-lg p-3 hover:bg-muted/40 cursor-pointer" onClick={() => openDetail(c)}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm truncate">{c.systemName}</span>
                        {c.isHardcoded     && <Badge variant="destructive" className="text-[10px] py-0 h-4">Hardcoded</Badge>}
                        {c.rotationOverdue && <Badge variant="outline"     className="text-[10px] py-0 h-4 border-amber-400 text-amber-600">Rotation Overdue</Badge>}
                        {c.reviewOverdue   && <Badge variant="outline"     className="text-[10px] py-0 h-4 border-amber-400 text-amber-600">Review Due</Badge>}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
                        {c.keyType         && <span>{c.keyType}</span>}
                        {c.storageProvider && <span>• {c.storageProvider}</span>}
                        {c.rotationPolicy  && <span>• {c.rotationPolicy} rotation</span>}
                        {c.riskLevel       && <span>• {c.riskLevel} risk</span>}
                      </div>
                    </div>
                    <Badge variant={km29StatusVariant(c.complianceStatus)} className="shrink-0">{c.complianceStatus}</Badge>
                  </div>
                </div>
              ))}
              {/* Pagination */}
              {(listQ.data?.count ?? 0) > 10 && (
                <div className="flex items-center justify-between pt-1">
                  <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
                  <span className="text-xs text-muted-foreground">Page {page} · {listQ.data?.count} total</span>
                  <Button variant="outline" size="sm" disabled={(listQ.data?.count ?? 0) <= page * 10} onClick={() => setPage(p => p + 1)}>Next</Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Detail Dialog ─────────────────────────────────────────────────────── */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selected?.systemName}</DialogTitle>
            <DialogDescription>Key Management Control Detail</DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant={km29StatusVariant(selected.complianceStatus)}>{selected.complianceStatus}</Badge>
                {selected.isHardcoded     && <Badge variant="destructive">Hardcoded</Badge>}
                {selected.rotationOverdue && <Badge variant="outline" className="border-amber-400 text-amber-600">Rotation Overdue</Badge>}
                {selected.reviewOverdue   && <Badge variant="outline" className="border-amber-400 text-amber-600">Review Due</Badge>}
                {selected.missingEvidence && <Badge variant="outline" className="border-orange-400 text-orange-600">No Evidence</Badge>}
                {selected.riskLevel       && <Badge variant="secondary">{selected.riskLevel} Risk</Badge>}
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-muted-foreground">Service Context:</span> {selected.serviceContext || "—"}</div>
                <div><span className="text-muted-foreground">Key Type:</span> {selected.keyType || "—"}</div>
                <div><span className="text-muted-foreground">Algorithm:</span> {selected.encryptionAlgorithm || "—"}</div>
                <div><span className="text-muted-foreground">Storage Provider:</span> {selected.storageProvider}</div>
                <div><span className="text-muted-foreground">Secret Method:</span> {selected.secretManagementMethod}</div>
                <div><span className="text-muted-foreground">Key Reference:</span> {selected.keyReference || "—"}</div>
                <div><span className="text-muted-foreground">Rotation Policy:</span> {selected.rotationPolicy || "—"}</div>
                <div><span className="text-muted-foreground">Rotation Interval:</span> {selected.rotationIntervalDays ? `${selected.rotationIntervalDays} days` : "—"}</div>
                <div><span className="text-muted-foreground">Last Rotation:</span> {selected.lastRotationDate?.slice(0,10) || "—"}</div>
                <div><span className="text-muted-foreground">Next Rotation:</span> {selected.nextRotationDate?.slice(0,10) || "—"}</div>
                <div><span className="text-muted-foreground">Access Controlled:</span> {selected.accessControlled ? "Yes" : "No"}</div>
                <div><span className="text-muted-foreground">Audit Logging:</span> {selected.auditLoggingEnabled ? "Enabled" : "Disabled"}</div>
                <div><span className="text-muted-foreground">Last Review:</span> {selected.lastReviewDate?.slice(0,10) || "—"}</div>
                <div><span className="text-muted-foreground">Next Review:</span> {selected.nextReviewDate?.slice(0,10) || "—"}</div>
                <div><span className="text-muted-foreground">Reviewer:</span> {selected.reviewerName || "—"}</div>
                <div><span className="text-muted-foreground">Evidence:</span> {selected.evidenceReference || "—"}</div>
              </div>
              {selected.description      && <p className="text-xs"><span className="text-muted-foreground">Description:</span> {selected.description}</p>}
              {selected.accessRestrictions && <p className="text-xs"><span className="text-muted-foreground">Access Restrictions:</span> {selected.accessRestrictions}</p>}
              {selected.reviewNotes      && <p className="text-xs"><span className="text-muted-foreground">Review Notes:</span> {selected.reviewNotes}</p>}
              {selected.remediationRequired && (
                <div className="rounded border border-red-200 bg-red-50 dark:bg-red-950/20 p-2 text-xs space-y-1">
                  <p className="font-medium text-red-700 dark:text-red-400">Remediation Required</p>
                  {selected.remediationStatus && <p>Status: {selected.remediationStatus}</p>}
                  {selected.remediationNotes  && <p>{selected.remediationNotes}</p>}
                </div>
              )}
              {canManage && (
                <div className="flex gap-2 flex-wrap pt-2">
                  <Button size="sm" variant="outline" onClick={() => { setShowDetail(false); openEdit(selected); }}>Edit</Button>
                  <Button size="sm" variant="outline" onClick={() => { setShowDetail(false); setShowValidate(true); }}>Validate</Button>
                  <Button size="sm" variant="outline" onClick={() => { setShowDetail(false); setShowRotate(true); }}>Record Rotation</Button>
                  <Button size="sm" variant="outline" onClick={() => { setShowDetail(false); setShowReview(true); }}>Review</Button>
                  <Button size="sm" variant="destructive" onClick={() => { setShowDetail(false); setShowDelete(true); }}>Delete</Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Create / Edit Dialog ──────────────────────────────────────────────── */}
      <Dialog open={showCreate || showEdit} onOpenChange={v => { if (!v) { setShowCreate(false); setShowEdit(false); setErrors({}); } }}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{showCreate ? "Add Key Management Control" : "Edit Key Management Control"}</DialogTitle>
            <DialogDescription>Track encryption key and secret management metadata. Do not enter actual key values.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="space-y-1">
              <label className="text-xs font-medium">System / Service Name *</label>
              <input className={fieldCls("w-full border rounded px-2 py-1.5 text-sm bg-background", errors, "systemName")} aria-invalid={!!errors.systemName} value={form.systemName} onChange={e => { setF("systemName", e.target.value); clearErr(setErrors, "systemName"); }} placeholder="e.g. Patient Portal Database Key" />
              {fErr(errors, "systemName")}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-xs font-medium">Service Context</label>
                <select className="w-full border rounded px-2 py-1.5 text-sm bg-background" value={form.serviceContext} onChange={e => setF("serviceContext", e.target.value)}>
                  <option value="">— Select —</option>
                  {KM29_SERVICE_CONTEXTS.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium">Key Type</label>
                <select className="w-full border rounded px-2 py-1.5 text-sm bg-background" value={form.keyType} onChange={e => setF("keyType", e.target.value)}>
                  <option value="">— Select —</option>
                  {KM29_KEY_TYPES.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium">Encryption Algorithm</label>
                <select className="w-full border rounded px-2 py-1.5 text-sm bg-background" value={form.encryptionAlgorithm} onChange={e => setF("encryptionAlgorithm", e.target.value)}>
                  <option value="">— Select —</option>
                  {KM29_ALGORITHMS.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium">Risk Level</label>
                <select className="w-full border rounded px-2 py-1.5 text-sm bg-background" value={form.riskLevel} onChange={e => setF("riskLevel", e.target.value)}>
                  <option value="">— Select —</option>
                  {KM29_RISK_LEVELS.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium">Storage Provider</label>
                <select className="w-full border rounded px-2 py-1.5 text-sm bg-background" value={form.storageProvider} onChange={e => { setF("storageProvider", e.target.value); if (e.target.value === "Hardcoded") setF("isHardcoded", true); }}>
                  {KM29_PROVIDERS.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium">Secret Management Method</label>
                <select className="w-full border rounded px-2 py-1.5 text-sm bg-background" value={form.secretManagementMethod} onChange={e => { setF("secretManagementMethod", e.target.value); if (e.target.value === "Hardcoded") setF("isHardcoded", true); }}>
                  {KM29_SECRET_METHODS.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">Key Reference (ARN / path / alias — not the key value)</label>
              <input className="w-full border rounded px-2 py-1.5 text-sm bg-background" value={form.keyReference} onChange={e => setF("keyReference", e.target.value)} placeholder="e.g. arn:aws:kms:us-east-1:123:key/mrk-abc" />
            </div>
            {/* Rotation */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-xs font-medium">Rotation Policy</label>
                <select className="w-full border rounded px-2 py-1.5 text-sm bg-background" value={form.rotationPolicy} onChange={e => setF("rotationPolicy", e.target.value)}>
                  <option value="">— Select —</option>
                  {KM29_ROTATION_POLICIES.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium">Rotation Interval (days)</label>
                <input type="number" className="w-full border rounded px-2 py-1.5 text-sm bg-background" value={form.rotationIntervalDays} onChange={e => setF("rotationIntervalDays", Number(e.target.value))} placeholder="90" />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium">Last Rotation Date</label>
                <input type="date" className="w-full border rounded px-2 py-1.5 text-sm bg-background" value={form.lastRotationDate} onChange={e => setF("lastRotationDate", e.target.value)} />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium">Next Rotation Date</label>
                <input type="date" className="w-full border rounded px-2 py-1.5 text-sm bg-background" value={form.nextRotationDate} onChange={e => setF("nextRotationDate", e.target.value)} />
              </div>
            </div>
            {/* Flags */}
            <div className="flex gap-4 flex-wrap text-xs">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={form.isHardcoded} onChange={e => setF("isHardcoded", e.target.checked)} className="rounded" />
                Hardcoded (non-compliant)
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={form.accessControlled} onChange={e => setF("accessControlled", e.target.checked)} className="rounded" />
                Access Controlled
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={form.auditLoggingEnabled} onChange={e => setF("auditLoggingEnabled", e.target.checked)} className="rounded" />
                Audit Logging Enabled
              </label>
            </div>
            {/* Review & Evidence */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-xs font-medium">Next Review Date</label>
                <input type="date" className="w-full border rounded px-2 py-1.5 text-sm bg-background" value={form.nextReviewDate} onChange={e => setF("nextReviewDate", e.target.value)} />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium">Compliance Status</label>
                <select className="w-full border rounded px-2 py-1.5 text-sm bg-background" value={form.complianceStatus} onChange={e => setF("complianceStatus", e.target.value)}>
                  {KM29_STATUSES.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">Evidence Reference</label>
              <input className="w-full border rounded px-2 py-1.5 text-sm bg-background" value={form.evidenceReference} onChange={e => setF("evidenceReference", e.target.value)} placeholder="e.g. evidence://key-mgmt-audit-2026.pdf" />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">Access Restrictions</label>
              <textarea className="w-full border rounded px-2 py-1.5 text-sm bg-background" rows={2} value={form.accessRestrictions} onChange={e => setF("accessRestrictions", e.target.value)} placeholder="Describe who/what can access this key…" />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">Description</label>
              <textarea className="w-full border rounded px-2 py-1.5 text-sm bg-background" rows={2} value={form.description} onChange={e => setF("description", e.target.value)} placeholder="Additional context…" />
            </div>
            {form.isHardcoded && (
              <div className="rounded border border-red-200 bg-red-50 dark:bg-red-950/20 p-2 text-xs text-red-700 dark:text-red-400">
                ⚠ Hardcoded keys/secrets are automatically marked Non-Compliant and require remediation.
              </div>
            )}
          </div>
          <DialogFooter className="pt-2">
            <Button variant="outline" onClick={() => { setShowCreate(false); setShowEdit(false); }}>Cancel</Button>
            <Button
              disabled={createM.isPending || updateM.isPending}
              onClick={() => {
                const errs: Record<string,string> = {}; required(errs, "systemName", form.systemName, "System / Service Name"); if (Object.keys(errs).length) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } setErrors({});
                const body = {
                  systemName: form.systemName.trim(), serviceContext: form.serviceContext || null,
                  description: form.description || null, keyType: form.keyType || null,
                  encryptionAlgorithm: form.encryptionAlgorithm || null, keyReference: form.keyReference || null,
                  storageProvider: form.storageProvider, secretManagementMethod: form.secretManagementMethod,
                  isHardcoded: form.isHardcoded, accessRestrictions: form.accessRestrictions || null,
                  accessControlled: form.accessControlled, auditLoggingEnabled: form.auditLoggingEnabled,
                  rotationPolicy: form.rotationPolicy || null, rotationIntervalDays: form.rotationIntervalDays || null,
                  lastRotationDate: form.lastRotationDate || null, nextRotationDate: form.nextRotationDate || null,
                  complianceStatus: form.complianceStatus, riskLevel: form.riskLevel || null,
                  nextReviewDate: form.nextReviewDate || null, evidenceReference: form.evidenceReference || null,
                  remediationRequired: form.isHardcoded || form.remediationRequired, remediationStatus: form.remediationStatus || null, remediationNotes: form.remediationNotes || null,
                };
                showCreate ? createM.mutate(body) : updateM.mutate(body);
              }}
            >
              {(createM.isPending || updateM.isPending) ? "Saving…" : showCreate ? "Add Control" : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Validate Dialog ───────────────────────────────────────────────────── */}
      <Dialog open={showValidate} onOpenChange={v => { if (!v) setShowValidate(false); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Validate Compliance</DialogTitle>
            <DialogDescription>{selected?.systemName}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-xs text-muted-foreground">Checks for hardcoded secrets, missing rotation policy, overdue rotation, missing evidence, access controls, and audit logging.</p>
            <div className="space-y-1">
              <label className="text-xs font-medium">Notes</label>
              <textarea className="w-full border rounded px-2 py-1.5 text-sm bg-background" rows={3} value={validateNotes} onChange={e => setValidateNotes(e.target.value)} placeholder="Optional notes…" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowValidate(false)}>Cancel</Button>
            <Button disabled={validateM.isPending} onClick={() => validateM.mutate()}>
              {validateM.isPending ? "Validating…" : "Run Validation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Rotate Dialog ─────────────────────────────────────────────────────── */}
      <Dialog open={showRotate} onOpenChange={v => { if (!v) setShowRotate(false); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Record Key Rotation</DialogTitle>
            <DialogDescription>{selected?.systemName}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-xs text-muted-foreground">Records that this key was rotated today and sets the next rotation date.</p>
            <div className="space-y-1">
              <label className="text-xs font-medium">Next Rotation Date</label>
              <input type="date" className="w-full border rounded px-2 py-1.5 text-sm bg-background" value={rotateNext} onChange={e => setRotateNext(e.target.value)} />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">Notes</label>
              <textarea className="w-full border rounded px-2 py-1.5 text-sm bg-background" rows={2} value={rotateNotes} onChange={e => setRotateNotes(e.target.value)} placeholder="Optional rotation notes…" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRotate(false)}>Cancel</Button>
            <Button disabled={rotateM.isPending} onClick={() => rotateM.mutate()}>
              {rotateM.isPending ? "Recording…" : "Record Rotation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Review Dialog ─────────────────────────────────────────────────────── */}
      <Dialog open={showReview} onOpenChange={v => { if (!v) setShowReview(false); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Review Key Management Control</DialogTitle>
            <DialogDescription>{selected?.systemName}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="space-y-1">
              <label className="text-xs font-medium">Reviewer Name *</label>
              <input className="w-full border rounded px-2 py-1.5 text-sm bg-background" value={reviewerName} onChange={e => setReviewerName(e.target.value)} placeholder="e.g. CISO" />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">Compliance Status</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm bg-background" value={reviewStatus} onChange={e => setReviewStatus(e.target.value)}>
                <option value="">Keep current</option>
                {KM29_STATUSES.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">Next Review Date</label>
              <input type="date" className="w-full border rounded px-2 py-1.5 text-sm bg-background" value={reviewNext} onChange={e => setReviewNext(e.target.value)} />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">Review Notes</label>
              <textarea className="w-full border rounded px-2 py-1.5 text-sm bg-background" rows={2} value={reviewNotes} onChange={e => setReviewNotes(e.target.value)} placeholder="Summary of review…" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReview(false)}>Cancel</Button>
            <Button disabled={reviewM.isPending || !reviewerName.trim()} onClick={() => reviewM.mutate()}>
              {reviewM.isPending ? "Saving…" : "Complete Review"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete Dialog ─────────────────────────────────────────────────────── */}
      <Dialog open={showDelete} onOpenChange={v => { if (!v) setShowDelete(false); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Key Management Control</DialogTitle>
            <DialogDescription>This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <p className="text-sm">Delete <strong>{selected?.systemName}</strong>?</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDelete(false)}>Cancel</Button>
            <Button variant="destructive" disabled={deleteM.isPending} onClick={() => deleteM.mutate()}>
              {deleteM.isPending ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// HIPAA-26 — Encryption During Transmission (§ 164.312(e)(2)(ii))
// ══════════════════════════════════════════════════════════════════════════════

interface TransmissionSummary26 {
  total: number; phiSystems: number; encEnabled: number; nonCompliant: number;
  insecureCnt: number; expiredCerts: number; pendingReviews: number;
  highRisk: number; issues: number; compliancePercentage: number;
}
interface TransmissionControl26 {
  id: string; organizationId: string;
  systemName: string; systemType?: string; integrationType?: string; dataTransferType?: string;
  containsPHI: boolean; transmissionEnabled: boolean;
  protocol?: string; tlsVersion?: string; encryptionCipher?: string;
  certificateReference?: string; certificateExpiryDate?: string; certificateIssuer?: string;
  mutualTLSRequired: boolean; mutualTLSEnabled: boolean;
  secureEndpointVerified: boolean; insecureProtocolBlocked: boolean; apiAuthenticationRequired: boolean;
  encryptionStatus: string; riskLevel?: string;
  securityReviewDate?: string; nextSecurityReviewDate?: string; reviewerName?: string; reviewNotes?: string;
  evidenceReference?: string;
  remediationRequired: boolean; remediationStatus?: string; remediationNotes?: string;
  createdAt: string; updatedAt: string;
  certificateExpired: boolean; certificateExpiringSoon: boolean;
  reviewOverdue: boolean; phiNotEncrypted: boolean; daysUntilCertExpiry?: number;
}

function useAuth26() {
  return (url: string, opts: RequestInit = {}) => {
    const token = sessionStorage.getItem("dotnetbot.jwt.session") || localStorage.getItem("dotnetbot.jwt") || "";
    return fetch(url, { ...opts, headers: { ...(opts.headers as Record<string,string> || {}), "Content-Type": "application/json", Authorization: `Bearer ${token}` } });
  };
}

function trans26StatusVariant(s: string): "default"|"secondary"|"outline"|"destructive" {
  if (s === "Compliant")     return "default";
  if (s === "PendingReview") return "outline";
  if (s === "NonCompliant")  return "destructive";
  if (s === "Disabled")      return "secondary";
  return "outline";
}

const TRANS_SYSTEM_TYPES   = ["API","Database Integration","External Vendor","Internal Service","Other"];
const TRANS_INT_TYPES      = ["REST API","GraphQL","SOAP","gRPC","File Transfer","Database","Other"];
const TRANS_PROTOCOLS      = ["HTTPS","TLS","SFTP","VPN","Other"];
const TRANS_TLS_VERSIONS   = ["TLS 1.3","TLS 1.2","TLS 1.1 (deprecated)","TLS 1.0 (deprecated)","Other"];
const TRANS_RISK_LEVELS    = ["Low","Medium","High","Critical"];
const TRANS_STATUSES       = ["Compliant","PendingReview","NonCompliant","Disabled"];
const TRANS_TRANSFER_TYPES = ["Inbound","Outbound","Bidirectional","Internal"];

function HipaaTransmissionEncryptionSection({ canManage }: { canManage: boolean }) {
  const { toast } = useToast();
  const authFetch = useAuth26();
  const qc = useQueryClient();
  const BASE = "/api/organization/compliance/hipaa/transmission-encryption";

  const invalidate = (id?: string) => {
    qc.invalidateQueries({ queryKey: ["hipaa26-list"] });
    qc.invalidateQueries({ queryKey: ["hipaa26-summary"] });
    qc.invalidateQueries({ queryKey: ["hipaa26-issues"] });
    if (id) qc.invalidateQueries({ queryKey: ["hipaa26-detail", id] });
    else if (detailId) qc.invalidateQueries({ queryKey: ["hipaa26-detail", detailId] });
  };

  const summaryQ = useQuery<TransmissionSummary26>({
    queryKey: ["hipaa26-summary"],
    queryFn: async () => { const r = await authFetch(`${BASE}/summary`); if (!r.ok) throw new Error(); return r.json(); },
    staleTime: 0,
  });

  const issuesQ = useQuery<TransmissionControl26[]>({
    queryKey: ["hipaa26-issues"],
    queryFn: async () => { const r = await authFetch(`${BASE}/issues`); if (!r.ok) throw new Error(); return r.json(); },
    staleTime: 0,
  });

  const [page,        setPage]        = useState(1);
  const [statusF,     setStatusF]     = useState("");
  const [systemTypeF, setSystemTypeF] = useState("");
  const [protocolF,   setProtocolF]   = useState("");
  const [phiF,        setPhiF]        = useState("");

  const listQ = useQuery<{ count: number; items: TransmissionControl26[] }>({
    queryKey: ["hipaa26-list", page, statusF, systemTypeF, protocolF, phiF],
    queryFn: async () => {
      const p = new URLSearchParams({ page: String(page), limit: "15" });
      if (statusF)     p.set("status",     statusF);
      if (systemTypeF) p.set("systemType", systemTypeF);
      if (protocolF)   p.set("protocol",   protocolF);
      if (phiF !== "") p.set("containsPhi", phiF);
      const r = await authFetch(`${BASE}?${p}`);
      if (!r.ok) throw new Error(); return r.json();
    },
    staleTime: 0,
  });

  const [detailId, setDetailId] = useState<string | null>(null);
  const detailQ = useQuery<TransmissionControl26>({
    queryKey: ["hipaa26-detail", detailId],
    queryFn: async () => { const r = await authFetch(`${BASE}/${detailId}`); if (!r.ok) throw new Error(); return r.json(); },
    enabled: !!detailId,
    staleTime: 0,
  });

  // ── Create ────────────────────────────────────────────────────────────────
  const [showCreate, setShowCreate] = useState(false);
  const defaultForm = () => ({
    systemName: "", systemType: "API", integrationType: "REST API",
    dataTransferType: "Bidirectional", containsPHI: true, transmissionEnabled: true,
    protocol: "HTTPS", tlsVersion: "TLS 1.3", encryptionCipher: "AES-256-GCM",
    certificateReference: "", certificateIssuer: "", certificateExpiryDate: "",
    mutualTLSRequired: false, mutualTLSEnabled: false,
    secureEndpointVerified: true, insecureProtocolBlocked: true, apiAuthenticationRequired: true,
    encryptionStatus: "PendingReview", riskLevel: "Low",
    nextSecurityReviewDate: "", evidenceReference: "", remediationRequired: false,
  });
  const [form, setForm] = useState(defaultForm());
  const setF = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));
  const [errors, setErrors] = useState<Record<string,string>>({});

  const createMut = useMutation({
    mutationFn: async () => {
      const body: Record<string,any> = { ...form };
      if (!body.nextSecurityReviewDate) delete body.nextSecurityReviewDate;
      else body.nextSecurityReviewDate = new Date(body.nextSecurityReviewDate).toISOString();
      if (!body.certificateExpiryDate) delete body.certificateExpiryDate;
      else body.certificateExpiryDate = new Date(body.certificateExpiryDate).toISOString();
      if (!body.certificateReference) { delete body.certificateReference; delete body.certificateIssuer; delete body.certificateExpiryDate; }
      const r = await authFetch(BASE, { method: "POST", body: JSON.stringify(body) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.message || e.error || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: (data: TransmissionControl26) => {
      toast({ title: "Transmission control created.", description: data.systemName });
      setShowCreate(false); setForm(defaultForm()); setErrors({});
      setDetailId(data.id); invalidate(data.id);
    },
    onError: (e: any) => { const s = extractServerErrors(e); if (s && Object.keys(s).length) { setErrors(s); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); } else toast({ title: "Unable to save", description: (e as Error)?.message, variant: "destructive" }); },
  });

  // ── Validate ──────────────────────────────────────────────────────────────
  const validateMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/${detailId}/validate`, { method: "PATCH", body: JSON.stringify({ notes: "Manual validation." }) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.message || e.error || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: (data: { passed: boolean; issueCodes: string[] }) => {
      toast({
        title: data.passed ? "Validation passed ✓" : "Validation issues found",
        description: data.passed ? "All transmission security checks passed." : data.issueCodes.join(", "),
        variant: data.passed ? "default" : "destructive",
      });
      invalidate();
    },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  // ── Update Certificate ────────────────────────────────────────────────────
  const [showCert, setShowCert] = useState(false);
  const [certForm, setCertForm] = useState({ certificateReference: "", certificateExpiryDate: "", certificateIssuer: "", notes: "" });
  const certMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/${detailId}/update-certificate`, {
        method: "PATCH",
        body: JSON.stringify({ ...certForm, certificateExpiryDate: new Date(certForm.certificateExpiryDate).toISOString() }),
      });
      if (!r.ok) { const e = await r.json(); throw new Error(e.message || e.error || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Certificate updated." }); setShowCert(false); invalidate(); },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  // ── Review ────────────────────────────────────────────────────────────────
  const [showReview, setShowReview] = useState(false);
  const [reviewForm, setReviewForm] = useState({ reviewerName: "", reviewNotes: "", encryptionStatus: "Compliant", nextSecurityReviewDate: "" });
  const reviewMut = useMutation({
    mutationFn: async () => {
      const body: Record<string,any> = { ...reviewForm };
      if (!body.nextSecurityReviewDate) delete body.nextSecurityReviewDate;
      else body.nextSecurityReviewDate = new Date(body.nextSecurityReviewDate).toISOString();
      const r = await authFetch(`${BASE}/${detailId}/review`, { method: "PATCH", body: JSON.stringify(body) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.message || e.error || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Review completed." }); setShowReview(false); invalidate(); },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  // ── Delete ────────────────────────────────────────────────────────────────
  const deleteMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/${detailId}`, { method: "DELETE" });
      if (!r.ok) { const e = await r.json(); throw new Error(e.message || e.error || "Unable to complete action"); }
    },
    onSuccess: () => { toast({ title: "Transmission control deleted." }); setDetailId(null); invalidate(); },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  const s = summaryQ.data;
  const detail = detailQ.data;
  const [showIssues, setShowIssues] = useState(false);

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Encryption in Transit</CardTitle>
              <CardDescription>§ 164.312(e)(2)(ii) — TLS/HTTPS security, certificate management, and API transport controls for PHI in motion</CardDescription>
            </div>
            <div className="flex gap-2">
              {canManage && <Button size="sm" onClick={() => setShowCreate(true)}>+ Add System</Button>}
              <Button size="sm" variant="outline" onClick={() => setShowIssues(i => !i)}>
                {showIssues ? "Hide Issues" : `Issues${s?.issues ? ` (${s.issues})` : ""}`}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Dashboard */}
          {s && (
            <div className="grid grid-cols-4 gap-2 sm:grid-cols-4 lg:grid-cols-8 text-center">
              {[
                { label: "Total",       value: s.total },
                { label: "PHI Systems", value: s.phiSystems },
                { label: "Encrypted",   value: s.encEnabled,     ok: true },
                { label: "NonCompliant",value: s.nonCompliant,   warn: s.nonCompliant > 0 },
                { label: "Insecure",    value: s.insecureCnt,    warn: s.insecureCnt > 0 },
                { label: "Expired Cert",value: s.expiredCerts,   warn: s.expiredCerts > 0 },
                { label: "Pending Rev", value: s.pendingReviews, warn: s.pendingReviews > 0 },
                { label: "High Risk",   value: s.highRisk,       warn: s.highRisk > 0 },
              ].map(m => (
                <div key={m.label} className="rounded border p-2">
                  <div className={`text-xl font-bold ${m.warn ? "text-red-600" : (m as any).ok ? "text-green-600" : ""}`}>{m.value}</div>
                  <div className="text-xs text-muted-foreground">{m.label}</div>
                </div>
              ))}
            </div>
          )}
          {s && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>Compliance: <span className={`font-semibold ${s.compliancePercentage >= 80 ? "text-green-600" : "text-red-600"}`}>{s.compliancePercentage}%</span></span>
              {s.issues > 0 && <Badge variant="destructive" className="text-xs">{s.issues} issue{s.issues !== 1 ? "s" : ""}</Badge>}
              {s.expiredCerts > 0 && <Badge variant="outline" className="text-xs">{s.expiredCerts} expired cert{s.expiredCerts !== 1 ? "s" : ""}</Badge>}
            </div>
          )}

          {/* Issues panel */}
          {showIssues && (
            <div className="border rounded p-3 space-y-1 bg-red-50/50">
              <p className="text-xs font-semibold text-red-700">Compliance Issues</p>
              {issuesQ.isLoading ? <Skeleton className="h-8 w-full" /> :
               issuesQ.data?.length === 0 ? <p className="text-xs text-muted-foreground">No issues.</p> :
               issuesQ.data?.map(i => (
                <div key={i.id} className="flex items-center gap-2 text-xs cursor-pointer hover:underline" onClick={() => setDetailId(i.id)}>
                  <Badge variant="destructive" className="text-xs shrink-0">
                    {i.phiNotEncrypted ? "PHI_TRANSMISSION_NOT_ENCRYPTED" : i.certificateExpired ? "CERTIFICATE_EXPIRED" : "NON_COMPLIANT"}
                  </Badge>
                  <span>{i.systemName}</span>
                  {i.riskLevel && <span className="text-muted-foreground">· {i.riskLevel} risk</span>}
                </div>
              ))}
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-wrap gap-2">
            <select className="border rounded px-2 py-1 text-sm" value={statusF} onChange={e => { setStatusF(e.target.value); setPage(1); }}>
              <option value="">All Statuses</option>
              {TRANS_STATUSES.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
            <select className="border rounded px-2 py-1 text-sm" value={systemTypeF} onChange={e => { setSystemTypeF(e.target.value); setPage(1); }}>
              <option value="">All Types</option>
              {TRANS_SYSTEM_TYPES.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
            <select className="border rounded px-2 py-1 text-sm" value={protocolF} onChange={e => { setProtocolF(e.target.value); setPage(1); }}>
              <option value="">All Protocols</option>
              {TRANS_PROTOCOLS.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
            <select className="border rounded px-2 py-1 text-sm" value={phiF} onChange={e => { setPhiF(e.target.value); setPage(1); }}>
              <option value="">All Systems</option>
              <option value="true">PHI Only</option>
              <option value="false">Non-PHI Only</option>
            </select>
          </div>

          {/* Control list */}
          {listQ.isLoading ? <Skeleton className="h-24 w-full" /> : listQ.data?.items?.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No transmission controls found. Add a system to start tracking.</p>
          ) : (
            <div className="space-y-2">
              {listQ.data?.items?.map(c => (
                <div key={c.id}
                  className={`flex items-start justify-between rounded border p-3 cursor-pointer hover:bg-muted/50 transition-colors ${detailId === c.id ? "border-primary bg-muted/30" : ""}`}
                  onClick={() => setDetailId(c.id)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm truncate">{c.systemName}</span>
                      <Badge variant={trans26StatusVariant(c.encryptionStatus)} className="text-xs">{c.encryptionStatus}</Badge>
                      {c.containsPHI && <Badge variant="outline" className="text-xs bg-amber-50">PHI</Badge>}
                      {c.phiNotEncrypted && <Badge variant="destructive" className="text-xs">Not Encrypted!</Badge>}
                      {c.certificateExpired && <Badge variant="destructive" className="text-xs">Cert Expired</Badge>}
                      {c.certificateExpiringSoon && !c.certificateExpired && <Badge variant="outline" className="text-xs">Cert Expiring</Badge>}
                      {c.reviewOverdue && <Badge variant="outline" className="text-xs">Review Overdue</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {c.systemType ?? "—"} · {c.protocol ?? "No protocol"} · {c.tlsVersion ?? "—"} · Risk: {c.riskLevel ?? "—"}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {c.certificateReference ? `Cert: ${c.certificateExpiryDate ? `expires ${c.certificateExpiryDate.slice(0,10)}` : "no expiry"}` : "No certificate"}
                      {c.nextSecurityReviewDate ? ` · Next review: ${c.nextSecurityReviewDate.slice(0,10)}` : ""}
                    </div>
                  </div>
                </div>
              ))}
              {(listQ.data?.count ?? 0) > 15 && (
                <div className="flex justify-center gap-2 pt-2">
                  <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
                  <span className="text-sm self-center">Page {page}</span>
                  <Button size="sm" variant="outline" disabled={(listQ.data?.count ?? 0) <= page * 15} onClick={() => setPage(p => p + 1)}>Next</Button>
                </div>
              )}
            </div>
          )}

          {/* Detail panel */}
          {detail && detailId && (
            <div className="border rounded-lg p-4 space-y-3 bg-muted/20">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h4 className="font-semibold text-sm">{detail.systemName}</h4>
                <div className="flex gap-1 flex-wrap">
                  {canManage && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => validateMut.mutate()} disabled={validateMut.isPending}>
                        {validateMut.isPending ? "Validating…" : "Validate"}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => { setCertForm(f => ({ ...f, certificateReference: detail.certificateReference ?? "", certificateIssuer: detail.certificateIssuer ?? "" })); setShowCert(true); }}>
                        Update Cert
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => { setReviewForm(f => ({ ...f, encryptionStatus: detail.encryptionStatus })); setShowReview(true); }}>
                        Complete Review
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => deleteMut.mutate()} disabled={deleteMut.isPending}>Delete</Button>
                    </>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => setDetailId(null)}>✕</Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs md:grid-cols-3">
                <div><span className="text-muted-foreground">System Type:</span> {detail.systemType ?? "—"}</div>
                <div><span className="text-muted-foreground">Integration:</span> {detail.integrationType ?? "—"}</div>
                <div><span className="text-muted-foreground">Transfer Type:</span> {detail.dataTransferType ?? "—"}</div>
                <div><span className="text-muted-foreground">Contains PHI:</span> {detail.containsPHI ? "Yes" : "No"}</div>
                <div><span className="text-muted-foreground">Encryption:</span> {detail.transmissionEnabled ? "Enabled" : "Disabled"}</div>
                <div><span className="text-muted-foreground">Status:</span> <Badge variant={trans26StatusVariant(detail.encryptionStatus)} className="text-xs">{detail.encryptionStatus}</Badge></div>
                <div><span className="text-muted-foreground">Protocol:</span> {detail.protocol ?? "—"}</div>
                <div><span className="text-muted-foreground">TLS Version:</span> {detail.tlsVersion ?? "—"}</div>
                <div><span className="text-muted-foreground">Cipher:</span> {detail.encryptionCipher ?? "—"}</div>
                <div><span className="text-muted-foreground">Risk Level:</span> {detail.riskLevel ?? "—"}</div>
                <div><span className="text-muted-foreground">Insecure Blocked:</span> {detail.insecureProtocolBlocked ? "Yes" : "No"}</div>
                <div><span className="text-muted-foreground">Endpoint Verified:</span> {detail.secureEndpointVerified ? "Yes" : "No"}</div>
                <div><span className="text-muted-foreground">API Auth Required:</span> {detail.apiAuthenticationRequired ? "Yes" : "No"}</div>
                <div><span className="text-muted-foreground">mTLS Required:</span> {detail.mutualTLSRequired ? "Yes" : "No"}</div>
                <div><span className="text-muted-foreground">mTLS Enabled:</span> {detail.mutualTLSEnabled ? "Yes" : "No"}</div>
              </div>

              {/* Certificate block */}
              <div className="text-xs border-t pt-2 space-y-1">
                <p className="font-medium text-muted-foreground">Certificate</p>
                {detail.certificateReference ? (
                  <>
                    <div><span className="text-muted-foreground">Reference:</span> <code className="bg-muted px-1 rounded">{detail.certificateReference}</code></div>
                    {detail.certificateIssuer && <div><span className="text-muted-foreground">Issuer:</span> {detail.certificateIssuer}</div>}
                    {detail.certificateExpiryDate && (
                      <div className={`${detail.certificateExpired ? "text-red-600 font-semibold" : detail.certificateExpiringSoon ? "text-amber-700" : ""}`}>
                        <span className="text-muted-foreground">Expires:</span> {detail.certificateExpiryDate.slice(0,10)}
                        {detail.certificateExpired && " ⚠ EXPIRED"}
                        {detail.certificateExpiringSoon && !detail.certificateExpired && detail.daysUntilCertExpiry !== undefined && ` ⚠ expires in ${detail.daysUntilCertExpiry} days`}
                      </div>
                    )}
                  </>
                ) : <span className="text-muted-foreground">No certificate registered.</span>}
              </div>

              {detail.evidenceReference && (
                <div className="text-xs"><span className="text-muted-foreground">Evidence:</span> <code className="bg-muted px-1 rounded">{detail.evidenceReference}</code></div>
              )}
              {detail.reviewNotes && (
                <div className="text-xs border-t pt-2"><span className="text-muted-foreground">Review Notes:</span> {detail.reviewNotes}</div>
              )}
              {detail.remediationRequired && (
                <div className="text-xs text-red-600 font-medium border-t pt-2">
                  ⚠ Remediation required — Status: {detail.remediationStatus ?? "Open"}
                  {detail.remediationNotes && <span> — {detail.remediationNotes}</span>}
                </div>
              )}
              {detail.phiNotEncrypted && <div className="text-xs text-red-700 font-semibold">⚠ PHI_TRANSMISSION_NOT_ENCRYPTED — PHI transmitted without encryption.</div>}
              {detail.reviewOverdue && <div className="text-xs text-amber-700 font-medium">⚠ Security review is overdue.</div>}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={v => { setShowCreate(v); if (!v) setErrors({}); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Transmission Encryption Control</DialogTitle>
            <DialogDescription>Register a system and its in-transit encryption configuration.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <label className="text-xs font-medium">System Name *</label>
              <input className={fieldCls("w-full border rounded px-2 py-1 text-sm mt-1", errors, "systemName")} aria-invalid={!!errors.systemName} value={form.systemName} onChange={e => { setF("systemName", e.target.value); clearErr(setErrors, "systemName"); }} placeholder="e.g. Patient Portal API" />
              {fErr(errors, "systemName")}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium">System Type</label>
                <select className="w-full border rounded px-2 py-1 text-sm mt-1" value={form.systemType} onChange={e => setF("systemType", e.target.value)}>
                  {TRANS_SYSTEM_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium">Integration Type</label>
                <select className="w-full border rounded px-2 py-1 text-sm mt-1" value={form.integrationType} onChange={e => setF("integrationType", e.target.value)}>
                  {TRANS_INT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium">Data Transfer Type</label>
                <select className="w-full border rounded px-2 py-1 text-sm mt-1" value={form.dataTransferType} onChange={e => setF("dataTransferType", e.target.value)}>
                  {TRANS_TRANSFER_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium">Risk Level</label>
                <select className="w-full border rounded px-2 py-1 text-sm mt-1" value={form.riskLevel} onChange={e => setF("riskLevel", e.target.value)}>
                  {TRANS_RISK_LEVELS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              {[
                ["containsPHI",           "Contains PHI"],
                ["transmissionEnabled",   "Encryption Enabled"],
                ["insecureProtocolBlocked","Insecure Blocked"],
                ["secureEndpointVerified","Endpoint Verified"],
                ["apiAuthenticationRequired","API Auth Required"],
                ["mutualTLSRequired",     "mTLS Required"],
              ].map(([k, label]) => (
                <label key={k} className="flex items-center gap-1 text-xs cursor-pointer">
                  <input type="checkbox" checked={(form as any)[k]} onChange={e => setF(k, e.target.checked)} />
                  {label}
                </label>
              ))}
            </div>
            {form.transmissionEnabled && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-medium">Protocol</label>
                  <select className="w-full border rounded px-2 py-1 text-sm mt-1" value={form.protocol} onChange={e => setF("protocol", e.target.value)}>
                    {TRANS_PROTOCOLS.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium">TLS Version</label>
                  <select className="w-full border rounded px-2 py-1 text-sm mt-1" value={form.tlsVersion} onChange={e => setF("tlsVersion", e.target.value)}>
                    {TRANS_TLS_VERSIONS.map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="text-xs font-medium">Cipher Suite</label>
                  <input className="w-full border rounded px-2 py-1 text-sm mt-1" value={form.encryptionCipher} onChange={e => setF("encryptionCipher", e.target.value)} placeholder="AES-256-GCM" />
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium">Certificate Reference</label>
                <input className="w-full border rounded px-2 py-1 text-sm mt-1" value={form.certificateReference} onChange={e => setF("certificateReference", e.target.value)} placeholder="cert://portal-api.pem" />
              </div>
              <div>
                <label className="text-xs font-medium">Certificate Expiry</label>
                <input type="date" className="w-full border rounded px-2 py-1 text-sm mt-1" value={form.certificateExpiryDate} onChange={e => setF("certificateExpiryDate", e.target.value)} />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-medium">Certificate Issuer</label>
                <input className="w-full border rounded px-2 py-1 text-sm mt-1" value={form.certificateIssuer} onChange={e => setF("certificateIssuer", e.target.value)} placeholder="Let's Encrypt, DigiCert…" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium">Evidence Reference</label>
              <input className="w-full border rounded px-2 py-1 text-sm mt-1" value={form.evidenceReference} onChange={e => setF("evidenceReference", e.target.value)} placeholder="evidence://tls-scan-2026.json" />
            </div>
            <div>
              <label className="text-xs font-medium">Next Security Review Date</label>
              <input type="date" className="w-full border rounded px-2 py-1 text-sm mt-1" value={form.nextSecurityReviewDate} onChange={e => setF("nextSecurityReviewDate", e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={() => { const errs: Record<string,string> = {}; required(errs, "systemName", form.systemName, "System Name"); if (Object.keys(errs).length) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } setErrors({}); createMut.mutate(); }} disabled={createMut.isPending}>
              {createMut.isPending ? "Creating…" : "Add Control"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Certificate Dialog */}
      {showCert && (
        <Dialog open={showCert} onOpenChange={setShowCert}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Update Certificate</DialogTitle>
              <DialogDescription>Record the new TLS certificate details.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <div>
                <label className="text-xs font-medium">Certificate Reference *</label>
                <input className="w-full border rounded px-2 py-1 text-sm mt-1" value={certForm.certificateReference} onChange={e => setCertForm(f => ({ ...f, certificateReference: e.target.value }))} placeholder="cert://new-cert-2027.pem" />
              </div>
              <div>
                <label className="text-xs font-medium">Expiry Date *</label>
                <input type="date" className="w-full border rounded px-2 py-1 text-sm mt-1" value={certForm.certificateExpiryDate} onChange={e => setCertForm(f => ({ ...f, certificateExpiryDate: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs font-medium">Issuer</label>
                <input className="w-full border rounded px-2 py-1 text-sm mt-1" value={certForm.certificateIssuer} onChange={e => setCertForm(f => ({ ...f, certificateIssuer: e.target.value }))} placeholder="Let's Encrypt Authority X3" />
              </div>
              <div>
                <label className="text-xs font-medium">Notes</label>
                <textarea className="w-full border rounded px-2 py-1 text-sm mt-1" rows={2} value={certForm.notes} onChange={e => setCertForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCert(false)}>Cancel</Button>
              <Button onClick={() => certMut.mutate()} disabled={certMut.isPending || !certForm.certificateReference.trim() || !certForm.certificateExpiryDate}>
                {certMut.isPending ? "Updating…" : "Update Certificate"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Review Dialog */}
      {showReview && (
        <Dialog open={showReview} onOpenChange={setShowReview}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Complete Security Review</DialogTitle>
              <DialogDescription>Record the transmission security review outcome.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <div>
                <label className="text-xs font-medium">Reviewer Name *</label>
                <input className="w-full border rounded px-2 py-1 text-sm mt-1" value={reviewForm.reviewerName} onChange={e => setReviewForm(f => ({ ...f, reviewerName: e.target.value }))} placeholder="e.g. CISO" />
              </div>
              <div>
                <label className="text-xs font-medium">Outcome Status</label>
                <select className="w-full border rounded px-2 py-1 text-sm mt-1" value={reviewForm.encryptionStatus} onChange={e => setReviewForm(f => ({ ...f, encryptionStatus: e.target.value }))}>
                  {TRANS_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium">Next Review Date</label>
                <input type="date" className="w-full border rounded px-2 py-1 text-sm mt-1" value={reviewForm.nextSecurityReviewDate} onChange={e => setReviewForm(f => ({ ...f, nextSecurityReviewDate: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs font-medium">Review Notes</label>
                <textarea className="w-full border rounded px-2 py-1 text-sm mt-1" rows={3} value={reviewForm.reviewNotes} onChange={e => setReviewForm(f => ({ ...f, reviewNotes: e.target.value }))} placeholder="Findings and observations…" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowReview(false)}>Cancel</Button>
              <Button onClick={() => reviewMut.mutate()} disabled={reviewMut.isPending || !reviewForm.reviewerName.trim()}>
                {reviewMut.isPending ? "Saving…" : "Complete Review"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// HIPAA-25 — Encryption (§ 164.312(a)(2)(iv) / § 164.312(e)(2)(ii))
// ══════════════════════════════════════════════════════════════════════════════

interface EncryptionSummary25 {
  total: number; phiSystems: number; encEnabled: number; encMissing: number;
  nonCompliant: number; pendingReviews: number; rotOverdue: number;
  highRisk: number; issues: number; compliancePercentage: number;
}
interface EncryptionControl25 {
  id: string; organizationId: string;
  systemName: string; systemType?: string; storageType?: string; dataCategory?: string;
  containsPHI: boolean; encryptionAtRestEnabled: boolean;
  encryptionMethod?: string; encryptionAlgorithm?: string;
  keyManagementMethod?: string; keyProvider?: string;
  keyRotationEnabled: boolean; lastKeyRotationDate?: string; nextKeyRotationDate?: string;
  certificateReference?: string; configurationEvidenceReference?: string;
  encryptionStatus: string; riskLevel?: string;
  reviewDate?: string; nextReviewDate?: string; reviewerName?: string; reviewNotes?: string;
  remediationRequired: boolean; remediationStatus?: string; remediationNotes?: string;
  createdAt: string; updatedAt: string; createdBy?: string; updatedBy?: string;
  rotationOverdue: boolean; reviewOverdue: boolean; phiWithoutEncryption: boolean;
  daysUntilRotation?: number;
}

function useAuth25() {
  return (url: string, opts: RequestInit = {}) => {
    const token = sessionStorage.getItem("dotnetbot.jwt.session") || localStorage.getItem("dotnetbot.jwt") || "";
    return fetch(url, { ...opts, headers: { ...(opts.headers as Record<string,string> || {}), "Content-Type": "application/json", Authorization: `Bearer ${token}` } });
  };
}

function enc25StatusVariant(s: string): "default"|"secondary"|"outline"|"destructive" {
  if (s === "Configured")    return "default";
  if (s === "PendingReview") return "outline";
  if (s === "NonCompliant")  return "destructive";
  if (s === "Disabled")      return "secondary";
  return "outline";
}

const ENC_SYSTEM_TYPES   = ["Database","Application","File Server","Cloud Service","Endpoint","Network","Backup","Other"];
const ENC_STORAGE_TYPES  = ["Database","File Storage","Object Storage","Backup Storage","Other"];
const ENC_METHODS        = ["AES-256","AES-128","Provider Managed Encryption","Customer Managed Keys","Other"];
const ENC_KEY_MGMT       = ["Cloud KMS","Hardware Security Module","Internal Key Management","Other"];
const ENC_RISK_LEVELS    = ["Low","Medium","High","Critical"];
const ENC_STATUSES       = ["Configured","PendingReview","NonCompliant","Disabled"];

function HipaaEncryptionSection({ canManage }: { canManage: boolean }) {
  const { toast } = useToast();
  const authFetch = useAuth25();
  const qc = useQueryClient();
  const BASE = "/api/organization/compliance/hipaa/encryption";

  const invalidate = (id?: string) => {
    qc.invalidateQueries({ queryKey: ["hipaa25-list"] });
    qc.invalidateQueries({ queryKey: ["hipaa25-summary"] });
    qc.invalidateQueries({ queryKey: ["hipaa25-issues"] });
    if (id) qc.invalidateQueries({ queryKey: ["hipaa25-detail", id] });
    else if (detailId) qc.invalidateQueries({ queryKey: ["hipaa25-detail", detailId] });
  };

  // ── Summary ──────────────────────────────────────────────────────────────
  const summaryQ = useQuery<EncryptionSummary25>({
    queryKey: ["hipaa25-summary"],
    queryFn: async () => { const r = await authFetch(`${BASE}/summary`); if (!r.ok) throw new Error(); return r.json(); },
    staleTime: 0,
  });

  // ── List + filters ────────────────────────────────────────────────────────
  const [page,         setPage]         = useState(1);
  const [statusF,      setStatusF]      = useState("");
  const [systemTypeF,  setSystemTypeF]  = useState("");
  const [phiF,         setPhiF]         = useState("");
  const [riskF,        setRiskF]        = useState("");

  const listQ = useQuery<{ count: number; items: EncryptionControl25[] }>({
    queryKey: ["hipaa25-list", page, statusF, systemTypeF, phiF, riskF],
    queryFn: async () => {
      const p = new URLSearchParams({ page: String(page), limit: "15" });
      if (statusF)     p.set("status", statusF);
      if (systemTypeF) p.set("systemType", systemTypeF);
      if (riskF)       p.set("riskLevel", riskF);
      if (phiF !== "") p.set("containsPhi", phiF);
      const r = await authFetch(`${BASE}?${p}`);
      if (!r.ok) throw new Error(); return r.json();
    },
    staleTime: 0,
  });

  // ── Issues ────────────────────────────────────────────────────────────────
  const issuesQ = useQuery<EncryptionControl25[]>({
    queryKey: ["hipaa25-issues"],
    queryFn: async () => { const r = await authFetch(`${BASE}/issues`); if (!r.ok) throw new Error(); return r.json(); },
    staleTime: 0,
  });

  // ── Detail ────────────────────────────────────────────────────────────────
  const [detailId, setDetailId] = useState<string | null>(null);
  const detailQ = useQuery<EncryptionControl25>({
    queryKey: ["hipaa25-detail", detailId],
    queryFn: async () => { const r = await authFetch(`${BASE}/${detailId}`); if (!r.ok) throw new Error(); return r.json(); },
    enabled: !!detailId,
    staleTime: 0,
  });

  // ── Create ────────────────────────────────────────────────────────────────
  const [showCreate, setShowCreate] = useState(false);
  const defaultForm = () => ({
    systemName: "", systemType: "Database", storageType: "Database",
    dataCategory: "PHI", containsPHI: true, encryptionAtRestEnabled: true,
    encryptionMethod: "AES-256", encryptionAlgorithm: "AES-256-GCM",
    keyManagementMethod: "Cloud KMS", keyProvider: "AWS KMS",
    keyRotationEnabled: true, configurationEvidenceReference: "",
    encryptionStatus: "PendingReview", riskLevel: "Low",
    nextReviewDate: "", remediationRequired: false,
  });
  const [form, setForm] = useState(defaultForm());
  const setF = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));
  const [errors, setErrors] = useState<Record<string,string>>({});

  const createMut = useMutation({
    mutationFn: async () => {
      const body: Record<string,any> = { ...form };
      if (!body.nextReviewDate) delete body.nextReviewDate;
      else body.nextReviewDate = new Date(body.nextReviewDate).toISOString();
      const r = await authFetch(BASE, { method: "POST", body: JSON.stringify(body) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.message || e.error || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: (data: EncryptionControl25) => {
      toast({ title: "Encryption control created.", description: data.systemName });
      setShowCreate(false); setForm(defaultForm()); setErrors({});
      setDetailId(data.id); invalidate(data.id);
    },
    onError: (e: any) => { const s = extractServerErrors(e); if (s && Object.keys(s).length) { setErrors(s); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); } else toast({ title: "Unable to save", description: (e as Error)?.message, variant: "destructive" }); },
  });

  // ── Validate ────────────────────────────────────────────────────────────────
  const validateMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/${detailId}/validate`, { method: "PATCH", body: JSON.stringify({ notes: "Manual validation check." }) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.message || e.error || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: (data: { passed: boolean; issueCodes: string[] }) => {
      toast({
        title: data.passed ? "Validation passed ✓" : "Validation issues found",
        description: data.passed ? "Encryption control is fully configured." : data.issueCodes.join(", "),
        variant: data.passed ? "default" : "destructive",
      });
      invalidate();
    },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  // ── Key rotation ────────────────────────────────────────────────────────────
  const [showRotate, setShowRotate] = useState(false);
  const [rotateNext, setRotateNext] = useState("");
  const rotateMut = useMutation({
    mutationFn: async () => {
      const body: Record<string,any> = { notes: "Key rotation completed." };
      if (rotateNext) body.nextKeyRotationDate = new Date(rotateNext).toISOString();
      const r = await authFetch(`${BASE}/${detailId}/rotate-key`, { method: "PATCH", body: JSON.stringify(body) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.message || e.error || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Key rotation recorded." }); setShowRotate(false); setRotateNext(""); invalidate(); },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  // ── Review ──────────────────────────────────────────────────────────────────
  const [showReview, setShowReview] = useState(false);
  const [reviewForm, setReviewForm] = useState({ reviewerName: "", reviewNotes: "", encryptionStatus: "Configured", nextReviewDate: "" });
  const reviewMut = useMutation({
    mutationFn: async () => {
      const body: Record<string,any> = { ...reviewForm };
      if (!body.nextReviewDate) delete body.nextReviewDate;
      else body.nextReviewDate = new Date(body.nextReviewDate).toISOString();
      const r = await authFetch(`${BASE}/${detailId}/review`, { method: "PATCH", body: JSON.stringify(body) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.message || e.error || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Review completed." }); setShowReview(false); invalidate(); },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  // ── Delete ──────────────────────────────────────────────────────────────────
  const deleteMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/${detailId}`, { method: "DELETE" });
      if (!r.ok) { const e = await r.json(); throw new Error(e.message || e.error || "Unable to complete action"); }
    },
    onSuccess: () => { toast({ title: "Encryption control deleted." }); setDetailId(null); invalidate(); },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  const s = summaryQ.data;
  const detail = detailQ.data;
  const [showIssues, setShowIssues] = useState(false);

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Encryption Controls</CardTitle>
              <CardDescription>§ 164.312(a)(2)(iv) — Encryption at rest for PHI/ePHI systems with key management and compliance validation</CardDescription>
            </div>
            <div className="flex gap-2">
              {canManage && (
                <Button size="sm" onClick={() => setShowCreate(true)}>+ Add System</Button>
              )}
              <Button size="sm" variant="outline" onClick={() => setShowIssues(i => !i)}>
                {showIssues ? "Hide Issues" : `Issues${s?.issues ? ` (${s.issues})` : ""}`}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Dashboard metrics */}
          {s && (
            <div className="grid grid-cols-4 gap-2 sm:grid-cols-4 lg:grid-cols-8 text-center">
              {[
                { label: "Total",       value: s.total },
                { label: "PHI Systems", value: s.phiSystems },
                { label: "Encrypted",   value: s.encEnabled,     ok: true },
                { label: "Missing Enc", value: s.encMissing,     warn: s.encMissing > 0 },
                { label: "NonCompliant",value: s.nonCompliant,   warn: s.nonCompliant > 0 },
                { label: "Pending Rev", value: s.pendingReviews, warn: s.pendingReviews > 0 },
                { label: "Rot Overdue", value: s.rotOverdue,     warn: s.rotOverdue > 0 },
                { label: "High Risk",   value: s.highRisk,       warn: s.highRisk > 0 },
              ].map(m => (
                <div key={m.label} className="rounded border p-2">
                  <div className={`text-xl font-bold ${m.warn ? "text-red-600" : (m as any).ok ? "text-green-600" : ""}`}>{m.value}</div>
                  <div className="text-xs text-muted-foreground">{m.label}</div>
                </div>
              ))}
            </div>
          )}
          {s && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>Compliance: <span className={`font-semibold ${s.compliancePercentage >= 80 ? "text-green-600" : "text-red-600"}`}>{s.compliancePercentage}%</span></span>
              {s.issues > 0 && <Badge variant="destructive" className="text-xs">{s.issues} compliance issue{s.issues !== 1 ? "s" : ""}</Badge>}
              {s.rotOverdue > 0 && <Badge variant="outline" className="text-xs">{s.rotOverdue} rotation overdue</Badge>}
            </div>
          )}

          {/* Issues panel */}
          {showIssues && (
            <div className="border rounded p-3 space-y-1 bg-red-50/50">
              <p className="text-xs font-semibold text-red-700">Compliance Issues</p>
              {issuesQ.isLoading ? <Skeleton className="h-8 w-full" /> :
               issuesQ.data?.length === 0 ? <p className="text-xs text-muted-foreground">No compliance issues.</p> :
               issuesQ.data?.map(i => (
                <div key={i.id} className="flex items-center gap-2 text-xs cursor-pointer hover:underline" onClick={() => setDetailId(i.id)}>
                  <Badge variant="destructive" className="text-xs shrink-0">
                    {i.phiWithoutEncryption ? "PHI_ENCRYPTION_REQUIRED" : "NON_COMPLIANT"}
                  </Badge>
                  <span>{i.systemName}</span>
                  {i.riskLevel && <span className="text-muted-foreground">· {i.riskLevel} risk</span>}
                </div>
              ))}
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-wrap gap-2">
            <select className="border rounded px-2 py-1 text-sm" value={statusF} onChange={e => { setStatusF(e.target.value); setPage(1); }}>
              <option value="">All Statuses</option>
              {ENC_STATUSES.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
            <select className="border rounded px-2 py-1 text-sm" value={systemTypeF} onChange={e => { setSystemTypeF(e.target.value); setPage(1); }}>
              <option value="">All Types</option>
              {ENC_SYSTEM_TYPES.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
            <select className="border rounded px-2 py-1 text-sm" value={phiF} onChange={e => { setPhiF(e.target.value); setPage(1); }}>
              <option value="">All Systems</option>
              <option value="true">PHI Only</option>
              <option value="false">Non-PHI Only</option>
            </select>
            <select className="border rounded px-2 py-1 text-sm" value={riskF} onChange={e => { setRiskF(e.target.value); setPage(1); }}>
              <option value="">All Risk Levels</option>
              {ENC_RISK_LEVELS.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>

          {/* Control list */}
          {listQ.isLoading ? <Skeleton className="h-24 w-full" /> : listQ.data?.items?.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No encryption controls found. Add a system to start tracking.</p>
          ) : (
            <div className="space-y-2">
              {listQ.data?.items?.map(c => (
                <div key={c.id}
                  className={`flex items-start justify-between rounded border p-3 cursor-pointer hover:bg-muted/50 transition-colors ${detailId === c.id ? "border-primary bg-muted/30" : ""}`}
                  onClick={() => setDetailId(c.id)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm truncate">{c.systemName}</span>
                      <Badge variant={enc25StatusVariant(c.encryptionStatus)} className="text-xs">{c.encryptionStatus}</Badge>
                      {c.containsPHI && <Badge variant="outline" className="text-xs bg-amber-50">PHI</Badge>}
                      {c.phiWithoutEncryption && <Badge variant="destructive" className="text-xs">No Encryption!</Badge>}
                      {c.rotationOverdue && <Badge variant="destructive" className="text-xs">Rotation Overdue</Badge>}
                      {c.reviewOverdue && <Badge variant="outline" className="text-xs">Review Overdue</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {c.systemType ?? "—"} · {c.storageType ?? "—"} · {c.encryptionMethod ?? "No encryption"} · Risk: {c.riskLevel ?? "—"}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {c.keyRotationEnabled ? `Key rotation: ${c.nextKeyRotationDate ? c.nextKeyRotationDate.slice(0,10) : "date not set"}` : "Key rotation disabled"}
                      {c.nextReviewDate ? ` · Next review: ${c.nextReviewDate.slice(0,10)}` : ""}
                    </div>
                  </div>
                </div>
              ))}
              {(listQ.data?.count ?? 0) > 15 && (
                <div className="flex justify-center gap-2 pt-2">
                  <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
                  <span className="text-sm self-center">Page {page}</span>
                  <Button size="sm" variant="outline" disabled={(listQ.data?.count ?? 0) <= page * 15} onClick={() => setPage(p => p + 1)}>Next</Button>
                </div>
              )}
            </div>
          )}

          {/* Detail panel */}
          {detail && detailId && (
            <div className="border rounded-lg p-4 space-y-3 bg-muted/20">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h4 className="font-semibold text-sm">{detail.systemName}</h4>
                <div className="flex gap-1 flex-wrap">
                  {canManage && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => validateMut.mutate()} disabled={validateMut.isPending}>
                        {validateMut.isPending ? "Validating…" : "Validate"}
                      </Button>
                      {detail.encryptionAtRestEnabled && detail.keyRotationEnabled && (
                        <Button size="sm" variant="outline" onClick={() => setShowRotate(true)}>Record Rotation</Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => { setReviewForm(f => ({ ...f, encryptionStatus: detail.encryptionStatus })); setShowReview(true); }}>Complete Review</Button>
                      <Button size="sm" variant="destructive" onClick={() => deleteMut.mutate()} disabled={deleteMut.isPending}>Delete</Button>
                    </>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => setDetailId(null)}>✕</Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs md:grid-cols-3">
                <div><span className="text-muted-foreground">System Type:</span> {detail.systemType ?? "—"}</div>
                <div><span className="text-muted-foreground">Storage Type:</span> {detail.storageType ?? "—"}</div>
                <div><span className="text-muted-foreground">Data Category:</span> {detail.dataCategory ?? "—"}</div>
                <div><span className="text-muted-foreground">Contains PHI:</span> {detail.containsPHI ? "Yes" : "No"}</div>
                <div><span className="text-muted-foreground">Encryption Enabled:</span> {detail.encryptionAtRestEnabled ? "Yes" : "No"}</div>
                <div><span className="text-muted-foreground">Status:</span> <Badge variant={enc25StatusVariant(detail.encryptionStatus)} className="text-xs">{detail.encryptionStatus}</Badge></div>
                <div><span className="text-muted-foreground">Method:</span> {detail.encryptionMethod ?? "—"}</div>
                <div><span className="text-muted-foreground">Algorithm:</span> {detail.encryptionAlgorithm ?? "—"}</div>
                <div><span className="text-muted-foreground">Risk Level:</span> {detail.riskLevel ?? "—"}</div>
                <div><span className="text-muted-foreground">Key Mgmt:</span> {detail.keyManagementMethod ?? "—"}</div>
                <div><span className="text-muted-foreground">Key Provider:</span> {detail.keyProvider ?? "—"}</div>
                <div><span className="text-muted-foreground">Key Rotation:</span> {detail.keyRotationEnabled ? "Enabled" : "Disabled"}</div>
                {detail.keyRotationEnabled && (
                  <>
                    <div><span className="text-muted-foreground">Last Rotation:</span> {detail.lastKeyRotationDate ? detail.lastKeyRotationDate.slice(0,10) : "Never"}</div>
                    <div><span className="text-muted-foreground">Next Rotation:</span> {detail.nextKeyRotationDate ? detail.nextKeyRotationDate.slice(0,10) : "—"}</div>
                  </>
                )}
                <div><span className="text-muted-foreground">Next Review:</span> {detail.nextReviewDate ? detail.nextReviewDate.slice(0,10) : "—"}</div>
                {detail.reviewerName && <div><span className="text-muted-foreground">Last Reviewer:</span> {detail.reviewerName}</div>}
              </div>

              {detail.configurationEvidenceReference && (
                <div className="text-xs"><span className="text-muted-foreground">Evidence Ref:</span> <code className="bg-muted px-1 rounded">{detail.configurationEvidenceReference}</code></div>
              )}
              {detail.certificateReference && (
                <div className="text-xs"><span className="text-muted-foreground">Certificate:</span> <code className="bg-muted px-1 rounded">{detail.certificateReference}</code></div>
              )}
              {detail.reviewNotes && (
                <div className="text-xs border-t pt-2"><span className="text-muted-foreground">Review Notes:</span> {detail.reviewNotes}</div>
              )}
              {detail.remediationRequired && (
                <div className="text-xs text-red-600 font-medium border-t pt-2">
                  ⚠ Remediation required. Status: {detail.remediationStatus ?? "Open"}
                  {detail.remediationNotes && <span> — {detail.remediationNotes}</span>}
                </div>
              )}
              {detail.phiWithoutEncryption && (
                <div className="text-xs text-red-700 font-semibold">⚠ PHI system without encryption — PHI_ENCRYPTION_REQUIRED violation.</div>
              )}
              {detail.rotationOverdue && detail.daysUntilRotation !== undefined && (
                <div className="text-xs text-red-600 font-medium">⚠ Key rotation is {Math.abs(detail.daysUntilRotation)} day{Math.abs(detail.daysUntilRotation) !== 1 ? "s" : ""} overdue.</div>
              )}
              {detail.reviewOverdue && (
                <div className="text-xs text-amber-700 font-medium">⚠ Review is overdue.</div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={v => { setShowCreate(v); if (!v) setErrors({}); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Encryption Control</DialogTitle>
            <DialogDescription>Register a system and its encryption configuration.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <label className="text-xs font-medium">System Name *</label>
              <input className={fieldCls("w-full border rounded px-2 py-1 text-sm mt-1", errors, "systemName")} aria-invalid={!!errors.systemName} value={form.systemName} onChange={e => { setF("systemName", e.target.value); clearErr(setErrors, "systemName"); }} placeholder="e.g. Patient Records Database" />
              {fErr(errors, "systemName")}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium">System Type</label>
                <select className="w-full border rounded px-2 py-1 text-sm mt-1" value={form.systemType} onChange={e => setF("systemType", e.target.value)}>
                  {ENC_SYSTEM_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium">Storage Type</label>
                <select className="w-full border rounded px-2 py-1 text-sm mt-1" value={form.storageType} onChange={e => setF("storageType", e.target.value)}>
                  {ENC_STORAGE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div className="flex gap-4">
              <label className="flex items-center gap-1 text-xs cursor-pointer">
                <input type="checkbox" checked={form.containsPHI} onChange={e => setF("containsPHI", e.target.checked)} />
                Contains PHI
              </label>
              <label className="flex items-center gap-1 text-xs cursor-pointer">
                <input type="checkbox" checked={form.encryptionAtRestEnabled} onChange={e => setF("encryptionAtRestEnabled", e.target.checked)} />
                Encryption Enabled
              </label>
              <label className="flex items-center gap-1 text-xs cursor-pointer">
                <input type="checkbox" checked={form.keyRotationEnabled} onChange={e => setF("keyRotationEnabled", e.target.checked)} />
                Key Rotation
              </label>
            </div>
            {form.encryptionAtRestEnabled && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-medium">Encryption Method</label>
                  <select className="w-full border rounded px-2 py-1 text-sm mt-1" value={form.encryptionMethod} onChange={e => setF("encryptionMethod", e.target.value)}>
                    {ENC_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium">Algorithm</label>
                  <input className="w-full border rounded px-2 py-1 text-sm mt-1" value={form.encryptionAlgorithm} onChange={e => setF("encryptionAlgorithm", e.target.value)} placeholder="AES-256-GCM" />
                </div>
                <div>
                  <label className="text-xs font-medium">Key Management</label>
                  <select className="w-full border rounded px-2 py-1 text-sm mt-1" value={form.keyManagementMethod} onChange={e => setF("keyManagementMethod", e.target.value)}>
                    {ENC_KEY_MGMT.map(k => <option key={k} value={k}>{k}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium">Key Provider</label>
                  <input className="w-full border rounded px-2 py-1 text-sm mt-1" value={form.keyProvider} onChange={e => setF("keyProvider", e.target.value)} placeholder="AWS KMS, Azure Key Vault…" />
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium">Risk Level</label>
                <select className="w-full border rounded px-2 py-1 text-sm mt-1" value={form.riskLevel} onChange={e => setF("riskLevel", e.target.value)}>
                  {ENC_RISK_LEVELS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium">Initial Status</label>
                <select className="w-full border rounded px-2 py-1 text-sm mt-1" value={form.encryptionStatus} onChange={e => setF("encryptionStatus", e.target.value)}>
                  {ENC_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium">Evidence Reference</label>
              <input className="w-full border rounded px-2 py-1 text-sm mt-1" value={form.configurationEvidenceReference} onChange={e => setF("configurationEvidenceReference", e.target.value)} placeholder="e.g. evidence://encryption-config-2026.json" />
            </div>
            <div>
              <label className="text-xs font-medium">Next Review Date</label>
              <input type="date" className="w-full border rounded px-2 py-1 text-sm mt-1" value={form.nextReviewDate} onChange={e => setF("nextReviewDate", e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={() => { const errs: Record<string,string> = {}; required(errs, "systemName", form.systemName, "System Name"); if (Object.keys(errs).length) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } setErrors({}); createMut.mutate(); }} disabled={createMut.isPending}>
              {createMut.isPending ? "Creating…" : "Add Control"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Key Rotation Dialog */}
      {showRotate && (
        <Dialog open={showRotate} onOpenChange={setShowRotate}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Record Key Rotation</DialogTitle>
              <DialogDescription>Confirm that key rotation has been completed for this system.</DialogDescription>
            </DialogHeader>
            <div className="space-y-2 text-sm">
              <label className="text-xs font-medium">Next Rotation Date</label>
              <input type="date" className="w-full border rounded px-2 py-1 text-sm mt-1" value={rotateNext} onChange={e => setRotateNext(e.target.value)} />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRotate(false)}>Cancel</Button>
              <Button onClick={() => rotateMut.mutate()} disabled={rotateMut.isPending}>
                {rotateMut.isPending ? "Recording…" : "Record Rotation"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Review Dialog */}
      {showReview && (
        <Dialog open={showReview} onOpenChange={setShowReview}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Complete Review</DialogTitle>
              <DialogDescription>Record the result of the encryption compliance review.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <div>
                <label className="text-xs font-medium">Reviewer Name *</label>
                <input className="w-full border rounded px-2 py-1 text-sm mt-1" value={reviewForm.reviewerName} onChange={e => setReviewForm(f => ({ ...f, reviewerName: e.target.value }))} placeholder="e.g. CISO" />
              </div>
              <div>
                <label className="text-xs font-medium">Outcome Status</label>
                <select className="w-full border rounded px-2 py-1 text-sm mt-1" value={reviewForm.encryptionStatus} onChange={e => setReviewForm(f => ({ ...f, encryptionStatus: e.target.value }))}>
                  {ENC_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium">Next Review Date</label>
                <input type="date" className="w-full border rounded px-2 py-1 text-sm mt-1" value={reviewForm.nextReviewDate} onChange={e => setReviewForm(f => ({ ...f, nextReviewDate: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs font-medium">Review Notes</label>
                <textarea className="w-full border rounded px-2 py-1 text-sm mt-1" rows={3} value={reviewForm.reviewNotes} onChange={e => setReviewForm(f => ({ ...f, reviewNotes: e.target.value }))} placeholder="Findings and observations…" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowReview(false)}>Cancel</Button>
              <Button onClick={() => reviewMut.mutate()} disabled={reviewMut.isPending || !reviewForm.reviewerName.trim()}>
                {reviewMut.isPending ? "Saving…" : "Complete Review"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// HIPAA-24 — Policies & Documentation (§ 164.316)
// ══════════════════════════════════════════════════════════════════════════════

interface PolicySummary24 {
  total: number; approved: number; draft: number; pending: number;
  archived: number; expired: number; reviewOverdue: number;
  expiringSoon: number; compliancePercentage: number;
}
interface PolicyRecord24 {
  id: string; organizationId: string;
  policyNumber: string; policyTitle: string; policyCategory?: string;
  description?: string; documentType?: string;
  versionNumber: string; versionStatus: string;
  effectiveDate?: string; expirationDate?: string;
  reviewDate?: string; nextReviewDate?: string; reviewFrequency?: string;
  ownerName?: string; ownerUserId?: string;
  approvedBy?: string; approvalDate?: string; approvalNotes?: string;
  documentReference?: string; storageLocation?: string;
  retentionPeriod: number; retentionExpiryDate?: string;
  requiresAcknowledgement: boolean; acknowledgementRequiredForRoles?: string;
  changeSummary?: string; lastUpdatedReason?: string;
  createdAt: string; updatedAt: string; createdBy?: string; updatedBy?: string;
  reviewOverdue: boolean; isExpired: boolean; expiringSoon: boolean;
  daysUntilExpiry?: number; daysSinceReview?: number;
}

function useAuth24() {
  return (url: string, opts: RequestInit = {}) => {
    const token = sessionStorage.getItem("dotnetbot.jwt.session") || localStorage.getItem("dotnetbot.jwt") || "";
    return fetch(url, { ...opts, headers: { ...(opts.headers as Record<string,string> || {}), "Content-Type": "application/json", Authorization: `Bearer ${token}` } });
  };
}

function policy24StatusVariant(s: string): "default"|"secondary"|"outline"|"destructive" {
  if (s === "Approved")        return "default";
  if (s === "Archived")        return "secondary";
  if (s === "PendingApproval") return "outline";
  if (s === "Draft")           return "outline";
  if (s === "Expired")         return "destructive";
  return "outline";
}

const POLICY_CATEGORIES = [
  "Privacy Policy", "Security Policy", "Access Control Policy",
  "Incident Response Policy", "Workforce Policy", "Vendor Policy",
  "Training Policy", "Other",
];
const POLICY_DOC_TYPES   = ["Policy", "Procedure", "Standard", "Guideline", "Form"];
const POLICY_FREQUENCIES = ["Annually", "BiAnnually", "Quarterly", "AsNeeded"];

function HipaaPolicyDocumentationSection({ canManage }: { canManage: boolean }) {
  const { toast } = useToast();
  const authFetch = useAuth24();
  const qc = useQueryClient();
  const BASE = "/api/organization/compliance/hipaa/policies";

  const invalidate = (id?: string) => {
    qc.invalidateQueries({ queryKey: ["hipaa24-list"] });
    qc.invalidateQueries({ queryKey: ["hipaa24-summary"] });
    qc.invalidateQueries({ queryKey: ["hipaa24-overdue"] });
    if (id) qc.invalidateQueries({ queryKey: ["hipaa24-detail", id] });
    else if (detailId) qc.invalidateQueries({ queryKey: ["hipaa24-detail", detailId] });
  };

  // ── Summary ──────────────────────────────────────────────────────────────
  const summaryQ = useQuery<PolicySummary24>({
    queryKey: ["hipaa24-summary"],
    queryFn: async () => { const r = await authFetch(`${BASE}/summary`); if (!r.ok) throw new Error(); return r.json(); },
    staleTime: 0,
  });

  // ── Overdue ──────────────────────────────────────────────────────────────
  const overdueQ = useQuery<PolicyRecord24[]>({
    queryKey: ["hipaa24-overdue"],
    queryFn: async () => { const r = await authFetch(`${BASE}/overdue`); if (!r.ok) throw new Error(); return r.json(); },
    staleTime: 0,
  });

  // ── List + filters ────────────────────────────────────────────────────────
  const [page,       setPage]       = useState(1);
  const [statusF,    setStatusF]    = useState("");
  const [categoryF,  setCategoryF]  = useState("");

  const listQ = useQuery<{ count: number; items: PolicyRecord24[] }>({
    queryKey: ["hipaa24-list", page, statusF, categoryF],
    queryFn: async () => {
      const p = new URLSearchParams({ page: String(page), limit: "15" });
      if (statusF)   p.set("status",   statusF);
      if (categoryF) p.set("category", categoryF);
      const r = await authFetch(`${BASE}?${p}`);
      if (!r.ok) throw new Error(); return r.json();
    },
    staleTime: 0,
  });

  // ── Detail ────────────────────────────────────────────────────────────────
  const [detailId, setDetailId] = useState<string | null>(null);
  const detailQ = useQuery<PolicyRecord24>({
    queryKey: ["hipaa24-detail", detailId],
    queryFn: async () => { const r = await authFetch(`${BASE}/${detailId}`); if (!r.ok) throw new Error(); return r.json(); },
    enabled: !!detailId,
    staleTime: 0,
  });

  // ── History ───────────────────────────────────────────────────────────────
  const [showHistory, setShowHistory] = useState(false);
  const historyQ = useQuery<PolicyRecord24[]>({
    queryKey: ["hipaa24-history", detailId],
    queryFn: async () => { const r = await authFetch(`${BASE}/${detailId}/history`); if (!r.ok) throw new Error(); return r.json(); },
    enabled: !!detailId && showHistory,
    staleTime: 0,
  });

  // ── Create ────────────────────────────────────────────────────────────────
  const [showCreate, setShowCreate] = useState(false);
  const defaultForm = () => ({
    policyNumber: "auto", policyTitle: "", policyCategory: "Privacy Policy",
    description: "", documentType: "Policy", versionNumber: "1.0",
    reviewFrequency: "Annually", ownerName: "",
    documentReference: "", storageLocation: "SharePoint",
    nextReviewDate: "", expirationDate: "", effectiveDate: "",
    retentionPeriod: 6, requiresAcknowledgement: false,
    acknowledgementRequiredForRoles: "", changeSummary: "", lastUpdatedReason: "",
  });
  const [form, setForm] = useState(defaultForm());
  const setF = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));
  const [errors, setErrors] = useState<Record<string,string>>({});

  const createMut = useMutation({
    mutationFn: async () => {
      const body: Record<string,any> = { ...form };
      if (!body.effectiveDate)   delete body.effectiveDate;
      if (!body.expirationDate)  delete body.expirationDate;
      if (!body.nextReviewDate)  delete body.nextReviewDate;
      else body.nextReviewDate = new Date(body.nextReviewDate).toISOString();
      const r = await authFetch(BASE, { method: "POST", body: JSON.stringify(body) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.message || e.error || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: (data: PolicyRecord24) => {
      toast({ title: "Policy created.", description: data.policyNumber });
      setShowCreate(false); setForm(defaultForm()); setErrors({});
      setDetailId(data.id); invalidate(data.id);
    },
    onError: (e: any) => { const s = extractServerErrors(e); if (s && Object.keys(s).length) { setErrors(s); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); } else toast({ title: "Unable to save", description: (e as Error)?.message, variant: "destructive" }); },
  });

  // ── Submit for Approval ────────────────────────────────────────────────────
  const submitMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/${detailId}/submit`, { method: "POST", body: JSON.stringify({}) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.message || e.error || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Policy submitted for approval." }); invalidate(); },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  // ── Approve ────────────────────────────────────────────────────────────────
  const [showApprove, setShowApprove] = useState(false);
  const [approveForm, setApproveForm] = useState({ approvedBy: "", approvalNotes: "" });
  const approveMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/${detailId}/approve`, { method: "PATCH", body: JSON.stringify(approveForm) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.message || e.error || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Policy approved." }); setShowApprove(false); invalidate(); },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  // ── Reject ─────────────────────────────────────────────────────────────────
  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const rejectMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/${detailId}/reject`, { method: "PATCH", body: JSON.stringify({ reason: rejectReason }) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.message || e.error || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Policy rejected, returned to Draft." }); setShowReject(false); setRejectReason(""); invalidate(); },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  // ── Archive ────────────────────────────────────────────────────────────────
  const archiveMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/${detailId}/archive`, { method: "PATCH", body: JSON.stringify({ reason: "Superseded by newer version." }) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.message || e.error || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Policy archived." }); invalidate(); },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  // ── New Version ────────────────────────────────────────────────────────────
  const newVersionMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/${detailId}/new-version`, { method: "POST", body: JSON.stringify({ changeSummary: "New version for review.", lastUpdatedReason: "Periodic policy review." }) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.message || e.error || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: (data: PolicyRecord24) => { toast({ title: "New version created.", description: data.policyNumber }); setDetailId(data.id); invalidate(data.id); },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  // ── Delete ─────────────────────────────────────────────────────────────────
  const deleteMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/${detailId}`, { method: "DELETE" });
      if (!r.ok) { const e = await r.json(); throw new Error(e.message || e.error || "Unable to complete action"); }
    },
    onSuccess: () => { toast({ title: "Policy deleted." }); setDetailId(null); invalidate(); },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  const s = summaryQ.data;
  const detail = detailQ.data;

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Policies &amp; Documentation</CardTitle>
              <CardDescription>§ 164.316 — Written policies, version control, approval workflow, and retention tracking</CardDescription>
            </div>
            {canManage && (
              <Button size="sm" onClick={() => setShowCreate(true)}>+ New Policy</Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Dashboard metrics */}
          {s && (
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-8 text-center">
              {[
                { label: "Total",    value: s.total },
                { label: "Approved", value: s.approved, ok: true },
                { label: "Draft",    value: s.draft },
                { label: "Pending",  value: s.pending },
                { label: "Archived", value: s.archived },
                { label: "Expired",  value: s.expired,      warn: s.expired > 0 },
                { label: "Overdue",  value: s.reviewOverdue,warn: s.reviewOverdue > 0 },
                { label: "Expiring", value: s.expiringSoon, warn: s.expiringSoon > 0 },
              ].map(m => (
                <div key={m.label} className="rounded border p-2">
                  <div className={`text-xl font-bold ${m.warn ? "text-red-600" : m.ok ? "text-green-600" : ""}`}>{m.value}</div>
                  <div className="text-xs text-muted-foreground">{m.label}</div>
                </div>
              ))}
            </div>
          )}
          {s && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>Compliance: <span className={`font-semibold ${s.compliancePercentage >= 80 ? "text-green-600" : "text-red-600"}`}>{s.compliancePercentage}%</span></span>
              {s.reviewOverdue > 0 && <Badge variant="destructive" className="text-xs">{s.reviewOverdue} overdue review{s.reviewOverdue !== 1 ? "s" : ""}</Badge>}
              {s.expiringSoon  > 0 && <Badge variant="outline"      className="text-xs">{s.expiringSoon} expiring soon</Badge>}
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-wrap gap-2">
            <select className="border rounded px-2 py-1 text-sm" value={statusF} onChange={e => { setStatusF(e.target.value); setPage(1); }}>
              <option value="">All Statuses</option>
              {["Draft","PendingApproval","Approved","Archived","Expired"].map(v => <option key={v} value={v}>{v}</option>)}
            </select>
            <select className="border rounded px-2 py-1 text-sm" value={categoryF} onChange={e => { setCategoryF(e.target.value); setPage(1); }}>
              <option value="">All Categories</option>
              {POLICY_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          {/* Policy list */}
          {listQ.isLoading ? <Skeleton className="h-24 w-full" /> : listQ.data?.items?.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No policies found. Create one to get started.</p>
          ) : (
            <div className="space-y-2">
              {listQ.data?.items?.map(p => (
                <div key={p.id}
                  className={`flex items-start justify-between rounded border p-3 cursor-pointer hover:bg-muted/50 transition-colors ${detailId === p.id ? "border-primary bg-muted/30" : ""}`}
                  onClick={() => { setDetailId(p.id); setShowHistory(false); }}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm truncate">{p.policyTitle}</span>
                      <Badge variant={policy24StatusVariant(p.versionStatus)} className="text-xs">{p.versionStatus}</Badge>
                      {p.reviewOverdue && <Badge variant="destructive" className="text-xs">Review Overdue</Badge>}
                      {p.isExpired     && <Badge variant="destructive" className="text-xs">Expired</Badge>}
                      {p.expiringSoon  && <Badge variant="outline"     className="text-xs">Expiring Soon</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {p.policyNumber} · v{p.versionNumber} · {p.policyCategory ?? "—"} · Owner: {p.ownerName ?? "—"}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {p.nextReviewDate ? `Next Review: ${p.nextReviewDate.slice(0,10)}` : "No review date"}
                      {p.expirationDate ? ` · Expires: ${p.expirationDate.slice(0,10)}` : ""}
                    </div>
                  </div>
                </div>
              ))}
              {/* Pagination */}
              {(listQ.data?.count ?? 0) > 15 && (
                <div className="flex justify-center gap-2 pt-2">
                  <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
                  <span className="text-sm self-center">Page {page}</span>
                  <Button size="sm" variant="outline" disabled={(listQ.data?.count ?? 0) <= page * 15} onClick={() => setPage(p => p + 1)}>Next</Button>
                </div>
              )}
            </div>
          )}

          {/* Detail panel */}
          {detail && detailId && (
            <div className="border rounded-lg p-4 space-y-3 bg-muted/20">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h4 className="font-semibold text-sm">{detail.policyTitle}</h4>
                <div className="flex gap-1 flex-wrap">
                  {canManage && detail.versionStatus === "Draft" && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => submitMut.mutate()} disabled={submitMut.isPending}>
                        {submitMut.isPending ? "Submitting…" : "Submit for Approval"}
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => deleteMut.mutate()} disabled={deleteMut.isPending}>Delete</Button>
                    </>
                  )}
                  {canManage && detail.versionStatus === "PendingApproval" && (
                    <>
                      <Button size="sm" onClick={() => setShowApprove(true)}>Approve</Button>
                      <Button size="sm" variant="outline" onClick={() => setShowReject(true)}>Reject</Button>
                    </>
                  )}
                  {canManage && detail.versionStatus === "Approved" && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => newVersionMut.mutate()} disabled={newVersionMut.isPending}>New Version</Button>
                      <Button size="sm" variant="secondary" onClick={() => archiveMut.mutate()} disabled={archiveMut.isPending}>Archive</Button>
                    </>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => setShowHistory(h => !h)}>
                    {showHistory ? "Hide History" : "Version History"}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => { setDetailId(null); setShowHistory(false); }}>✕</Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs md:grid-cols-3">
                <div><span className="text-muted-foreground">Policy #:</span> {detail.policyNumber}</div>
                <div><span className="text-muted-foreground">Version:</span> v{detail.versionNumber}</div>
                <div><span className="text-muted-foreground">Status:</span> <Badge variant={policy24StatusVariant(detail.versionStatus)} className="text-xs">{detail.versionStatus}</Badge></div>
                <div><span className="text-muted-foreground">Category:</span> {detail.policyCategory ?? "—"}</div>
                <div><span className="text-muted-foreground">Type:</span> {detail.documentType ?? "—"}</div>
                <div><span className="text-muted-foreground">Owner:</span> {detail.ownerName ?? "—"}</div>
                <div><span className="text-muted-foreground">Effective:</span> {detail.effectiveDate ? detail.effectiveDate.slice(0,10) : "—"}</div>
                <div><span className="text-muted-foreground">Expires:</span> {detail.expirationDate ? detail.expirationDate.slice(0,10) : "—"}</div>
                <div><span className="text-muted-foreground">Next Review:</span> {detail.nextReviewDate ? detail.nextReviewDate.slice(0,10) : "—"}</div>
                <div><span className="text-muted-foreground">Review Freq:</span> {detail.reviewFrequency ?? "—"}</div>
                <div><span className="text-muted-foreground">Retention:</span> {detail.retentionPeriod} yr</div>
                <div><span className="text-muted-foreground">Storage:</span> {detail.storageLocation ?? "—"}</div>
              </div>

              {detail.approvedBy && (
                <div className="text-xs border-t pt-2 space-y-1">
                  <div><span className="text-muted-foreground">Approved by:</span> {detail.approvedBy} on {detail.approvalDate?.slice(0,10)}</div>
                  {detail.approvalNotes && <div><span className="text-muted-foreground">Notes:</span> {detail.approvalNotes}</div>}
                </div>
              )}

              {detail.documentReference && (
                <div className="text-xs"><span className="text-muted-foreground">Document Ref:</span> <code className="bg-muted px-1 rounded">{detail.documentReference}</code></div>
              )}

              {detail.description && (
                <div className="text-xs border-t pt-2"><span className="text-muted-foreground">Description:</span> {detail.description}</div>
              )}

              {detail.changeSummary && (
                <div className="text-xs"><span className="text-muted-foreground">Change Summary:</span> {detail.changeSummary}</div>
              )}

              {/* Overdue / expiry warnings */}
              {detail.reviewOverdue && (
                <div className="text-xs text-red-600 font-medium">⚠ Review overdue — next review date has passed.</div>
              )}
              {detail.isExpired && (
                <div className="text-xs text-red-600 font-medium">⚠ Policy has expired.</div>
              )}
              {detail.expiringSoon && detail.daysUntilExpiry !== undefined && (
                <div className="text-xs text-amber-700 font-medium">⚠ Expires in {detail.daysUntilExpiry} day{detail.daysUntilExpiry !== 1 ? "s" : ""}.</div>
              )}

              {/* Version history */}
              {showHistory && (
                <div className="border-t pt-2 space-y-1">
                  <p className="text-xs font-medium text-muted-foreground">Version History</p>
                  {historyQ.isLoading ? <Skeleton className="h-8 w-full" /> : (
                    historyQ.data?.map(h => (
                      <div key={h.id} className="flex items-center gap-2 text-xs p-1 rounded hover:bg-muted/40 cursor-pointer" onClick={() => { setDetailId(h.id); }}>
                        <Badge variant={policy24StatusVariant(h.versionStatus)} className="text-xs">v{h.versionNumber}</Badge>
                        <span>{h.versionStatus}</span>
                        {h.approvalDate && <span className="text-muted-foreground">Approved {h.approvalDate.slice(0,10)}</span>}
                        {h.changeSummary && <span className="text-muted-foreground truncate">— {h.changeSummary}</span>}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Policy Dialog */}
      <Dialog open={showCreate} onOpenChange={v => { setShowCreate(v); if (!v) setErrors({}); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New HIPAA Policy</DialogTitle>
            <DialogDescription>Create a written policy document with version tracking.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <label className="text-xs font-medium">Policy Title *</label>
              <input className={fieldCls("w-full border rounded px-2 py-1 text-sm mt-1", errors, "policyTitle")} aria-invalid={!!errors.policyTitle} value={form.policyTitle} onChange={e => { setF("policyTitle", e.target.value); clearErr(setErrors, "policyTitle"); }} placeholder="e.g. HIPAA Privacy Policy" />
              {fErr(errors, "policyTitle")}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium">Category</label>
                <select className="w-full border rounded px-2 py-1 text-sm mt-1" value={form.policyCategory} onChange={e => setF("policyCategory", e.target.value)}>
                  {POLICY_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium">Document Type</label>
                <select className="w-full border rounded px-2 py-1 text-sm mt-1" value={form.documentType} onChange={e => setF("documentType", e.target.value)}>
                  {POLICY_DOC_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium">Version Number</label>
                <input className="w-full border rounded px-2 py-1 text-sm mt-1" value={form.versionNumber} onChange={e => setF("versionNumber", e.target.value)} placeholder="1.0" />
              </div>
              <div>
                <label className="text-xs font-medium">Review Frequency</label>
                <select className="w-full border rounded px-2 py-1 text-sm mt-1" value={form.reviewFrequency} onChange={e => setF("reviewFrequency", e.target.value)}>
                  {POLICY_FREQUENCIES.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium">Owner Name</label>
              <input className="w-full border rounded px-2 py-1 text-sm mt-1" value={form.ownerName} onChange={e => setF("ownerName", e.target.value)} placeholder="e.g. Privacy Officer" />
            </div>
            <div>
              <label className="text-xs font-medium">Document Reference <span className="text-muted-foreground">(required to submit for approval)</span></label>
              <input className="w-full border rounded px-2 py-1 text-sm mt-1" value={form.documentReference} onChange={e => setF("documentReference", e.target.value)} placeholder="e.g. sharepoint://hipaa/policies/privacy-v1.docx" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium">Storage Location</label>
                <input className="w-full border rounded px-2 py-1 text-sm mt-1" value={form.storageLocation} onChange={e => setF("storageLocation", e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-medium">Retention Period (years)</label>
                <input type="number" className="w-full border rounded px-2 py-1 text-sm mt-1" min={1} max={50} value={form.retentionPeriod} onChange={e => setF("retentionPeriod", Number(e.target.value))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium">Expiration Date</label>
                <input type="date" className="w-full border rounded px-2 py-1 text-sm mt-1" value={form.expirationDate} onChange={e => setF("expirationDate", e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-medium">Next Review Date</label>
                <input type="date" className="w-full border rounded px-2 py-1 text-sm mt-1" value={form.nextReviewDate} onChange={e => setF("nextReviewDate", e.target.value)} />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium">Description</label>
              <textarea className="w-full border rounded px-2 py-1 text-sm mt-1" rows={3} value={form.description} onChange={e => setF("description", e.target.value)} placeholder="Policy purpose and scope…" />
            </div>
            <div>
              <label className="text-xs font-medium">Change Summary</label>
              <textarea className="w-full border rounded px-2 py-1 text-sm mt-1" rows={2} value={form.changeSummary} onChange={e => setF("changeSummary", e.target.value)} placeholder="What changed in this version…" />
            </div>
          </div>
          <DialogFooter className="pt-2">
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={() => { const errs: Record<string,string> = {}; required(errs, "policyTitle", form.policyTitle, "Policy Title"); if (Object.keys(errs).length) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } setErrors({}); createMut.mutate(); }} disabled={createMut.isPending}>
              {createMut.isPending ? "Creating…" : "Create Policy"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve Dialog */}
      {showApprove && (
        <Dialog open={showApprove} onOpenChange={setShowApprove}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Approve Policy</DialogTitle>
              <DialogDescription>Record approval for this policy version.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <div>
                <label className="text-xs font-medium">Approved By *</label>
                <input className="w-full border rounded px-2 py-1 text-sm mt-1" value={approveForm.approvedBy} onChange={e => setApproveForm(f => ({ ...f, approvedBy: e.target.value }))} placeholder="e.g. Chief Privacy Officer" />
              </div>
              <div>
                <label className="text-xs font-medium">Approval Notes</label>
                <textarea className="w-full border rounded px-2 py-1 text-sm mt-1" rows={3} value={approveForm.approvalNotes} onChange={e => setApproveForm(f => ({ ...f, approvalNotes: e.target.value }))} placeholder="Optional approval notes…" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowApprove(false)}>Cancel</Button>
              <Button onClick={() => approveMut.mutate()} disabled={approveMut.isPending || !approveForm.approvedBy.trim()}>
                {approveMut.isPending ? "Approving…" : "Approve Policy"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Reject Dialog */}
      {showReject && (
        <Dialog open={showReject} onOpenChange={setShowReject}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Reject Policy</DialogTitle>
              <DialogDescription>Policy will be returned to Draft status.</DialogDescription>
            </DialogHeader>
            <div className="space-y-2 text-sm">
              <label className="text-xs font-medium">Rejection Reason *</label>
              <textarea className="w-full border rounded px-2 py-1 text-sm mt-1" rows={4} value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Explain why the policy was rejected…" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowReject(false)}>Cancel</Button>
              <Button variant="destructive" onClick={() => rejectMut.mutate()} disabled={rejectMut.isPending || !rejectReason.trim()}>
                {rejectMut.isPending ? "Rejecting…" : "Reject Policy"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

function useAuth23() {
  return (url: string, opts: RequestInit = {}) => {
    const token = sessionStorage.getItem("dotnetbot.jwt.session") || localStorage.getItem("dotnetbot.jwt") || "";
    return fetch(url, { ...opts, headers: { ...(opts.headers as Record<string,string> || {}), "Content-Type": "application/json", Authorization: `Bearer ${token}` } });
  };
}

function HipaaBreachNotificationSection({ canManage }: { canManage: boolean }) {
  const { toast } = useToast();
  const authFetch = useAuth23();
  const qc = useQueryClient();
  const BASE = "/api/organization/compliance/hipaa/breach-notifications";

  const invalidate = (id?: string) => {
    qc.invalidateQueries({ queryKey: ["hipaa23-list"] });
    qc.invalidateQueries({ queryKey: ["hipaa23-summary"] });
    qc.invalidateQueries({ queryKey: ["hipaa23-overdue"] });
    if (id) qc.invalidateQueries({ queryKey: ["hipaa23-detail", id] });
    else if (detailId) qc.invalidateQueries({ queryKey: ["hipaa23-detail", detailId] });
  };

  // ── Summary ──────────────────────────────────────────────────────────────────
  const summaryQ = useQuery<BreachSummary23>({
    queryKey: ["hipaa23-summary"],
    queryFn: async () => { const r = await authFetch(`${BASE}/summary`); if (!r.ok) throw new Error(); return r.json(); },
    staleTime: 0,
  });

  // ── Overdue ──────────────────────────────────────────────────────────────────
  const overdueQ = useQuery<{ items: BreachRecord23[]; total: number }>({
    queryKey: ["hipaa23-overdue"],
    queryFn: async () => { const r = await authFetch(`${BASE}/overdue`); if (!r.ok) throw new Error(); return r.json(); },
    staleTime: 0,
  });

  // ── List + filters ────────────────────────────────────────────────────────────
  const [page,         setPage]         = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [riskFilter,   setRiskFilter]   = useState("");
  const [notifFilter,  setNotifFilter]  = useState("");
  const [typeFilter,   setTypeFilter]   = useState("");
  const [baFilter,     setBaFilter]     = useState("");

  const listQ = useQuery<{ items: BreachRecord23[]; total: number }>({
    queryKey: ["hipaa23-list", page, statusFilter, riskFilter, notifFilter, typeFilter, baFilter],
    queryFn: async () => {
      const p = new URLSearchParams({ page: String(page), pageSize: "15" });
      if (statusFilter) p.set("status", statusFilter);
      if (riskFilter)   p.set("riskLevel", riskFilter);
      if (notifFilter)  p.set("notificationStatus", notifFilter);
      if (typeFilter)   p.set("breachType", typeFilter);
      if (baFilter)     p.set("businessAssociateInvolved", baFilter);
      const r = await authFetch(`${BASE}?${p}`);
      if (!r.ok) throw new Error(); return r.json();
    },
    staleTime: 0,
  });

  // ── Detail ────────────────────────────────────────────────────────────────────
  const [detailId, setDetailId] = useState<string | null>(null);
  const detailQ = useQuery<BreachRecord23>({
    queryKey: ["hipaa23-detail", detailId],
    queryFn: async () => { const r = await authFetch(`${BASE}/${detailId}`); if (!r.ok) throw new Error(); return r.json(); },
    enabled: !!detailId,
    staleTime: 0,
  });

  // ── Create ────────────────────────────────────────────────────────────────────
  const [showCreate, setShowCreate] = useState(false);
  const defaultForm = () => ({
    breachNumber: "auto",
    incidentReference: "", discoverySource: "IT Monitoring",
    breachType: "Unauthorized Access",
    discoveryDate: new Date().toISOString().slice(0, 10),
    affectedIndividualsCount: 0,
    phiDataCategories: "",
    individualsAffectedDescription: "",
    businessAssociateInvolved: false,
    businessAssociateName: "",
    assignedReviewer: "",
    reviewerNotes: "",
    correctiveActions: "",
  });
  const [form, setForm] = useState(defaultForm());
  const setF = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));
  const [errors,       setErrors]       = useState<Record<string,string>>({});
  const [assessErrors, setAssessErrors] = useState<Record<string,string>>({});
  const [decideErrors, setDecideErrors] = useState<Record<string,string>>({});

  const createMut = useMutation({
    mutationFn: async () => {
      const body: Record<string,any> = {
        ...form,
        discoveryDate: new Date(form.discoveryDate).toISOString(),
        affectedIndividualsCount: Number(form.affectedIndividualsCount),
      };
      if (!body.businessAssociateInvolved) delete body.businessAssociateName;
      const r = await authFetch(BASE, { method: "POST", body: JSON.stringify(body) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || e.error || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: (data: BreachRecord23) => {
      toast({ title: "Breach record created.", description: data.breachNumber });
      setShowCreate(false); setForm(defaultForm()); setErrors({});
      setDetailId(data.id);
      invalidate(data.id);
    },
    onError: (e: any) => { const s = extractServerErrors(e); if (s && Object.keys(s).length) { setErrors(s); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); } else toast({ title: "Unable to save", description: (e as Error)?.message, variant: "destructive" }); },
  });

  // ── Assess Risk dialog ────────────────────────────────────────────────────────
  const [showAssess, setShowAssess] = useState(false);
  const [assessForm, setAssessForm] = useState({ riskLevel: "High", probabilityOfCompromise: "High", riskAssessmentSummary: "", mitigationActionsTaken: "" });
  const assessMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/${detailId}/assess-risk`, { method: "PATCH", body: JSON.stringify(assessForm) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || e.message || e.error || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Risk assessment recorded." }); setShowAssess(false); setAssessErrors({}); invalidate(); },
    onError: (e: any) => { const s = extractServerErrors(e); if (s && Object.keys(s).length) { setAssessErrors(s); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); } else toast({ title: "Unable to save", description: (e as Error)?.message, variant: "destructive" }); },
  });

  // ── Notification Decision dialog ──────────────────────────────────────────────
  const [showDecide, setShowDecide] = useState(false);
  const [decideForm, setDecideForm] = useState({ notificationRequired: true, notificationDecisionReason: "", hhsNotificationRequired: false, mediaNotificationRequired: false });
  const decideMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/${detailId}/decide-notification`, { method: "PATCH", body: JSON.stringify(decideForm) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || e.message || e.error || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Notification decision recorded." }); setShowDecide(false); setDecideErrors({}); invalidate(); },
    onError: (e: any) => { const s = extractServerErrors(e); if (s && Object.keys(s).length) { setDecideErrors(s); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); } else toast({ title: "Unable to save", description: (e as Error)?.message, variant: "destructive" }); },
  });

  // ── Notify dialog (individuals / HHS / media) ─────────────────────────────────
  const [notifyTarget, setNotifyTarget] = useState<"notify-individuals"|"notify-hhs"|"notify-media"|null>(null);
  const [notifyForm, setNotifyForm] = useState({ notificationDate: new Date().toISOString().slice(0, 10), notes: "" });
  const notifyMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/${detailId}/${notifyTarget}`, {
        method: "PATCH",
        body: JSON.stringify({ notificationDate: new Date(notifyForm.notificationDate).toISOString(), notes: notifyForm.notes }),
      });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || e.message || e.error || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Notification recorded." }); setNotifyTarget(null); invalidate(); },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  // ── Complete dialog ────────────────────────────────────────────────────────────
  const [showComplete, setShowComplete] = useState(false);
  const [completeForm, setCompleteForm] = useState({ correctiveActions: "", reviewerNotes: "" });
  const completeMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/${detailId}/complete`, { method: "PATCH", body: JSON.stringify(completeForm) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || e.message || e.error || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Breach marked as completed." }); setShowComplete(false); invalidate(); },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  // ── Delete ─────────────────────────────────────────────────────────────────────
  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      const r = await authFetch(`${BASE}/${id}`, { method: "DELETE" });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || e.error || "Unable to complete action"); }
      // Backend returns 200 + JSON for this endpoint; consume it to satisfy the runtime.
      return r.json();
    },
    onSuccess: () => { toast({ title: "Breach record deleted." }); setDetailId(null); invalidate(); },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  const s    = summaryQ.data;
  const list = listQ.data;
  const detail = detailQ.data;

  return (
    <div className="border rounded-lg p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-sm">Breach Notification <span className="text-muted-foreground font-normal">§ 164.400–414</span></h3>
          <p className="text-xs text-muted-foreground mt-0.5">Breach assessment, risk analysis, notification workflows, and corrective actions</p>
        </div>
        {canManage && (
          <Button size="sm" onClick={() => setShowCreate(true)}>+ Breach Record</Button>
        )}
      </div>

      {/* Dashboard tiles */}
      {s && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[
            { label: "Total Breaches",        val: s.total,                   cls: "" },
            { label: "Open Assessments",      val: s.openAssessments,         cls: s.openAssessments > 0 ? "text-amber-600" : "" },
            { label: "High Risk",             val: s.highRisk,                cls: s.highRisk > 0 ? "text-red-600" : "" },
            { label: "Notification Required", val: s.notificationRequired,    cls: "" },
            { label: "Pending Notifications", val: s.notificationsPending,    cls: s.notificationsPending > 0 ? "text-amber-600" : "" },
            { label: "Completed",             val: s.notificationsCompleted,  cls: "text-green-700" },
            { label: "Overdue",               val: s.overdueNotifications,    cls: s.overdueNotifications > 0 ? "text-red-600 font-bold" : "" },
            { label: "Compliance",            val: `${s.compliancePercentage}%`, cls: s.compliancePercentage >= 90 ? "text-green-700" : "text-red-600" },
          ].map(({ label, val, cls }) => (
            <div key={label} className="border rounded p-2 text-center">
              <div className={`text-lg font-bold ${cls}`}>{val}</div>
              <div className="text-xs text-muted-foreground">{label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Overdue alert strip */}
      {overdueQ.data && overdueQ.data.total > 0 && (
        <div className="bg-red-50 border border-red-200 rounded p-3">
          <p className="text-xs font-semibold text-red-700 mb-1">⚠ Overdue Items ({overdueQ.data.total})</p>
          {overdueQ.data.items.slice(0, 3).map(b => (
            <div key={b.id} className="flex items-center gap-2 text-xs text-red-700 mb-0.5">
              <button className="font-mono underline" onClick={() => setDetailId(b.id)}>{b.breachNumber}</button>
              <span>{b.assessmentOverdue ? "Assessment overdue" : "Notification overdue"}</span>
              <span className="text-muted-foreground">({b.daysSinceDiscovery}d since discovery)</span>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        {[
          { label: "Status", value: statusFilter, set: setStatusFilter, opts: BREACH23_STATUSES },
          { label: "Risk", value: riskFilter, set: setRiskFilter, opts: BREACH23_RISK_LEVELS },
          { label: "Notif. Status", value: notifFilter, set: setNotifFilter, opts: BREACH23_NOTIF_STATUSES },
          { label: "Type", value: typeFilter, set: setTypeFilter, opts: BREACH23_TYPES },
        ].map(({ label, value, set, opts }) => (
          <select key={label} className="text-xs border rounded px-2 py-1"
            value={value} onChange={e => { set(e.target.value); setPage(1); }}>
            <option value="">All {label}</option>
            {opts.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        ))}
        <select className="text-xs border rounded px-2 py-1"
          value={baFilter} onChange={e => { setBaFilter(e.target.value); setPage(1); }}>
          <option value="">All BA</option>
          <option value="true">BA Involved</option>
          <option value="false">No BA</option>
        </select>
      </div>

      {/* List */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b text-muted-foreground">
              <th className="text-left py-1 pr-2">Breach #</th>
              <th className="text-left py-1 pr-2">Incident Ref</th>
              <th className="text-left py-1 pr-2">Discovered</th>
              <th className="text-left py-1 pr-2">Risk</th>
              <th className="text-right py-1 pr-2">Affected</th>
              <th className="text-left py-1 pr-2">Notif. Status</th>
              <th className="text-left py-1 pr-2">Status</th>
              <th className="text-left py-1 pr-2">Reviewer</th>
            </tr>
          </thead>
          <tbody>
            {list?.items.map(b => (
              <tr key={b.id}
                className={`border-b hover:bg-muted/40 cursor-pointer ${detailId === b.id ? "bg-muted/60" : ""}`}
                onClick={() => setDetailId(b.id)}>
                <td className="py-1 pr-2 font-mono whitespace-nowrap">
                  {(b.assessmentOverdue || b.notificationOverdue) && <span className="text-red-500 mr-1">⚠</span>}
                  {b.breachNumber}
                </td>
                <td className="py-1 pr-2">{b.incidentReference || "—"}</td>
                <td className="py-1 pr-2 whitespace-nowrap">{new Date(b.discoveryDate).toLocaleDateString()}</td>
                <td className={`py-1 pr-2 ${breach23RiskColor(b.riskLevel)}`}>{b.riskLevel || "—"}</td>
                <td className="py-1 pr-2 text-right">{b.affectedIndividualsCount.toLocaleString()}</td>
                <td className="py-1 pr-2">
                  <Badge variant={breach23NotifVariant(b.notificationStatus)} className="text-[10px]">{b.notificationStatus}</Badge>
                </td>
                <td className="py-1 pr-2">
                  <Badge variant={breach23StatusVariant(b.status)} className="text-[10px]">{b.status}</Badge>
                </td>
                <td className="py-1 pr-2">{b.assignedReviewer || "—"}</td>
              </tr>
            ))}
            {!listQ.isLoading && !list?.items.length && (
              <tr><td colSpan={8} className="py-4 text-center text-muted-foreground">No breach records found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {list && list.total > 15 && (
        <div className="flex gap-2 justify-end">
          <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
          <span className="text-xs self-center">{page} / {Math.ceil(list.total / 15)}</span>
          <Button size="sm" variant="outline" disabled={page >= Math.ceil(list.total / 15)} onClick={() => setPage(p => p + 1)}>Next</Button>
        </div>
      )}

      {/* Detail panel */}
      {detailId && detail && (
        <div className="border rounded p-4 space-y-3 bg-muted/30">
          <div className="flex items-start justify-between">
            <div>
              <p className="font-semibold text-sm font-mono">{detail.breachNumber}</p>
              <p className="text-xs text-muted-foreground">{detail.breachType} · {detail.daysSinceDiscovery}d since discovery</p>
            </div>
            <Button size="sm" variant="ghost" onClick={() => setDetailId(null)}>✕</Button>
          </div>

          {/* Overdue warnings */}
          {(detail.assessmentOverdue || detail.notificationOverdue) && (
            <div className="bg-red-50 border border-red-200 rounded p-2 text-xs text-red-700 space-y-0.5">
              {detail.assessmentOverdue && <p>⚠ Risk assessment is overdue (discovered {detail.daysSinceDiscovery}d ago)</p>}
              {detail.notificationOverdue && <p>⚠ Notification deadline passed (60-day window exceeded)</p>}
            </div>
          )}

          {/* Info grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
            {[
              { label: "Discovery Date",      val: new Date(detail.discoveryDate).toLocaleDateString() },
              { label: "Discovery Source",    val: detail.discoverySource || "—" },
              { label: "Reported Date",       val: detail.reportedDate ? new Date(detail.reportedDate).toLocaleDateString() : "—" },
              { label: "Affected Individuals",val: detail.affectedIndividualsCount.toLocaleString() },
              { label: "Risk Level",          val: detail.riskLevel || "Not assessed", cls: breach23RiskColor(detail.riskLevel) },
              { label: "Probability",         val: detail.probabilityOfCompromise || "—" },
              { label: "Risk Assessment",     val: detail.riskAssessmentStatus },
              { label: "Notification Status", val: detail.notificationStatus },
              { label: "Assigned Reviewer",   val: detail.assignedReviewer || "—" },
              { label: "BA Involved",         val: detail.businessAssociateInvolved ? (detail.businessAssociateName || "Yes") : "No" },
              { label: "Status",              val: detail.status },
              { label: "Resolution Date",     val: detail.resolutionDate ? new Date(detail.resolutionDate).toLocaleDateString() : "—" },
            ].map(({ label, val, cls = "" }) => (
              <div key={label}><p className="text-muted-foreground">{label}</p><p className={`font-medium ${cls}`}>{val}</p></div>
            ))}
          </div>

          {/* PHI Categories */}
          {detail.phiDataCategories && (
            <div className="bg-blue-50 border border-blue-100 rounded p-2 text-xs">
              <p className="text-muted-foreground mb-1">PHI Data Categories</p>
              <p>{detail.phiDataCategories}</p>
            </div>
          )}

          {/* Risk Assessment Summary */}
          {detail.riskAssessmentSummary && (
            <div className="border rounded p-2 text-xs space-y-1">
              <p className="font-medium">Risk Assessment Summary</p>
              <p className="text-muted-foreground">{detail.riskAssessmentSummary}</p>
              {detail.mitigationActionsTaken && <><p className="font-medium mt-1">Mitigation Actions</p><p className="text-muted-foreground">{detail.mitigationActionsTaken}</p></>}
            </div>
          )}

          {/* Notification Timeline */}
          <div className="border rounded p-2 text-xs space-y-1">
            <p className="font-medium">Notification Timeline</p>
            <div className="grid grid-cols-2 gap-1">
              <div>
                <span className="text-muted-foreground">Individuals: </span>
                {detail.individualNotificationDate
                  ? <span className="text-green-700">{new Date(detail.individualNotificationDate).toLocaleDateString()}</span>
                  : <span className={detail.notificationRequired ? "text-amber-600" : "text-muted-foreground"}>{detail.notificationRequired ? "Pending" : "Not required"}</span>}
              </div>
              <div>
                <span className="text-muted-foreground">HHS: </span>
                {detail.hhsNotificationDate
                  ? <span className="text-green-700">{new Date(detail.hhsNotificationDate).toLocaleDateString()}</span>
                  : <span className={detail.hhsNotificationRequired ? "text-amber-600" : "text-muted-foreground"}>{detail.hhsNotificationRequired ? "Pending" : "Not required"}</span>}
              </div>
              <div>
                <span className="text-muted-foreground">Media: </span>
                {detail.mediaNotificationDate
                  ? <span className="text-green-700">{new Date(detail.mediaNotificationDate).toLocaleDateString()}</span>
                  : <span className={detail.mediaNotificationRequired ? "text-amber-600" : "text-muted-foreground"}>{detail.mediaNotificationRequired ? "Pending" : "Not required"}</span>}
              </div>
              {detail.businessAssociateInvolved && (
                <div>
                  <span className="text-muted-foreground">BA: </span>
                  {detail.businessAssociateNotificationDate
                    ? <span className="text-green-700">{new Date(detail.businessAssociateNotificationDate).toLocaleDateString()}</span>
                    : <span className="text-amber-600">Pending</span>}
                </div>
              )}
            </div>
            {detail.daysUntilDeadline !== undefined && detail.daysUntilDeadline !== null && (
              <p className={`mt-1 font-medium ${detail.daysUntilDeadline < 0 ? "text-red-600" : detail.daysUntilDeadline <= 10 ? "text-amber-600" : "text-muted-foreground"}`}>
                {detail.daysUntilDeadline < 0
                  ? `Notification deadline passed ${Math.abs(detail.daysUntilDeadline)}d ago`
                  : `${detail.daysUntilDeadline}d until notification deadline`}
              </p>
            )}
          </div>

          {/* Corrective Actions */}
          {detail.correctiveActions && (
            <div className="bg-amber-50 border border-amber-100 rounded p-2 text-xs">
              <p className="font-medium mb-1">Corrective Actions</p>
              <p className="text-muted-foreground">{detail.correctiveActions}</p>
            </div>
          )}

          {/* Actions */}
          {canManage && (
            <div className="flex flex-wrap gap-2 pt-1">
              {detail.riskAssessmentStatus !== "Completed" && detail.status !== "Completed" && detail.status !== "Closed" && (
                <Button size="sm" variant="outline" onClick={() => {
                  setAssessForm({ riskLevel: "High", probabilityOfCompromise: "High", riskAssessmentSummary: "", mitigationActionsTaken: "" });
                  setShowAssess(true);
                }}>Assess Risk</Button>
              )}
              {detail.riskAssessmentStatus === "Completed" && detail.notificationStatus === "Pending" && (
                <Button size="sm" variant="outline" onClick={() => {
                  setDecideForm({ notificationRequired: true, notificationDecisionReason: "", hhsNotificationRequired: false, mediaNotificationRequired: false });
                  setShowDecide(true);
                }}>Decide Notification</Button>
              )}
              {detail.notificationRequired && !detail.individualNotificationDate && detail.notificationStatus !== "Pending" && (
                <Button size="sm" variant="outline" onClick={() => {
                  setNotifyForm({ notificationDate: new Date().toISOString().slice(0,10), notes: "" });
                  setNotifyTarget("notify-individuals");
                }}>Record Individual Notif.</Button>
              )}
              {detail.hhsNotificationRequired && !detail.hhsNotificationDate && (
                <Button size="sm" variant="outline" onClick={() => {
                  setNotifyForm({ notificationDate: new Date().toISOString().slice(0,10), notes: "" });
                  setNotifyTarget("notify-hhs");
                }}>Record HHS Notif.</Button>
              )}
              {detail.mediaNotificationRequired && !detail.mediaNotificationDate && (
                <Button size="sm" variant="outline" onClick={() => {
                  setNotifyForm({ notificationDate: new Date().toISOString().slice(0,10), notes: "" });
                  setNotifyTarget("notify-media");
                }}>Record Media Notif.</Button>
              )}
              {detail.status !== "Completed" && detail.status !== "Closed" && detail.notificationStatus !== "Pending" && (
                <Button size="sm" variant="outline" onClick={() => {
                  setCompleteForm({ correctiveActions: detail.correctiveActions || "", reviewerNotes: detail.reviewerNotes || "" });
                  setShowComplete(true);
                }}>Mark Complete</Button>
              )}
              <Button size="sm" variant="ghost" className="text-destructive" onClick={() => {
                if (confirm("Delete this breach record? This cannot be undone.")) deleteMut.mutate(detailId!);
              }}>Delete</Button>
            </div>
          )}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={v => { setShowCreate(v); if (!v) setErrors({}); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Create Breach Record</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Discovery Date *</label>
                <input type="date" className="w-full border rounded px-2 py-1 text-sm mt-0.5"
                  value={form.discoveryDate} onChange={e => setF("discoveryDate", e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Breach Type</label>
                <select className="w-full border rounded px-2 py-1 text-sm mt-0.5"
                  value={form.breachType} onChange={e => setF("breachType", e.target.value)}>
                  {BREACH23_TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Discovery Source</label>
                <select className="w-full border rounded px-2 py-1 text-sm mt-0.5"
                  value={form.discoverySource} onChange={e => setF("discoverySource", e.target.value)}>
                  {BREACH23_SOURCES.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Affected Individuals</label>
                <input type="number" min={0} className="w-full border rounded px-2 py-1 text-sm mt-0.5"
                  value={form.affectedIndividualsCount} onChange={e => setF("affectedIndividualsCount", e.target.value)} />
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Incident Reference</label>
              <input className="w-full border rounded px-2 py-1 text-sm mt-0.5"
                value={form.incidentReference} onChange={e => setF("incidentReference", e.target.value)}
                placeholder="e.g. INC-2026-001" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">PHI Data Categories * (comma-separated)</label>
              <input className={fieldCls("w-full border rounded px-2 py-1 text-sm mt-0.5", errors, "phiDataCategories")} aria-invalid={!!errors.phiDataCategories}
                value={form.phiDataCategories} onChange={e => { setF("phiDataCategories", e.target.value); clearErr(setErrors, "phiDataCategories"); }}
                placeholder="e.g. Medical Records, Demographics" />
              {fErr(errors, "phiDataCategories")}
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Individuals Affected Description</label>
              <textarea rows={2} className="w-full border rounded px-2 py-1 text-sm mt-0.5"
                value={form.individualsAffectedDescription} onChange={e => setF("individualsAffectedDescription", e.target.value)} />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="ba23inv" checked={form.businessAssociateInvolved}
                onChange={e => setF("businessAssociateInvolved", e.target.checked)} />
              <label htmlFor="ba23inv" className="text-xs">Business Associate Involved</label>
            </div>
            {form.businessAssociateInvolved && (
              <div>
                <label className="text-xs text-muted-foreground">Business Associate Name</label>
                <input className="w-full border rounded px-2 py-1 text-sm mt-0.5"
                  value={form.businessAssociateName} onChange={e => setF("businessAssociateName", e.target.value)} />
              </div>
            )}
            <div>
              <label className="text-xs text-muted-foreground">Assigned Reviewer</label>
              <input className="w-full border rounded px-2 py-1 text-sm mt-0.5"
                value={form.assignedReviewer} onChange={e => setF("assignedReviewer", e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Initial Notes</label>
              <textarea rows={2} className="w-full border rounded px-2 py-1 text-sm mt-0.5"
                value={form.correctiveActions} onChange={e => setF("correctiveActions", e.target.value)}
                placeholder="Initial corrective actions or notes…" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={() => { const errs: Record<string,string> = {}; required(errs, "phiDataCategories", form.phiDataCategories, "PHI Data Categories"); if (Object.keys(errs).length) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } setErrors({}); createMut.mutate(); }} disabled={createMut.isPending}>
              {createMut.isPending ? "Creating…" : "Create Record"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assess Risk dialog */}
      <Dialog open={showAssess} onOpenChange={v => { setShowAssess(v); if (!v) setAssessErrors({}); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Assess Risk</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Risk Level *</label>
                <select className="w-full border rounded px-2 py-1 text-sm mt-0.5"
                  value={assessForm.riskLevel} onChange={e => setAssessForm(f => ({ ...f, riskLevel: e.target.value }))}>
                  {BREACH23_RISK_LEVELS.map(l => <option key={l}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Probability of Compromise</label>
                <select className="w-full border rounded px-2 py-1 text-sm mt-0.5"
                  value={assessForm.probabilityOfCompromise} onChange={e => setAssessForm(f => ({ ...f, probabilityOfCompromise: e.target.value }))}>
                  {BREACH23_PROBABILITY.map(p => <option key={p}>{p}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Assessment Summary *</label>
              <textarea rows={3} className={fieldCls("w-full border rounded px-2 py-1 text-sm mt-0.5", assessErrors, "riskAssessmentSummary")} aria-invalid={!!assessErrors.riskAssessmentSummary}
                value={assessForm.riskAssessmentSummary}
                onChange={e => { setAssessForm(f => ({ ...f, riskAssessmentSummary: e.target.value })); clearErr(setAssessErrors, "riskAssessmentSummary"); }}
                placeholder="Describe the nature and extent of the breach…" />
              {fErr(assessErrors, "riskAssessmentSummary")}
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Mitigation Actions Taken</label>
              <textarea rows={2} className="w-full border rounded px-2 py-1 text-sm mt-0.5"
                value={assessForm.mitigationActionsTaken}
                onChange={e => setAssessForm(f => ({ ...f, mitigationActionsTaken: e.target.value }))}
                placeholder="Access revoked, credentials rotated, etc." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssess(false)}>Cancel</Button>
            <Button onClick={() => { const errs: Record<string,string> = {}; required(errs, "riskAssessmentSummary", assessForm.riskAssessmentSummary, "Assessment Summary"); if (Object.keys(errs).length) { setAssessErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } setAssessErrors({}); assessMut.mutate(); }} disabled={assessMut.isPending}>
              {assessMut.isPending ? "Saving…" : "Record Assessment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notification Decision dialog */}
      <Dialog open={showDecide} onOpenChange={v => { setShowDecide(v); if (!v) setDecideErrors({}); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Notification Decision</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-2">
              <input type="checkbox" id="notifReq23" checked={decideForm.notificationRequired}
                onChange={e => setDecideForm(f => ({ ...f, notificationRequired: e.target.checked }))} />
              <label htmlFor="notifReq23" className="text-xs font-medium">Notification Required</label>
            </div>
            {decideForm.notificationRequired && (
              <div className="pl-4 space-y-2">
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="hhs23" checked={decideForm.hhsNotificationRequired}
                    onChange={e => setDecideForm(f => ({ ...f, hhsNotificationRequired: e.target.checked }))} />
                  <label htmlFor="hhs23" className="text-xs">HHS Notification Required</label>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="media23" checked={decideForm.mediaNotificationRequired}
                    onChange={e => setDecideForm(f => ({ ...f, mediaNotificationRequired: e.target.checked }))} />
                  <label htmlFor="media23" className="text-xs">Media Notification Required (&gt;500 affected in a state)</label>
                </div>
              </div>
            )}
            <div>
              <label className="text-xs text-muted-foreground">Decision Reason *</label>
              <textarea rows={3} className={fieldCls("w-full border rounded px-2 py-1 text-sm mt-0.5", decideErrors, "notificationDecisionReason")} aria-invalid={!!decideErrors.notificationDecisionReason}
                value={decideForm.notificationDecisionReason}
                onChange={e => { setDecideForm(f => ({ ...f, notificationDecisionReason: e.target.value })); clearErr(setDecideErrors, "notificationDecisionReason"); }}
                placeholder="Explain the basis for the notification decision…" />
              {fErr(decideErrors, "notificationDecisionReason")}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDecide(false)}>Cancel</Button>
            <Button onClick={() => { const errs: Record<string,string> = {}; required(errs, "notificationDecisionReason", decideForm.notificationDecisionReason, "Decision Reason"); if (Object.keys(errs).length) { setDecideErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } setDecideErrors({}); decideMut.mutate(); }} disabled={decideMut.isPending}>
              {decideMut.isPending ? "Saving…" : "Record Decision"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notify dialog (individuals / HHS / media) */}
      <Dialog open={!!notifyTarget} onOpenChange={v => { if (!v) setNotifyTarget(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {notifyTarget === "notify-individuals" ? "Record Individual Notification"
               : notifyTarget === "notify-hhs" ? "Record HHS Notification"
               : "Record Media Notification"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <label className="text-xs text-muted-foreground">Notification Date *</label>
              <input type="date" className="w-full border rounded px-2 py-1 text-sm mt-0.5"
                value={notifyForm.notificationDate}
                onChange={e => setNotifyForm(f => ({ ...f, notificationDate: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Notes</label>
              <textarea rows={2} className="w-full border rounded px-2 py-1 text-sm mt-0.5"
                value={notifyForm.notes}
                onChange={e => setNotifyForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Reference number, method, etc." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNotifyTarget(null)}>Cancel</Button>
            <Button onClick={() => notifyMut.mutate()} disabled={notifyMut.isPending}>
              {notifyMut.isPending ? "Saving…" : "Record Notification"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complete dialog */}
      <Dialog open={showComplete} onOpenChange={setShowComplete}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Mark Breach Complete</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-xs text-muted-foreground">This will mark the breach as Completed and record the resolution date. Ensure all required notifications have been documented.</p>
            <div>
              <label className="text-xs text-muted-foreground">Corrective Actions</label>
              <textarea rows={3} className="w-full border rounded px-2 py-1 text-sm mt-0.5"
                value={completeForm.correctiveActions}
                onChange={e => setCompleteForm(f => ({ ...f, correctiveActions: e.target.value }))}
                placeholder="Final corrective actions implemented…" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Final Reviewer Notes</label>
              <textarea rows={2} className="w-full border rounded px-2 py-1 text-sm mt-0.5"
                value={completeForm.reviewerNotes}
                onChange={e => setCompleteForm(f => ({ ...f, reviewerNotes: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowComplete(false)}>Cancel</Button>
            <Button onClick={() => completeMut.mutate()} disabled={completeMut.isPending}>
              {completeMut.isPending ? "Completing…" : "Mark Complete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── HIPAA-22 — Business Associates (§ 164.308(b) / § 164.314) ───────────────

type BaSummary22 = {
  total: number; activeVendors: number; vendorsWithPHIAccess: number;
  activeBaas: number; missingBaas: number; expiredBaas: number;
  securityReviewsPending: number; highRiskVendors: number; compliancePercentage: number;
};

type BaVendor22 = {
  id: string; vendorName: string; vendorType?: string;
  contactName?: string; contactEmail?: string;
  servicesProvided?: string; phiAccessType?: string; dataCategoriesAccessed?: string;
  baaRequired: boolean; baaStatus: string;
  agreementReference?: string; agreementStartDate?: string;
  agreementEndDate?: string; renewalDate?: string;
  securityAssessmentCompleted: boolean; riskLevel?: string;
  vendorStatus: string;
  responsibilities?: string; permittedUses?: string; disclosureRestrictions?: string;
  incidentReportingRequired: boolean;
  lastReviewDate?: string; nextReviewDate?: string;
  notes?: string; createdAt: string; updatedAt: string;
  isAgreementExpired: boolean; isReviewOverdue: boolean;
  complianceIssues: string[];
};

const BA22_VENDOR_TYPES   = ["Cloud Provider","Software Provider","Healthcare Service Provider","Data Processing Vendor","Hosting Provider","Other"];
const BA22_PHI_TYPES      = ["No PHI Access","Limited PHI Access","Full PHI Access","Administrative Access"];
const BA22_BAA_STATUSES   = ["NotRequired","Pending","Active","Expired","Terminated"];
const BA22_VENDOR_STATUSES= ["Active","Suspended","Terminated"];
const BA22_RISK_LEVELS    = ["Low","Medium","High","Critical"];

const ISSUE_LABELS: Record<string,string> = {
  BAA_REQUIRED:           "BAA required but not configured",
  BAA_PENDING:            "BAA pending — not yet active",
  BAA_EXPIRED:            "BAA is expired",
  SECURITY_REVIEW_REQUIRED:"Security assessment not completed",
};

function ba22BaaVariant(s: string): "default"|"secondary"|"outline"|"destructive" {
  if (s === "Active") return "default";
  if (["Expired","Terminated"].includes(s)) return "destructive";
  if (s === "Pending") return "secondary";
  return "outline";
}

function ba22RiskColor(r?: string) {
  if (r === "Critical") return "text-red-700 font-bold";
  if (r === "High")     return "text-red-600 font-semibold";
  if (r === "Medium")   return "text-amber-700";
  return "text-muted-foreground";
}

function useAuth22() {
  return (url: string, opts: RequestInit = {}) => {
    const token = sessionStorage.getItem("dotnetbot.jwt.session") || localStorage.getItem("dotnetbot.jwt") || "";
    return fetch(url, { ...opts, headers: { ...(opts.headers as Record<string,string> || {}), "Content-Type": "application/json", Authorization: `Bearer ${token}` } });
  };
}

function HipaaBusinessAssociateSection({ canManage }: { canManage: boolean }) {
  const { toast } = useToast();
  const authFetch = useAuth22();
  const qc = useQueryClient();
  const BASE = "/api/organization/compliance/hipaa/business-associates";

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["hipaa22-list"] });
    qc.invalidateQueries({ queryKey: ["hipaa22-summary"] });
    qc.invalidateQueries({ queryKey: ["hipaa22-issues"] });
    if (detailId) qc.invalidateQueries({ queryKey: ["hipaa22-detail", detailId] });
  };

  // ── Summary ────────────────────────────────────────────────────────────────
  const summaryQ = useQuery<BaSummary22>({
    queryKey: ["hipaa22-summary"],
    queryFn: async () => { const r = await authFetch(`${BASE}/summary`); if (!r.ok) throw new Error(); return r.json(); },
    staleTime: 0,
  });

  // ── Issues ─────────────────────────────────────────────────────────────────
  const issuesQ = useQuery<{ items: { vendor: BaVendor22; issues: string[] }[]; total: number }>({
    queryKey: ["hipaa22-issues"],
    queryFn: async () => { const r = await authFetch(`${BASE}/issues`); if (!r.ok) throw new Error(); return r.json(); },
    staleTime: 0,
  });

  // ── List + filters ─────────────────────────────────────────────────────────
  const [page,          setPage]          = useState(1);
  const [typeFilter,    setTypeFilter]    = useState("");
  const [statusFilter,  setStatusFilter]  = useState("");
  const [baaFilter,     setBaaFilter]     = useState("");
  const [riskFilter,    setRiskFilter]    = useState("");
  const [phiFilter,     setPhiFilter]     = useState("");

  const listQ = useQuery<{ items: BaVendor22[]; total: number }>({
    queryKey: ["hipaa22-list", page, typeFilter, statusFilter, baaFilter, riskFilter, phiFilter],
    queryFn: async () => {
      const p = new URLSearchParams({ page: String(page), pageSize: "15" });
      if (typeFilter)   p.set("vendorType",   typeFilter);
      if (statusFilter) p.set("vendorStatus", statusFilter);
      if (baaFilter)    p.set("baaStatus",    baaFilter);
      if (riskFilter)   p.set("riskLevel",    riskFilter);
      if (phiFilter)    p.set("phiAccess",    phiFilter);
      const r = await authFetch(`${BASE}?${p}`);
      if (!r.ok) throw new Error(); return r.json();
    },
    staleTime: 0,
  });

  // ── Detail ─────────────────────────────────────────────────────────────────
  const [detailId, setDetailId] = useState<string | null>(null);
  const detailQ = useQuery<BaVendor22>({
    queryKey: ["hipaa22-detail", detailId],
    queryFn: async () => { const r = await authFetch(`${BASE}/${detailId}`); if (!r.ok) throw new Error(); return r.json(); },
    enabled: !!detailId,
    staleTime: 0,
  });

  // ── Create ─────────────────────────────────────────────────────────────────
  const [showCreate, setShowCreate] = useState(false);
  const defaultForm = () => ({
    vendorName: "", vendorType: "Cloud Provider",
    contactName: "", contactEmail: "",
    servicesProvided: "", phiAccessType: "No PHI Access", dataCategoriesAccessed: "",
    baaRequired: false, agreementReference: "",
    agreementStartDate: "", agreementEndDate: "", renewalDate: "",
    securityAssessmentCompleted: false, riskLevel: "Medium",
    responsibilities: "", permittedUses: "", disclosureRestrictions: "",
    incidentReportingRequired: true, notes: "",
  });
  const [form, setForm] = useState(defaultForm());
  const setF = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));
  const [errors, setErrors] = useState<Record<string,string>>({});

  const createMut = useMutation({
    mutationFn: async () => {
      const body: Record<string,any> = { ...form };
      ["agreementStartDate","agreementEndDate","renewalDate"].forEach(k => { if (!body[k]) delete body[k]; });
      const r = await authFetch(BASE, { method: "POST", body: JSON.stringify(body) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || e.error || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: (data: BaVendor22) => {
      toast({ title: "Business associate added." });
      setShowCreate(false); setForm(defaultForm()); setErrors({});
      setDetailId(data.id);
      invalidate();
    },
    onError: (e: any) => { const s = extractServerErrors(e); if (s && Object.keys(s).length) { setErrors(s); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); } else toast({ title: "Unable to save", description: (e as Error)?.message, variant: "destructive" }); },
  });

  // ── Status mutations ───────────────────────────────────────────────────────
  const statusMut = useMutation({
    mutationFn: async ({ id, action, notes }: { id: string; action: string; notes?: string }) => {
      const r = await authFetch(`${BASE}/${id}/${action}`, { method: "PATCH", body: JSON.stringify({ notes: notes ?? "" }) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || e.error || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: (_: any, vars: any) => {
      const labels: Record<string,string> = { activate: "Vendor activated.", suspend: "Vendor suspended.", terminate: "Vendor terminated." };
      toast({ title: labels[vars.action] ?? "Updated." });
      if (vars.action === "terminate") setDetailId(null);
      invalidate();
    },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  // ── Review dialog ──────────────────────────────────────────────────────────
  const [showReview, setShowReview] = useState(false);
  const [reviewForm, setReviewForm] = useState({ securityAssessmentCompleted: true, riskLevel: "Medium", nextReviewDate: "", notes: "" });

  const reviewMut = useMutation({
    mutationFn: async () => {
      const body: Record<string,any> = { ...reviewForm };
      if (!body.nextReviewDate) delete body.nextReviewDate;
      const r = await authFetch(`${BASE}/${detailId}/review`, { method: "PATCH", body: JSON.stringify(body) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || e.error || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Security review recorded." }); setShowReview(false); invalidate(); },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  // ── BAA dialog ─────────────────────────────────────────────────────────────
  const [showBaa, setShowBaa] = useState(false);
  const [baaForm, setBaaForm] = useState({ agreementReference: "", agreementStartDate: "", agreementEndDate: "", renewalDate: "", baaStatus: "Active" });

  const baaMut = useMutation({
    mutationFn: async () => {
      const body: Record<string,any> = { ...baaForm };
      ["agreementStartDate","agreementEndDate","renewalDate"].forEach(k => { if (!body[k]) delete body[k]; });
      const r = await authFetch(`${BASE}/${detailId}/baa`, { method: "PATCH", body: JSON.stringify(body) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || e.error || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "BAA updated." }); setShowBaa(false); invalidate(); },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  // ── Suspend / terminate dialog ─────────────────────────────────────────────
  const [actionId,    setActionId]    = useState<string | null>(null);
  const [actionType,  setActionType]  = useState<"suspend"|"terminate">("suspend");
  const [actionNote,  setActionNote]  = useState("");

  const s      = summaryQ.data;
  const items  = listQ.data?.items ?? [];
  const total  = listQ.data?.total ?? 0;
  const issues = issuesQ.data?.items ?? [];
  const pageSize = 15;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-teal-700" />
            Business Associates
          </CardTitle>
          <CardDescription>§ 164.308(b) / § 164.314 — Maintain vendor inventory, BAA lifecycle, PHI access controls, and security review cadence for all business associates.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">

          {/* Dashboard tiles */}
          {summaryQ.isLoading ? <Skeleton className="h-24 w-full" /> : s && (
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
              <div className="rounded-lg border p-3 text-center">
                <p className="text-2xl font-bold">{s.total}</p>
                <p className="text-xs text-muted-foreground">Total Vendors</p>
              </div>
              <div className="rounded-lg border p-3 text-center border-teal-300 bg-teal-50">
                <p className="text-2xl font-bold text-teal-700">{s.activeVendors}</p>
                <p className="text-xs text-muted-foreground">Active</p>
              </div>
              <div className="rounded-lg border p-3 text-center">
                <p className="text-2xl font-bold">{s.vendorsWithPHIAccess}</p>
                <p className="text-xs text-muted-foreground">PHI Access</p>
              </div>
              <div className="rounded-lg border p-3 text-center border-teal-300 bg-teal-50">
                <p className="text-2xl font-bold text-teal-700">{s.activeBaas}</p>
                <p className="text-xs text-muted-foreground">Active BAAs</p>
              </div>
              <div className={`rounded-lg border p-3 text-center ${s.missingBaas > 0 ? "border-red-400 bg-red-50" : ""}`}>
                <p className={`text-2xl font-bold ${s.missingBaas > 0 ? "text-red-600" : ""}`}>{s.missingBaas}</p>
                <p className="text-xs text-muted-foreground">Missing BAAs</p>
              </div>
              <div className={`rounded-lg border p-3 text-center ${s.expiredBaas > 0 ? "border-red-400 bg-red-50" : ""}`}>
                <p className={`text-2xl font-bold ${s.expiredBaas > 0 ? "text-red-600" : ""}`}>{s.expiredBaas}</p>
                <p className="text-xs text-muted-foreground">Expired BAAs</p>
              </div>
              <div className={`rounded-lg border p-3 text-center ${s.securityReviewsPending > 0 ? "border-amber-300 bg-amber-50" : ""}`}>
                <p className="text-2xl font-bold">{s.securityReviewsPending}</p>
                <p className="text-xs text-muted-foreground">Reviews Pending</p>
              </div>
              <div className={`rounded-lg border p-3 text-center ${s.highRiskVendors > 0 ? "border-red-300 bg-red-50" : ""}`}>
                <p className={`text-2xl font-bold ${s.highRiskVendors > 0 ? "text-red-600" : ""}`}>{s.highRiskVendors}</p>
                <p className="text-xs text-muted-foreground">High Risk</p>
              </div>
              <div className={`rounded-lg border p-3 text-center ${s.compliancePercentage < 100 ? "border-amber-400 bg-amber-50" : "border-teal-400 bg-teal-50"}`}>
                <p className="text-2xl font-bold text-teal-700">{s.compliancePercentage}%</p>
                <p className="text-xs text-muted-foreground">Compliance</p>
              </div>
            </div>
          )}

          {/* Compliance issues panel */}
          {issues.length > 0 && (
            <div className="rounded-lg border border-red-300 bg-red-50 p-3 space-y-2">
              <div className="flex items-center gap-2 text-sm font-semibold text-red-700">
                <AlertTriangle className="h-4 w-4" />
                {issues.length} vendor{issues.length > 1 ? "s" : ""} with compliance issues
              </div>
              {issues.slice(0, 3).map(({ vendor, issues: vi }) => (
                <div key={vendor.id} className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="font-medium">{vendor.vendorName}</span>
                  {vi.map(code => (
                    <Badge key={code} variant="destructive" className="text-xs">{ISSUE_LABELS[code] ?? code}</Badge>
                  ))}
                </div>
              ))}
              {issues.length > 3 && <p className="text-xs text-red-600">…and {issues.length - 3} more.</p>}
            </div>
          )}

          {/* Toolbar */}
          <div className="flex flex-wrap gap-2 items-end">
            <div>
              <Label className="text-xs">Type</Label>
              <select className="border rounded px-2 py-1 text-sm h-8" value={typeFilter}
                onChange={e => { setTypeFilter(e.target.value); setPage(1); }}>
                <option value="">All</option>
                {BA22_VENDOR_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <select className="border rounded px-2 py-1 text-sm h-8" value={statusFilter}
                onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
                <option value="">All</option>
                {BA22_VENDOR_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">BAA Status</Label>
              <select className="border rounded px-2 py-1 text-sm h-8" value={baaFilter}
                onChange={e => { setBaaFilter(e.target.value); setPage(1); }}>
                <option value="">All</option>
                {BA22_BAA_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Risk</Label>
              <select className="border rounded px-2 py-1 text-sm h-8" value={riskFilter}
                onChange={e => { setRiskFilter(e.target.value); setPage(1); }}>
                <option value="">All</option>
                {BA22_RISK_LEVELS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">PHI Access</Label>
              <select className="border rounded px-2 py-1 text-sm h-8" value={phiFilter}
                onChange={e => { setPhiFilter(e.target.value); setPage(1); }}>
                <option value="">All</option>
                {BA22_PHI_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            {canManage && (
              <Button className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2 h-8 ml-auto"
                onClick={() => setShowCreate(true)}>
                <Plus className="h-4 w-4" /> Add Business Associate
              </Button>
            )}
          </div>

          {/* List */}
          {listQ.isLoading ? <Skeleton className="h-40 w-full" /> : items.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No business associates found. Click "Add Business Associate" to start tracking vendors.</p>
          ) : (
            <div className="space-y-2">
              {items.map(v => (
                <div key={v.id}
                  className={`flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer hover:bg-muted/40 transition-colors ${v.complianceIssues.length > 0 ? "border-red-300 bg-red-50/20" : ""}`}
                  onClick={() => setDetailId(v.id)}>
                  <div className="flex-1 min-w-0">
                    <span className="font-medium">{v.vendorName}</span>
                    {v.vendorType && <span className="ml-2 text-xs text-muted-foreground">{v.vendorType}</span>}
                  </div>
                  <span className="text-xs text-muted-foreground hidden sm:block">{v.phiAccessType ?? "No PHI Access"}</span>
                  <Badge variant={ba22BaaVariant(v.baaStatus)} className="text-xs">{v.baaStatus}</Badge>
                  <span className={`text-xs ${ba22RiskColor(v.riskLevel)}`}>{v.riskLevel ?? "—"}</span>
                  <Badge variant={v.vendorStatus === "Active" ? "default" : "destructive"} className="text-xs">{v.vendorStatus}</Badge>
                  {v.isAgreementExpired && <span className="text-xs text-red-600 font-medium">BAA expired</span>}
                  {v.isReviewOverdue    && <span className="text-xs text-amber-700 font-medium">Review overdue</span>}
                  {v.complianceIssues.length > 0 && <AlertTriangle className="h-3.5 w-3.5 text-red-500 shrink-0" />}
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {total > pageSize && (
            <div className="flex items-center gap-2 justify-center">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
              <span className="text-sm text-muted-foreground">Page {page} of {Math.ceil(total / pageSize)}</span>
              <Button variant="outline" size="sm" disabled={page * pageSize >= total} onClick={() => setPage(p => p + 1)}>Next</Button>
            </div>
          )}

          {/* Detail panel */}
          {detailId && detailQ.data && (() => {
            const v = detailQ.data;
            const isTerminal = v.vendorStatus === "Terminated";
            return (
              <div className="rounded-lg border p-4 space-y-4 bg-muted/20">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <h4 className="font-semibold text-base">{v.vendorName}</h4>
                    {v.vendorType && <p className="text-xs text-muted-foreground">{v.vendorType}</p>}
                  </div>
                  <div className="flex gap-2 items-center flex-wrap">
                    <Badge variant={v.vendorStatus === "Active" ? "default" : "destructive"}>{v.vendorStatus}</Badge>
                    <Badge variant={ba22BaaVariant(v.baaStatus)}>BAA: {v.baaStatus}</Badge>
                    {v.isAgreementExpired && <Badge variant="destructive" className="text-xs">Agreement Expired</Badge>}
                    {v.isReviewOverdue    && <Badge variant="outline" className="text-xs border-amber-400 text-amber-700">Review Overdue</Badge>}
                    <Button variant="ghost" size="sm" onClick={() => setDetailId(null)}>✕</Button>
                  </div>
                </div>

                {/* Compliance issues */}
                {v.complianceIssues.length > 0 && (
                  <div className="rounded bg-red-50 border border-red-300 p-2 text-xs space-y-1">
                    <p className="font-semibold text-red-700">Compliance Issues</p>
                    {v.complianceIssues.map(code => (
                      <p key={code} className="flex items-center gap-1"><AlertTriangle className="h-3 w-3" />{ISSUE_LABELS[code] ?? code}</p>
                    ))}
                  </div>
                )}

                {/* Info grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                  <div><p className="text-muted-foreground">PHI Access</p><p className="font-medium">{v.phiAccessType ?? "—"}</p></div>
                  <div><p className="text-muted-foreground">Risk Level</p><p className={`font-medium ${ba22RiskColor(v.riskLevel)}`}>{v.riskLevel ?? "—"}</p></div>
                  <div><p className="text-muted-foreground">Contact</p><p className="font-medium">{v.contactName ?? "—"}{v.contactEmail ? ` <${v.contactEmail}>` : ""}</p></div>
                  {v.agreementReference && <div><p className="text-muted-foreground">BAA Reference</p><p className="font-medium">{v.agreementReference}</p></div>}
                  {v.agreementEndDate   && <div><p className="text-muted-foreground">BAA Expires</p><p className={`font-medium ${v.isAgreementExpired ? "text-red-600" : ""}`}>{new Date(v.agreementEndDate).toLocaleDateString()}</p></div>}
                  {v.renewalDate        && <div><p className="text-muted-foreground">Renewal Date</p><p className="font-medium">{new Date(v.renewalDate).toLocaleDateString()}</p></div>}
                  <div><p className="text-muted-foreground">Security Assessment</p><p className={`font-medium ${v.securityAssessmentCompleted ? "text-teal-700" : "text-red-600"}`}>{v.securityAssessmentCompleted ? "Completed" : "Not completed"}</p></div>
                  {v.lastReviewDate     && <div><p className="text-muted-foreground">Last Review</p><p className="font-medium">{new Date(v.lastReviewDate).toLocaleDateString()}</p></div>}
                  {v.nextReviewDate     && <div><p className="text-muted-foreground">Next Review</p><p className={`font-medium ${v.isReviewOverdue ? "text-amber-700" : ""}`}>{new Date(v.nextReviewDate).toLocaleDateString()}</p></div>}
                  <div><p className="text-muted-foreground">Incident Reporting</p><p className="font-medium">{v.incidentReportingRequired ? "Required" : "Not required"}</p></div>
                </div>

                {v.servicesProvided && (
                  <div className="rounded bg-blue-50 border border-blue-200 p-2 text-xs">
                    <p className="font-semibold text-blue-700 mb-0.5">Services Provided</p>
                    <p>{v.servicesProvided}</p>
                  </div>
                )}

                {(v.dataCategoriesAccessed || v.responsibilities) && (
                  <div className="rounded bg-muted p-2 text-xs space-y-1">
                    {v.dataCategoriesAccessed && <p><span className="text-muted-foreground">Data categories: </span>{v.dataCategoriesAccessed}</p>}
                    {v.responsibilities       && <p><span className="text-muted-foreground">Responsibilities: </span>{v.responsibilities}</p>}
                    {v.permittedUses          && <p><span className="text-muted-foreground">Permitted uses: </span>{v.permittedUses}</p>}
                    {v.disclosureRestrictions && <p><span className="text-muted-foreground">Restrictions: </span>{v.disclosureRestrictions}</p>}
                  </div>
                )}

                {v.notes && <p className="text-xs text-muted-foreground italic">{v.notes}</p>}

                <LinkedSourceRow entityType="HipaaBusinessAssociate" entityId={v.id} canManage={canManage} />

                {/* Actions */}
                {canManage && (
                  <div className="flex flex-wrap gap-2">
                    {!isTerminal && (
                      <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-1"
                        onClick={() => { setBaaForm({ agreementReference: v.agreementReference ?? "", agreementStartDate: "", agreementEndDate: "", renewalDate: "", baaStatus: "Active" }); setShowBaa(true); }}>
                        <FileText className="h-3.5 w-3.5" /> {v.baaStatus === "Active" ? "Update BAA" : "Add BAA"}
                      </Button>
                    )}
                    {!isTerminal && (
                      <Button size="sm" variant="outline" className="gap-1"
                        onClick={() => { setReviewForm({ securityAssessmentCompleted: v.securityAssessmentCompleted, riskLevel: v.riskLevel ?? "Medium", nextReviewDate: "", notes: "" }); setShowReview(true); }}>
                        <CheckCircle2 className="h-3.5 w-3.5" /> Security Review
                      </Button>
                    )}
                    {v.vendorStatus === "Suspended" && (
                      <Button size="sm" variant="outline" className="gap-1"
                        onClick={() => statusMut.mutate({ id: v.id, action: "activate" })} disabled={statusMut.isPending}>
                        Activate
                      </Button>
                    )}
                    {v.vendorStatus === "Active" && (
                      <Button size="sm" variant="outline" className="border-amber-400 text-amber-700 hover:bg-amber-50 gap-1"
                        onClick={() => { setActionId(v.id); setActionType("suspend"); setActionNote(""); }}>
                        Suspend
                      </Button>
                    )}
                    {!isTerminal && (
                      <Button size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-50 gap-1"
                        onClick={() => { setActionId(v.id); setActionType("terminate"); setActionNote(""); }}>
                        Terminate
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={v => { setShowCreate(v); if (!v) setErrors({}); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Business Associate</DialogTitle>
            <DialogDescription>Register a vendor that handles PHI on behalf of your organization under § 164.308(b).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Vendor Name *</Label><Input className={fieldCls("", errors, "vendorName")} aria-invalid={!!errors.vendorName} value={form.vendorName} onChange={e => { setF("vendorName", e.target.value); clearErr(setErrors, "vendorName"); }} placeholder="ACME Cloud Services Inc." />{fErr(errors, "vendorName")}</div>
              <div>
                <Label>Vendor Type</Label>
                <select className="border rounded px-2 py-1 text-sm w-full" value={form.vendorType} onChange={e => setF("vendorType", e.target.value)}>
                  {BA22_VENDOR_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <Label>PHI Access Type</Label>
                <select className="border rounded px-2 py-1 text-sm w-full" value={form.phiAccessType}
                  onChange={e => { setF("phiAccessType", e.target.value); if (e.target.value !== "No PHI Access") setF("baaRequired", true); }}>
                  {BA22_PHI_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div><Label>Risk Level</Label>
                <select className="border rounded px-2 py-1 text-sm w-full" value={form.riskLevel} onChange={e => setF("riskLevel", e.target.value)}>
                  {BA22_RISK_LEVELS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div><Label>Contact Name</Label><Input value={form.contactName} onChange={e => setF("contactName", e.target.value)} /></div>
              <div><Label>Contact Email</Label><Input type="email" value={form.contactEmail} onChange={e => setF("contactEmail", e.target.value)} /></div>
            </div>

            <div><Label>Services Provided</Label><Textarea rows={2} value={form.servicesProvided} onChange={e => setF("servicesProvided", e.target.value)} placeholder="Cloud storage, data processing, billing services…" /></div>
            <div><Label>Data Categories Accessed</Label><Input value={form.dataCategoriesAccessed} onChange={e => setF("dataCategoriesAccessed", e.target.value)} placeholder="Medical Records,Demographics,Billing" /></div>

            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.baaRequired} onChange={e => setF("baaRequired", e.target.checked)} />
                BAA Required
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.securityAssessmentCompleted} onChange={e => setF("securityAssessmentCompleted", e.target.checked)} />
                Security Assessment Completed
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.incidentReportingRequired} onChange={e => setF("incidentReportingRequired", e.target.checked)} />
                Incident Reporting Required
              </label>
            </div>

            {form.baaRequired && (
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Agreement Reference</Label><Input value={form.agreementReference} onChange={e => setF("agreementReference", e.target.value)} placeholder="BAA-2026-001" /></div>
                <div><Label>Start Date</Label><Input type="date" value={form.agreementStartDate} onChange={e => setF("agreementStartDate", e.target.value)} /></div>
                <div><Label>End Date</Label><Input type="date" value={form.agreementEndDate} onChange={e => setF("agreementEndDate", e.target.value)} /></div>
                <div><Label>Renewal Date</Label><Input type="date" value={form.renewalDate} onChange={e => setF("renewalDate", e.target.value)} /></div>
              </div>
            )}

            <div><Label>Responsibilities</Label><Textarea rows={2} value={form.responsibilities} onChange={e => setF("responsibilities", e.target.value)} placeholder="Secure storage of ePHI per BAA terms…" /></div>
            <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={e => setF("notes", e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2"
              onClick={() => { const errs: Record<string,string> = {}; required(errs, "vendorName", form.vendorName, "Vendor Name"); if (Object.keys(errs).length) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } setErrors({}); createMut.mutate(); }} disabled={createMut.isPending}>
              <Plus className="h-4 w-4" />{createMut.isPending ? "Adding…" : "Add Business Associate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suspend / Terminate dialog */}
      <Dialog open={!!actionId} onOpenChange={o => { if (!o) setActionId(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{actionType === "suspend" ? "Suspend" : "Terminate"} Vendor</DialogTitle>
            <DialogDescription>
              {actionType === "suspend"
                ? "Suspended vendors cannot access PHI until reactivated."
                : "Termination is permanent. The vendor record is retained for audit purposes."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div><Label>Reason</Label><Textarea rows={3} value={actionNote} onChange={e => setActionNote(e.target.value)} placeholder={actionType === "suspend" ? "Pending security review…" : "Contract ended…"} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionId(null)}>Cancel</Button>
            <Button variant="destructive"
              onClick={() => actionId && statusMut.mutate({ id: actionId, action: actionType, notes: actionNote })}
              disabled={statusMut.isPending}>
              {statusMut.isPending ? "Processing…" : actionType === "suspend" ? "Suspend Vendor" : "Terminate Vendor"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Security review dialog */}
      <Dialog open={showReview} onOpenChange={o => { if (!o) setShowReview(false); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Security Review</DialogTitle>
            <DialogDescription>Update security assessment status and schedule the next review.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <Label>Risk Level</Label>
              <select className="border rounded px-2 py-1 text-sm w-full" value={reviewForm.riskLevel}
                onChange={e => setReviewForm(f => ({ ...f, riskLevel: e.target.value }))}>
                {BA22_RISK_LEVELS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={reviewForm.securityAssessmentCompleted}
                onChange={e => setReviewForm(f => ({ ...f, securityAssessmentCompleted: e.target.checked }))} />
              Security Assessment Completed
            </label>
            <div><Label>Next Review Date</Label><Input type="date" value={reviewForm.nextReviewDate} onChange={e => setReviewForm(f => ({ ...f, nextReviewDate: e.target.value }))} /></div>
            <div><Label>Notes</Label><Textarea rows={2} value={reviewForm.notes} onChange={e => setReviewForm(f => ({ ...f, notes: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReview(false)}>Cancel</Button>
            <Button className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2"
              onClick={() => reviewMut.mutate()} disabled={reviewMut.isPending}>
              <CheckCircle2 className="h-4 w-4" />{reviewMut.isPending ? "Saving…" : "Record Review"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* BAA dialog */}
      <Dialog open={showBaa} onOpenChange={o => { if (!o) setShowBaa(false); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Business Associate Agreement</DialogTitle>
            <DialogDescription>Record BAA details. Setting status to Active marks the agreement as in force.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div><Label>Agreement Reference</Label><Input value={baaForm.agreementReference} onChange={e => setBaaForm(f => ({ ...f, agreementReference: e.target.value }))} placeholder="BAA-2026-001" /></div>
            <div>
              <Label>BAA Status</Label>
              <select className="border rounded px-2 py-1 text-sm w-full" value={baaForm.baaStatus}
                onChange={e => setBaaForm(f => ({ ...f, baaStatus: e.target.value }))}>
                {["Pending","Active","Expired","Terminated"].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Start Date</Label><Input type="date" value={baaForm.agreementStartDate} onChange={e => setBaaForm(f => ({ ...f, agreementStartDate: e.target.value }))} /></div>
              <div><Label>End Date</Label><Input type="date" value={baaForm.agreementEndDate} onChange={e => setBaaForm(f => ({ ...f, agreementEndDate: e.target.value }))} /></div>
              <div><Label>Renewal Date</Label><Input type="date" value={baaForm.renewalDate} onChange={e => setBaaForm(f => ({ ...f, renewalDate: e.target.value }))} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBaa(false)}>Cancel</Button>
            <Button className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2"
              onClick={() => baaMut.mutate()} disabled={baaMut.isPending}>
              <FileText className="h-4 w-4" />{baaMut.isPending ? "Saving…" : "Update BAA"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── HIPAA-21 — Accounting of Disclosures (§ 164.528) ─────────────────────────

type DaSummary21 = {
  total: number; accountingEligible: number; excludedDisclosures: number;
  pendingReview: number; expiredRecords: number; exceptionsApplied: number;
  recentDisclosures: number; compliancePercentage: number;
};

type DaRecord21 = {
  id: string; disclosureNumber: string;
  individualName?: string; individualId?: string;
  requesterName?: string; requesterEmail?: string;
  recipientName: string; recipientOrganization?: string; recipientType?: string;
  disclosureDate: string; disclosureMethod?: string;
  disclosurePurpose: string; disclosureType: string; phiDataCategory: string;
  recordTypesDisclosed?: string; dataElementsDisclosed?: string;
  authorizationRequired: boolean; authorizationReference?: string;
  exceptionApplied: boolean; exceptionType?: string; exceptionReason?: string;
  accountingEligible: boolean; accountingExcludedReason?: string;
  retentionPeriod: number; retentionExpiryDate?: string;
  status: string; reviewedBy?: string; reviewDate?: string;
  notes?: string; createdAt: string; updatedAt: string;
  isRetentionExpired: boolean;
};

const DA21_STATUSES  = ["Recorded","UnderReview","Approved","Rejected","Expired"];
const DA21_TERMINAL  = ["Approved","Rejected","Expired"];
const DA21_TYPES     = ["Treatment","Payment","Healthcare Operations","Public Health","Legal Requirement","Law Enforcement","Research","Individual Request","Other"];
const DA21_PHI_CATS  = ["Demographics","Contact Information","Medical Records","Diagnosis","Medication","Laboratory Results","Billing Information","Insurance Information","Clinical Notes","Other"];
const DA21_METHODS   = ["Electronic","Fax","Mail","InPerson","Other"];
const DA21_RECIPIENT_TYPES = ["Government","Healthcare","Legal","Research","Individual","Other"];
const DA21_EXCEPTION_TYPES = ["TreatmentException","PaymentException","HealthcareOperationsException","LegalRestriction","Other"];

function da21StatusVariant(s: string): "default"|"secondary"|"outline"|"destructive" {
  if (s === "Approved") return "default";
  if (["Rejected","Expired"].includes(s)) return "destructive";
  if (s === "Recorded") return "outline";
  return "secondary";
}

function useAuth21() {
  return (url: string, opts: RequestInit = {}) => {
    const token = sessionStorage.getItem("dotnetbot.jwt.session") || localStorage.getItem("dotnetbot.jwt") || "";
    return fetch(url, { ...opts, headers: { ...(opts.headers as Record<string,string> || {}), "Content-Type": "application/json", Authorization: `Bearer ${token}` } });
  };
}

function HipaaDisclosureAccountingSection({ canManage }: { canManage: boolean }) {
  const { toast } = useToast();
  const authFetch = useAuth21();
  const qc = useQueryClient();
  const BASE = "/api/organization/compliance/hipaa/disclosures";

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["hipaa21-list"] });
    qc.invalidateQueries({ queryKey: ["hipaa21-summary"] });
    if (detailId) qc.invalidateQueries({ queryKey: ["hipaa21-detail", detailId] });
  };

  // ── Summary ────────────────────────────────────────────────────────────────
  const summaryQ = useQuery<DaSummary21>({
    queryKey: ["hipaa21-summary"],
    queryFn: async () => { const r = await authFetch(`${BASE}/summary`); if (!r.ok) throw new Error(); return r.json(); },
    staleTime: 0,
  });

  // ── List + filters ─────────────────────────────────────────────────────────
  const [page, setPage] = useState(1);
  const [typeFilter,   setTypeFilter]   = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [catFilter,    setCatFilter]    = useState("");
  const [eligFilter,   setEligFilter]   = useState("");
  const [excFilter,    setExcFilter]    = useState("");

  const listQ = useQuery<{ items: DaRecord21[]; total: number }>({
    queryKey: ["hipaa21-list", page, typeFilter, statusFilter, catFilter, eligFilter, excFilter],
    queryFn: async () => {
      const p = new URLSearchParams({ page: String(page), pageSize: "15" });
      if (typeFilter)   p.set("disclosureType", typeFilter);
      if (statusFilter) p.set("status", statusFilter);
      if (catFilter)    p.set("phiCategory", catFilter);
      if (eligFilter)   p.set("accountingEligible", eligFilter);
      if (excFilter)    p.set("exceptionApplied", excFilter);
      const r = await authFetch(`${BASE}?${p}`);
      if (!r.ok) throw new Error(); return r.json();
    },
    staleTime: 0,
  });

  // ── Detail ─────────────────────────────────────────────────────────────────
  const [detailId, setDetailId] = useState<string | null>(null);
  const detailQ = useQuery<DaRecord21>({
    queryKey: ["hipaa21-detail", detailId],
    queryFn: async () => { const r = await authFetch(`${BASE}/${detailId}`); if (!r.ok) throw new Error(); return r.json(); },
    enabled: !!detailId,
    staleTime: 0,
  });

  // ── Create ─────────────────────────────────────────────────────────────────
  const [showCreate, setShowCreate] = useState(false);
  const defaultForm = () => ({
    recipientName: "", recipientOrganization: "", recipientType: "Government",
    disclosurePurpose: "", disclosureType: "Public Health", phiDataCategory: "Diagnosis",
    disclosureMethod: "Electronic", disclosureDate: "",
    recordTypesDisclosed: "", dataElementsDisclosed: "",
    authorizationRequired: false, authorizationReference: "",
    exceptionApplied: false, exceptionType: "", exceptionReason: "",
    accountingEligible: true, accountingExcludedReason: "",
    retentionPeriod: 6, notes: "",
  });
  const [form, setForm] = useState(defaultForm());
  const setF = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));
  const [errors, setErrors] = useState<Record<string,string>>({});

  const createMut = useMutation({
    mutationFn: async () => {
      const body: Record<string,any> = { ...form };
      if (!body.disclosureDate) delete body.disclosureDate;
      const r = await authFetch(BASE, { method: "POST", body: JSON.stringify(body) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || e.error || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Disclosure record created." }); setShowCreate(false); setForm(defaultForm()); setErrors({}); invalidate(); },
    onError: (e: any) => { const s = extractServerErrors(e); if (s && Object.keys(s).length) { setErrors(s); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); } else toast({ title: "Unable to save", description: (e as Error)?.message, variant: "destructive" }); },
  });

  // ── Review / Approve / Reject ──────────────────────────────────────────────
  const reviewMut = useMutation({
    mutationFn: async (id: string) => {
      const r = await authFetch(`${BASE}/${id}/review`, { method: "PATCH", body: JSON.stringify({ notes: "" }) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || e.error || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Record under review." }); invalidate(); },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  const approveMut = useMutation({
    mutationFn: async (id: string) => {
      const r = await authFetch(`${BASE}/${id}/approve`, { method: "PATCH", body: JSON.stringify({ notes: "" }) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || e.error || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Disclosure approved for accounting report." }); invalidate(); },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  const rejectMut = useMutation({
    mutationFn: async ({ id, notes }: { id: string; notes: string }) => {
      const r = await authFetch(`${BASE}/${id}/reject`, { method: "PATCH", body: JSON.stringify({ notes }) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || e.error || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Disclosure rejected." }); setRejectId(null); invalidate(); },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  // ── Exception ──────────────────────────────────────────────────────────────
  const [showException, setShowException] = useState(false);
  const [exceptionId,   setExceptionId]   = useState<string | null>(null);
  const [exceptionForm, setExceptionForm] = useState({ exceptionType: "TreatmentException", exceptionReason: "", accountingExcludedReason: "" });

  const exceptionMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/${exceptionId}/mark-exception`, { method: "PATCH", body: JSON.stringify(exceptionForm) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || e.error || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Exception recorded." }); setShowException(false); setExceptionId(null); invalidate(); },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  // ── Reject dialog ──────────────────────────────────────────────────────────
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [rejectNote, setRejectNote] = useState("");

  // ── Accounting report ──────────────────────────────────────────────────────
  const [showReport, setShowReport] = useState(false);
  const reportQ = useQuery({
    queryKey: ["hipaa21-report"],
    queryFn: async () => { const r = await authFetch(`${BASE}/accounting-report`); if (!r.ok) throw new Error(); return r.json(); },
    enabled: showReport,
    staleTime: 0,
  });

  const s     = summaryQ.data;
  const items = listQ.data?.items ?? [];
  const total = listQ.data?.total ?? 0;
  const pageSize = 15;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-teal-700" />
            Accounting of Disclosures
          </CardTitle>
          <CardDescription>§ 164.528 — Maintain accounting records of applicable PHI disclosures. Track recipient, purpose, exception status, accounting eligibility, and 6-year retention requirements.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">

          {/* Dashboard tiles */}
          {summaryQ.isLoading ? <Skeleton className="h-24 w-full" /> : s && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="rounded-lg border p-3 text-center">
                <p className="text-2xl font-bold">{s.total}</p>
                <p className="text-xs text-muted-foreground">Total Disclosures</p>
              </div>
              <div className="rounded-lg border p-3 text-center border-teal-300 bg-teal-50">
                <p className="text-2xl font-bold text-teal-700">{s.accountingEligible}</p>
                <p className="text-xs text-muted-foreground">Accounting Eligible</p>
              </div>
              <div className="rounded-lg border p-3 text-center">
                <p className="text-2xl font-bold">{s.excludedDisclosures}</p>
                <p className="text-xs text-muted-foreground">Excluded</p>
              </div>
              <div className={`rounded-lg border p-3 text-center ${s.pendingReview > 0 ? "border-amber-300 bg-amber-50" : ""}`}>
                <p className="text-2xl font-bold">{s.pendingReview}</p>
                <p className="text-xs text-muted-foreground">Pending Review</p>
              </div>
              <div className={`rounded-lg border p-3 text-center ${s.expiredRecords > 0 ? "border-red-400 bg-red-50" : ""}`}>
                <p className={`text-2xl font-bold ${s.expiredRecords > 0 ? "text-red-600" : ""}`}>{s.expiredRecords}</p>
                <p className="text-xs text-muted-foreground">Expired Records</p>
              </div>
              <div className={`rounded-lg border p-3 text-center ${s.exceptionsApplied > 0 ? "border-amber-200 bg-amber-50" : ""}`}>
                <p className="text-2xl font-bold">{s.exceptionsApplied}</p>
                <p className="text-xs text-muted-foreground">Exceptions</p>
              </div>
              <div className="rounded-lg border p-3 text-center">
                <p className="text-2xl font-bold">{s.recentDisclosures}</p>
                <p className="text-xs text-muted-foreground">Last 30 Days</p>
              </div>
              <div className={`rounded-lg border p-3 text-center ${s.compliancePercentage < 100 ? "border-amber-400 bg-amber-50" : "border-teal-400 bg-teal-50"}`}>
                <p className="text-2xl font-bold text-teal-700">{s.compliancePercentage}%</p>
                <p className="text-xs text-muted-foreground">Compliance</p>
              </div>
            </div>
          )}

          {/* Alerts */}
          {s && s.expiredRecords > 0 && (
            <div className="flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span><strong>{s.expiredRecords}</strong> disclosure record{s.expiredRecords > 1 ? "s have" : " has"} exceeded the 6-year retention window.</span>
            </div>
          )}

          {/* Toolbar */}
          <div className="flex flex-wrap gap-2 items-end">
            <div>
              <Label className="text-xs">Type</Label>
              <select className="border rounded px-2 py-1 text-sm h-8" value={typeFilter}
                onChange={e => { setTypeFilter(e.target.value); setPage(1); }}>
                <option value="">All</option>
                {DA21_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <select className="border rounded px-2 py-1 text-sm h-8" value={statusFilter}
                onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
                <option value="">All</option>
                {DA21_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">PHI Category</Label>
              <select className="border rounded px-2 py-1 text-sm h-8" value={catFilter}
                onChange={e => { setCatFilter(e.target.value); setPage(1); }}>
                <option value="">All</option>
                {DA21_PHI_CATS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Eligible</Label>
              <select className="border rounded px-2 py-1 text-sm h-8" value={eligFilter}
                onChange={e => { setEligFilter(e.target.value); setPage(1); }}>
                <option value="">All</option>
                <option value="true">Eligible</option>
                <option value="false">Excluded</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Exception</Label>
              <select className="border rounded px-2 py-1 text-sm h-8" value={excFilter}
                onChange={e => { setExcFilter(e.target.value); setPage(1); }}>
                <option value="">All</option>
                <option value="true">Applied</option>
                <option value="false">None</option>
              </select>
            </div>
            <div className="flex gap-2 ml-auto">
              <Button variant="outline" className="h-8 gap-2 text-sm"
                onClick={() => setShowReport(true)}>
                <FileText className="h-4 w-4" /> Accounting Report
              </Button>
              {canManage && (
                <Button className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2 h-8"
                  onClick={() => setShowCreate(true)}>
                  <Plus className="h-4 w-4" /> Add Record
                </Button>
              )}
            </div>
          </div>

          {/* List */}
          {listQ.isLoading ? <Skeleton className="h-40 w-full" /> : items.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No disclosure records found. Click "Add Record" to log a PHI disclosure.</p>
          ) : (
            <div className="space-y-2">
              {items.map(r => (
                <div key={r.id}
                  className={`flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer hover:bg-muted/40 transition-colors ${r.isRetentionExpired ? "border-red-400 bg-red-50/20" : ""}`}
                  onClick={() => setDetailId(r.id)}>
                  <div className="flex-1 min-w-0">
                    <span className="font-mono text-xs text-muted-foreground mr-2">{r.disclosureNumber}</span>
                    <span className="font-medium">{r.recipientName}</span>
                    {r.recipientOrganization && <span className="ml-1 text-xs text-muted-foreground">· {r.recipientOrganization}</span>}
                  </div>
                  <Badge variant="outline" className="text-xs">{r.disclosureType}</Badge>
                  <span className="text-xs text-muted-foreground hidden sm:block">{r.phiDataCategory}</span>
                  <Badge variant={r.accountingEligible ? "default" : "outline"} className={`text-xs ${r.accountingEligible ? "bg-teal-700 text-white" : "text-muted-foreground"}`}>
                    {r.accountingEligible ? "Eligible" : "Excluded"}
                  </Badge>
                  {r.exceptionApplied && <Badge variant="outline" className="text-xs border-amber-400 text-amber-700">Exception</Badge>}
                  <Badge variant={da21StatusVariant(r.status)} className="text-xs">{r.status}</Badge>
                  <span className="text-xs text-muted-foreground">{new Date(r.disclosureDate).toLocaleDateString()}</span>
                  {r.isRetentionExpired && <span className="text-xs text-red-600 font-medium">Retention expired</span>}
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {total > pageSize && (
            <div className="flex items-center gap-2 justify-center">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
              <span className="text-sm text-muted-foreground">Page {page} of {Math.ceil(total / pageSize)}</span>
              <Button variant="outline" size="sm" disabled={page * pageSize >= total} onClick={() => setPage(p => p + 1)}>Next</Button>
            </div>
          )}

          {/* Detail panel */}
          {detailId && detailQ.data && (() => {
            const r = detailQ.data;
            const isTerminal = DA21_TERMINAL.includes(r.status);
            return (
              <div className="rounded-lg border p-4 space-y-4 bg-muted/20">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <span className="font-mono text-xs text-muted-foreground">{r.disclosureNumber}</span>
                    <h4 className="font-semibold">{r.recipientName}{r.recipientOrganization ? ` — ${r.recipientOrganization}` : ""}</h4>
                  </div>
                  <div className="flex gap-2 items-center flex-wrap">
                    <Badge variant={da21StatusVariant(r.status)}>{r.status}</Badge>
                    {r.accountingEligible ? <Badge className="bg-teal-700 text-white text-xs">Accounting Eligible</Badge> : <Badge variant="outline" className="text-xs text-muted-foreground">Excluded</Badge>}
                    {r.exceptionApplied && <Badge variant="outline" className="text-xs border-amber-400 text-amber-700">Exception Applied</Badge>}
                    {r.isRetentionExpired && <Badge variant="destructive" className="text-xs">Retention Expired</Badge>}
                    <Button variant="ghost" size="sm" onClick={() => setDetailId(null)}>✕</Button>
                  </div>
                </div>

                {/* Recipient + disclosure info */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                  <div><p className="text-muted-foreground">Recipient Type</p><p className="font-medium">{r.recipientType ?? "—"}</p></div>
                  <div><p className="text-muted-foreground">Disclosure Type</p><p className="font-medium">{r.disclosureType}</p></div>
                  <div><p className="text-muted-foreground">PHI Category</p><p className="font-medium">{r.phiDataCategory}</p></div>
                  <div><p className="text-muted-foreground">Disclosure Date</p><p className="font-medium">{new Date(r.disclosureDate).toLocaleDateString()}</p></div>
                  <div><p className="text-muted-foreground">Method</p><p className="font-medium">{r.disclosureMethod ?? "—"}</p></div>
                  <div><p className="text-muted-foreground">Authorization</p><p className="font-medium">{r.authorizationRequired ? `Required${r.authorizationReference ? ` (${r.authorizationReference})` : ""}` : "Not Required"}</p></div>
                  {r.retentionExpiryDate && (
                    <div><p className="text-muted-foreground">Retention Expires</p><p className={`font-medium ${r.isRetentionExpired ? "text-red-600" : ""}`}>{new Date(r.retentionExpiryDate).toLocaleDateString()}</p></div>
                  )}
                  {r.reviewDate && <div><p className="text-muted-foreground">Review Date</p><p className="font-medium">{new Date(r.reviewDate).toLocaleDateString()}</p></div>}
                </div>

                {/* Purpose */}
                <div className="rounded bg-blue-50 border border-blue-200 p-2 text-xs">
                  <p className="font-semibold text-blue-700 mb-0.5">Purpose of Disclosure</p>
                  <p>{r.disclosurePurpose}</p>
                </div>

                {/* PHI details */}
                {(r.recordTypesDisclosed || r.dataElementsDisclosed) && (
                  <div className="rounded bg-muted p-2 text-xs space-y-1">
                    {r.recordTypesDisclosed && <p><span className="text-muted-foreground">Record types: </span>{r.recordTypesDisclosed}</p>}
                    {r.dataElementsDisclosed && <p><span className="text-muted-foreground">Data elements: </span>{r.dataElementsDisclosed}</p>}
                  </div>
                )}

                {/* Exception */}
                {r.exceptionApplied && (
                  <div className="rounded bg-amber-50 border border-amber-300 p-2 text-xs">
                    <p className="font-semibold text-amber-700 mb-1">Exception — {r.exceptionType}</p>
                    <p>{r.exceptionReason}</p>
                    {r.accountingExcludedReason && <p className="mt-1 text-muted-foreground">{r.accountingExcludedReason}</p>}
                  </div>
                )}

                {r.notes && <p className="text-xs text-muted-foreground italic">{r.notes}</p>}

                {/* Actions */}
                {canManage && (
                  <div className="flex flex-wrap gap-2">
                    {!isTerminal && (
                      <Button size="sm" variant="outline" className="gap-1"
                        onClick={() => reviewMut.mutate(r.id)} disabled={reviewMut.isPending}>
                        Start Review
                      </Button>
                    )}
                    {!isTerminal && r.accountingEligible && (
                      <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-1"
                        onClick={() => approveMut.mutate(r.id)} disabled={approveMut.isPending}>
                        <CheckCircle2 className="h-3.5 w-3.5" /> Approve
                      </Button>
                    )}
                    {!isTerminal && (
                      <Button size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-50 gap-1"
                        onClick={() => { setRejectId(r.id); setRejectNote(""); }}>
                        Reject
                      </Button>
                    )}
                    {!r.exceptionApplied && !isTerminal && (
                      <Button size="sm" variant="outline" className="border-amber-400 text-amber-700 hover:bg-amber-50 gap-1"
                        onClick={() => { setExceptionId(r.id); setExceptionForm({ exceptionType: "TreatmentException", exceptionReason: "", accountingExcludedReason: "" }); setShowException(true); }}>
                        Apply Exception
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={v => { setShowCreate(v); if (!v) setErrors({}); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Disclosure Record</DialogTitle>
            <DialogDescription>Log a PHI disclosure under § 164.528. Records must be retained for 6 years.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Recipient Name *</Label>
                <Input className={fieldCls("", errors, "recipientName")} aria-invalid={!!errors.recipientName} value={form.recipientName} onChange={e => { setF("recipientName", e.target.value); clearErr(setErrors, "recipientName"); }} placeholder="State Health Department" />
                {fErr(errors, "recipientName")}
              </div>
              <div><Label>Recipient Organization</Label><Input value={form.recipientOrganization} onChange={e => setF("recipientOrganization", e.target.value)} /></div>
              <div>
                <Label>Recipient Type</Label>
                <select className="border rounded px-2 py-1 text-sm w-full" value={form.recipientType} onChange={e => setF("recipientType", e.target.value)}>
                  {DA21_RECIPIENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <Label>Disclosure Type *</Label>
                <select className="border rounded px-2 py-1 text-sm w-full" value={form.disclosureType} onChange={e => setF("disclosureType", e.target.value)}>
                  {DA21_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <Label>PHI Category *</Label>
                <select className="border rounded px-2 py-1 text-sm w-full" value={form.phiDataCategory} onChange={e => setF("phiDataCategory", e.target.value)}>
                  {DA21_PHI_CATS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <Label>Disclosure Method</Label>
                <select className="border rounded px-2 py-1 text-sm w-full" value={form.disclosureMethod} onChange={e => setF("disclosureMethod", e.target.value)}>
                  {DA21_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div><Label>Disclosure Date</Label><Input type="date" value={form.disclosureDate} onChange={e => setF("disclosureDate", e.target.value)} /></div>
              <div><Label>Retention Period (years)</Label><Input type="number" min={1} max={50} value={form.retentionPeriod} onChange={e => setF("retentionPeriod", Number(e.target.value))} /></div>
            </div>

            <div>
              <Label>Purpose of Disclosure *</Label>
              <Textarea className={fieldCls("", errors, "disclosurePurpose")} aria-invalid={!!errors.disclosurePurpose} rows={2} value={form.disclosurePurpose} onChange={e => { setF("disclosurePurpose", e.target.value); clearErr(setErrors, "disclosurePurpose"); }} placeholder="Mandatory public health report per state law…" />
              {fErr(errors, "disclosurePurpose")}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div><Label>Record Types Disclosed</Label><Input value={form.recordTypesDisclosed} onChange={e => setF("recordTypesDisclosed", e.target.value)} placeholder="Medical Records,Diagnosis" /></div>
              <div><Label>Data Elements Disclosed</Label><Input value={form.dataElementsDisclosed} onChange={e => setF("dataElementsDisclosed", e.target.value)} placeholder="DiagnosisCode,DateOfBirth" /></div>
            </div>

            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.authorizationRequired} onChange={e => setF("authorizationRequired", e.target.checked)} />
                Authorization Required
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={!form.accountingEligible} onChange={e => setF("accountingEligible", !e.target.checked)} />
                Exclude from Accounting Report
              </label>
            </div>

            {form.authorizationRequired && (
              <div><Label>Authorization Reference</Label><Input value={form.authorizationReference} onChange={e => setF("authorizationReference", e.target.value)} placeholder="Consent form ID…" /></div>
            )}

            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.exceptionApplied} onChange={e => setF("exceptionApplied", e.target.checked)} />
                Exception Applies
              </label>
            </div>

            {form.exceptionApplied && (
              <div className="space-y-2">
                <div>
                  <Label>Exception Type</Label>
                  <select className="border rounded px-2 py-1 text-sm w-full" value={form.exceptionType} onChange={e => setF("exceptionType", e.target.value)}>
                    {DA21_EXCEPTION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Exception Reason *</Label>
                  <Textarea rows={2} value={form.exceptionReason} onChange={e => setF("exceptionReason", e.target.value)} placeholder="Reason the exception applies…" />
                </div>
              </div>
            )}

            <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={e => setF("notes", e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2"
              onClick={() => { const errs: Record<string,string> = {}; required(errs, "recipientName", form.recipientName, "Recipient Name"); required(errs, "disclosurePurpose", form.disclosurePurpose, "Purpose of Disclosure"); if (Object.keys(errs).length) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } setErrors({}); createMut.mutate(); }} disabled={createMut.isPending}>
              <Plus className="h-4 w-4" />{createMut.isPending ? "Creating…" : "Add Disclosure Record"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={!!rejectId} onOpenChange={o => { if (!o) setRejectId(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Disclosure Record</DialogTitle>
            <DialogDescription>Provide a reason for rejection.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div><Label>Rejection Reason</Label><Textarea rows={3} value={rejectNote} onChange={e => setRejectNote(e.target.value)} placeholder="Incomplete recipient information…" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectId(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => rejectId && rejectMut.mutate({ id: rejectId, notes: rejectNote })} disabled={rejectMut.isPending}>
              {rejectMut.isPending ? "Rejecting…" : "Reject Record"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Exception dialog */}
      <Dialog open={showException} onOpenChange={o => { if (!o) { setShowException(false); setExceptionId(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Apply Disclosure Exception</DialogTitle>
            <DialogDescription>Record that an exception applies to this disclosure. The record will be excluded from the accounting report.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <Label>Exception Type</Label>
              <select className="border rounded px-2 py-1 text-sm w-full" value={exceptionForm.exceptionType}
                onChange={e => setExceptionForm(f => ({ ...f, exceptionType: e.target.value }))}>
                {DA21_EXCEPTION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <Label>Exception Reason *</Label>
              <Textarea rows={3} value={exceptionForm.exceptionReason}
                onChange={e => setExceptionForm(f => ({ ...f, exceptionReason: e.target.value }))}
                placeholder="Treatment disclosure — not subject to accounting under § 164.528(a)(1)(i)." />
            </div>
            <div>
              <Label>Excluded Reason <span className="text-muted-foreground text-xs">(for audit trail)</span></Label>
              <Input value={exceptionForm.accountingExcludedReason}
                onChange={e => setExceptionForm(f => ({ ...f, accountingExcludedReason: e.target.value }))}
                placeholder="Optional additional context…" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowException(false); setExceptionId(null); }}>Cancel</Button>
            <Button className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2"
              onClick={() => exceptionMut.mutate()} disabled={exceptionMut.isPending || !exceptionForm.exceptionReason}>
              <CheckCircle2 className="h-4 w-4" />{exceptionMut.isPending ? "Applying…" : "Apply Exception"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Accounting Report dialog */}
      <Dialog open={showReport} onOpenChange={o => { if (!o) setShowReport(false); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Accounting of Disclosures Report</DialogTitle>
            <DialogDescription>Approved, accounting-eligible disclosures. Share this report when individuals request an accounting of disclosures under § 164.528.</DialogDescription>
          </DialogHeader>
          {reportQ.isLoading ? <Skeleton className="h-32 w-full" /> : reportQ.data && (
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Generated: {new Date().toLocaleDateString()}</span>
                <span>Total eligible disclosures: <strong>{reportQ.data.total}</strong></span>
              </div>
              {reportQ.data.items.length === 0 ? (
                <p className="text-center text-muted-foreground py-6">No approved eligible disclosures found.</p>
              ) : (
                <div className="space-y-2">
                  {reportQ.data.items.map((item: any) => (
                    <div key={item.disclosureNumber} className="rounded border p-3 text-xs space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="font-mono font-semibold">{item.disclosureNumber}</span>
                        <span className="text-muted-foreground">{new Date(item.disclosureDate).toLocaleDateString()}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                        <p><span className="text-muted-foreground">Recipient: </span>{item.recipientName}{item.recipientOrganization ? ` — ${item.recipientOrganization}` : ""}</p>
                        <p><span className="text-muted-foreground">Type: </span>{item.disclosureType}</p>
                        <p><span className="text-muted-foreground">Purpose: </span>{item.disclosurePurpose}</p>
                        <p><span className="text-muted-foreground">PHI Category: </span>{item.phiDataCategory}</p>
                        {item.exceptionApplied && <p className="text-amber-700"><span className="text-muted-foreground">Exception: </span>{item.exceptionType}</p>}
                        {item.retentionExpiryDate && <p><span className="text-muted-foreground">Retention expires: </span>{new Date(item.retentionExpiryDate).toLocaleDateString()}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReport(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── HIPAA-20 — Privacy Amendment (§ 164.526) ─────────────────────────────────

type AmSummary20 = {
  total: number; pending: number; approved: number; denied: number;
  completed: number; cancelled: number; overdue: number;
  approvalPercentage: number; compliancePercentage: number;
  averageResolutionHours: number;
};

type AmRequest20 = {
  id: string; requestNumber: string;
  requesterName: string; requesterEmail?: string;
  relationshipToIndividual?: string;
  requestedRecordTypes: string; affectedRecordDescription?: string;
  amendmentReason: string; requestedChanges: string;
  requestDate: string; dueDate: string; status: string;
  assignedReviewer?: string; reviewerName?: string;
  reviewDate?: string; decisionReason?: string; denialReason?: string;
  supportingEvidenceReference?: string;
  amendmentApplied: boolean; amendmentAppliedDate?: string;
  updatedRecordReference?: string;
  notificationSent: boolean; notificationDate?: string;
  notes?: string; createdAt: string; updatedAt: string;
  isOverdue: boolean; daysUntilDue: number;
};

const AM20_STATUSES   = ["Submitted","UnderReview","Approved","PartiallyApproved","Denied","Completed","Cancelled","Expired"];
const AM20_TERMINAL   = ["Completed","Denied","Cancelled","Expired"];
const AM20_CAN_APPROVE= ["UnderReview"];
const AM20_CAN_COMPLETE = ["Approved","PartiallyApproved"];
const AM20_CAN_DENY   = ["Submitted","UnderReview","Approved","PartiallyApproved"];
const AM20_CAN_CANCEL = ["Submitted","UnderReview","Approved"];
const AM20_RECORD_TYPES = ["Medical Records","Clinical Notes","Diagnosis","Medication History","Laboratory Results","Imaging Reports","Billing Records","Insurance Records","Other"];

function am20StatusVariant(s: string): "default"|"secondary"|"outline"|"destructive" {
  if (s === "Completed") return "default";
  if (["Denied","Expired"].includes(s)) return "destructive";
  if (["Cancelled"].includes(s)) return "outline";
  if (["Approved","PartiallyApproved"].includes(s)) return "secondary";
  return "secondary";
}

function useAuth20() {
  return (url: string, opts: RequestInit = {}) => {
    const token = sessionStorage.getItem("dotnetbot.jwt.session") || localStorage.getItem("dotnetbot.jwt") || "";
    return fetch(url, { ...opts, headers: { ...(opts.headers as Record<string,string> || {}), "Content-Type": "application/json", Authorization: `Bearer ${token}` } });
  };
}

function HipaaAmendmentSection({ canManage }: { canManage: boolean }) {
  const { toast } = useToast();
  const authFetch = useAuth20();
  const qc = useQueryClient();
  const BASE = "/api/organization/compliance/hipaa/amendments";

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["hipaa20-list"] });
    qc.invalidateQueries({ queryKey: ["hipaa20-summary"] });
    if (detailId) qc.invalidateQueries({ queryKey: ["hipaa20-detail", detailId] });
  };

  // ── Summary ────────────────────────────────────────────────────────────────
  const summaryQ = useQuery<AmSummary20>({
    queryKey: ["hipaa20-summary"],
    queryFn: async () => { const r = await authFetch(`${BASE}/summary`); if (!r.ok) throw new Error(); return r.json(); },
    staleTime: 0,
  });

  // ── List + filters ─────────────────────────────────────────────────────────
  const [page, setPage]               = useState(1);
  const [statusFilter, setStatusFilter]   = useState("");
  const [requesterFilter, setRequesterFilter] = useState("");
  const [reviewerFilter, setReviewerFilter]   = useState("");

  const listQ = useQuery<{ items: AmRequest20[]; total: number }>({
    queryKey: ["hipaa20-list", page, statusFilter, requesterFilter, reviewerFilter],
    queryFn: async () => {
      const p = new URLSearchParams({ page: String(page), pageSize: "15" });
      if (statusFilter)   p.set("status",    statusFilter);
      if (requesterFilter)p.set("requester", requesterFilter);
      if (reviewerFilter) p.set("reviewer",  reviewerFilter);
      const r = await authFetch(`${BASE}?${p}`);
      if (!r.ok) throw new Error(); return r.json();
    },
    staleTime: 0,
  });

  // ── Detail ─────────────────────────────────────────────────────────────────
  const [detailId, setDetailId] = useState<string | null>(null);
  const detailQ = useQuery<AmRequest20>({
    queryKey: ["hipaa20-detail", detailId],
    queryFn: async () => { const r = await authFetch(`${BASE}/${detailId}`); if (!r.ok) throw new Error(); return r.json(); },
    enabled: !!detailId,
    staleTime: 0,
  });

  // ── Create ─────────────────────────────────────────────────────────────────
  const [showCreate, setShowCreate] = useState(false);
  const defaultForm = () => ({
    requesterName: "", requesterEmail: "", relationshipToIndividual: "Self",
    requestedRecordTypes: "", affectedRecordDescription: "",
    amendmentReason: "", requestedChanges: "",
    dueDate: "", supportingEvidenceReference: "", notes: "",
  });
  const [form, setForm] = useState(defaultForm());
  const setF = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));
  const [errors, setErrors] = useState<Record<string,string>>({});

  const createMut = useMutation({
    mutationFn: async () => {
      const body: Record<string,any> = { ...form };
      if (!body.dueDate) delete body.dueDate;
      const r = await authFetch(BASE, { method: "POST", body: JSON.stringify(body) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.error || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Amendment request created." }); setShowCreate(false); setForm(defaultForm()); setErrors({}); invalidate(); },
    onError: (e: any) => { const s = extractServerErrors(e); if (s && Object.keys(s).length) { setErrors(s); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); } else toast({ title: "Unable to save", description: (e as Error)?.message, variant: "destructive" }); },
  });

  // ── Assign reviewer ────────────────────────────────────────────────────────
  const [showAssign, setShowAssign] = useState(false);
  const [assignId,   setAssignId]   = useState<string | null>(null);
  const [assignForm, setAssignForm] = useState({ reviewerName: "" });

  const assignMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/${assignId}/assign-reviewer`, { method: "PATCH", body: JSON.stringify({ reviewerName: assignForm.reviewerName }) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.error || e.detail || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Reviewer assigned." }); setShowAssign(false); setAssignId(null); invalidate(); },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  // ── Approve ────────────────────────────────────────────────────────────────
  const [showApprove, setShowApprove] = useState(false);
  const [approveId,   setApproveId]   = useState<string | null>(null);
  const [approveForm, setApproveForm] = useState({ partial: false, decisionReason: "", notes: "" });

  const approveMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/${approveId}/approve`, { method: "PATCH", body: JSON.stringify(approveForm) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.error || e.detail || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Amendment approved." }); setShowApprove(false); setApproveId(null); invalidate(); },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  // ── Deny ───────────────────────────────────────────────────────────────────
  const [showDeny, setShowDeny] = useState(false);
  const [denyId,   setDenyId]   = useState<string | null>(null);
  const [denyForm, setDenyForm] = useState({ denialReason: "", notes: "" });

  const denyMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/${denyId}/deny`, { method: "PATCH", body: JSON.stringify(denyForm) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.error || e.detail || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Amendment denied." }); setShowDeny(false); setDenyId(null); invalidate(); },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  // ── Complete ───────────────────────────────────────────────────────────────
  const [showComplete, setShowComplete] = useState(false);
  const [completeId,   setCompleteId]   = useState<string | null>(null);
  const [completeForm, setCompleteForm] = useState({ updatedRecordReference: "", notificationSent: false, notes: "" });

  const completeMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/${completeId}/complete`, { method: "PATCH", body: JSON.stringify(completeForm) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.error || e.detail || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Amendment marked complete." }); setShowComplete(false); setCompleteId(null); invalidate(); },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  // ── Cancel ─────────────────────────────────────────────────────────────────
  const cancelMut = useMutation({
    mutationFn: async (id: string) => {
      const r = await authFetch(`${BASE}/${id}/cancel`, { method: "PATCH", body: JSON.stringify({ notes: "" }) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.error || e.detail || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Request cancelled." }); invalidate(); },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  const s     = summaryQ.data;
  const items = listQ.data?.items ?? [];
  const total = listQ.data?.total ?? 0;
  const pageSize = 15;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-teal-700" />
            Privacy — Amendment Requests
          </CardTitle>
          <CardDescription>§ 164.526 — Support individuals' right to request amendment of inaccurate or incomplete PHI. Track requests through review, decision, application, and notification with full audit history.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">

          {/* Dashboard tiles */}
          {summaryQ.isLoading ? <Skeleton className="h-24 w-full" /> : s && (
            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 gap-3">
              <div className="rounded-lg border p-3 text-center">
                <p className="text-2xl font-bold">{s.total}</p>
                <p className="text-xs text-muted-foreground">Total</p>
              </div>
              <div className={`rounded-lg border p-3 text-center ${s.pending > 0 ? "border-amber-300 bg-amber-50" : ""}`}>
                <p className="text-2xl font-bold">{s.pending}</p>
                <p className="text-xs text-muted-foreground">Pending Review</p>
              </div>
              <div className="rounded-lg border p-3 text-center border-blue-200 bg-blue-50">
                <p className="text-2xl font-bold text-blue-700">{s.approved}</p>
                <p className="text-xs text-muted-foreground">Approved</p>
              </div>
              <div className="rounded-lg border p-3 text-center">
                <p className="text-2xl font-bold">{s.denied}</p>
                <p className="text-xs text-muted-foreground">Denied</p>
              </div>
              <div className="rounded-lg border p-3 text-center border-teal-300 bg-teal-50">
                <p className="text-2xl font-bold text-teal-700">{s.completed}</p>
                <p className="text-xs text-muted-foreground">Completed</p>
              </div>
              <div className={`rounded-lg border p-3 text-center ${s.overdue > 0 ? "border-red-400 bg-red-50" : ""}`}>
                <p className={`text-2xl font-bold ${s.overdue > 0 ? "text-red-600" : ""}`}>{s.overdue}</p>
                <p className="text-xs text-muted-foreground">Overdue</p>
              </div>
              <div className="rounded-lg border p-3 text-center">
                <p className="text-2xl font-bold">{s.averageResolutionHours}h</p>
                <p className="text-xs text-muted-foreground">Avg. Resolution</p>
              </div>
              <div className="rounded-lg border p-3 text-center">
                <p className="text-2xl font-bold">{s.approvalPercentage}%</p>
                <p className="text-xs text-muted-foreground">Approval Rate</p>
              </div>
              <div className={`rounded-lg border p-3 text-center ${s.compliancePercentage < 100 ? "border-amber-400 bg-amber-50" : "border-teal-400 bg-teal-50"}`}>
                <p className="text-2xl font-bold text-teal-700">{s.compliancePercentage}%</p>
                <p className="text-xs text-muted-foreground">Compliance</p>
              </div>
            </div>
          )}

          {/* Overdue alert */}
          {s && s.overdue > 0 && (
            <div className="flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span><strong>{s.overdue}</strong> amendment request{s.overdue > 1 ? "s are" : " is"} past the 60-day response deadline.</span>
            </div>
          )}

          {/* Toolbar */}
          <div className="flex flex-wrap gap-2 items-end">
            <div>
              <Label className="text-xs">Status</Label>
              <select className="border rounded px-2 py-1 text-sm h-8" value={statusFilter}
                onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
                <option value="">All</option>
                {AM20_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Requester</Label>
              <Input className="h-8 text-sm w-36" placeholder="Name…" value={requesterFilter}
                onChange={e => { setRequesterFilter(e.target.value); setPage(1); }} />
            </div>
            <div>
              <Label className="text-xs">Reviewer</Label>
              <Input className="h-8 text-sm w-36" placeholder="Name…" value={reviewerFilter}
                onChange={e => { setReviewerFilter(e.target.value); setPage(1); }} />
            </div>
            {canManage && (
              <Button className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2 h-8 ml-auto"
                onClick={() => setShowCreate(true)}>
                <Plus className="h-4 w-4" /> New Request
              </Button>
            )}
          </div>

          {/* List */}
          {listQ.isLoading ? <Skeleton className="h-40 w-full" /> : items.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No amendment requests found. Click "New Request" to create one.</p>
          ) : (
            <div className="space-y-2">
              {items.map(r => (
                <div key={r.id}
                  className={`flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer hover:bg-muted/40 transition-colors ${r.isOverdue ? "border-red-400 bg-red-50/20" : ""}`}
                  onClick={() => setDetailId(r.id)}>
                  <div className="flex-1 min-w-0">
                    <span className="font-mono text-xs text-muted-foreground mr-2">{r.requestNumber}</span>
                    <span className="font-medium">{r.requesterName}</span>
                  </div>
                  <span className="text-xs text-muted-foreground hidden sm:block">{r.requestedRecordTypes.split(",")[0]}</span>
                  {r.reviewerName && <span className="text-xs text-muted-foreground">→ {r.reviewerName}</span>}
                  <Badge variant={am20StatusVariant(r.status)} className="text-xs">{r.status}</Badge>
                  {r.isOverdue && <span className="text-xs text-red-600 font-medium">Overdue</span>}
                  <span className="text-xs text-muted-foreground">Due: {new Date(r.dueDate).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {total > pageSize && (
            <div className="flex items-center gap-2 justify-center">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
              <span className="text-sm text-muted-foreground">Page {page} of {Math.ceil(total / pageSize)}</span>
              <Button variant="outline" size="sm" disabled={page * pageSize >= total} onClick={() => setPage(p => p + 1)}>Next</Button>
            </div>
          )}

          {/* Detail panel */}
          {detailId && detailQ.data && (() => {
            const r = detailQ.data;
            const isTerminal = AM20_TERMINAL.includes(r.status);
            return (
              <div className="rounded-lg border p-4 space-y-4 bg-muted/20">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <span className="font-mono text-xs text-muted-foreground">{r.requestNumber}</span>
                    <h4 className="font-semibold">{r.requesterName}</h4>
                  </div>
                  <div className="flex gap-2 items-center flex-wrap">
                    <Badge variant={am20StatusVariant(r.status)}>{r.status}</Badge>
                    {r.isOverdue && <Badge variant="destructive">Overdue</Badge>}
                    {r.amendmentApplied && <Badge className="bg-teal-700 text-white">Amendment Applied</Badge>}
                    {r.notificationSent && <Badge variant="secondary">Notified</Badge>}
                    <Button variant="ghost" size="sm" onClick={() => setDetailId(null)}>✕</Button>
                  </div>
                </div>

                {/* Request info */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                  <div><p className="text-muted-foreground">Relationship</p><p className="font-medium">{r.relationshipToIndividual ?? "—"}</p></div>
                  <div><p className="text-muted-foreground">Record Types</p><p className="font-medium">{r.requestedRecordTypes}</p></div>
                  {r.affectedRecordDescription && <div><p className="text-muted-foreground">Affected Record</p><p className="font-medium">{r.affectedRecordDescription}</p></div>}
                  <div><p className="text-muted-foreground">Request Date</p><p className="font-medium">{new Date(r.requestDate).toLocaleDateString()}</p></div>
                  <div><p className="text-muted-foreground">Due Date</p><p className={`font-medium ${r.isOverdue ? "text-red-600" : ""}`}>{new Date(r.dueDate).toLocaleDateString()}</p></div>
                  {r.reviewerName && <div><p className="text-muted-foreground">Reviewer</p><p className="font-medium">{r.reviewerName}</p></div>}
                  {r.reviewDate && <div><p className="text-muted-foreground">Review Date</p><p className="font-medium">{new Date(r.reviewDate).toLocaleDateString()}</p></div>}
                </div>

                {/* Amendment reason & changes */}
                <div className="rounded bg-amber-50 border border-amber-200 p-3 text-xs space-y-1">
                  <p className="font-semibold text-amber-700">Amendment Reason</p>
                  <p>{r.amendmentReason}</p>
                  <p className="font-semibold text-amber-700 mt-2">Requested Changes</p>
                  <p>{r.requestedChanges}</p>
                </div>

                {/* Decision details */}
                {(r.decisionReason || r.denialReason) && (
                  <div className={`rounded p-3 text-xs border ${r.status === "Denied" ? "border-red-300 bg-red-50" : "border-teal-300 bg-teal-50"}`}>
                    <p className={`font-semibold mb-1 ${r.status === "Denied" ? "text-red-700" : "text-teal-700"}`}>Decision</p>
                    {r.decisionReason && <p>{r.decisionReason}</p>}
                    {r.denialReason   && <p className="text-red-700">{r.denialReason}</p>}
                  </div>
                )}

                {/* Supporting evidence */}
                {r.supportingEvidenceReference && (
                  <div className="text-xs">
                    <span className="text-muted-foreground">Supporting Evidence: </span>
                    <span className="font-medium font-mono">{r.supportingEvidenceReference}</span>
                  </div>
                )}

                {/* Completion / notification */}
                {r.status === "Completed" && (
                  <div className="rounded p-3 text-xs border border-teal-300 bg-teal-50">
                    <p className="font-semibold text-teal-700 mb-1">Completion</p>
                    <div className="grid grid-cols-2 gap-2">
                      {r.amendmentAppliedDate && <div><span className="text-muted-foreground">Applied: </span>{new Date(r.amendmentAppliedDate).toLocaleDateString()}</div>}
                      {r.updatedRecordReference && <div><span className="text-muted-foreground">Record Ref: </span><span className="font-mono">{r.updatedRecordReference}</span></div>}
                      <div><span className="text-muted-foreground">Notification: </span><span className={r.notificationSent ? "text-teal-700 font-medium" : "text-muted-foreground"}>{r.notificationSent ? `Sent${r.notificationDate ? " " + new Date(r.notificationDate).toLocaleDateString() : ""}` : "Not Sent"}</span></div>
                    </div>
                  </div>
                )}

                {r.notes && <p className="text-xs text-muted-foreground italic">{r.notes}</p>}

                {/* Actions */}
                {canManage && !isTerminal && (
                  <div className="flex flex-wrap gap-2">
                    {!AM20_TERMINAL.includes(r.status) && (
                      <Button size="sm" variant="outline" className="gap-1"
                        onClick={() => { setAssignId(r.id); setAssignForm({ reviewerName: r.reviewerName ?? "" }); setShowAssign(true); }}>
                        <Users className="h-3.5 w-3.5" /> {r.reviewerName ? "Reassign" : "Assign Reviewer"}
                      </Button>
                    )}
                    {AM20_CAN_APPROVE.includes(r.status) && (
                      <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-1"
                        onClick={() => { setApproveId(r.id); setApproveForm({ partial: false, decisionReason: "", notes: "" }); setShowApprove(true); }}>
                        <CheckCircle2 className="h-3.5 w-3.5" /> Approve
                      </Button>
                    )}
                    {AM20_CAN_COMPLETE.includes(r.status) && (
                      <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-1"
                        onClick={() => { setCompleteId(r.id); setCompleteForm({ updatedRecordReference: "", notificationSent: false, notes: "" }); setShowComplete(true); }}>
                        <CheckCircle2 className="h-3.5 w-3.5" /> Complete Amendment
                      </Button>
                    )}
                    {AM20_CAN_DENY.includes(r.status) && (
                      <Button size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-50 gap-1"
                        onClick={() => { setDenyId(r.id); setDenyForm({ denialReason: "", notes: "" }); setShowDeny(true); }}>
                        Deny
                      </Button>
                    )}
                    {AM20_CAN_CANCEL.includes(r.status) && (
                      <Button size="sm" variant="ghost" className="text-muted-foreground"
                        onClick={() => cancelMut.mutate(r.id)} disabled={cancelMut.isPending}>
                        Cancel
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={v => { setShowCreate(v); if (!v) setErrors({}); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Amendment Request</DialogTitle>
            <DialogDescription>Create a PHI amendment request under § 164.526. A 60-day response deadline applies by default.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Requester Name *</Label>
                <Input className={fieldCls("", errors, "requesterName")} aria-invalid={!!errors.requesterName} value={form.requesterName} onChange={e => { setF("requesterName", e.target.value); clearErr(setErrors, "requesterName"); }} placeholder="Full legal name" />
                {fErr(errors, "requesterName")}
              </div>
              <div><Label>Email</Label><Input type="email" value={form.requesterEmail} onChange={e => setF("requesterEmail", e.target.value)} /></div>
              <div>
                <Label>Relationship to Individual</Label>
                <select className="border rounded px-2 py-1 text-sm w-full" value={form.relationshipToIndividual}
                  onChange={e => setF("relationshipToIndividual", e.target.value)}>
                  {["Self","Parent","Guardian","PowerOfAttorney","Other"].map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <Label>Requested Record Types * <span className="text-muted-foreground text-xs">(click to select)</span></Label>
                <div className="flex flex-wrap gap-1 mb-1">
                  {AM20_RECORD_TYPES.map(rt => (
                    <button key={rt} type="button"
                      className={`px-2 py-0.5 rounded-full text-xs border transition-colors ${form.requestedRecordTypes.split(",").map(s => s.trim()).includes(rt) ? "bg-teal-700 text-white border-teal-700" : "border-muted-foreground text-muted-foreground hover:border-teal-700"}`}
                      onClick={() => {
                        const sel = form.requestedRecordTypes.split(",").map(s => s.trim()).filter(Boolean);
                        if (sel.includes(rt)) setF("requestedRecordTypes", sel.filter(s => s !== rt).join(", "));
                        else { setF("requestedRecordTypes", [...sel, rt].join(", ")); clearErr(setErrors, "requestedRecordTypes"); }
                      }}>{rt}</button>
                  ))}
                </div>
                {fErr(errors, "requestedRecordTypes")}
              </div>
              <div className="col-span-2">
                <Label>Affected Record Description</Label>
                <Input value={form.affectedRecordDescription} onChange={e => setF("affectedRecordDescription", e.target.value)}
                  placeholder="e.g. Encounter note dated 2026-01-15, record #12345" />
              </div>
            </div>
            <div>
              <Label>Amendment Reason * <span className="text-muted-foreground text-xs">(why is the record inaccurate?)</span></Label>
              <Textarea className={fieldCls("", errors, "amendmentReason")} aria-invalid={!!errors.amendmentReason} rows={2} value={form.amendmentReason} onChange={e => { setF("amendmentReason", e.target.value); clearErr(setErrors, "amendmentReason"); }}
                placeholder="Describe why the PHI is inaccurate or incomplete" />
              {fErr(errors, "amendmentReason")}
            </div>
            <div>
              <Label>Requested Changes * <span className="text-muted-foreground text-xs">(what should be corrected?)</span></Label>
              <Textarea className={fieldCls("", errors, "requestedChanges")} aria-invalid={!!errors.requestedChanges} rows={3} value={form.requestedChanges} onChange={e => { setF("requestedChanges", e.target.value); clearErr(setErrors, "requestedChanges"); }}
                placeholder="Describe the specific changes requested" />
              {fErr(errors, "requestedChanges")}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Due Date <span className="text-muted-foreground text-xs">(default 60 days)</span></Label><Input type="date" value={form.dueDate} onChange={e => setF("dueDate", e.target.value)} /></div>
              <div><Label>Supporting Evidence Reference</Label><Input value={form.supportingEvidenceReference} onChange={e => setF("supportingEvidenceReference", e.target.value)} placeholder="Lab report ref, document ID…" /></div>
            </div>
            <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={e => setF("notes", e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2"
              onClick={() => { const errs: Record<string,string> = {}; required(errs, "requesterName", form.requesterName, "Requester Name"); required(errs, "requestedRecordTypes", form.requestedRecordTypes, "Requested Record Types"); required(errs, "amendmentReason", form.amendmentReason, "Amendment Reason"); required(errs, "requestedChanges", form.requestedChanges, "Requested Changes"); if (Object.keys(errs).length) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } setErrors({}); createMut.mutate(); }} disabled={createMut.isPending}>
              <Plus className="h-4 w-4" />{createMut.isPending ? "Creating…" : "Create Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Reviewer dialog */}
      <Dialog open={showAssign} onOpenChange={o => { if (!o) { setShowAssign(false); setAssignId(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Reviewer</DialogTitle>
            <DialogDescription>Assign a reviewer to this amendment request. The request will advance to Under Review status.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <Label>Reviewer Name *</Label>
              <Input value={assignForm.reviewerName} onChange={e => setAssignForm(f => ({ ...f, reviewerName: e.target.value }))} placeholder="Dr. Jane Smith" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAssign(false); setAssignId(null); }}>Cancel</Button>
            <Button className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2"
              onClick={() => assignMut.mutate()} disabled={assignMut.isPending || !assignForm.reviewerName}>
              <Users className="h-4 w-4" />{assignMut.isPending ? "Assigning…" : "Assign Reviewer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve dialog */}
      <Dialog open={showApprove} onOpenChange={o => { if (!o) { setShowApprove(false); setApproveId(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Amendment Request</DialogTitle>
            <DialogDescription>Record your approval decision. Select Partial Approval if only some changes are supported by the evidence.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={approveForm.partial}
                onChange={e => setApproveForm(f => ({ ...f, partial: e.target.checked }))} />
              Partial Approval only
            </label>
            <div>
              <Label>Decision Rationale</Label>
              <Textarea rows={3} value={approveForm.decisionReason}
                onChange={e => setApproveForm(f => ({ ...f, decisionReason: e.target.value }))}
                placeholder="Evidence reviewed, changes supported by documentation…" />
            </div>
            <div><Label>Notes</Label><Textarea rows={2} value={approveForm.notes} onChange={e => setApproveForm(f => ({ ...f, notes: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowApprove(false); setApproveId(null); }}>Cancel</Button>
            <Button className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2"
              onClick={() => approveMut.mutate()} disabled={approveMut.isPending}>
              <CheckCircle2 className="h-4 w-4" />{approveMut.isPending ? "Approving…" : approveForm.partial ? "Partially Approve" : "Approve Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deny dialog */}
      <Dialog open={showDeny} onOpenChange={o => { if (!o) { setShowDeny(false); setDenyId(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deny Amendment Request</DialogTitle>
            <DialogDescription>Provide a denial reason. The requester has the right to submit a statement of disagreement.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <Label>Denial Reason</Label>
              <Textarea rows={3} value={denyForm.denialReason}
                onChange={e => setDenyForm(f => ({ ...f, denialReason: e.target.value }))}
                placeholder="Record is accurate as documented; amendment is not warranted because…" />
            </div>
            <div><Label>Notes</Label><Textarea rows={2} value={denyForm.notes} onChange={e => setDenyForm(f => ({ ...f, notes: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowDeny(false); setDenyId(null); }}>Cancel</Button>
            <Button variant="destructive" onClick={() => denyMut.mutate()} disabled={denyMut.isPending}>
              {denyMut.isPending ? "Denying…" : "Deny Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complete dialog */}
      <Dialog open={showComplete} onOpenChange={o => { if (!o) { setShowComplete(false); setCompleteId(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Complete Amendment</DialogTitle>
            <DialogDescription>Record that the PHI amendment has been applied and the individual notified. This closes the request.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <Label>Updated Record Reference <span className="text-muted-foreground text-xs">(EHR ref, document ID, etc.)</span></Label>
              <Input value={completeForm.updatedRecordReference}
                onChange={e => setCompleteForm(f => ({ ...f, updatedRecordReference: e.target.value }))}
                placeholder="EHR-RECORD-2026-ABC" />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={completeForm.notificationSent}
                onChange={e => setCompleteForm(f => ({ ...f, notificationSent: e.target.checked }))} />
              Individual has been notified of the amendment
            </label>
            <div><Label>Notes</Label><Textarea rows={2} value={completeForm.notes} onChange={e => setCompleteForm(f => ({ ...f, notes: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowComplete(false); setCompleteId(null); }}>Cancel</Button>
            <Button className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2"
              onClick={() => completeMut.mutate()} disabled={completeMut.isPending}>
              <CheckCircle2 className="h-4 w-4" />{completeMut.isPending ? "Completing…" : "Mark as Completed"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── HIPAA-19 — Individual Access (§ 164.524) ─────────────────────────────────

type IaSummary19 = {
  total: number; pending: number; identityPending: number;
  approved: number; processing: number; fulfilled: number;
  denied: number; overdue: number;
  averageFulfillmentHours: number; compliancePercentage: number;
};

type IaRequest19 = {
  id: string; requestNumber: string;
  requesterName: string; requesterEmail?: string;
  requestType: string; relationshipToIndividual?: string;
  requestPurpose?: string; requestedRecordTypes: string;
  designatedRecordSet?: string;
  identityVerificationStatus: string; identityVerificationMethod?: string;
  identityVerifiedDate?: string; identityVerificationRef?: string;
  identityVerified: boolean;
  requestDate: string; dueDate: string; status: string;
  assignedTo?: string; fulfillmentDate?: string;
  deliveryMethod?: string; deliveryReference?: string;
  exceptionApplied: boolean; exceptionReason?: string;
  denialReason?: string; reviewNotes?: string;
  createdAt: string; updatedAt: string;
  isOverdue: boolean; daysUntilDue: number;
};

const IA19_STATUSES     = ["Submitted","IdentityPending","Approved","Processing","Fulfilled","Denied","Cancelled","Expired"];
const IA19_REQUEST_TYPES= ["SelfAccess","AuthorizedRepresentative","LegalRepresentative","ResearchPurpose","Other"];
const IA19_RELATIONSHIPS= ["Self","Parent","Guardian","PowerOfAttorney","Other"];
const IA19_RECORD_TYPES = ["Medical Records","Clinical Notes","Diagnosis","Medication History","Laboratory Results","Imaging Reports","Billing Records","Insurance Records","Other"];
const IA19_DELIVERY     = ["Electronic","Mail","InPerson","Fax","Other"];
const IA19_EXCEPTIONS   = ["AccessDeniedByPolicy","LegalRestriction","RegulatoryException","Other"];
const IA19_VERIFY_METHODS = ["GovernmentID","Passport","DriversLicense","NotarizedDocument","VideoVerification","InPerson","Other"];

const IA19_TERMINAL = ["Fulfilled","Denied","Cancelled","Expired"];

function ia19StatusVariant(s: string): "default"|"secondary"|"outline"|"destructive" {
  if (s === "Fulfilled") return "default";
  if (["Denied","Expired"].includes(s)) return "destructive";
  if (["Cancelled"].includes(s)) return "outline";
  return "secondary";
}

function useAuth19() {
  return (url: string, opts: RequestInit = {}) => {
    const token = sessionStorage.getItem("dotnetbot.jwt.session") || localStorage.getItem("dotnetbot.jwt") || "";
    return fetch(url, { ...opts, headers: { ...(opts.headers as Record<string,string> || {}), "Content-Type": "application/json", Authorization: `Bearer ${token}` } });
  };
}

function HipaaIndividualAccessSection({ canManage }: { canManage: boolean }) {
  const { toast } = useToast();
  const authFetch = useAuth19();
  const qc = useQueryClient();
  const BASE = "/api/organization/compliance/hipaa/individual-access";

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["hipaa19-list"] });
    qc.invalidateQueries({ queryKey: ["hipaa19-summary"] });
    if (detailId) qc.invalidateQueries({ queryKey: ["hipaa19-detail", detailId] });
  };

  // ── Summary ────────────────────────────────────────────────────────────────
  const summaryQ = useQuery<IaSummary19>({
    queryKey: ["hipaa19-summary"],
    queryFn: async () => { const r = await authFetch(`${BASE}/summary`); if (!r.ok) throw new Error(); return r.json(); },
    staleTime: 0,
  });

  // ── List + filters ─────────────────────────────────────────────────────────
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter]   = useState("");
  const [requesterFilter, setRequesterFilter] = useState("");
  const [idStatusFilter, setIdStatusFilter]   = useState("");

  const listQ = useQuery<{ items: IaRequest19[]; total: number }>({
    queryKey: ["hipaa19-list", page, statusFilter, requesterFilter, idStatusFilter],
    queryFn: async () => {
      const p = new URLSearchParams({ page: String(page), pageSize: "15" });
      if (statusFilter)    p.set("status", statusFilter);
      if (requesterFilter) p.set("requester", requesterFilter);
      if (idStatusFilter)  p.set("idStatus", idStatusFilter);
      const r = await authFetch(`${BASE}?${p}`);
      if (!r.ok) throw new Error(); return r.json();
    },
    staleTime: 0,
  });

  // ── Detail ─────────────────────────────────────────────────────────────────
  const [detailId, setDetailId] = useState<string | null>(null);
  const detailQ = useQuery<IaRequest19>({
    queryKey: ["hipaa19-detail", detailId],
    queryFn: async () => { const r = await authFetch(`${BASE}/${detailId}`); if (!r.ok) throw new Error(); return r.json(); },
    enabled: !!detailId,
    staleTime: 0,
  });

  // ── Create request ─────────────────────────────────────────────────────────
  const [showCreate, setShowCreate] = useState(false);
  const defaultForm = () => ({
    requesterName: "", requesterEmail: "", requestType: "SelfAccess",
    relationshipToIndividual: "Self", requestPurpose: "",
    requestedRecordTypes: "", designatedRecordSet: "", dueDate: "",
    reviewNotes: "",
  });
  const [form, setForm] = useState(defaultForm());
  const setF = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));
  const [errors, setErrors] = useState<Record<string,string>>({});

  const createMut = useMutation({
    mutationFn: async () => {
      const body: Record<string, any> = { ...form };
      if (!body.dueDate) delete body.dueDate;
      const r = await authFetch(BASE, { method: "POST", body: JSON.stringify(body) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.error || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Access request created." }); setShowCreate(false); setForm(defaultForm()); setErrors({}); invalidate(); },
    onError: (e: any) => { const s = extractServerErrors(e); if (s && Object.keys(s).length) { setErrors(s); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); } else toast({ title: "Unable to save", description: (e as Error)?.message, variant: "destructive" }); },
  });

  // ── Verify identity ────────────────────────────────────────────────────────
  const [showVerify, setShowVerify] = useState(false);
  const [verifyId,   setVerifyId]   = useState<string | null>(null);
  const [verifyForm, setVerifyForm] = useState({ verificationMethod: "GovernmentID", verificationRef: "" });

  const verifyMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/${verifyId}/verify-identity`, { method: "PATCH", body: JSON.stringify(verifyForm) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.error || e.detail || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Identity verified." }); setShowVerify(false); setVerifyId(null); invalidate(); },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  // ── Approve ────────────────────────────────────────────────────────────────
  const approveMut = useMutation({
    mutationFn: async (id: string) => {
      const r = await authFetch(`${BASE}/${id}/approve`, { method: "PATCH", body: JSON.stringify({ reviewNotes: "" }) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.error || e.detail || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Request approved." }); invalidate(); },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  // ── Deny ───────────────────────────────────────────────────────────────────
  const [showDeny, setShowDeny] = useState(false);
  const [denyId,   setDenyId]   = useState<string | null>(null);
  const [denyForm, setDenyForm] = useState({ denialReason: "", exceptionApplied: false, exceptionReason: "", reviewNotes: "" });

  const denyMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/${denyId}/deny`, { method: "PATCH", body: JSON.stringify(denyForm) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.error || e.detail || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Request denied." }); setShowDeny(false); setDenyId(null); invalidate(); },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  // ── Fulfill ────────────────────────────────────────────────────────────────
  const [showFulfill, setShowFulfill] = useState(false);
  const [fulfillId,   setFulfillId]   = useState<string | null>(null);
  const [fulfillForm, setFulfillForm] = useState({ deliveryMethod: "Electronic", deliveryReference: "", reviewNotes: "" });

  const fulfillMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/${fulfillId}/fulfill`, { method: "PATCH", body: JSON.stringify(fulfillForm) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.error || e.detail || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Request fulfilled." }); setShowFulfill(false); setFulfillId(null); invalidate(); },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  // ── Cancel ─────────────────────────────────────────────────────────────────
  const cancelMut = useMutation({
    mutationFn: async (id: string) => {
      const r = await authFetch(`${BASE}/${id}/cancel`, { method: "PATCH", body: JSON.stringify({ reviewNotes: "" }) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.error || e.detail || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Request cancelled." }); invalidate(); },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  const s = summaryQ.data;
  const items = listQ.data?.items ?? [];
  const total = listQ.data?.total ?? 0;
  const pageSize = 15;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-teal-700" />
            Privacy — Individual Access Rights
          </CardTitle>
          <CardDescription>§ 164.524 — Support individuals' right to access their PHI in designated record sets. Track requests through identity verification, approval, fulfillment, and delivery with full audit trail.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">

          {/* Dashboard tiles */}
          {summaryQ.isLoading ? <Skeleton className="h-24 w-full" /> : s && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              <div className="rounded-lg border p-3 text-center">
                <p className="text-2xl font-bold">{s.total}</p>
                <p className="text-xs text-muted-foreground">Total</p>
              </div>
              <div className={`rounded-lg border p-3 text-center ${s.pending > 0 ? "border-amber-300 bg-amber-50" : ""}`}>
                <p className="text-2xl font-bold">{s.pending}</p>
                <p className="text-xs text-muted-foreground">Pending</p>
              </div>
              <div className={`rounded-lg border p-3 text-center ${s.identityPending > 0 ? "border-amber-300 bg-amber-50" : ""}`}>
                <p className="text-2xl font-bold">{s.identityPending}</p>
                <p className="text-xs text-muted-foreground">ID Pending</p>
              </div>
              <div className="rounded-lg border p-3 text-center">
                <p className="text-2xl font-bold">{s.approved}</p>
                <p className="text-xs text-muted-foreground">Approved</p>
              </div>
              <div className="rounded-lg border p-3 text-center">
                <p className="text-2xl font-bold">{s.processing}</p>
                <p className="text-xs text-muted-foreground">Processing</p>
              </div>
              <div className="rounded-lg border p-3 text-center border-teal-300 bg-teal-50">
                <p className="text-2xl font-bold text-teal-700">{s.fulfilled}</p>
                <p className="text-xs text-muted-foreground">Fulfilled</p>
              </div>
              <div className="rounded-lg border p-3 text-center">
                <p className="text-2xl font-bold">{s.denied}</p>
                <p className="text-xs text-muted-foreground">Denied</p>
              </div>
              <div className={`rounded-lg border p-3 text-center ${s.overdue > 0 ? "border-red-400 bg-red-50" : ""}`}>
                <p className={`text-2xl font-bold ${s.overdue > 0 ? "text-red-600" : ""}`}>{s.overdue}</p>
                <p className="text-xs text-muted-foreground">Overdue</p>
              </div>
              <div className="rounded-lg border p-3 text-center">
                <p className="text-2xl font-bold">{s.averageFulfillmentHours}h</p>
                <p className="text-xs text-muted-foreground">Avg. Fulfillment</p>
              </div>
              <div className={`rounded-lg border p-3 text-center ${s.compliancePercentage < 100 ? "border-amber-400 bg-amber-50" : "border-teal-400 bg-teal-50"}`}>
                <p className="text-2xl font-bold text-teal-700">{s.compliancePercentage}%</p>
                <p className="text-xs text-muted-foreground">Compliance</p>
              </div>
            </div>
          )}

          {/* Overdue alert */}
          {s && s.overdue > 0 && (
            <div className="flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span><strong>{s.overdue}</strong> request{s.overdue > 1 ? "s are" : " is"} past the HIPAA 30-day response deadline.</span>
            </div>
          )}

          {/* Toolbar */}
          <div className="flex flex-wrap gap-2 items-end">
            <div>
              <Label className="text-xs">Status</Label>
              <select className="border rounded px-2 py-1 text-sm h-8" value={statusFilter}
                onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
                <option value="">All</option>
                {IA19_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Requester</Label>
              <Input className="h-8 text-sm w-36" placeholder="Name or email…" value={requesterFilter}
                onChange={e => { setRequesterFilter(e.target.value); setPage(1); }} />
            </div>
            <div>
              <Label className="text-xs">Identity Status</Label>
              <select className="border rounded px-2 py-1 text-sm h-8" value={idStatusFilter}
                onChange={e => { setIdStatusFilter(e.target.value); setPage(1); }}>
                <option value="">All</option>
                <option value="Pending">Pending</option>
                <option value="Verified">Verified</option>
                <option value="Unable to complete action">Failed</option>
              </select>
            </div>
            {canManage && (
              <Button className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2 h-8 ml-auto"
                onClick={() => setShowCreate(true)}>
                <Plus className="h-4 w-4" /> New Request
              </Button>
            )}
          </div>

          {/* List */}
          {listQ.isLoading ? <Skeleton className="h-40 w-full" /> : items.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No access requests found. Click "New Request" to create one.</p>
          ) : (
            <div className="space-y-2">
              {items.map(r => (
                <div key={r.id}
                  className={`flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer hover:bg-muted/40 transition-colors ${r.isOverdue ? "border-red-400 bg-red-50/20" : ""}`}
                  onClick={() => setDetailId(r.id)}>
                  <div className="flex-1 min-w-0">
                    <span className="font-mono text-xs text-muted-foreground mr-2">{r.requestNumber}</span>
                    <span className="font-medium">{r.requesterName}</span>
                    {r.requesterEmail && <span className="ml-1 text-xs text-muted-foreground">· {r.requesterEmail}</span>}
                  </div>
                  <span className="text-xs text-muted-foreground">{r.requestedRecordTypes.split(",")[0]}{r.requestedRecordTypes.split(",").length > 1 ? ` +${r.requestedRecordTypes.split(",").length - 1}` : ""}</span>
                  <Badge variant={r.identityVerified ? "default" : "outline"} className={`text-xs ${r.identityVerified ? "bg-teal-700 text-white" : "border-amber-400 text-amber-700"}`}>
                    {r.identityVerified ? "ID Verified" : "ID Pending"}
                  </Badge>
                  <Badge variant={ia19StatusVariant(r.status)} className="text-xs">{r.status}</Badge>
                  {r.isOverdue && <span className="text-xs text-red-600 font-medium">Overdue</span>}
                  <span className="text-xs text-muted-foreground">Due: {new Date(r.dueDate).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {total > pageSize && (
            <div className="flex items-center gap-2 justify-center">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
              <span className="text-sm text-muted-foreground">Page {page} of {Math.ceil(total / pageSize)}</span>
              <Button variant="outline" size="sm" disabled={page * pageSize >= total} onClick={() => setPage(p => p + 1)}>Next</Button>
            </div>
          )}

          {/* Detail panel */}
          {detailId && detailQ.data && (() => {
            const r = detailQ.data;
            const isTerminal = IA19_TERMINAL.includes(r.status);
            return (
              <div className="rounded-lg border p-4 space-y-4 bg-muted/20">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <span className="font-mono text-xs text-muted-foreground">{r.requestNumber}</span>
                    <h4 className="font-semibold">{r.requesterName}</h4>
                  </div>
                  <div className="flex gap-2 items-center flex-wrap">
                    <Badge variant={ia19StatusVariant(r.status)}>{r.status}</Badge>
                    {r.isOverdue && <Badge variant="destructive">Overdue</Badge>}
                    {r.exceptionApplied && <Badge variant="outline" className="border-red-400 text-red-700">Exception Applied</Badge>}
                    <Button variant="ghost" size="sm" onClick={() => setDetailId(null)}>✕</Button>
                  </div>
                </div>

                {/* Request info grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                  <div><p className="text-muted-foreground">Request Type</p><p className="font-medium">{r.requestType}</p></div>
                  <div><p className="text-muted-foreground">Relationship</p><p className="font-medium">{r.relationshipToIndividual ?? "—"}</p></div>
                  <div><p className="text-muted-foreground">Email</p><p className="font-medium">{r.requesterEmail ?? "—"}</p></div>
                  <div><p className="text-muted-foreground">Record Types</p><p className="font-medium">{r.requestedRecordTypes}</p></div>
                  <div><p className="text-muted-foreground">Record Set</p><p className="font-medium">{r.designatedRecordSet ?? "—"}</p></div>
                  <div><p className="text-muted-foreground">Purpose</p><p className="font-medium">{r.requestPurpose ?? "—"}</p></div>
                  <div><p className="text-muted-foreground">Request Date</p><p className="font-medium">{new Date(r.requestDate).toLocaleDateString()}</p></div>
                  <div><p className="text-muted-foreground">Due Date</p><p className={`font-medium ${r.isOverdue ? "text-red-600" : ""}`}>{new Date(r.dueDate).toLocaleDateString()}</p></div>
                  {r.fulfillmentDate && <div><p className="text-muted-foreground">Fulfilled</p><p className="font-medium text-teal-700">{new Date(r.fulfillmentDate).toLocaleDateString()}</p></div>}
                </div>

                {/* Identity verification */}
                <div className={`rounded p-3 text-xs border ${r.identityVerified ? "border-teal-300 bg-teal-50" : "border-amber-300 bg-amber-50"}`}>
                  <p className={`font-semibold mb-1 ${r.identityVerified ? "text-teal-700" : "text-amber-700"}`}>
                    Identity Verification — {r.identityVerificationStatus}
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {r.identityVerificationMethod && <div><span className="text-muted-foreground">Method:</span> {r.identityVerificationMethod}</div>}
                    {r.identityVerifiedDate && <div><span className="text-muted-foreground">Verified:</span> {new Date(r.identityVerifiedDate).toLocaleDateString()}</div>}
                    {r.identityVerificationRef && <div><span className="text-muted-foreground">Reference:</span> {r.identityVerificationRef}</div>}
                  </div>
                </div>

                {/* Delivery */}
                {r.status === "Fulfilled" && (r.deliveryMethod || r.deliveryReference) && (
                  <div className="rounded p-3 text-xs border border-teal-300 bg-teal-50">
                    <p className="font-semibold text-teal-700 mb-1">Delivery</p>
                    <div className="grid grid-cols-2 gap-2">
                      {r.deliveryMethod && <div><span className="text-muted-foreground">Method:</span> {r.deliveryMethod}</div>}
                      {r.deliveryReference && <div><span className="text-muted-foreground">Reference:</span> {r.deliveryReference}</div>}
                    </div>
                  </div>
                )}

                {/* Exception / denial */}
                {(r.exceptionApplied || r.denialReason) && (
                  <div className="rounded p-3 text-xs border border-red-300 bg-red-50">
                    <p className="font-semibold text-red-700 mb-1">Denial / Exception</p>
                    {r.exceptionReason && <p><span className="text-muted-foreground">Exception:</span> {r.exceptionReason}</p>}
                    {r.denialReason && <p className="mt-1">{r.denialReason}</p>}
                  </div>
                )}

                {r.reviewNotes && <p className="text-xs text-muted-foreground italic">{r.reviewNotes}</p>}

                {/* Actions */}
                {canManage && !isTerminal && (
                  <div className="flex flex-wrap gap-2">
                    {!r.identityVerified && (
                      <Button size="sm" variant="outline" className="border-amber-400 text-amber-700 hover:bg-amber-50 gap-1"
                        onClick={() => { setVerifyId(r.id); setShowVerify(true); }}>
                        <CheckCircle2 className="h-3.5 w-3.5" /> Verify Identity
                      </Button>
                    )}
                    {["IdentityPending"].includes(r.status) && r.identityVerified && (
                      <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-1"
                        onClick={() => approveMut.mutate(r.id)} disabled={approveMut.isPending}>
                        <CheckCircle2 className="h-3.5 w-3.5" /> Approve
                      </Button>
                    )}
                    {r.status === "Approved" && (
                      <Button size="sm" variant="outline" className="gap-1"
                        onClick={async () => {
                          const res = await authFetch(`${BASE}/${r.id}/approve`, { method: "PATCH", body: JSON.stringify({ reviewNotes: "" }) });
                          if (!res.ok) { toast({ title: "Unable to complete action", variant: "destructive" }); return; }
                          // Move to processing first
                          const res2 = await authFetch(`${BASE}/${r.id}`, { method: "PUT", body: JSON.stringify({ status: "Processing" }) });
                          invalidate();
                        }}>
                        Start Processing
                      </Button>
                    )}
                    {r.status === "Processing" && (
                      <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-1"
                        onClick={() => { setFulfillId(r.id); setShowFulfill(true); }}>
                        <CheckCircle2 className="h-3.5 w-3.5" /> Fulfill Request
                      </Button>
                    )}
                    <Button size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-50 gap-1"
                      onClick={() => { setDenyId(r.id); setShowDeny(true); }}>
                      Deny
                    </Button>
                    <Button size="sm" variant="ghost" className="gap-1 text-muted-foreground"
                      onClick={() => cancelMut.mutate(r.id)} disabled={cancelMut.isPending}>
                      Cancel Request
                    </Button>
                  </div>
                )}
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={v => { setShowCreate(v); if (!v) setErrors({}); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Individual Access Request</DialogTitle>
            <DialogDescription>Create a PHI access request under § 164.524. A 30-day response deadline applies by default.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Requester Name *</Label>
                <Input className={fieldCls("", errors, "requesterName")} aria-invalid={!!errors.requesterName} value={form.requesterName} onChange={e => { setF("requesterName", e.target.value); clearErr(setErrors, "requesterName"); }} placeholder="Full legal name" />
                {fErr(errors, "requesterName")}
              </div>
              <div><Label>Email</Label><Input type="email" value={form.requesterEmail} onChange={e => setF("requesterEmail", e.target.value)} placeholder="requester@example.com" /></div>
              <div>
                <Label>Request Type *</Label>
                <select className="border rounded px-2 py-1 text-sm w-full" value={form.requestType} onChange={e => setF("requestType", e.target.value)}>
                  {IA19_REQUEST_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <Label>Relationship to Individual</Label>
                <select className="border rounded px-2 py-1 text-sm w-full" value={form.relationshipToIndividual} onChange={e => setF("relationshipToIndividual", e.target.value)}>
                  {IA19_RELATIONSHIPS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <Label>Due Date <span className="text-muted-foreground text-xs">(default 30 days)</span></Label>
                <Input type="date" value={form.dueDate} onChange={e => setF("dueDate", e.target.value)} />
              </div>
            </div>
            <div>
              <Label>Requested Record Types * <span className="text-muted-foreground text-xs">(comma-separated)</span></Label>
              <div className="flex flex-wrap gap-1 mb-1">
                {IA19_RECORD_TYPES.map(rt => (
                  <button key={rt} type="button"
                    className={`px-2 py-0.5 rounded-full text-xs border transition-colors ${form.requestedRecordTypes.split(",").map(s => s.trim()).includes(rt) ? "bg-teal-700 text-white border-teal-700" : "border-muted-foreground text-muted-foreground hover:border-teal-700"}`}
                    onClick={() => {
                      const selected = form.requestedRecordTypes.split(",").map(s => s.trim()).filter(Boolean);
                      if (selected.includes(rt)) setF("requestedRecordTypes", selected.filter(s => s !== rt).join(", "));
                      else setF("requestedRecordTypes", [...selected, rt].join(", "));
                    }}>{rt}</button>
                ))}
              </div>
              <Input value={form.requestedRecordTypes} onChange={e => { setF("requestedRecordTypes", e.target.value); clearErr(setErrors, "requestedRecordTypes"); }} placeholder="Medical Records, Diagnosis…" />
              {fErr(errors, "requestedRecordTypes")}
            </div>
            <div>
              <Label>Primary Designated Record Set</Label>
              <select className="border rounded px-2 py-1 text-sm w-full" value={form.designatedRecordSet} onChange={e => setF("designatedRecordSet", e.target.value)}>
                <option value="">Select…</option>
                {IA19_RECORD_TYPES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div><Label>Request Purpose</Label><Textarea rows={2} value={form.requestPurpose} onChange={e => setF("requestPurpose", e.target.value)} placeholder="Why is this access needed?" /></div>
            <div><Label>Review Notes</Label><Textarea rows={2} value={form.reviewNotes} onChange={e => setF("reviewNotes", e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2"
              onClick={() => { const errs: Record<string,string> = {}; required(errs, "requesterName", form.requesterName, "Requester Name"); required(errs, "requestedRecordTypes", form.requestedRecordTypes, "Requested Record Types"); if (Object.keys(errs).length) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } setErrors({}); createMut.mutate(); }} disabled={createMut.isPending}>
              <Plus className="h-4 w-4" />{createMut.isPending ? "Creating…" : "Create Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Verify identity dialog */}
      <Dialog open={showVerify} onOpenChange={o => { if (!o) { setShowVerify(false); setVerifyId(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Verify Requester Identity</DialogTitle>
            <DialogDescription>Record the identity verification method and reference document used to confirm the requester's identity.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <Label>Verification Method *</Label>
              <select className="border rounded px-2 py-1 text-sm w-full" value={verifyForm.verificationMethod}
                onChange={e => setVerifyForm(f => ({ ...f, verificationMethod: e.target.value }))}>
                {IA19_VERIFY_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <Label>Verification Reference <span className="text-muted-foreground text-xs">(document number, ticket ref, etc.)</span></Label>
              <Input value={verifyForm.verificationRef}
                onChange={e => setVerifyForm(f => ({ ...f, verificationRef: e.target.value }))}
                placeholder="e.g. DL-2026-12345" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowVerify(false); setVerifyId(null); }}>Cancel</Button>
            <Button className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2"
              onClick={() => verifyMut.mutate()} disabled={verifyMut.isPending}>
              <CheckCircle2 className="h-4 w-4" />{verifyMut.isPending ? "Verifying…" : "Confirm Identity Verified"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deny dialog */}
      <Dialog open={showDeny} onOpenChange={o => { if (!o) { setShowDeny(false); setDenyId(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deny Access Request</DialogTitle>
            <DialogDescription>Provide a denial reason. If an exception applies, record the exception type.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <Label>Denial Reason</Label>
              <Textarea rows={3} value={denyForm.denialReason}
                onChange={e => setDenyForm(f => ({ ...f, denialReason: e.target.value }))}
                placeholder="Reason for denial…" />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={denyForm.exceptionApplied}
                onChange={e => setDenyForm(f => ({ ...f, exceptionApplied: e.target.checked }))} />
              HIPAA exception applies
            </label>
            {denyForm.exceptionApplied && (
              <div>
                <Label>Exception Type</Label>
                <select className="border rounded px-2 py-1 text-sm w-full" value={denyForm.exceptionReason}
                  onChange={e => setDenyForm(f => ({ ...f, exceptionReason: e.target.value }))}>
                  <option value="">Select…</option>
                  {IA19_EXCEPTIONS.map(e => <option key={e} value={e}>{e}</option>)}
                </select>
              </div>
            )}
            <div>
              <Label>Review Notes</Label>
              <Textarea rows={2} value={denyForm.reviewNotes}
                onChange={e => setDenyForm(f => ({ ...f, reviewNotes: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowDeny(false); setDenyId(null); }}>Cancel</Button>
            <Button variant="destructive" onClick={() => denyMut.mutate()} disabled={denyMut.isPending}>
              {denyMut.isPending ? "Denying…" : "Deny Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fulfill dialog */}
      <Dialog open={showFulfill} onOpenChange={o => { if (!o) { setShowFulfill(false); setFulfillId(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Fulfill Access Request</DialogTitle>
            <DialogDescription>Record how the PHI was delivered to the requester. This completes the access request workflow.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <Label>Delivery Method</Label>
              <select className="border rounded px-2 py-1 text-sm w-full" value={fulfillForm.deliveryMethod}
                onChange={e => setFulfillForm(f => ({ ...f, deliveryMethod: e.target.value }))}>
                {IA19_DELIVERY.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <Label>Delivery Reference <span className="text-muted-foreground text-xs">(tracking ID, portal link, etc.)</span></Label>
              <Input value={fulfillForm.deliveryReference}
                onChange={e => setFulfillForm(f => ({ ...f, deliveryReference: e.target.value }))}
                placeholder="e.g. SECURE-PORTAL-LINK-2026" />
            </div>
            <div><Label>Review Notes</Label><Textarea rows={2} value={fulfillForm.reviewNotes}
              onChange={e => setFulfillForm(f => ({ ...f, reviewNotes: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowFulfill(false); setFulfillId(null); }}>Cancel</Button>
            <Button className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2"
              onClick={() => fulfillMut.mutate()} disabled={fulfillMut.isPending}>
              <CheckCircle2 className="h-4 w-4" />{fulfillMut.isPending ? "Recording…" : "Mark as Fulfilled"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── HIPAA-18 — Privacy Minimum Necessary (§ 164.514(d)) ─────────────────────

type MnSummary = {
  total: number;
  active: number;
  disabled: number;
  expired: number;
  approvalRequired: number;
  disclosureRules: number;
  reviewOverdue: number;
  failedAccessChecks: number;
  compliancePercentage: number;
};

type MnRule = {
  id: string;
  ruleName: string;
  description?: string;
  userRole?: string;
  department?: string;
  accessPurpose?: string;
  phiDataCategory?: string;
  allowedDataElements?: string;
  restrictedDataElements?: string;
  accessLevel?: string;
  disclosureAllowed: boolean;
  minimumNecessaryRequired: boolean;
  approvalRequired: boolean;
  reviewRequired: boolean;
  status: string;
  effectiveDate?: string;
  expirationDate?: string;
  lastReviewedDate?: string;
  nextReviewDate?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  isExpired: boolean;
  isReviewOverdue: boolean;
};

type MnCheckResult = {
  allowed: boolean;
  denialCodes: string[];
  appliedRuleId?: string;
  approvalRequired: boolean;
  appliedRule?: MnRule;
};

const MN_PHI_CATEGORIES = ["Demographics","Contact Information","Medical Records","Diagnosis","Medication","Laboratory Results","Billing Information","Insurance Information","Clinical Notes","Other"];
const MN_ACCESS_LEVELS  = ["Read","Write","ReadWrite","None"];
const MN_STATUSES       = ["Active","Disabled","Expired"];

function mnStatusBadge(s: string): "default"|"secondary"|"outline"|"destructive" {
  if (s === "Active")   return "default";
  if (s === "Disabled") return "outline";
  if (s === "Expired")  return "destructive";
  return "secondary";
}

function useAuth18() {
  return (url: string, opts: RequestInit = {}) => {
    const token = sessionStorage.getItem("dotnetbot.jwt.session") || localStorage.getItem("dotnetbot.jwt") || "";
    return fetch(url, { ...opts, headers: { ...(opts.headers as Record<string,string> || {}), "Content-Type": "application/json", Authorization: `Bearer ${token}` } });
  };
}

function HipaaMinimumNecessarySection({ canManage }: { canManage: boolean }) {
  const { toast } = useToast();
  const authFetch = useAuth18();
  const qc = useQueryClient();
  const BASE = "/api/organization/compliance/hipaa/minimum-necessary";

  // ── Summary ────────────────────────────────────────────────────────────────
  const summaryQ = useQuery<MnSummary>({
    queryKey: ["hipaa18-summary"],
    queryFn: async () => { const r = await authFetch(`${BASE}/summary`); if (!r.ok) throw new Error(); return r.json(); },
    staleTime: 0,
  });

  // ── Filters + list ─────────────────────────────────────────────────────────
  const [page, setPage]           = useState(1);
  const [roleFilter, setRoleFilter]   = useState("");
  const [deptFilter, setDeptFilter]   = useState("");
  const [catFilter,  setCatFilter]    = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [approvalFilter, setApprovalFilter] = useState("");

  const listQ = useQuery<{ items: MnRule[]; total: number }>({
    queryKey: ["hipaa18-list", page, roleFilter, deptFilter, catFilter, statusFilter, approvalFilter],
    queryFn: async () => {
      const p = new URLSearchParams({ page: String(page), pageSize: "15" });
      if (roleFilter)     p.set("role", roleFilter);
      if (deptFilter)     p.set("department", deptFilter);
      if (catFilter)      p.set("phiCategory", catFilter);
      if (statusFilter)   p.set("status", statusFilter);
      if (approvalFilter) p.set("approvalRequired", approvalFilter);
      const r = await authFetch(`${BASE}?${p}`);
      if (!r.ok) throw new Error(); return r.json();
    },
    staleTime: 0,
  });

  // ── Detail ─────────────────────────────────────────────────────────────────
  const [detailId, setDetailId] = useState<string | null>(null);
  const detailQ = useQuery<MnRule>({
    queryKey: ["hipaa18-detail", detailId],
    queryFn: async () => { const r = await authFetch(`${BASE}/${detailId}`); if (!r.ok) throw new Error(); return r.json(); },
    enabled: !!detailId,
    staleTime: 0,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["hipaa18-list"] });
    qc.invalidateQueries({ queryKey: ["hipaa18-summary"] });
    if (detailId) qc.invalidateQueries({ queryKey: ["hipaa18-detail", detailId] });
  };

  // ── Create / Edit ──────────────────────────────────────────────────────────
  const [showCreate, setShowCreate] = useState(false);
  const defaultForm = () => ({
    ruleName: "", description: "",
    userRole: "", department: "", accessPurpose: "",
    phiDataCategory: "", allowedDataElements: "", restrictedDataElements: "",
    accessLevel: "Read",
    disclosureAllowed: false, minimumNecessaryRequired: true,
    approvalRequired: false, reviewRequired: false,
    status: "Active", effectiveDate: "", expirationDate: "", nextReviewDate: "",
    notes: "",
  });
  const [form, setForm] = useState(defaultForm());
  const setF = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));
  const [errors, setErrors] = useState<Record<string,string>>({});

  const createMut = useMutation({
    mutationFn: async () => {
      const body: Record<string, any> = { ...form };
      if (!body.effectiveDate) delete body.effectiveDate;
      if (!body.expirationDate) delete body.expirationDate;
      if (!body.nextReviewDate) delete body.nextReviewDate;
      const r = await authFetch(BASE, { method: "POST", body: JSON.stringify(body) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.error || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Rule created." }); setShowCreate(false); setForm(defaultForm()); setErrors({}); invalidate(); },
    onError: (e: any) => { const s = extractServerErrors(e); if (s && Object.keys(s).length) { setErrors(s); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); } else toast({ title: "Unable to save", description: (e as Error)?.message, variant: "destructive" }); },
  });

  // ── Activate / Disable ─────────────────────────────────────────────────────
  const activateMut = useMutation({
    mutationFn: async (id: string) => {
      const r = await authFetch(`${BASE}/${id}/activate`, { method: "PATCH", body: JSON.stringify({}) });
      if (!r.ok) throw new Error(); return r.json();
    },
    onSuccess: () => { toast({ title: "Rule activated." }); invalidate(); },
    onError: () => toast({ title: "Unable to complete action", variant: "destructive" }),
  });
  const disableMut = useMutation({
    mutationFn: async (id: string) => {
      const r = await authFetch(`${BASE}/${id}/disable`, { method: "PATCH", body: JSON.stringify({}) });
      if (!r.ok) throw new Error(); return r.json();
    },
    onSuccess: () => { toast({ title: "Rule disabled." }); invalidate(); },
    onError: () => toast({ title: "Unable to complete action", variant: "destructive" }),
  });

  // ── Access Check ───────────────────────────────────────────────────────────
  const [showCheck, setShowCheck] = useState(false);
  const [checkForm, setCheckForm] = useState({
    userRole: "", department: "", accessPurpose: "",
    phiDataCategory: "", requestedDataElements: "", disclosureRequested: false,
  });
  const [checkResult, setCheckResult] = useState<MnCheckResult | null>(null);

  const checkMut = useMutation({
    mutationFn: async () => {
      const body = {
        userRole: checkForm.userRole,
        department: checkForm.department || null,
        accessPurpose: checkForm.accessPurpose || null,
        phiDataCategory: checkForm.phiDataCategory || null,
        requestedDataElements: checkForm.requestedDataElements
          ? checkForm.requestedDataElements.split(",").map(s => s.trim()).filter(Boolean)
          : [],
        disclosureRequested: checkForm.disclosureRequested,
      };
      const r = await authFetch(`${BASE}/check`, { method: "POST", body: JSON.stringify(body) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.error || "Unable to complete action"); }
      return r.json() as Promise<MnCheckResult>;
    },
    onSuccess: (data) => { setCheckResult(data); invalidate(); },
    onError: (e: Error) => toast({ title: "Check error", description: e.message, variant: "destructive" }),
  });

  const s = summaryQ.data;
  const items = listQ.data?.items ?? [];
  const total = listQ.data?.total ?? 0;
  const pageSize = 15;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-teal-700" />
            Privacy — Minimum Necessary
          </CardTitle>
          <CardDescription>§ 164.514(d) — Limit PHI use, disclosure, and requests to the minimum necessary for the intended purpose. Configure role-based and purpose-based access rules.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">

          {/* Dashboard tiles */}
          {summaryQ.isLoading ? <Skeleton className="h-24 w-full" /> : s && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="rounded-lg border p-3 text-center">
                <p className="text-2xl font-bold">{s.total}</p>
                <p className="text-xs text-muted-foreground">Total Rules</p>
              </div>
              <div className="rounded-lg border p-3 text-center border-teal-300 bg-teal-50">
                <p className="text-2xl font-bold text-teal-700">{s.active}</p>
                <p className="text-xs text-muted-foreground">Active Rules</p>
              </div>
              <div className={`rounded-lg border p-3 text-center ${s.expired > 0 ? "border-red-400 bg-red-50" : ""}`}>
                <p className={`text-2xl font-bold ${s.expired > 0 ? "text-red-600" : ""}`}>{s.expired}</p>
                <p className="text-xs text-muted-foreground">Expired Rules</p>
              </div>
              <div className={`rounded-lg border p-3 text-center ${s.approvalRequired > 0 ? "border-amber-400 bg-amber-50" : ""}`}>
                <p className="text-2xl font-bold">{s.approvalRequired}</p>
                <p className="text-xs text-muted-foreground">Require Approval</p>
              </div>
              <div className={`rounded-lg border p-3 text-center ${s.reviewOverdue > 0 ? "border-amber-400 bg-amber-50" : ""}`}>
                <p className="text-2xl font-bold">{s.reviewOverdue}</p>
                <p className="text-xs text-muted-foreground">Reviews Overdue</p>
              </div>
              <div className="rounded-lg border p-3 text-center">
                <p className="text-2xl font-bold">{s.disclosureRules}</p>
                <p className="text-xs text-muted-foreground">Disclosure Rules</p>
              </div>
              <div className="rounded-lg border p-3 text-center">
                <p className="text-2xl font-bold">{s.disabled}</p>
                <p className="text-xs text-muted-foreground">Disabled</p>
              </div>
              <div className={`rounded-lg border p-3 text-center ${s.compliancePercentage < 100 ? "border-amber-400 bg-amber-50" : "border-teal-400 bg-teal-50"}`}>
                <p className="text-2xl font-bold text-teal-700">{s.compliancePercentage}%</p>
                <p className="text-xs text-muted-foreground">Compliance Rate</p>
              </div>
            </div>
          )}

          {/* Alert strip */}
          {s && s.reviewOverdue > 0 && (
            <div className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-700">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span><strong>{s.reviewOverdue}</strong> rule{s.reviewOverdue > 1 ? "s" : ""} {s.reviewOverdue > 1 ? "are" : "is"} overdue for review.</span>
            </div>
          )}

          {/* Toolbar */}
          <div className="flex flex-wrap gap-2 items-end">
            <div>
              <Label className="text-xs">Role</Label>
              <Input className="h-8 text-sm w-32" placeholder="Filter…" value={roleFilter}
                onChange={e => { setRoleFilter(e.target.value); setPage(1); }} />
            </div>
            <div>
              <Label className="text-xs">Department</Label>
              <Input className="h-8 text-sm w-32" placeholder="Filter…" value={deptFilter}
                onChange={e => { setDeptFilter(e.target.value); setPage(1); }} />
            </div>
            <div>
              <Label className="text-xs">PHI Category</Label>
              <select className="border rounded px-2 py-1 text-sm h-8" value={catFilter}
                onChange={e => { setCatFilter(e.target.value); setPage(1); }}>
                <option value="">All</option>
                {MN_PHI_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <select className="border rounded px-2 py-1 text-sm h-8" value={statusFilter}
                onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
                <option value="">All</option>
                {MN_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Approval</Label>
              <select className="border rounded px-2 py-1 text-sm h-8" value={approvalFilter}
                onChange={e => { setApprovalFilter(e.target.value); setPage(1); }}>
                <option value="">All</option>
                <option value="true">Required</option>
                <option value="false">Not Required</option>
              </select>
            </div>
            <div className="flex gap-2 ml-auto">
              <Button variant="outline" className="gap-2 h-8"
                onClick={() => { setShowCheck(true); setCheckResult(null); }}>
                <CheckCircle2 className="h-4 w-4" /> Run Access Check
              </Button>
              {canManage && (
                <Button className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2 h-8"
                  onClick={() => setShowCreate(true)}>
                  <Plus className="h-4 w-4" /> Add Rule
                </Button>
              )}
            </div>
          </div>

          {/* List */}
          {listQ.isLoading ? <Skeleton className="h-32 w-full" /> : items.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No minimum necessary rules found. Add one to define PHI access boundaries.</p>
          ) : (
            <div className="space-y-2">
              {items.map(r => (
                <div key={r.id}
                  className={`flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer hover:bg-muted/40 transition-colors ${r.isExpired ? "border-red-300 bg-red-50/30" : ""} ${r.isReviewOverdue ? "border-amber-400" : ""}`}
                  onClick={() => setDetailId(r.id)}>
                  <div className="flex-1 min-w-0">
                    <span className="font-medium truncate">{r.ruleName}</span>
                    {r.userRole && <span className="ml-2 text-xs text-muted-foreground">Role: {r.userRole}</span>}
                    {r.department && <span className="ml-1 text-xs text-muted-foreground">· {r.department}</span>}
                  </div>
                  {r.phiDataCategory && <Badge variant="outline" className="text-xs">{r.phiDataCategory}</Badge>}
                  <Badge variant={mnStatusBadge(r.status)} className="text-xs">{r.status}</Badge>
                  {r.approvalRequired && <Badge variant="outline" className="text-xs border-amber-400 text-amber-700">Approval Required</Badge>}
                  {r.disclosureAllowed && <Badge variant="secondary" className="text-xs">Disclosure OK</Badge>}
                  {r.isReviewOverdue && <span className="text-xs text-amber-600 font-medium">Review overdue</span>}
                  {r.nextReviewDate && <span className="text-xs text-muted-foreground">Review: {new Date(r.nextReviewDate).toLocaleDateString()}</span>}
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {total > pageSize && (
            <div className="flex items-center gap-2 justify-center">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
              <span className="text-sm text-muted-foreground">Page {page} of {Math.ceil(total / pageSize)}</span>
              <Button variant="outline" size="sm" disabled={page * pageSize >= total} onClick={() => setPage(p => p + 1)}>Next</Button>
            </div>
          )}

          {/* Detail view */}
          {detailId && detailQ.data && (() => {
            const r = detailQ.data;
            return (
              <div className="rounded-lg border p-4 space-y-4 bg-muted/20">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h4 className="font-semibold">{r.ruleName}</h4>
                  <div className="flex gap-2 flex-wrap items-center">
                    <Badge variant={mnStatusBadge(r.status)}>{r.status}</Badge>
                    {r.approvalRequired && <Badge variant="outline" className="border-amber-400 text-amber-700">Approval Required</Badge>}
                    {r.isExpired && <Badge variant="destructive">Expired</Badge>}
                    <Button variant="ghost" size="sm" onClick={() => setDetailId(null)}>✕</Button>
                  </div>
                </div>

                {r.description && <p className="text-sm text-muted-foreground">{r.description}</p>}

                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                  <div><p className="text-muted-foreground">Role</p><p className="font-medium">{r.userRole ?? "—"}</p></div>
                  <div><p className="text-muted-foreground">Department</p><p className="font-medium">{r.department ?? "—"}</p></div>
                  <div><p className="text-muted-foreground">Purpose</p><p className="font-medium">{r.accessPurpose ?? "—"}</p></div>
                  <div><p className="text-muted-foreground">PHI Category</p><p className="font-medium">{r.phiDataCategory ?? "—"}</p></div>
                  <div><p className="text-muted-foreground">Access Level</p><p className="font-medium">{r.accessLevel ?? "—"}</p></div>
                  <div><p className="text-muted-foreground">Min. Necessary</p><p className="font-medium">{r.minimumNecessaryRequired ? "Yes" : "No"}</p></div>
                  <div><p className="text-muted-foreground">Disclosure</p><p className={`font-medium ${r.disclosureAllowed ? "text-teal-700" : "text-muted-foreground"}`}>{r.disclosureAllowed ? "Allowed" : "Not Allowed"}</p></div>
                  {r.effectiveDate && <div><p className="text-muted-foreground">Effective</p><p className="font-medium">{new Date(r.effectiveDate).toLocaleDateString()}</p></div>}
                  {r.expirationDate && <div><p className="text-muted-foreground">Expires</p><p className={`font-medium ${r.isExpired ? "text-red-600" : ""}`}>{new Date(r.expirationDate).toLocaleDateString()}</p></div>}
                  {r.lastReviewedDate && <div><p className="text-muted-foreground">Last Review</p><p className="font-medium">{new Date(r.lastReviewedDate).toLocaleDateString()}</p></div>}
                  {r.nextReviewDate && <div><p className="text-muted-foreground">Next Review</p><p className={`font-medium ${r.isReviewOverdue ? "text-amber-600" : ""}`}>{new Date(r.nextReviewDate).toLocaleDateString()}</p></div>}
                </div>

                {r.allowedDataElements && (
                  <div className="rounded bg-teal-50 border border-teal-200 p-2 text-xs">
                    <p className="font-semibold text-teal-700 mb-1">Allowed Data Elements</p>
                    <div className="flex flex-wrap gap-1">
                      {r.allowedDataElements.split(",").map(e => e.trim()).filter(Boolean).map(el => (
                        <span key={el} className="px-2 py-0.5 rounded-full bg-teal-100 text-teal-700">{el}</span>
                      ))}
                    </div>
                  </div>
                )}

                {r.restrictedDataElements && (
                  <div className="rounded bg-red-50 border border-red-200 p-2 text-xs">
                    <p className="font-semibold text-red-700 mb-1">Restricted Data Elements</p>
                    <div className="flex flex-wrap gap-1">
                      {r.restrictedDataElements.split(",").map(e => e.trim()).filter(Boolean).map(el => (
                        <span key={el} className="px-2 py-0.5 rounded-full bg-red-100 text-red-700">{el}</span>
                      ))}
                    </div>
                  </div>
                )}

                {r.notes && <p className="text-xs text-muted-foreground">{r.notes}</p>}

                <LinkedSourceRow entityType="HipaaMinNecessaryAccessPolicy" entityId={r.id} canManage={canManage} />

                {canManage && (
                  <div className="flex flex-wrap gap-2">
                    {r.status !== "Active" ? (
                      <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2"
                        onClick={() => activateMut.mutate(r.id)} disabled={activateMut.isPending}>
                        <CheckCircle2 className="h-3.5 w-3.5" /> Activate Rule
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" className="border-amber-400 text-amber-700 hover:bg-amber-50"
                        onClick={() => disableMut.mutate(r.id)} disabled={disableMut.isPending}>
                        Disable Rule
                      </Button>
                    )}
                    <Button size="sm" variant="outline"
                      onClick={() => { setShowCheck(true); setCheckResult(null); setCheckForm(f => ({ ...f, userRole: r.userRole ?? "", phiDataCategory: r.phiDataCategory ?? "", accessPurpose: r.accessPurpose ?? "" })); }}>
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Run Access Check
                    </Button>
                  </div>
                )}
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={v => { setShowCreate(v); if (!v) setErrors({}); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Minimum Necessary Rule</DialogTitle>
            <DialogDescription>Define PHI access boundaries for a role or purpose under § 164.514(d).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <Label>Rule Name *</Label>
              <Input className={fieldCls("", errors, "ruleName")} aria-invalid={!!errors.ruleName} value={form.ruleName} onChange={e => { setF("ruleName", e.target.value); clearErr(setErrors, "ruleName"); }} placeholder="e.g. Billing Role — Billing Data Only" />
              {fErr(errors, "ruleName")}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>User Role</Label><Input value={form.userRole} onChange={e => setF("userRole", e.target.value)} placeholder="Billing, Clinical, Support…" /></div>
              <div><Label>Department</Label><Input value={form.department} onChange={e => setF("department", e.target.value)} placeholder="Finance, Emergency…" /></div>
              <div><Label>Access Purpose</Label><Input value={form.accessPurpose} onChange={e => setF("accessPurpose", e.target.value)} placeholder="Payment Processing, Patient Care…" /></div>
              <div>
                <Label>PHI Data Category</Label>
                <select className="border rounded px-2 py-1 text-sm w-full" value={form.phiDataCategory} onChange={e => setF("phiDataCategory", e.target.value)}>
                  <option value="">Select…</option>
                  {MN_PHI_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <Label>Access Level</Label>
                <select className="border rounded px-2 py-1 text-sm w-full" value={form.accessLevel} onChange={e => setF("accessLevel", e.target.value)}>
                  {MN_ACCESS_LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div>
                <Label>Status</Label>
                <select className="border rounded px-2 py-1 text-sm w-full" value={form.status} onChange={e => setF("status", e.target.value)}>
                  {MN_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div><Label>Effective Date</Label><Input type="date" value={form.effectiveDate} onChange={e => setF("effectiveDate", e.target.value)} /></div>
              <div><Label>Expiration Date</Label><Input type="date" value={form.expirationDate} onChange={e => setF("expirationDate", e.target.value)} /></div>
              <div><Label>Next Review Date</Label><Input type="date" value={form.nextReviewDate} onChange={e => setF("nextReviewDate", e.target.value)} /></div>
            </div>

            <div>
              <Label>Allowed Data Elements <span className="text-muted-foreground text-xs">(comma-separated)</span></Label>
              <Input value={form.allowedDataElements} onChange={e => setF("allowedDataElements", e.target.value)} placeholder="InvoiceId, Amount, ServiceDate" />
            </div>
            <div>
              <Label>Restricted Data Elements <span className="text-muted-foreground text-xs">(comma-separated)</span></Label>
              <Input value={form.restrictedDataElements} onChange={e => setF("restrictedDataElements", e.target.value)} placeholder="Diagnosis, ClinicalNotes, MedicationHistory" />
            </div>

            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.minimumNecessaryRequired} onChange={e => setF("minimumNecessaryRequired", e.target.checked)} />
                Minimum Necessary Required
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.disclosureAllowed} onChange={e => setF("disclosureAllowed", e.target.checked)} />
                Disclosure Allowed
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.approvalRequired} onChange={e => setF("approvalRequired", e.target.checked)} />
                Approval Required
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.reviewRequired} onChange={e => setF("reviewRequired", e.target.checked)} />
                Review Required
              </label>
            </div>

            <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={e => setF("description", e.target.value)} /></div>
            <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={e => setF("notes", e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2"
              onClick={() => { const errs: Record<string,string> = {}; required(errs, "ruleName", form.ruleName, "Rule Name"); if (Object.keys(errs).length) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } setErrors({}); createMut.mutate(); }} disabled={createMut.isPending}>
              <Plus className="h-4 w-4" /> {createMut.isPending ? "Creating…" : "Create Rule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Access Check dialog */}
      <Dialog open={showCheck} onOpenChange={o => { if (!o) { setShowCheck(false); setCheckResult(null); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Run Minimum Necessary Access Check</DialogTitle>
            <DialogDescription>Validate whether a role / purpose combination is permitted access to the requested PHI data elements.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>User Role *</Label>
                <Input value={checkForm.userRole} onChange={e => setCheckForm(f => ({ ...f, userRole: e.target.value }))} placeholder="Billing" />
              </div>
              <div>
                <Label>Department</Label>
                <Input value={checkForm.department} onChange={e => setCheckForm(f => ({ ...f, department: e.target.value }))} placeholder="Finance" />
              </div>
              <div>
                <Label>Access Purpose</Label>
                <Input value={checkForm.accessPurpose} onChange={e => setCheckForm(f => ({ ...f, accessPurpose: e.target.value }))} placeholder="Payment Processing" />
              </div>
              <div>
                <Label>PHI Category</Label>
                <select className="border rounded px-2 py-1 text-sm w-full" value={checkForm.phiDataCategory}
                  onChange={e => setCheckForm(f => ({ ...f, phiDataCategory: e.target.value }))}>
                  <option value="">Select…</option>
                  {MN_PHI_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label>Requested Data Elements <span className="text-muted-foreground text-xs">(comma-separated)</span></Label>
              <Input value={checkForm.requestedDataElements}
                onChange={e => setCheckForm(f => ({ ...f, requestedDataElements: e.target.value }))}
                placeholder="InvoiceId, Amount" />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={checkForm.disclosureRequested}
                onChange={e => setCheckForm(f => ({ ...f, disclosureRequested: e.target.checked }))} />
              This is a disclosure request
            </label>

            {/* Result panel */}
            {checkResult && (
              <div className={`rounded-lg border p-3 ${checkResult.allowed ? "border-teal-400 bg-teal-50" : "border-red-400 bg-red-50"}`}>
                <div className="flex items-center gap-2 mb-2">
                  {checkResult.allowed
                    ? <CheckCircle2 className="h-5 w-5 text-teal-700" />
                    : <AlertTriangle className="h-5 w-5 text-red-600" />}
                  <span className={`font-semibold ${checkResult.allowed ? "text-teal-700" : "text-red-600"}`}>
                    {checkResult.allowed ? "ACCESS ALLOWED" : "ACCESS DENIED"}
                  </span>
                </div>
                {checkResult.denialCodes.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {checkResult.denialCodes.map(c => (
                      <Badge key={c} variant="outline" className="text-xs border-red-400 text-red-700">{c}</Badge>
                    ))}
                  </div>
                )}
                {checkResult.approvalRequired && (
                  <p className="text-xs text-amber-700 font-medium">⚠ Supervisor approval is required for this access.</p>
                )}
                {checkResult.appliedRule && (
                  <p className="text-xs text-muted-foreground mt-1">Applied rule: <strong>{checkResult.appliedRule.ruleName}</strong></p>
                )}
                {!checkResult.appliedRule && (
                  <p className="text-xs text-muted-foreground mt-1">No matching rule found for this role / category combination.</p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCheck(false); setCheckResult(null); }}>Close</Button>
            <Button className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2"
              onClick={() => checkMut.mutate()} disabled={checkMut.isPending || !checkForm.userRole}>
              <CheckCircle2 className="h-4 w-4" /> {checkMut.isPending ? "Checking…" : "Check Access"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── HIPAA-17 — Transmission Security (§ 164.312(e)) ──────────────────────────

type TxSummary = {
  total: number;
  secureTransport: number;
  tlsCompliant: number;
  encryptionEnabled: number;
  expiredCerts: number;
  expiringCerts: number;
  securityIssues: number;
  reviewOverdue: number;
  remediationRequired: number;
  compliancePercentage: number;
};

type TxControl = {
  id: string;
  controlName: string;
  description?: string;
  transmissionType: string;
  dataCategory?: string;
  endpointName?: string;
  endpointType?: string;
  integrationName?: string;
  vendorName?: string;
  encryptionRequired: boolean;
  encryptionProtocol?: string;
  tlsVersion?: string;
  secureTransportEnabled: boolean;
  insecureProtocolBlocked: boolean;
  certificateRequired: boolean;
  certificateReference?: string;
  certificateExpiryDate?: string;
  status: string;
  lastSecurityReviewDate?: string;
  nextSecurityReviewDate?: string;
  riskLevel?: string;
  complianceStatus?: string;
  findings?: string;
  remediationRequired: boolean;
  remediationNotes?: string;
  notes?: string;
  lastValidatedAt?: string;
  lastValidationResult?: string;
  lastValidationFailures?: string;
  createdAt: string;
  updatedAt: string;
  validationFailures: string[];
  isCertificateExpiringSoon: boolean;
};

const TX_TYPES       = ["API Communication","Internal System Transfer","External Integration","File Transfer","Email Transmission","Database Connection","Other"];
const TX_PROTOCOLS   = ["TLS 1.2","TLS 1.3","HTTPS","SFTP","VPN","Other"];
const TX_TLS_VERSIONS= ["TLS 1.2","TLS 1.3"];
const TX_STATUSES    = ["Active","Disabled","Expired","ReviewRequired"];
const TX_RISK_LEVELS = ["Low","Medium","High","Critical"];
const TX_COMPLIANCE  = ["Compliant","NonCompliant","PartiallyCompliant","UnderReview"];

function txStatusBadge(s: string): "default"|"secondary"|"outline"|"destructive" {
  if (s === "Active")           return "default";
  if (s === "Disabled")         return "outline";
  if (s === "Expired")          return "destructive";
  if (s === "ReviewRequired")   return "secondary";
  return "secondary";
}
function txComplianceBadge(c?: string): "default"|"secondary"|"outline"|"destructive" {
  if (!c) return "secondary";
  if (c === "Compliant")        return "default";
  if (c === "NonCompliant")     return "destructive";
  if (c === "PartiallyCompliant") return "outline";
  return "secondary";
}
function txRiskBadge(r?: string): "default"|"secondary"|"outline"|"destructive" {
  if (!r) return "secondary";
  if (r === "Critical" || r === "High") return "destructive";
  if (r === "Medium") return "outline";
  return "default";
}
function useAuth17() {
  return (url: string, opts: RequestInit = {}) => {
    const token = sessionStorage.getItem("dotnetbot.jwt.session") || localStorage.getItem("dotnetbot.jwt") || "";
    return fetch(url, { ...opts, headers: { ...(opts.headers as Record<string,string> || {}), "Content-Type": "application/json", Authorization: `Bearer ${token}` } });
  };
}

function HipaaTransmissionSecuritySection({ canManage }: { canManage: boolean }) {
  const { toast } = useToast();
  const authFetch = useAuth17();
  const qc = useQueryClient();
  const BASE = "/api/organization/compliance/hipaa/transmission-security";

  // ── Summary ────────────────────────────────────────────────────────────────
  const summaryQ = useQuery<TxSummary>({
    queryKey: ["hipaa17-summary"],
    queryFn: async () => { const r = await authFetch(`${BASE}/summary`); if (!r.ok) throw new Error(); return r.json(); },
    staleTime: 0,
  });

  // ── Filters + list ─────────────────────────────────────────────────────────
  const [page, setPage]               = useState(1);
  const [typeFilter, setTypeFilter]   = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [encFilter, setEncFilter]     = useState("");
  const [tlsFilter, setTlsFilter]     = useState("");
  const [riskFilter, setRiskFilter]   = useState("");
  const [compFilter, setCompFilter]   = useState("");
  const [vendorFilter, setVendorFilter] = useState("");
  const [integFilter, setIntegFilter] = useState("");

  const listQ = useQuery<{ items: TxControl[]; total: number }>({
    queryKey: ["hipaa17-list", page, typeFilter, statusFilter, encFilter, tlsFilter, riskFilter, compFilter, vendorFilter, integFilter],
    queryFn: async () => {
      const p = new URLSearchParams({ page: String(page), pageSize: "15" });
      if (typeFilter)   p.set("transmissionType", typeFilter);
      if (statusFilter) p.set("status", statusFilter);
      if (encFilter)    p.set("encryptionEnabled", encFilter);
      if (tlsFilter)    p.set("tlsVersion", tlsFilter);
      if (riskFilter)   p.set("riskLevel", riskFilter);
      if (compFilter)   p.set("complianceStatus", compFilter);
      if (vendorFilter) p.set("vendor", vendorFilter);
      if (integFilter)  p.set("integration", integFilter);
      const r = await authFetch(`${BASE}?${p}`);
      if (!r.ok) throw new Error(); return r.json();
    },
    staleTime: 0,
  });

  // ── Detail ─────────────────────────────────────────────────────────────────
  const [detailId, setDetailId] = useState<string | null>(null);
  const detailQ = useQuery<TxControl>({
    queryKey: ["hipaa17-detail", detailId],
    queryFn: async () => { const r = await authFetch(`${BASE}/${detailId}`); if (!r.ok) throw new Error(); return r.json(); },
    enabled: !!detailId,
    staleTime: 0,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["hipaa17-list"] });
    qc.invalidateQueries({ queryKey: ["hipaa17-summary"] });
    if (detailId) qc.invalidateQueries({ queryKey: ["hipaa17-detail", detailId] });
  };

  // ── Create ─────────────────────────────────────────────────────────────────
  const [showCreate, setShowCreate] = useState(false);
  const defaultForm = () => ({
    controlName: "", transmissionType: "API Communication", description: "",
    dataCategory: "", endpointName: "", endpointType: "", integrationName: "", vendorName: "",
    encryptionRequired: true, encryptionProtocol: "", tlsVersion: "",
    secureTransportEnabled: false, insecureProtocolBlocked: false,
    certificateRequired: false, certificateReference: "", certificateExpiryDate: "",
    status: "Active", riskLevel: "Low", complianceStatus: "Compliant",
    lastSecurityReviewDate: "", nextSecurityReviewDate: "",
    findings: "", remediationRequired: false, remediationNotes: "", notes: "",
  });
  const [form, setForm] = useState(defaultForm());
  const setF = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));
  const [errors, setErrors] = useState<Record<string,string>>({});

  const createMut = useMutation({
    mutationFn: async () => {
      const body: Record<string, any> = { ...form };
      if (!body.encryptionProtocol) delete body.encryptionProtocol;
      if (!body.tlsVersion) delete body.tlsVersion;
      if (!body.certificateExpiryDate) delete body.certificateExpiryDate;
      if (!body.lastSecurityReviewDate) delete body.lastSecurityReviewDate;
      if (!body.nextSecurityReviewDate) delete body.nextSecurityReviewDate;
      const r = await authFetch(BASE, { method: "POST", body: JSON.stringify(body) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.error || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Transmission control created." }); setShowCreate(false); setForm(defaultForm()); setErrors({}); invalidate(); },
    onError: (e: any) => { const s = extractServerErrors(e); if (s && Object.keys(s).length) { setErrors(s); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); } else toast({ title: "Unable to save", description: (e as Error)?.message, variant: "destructive" }); },
  });

  // ── Validate ───────────────────────────────────────────────────────────────
  const validateMut = useMutation({
    mutationFn: async (id: string) => {
      const r = await authFetch(`${BASE}/${id}/validate`, { method: "PATCH", body: JSON.stringify({}) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.error || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: (data) => {
      toast({ title: data.result === "Pass" ? "Validation passed ✓" : "Validation error", description: data.failures?.join(", ") || undefined, variant: data.result === "Pass" ? "default" : "destructive" });
      invalidate();
    },
    onError: (e: Error) => toast({ title: "Validation error", description: e.message, variant: "destructive" }),
  });

  // ── Review ─────────────────────────────────────────────────────────────────
  const [reviewId, setReviewId] = useState<string | null>(null);
  const [reviewForm, setReviewForm] = useState({ nextReviewDate: "", findings: "" });

  const reviewMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/${reviewId}/review`, {
        method: "PATCH",
        body: JSON.stringify({ nextReviewDate: reviewForm.nextReviewDate || null, findings: reviewForm.findings || null }),
      });
      if (!r.ok) { const e = await r.json(); throw new Error(e.error || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Review completed." }); setReviewId(null); invalidate(); },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  // ── Enable / Disable ───────────────────────────────────────────────────────
  const enableMut = useMutation({
    mutationFn: async (id: string) => {
      const r = await authFetch(`${BASE}/${id}/enable`, { method: "PATCH", body: JSON.stringify({}) });
      if (!r.ok) throw new Error(); return r.json();
    },
    onSuccess: () => { toast({ title: "Control enabled." }); invalidate(); },
    onError: () => toast({ title: "Error enabling control.", variant: "destructive" }),
  });
  const disableMut = useMutation({
    mutationFn: async (id: string) => {
      const r = await authFetch(`${BASE}/${id}/disable`, { method: "PATCH", body: JSON.stringify({}) });
      if (!r.ok) throw new Error(); return r.json();
    },
    onSuccess: () => { toast({ title: "Control disabled." }); invalidate(); },
    onError: () => toast({ title: "Error disabling control.", variant: "destructive" }),
  });

  const s = summaryQ.data;
  const items = listQ.data?.items ?? [];
  const total = listQ.data?.total ?? 0;
  const pageSize = 15;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-teal-700" />
            Transmission Security
          </CardTitle>
          <CardDescription>§ 164.312(e) — Protect ePHI against unauthorized access during electronic transmission. Track encryption, TLS posture, certificates, and secure transport policies.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">

          {/* Dashboard tiles */}
          {summaryQ.isLoading ? <Skeleton className="h-24 w-full" /> : s && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="rounded-lg border p-3 text-center">
                <p className="text-2xl font-bold">{s.total}</p>
                <p className="text-xs text-muted-foreground">Total Controls</p>
              </div>
              <div className={`rounded-lg border p-3 text-center ${s.secureTransport < s.total ? "border-amber-400 bg-amber-50" : ""}`}>
                <p className="text-2xl font-bold">{s.secureTransport}<span className="text-sm text-muted-foreground">/{s.total}</span></p>
                <p className="text-xs text-muted-foreground">Secure Transport</p>
              </div>
              <div className={`rounded-lg border p-3 text-center ${s.tlsCompliant < s.total ? "border-amber-400 bg-amber-50" : ""}`}>
                <p className="text-2xl font-bold">{s.tlsCompliant}</p>
                <p className="text-xs text-muted-foreground">TLS Compliant</p>
              </div>
              <div className={`rounded-lg border p-3 text-center ${s.encryptionEnabled < s.total ? "border-amber-400 bg-amber-50" : ""}`}>
                <p className="text-2xl font-bold">{s.encryptionEnabled}</p>
                <p className="text-xs text-muted-foreground">Encryption Coverage</p>
              </div>
              <div className={`rounded-lg border p-3 text-center ${s.expiredCerts > 0 ? "border-red-500 bg-red-50" : s.expiringCerts > 0 ? "border-amber-400 bg-amber-50" : ""}`}>
                <p className={`text-2xl font-bold ${s.expiredCerts > 0 ? "text-red-600" : ""}`}>{s.expiredCerts}</p>
                <p className="text-xs text-muted-foreground">Expired Certs</p>
                {s.expiringCerts > 0 && <p className="text-xs text-amber-600">{s.expiringCerts} expiring soon</p>}
              </div>
              <div className={`rounded-lg border p-3 text-center ${s.securityIssues > 0 ? "border-red-500 bg-red-50" : ""}`}>
                <p className={`text-2xl font-bold ${s.securityIssues > 0 ? "text-red-600" : ""}`}>{s.securityIssues}</p>
                <p className="text-xs text-muted-foreground">Security Issues</p>
              </div>
              <div className={`rounded-lg border p-3 text-center ${s.reviewOverdue > 0 ? "border-amber-400 bg-amber-50" : ""}`}>
                <p className="text-2xl font-bold">{s.reviewOverdue}</p>
                <p className="text-xs text-muted-foreground">Reviews Overdue</p>
              </div>
              <div className={`rounded-lg border p-3 text-center ${s.compliancePercentage < 100 ? "border-amber-400 bg-amber-50" : "border-teal-400 bg-teal-50"}`}>
                <p className="text-2xl font-bold text-teal-700">{s.compliancePercentage}%</p>
                <p className="text-xs text-muted-foreground">Compliance Rate</p>
              </div>
            </div>
          )}

          {/* Alert strip */}
          {s && s.securityIssues > 0 && (
            <div className="flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span><strong>{s.securityIssues}</strong> transmission control{s.securityIssues > 1 ? "s have" : " has"} active security issues requiring remediation.</span>
            </div>
          )}
          {s && s.expiredCerts > 0 && (
            <div className="flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span><strong>{s.expiredCerts}</strong> expired certificate{s.expiredCerts > 1 ? "s" : ""} — renew immediately to maintain secure transmission.</span>
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-wrap gap-2 items-end">
            <div>
              <Label className="text-xs">Type</Label>
              <select className="border rounded px-2 py-1 text-sm h-8" value={typeFilter}
                onChange={e => { setTypeFilter(e.target.value); setPage(1); }}>
                <option value="">All</option>
                {TX_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <select className="border rounded px-2 py-1 text-sm h-8" value={statusFilter}
                onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
                <option value="">All</option>
                {TX_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Encryption</Label>
              <select className="border rounded px-2 py-1 text-sm h-8" value={encFilter}
                onChange={e => { setEncFilter(e.target.value); setPage(1); }}>
                <option value="">All</option>
                <option value="true">Enabled</option>
                <option value="false">Missing</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">TLS</Label>
              <select className="border rounded px-2 py-1 text-sm h-8" value={tlsFilter}
                onChange={e => { setTlsFilter(e.target.value); setPage(1); }}>
                <option value="">All</option>
                {TX_TLS_VERSIONS.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Risk</Label>
              <select className="border rounded px-2 py-1 text-sm h-8" value={riskFilter}
                onChange={e => { setRiskFilter(e.target.value); setPage(1); }}>
                <option value="">All</option>
                {TX_RISK_LEVELS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Compliance</Label>
              <select className="border rounded px-2 py-1 text-sm h-8" value={compFilter}
                onChange={e => { setCompFilter(e.target.value); setPage(1); }}>
                <option value="">All</option>
                {TX_COMPLIANCE.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Vendor</Label>
              <Input className="h-8 text-sm w-28" placeholder="Filter…" value={vendorFilter}
                onChange={e => { setVendorFilter(e.target.value); setPage(1); }} />
            </div>
            {canManage && (
              <Button className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2 ml-auto"
                onClick={() => setShowCreate(true)}>
                <Plus className="h-4 w-4" /> Add Transmission Control
              </Button>
            )}
          </div>

          {/* List */}
          {listQ.isLoading ? <Skeleton className="h-32 w-full" /> : items.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No transmission controls found. Add one to start tracking secure transmission posture.</p>
          ) : (
            <div className="space-y-2">
              {items.map(c => (
                <div key={c.id}
                  className={`flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer hover:bg-muted/40 transition-colors ${c.validationFailures.length > 0 ? "border-amber-400 bg-amber-50/40" : ""} ${c.complianceStatus === "NonCompliant" ? "border-red-300" : ""}`}
                  onClick={() => setDetailId(c.id)}>
                  <div className="flex-1 min-w-0">
                    <span className="font-medium truncate">{c.controlName}</span>
                    {c.vendorName && <span className="ml-2 text-xs text-muted-foreground">{c.vendorName}</span>}
                    {c.integrationName && <span className="ml-1 text-xs text-muted-foreground">· {c.integrationName}</span>}
                  </div>
                  <Badge variant="outline" className="text-xs">{c.transmissionType}</Badge>
                  <Badge variant={txStatusBadge(c.status)} className="text-xs">{c.status}</Badge>
                  {c.riskLevel && <Badge variant={txRiskBadge(c.riskLevel)} className="text-xs">{c.riskLevel}</Badge>}
                  {c.complianceStatus && <Badge variant={txComplianceBadge(c.complianceStatus)} className="text-xs">{c.complianceStatus}</Badge>}
                  <span className={`text-xs px-2 py-0.5 rounded-full ${c.secureTransportEnabled ? "bg-teal-100 text-teal-700" : "bg-red-100 text-red-700"}`}>
                    TLS {c.secureTransportEnabled ? c.tlsVersion ?? "✓" : "✗"}
                  </span>
                  {c.encryptionProtocol && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-teal-100 text-teal-700">{c.encryptionProtocol}</span>
                  )}
                  {c.isCertificateExpiringSoon && (
                    <span className="text-xs text-amber-600 font-medium">Cert expiring</span>
                  )}
                  {c.certificateExpiryDate && new Date(c.certificateExpiryDate) < new Date() && (
                    <span className="text-xs text-red-600 font-medium">Cert expired</span>
                  )}
                  {canManage && (
                    <Button size="sm" variant="ghost" className="text-xs h-6 px-2"
                      onClick={e => { e.stopPropagation(); validateMut.mutate(c.id); }}>
                      Validate
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {total > pageSize && (
            <div className="flex items-center gap-2 justify-center">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
              <span className="text-sm text-muted-foreground">Page {page} of {Math.ceil(total / pageSize)}</span>
              <Button variant="outline" size="sm" disabled={page * pageSize >= total} onClick={() => setPage(p => p + 1)}>Next</Button>
            </div>
          )}

          {/* Detail view */}
          {detailId && detailQ.data && (() => {
            const c = detailQ.data;
            return (
              <div className="rounded-lg border p-4 space-y-4 bg-muted/20">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold">{c.controlName}</h4>
                  <div className="flex gap-2 flex-wrap">
                    <Badge variant={txStatusBadge(c.status)}>{c.status}</Badge>
                    {c.complianceStatus && <Badge variant={txComplianceBadge(c.complianceStatus)}>{c.complianceStatus}</Badge>}
                    {c.riskLevel && <Badge variant={txRiskBadge(c.riskLevel)}>{c.riskLevel}</Badge>}
                    <Button variant="ghost" size="sm" onClick={() => setDetailId(null)}>✕</Button>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                  <div><p className="text-muted-foreground">Type</p><p className="font-medium">{c.transmissionType}</p></div>
                  <div><p className="text-muted-foreground">Protocol</p><p className="font-medium">{c.encryptionProtocol ?? "—"}</p></div>
                  <div><p className="text-muted-foreground">TLS Version</p><p className="font-medium">{c.tlsVersion ?? "—"}</p></div>
                  <div><p className="text-muted-foreground">Secure Transport</p><p className={`font-medium ${c.secureTransportEnabled ? "text-teal-700" : "text-red-600"}`}>{c.secureTransportEnabled ? "Enabled" : "Disabled"}</p></div>
                  <div><p className="text-muted-foreground">Insecure Blocked</p><p className={`font-medium ${c.insecureProtocolBlocked ? "text-teal-700" : "text-red-600"}`}>{c.insecureProtocolBlocked ? "Yes" : "No"}</p></div>
                  <div><p className="text-muted-foreground">Encryption Required</p><p className="font-medium">{c.encryptionRequired ? "Yes" : "No"}</p></div>
                  {c.endpointName && <div><p className="text-muted-foreground">Endpoint</p><p className="font-medium">{c.endpointName}</p></div>}
                  {c.vendorName && <div><p className="text-muted-foreground">Vendor</p><p className="font-medium">{c.vendorName}</p></div>}
                  {c.integrationName && <div><p className="text-muted-foreground">Integration</p><p className="font-medium">{c.integrationName}</p></div>}
                  {c.dataCategory && <div><p className="text-muted-foreground">Data Category</p><p className="font-medium">{c.dataCategory}</p></div>}
                </div>

                {/* Certificate */}
                {c.certificateRequired && (
                  <div className={`rounded p-2 text-xs ${c.certificateExpiryDate && new Date(c.certificateExpiryDate) < new Date() ? "bg-red-50 border border-red-300" : c.isCertificateExpiringSoon ? "bg-amber-50 border border-amber-300" : "bg-muted"}`}>
                    <p className="font-semibold mb-1">Certificate</p>
                    <p>Reference: {c.certificateReference ?? "—"}</p>
                    {c.certificateExpiryDate && <p>Expires: {new Date(c.certificateExpiryDate).toLocaleDateString()} {new Date(c.certificateExpiryDate) < new Date() ? "⚠ EXPIRED" : c.isCertificateExpiringSoon ? "⚠ Expiring Soon" : "✓"}</p>}
                  </div>
                )}

                {/* Review info */}
                <div className="grid grid-cols-2 gap-3 text-xs">
                  {c.lastSecurityReviewDate && <div><p className="text-muted-foreground">Last Review</p><p className="font-medium">{new Date(c.lastSecurityReviewDate).toLocaleDateString()}</p></div>}
                  {c.nextSecurityReviewDate && <div><p className="text-muted-foreground">Next Review</p><p className={`font-medium ${new Date(c.nextSecurityReviewDate) < new Date() ? "text-red-600" : ""}`}>{new Date(c.nextSecurityReviewDate).toLocaleDateString()}</p></div>}
                  {c.lastValidatedAt && <div><p className="text-muted-foreground">Last Validated</p><p className="font-medium">{new Date(c.lastValidatedAt).toLocaleDateString()} — <span className={c.lastValidationResult === "Pass" ? "text-teal-700" : "text-red-600"}>{c.lastValidationResult}</span></p></div>}
                </div>

                {/* Validation failures */}
                {c.validationFailures.length > 0 && (
                  <div className="rounded bg-amber-50 border border-amber-300 p-2">
                    <p className="text-xs font-semibold text-amber-700 mb-1">Validation Failures</p>
                    <div className="flex flex-wrap gap-1">
                      {c.validationFailures.map(f => <Badge key={f} variant="outline" className="text-xs border-amber-500 text-amber-700">{f}</Badge>)}
                    </div>
                  </div>
                )}

                {c.findings && <p className="text-sm text-muted-foreground">Findings: {c.findings}</p>}
                {c.remediationRequired && c.remediationNotes && <p className="text-sm text-amber-700">Remediation: {c.remediationNotes}</p>}
                {c.notes && <p className="text-xs text-muted-foreground">{c.notes}</p>}

                <LinkedSourceRow entityType="HipaaTransmissionSecurityControl" entityId={detailId!} canManage={canManage} />

                {canManage && (
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2"
                      onClick={() => validateMut.mutate(c.id)} disabled={validateMut.isPending}>
                      <CheckCircle2 className="h-3.5 w-3.5" /> Run Validation
                    </Button>
                    <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2"
                      onClick={() => { setReviewId(c.id); setReviewForm({ nextReviewDate: "", findings: "" }); }}>
                      <Shield className="h-3.5 w-3.5" /> Review Control
                    </Button>
                    {c.status !== "Active" ? (
                      <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2"
                        onClick={() => enableMut.mutate(c.id)} disabled={enableMut.isPending}>
                        <CheckCircle2 className="h-3.5 w-3.5" /> Enable
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" className="border-amber-400 text-amber-700 hover:bg-amber-50"
                        onClick={() => disableMut.mutate(c.id)} disabled={disableMut.isPending}>
                        Disable
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={v => { setShowCreate(v); if (!v) setErrors({}); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Transmission Security Control</DialogTitle>
            <DialogDescription>Document an ePHI transmission channel for § 164.312(e) compliance.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Control Name *</Label>
                <Input className={fieldCls("", errors, "controlName")} aria-invalid={!!errors.controlName} value={form.controlName} onChange={e => { setF("controlName", e.target.value); clearErr(setErrors, "controlName"); }} placeholder="e.g. Patient Portal API Gateway" />
                {fErr(errors, "controlName")}
              </div>
              <div>
                <Label>Transmission Type *</Label>
                <select className="border rounded px-2 py-1 text-sm w-full" value={form.transmissionType} onChange={e => setF("transmissionType", e.target.value)}>
                  {TX_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <Label>Encryption Protocol</Label>
                <select className="border rounded px-2 py-1 text-sm w-full" value={form.encryptionProtocol} onChange={e => setF("encryptionProtocol", e.target.value)}>
                  <option value="">None / Not configured</option>
                  {TX_PROTOCOLS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <Label>TLS Version</Label>
                <select className="border rounded px-2 py-1 text-sm w-full" value={form.tlsVersion} onChange={e => setF("tlsVersion", e.target.value)}>
                  <option value="">Unknown</option>
                  {TX_TLS_VERSIONS.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div>
                <Label>Status</Label>
                <select className="border rounded px-2 py-1 text-sm w-full" value={form.status} onChange={e => setF("status", e.target.value)}>
                  {TX_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <Label>Risk Level</Label>
                <select className="border rounded px-2 py-1 text-sm w-full" value={form.riskLevel} onChange={e => setF("riskLevel", e.target.value)}>
                  {TX_RISK_LEVELS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <Label>Compliance Status</Label>
                <select className="border rounded px-2 py-1 text-sm w-full" value={form.complianceStatus} onChange={e => setF("complianceStatus", e.target.value)}>
                  {TX_COMPLIANCE.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div><Label>Vendor</Label><Input value={form.vendorName} onChange={e => setF("vendorName", e.target.value)} placeholder="Partner Co." /></div>
              <div><Label>Integration</Label><Input value={form.integrationName} onChange={e => setF("integrationName", e.target.value)} placeholder="HL7 Lab Results" /></div>
              <div><Label>Endpoint Name</Label><Input value={form.endpointName} onChange={e => setF("endpointName", e.target.value)} placeholder="api.hospital.org" /></div>
              <div><Label>Data Category</Label><Input value={form.dataCategory} onChange={e => setF("dataCategory", e.target.value)} placeholder="PHI / ePHI" /></div>
            </div>

            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.encryptionRequired} onChange={e => setF("encryptionRequired", e.target.checked)} />
                Encryption Required
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.secureTransportEnabled} onChange={e => setF("secureTransportEnabled", e.target.checked)} />
                Secure Transport Enabled
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.insecureProtocolBlocked} onChange={e => setF("insecureProtocolBlocked", e.target.checked)} />
                Insecure Protocol Blocked
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.certificateRequired} onChange={e => setF("certificateRequired", e.target.checked)} />
                Certificate Required
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.remediationRequired} onChange={e => setF("remediationRequired", e.target.checked)} />
                Remediation Required
              </label>
            </div>

            {form.certificateRequired && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Certificate Reference</Label><Input value={form.certificateReference} onChange={e => setF("certificateReference", e.target.value)} placeholder="CERT-2026-001" /></div>
                <div><Label>Certificate Expiry</Label><Input type="date" value={form.certificateExpiryDate} onChange={e => setF("certificateExpiryDate", e.target.value)} /></div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div><Label>Next Security Review</Label><Input type="date" value={form.nextSecurityReviewDate} onChange={e => setF("nextSecurityReviewDate", e.target.value)} /></div>
            </div>

            <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={e => setF("description", e.target.value)} /></div>
            <div><Label>Findings</Label><Textarea rows={2} value={form.findings} onChange={e => setF("findings", e.target.value)} /></div>
            {form.remediationRequired && <div><Label>Remediation Notes</Label><Textarea rows={2} value={form.remediationNotes} onChange={e => setF("remediationNotes", e.target.value)} /></div>}
            <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={e => setF("notes", e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2"
              onClick={() => { const errs: Record<string,string> = {}; required(errs, "controlName", form.controlName, "Control Name"); if (Object.keys(errs).length) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } setErrors({}); createMut.mutate(); }} disabled={createMut.isPending}>
              <Plus className="h-4 w-4" /> {createMut.isPending ? "Creating…" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review dialog */}
      <Dialog open={!!reviewId} onOpenChange={o => !o && setReviewId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Complete Security Review</DialogTitle>
            <DialogDescription>Record the outcome of the security review for this transmission control.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <Label>Next Review Date *</Label>
              <Input type="date" value={reviewForm.nextReviewDate}
                onChange={e => setReviewForm(f => ({ ...f, nextReviewDate: e.target.value }))} />
            </div>
            <div>
              <Label>Findings</Label>
              <Textarea rows={3} value={reviewForm.findings}
                onChange={e => setReviewForm(f => ({ ...f, findings: e.target.value }))}
                placeholder="Describe any issues found during the security review…" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewId(null)}>Cancel</Button>
            <Button className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2"
              onClick={() => reviewMut.mutate()} disabled={reviewMut.isPending}>
              <CheckCircle2 className="h-4 w-4" /> {reviewMut.isPending ? "Saving…" : "Complete Review"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── HIPAA-16 — Person / Entity Authentication (§ 164.312(d)) ─────────────────

type AuthSummary = {
  total: number;
  verifiedIdentities: number;
  mfaEnabled: number;
  mfaRequired: number;
  privilegedAccounts: number;
  privilegedMissingMFA: number;
  lockedAccounts: number;
  disabledAccounts: number;
  suspendedAccounts: number;
  totalFailedAttempts: number;
  highRiskAccounts: number;
  compliancePercentage: number;
};

type AuthControl = {
  id: string;
  organizationId: string;
  userId?: string;
  username: string;
  authenticationMethod: string;
  authenticationStrength?: string;
  mfaRequired: boolean;
  mfaEnabled: boolean;
  mfaType?: string;
  identityVerified: boolean;
  verificationMethod?: string;
  verificationDate?: string;
  verificationEvidence?: string;
  verifiedBy?: string;
  lastAuthenticationDate?: string;
  lastSuccessfulLogin?: string;
  lastFailedAttempt?: string;
  failedAttemptCount: number;
  lockoutThreshold: number;
  accountStatus: string;
  lockReason?: string;
  lockedBy?: string;
  lockedAt?: string;
  unlockReason?: string;
  unlockedBy?: string;
  unlockedAt?: string;
  privilegedAccount: boolean;
  privilegedAccountType?: string;
  riskLevel?: string;
  sessionTimeoutMinutes: number;
  passwordPolicyApplied: boolean;
  encryptionRequired: boolean;
  notes?: string;
  evidenceReference?: string;
  createdAt: string;
  updatedAt: string;
  denialCodes: string[];
};

const AUTH_METHODS    = ["Password","Password+MFA","SSO","SAML","OAuth","CertificateBased","Biometric","Other"];
const AUTH_STATUSES   = ["Active","Locked","Disabled","Suspended"];
const AUTH_RISK_LEVELS = ["Low","Medium","High","Critical"];
const AUTH_MFA_TYPES  = ["TOTP","SMS","Email","HardwareToken","PushNotification","Biometric","Other"];
const AUTH_STRENGTHS  = ["Low","Medium","High","VeryHigh"];
const AUTH_PRIV_TYPES = ["Admin","ServiceAccount","SuperUser","Other"];

function authStatusBadge(s: string): "default"|"secondary"|"outline"|"destructive" {
  if (s === "Active")   return "default";
  if (s === "Locked")   return "destructive";
  if (s === "Disabled" || s === "Suspended") return "outline";
  return "secondary";
}
function authRiskBadge(r?: string): "default"|"secondary"|"outline"|"destructive" {
  if (!r) return "secondary";
  if (r === "Critical" || r === "High") return "destructive";
  if (r === "Medium") return "outline";
  return "default";
}
function useAuth16() {
  return (url: string, opts: RequestInit = {}) => {
    const token = sessionStorage.getItem("dotnetbot.jwt.session") || localStorage.getItem("dotnetbot.jwt") || "";
    return fetch(url, { ...opts, headers: { ...(opts.headers as Record<string,string> || {}), "Content-Type": "application/json", Authorization: `Bearer ${token}` } });
  };
}

function HipaaPersonAuthenticationSection({ canManage }: { canManage: boolean }) {
  const { toast } = useToast();
  const authFetch = useAuth16();
  const qc = useQueryClient();
  const BASE = "/api/organization/compliance/hipaa/authentication-controls";

  // ── Summary ────────────────────────────────────────────────────────────────
  const summaryQ = useQuery<AuthSummary>({
    queryKey: ["hipaa16-summary"],
    queryFn: async () => { const r = await authFetch(`${BASE}/summary`); if (!r.ok) throw new Error(); return r.json(); },
    staleTime: 0,
  });

  // ── Filters + list ─────────────────────────────────────────────────────────
  const [page, setPage]               = useState(1);
  const [usernameFilter, setUsernameFilter] = useState("");
  const [methodFilter, setMethodFilter]     = useState("");
  const [statusFilter, setStatusFilter]     = useState("");
  const [privFilter, setPrivFilter]         = useState("");
  const [mfaFilter, setMfaFilter]           = useState("");
  const [riskFilter, setRiskFilter]         = useState("");
  const [verifiedFilter, setVerifiedFilter] = useState("");

  const listQ = useQuery<{ items: AuthControl[]; total: number }>({
    queryKey: ["hipaa16-list", page, usernameFilter, methodFilter, statusFilter, privFilter, mfaFilter, riskFilter, verifiedFilter],
    queryFn: async () => {
      const p = new URLSearchParams({ page: String(page), pageSize: "15" });
      if (usernameFilter) p.set("username", usernameFilter);
      if (methodFilter)   p.set("authMethod", methodFilter);
      if (statusFilter)   p.set("accountStatus", statusFilter);
      if (privFilter)     p.set("privileged", privFilter);
      if (mfaFilter)      p.set("mfaEnabled", mfaFilter);
      if (riskFilter)     p.set("riskLevel", riskFilter);
      if (verifiedFilter) p.set("identityVerified", verifiedFilter);
      const r = await authFetch(`${BASE}?${p}`);
      if (!r.ok) throw new Error(); return r.json();
    },
    staleTime: 0,
  });

  // ── Detail ─────────────────────────────────────────────────────────────────
  const [detailId, setDetailId] = useState<string | null>(null);
  const detailQ = useQuery<AuthControl>({
    queryKey: ["hipaa16-detail", detailId],
    queryFn: async () => { const r = await authFetch(`${BASE}/${detailId}`); if (!r.ok) throw new Error(); return r.json(); },
    enabled: !!detailId,
    staleTime: 0,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["hipaa16-list"] });
    qc.invalidateQueries({ queryKey: ["hipaa16-summary"] });
    if (detailId) qc.invalidateQueries({ queryKey: ["hipaa16-detail", detailId] });
  };

  // ── Create ─────────────────────────────────────────────────────────────────
  const [showCreate, setShowCreate] = useState(false);
  const defaultForm = () => ({
    username: "", authenticationMethod: "Password", authenticationStrength: "",
    mfaRequired: false, mfaEnabled: false, mfaType: "",
    identityVerified: false, verificationMethod: "", verifiedBy: "", verificationEvidence: "",
    accountStatus: "Active", privilegedAccount: false, privilegedAccountType: "",
    riskLevel: "Low", sessionTimeoutMinutes: 30, lockoutThreshold: 5,
    passwordPolicyApplied: false, encryptionRequired: false,
    notes: "", evidenceReference: "",
  });
  const [form, setForm] = useState(defaultForm());
  const setF = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));
  const [errors, setErrors] = useState<Record<string,string>>({});

  const createMut = useMutation({
    mutationFn: async () => {
      const body: Record<string, any> = { ...form };
      if (!body.authenticationStrength) delete body.authenticationStrength;
      if (!body.mfaType) delete body.mfaType;
      if (!body.verificationMethod) delete body.verificationMethod;
      if (!body.privilegedAccountType) delete body.privilegedAccountType;
      const r = await authFetch(BASE, { method: "POST", body: JSON.stringify(body) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.error || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => {
      toast({ title: "Authentication control created." });
      setShowCreate(false);
      setForm(defaultForm());
      setErrors({});
      invalidate();
    },
    onError: (e: any) => { const s = extractServerErrors(e); if (s && Object.keys(s).length) { setErrors(s); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); } else toast({ title: "Unable to save", description: (e as Error)?.message, variant: "destructive" }); },
  });

  // ── Verify identity ────────────────────────────────────────────────────────
  const [verifyId, setVerifyId] = useState<string | null>(null);
  const [verifyForm, setVerifyForm] = useState({ verificationMethod: "", verifiedBy: "", verificationEvidence: "" });

  const verifyMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/${verifyId}/verify`, { method: "PATCH", body: JSON.stringify(verifyForm) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.error || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Identity verified." }); setVerifyId(null); invalidate(); },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  // ── Enable MFA ─────────────────────────────────────────────────────────────
  const [enableMFAId, setEnableMFAId] = useState<string | null>(null);
  const [mfaTypeForm, setMfaTypeForm] = useState("TOTP");

  const enableMFAMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/${enableMFAId}/enable-mfa`, { method: "PATCH", body: JSON.stringify({ mfaType: mfaTypeForm }) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.error || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "MFA enabled." }); setEnableMFAId(null); invalidate(); },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  // ── Disable MFA ────────────────────────────────────────────────────────────
  const disableMFAMut = useMutation({
    mutationFn: async (id: string) => {
      const r = await authFetch(`${BASE}/${id}/disable-mfa`, { method: "PATCH", body: JSON.stringify({}) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.error || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "MFA disabled." }); invalidate(); },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  // ── Lock account ───────────────────────────────────────────────────────────
  const [lockId, setLockId] = useState<string | null>(null);
  const [lockReason, setLockReason] = useState("");

  const lockMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/${lockId}/lock`, { method: "PATCH", body: JSON.stringify({ lockReason }) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.error || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Account locked." }); setLockId(null); setLockReason(""); invalidate(); },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  // ── Unlock account ─────────────────────────────────────────────────────────
  const [unlockId, setUnlockId] = useState<string | null>(null);
  const [unlockReason, setUnlockReason] = useState("");

  const unlockMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/${unlockId}/unlock`, { method: "PATCH", body: JSON.stringify({ unlockReason }) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.error || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => { toast({ title: "Account unlocked." }); setUnlockId(null); setUnlockReason(""); invalidate(); },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  const s = summaryQ.data;
  const items = listQ.data?.items ?? [];
  const total = listQ.data?.total ?? 0;
  const pageSize = 15;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-teal-700" />
            Person / Entity Authentication
          </CardTitle>
          <CardDescription>§ 164.312(d) — Verify identity before granting access to ePHI systems. Manage strong authentication controls, MFA enrollment, and identity verification.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">

          {/* Dashboard tiles */}
          {summaryQ.isLoading ? <Skeleton className="h-28 w-full" /> : s && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="rounded-lg border p-3 text-center">
                <p className="text-2xl font-bold">{s.total}</p>
                <p className="text-xs text-muted-foreground">Total Users</p>
              </div>
              <div className={`rounded-lg border p-3 text-center ${s.verifiedIdentities < s.total ? "border-amber-400 bg-amber-50" : ""}`}>
                <p className="text-2xl font-bold">{s.verifiedIdentities}</p>
                <p className="text-xs text-muted-foreground">Verified Identities</p>
              </div>
              <div className={`rounded-lg border p-3 text-center ${s.mfaEnabled < s.mfaRequired ? "border-amber-400 bg-amber-50" : ""}`}>
                <p className="text-2xl font-bold">{s.mfaEnabled}<span className="text-sm text-muted-foreground">/{s.mfaRequired} req.</span></p>
                <p className="text-xs text-muted-foreground">MFA Coverage</p>
              </div>
              <div className={`rounded-lg border p-3 text-center ${s.privilegedMissingMFA > 0 ? "border-red-500 bg-red-50" : ""}`}>
                <p className="text-2xl font-bold">{s.privilegedAccounts}</p>
                <p className="text-xs text-muted-foreground">Privileged Accounts</p>
                {s.privilegedMissingMFA > 0 && <p className="text-xs text-red-600 font-medium">{s.privilegedMissingMFA} missing MFA</p>}
              </div>
              <div className={`rounded-lg border p-3 text-center ${s.lockedAccounts > 0 ? "border-amber-400 bg-amber-50" : ""}`}>
                <p className="text-2xl font-bold">{s.lockedAccounts}</p>
                <p className="text-xs text-muted-foreground">Locked Accounts</p>
              </div>
              <div className={`rounded-lg border p-3 text-center ${s.totalFailedAttempts > 0 ? "border-amber-400 bg-amber-50" : ""}`}>
                <p className="text-2xl font-bold">{s.totalFailedAttempts}</p>
                <p className="text-xs text-muted-foreground">Failed Attempts</p>
              </div>
              <div className={`rounded-lg border p-3 text-center ${s.highRiskAccounts > 0 ? "border-amber-400 bg-amber-50" : ""}`}>
                <p className="text-2xl font-bold">{s.highRiskAccounts}</p>
                <p className="text-xs text-muted-foreground">High/Critical Risk</p>
              </div>
              <div className={`rounded-lg border p-3 text-center ${s.compliancePercentage < 100 ? "border-amber-400 bg-amber-50" : "border-teal-400 bg-teal-50"}`}>
                <p className="text-2xl font-bold text-teal-700">{s.compliancePercentage}%</p>
                <p className="text-xs text-muted-foreground">Compliance Rate</p>
              </div>
            </div>
          )}

          {/* Alert strip */}
          {s && s.privilegedMissingMFA > 0 && (
            <div className="flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span><strong>{s.privilegedMissingMFA}</strong> privileged account{s.privilegedMissingMFA > 1 ? "s" : ""} have MFA required but not enabled — immediate action required.</span>
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-wrap gap-2 items-end">
            <div>
              <Label className="text-xs">Username</Label>
              <Input className="h-8 text-sm w-40" placeholder="Filter…" value={usernameFilter}
                onChange={e => { setUsernameFilter(e.target.value); setPage(1); }} />
            </div>
            <div>
              <Label className="text-xs">Auth Method</Label>
              <select className="border rounded px-2 py-1 text-sm h-8" value={methodFilter}
                onChange={e => { setMethodFilter(e.target.value); setPage(1); }}>
                <option value="">All</option>
                {AUTH_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <select className="border rounded px-2 py-1 text-sm h-8" value={statusFilter}
                onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
                <option value="">All</option>
                {AUTH_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Privileged</Label>
              <select className="border rounded px-2 py-1 text-sm h-8" value={privFilter}
                onChange={e => { setPrivFilter(e.target.value); setPage(1); }}>
                <option value="">All</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">MFA</Label>
              <select className="border rounded px-2 py-1 text-sm h-8" value={mfaFilter}
                onChange={e => { setMfaFilter(e.target.value); setPage(1); }}>
                <option value="">All</option>
                <option value="true">Enabled</option>
                <option value="false">Disabled</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Risk</Label>
              <select className="border rounded px-2 py-1 text-sm h-8" value={riskFilter}
                onChange={e => { setRiskFilter(e.target.value); setPage(1); }}>
                <option value="">All</option>
                {AUTH_RISK_LEVELS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Verified</Label>
              <select className="border rounded px-2 py-1 text-sm h-8" value={verifiedFilter}
                onChange={e => { setVerifiedFilter(e.target.value); setPage(1); }}>
                <option value="">All</option>
                <option value="true">Verified</option>
                <option value="false">Unverified</option>
              </select>
            </div>
            {canManage && (
              <Button className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2 ml-auto"
                onClick={() => setShowCreate(true)}>
                <Plus className="h-4 w-4" /> Add Authentication Control
              </Button>
            )}
          </div>

          {/* List */}
          {listQ.isLoading ? <Skeleton className="h-32 w-full" /> : items.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No authentication controls found. Add one to start tracking.</p>
          ) : (
            <div className="space-y-2">
              {items.map(c => (
                <div key={c.id}
                  className={`flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer hover:bg-muted/40 transition-colors ${c.denialCodes.length > 0 ? "border-amber-400 bg-amber-50/40" : ""} ${c.accountStatus === "Locked" ? "border-red-400 bg-red-50/40" : ""}`}
                  onClick={() => setDetailId(c.id)}>
                  <div className="flex-1 min-w-0">
                    <span className="font-medium truncate">{c.username}</span>
                    {c.privilegedAccount && <Badge variant="outline" className="ml-2 text-xs border-orange-400 text-orange-700">Privileged</Badge>}
                  </div>
                  <Badge variant="outline" className="text-xs">{c.authenticationMethod}</Badge>
                  <Badge variant={authStatusBadge(c.accountStatus)} className="text-xs">{c.accountStatus}</Badge>
                  {c.riskLevel && <Badge variant={authRiskBadge(c.riskLevel)} className="text-xs">{c.riskLevel}</Badge>}
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.mfaEnabled ? "bg-teal-100 text-teal-700" : c.mfaRequired ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-600"}`}>
                    MFA {c.mfaEnabled ? "✓" : c.mfaRequired ? "REQUIRED" : "off"}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${c.identityVerified ? "bg-teal-100 text-teal-700" : "bg-amber-100 text-amber-700"}`}>
                    {c.identityVerified ? "ID Verified" : "Unverified"}
                  </span>
                  {c.failedAttemptCount > 0 && (
                    <span className="text-xs text-red-600 font-medium">{c.failedAttemptCount} failures</span>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {total > pageSize && (
            <div className="flex items-center gap-2 justify-center">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
              <span className="text-sm text-muted-foreground">Page {page} of {Math.ceil(total / pageSize)}</span>
              <Button variant="outline" size="sm" disabled={page * pageSize >= total} onClick={() => setPage(p => p + 1)}>Next</Button>
            </div>
          )}

          {/* Detail view */}
          {detailId && detailQ.data && (
            <div className="rounded-lg border p-4 space-y-4 bg-muted/20">
              {(() => {
                const c = detailQ.data;
                return (
                  <>
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold">{c.username}</h4>
                      <div className="flex gap-2">
                        <Badge variant={authStatusBadge(c.accountStatus)}>{c.accountStatus}</Badge>
                        {c.privilegedAccount && <Badge variant="outline" className="border-orange-400 text-orange-700">Privileged{c.privilegedAccountType ? ` · ${c.privilegedAccountType}` : ""}</Badge>}
                        <Button variant="ghost" size="sm" onClick={() => setDetailId(null)}>✕</Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                      <div><p className="text-muted-foreground">Auth Method</p><p className="font-medium">{c.authenticationMethod}</p></div>
                      <div><p className="text-muted-foreground">Strength</p><p className="font-medium">{c.authenticationStrength ?? "—"}</p></div>
                      <div><p className="text-muted-foreground">Risk Level</p><p className="font-medium">{c.riskLevel ?? "—"}</p></div>
                      <div><p className="text-muted-foreground">Session Timeout</p><p className="font-medium">{c.sessionTimeoutMinutes}m</p></div>
                      <div><p className="text-muted-foreground">MFA Required</p><p className="font-medium">{c.mfaRequired ? "Yes" : "No"}</p></div>
                      <div><p className="text-muted-foreground">MFA Enabled</p><p className="font-medium">{c.mfaEnabled ? `Yes (${c.mfaType ?? "—"})` : "No"}</p></div>
                      <div><p className="text-muted-foreground">Identity Verified</p><p className="font-medium">{c.identityVerified ? "Yes" : "No"}</p></div>
                      <div><p className="text-muted-foreground">Failed Attempts</p><p className={`font-medium ${c.failedAttemptCount > 0 ? "text-red-600" : ""}`}>{c.failedAttemptCount} / {c.lockoutThreshold}</p></div>
                      <div><p className="text-muted-foreground">Password Policy</p><p className="font-medium">{c.passwordPolicyApplied ? "Applied" : "Not Applied"}</p></div>
                      <div><p className="text-muted-foreground">Encryption Required</p><p className="font-medium">{c.encryptionRequired ? "Yes" : "No"}</p></div>
                      {c.verificationMethod && <div><p className="text-muted-foreground">Verification Method</p><p className="font-medium">{c.verificationMethod}</p></div>}
                      {c.verifiedBy && <div><p className="text-muted-foreground">Verified By</p><p className="font-medium">{c.verifiedBy}</p></div>}
                      {c.lastSuccessfulLogin && <div><p className="text-muted-foreground">Last Login</p><p className="font-medium">{new Date(c.lastSuccessfulLogin).toLocaleDateString()}</p></div>}
                      {c.lockReason && <div><p className="text-muted-foreground">Lock Reason</p><p className="font-medium text-red-600">{c.lockReason}</p></div>}
                    </div>

                    {/* Denial codes */}
                    {c.denialCodes.length > 0 && (
                      <div className="rounded bg-amber-50 border border-amber-300 p-2">
                        <p className="text-xs font-semibold text-amber-700 mb-1">Active Denial Codes</p>
                        <div className="flex flex-wrap gap-1">
                          {c.denialCodes.map(d => <Badge key={d} variant="outline" className="text-xs border-amber-500 text-amber-700">{d}</Badge>)}
                        </div>
                      </div>
                    )}

                    {c.notes && <p className="text-sm text-muted-foreground">{c.notes}</p>}
                    {c.evidenceReference && <p className="text-xs text-muted-foreground">Evidence: {c.evidenceReference}</p>}

                    {canManage && (
                      <div className="flex flex-wrap gap-2">
                        {!c.identityVerified && (
                          <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2"
                            onClick={() => { setVerifyId(c.id); setVerifyForm({ verificationMethod: "", verifiedBy: "", verificationEvidence: "" }); }}>
                            <CheckCircle2 className="h-3.5 w-3.5" /> Verify Identity
                          </Button>
                        )}
                        {!c.mfaEnabled && (
                          <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2"
                            onClick={() => { setEnableMFAId(c.id); setMfaTypeForm("TOTP"); }}>
                            <Shield className="h-3.5 w-3.5" /> Enable MFA
                          </Button>
                        )}
                        {c.mfaEnabled && !(c.privilegedAccount && c.mfaRequired) && (
                          <Button size="sm" variant="outline"
                            onClick={() => disableMFAMut.mutate(c.id)}>
                            Disable MFA
                          </Button>
                        )}
                        {c.accountStatus !== "Locked" && (
                          <Button size="sm" variant="outline" className="border-red-400 text-red-700 hover:bg-red-50"
                            onClick={() => { setLockId(c.id); setLockReason(""); }}>
                            Lock Account
                          </Button>
                        )}
                        {c.accountStatus === "Locked" && (
                          <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2"
                            onClick={() => { setUnlockId(c.id); setUnlockReason(""); }}>
                            <CheckCircle2 className="h-3.5 w-3.5" /> Unlock Account
                          </Button>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={v => { setShowCreate(v); if (!v) setErrors({}); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Authentication Control</DialogTitle>
            <DialogDescription>Register a user's authentication posture for § 164.312(d) tracking.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Username / Email *</Label>
                <Input className={fieldCls("", errors, "username")} aria-invalid={!!errors.username} value={form.username} onChange={e => { setF("username", e.target.value); clearErr(setErrors, "username"); }} placeholder="user@example.com" />
                {fErr(errors, "username")}
              </div>
              <div>
                <Label>Authentication Method</Label>
                <select className="border rounded px-2 py-1 text-sm w-full" value={form.authenticationMethod} onChange={e => setF("authenticationMethod", e.target.value)}>
                  {AUTH_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <Label>Authentication Strength</Label>
                <select className="border rounded px-2 py-1 text-sm w-full" value={form.authenticationStrength} onChange={e => setF("authenticationStrength", e.target.value)}>
                  <option value="">Select…</option>
                  {AUTH_STRENGTHS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <Label>Risk Level</Label>
                <select className="border rounded px-2 py-1 text-sm w-full" value={form.riskLevel} onChange={e => setF("riskLevel", e.target.value)}>
                  {AUTH_RISK_LEVELS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <Label>Account Status</Label>
                <select className="border rounded px-2 py-1 text-sm w-full" value={form.accountStatus} onChange={e => setF("accountStatus", e.target.value)}>
                  {AUTH_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <Label>Session Timeout (minutes)</Label>
                <Input type="number" value={form.sessionTimeoutMinutes} onChange={e => setF("sessionTimeoutMinutes", parseInt(e.target.value) || 30)} />
              </div>
              <div>
                <Label>Lockout Threshold</Label>
                <Input type="number" value={form.lockoutThreshold} onChange={e => setF("lockoutThreshold", parseInt(e.target.value) || 5)} />
              </div>
            </div>

            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.mfaRequired} onChange={e => setF("mfaRequired", e.target.checked)} />
                MFA Required
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.mfaEnabled} onChange={e => setF("mfaEnabled", e.target.checked)} />
                MFA Enabled
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.privilegedAccount} onChange={e => setF("privilegedAccount", e.target.checked)} />
                Privileged Account
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.passwordPolicyApplied} onChange={e => setF("passwordPolicyApplied", e.target.checked)} />
                Password Policy Applied
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.encryptionRequired} onChange={e => setF("encryptionRequired", e.target.checked)} />
                Encryption Required
              </label>
            </div>

            {form.mfaEnabled && (
              <div>
                <Label>MFA Type</Label>
                <select className="border rounded px-2 py-1 text-sm w-full" value={form.mfaType} onChange={e => setF("mfaType", e.target.value)}>
                  <option value="">Select…</option>
                  {AUTH_MFA_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            )}

            {form.privilegedAccount && (
              <div>
                <Label>Privileged Account Type</Label>
                <select className="border rounded px-2 py-1 text-sm w-full" value={form.privilegedAccountType} onChange={e => setF("privilegedAccountType", e.target.value)}>
                  <option value="">Select…</option>
                  {AUTH_PRIV_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            )}

            <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={e => setF("notes", e.target.value)} /></div>
            <div><Label>Evidence Reference</Label><Input value={form.evidenceReference} onChange={e => setF("evidenceReference", e.target.value)} placeholder="GOV-ID-2026-001" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2"
              onClick={() => { const errs: Record<string,string> = {}; required(errs, "username", form.username, "Username / Email"); if (Object.keys(errs).length) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } setErrors({}); createMut.mutate(); }} disabled={createMut.isPending}>
              <Plus className="h-4 w-4" /> {createMut.isPending ? "Creating…" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Verify identity dialog */}
      <Dialog open={!!verifyId} onOpenChange={o => !o && setVerifyId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Verify Identity</DialogTitle>
            <DialogDescription>Record identity verification details for § 164.312(d) compliance.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <Label>Verification Method *</Label>
              <select className="border rounded px-2 py-1 text-sm w-full" value={verifyForm.verificationMethod}
                onChange={e => setVerifyForm(f => ({ ...f, verificationMethod: e.target.value }))}>
                <option value="">Select…</option>
                {["GovernmentID","Passport","EmployeeID","InPersonVerification","VideoCall","TrustedIdentityProvider","Other"].map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Verified By</Label>
              <Input value={verifyForm.verifiedBy} onChange={e => setVerifyForm(f => ({ ...f, verifiedBy: e.target.value }))} placeholder="Security Officer" />
            </div>
            <div>
              <Label>Evidence Reference</Label>
              <Input value={verifyForm.verificationEvidence} onChange={e => setVerifyForm(f => ({ ...f, verificationEvidence: e.target.value }))} placeholder="DL-2026-7890" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVerifyId(null)}>Cancel</Button>
            <Button className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2"
              onClick={() => verifyMut.mutate()} disabled={verifyMut.isPending}>
              <CheckCircle2 className="h-4 w-4" /> {verifyMut.isPending ? "Verifying…" : "Verify Identity"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Enable MFA dialog */}
      <Dialog open={!!enableMFAId} onOpenChange={o => !o && setEnableMFAId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enable MFA</DialogTitle>
            <DialogDescription>Select the MFA method to enable for this account.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <Label>MFA Type *</Label>
              <select className="border rounded px-2 py-1 text-sm w-full" value={mfaTypeForm}
                onChange={e => setMfaTypeForm(e.target.value)}>
                {AUTH_MFA_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEnableMFAId(null)}>Cancel</Button>
            <Button className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2"
              onClick={() => enableMFAMut.mutate()} disabled={enableMFAMut.isPending}>
              <Shield className="h-4 w-4" /> {enableMFAMut.isPending ? "Enabling…" : "Enable MFA"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lock account dialog */}
      <Dialog open={!!lockId} onOpenChange={o => !o && setLockId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Lock Account</DialogTitle>
            <DialogDescription>Provide a reason for locking this account.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <Label>Lock Reason</Label>
              <Textarea rows={2} value={lockReason} onChange={e => setLockReason(e.target.value)} placeholder="e.g. Exceeded failed attempt threshold" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLockId(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => lockMut.mutate()} disabled={lockMut.isPending}>
              {lockMut.isPending ? "Locking…" : "Lock Account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unlock account dialog */}
      <Dialog open={!!unlockId} onOpenChange={o => !o && setUnlockId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unlock Account</DialogTitle>
            <DialogDescription>Provide a reason for unlocking this account.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <Label>Unlock Reason</Label>
              <Textarea rows={2} value={unlockReason} onChange={e => setUnlockReason(e.target.value)} placeholder="e.g. Verified by administrator" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUnlockId(null)}>Cancel</Button>
            <Button className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2"
              onClick={() => unlockMut.mutate()} disabled={unlockMut.isPending}>
              <CheckCircle2 className="h-4 w-4" /> {unlockMut.isPending ? "Unlocking…" : "Unlock Account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── HIPAA-15 — Integrity (§ 164.312(c)) ──────────────────────────────────────

type IntegritySummary = {
  total:number; totalControls:number; activeControls:number; underReviewControls:number;
  totalValidations:number; validationPassed:number; validationFailed:number;
  validationUnableToVerify:number; recentValidationFailures:number;
  totalIncidents:number; openIncidents:number; criticalIncidents:number; resolvedIncidents:number;
  totalChanges:number; approvedChanges:number; unauthorizedChanges:number;
  totalBackupTests:number; backupPassed:number; backupFailed:number; backupPartial:number;
  pendingCorrectiveActions:number; completedCorrectiveActions:number; ephiRecords:number;
};

type IntegrityRecord = {
  id:string; recordType:string; title:string; description?:string;
  affectedSystem?:string; affectedDataTypes?:string; ephiInvolved:boolean;
  controlType?:string; controlMechanism?:string; reviewFrequency?:string;
  lastReviewDate?:string; nextReviewDate?:string; ownerName?:string;
  validationResult?:string; validationDate?:string; validatedBy?:string;
  validationMethod?:string; failureReason?:string;
  severityLevel?:string; detectedAt?:string; detectionMethod?:string;
  resolvedAt?:string; resolutionSummary?:string;
  changeType?:string; changeRequestRef?:string; changeDate?:string;
  changedBy?:string; changeApprovedBy?:string; changeApproved:boolean;
  backupType?:string; backupDate?:string; restorationTestDate?:string;
  restorationSuccess?:boolean; restorationDurationMinutes?:number; backupLocationReference?:string;
  findingsSummary?:string; correctiveActions?:string; correctiveActionStatus?:string;
  correctiveActionDue?:string; correctiveActionCompletedAt?:string;
  status:string; evidenceReference?:string; notes?:string;
  createdAt:string; updatedAt:string; isHighPriority:boolean;
};

const IR_RECORD_TYPES = ["IntegrityControl","ValidationCheck","IntegrityIncident","ChangeRecord","BackupEvidence"];
const IR_VALIDATION_RESULTS = ["Pass","Fail","UnableToVerify"];
const IR_SEVERITY_LEVELS = ["Low","Medium","High","Critical"];
const IR_CONTROL_TYPES = ["Checksum","HashVerification","DigitalSignature","AccessLog","IDS","MFA","Audit","Other"];
const IR_CHANGE_TYPES = ["Planned","Emergency","Unauthorized"];
const IR_BACKUP_TYPES = ["Full","Incremental","Differential"];
const IR_CORRECTIVE_STATUSES = ["Pending","InProgress","Completed","NA"];

function irTypeBadge(type:string): "default"|"secondary"|"outline"|"destructive" {
  if (type==="IntegrityControl") return "default";
  if (type==="ValidationCheck") return "secondary";
  if (type==="IntegrityIncident") return "destructive";
  if (type==="ChangeRecord") return "outline";
  return "secondary";
}
function irStatusBadge(status:string): "default"|"secondary"|"outline"|"destructive" {
  if (status==="Active"||status==="Passed"||status==="Completed"||status==="Approved"||status==="Resolved") return "default";
  if (status==="Open"||status==="Unable to complete action"||status==="Pending"||status==="InProgress") return "destructive";
  return "outline";
}
function irValidationBadge(v?:string): "default"|"secondary"|"outline"|"destructive" {
  if (v==="Pass") return "default";
  if (v==="Fail") return "destructive";
  if (v==="UnableToVerify") return "outline";
  return "secondary";
}

function HipaaIntegritySection({ canManage }: { canManage: boolean }) {
  const { toast } = useToast();
  const authFetch = useEvalAuth();
  const qc = useQueryClient();
  const BASE = "/api/organization/compliance/hipaa/integrity";

  // Summary
  const summaryQ = useQuery<IntegritySummary>({
    queryKey: ["hipaa15-summary"],
    queryFn: async () => { const r = await authFetch(`${BASE}/summary`); if (!r.ok) throw new Error(); return r.json(); },
    staleTime: 0,
  });

  // Filters + list
  const [page, setPage]                       = useState(1);
  const [typeFilter, setTypeFilter]           = useState("");
  const [statusFilter, setStatusFilter]       = useState("");
  const [valFilter, setValFilter]             = useState("");
  const [sevFilter, setSevFilter]             = useState("");
  const [ephiFilter, setEphiFilter]           = useState("");

  const listQ = useQuery<{ items: IntegrityRecord[]; total: number }>({
    queryKey: ["hipaa15-list", page, typeFilter, statusFilter, valFilter, sevFilter, ephiFilter],
    queryFn: async () => {
      const p = new URLSearchParams({ page: String(page), pageSize: "10" });
      if (typeFilter)   p.set("recordType", typeFilter);
      if (statusFilter) p.set("status", statusFilter);
      if (valFilter)    p.set("validationResult", valFilter);
      if (sevFilter)    p.set("severityLevel", sevFilter);
      if (ephiFilter)   p.set("ephiInvolved", ephiFilter);
      const r = await authFetch(`${BASE}?${p}`);
      if (!r.ok) throw new Error(); return r.json();
    },
    staleTime: 0,
  });

  // Failed report
  const failedQ = useQuery<{ items: IntegrityRecord[]; total: number }>({
    queryKey: ["hipaa15-failed"],
    queryFn: async () => { const r = await authFetch(`${BASE}/failed`); if (!r.ok) throw new Error(); return r.json(); },
    staleTime: 0,
  });

  // Detail
  const [detailId, setDetailId] = useState<string | null>(null);
  const detailQ = useQuery<IntegrityRecord>({
    queryKey: ["hipaa15-detail", detailId],
    queryFn: async () => { const r = await authFetch(`${BASE}/${detailId}`); if (!r.ok) throw new Error(); return r.json(); },
    enabled: !!detailId,
    staleTime: 0,
  });

  // Create
  const [showCreate, setShowCreate] = useState(false);
  const defaultIrForm = () => ({
    recordType: "IntegrityControl", title: "", description: "",
    affectedSystem: "", affectedDataTypes: "", ephiInvolved: false,
    controlType: "", controlMechanism: "", reviewFrequency: "", ownerName: "",
    validationResult: "", validatedBy: "", validationMethod: "",
    severityLevel: "", detectionMethod: "",
    changeType: "Planned", changeRequestRef: "", changedBy: "", changeApprovedBy: "", changeApproved: false,
    backupType: "Full", backupLocationReference: "",
    findingsSummary: "", correctiveActions: "", correctiveActionStatus: "",
    status: "Active", evidenceReference: "", notes: "",
  });
  const [form, setForm] = useState(defaultIrForm());
  const [errors, setErrors] = useState<Record<string,string>>({});

  const setF = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));

  const createMut = useMutation({
    mutationFn: async () => {
      const body: Record<string, any> = { ...form };
      if (!body.controlType) delete body.controlType;
      if (!body.validationResult) delete body.validationResult;
      if (!body.severityLevel) delete body.severityLevel;
      if (!body.changeType) delete body.changeType;
      if (!body.backupType) delete body.backupType;
      if (!body.correctiveActionStatus) delete body.correctiveActionStatus;
      const r = await authFetch(BASE, { method: "POST", body: JSON.stringify(body) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.message || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => {
      toast({ title: "Integrity record created." });
      setShowCreate(false);
      setForm(defaultIrForm());
      setErrors({});
      qc.invalidateQueries({ queryKey: ["hipaa15-list"] });
      qc.invalidateQueries({ queryKey: ["hipaa15-summary"] });
      qc.invalidateQueries({ queryKey: ["hipaa15-failed"] });
    },
    onError: (e: any) => { const s = extractServerErrors(e); if (s && Object.keys(s).length) { setErrors(s); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); } else toast({ title: "Unable to save", description: (e as Error)?.message, variant: "destructive" }); },
  });

  // Validate
  const [validateId, setValidateId] = useState<string | null>(null);
  const [valForm, setValForm] = useState({ validationResult: "Pass", validatedBy: "", validationMethod: "", failureReason: "", findingsSummary: "", correctiveActions: "", evidenceReference: "" });

  const validateMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/${validateId}/validate`, { method: "PATCH", body: JSON.stringify({ ...valForm, validationDate: new Date().toISOString() }) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.message || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => {
      toast({ title: "Validation recorded." });
      setValidateId(null);
      qc.invalidateQueries({ queryKey: ["hipaa15-list"] });
      qc.invalidateQueries({ queryKey: ["hipaa15-summary"] });
      qc.invalidateQueries({ queryKey: ["hipaa15-failed"] });
      if (detailId) qc.invalidateQueries({ queryKey: ["hipaa15-detail", detailId] });
    },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  // Resolve
  const [resolveId, setResolveId] = useState<string | null>(null);
  const [resolveForm, setResolveForm] = useState({ status: "Resolved", resolutionSummary: "", correctiveActionStatus: "Completed", evidenceReference: "" });

  const resolveMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/${resolveId}/resolve`, { method: "PATCH", body: JSON.stringify(resolveForm) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.message || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => {
      toast({ title: "Record resolved." });
      setResolveId(null);
      qc.invalidateQueries({ queryKey: ["hipaa15-list"] });
      qc.invalidateQueries({ queryKey: ["hipaa15-summary"] });
      qc.invalidateQueries({ queryKey: ["hipaa15-failed"] });
      if (detailId) qc.invalidateQueries({ queryKey: ["hipaa15-detail", detailId] });
    },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  // Delete
  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      const r = await authFetch(`${BASE}/${id}`, { method: "DELETE" });
      if (!r.ok) throw new Error("Failed to delete");
    },
    onSuccess: () => {
      toast({ title: "Record deleted." });
      setDetailId(null);
      qc.invalidateQueries({ queryKey: ["hipaa15-list"] });
      qc.invalidateQueries({ queryKey: ["hipaa15-summary"] });
      qc.invalidateQueries({ queryKey: ["hipaa15-failed"] });
    },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  // Detail view
  if (detailId && detailQ.data) {
    const rec = detailQ.data;
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-teal-600" />
              {rec.title}
            </span>
            <Button size="sm" variant="ghost" onClick={() => setDetailId(null)}>← Back</Button>
          </CardTitle>
          <CardDescription>{rec.recordType}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <div className="flex flex-wrap gap-2">
            <Badge variant={irTypeBadge(rec.recordType) as any}>{rec.recordType}</Badge>
            <Badge variant={irStatusBadge(rec.status) as any}>{rec.status}</Badge>
            {rec.validationResult && <Badge variant={irValidationBadge(rec.validationResult) as any}>Result: {rec.validationResult}</Badge>}
            {rec.severityLevel && <Badge variant={rec.severityLevel==="Critical"||rec.severityLevel==="High" ? "destructive" : "outline"}>Severity: {rec.severityLevel}</Badge>}
            {rec.ephiInvolved && <Badge variant="outline" className="border-amber-400 text-amber-700">ePHI</Badge>}
            {rec.isHighPriority && <Badge variant="destructive">High Priority</Badge>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            {rec.affectedSystem    && <div><p className="font-medium text-xs text-muted-foreground">Affected System</p><p>{rec.affectedSystem}</p></div>}
            {rec.affectedDataTypes && <div><p className="font-medium text-xs text-muted-foreground">Data Types</p><p>{rec.affectedDataTypes}</p></div>}
            {rec.ownerName         && <div><p className="font-medium text-xs text-muted-foreground">Owner</p><p>{rec.ownerName}</p></div>}
            {rec.reviewFrequency   && <div><p className="font-medium text-xs text-muted-foreground">Review Frequency</p><p>{rec.reviewFrequency}</p></div>}
            {rec.controlType       && <div><p className="font-medium text-xs text-muted-foreground">Control Type</p><p>{rec.controlType}</p></div>}
            {rec.nextReviewDate    && <div><p className="font-medium text-xs text-muted-foreground">Next Review</p><p>{rec.nextReviewDate.slice(0,10)}</p></div>}
            {rec.validatedBy       && <div><p className="font-medium text-xs text-muted-foreground">Validated By</p><p>{rec.validatedBy}</p></div>}
            {rec.validationDate    && <div><p className="font-medium text-xs text-muted-foreground">Validation Date</p><p>{rec.validationDate.slice(0,10)}</p></div>}
            {rec.validationMethod  && <div><p className="font-medium text-xs text-muted-foreground">Method</p><p>{rec.validationMethod}</p></div>}
            {rec.detectedAt        && <div><p className="font-medium text-xs text-muted-foreground">Detected At</p><p>{rec.detectedAt.slice(0,10)}</p></div>}
            {rec.detectionMethod   && <div><p className="font-medium text-xs text-muted-foreground">Detection Method</p><p>{rec.detectionMethod}</p></div>}
            {rec.resolvedAt        && <div><p className="font-medium text-xs text-muted-foreground">Resolved At</p><p>{rec.resolvedAt.slice(0,10)}</p></div>}
            {rec.changeRequestRef  && <div><p className="font-medium text-xs text-muted-foreground">Change Ref</p><p>{rec.changeRequestRef}</p></div>}
            {rec.changeType        && <div><p className="font-medium text-xs text-muted-foreground">Change Type</p><p>{rec.changeType}</p></div>}
            {rec.changedBy         && <div><p className="font-medium text-xs text-muted-foreground">Changed By</p><p>{rec.changedBy}</p></div>}
            {rec.changeApprovedBy  && <div><p className="font-medium text-xs text-muted-foreground">Approved By</p><p>{rec.changeApprovedBy}</p></div>}
            {rec.backupType        && <div><p className="font-medium text-xs text-muted-foreground">Backup Type</p><p>{rec.backupType}</p></div>}
            {rec.backupDate        && <div><p className="font-medium text-xs text-muted-foreground">Backup Date</p><p>{rec.backupDate.slice(0,10)}</p></div>}
            {rec.restorationTestDate && <div><p className="font-medium text-xs text-muted-foreground">Restore Test</p><p>{rec.restorationTestDate.slice(0,10)}</p></div>}
            {rec.restorationDurationMinutes && <div><p className="font-medium text-xs text-muted-foreground">Duration</p><p>{rec.restorationDurationMinutes} min</p></div>}
            {rec.evidenceReference && <div><p className="font-medium text-xs text-muted-foreground">Evidence Reference</p><p>{rec.evidenceReference}</p></div>}
          </div>

          {rec.failureReason && (
            <div><p className="font-medium text-xs text-muted-foreground mb-1">Failure Reason</p>
              <p className="rounded bg-red-50 border border-red-200 p-2 text-red-800">{rec.failureReason}</p></div>
          )}

          {rec.findingsSummary && (
            <div><p className="font-medium text-xs text-muted-foreground mb-1">Findings</p>
              <p className="rounded bg-muted/30 border p-2">{rec.findingsSummary}</p></div>
          )}

          {rec.resolutionSummary && (
            <div><p className="font-medium text-xs text-muted-foreground mb-1">Resolution</p>
              <p className="rounded bg-muted/30 border p-2">{rec.resolutionSummary}</p></div>
          )}

          {rec.correctiveActions && (
            <div className="rounded border border-amber-200 bg-amber-50 p-3 space-y-1">
              <div className="flex items-center justify-between">
                <p className="font-medium text-xs text-amber-700">Corrective Actions</p>
                {rec.correctiveActionStatus && <Badge variant="outline">{rec.correctiveActionStatus}</Badge>}
              </div>
              <p className="text-sm">{rec.correctiveActions}</p>
              {rec.correctiveActionDue && <p className="text-xs">Due: {rec.correctiveActionDue.slice(0,10)}</p>}
              {rec.correctiveActionCompletedAt && <p className="text-xs">Completed: {rec.correctiveActionCompletedAt.slice(0,10)}</p>}
            </div>
          )}

          {rec.controlMechanism && (
            <div><p className="font-medium text-xs text-muted-foreground mb-1">Control Mechanism</p>
              <p className="rounded bg-muted/30 border p-2">{rec.controlMechanism}</p></div>
          )}

          {rec.notes && <div><p className="font-medium text-xs text-muted-foreground mb-1">Notes</p><p>{rec.notes}</p></div>}

          {canManage && (
            <div className="flex gap-2 flex-wrap pt-2">
              {rec.recordType === "ValidationCheck" && rec.status !== "Completed" && (
                <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2"
                  onClick={() => { setValidateId(rec.id); setValForm({ validationResult: "Pass", validatedBy: "", validationMethod: "", failureReason: "", findingsSummary: "", correctiveActions: "", evidenceReference: "" }); }}>
                  <CheckCircle2 className="h-3 w-3" /> Record Validation
                </Button>
              )}
              {(rec.recordType === "IntegrityIncident" || rec.correctiveActionStatus === "InProgress" || rec.correctiveActionStatus === "Pending") &&
               rec.status !== "Closed" && rec.status !== "Resolved" && (
                <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2"
                  onClick={() => { setResolveId(rec.id); setResolveForm({ status: "Resolved", resolutionSummary: "", correctiveActionStatus: "Completed", evidenceReference: "" }); }}>
                  <CheckCircle2 className="h-3 w-3" /> Resolve
                </Button>
              )}
              <Button size="sm" variant="destructive"
                onClick={() => { if (confirm("Delete this record?")) deleteMut.mutate(rec.id); }}
                disabled={deleteMut.isPending}>Delete</Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <>
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-teal-600" />
          Integrity Controls
        </CardTitle>
        <CardDescription>
          Technical security measures to guard against unauthorized modification or destruction of ePHI (§ 164.312(c)).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">

        {/* Dashboard */}
        {summaryQ.isLoading ? <Skeleton className="h-20 w-full" /> : summaryQ.data && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { label: "Active Controls",       value: summaryQ.data.activeControls,          warn: false },
              { label: "Validation Passed",      value: summaryQ.data.validationPassed,        warn: false },
              { label: "Validation Failed",      value: summaryQ.data.validationFailed,        warn: summaryQ.data.validationFailed > 0 },
              { label: "Unable to Verify",       value: summaryQ.data.validationUnableToVerify, warn: summaryQ.data.validationUnableToVerify > 0 },
              { label: "Open Incidents",         value: summaryQ.data.openIncidents,           warn: summaryQ.data.openIncidents > 0 },
              { label: "Critical Incidents",     value: summaryQ.data.criticalIncidents,       warn: summaryQ.data.criticalIncidents > 0 },
              { label: "Backup Tests Passed",    value: summaryQ.data.backupPassed,            warn: false },
              { label: "Backup Tests Failed",    value: summaryQ.data.backupFailed,            warn: summaryQ.data.backupFailed > 0 },
              { label: "Pending Corrections",    value: summaryQ.data.pendingCorrectiveActions, warn: summaryQ.data.pendingCorrectiveActions > 0 },
              { label: "ePHI Records",           value: summaryQ.data.ephiRecords,             warn: false },
            ].map(m => (
              <div key={m.label} className={`rounded-lg border p-3 text-center ${m.warn ? "border-amber-300 bg-amber-50" : "border-muted bg-muted/30"}`}>
                <p className={`text-xl font-bold ${m.warn ? "text-amber-700" : "text-foreground"}`}>{m.value}</p>
                <p className="text-xs text-muted-foreground mt-1">{m.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* High-priority alert */}
        {(failedQ.data?.total ?? 0) > 0 && (
          <div className="rounded border border-red-300 bg-red-50 p-2 flex items-center gap-2 text-red-700 text-sm">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            {failedQ.data!.total} record(s) require attention: open incidents, failed validations, or pending corrective actions.
          </div>
        )}

        {/* Controls row */}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex gap-2 flex-wrap">
            <select className="border rounded px-2 py-1 text-xs" value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1); }}>
              <option value="">All Types</option>
              {IR_RECORD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select className="border rounded px-2 py-1 text-xs" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
              <option value="">All Statuses</option>
              {["Active","Inactive","UnderReview","Open","InProgress","Resolved","Closed","Pending","Approved","Completed","Passed","Unable to complete action","Partial"].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select className="border rounded px-2 py-1 text-xs" value={valFilter} onChange={e => { setValFilter(e.target.value); setPage(1); }}>
              <option value="">All Results</option>
              {IR_VALIDATION_RESULTS.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
            <select className="border rounded px-2 py-1 text-xs" value={sevFilter} onChange={e => { setSevFilter(e.target.value); setPage(1); }}>
              <option value="">All Severity</option>
              {IR_SEVERITY_LEVELS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select className="border rounded px-2 py-1 text-xs" value={ephiFilter} onChange={e => { setEphiFilter(e.target.value); setPage(1); }}>
              <option value="">All Records</option>
              <option value="true">ePHI Only</option>
              <option value="false">Non-ePHI</option>
            </select>
          </div>
          {canManage && (
            <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2"
              onClick={() => { setShowCreate(true); setForm(defaultIrForm()); }}>
              <Plus className="h-3 w-3" /> Add Record
            </Button>
          )}
        </div>

        {/* List */}
        {listQ.isLoading ? <Skeleton className="h-24 w-full" /> : (
          <div className="space-y-2">
            {(listQ.data?.items ?? []).length === 0 && <p className="text-xs text-muted-foreground">No records found.</p>}
            {(listQ.data?.items ?? []).map(rec => (
              <div key={rec.id} className={`rounded border p-3 space-y-1 cursor-pointer hover:bg-muted/10 transition-colors ${rec.isHighPriority ? "border-red-300 bg-red-50/50" : ""}`}
                   onClick={() => setDetailId(rec.id)}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <span className="font-medium text-sm">{rec.title}</span>
                  <div className="flex gap-1 flex-wrap">
                    <Badge variant={irTypeBadge(rec.recordType) as any} className="text-xs">{rec.recordType}</Badge>
                    <Badge variant={irStatusBadge(rec.status) as any} className="text-xs">{rec.status}</Badge>
                    {rec.validationResult && <Badge variant={irValidationBadge(rec.validationResult) as any} className="text-xs">{rec.validationResult}</Badge>}
                    {rec.severityLevel && <Badge variant={rec.severityLevel==="Critical"||rec.severityLevel==="High" ? "destructive" : "outline"} className="text-xs">{rec.severityLevel}</Badge>}
                    {rec.ephiInvolved && <Badge variant="outline" className="text-xs border-amber-400 text-amber-700">ePHI</Badge>}
                  </div>
                </div>
                <div className="text-xs text-muted-foreground flex flex-wrap gap-3">
                  {rec.affectedSystem && <span>System: {rec.affectedSystem}</span>}
                  {rec.ownerName && <span>Owner: {rec.ownerName}</span>}
                  {rec.validationDate && <span>Validated: {rec.validationDate.slice(0,10)}</span>}
                  {rec.detectedAt && <span>Detected: {rec.detectedAt.slice(0,10)}</span>}
                </div>
                {canManage && (
                  <div className="flex gap-1 pt-1" onClick={e => e.stopPropagation()}>
                    {rec.recordType === "ValidationCheck" && rec.status !== "Completed" && (
                      <Button size="sm" variant="ghost" className="h-6 text-xs px-2"
                        onClick={() => { setValidateId(rec.id); setValForm({ validationResult: "Pass", validatedBy: "", validationMethod: "", failureReason: "", findingsSummary: "", correctiveActions: "", evidenceReference: "" }); }}>
                        Validate
                      </Button>
                    )}
                    {rec.status !== "Closed" && rec.status !== "Resolved" && rec.status !== "Passed" && rec.status !== "Completed" && (
                      <Button size="sm" variant="ghost" className="h-6 text-xs px-2"
                        onClick={() => { setResolveId(rec.id); setResolveForm({ status: "Resolved", resolutionSummary: "", correctiveActionStatus: "Completed", evidenceReference: "" }); }}>
                        Resolve
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="h-6 text-xs px-2 text-red-600"
                      onClick={() => { if (confirm("Delete?")) deleteMut.mutate(rec.id); }} disabled={deleteMut.isPending}>
                      Delete
                    </Button>
                  </div>
                )}
              </div>
            ))}
            {(listQ.data?.total ?? 0) > 10 && (
              <div className="flex items-center justify-center gap-2">
                <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
                <span className="text-xs text-muted-foreground">Page {page}</span>
                <Button size="sm" variant="ghost" disabled={(listQ.data?.items?.length ?? 0) < 10} onClick={() => setPage(p => p + 1)}>Next</Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>

    {/* Create Dialog */}
    {showCreate && (
      <Dialog open onOpenChange={v => { setShowCreate(v); if (!v) setErrors({}); }}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Integrity Record</DialogTitle>
            <DialogDescription>Create an integrity control, validation check, incident, change record, or backup evidence record.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Record Type *</Label>
              <select className="w-full border rounded px-2 py-1 text-sm" value={form.recordType} onChange={e => setF("recordType", e.target.value)}>
                {IR_RECORD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <Label>Title *</Label>
              <Input className={fieldCls("", errors, "title")} aria-invalid={!!errors.title} value={form.title} onChange={e => { setF("title", e.target.value); clearErr(setErrors, "title"); }} placeholder="Descriptive title" />
              {fErr(errors, "title")}
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={form.description} onChange={e => setF("description", e.target.value)} rows={2} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Affected System</Label>
                <Input value={form.affectedSystem} onChange={e => setF("affectedSystem", e.target.value)} placeholder="e.g. EHR Database" />
              </div>
              <div>
                <Label>Data Types</Label>
                <Input value={form.affectedDataTypes} onChange={e => setF("affectedDataTypes", e.target.value)} placeholder="e.g. ePHI, PII" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="ir-ephi" checked={form.ephiInvolved} onChange={e => setF("ephiInvolved", e.target.checked)} />
              <Label htmlFor="ir-ephi">ePHI Involved</Label>
            </div>

            {form.recordType === "IntegrityControl" && (<>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Control Type</Label>
                  <select className="w-full border rounded px-2 py-1 text-sm" value={form.controlType} onChange={e => setF("controlType", e.target.value)}>
                    <option value="">Select…</option>
                    {IR_CONTROL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Review Frequency</Label>
                  <Input value={form.reviewFrequency} onChange={e => setF("reviewFrequency", e.target.value)} placeholder="e.g. Quarterly" />
                </div>
              </div>
              <div>
                <Label>Owner Name</Label>
                <Input value={form.ownerName} onChange={e => setF("ownerName", e.target.value)} />
              </div>
              <div>
                <Label>Control Mechanism</Label>
                <Textarea value={form.controlMechanism} onChange={e => setF("controlMechanism", e.target.value)} rows={2} placeholder="How the control works…" />
              </div>
            </>)}

            {form.recordType === "ValidationCheck" && (<>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Validation Result</Label>
                  <select className="w-full border rounded px-2 py-1 text-sm" value={form.validationResult} onChange={e => setF("validationResult", e.target.value)}>
                    <option value="">Not yet run</option>
                    {IR_VALIDATION_RESULTS.map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Validated By</Label>
                  <Input value={form.validatedBy} onChange={e => setF("validatedBy", e.target.value)} />
                </div>
              </div>
              <div>
                <Label>Validation Method</Label>
                <Input value={form.validationMethod} onChange={e => setF("validationMethod", e.target.value)} placeholder="e.g. Automated hash comparison" />
              </div>
            </>)}

            {form.recordType === "IntegrityIncident" && (<>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Severity Level</Label>
                  <select className="w-full border rounded px-2 py-1 text-sm" value={form.severityLevel} onChange={e => setF("severityLevel", e.target.value)}>
                    <option value="">Select…</option>
                    {IR_SEVERITY_LEVELS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Detection Method</Label>
                  <Input value={form.detectionMethod} onChange={e => setF("detectionMethod", e.target.value)} placeholder="e.g. IDS alert" />
                </div>
              </div>
            </>)}

            {form.recordType === "ChangeRecord" && (<>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Change Type</Label>
                  <select className="w-full border rounded px-2 py-1 text-sm" value={form.changeType} onChange={e => setF("changeType", e.target.value)}>
                    {IR_CHANGE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Change Request Ref</Label>
                  <Input value={form.changeRequestRef} onChange={e => setF("changeRequestRef", e.target.value)} placeholder="e.g. CHG-2026-042" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Changed By</Label>
                  <Input value={form.changedBy} onChange={e => setF("changedBy", e.target.value)} />
                </div>
                <div>
                  <Label>Approved By</Label>
                  <Input value={form.changeApprovedBy} onChange={e => setF("changeApprovedBy", e.target.value)} />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="ir-approved" checked={form.changeApproved} onChange={e => setF("changeApproved", e.target.checked)} />
                <Label htmlFor="ir-approved">Change Approved</Label>
              </div>
            </>)}

            {form.recordType === "BackupEvidence" && (<>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Backup Type</Label>
                  <select className="w-full border rounded px-2 py-1 text-sm" value={form.backupType} onChange={e => setF("backupType", e.target.value)}>
                    {IR_BACKUP_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Backup Location Ref</Label>
                  <Input value={form.backupLocationReference} onChange={e => setF("backupLocationReference", e.target.value)} placeholder="e.g. s3://backups/…" />
                </div>
              </div>
            </>)}

            <div>
              <Label>Findings Summary</Label>
              <Textarea value={form.findingsSummary} onChange={e => setF("findingsSummary", e.target.value)} rows={2} />
            </div>
            <div>
              <Label>Status</Label>
              <Input value={form.status} onChange={e => setF("status", e.target.value)} placeholder="Active" />
            </div>
            <div>
              <Label>Evidence Reference</Label>
              <Input value={form.evidenceReference} onChange={e => setF("evidenceReference", e.target.value)} />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={e => setF("notes", e.target.value)} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2"
              onClick={() => { const errs: Record<string,string> = {}; required(errs, "title", form.title, "Title"); if (Object.keys(errs).length) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } setErrors({}); createMut.mutate(); }} disabled={createMut.isPending}>
              {createMut.isPending ? "Saving…" : "Create Record"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )}

    {/* Validate Dialog */}
    {validateId && (
      <Dialog open onOpenChange={() => setValidateId(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Record Validation Result</DialogTitle>
            <DialogDescription>Document the outcome of this integrity validation check.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Validation Result *</Label>
              <select className="w-full border rounded px-2 py-1 text-sm" value={valForm.validationResult} onChange={e => setValForm(f => ({ ...f, validationResult: e.target.value }))}>
                {IR_VALIDATION_RESULTS.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <Label>Validated By</Label>
              <Input value={valForm.validatedBy} onChange={e => setValForm(f => ({ ...f, validatedBy: e.target.value }))} />
            </div>
            <div>
              <Label>Validation Method</Label>
              <Input value={valForm.validationMethod} onChange={e => setValForm(f => ({ ...f, validationMethod: e.target.value }))} />
            </div>
            {valForm.validationResult === "Fail" && (
              <div>
                <Label>Failure Reason</Label>
                <Textarea value={valForm.failureReason} onChange={e => setValForm(f => ({ ...f, failureReason: e.target.value }))} rows={2} />
              </div>
            )}
            <div>
              <Label>Findings Summary</Label>
              <Textarea value={valForm.findingsSummary} onChange={e => setValForm(f => ({ ...f, findingsSummary: e.target.value }))} rows={2} />
            </div>
            {valForm.validationResult !== "Pass" && (
              <div>
                <Label>Corrective Actions</Label>
                <Textarea value={valForm.correctiveActions} onChange={e => setValForm(f => ({ ...f, correctiveActions: e.target.value }))} rows={2} />
              </div>
            )}
            <div>
              <Label>Evidence Reference</Label>
              <Input value={valForm.evidenceReference} onChange={e => setValForm(f => ({ ...f, evidenceReference: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setValidateId(null)}>Cancel</Button>
            <Button className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2"
              onClick={() => validateMut.mutate()} disabled={validateMut.isPending}>
              {validateMut.isPending ? "Saving…" : "Record Result"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )}

    {/* Resolve Dialog */}
    {resolveId && (
      <Dialog open onOpenChange={() => setResolveId(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Resolve Record</DialogTitle>
            <DialogDescription>Mark this incident or corrective action as resolved.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>New Status</Label>
              <select className="w-full border rounded px-2 py-1 text-sm" value={resolveForm.status} onChange={e => setResolveForm(f => ({ ...f, status: e.target.value }))}>
                {["Resolved","Closed","Completed","Passed"].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <Label>Resolution Summary</Label>
              <Textarea value={resolveForm.resolutionSummary} onChange={e => setResolveForm(f => ({ ...f, resolutionSummary: e.target.value }))} rows={2} />
            </div>
            <div>
              <Label>Corrective Action Status</Label>
              <select className="w-full border rounded px-2 py-1 text-sm" value={resolveForm.correctiveActionStatus} onChange={e => setResolveForm(f => ({ ...f, correctiveActionStatus: e.target.value }))}>
                {IR_CORRECTIVE_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <Label>Evidence Reference</Label>
              <Input value={resolveForm.evidenceReference} onChange={e => setResolveForm(f => ({ ...f, evidenceReference: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveId(null)}>Cancel</Button>
            <Button className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2"
              onClick={() => resolveMut.mutate()} disabled={resolveMut.isPending}>
              {resolveMut.isPending ? "Saving…" : "Resolve"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )}
    </>
  );
}

const EVAL_TYPES = [
  "PeriodicSecurityEvaluation","PostMaterialChangeEvaluation","RiskControlAssessment",
  "PolicyReview","SafeguardReview","TechnicalSecurityReview",
  "AdministrativeSecurityReview","PhysicalSecurityReview","Other",
];

function HipaaSecurityEvaluationSection({ canManage }: { canManage: boolean }) {
  const { toast } = useToast();
  const authFetch = useEvalAuth();
  const BASE = "/api/organization/compliance/hipaa/security-evaluations";

  // Summary
  const summaryQ = useQuery<EvalSummary>({
    queryKey: ["hipaa09-summary"],
    queryFn: async () => { const r = await authFetch(`${BASE}/summary`); if (!r.ok) throw new Error(); return r.json(); },
    staleTime: 0,
  });

  // List with filters
  const [page, setPage]           = useState(1);
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [riskFilter, setRiskFilter]     = useState("");
  const [matFilter, setMatFilter]       = useState("");

  const listQ = useQuery<{ items: SecurityEvaluation[]; total: number }>({
    queryKey: ["hipaa09-list", page, typeFilter, statusFilter, riskFilter, matFilter],
    queryFn: async () => {
      const p = new URLSearchParams({ page: String(page), pageSize: "10" });
      if (typeFilter)   p.set("evaluationType", typeFilter);
      if (statusFilter) p.set("status", statusFilter);
      if (riskFilter)   p.set("riskLevel", riskFilter);
      if (matFilter)    p.set("materialChange", matFilter);
      const r = await authFetch(`${BASE}?${p}`);
      if (!r.ok) throw new Error(); return r.json();
    },
    staleTime: 0,
  });

  // Overdue
  const overdueQ = useQuery<{ items: SecurityEvaluation[]; total: number }>({
    queryKey: ["hipaa09-overdue"],
    queryFn: async () => { const r = await authFetch(`${BASE}/overdue`); if (!r.ok) throw new Error(); return r.json(); },
    staleTime: 0,
  });

  // Detail view
  const [detailId, setDetailId] = useState<string | null>(null);
  const detailQ = useQuery<SecurityEvaluation>({
    queryKey: ["hipaa09-detail", detailId],
    queryFn: async () => { const r = await authFetch(`${BASE}/${detailId}`); if (!r.ok) throw new Error(); return r.json(); },
    enabled: !!detailId,
    staleTime: 0,
  });

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const defaultForm = () => ({
    evaluationTitle: "", evaluationType: "PeriodicSecurityEvaluation",
    description: "", scope: "", evaluatedControls: "",
    evaluatorName: "", evaluatorRole: "", frequency: "Annual",
    methodology: "", nextEvaluationDate: "", status: "Planned",
    materialChangeTriggered: false, materialChangeDescription: "",
    relatedSystemOrPolicy: "", notes: "",
  });
  const [form, setForm] = useState(defaultForm());
  const [errors, setErrors] = useState<Record<string,string>>({});

  const createMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(BASE, {
        method: "POST",
        body: JSON.stringify({
          ...form,
          nextEvaluationDate: form.nextEvaluationDate || null,
        }),
      });
      if (!r.ok) { const e = await r.json(); throw new Error(e.message || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => {
      toast({ title: "Evaluation created." });
      setShowCreate(false);
      setForm(defaultForm());
      setErrors({});
      queryClient.invalidateQueries({ queryKey: ["hipaa09-list"] });
      queryClient.invalidateQueries({ queryKey: ["hipaa09-summary"] });
    },
    onError: (e: any) => { const s = extractServerErrors(e); if (s && Object.keys(s).length) { setErrors(s); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); } else toast({ title: "Unable to save", description: (e as Error)?.message, variant: "destructive" }); },
  });

  // Complete form
  const [completeId, setCompleteId] = useState<string | null>(null);
  const [completeForm, setCompleteForm] = useState({
    evaluationDate: "", evaluatorName: "", scope: "", methodology: "",
    findingsSummary: "", overallResult: "Satisfactory", riskLevel: "Low",
    evidenceReference: "", remediationRequired: false, remediationSummary: "",
    remediationDueDate: "",
  });

  const completeMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/${completeId}/complete`, {
        method: "PATCH",
        body: JSON.stringify({
          ...completeForm,
          evaluationDate: completeForm.evaluationDate || new Date().toISOString(),
          remediationDueDate: completeForm.remediationDueDate || null,
        }),
      });
      if (!r.ok) { const e = await r.json(); throw new Error(e.message || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => {
      toast({ title: "Evaluation completed." });
      setCompleteId(null);
      queryClient.invalidateQueries({ queryKey: ["hipaa09-list"] });
      queryClient.invalidateQueries({ queryKey: ["hipaa09-summary"] });
      queryClient.invalidateQueries({ queryKey: ["hipaa09-overdue"] });
      if (detailId) queryClient.invalidateQueries({ queryKey: ["hipaa09-detail", detailId] });
    },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  // Remediation update form
  const [remId, setRemId] = useState<string | null>(null);
  const [remForm, setRemForm] = useState({
    remediationStatus: "InProgress", remediationSummary: "",
    remediationEvidenceReference: "", remediationDueDate: "",
  });

  const remMut = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`${BASE}/${remId}/remediation`, {
        method: "PATCH",
        body: JSON.stringify({
          ...remForm,
          remediationDueDate: remForm.remediationDueDate || null,
        }),
      });
      if (!r.ok) { const e = await r.json(); throw new Error(e.message || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => {
      toast({ title: "Remediation updated." });
      setRemId(null);
      queryClient.invalidateQueries({ queryKey: ["hipaa09-list"] });
      queryClient.invalidateQueries({ queryKey: ["hipaa09-summary"] });
      if (detailId) queryClient.invalidateQueries({ queryKey: ["hipaa09-detail", detailId] });
    },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  // Start / Delete mutations
  const startMut = useMutation({
    mutationFn: async (id: string) => {
      const r = await authFetch(`${BASE}/${id}/start`, { method: "PATCH" });
      if (!r.ok) { const e = await r.json(); throw new Error(e.message || "Unable to complete action"); }
      return r.json();
    },
    onSuccess: () => {
      toast({ title: "Evaluation started." });
      queryClient.invalidateQueries({ queryKey: ["hipaa09-list"] });
      queryClient.invalidateQueries({ queryKey: ["hipaa09-summary"] });
      if (detailId) queryClient.invalidateQueries({ queryKey: ["hipaa09-detail", detailId] });
    },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      const r = await authFetch(`${BASE}/${id}`, { method: "DELETE" });
      if (!r.ok) throw new Error("Unable to delete");
    },
    onSuccess: () => {
      toast({ title: "Evaluation deleted." });
      if (detailId) setDetailId(null);
      queryClient.invalidateQueries({ queryKey: ["hipaa09-list"] });
      queryClient.invalidateQueries({ queryKey: ["hipaa09-summary"] });
    },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  const summary = summaryQ.data;

  // ── Render ───────────────────────────────────────────────────────────────────

  // Detail view
  if (detailId && detailQ.data) {
    const ev = detailQ.data;
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-violet-600" />
              {ev.evaluationTitle}
            </span>
            <Button size="sm" variant="ghost" onClick={() => setDetailId(null)}>← Back</Button>
          </CardTitle>
          <CardDescription>{ev.evaluationType}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <div className="flex flex-wrap gap-2">
            <Badge variant={evalStatusBadge(ev.status) as any}>{ev.status}</Badge>
            {ev.riskLevel && <Badge variant={evalRiskBadge(ev.riskLevel) as any}>Risk: {ev.riskLevel}</Badge>}
            {ev.overallResult && <Badge variant="outline">Result: {ev.overallResult}</Badge>}
            {ev.materialChangeTriggered && <Badge variant="secondary">Material Change</Badge>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            {ev.scope           && <div><p className="font-medium text-xs text-muted-foreground">Scope</p><p>{ev.scope}</p></div>}
            {ev.evaluatedControls && <div><p className="font-medium text-xs text-muted-foreground">Controls Evaluated</p><p>{ev.evaluatedControls}</p></div>}
            {ev.evaluatorName   && <div><p className="font-medium text-xs text-muted-foreground">Evaluator</p><p>{ev.evaluatorName} ({ev.evaluatorRole})</p></div>}
            {ev.methodology     && <div><p className="font-medium text-xs text-muted-foreground">Methodology</p><p>{ev.methodology}</p></div>}
            {ev.evaluationDate  && <div><p className="font-medium text-xs text-muted-foreground">Evaluation Date</p><p>{ev.evaluationDate.slice(0,10)}</p></div>}
            {ev.nextEvaluationDate && <div><p className="font-medium text-xs text-muted-foreground">Next Evaluation</p><p>{ev.nextEvaluationDate.slice(0,10)}</p></div>}
            {ev.frequency       && <div><p className="font-medium text-xs text-muted-foreground">Frequency</p><p>{ev.frequency}</p></div>}
            {ev.evidenceReference && <div><p className="font-medium text-xs text-muted-foreground">Evidence Reference</p><p>{ev.evidenceReference}</p></div>}
          </div>

          {ev.findingsSummary && (
            <div><p className="font-medium text-xs text-muted-foreground mb-1">Findings</p>
              <p className="rounded bg-muted/30 border p-2">{ev.findingsSummary}</p></div>
          )}

          {ev.materialChangeTriggered && (
            <div className="rounded border border-amber-200 bg-amber-50 p-3 space-y-1">
              <p className="font-medium text-xs text-amber-700">Material Change</p>
              {ev.materialChangeDescription && <p>{ev.materialChangeDescription}</p>}
              {ev.relatedSystemOrPolicy     && <p className="text-xs">System/Policy: {ev.relatedSystemOrPolicy}</p>}
              {ev.materialChangeTriggerDate && <p className="text-xs">Trigger date: {ev.materialChangeTriggerDate.slice(0,10)}</p>}
            </div>
          )}

          {ev.remediationRequired && (
            <div className="rounded border border-red-200 bg-red-50 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <p className="font-medium text-xs text-red-700">Remediation</p>
                <Badge variant={remStatusBadge(ev.remediationStatus) as any}>{ev.remediationStatus ?? "Open"}</Badge>
              </div>
              {ev.remediationSummary      && <p className="text-sm">{ev.remediationSummary}</p>}
              {ev.remediationDueDate      && <p className="text-xs">Due: {ev.remediationDueDate.slice(0,10)}</p>}
              {ev.remediationCompletedDate && <p className="text-xs">Completed: {ev.remediationCompletedDate.slice(0,10)}</p>}
              {ev.remediationEvidenceReference && <p className="text-xs">Evidence: {ev.remediationEvidenceReference}</p>}
              {canManage && ev.remediationStatus !== "Completed" && (
                <Button size="sm" variant="outline" onClick={() => {
                  setRemId(ev.id);
                  setRemForm({ remediationStatus: "InProgress", remediationSummary: ev.remediationSummary ?? "", remediationEvidenceReference: ev.remediationEvidenceReference ?? "", remediationDueDate: ev.remediationDueDate?.slice(0,10) ?? "" });
                }}>Update Remediation</Button>
              )}
            </div>
          )}

          {canManage && (
            <div className="flex gap-2 flex-wrap pt-2">
              {(ev.status === "Planned" || ev.status === "Overdue") && (
                <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg gap-2" onClick={() => startMut.mutate(ev.id)} disabled={startMut.isPending}>Start</Button>
              )}
              {ev.status !== "Completed" && (
                <Button size="sm" variant="outline" onClick={() => {
                  setCompleteId(ev.id);
                  setCompleteForm({ evaluationDate: "", evaluatorName: ev.evaluatorName ?? "", scope: ev.scope ?? "", methodology: ev.methodology ?? "", findingsSummary: "", overallResult: "Satisfactory", riskLevel: "Low", evidenceReference: "", remediationRequired: false, remediationSummary: "", remediationDueDate: "" });
                }}>Complete</Button>
              )}
              {ev.remediationRequired && ev.remediationStatus !== "Completed" && (
                <Button size="sm" variant="outline" onClick={() => { setRemId(ev.id); setRemForm({ remediationStatus: "InProgress", remediationSummary: ev.remediationSummary ?? "", remediationEvidenceReference: ev.remediationEvidenceReference ?? "", remediationDueDate: ev.remediationDueDate?.slice(0,10) ?? "" }); }}>
                  Update Remediation
                </Button>
              )}
              <Button size="sm" variant="destructive" onClick={() => { if (confirm("Delete this evaluation?")) deleteMut.mutate(ev.id); }} disabled={deleteMut.isPending}>Delete</Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // List view
  return (
    <>
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-violet-600" />
          Security Evaluations
        </CardTitle>
        <CardDescription>
          Periodic evaluation of whether security policies, procedures, and safeguards remain reasonable and appropriate (§ 164.308(a)(8)).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">

        {/* Dashboard */}
        {summaryQ.isLoading ? <Skeleton className="h-20 w-full" /> : summary && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { label: "Total",               value: summary.total,               warn: false },
              { label: "Completed",           value: summary.completed,           warn: false },
              { label: "In Progress",         value: summary.inProgress,          warn: false },
              { label: "Planned",             value: summary.planned,             warn: false },
              { label: "Due Soon (30 days)",  value: summary.dueSoon,             warn: summary.dueSoon > 0 },
              { label: "Overdue",             value: summary.overdue,             warn: summary.overdue > 0 },
              { label: "Material Change",     value: summary.materialChange,      warn: false },
              { label: "Remediation Required", value: summary.remediationRequired, warn: false },
              { label: "Open Remediations",   value: summary.openRemediation,     warn: summary.openRemediation > 0 },
              { label: "Completion %",        value: `${summary.completionPct}%`, warn: summary.completionPct < 50 },
            ].map(m => (
              <div key={m.label} className={`rounded-lg border p-3 text-center ${m.warn ? "border-amber-300 bg-amber-50" : "border-muted bg-muted/30"}`}>
                <p className={`text-xl font-bold ${m.warn ? "text-amber-700" : "text-foreground"}`}>{m.value}</p>
                <p className="text-xs text-muted-foreground mt-1">{m.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Overdue alert */}
        {(overdueQ.data?.total ?? 0) > 0 && (
          <div className="rounded border border-red-300 bg-red-50 p-2 flex items-center gap-2 text-red-700 text-sm">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            {overdueQ.data!.total} evaluation(s) are overdue.
          </div>
        )}

        {/* Controls */}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex gap-2 flex-wrap">
            <select className="border rounded px-2 py-1 text-xs" value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1); }}>
              <option value="">All Types</option>
              {EVAL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select className="border rounded px-2 py-1 text-xs" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
              <option value="">All Statuses</option>
              {["Planned","InProgress","Completed","Overdue"].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select className="border rounded px-2 py-1 text-xs" value={riskFilter} onChange={e => { setRiskFilter(e.target.value); setPage(1); }}>
              <option value="">All Risk Levels</option>
              {["Low","Medium","High","Critical"].map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <select className="border rounded px-2 py-1 text-xs" value={matFilter} onChange={e => { setMatFilter(e.target.value); setPage(1); }}>
              <option value="">All Triggers</option>
              <option value="true">Material Change</option>
              <option value="false">Routine</option>
            </select>
          </div>
          {canManage && (
            <Button size="sm" variant="outline" onClick={() => setShowCreate(true)}>
              <Plus className="h-3 w-3 mr-1" /> Add Evaluation
            </Button>
          )}
        </div>

        {/* Evaluation list */}
        {listQ.isLoading ? <Skeleton className="h-24 w-full" /> : (
          <div className="space-y-2">
            {(listQ.data?.items ?? []).length === 0 && <p className="text-xs text-muted-foreground">No evaluations found.</p>}
            {(listQ.data?.items ?? []).map(ev => (
              <div key={ev.id} className="rounded border p-3 space-y-1 cursor-pointer hover:bg-muted/10 transition-colors"
                   onClick={() => setDetailId(ev.id)}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <span className="font-medium text-sm">{ev.evaluationTitle}</span>
                  <div className="flex gap-1 flex-wrap">
                    <Badge variant={evalStatusBadge(ev.status) as any}>{ev.status}</Badge>
                    {ev.riskLevel && <Badge variant={evalRiskBadge(ev.riskLevel) as any}>{ev.riskLevel}</Badge>}
                    {ev.materialChangeTriggered && <Badge variant="secondary">MatChange</Badge>}
                    {ev.remediationRequired && ev.remediationStatus !== "Completed" && (
                      <Badge variant={remStatusBadge(ev.remediationStatus) as any}>Rem:{ev.remediationStatus ?? "Open"}</Badge>
                    )}
                  </div>
                </div>
                <div className="text-xs text-muted-foreground flex flex-wrap gap-3">
                  <span>{ev.evaluationType}</span>
                  {ev.evaluatorName && <span>By: {ev.evaluatorName}</span>}
                  {ev.evaluationDate && <span>Evaluated: {ev.evaluationDate.slice(0,10)}</span>}
                  {ev.nextEvaluationDate && <span>Next: {ev.nextEvaluationDate.slice(0,10)}</span>}
                </div>
                {canManage && (
                  <div className="flex gap-1 pt-1" onClick={e => e.stopPropagation()}>
                    {(ev.status === "Planned" || ev.status === "Overdue") && (
                      <Button size="sm" variant="ghost" className="h-6 text-xs px-2"
                        onClick={() => startMut.mutate(ev.id)} disabled={startMut.isPending}>
                        Start
                      </Button>
                    )}
                    {ev.status !== "Completed" && (
                      <Button size="sm" variant="ghost" className="h-6 text-xs px-2"
                        onClick={() => { setCompleteId(ev.id); setCompleteForm({ evaluationDate: "", evaluatorName: ev.evaluatorName ?? "", scope: ev.scope ?? "", methodology: ev.methodology ?? "", findingsSummary: "", overallResult: "Satisfactory", riskLevel: "Low", evidenceReference: "", remediationRequired: false, remediationSummary: "", remediationDueDate: "" }); }}>
                        Complete
                      </Button>
                    )}
                    {ev.remediationRequired && ev.remediationStatus !== "Completed" && (
                      <Button size="sm" variant="ghost" className="h-6 text-xs px-2"
                        onClick={() => { setRemId(ev.id); setRemForm({ remediationStatus: "InProgress", remediationSummary: ev.remediationSummary ?? "", remediationEvidenceReference: ev.remediationEvidenceReference ?? "", remediationDueDate: ev.remediationDueDate?.slice(0,10) ?? "" }); }}>
                        Remediate
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="h-6 text-xs px-2 text-red-600"
                      onClick={() => { if (confirm("Delete?")) deleteMut.mutate(ev.id); }} disabled={deleteMut.isPending}>
                      Delete
                    </Button>
                  </div>
                )}
              </div>
            ))}
            {(listQ.data?.total ?? 0) > 10 && (
              <div className="flex items-center justify-center gap-2">
                <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
                <span className="text-xs text-muted-foreground">Page {page}</span>
                <Button size="sm" variant="ghost" disabled={(listQ.data?.items?.length ?? 0) < 10} onClick={() => setPage(p => p + 1)}>Next</Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>

    {/* Create Dialog */}
      {showCreate && (
        <Dialog open onOpenChange={v => { setShowCreate(v); if (!v) setErrors({}); }}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Security Evaluation</DialogTitle>
              <DialogDescription>Schedule or record a HIPAA security evaluation.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <div><Label>Evaluation Title *</Label><Input className={fieldCls("", errors, "evaluationTitle")} aria-invalid={!!errors.evaluationTitle} value={form.evaluationTitle} onChange={e => { setForm(p => ({ ...p, evaluationTitle: e.target.value })); clearErr(setErrors, "evaluationTitle"); }} />{fErr(errors, "evaluationTitle")}</div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Evaluation Type *</Label>
                  <select className="border rounded px-2 py-1 text-sm w-full" value={form.evaluationType} onChange={e => setForm(p => ({ ...p, evaluationType: e.target.value }))}>
                    {EVAL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Status *</Label>
                  <select className="border rounded px-2 py-1 text-sm w-full" value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}>
                    {["Planned","InProgress","Completed","Overdue"].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div><Label>Evaluator Name</Label><Input value={form.evaluatorName} onChange={e => setForm(p => ({ ...p, evaluatorName: e.target.value }))} /></div>
                <div><Label>Evaluator Role</Label><Input value={form.evaluatorRole} onChange={e => setForm(p => ({ ...p, evaluatorRole: e.target.value }))} /></div>
                <div><Label>Frequency</Label><Input value={form.frequency} onChange={e => setForm(p => ({ ...p, frequency: e.target.value }))} placeholder="Annual" /></div>
                <div><Label>Next Evaluation Date</Label><Input type="date" value={form.nextEvaluationDate} onChange={e => setForm(p => ({ ...p, nextEvaluationDate: e.target.value }))} /></div>
              </div>
              <div><Label>Scope</Label><Textarea value={form.scope} onChange={e => setForm(p => ({ ...p, scope: e.target.value }))} rows={2} /></div>
              <div><Label>Evaluated Controls</Label><Input value={form.evaluatedControls} onChange={e => setForm(p => ({ ...p, evaluatedControls: e.target.value }))} /></div>
              <div><Label>Methodology</Label><Input value={form.methodology} onChange={e => setForm(p => ({ ...p, methodology: e.target.value }))} /></div>
              <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={2} /></div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="mat-change" checked={form.materialChangeTriggered} onChange={e => setForm(p => ({ ...p, materialChangeTriggered: e.target.checked }))} />
                <Label htmlFor="mat-change">Triggered by material change</Label>
              </div>
              {form.materialChangeTriggered && (
                <div><Label>Material Change Description</Label><Textarea value={form.materialChangeDescription} onChange={e => setForm(p => ({ ...p, materialChangeDescription: e.target.value }))} rows={2} /></div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={() => { const errs: Record<string,string> = {}; required(errs, "evaluationTitle", form.evaluationTitle, "Evaluation Title"); if (Object.keys(errs).length) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } setErrors({}); createMut.mutate(); }} disabled={createMut.isPending}>
                {createMut.isPending ? "Saving…" : "Create Evaluation"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Complete Dialog */}
      {completeId && (
        <Dialog open onOpenChange={() => setCompleteId(null)}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Complete Evaluation</DialogTitle>
              <DialogDescription>All required fields must be provided to complete this evaluation.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <div><Label>Evaluation Date *</Label><Input type="date" value={completeForm.evaluationDate} onChange={e => setCompleteForm(p => ({ ...p, evaluationDate: e.target.value }))} /></div>
              <div><Label>Evaluator Name *</Label><Input value={completeForm.evaluatorName} onChange={e => setCompleteForm(p => ({ ...p, evaluatorName: e.target.value }))} /></div>
              <div><Label>Scope *</Label><Textarea value={completeForm.scope} onChange={e => setCompleteForm(p => ({ ...p, scope: e.target.value }))} rows={2} /></div>
              <div><Label>Methodology *</Label><Input value={completeForm.methodology} onChange={e => setCompleteForm(p => ({ ...p, methodology: e.target.value }))} /></div>
              <div><Label>Findings Summary *</Label><Textarea value={completeForm.findingsSummary} onChange={e => setCompleteForm(p => ({ ...p, findingsSummary: e.target.value }))} rows={3} /></div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Overall Result *</Label>
                  <select className="border rounded px-2 py-1 text-sm w-full" value={completeForm.overallResult} onChange={e => setCompleteForm(p => ({ ...p, overallResult: e.target.value }))}>
                    {["Satisfactory","Unsatisfactory","NeedsImprovement","NA"].map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Risk Level *</Label>
                  <select className="border rounded px-2 py-1 text-sm w-full" value={completeForm.riskLevel} onChange={e => setCompleteForm(p => ({ ...p, riskLevel: e.target.value }))}>
                    {["Low","Medium","High","Critical"].map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              </div>
              <div><Label>Evidence Reference</Label><Input value={completeForm.evidenceReference} onChange={e => setCompleteForm(p => ({ ...p, evidenceReference: e.target.value }))} /></div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="rem-req" checked={completeForm.remediationRequired} onChange={e => setCompleteForm(p => ({ ...p, remediationRequired: e.target.checked }))} />
                <Label htmlFor="rem-req">Remediation required</Label>
              </div>
              {completeForm.remediationRequired && (
                <div className="space-y-2">
                  <div><Label>Remediation Plan</Label><Textarea value={completeForm.remediationSummary} onChange={e => setCompleteForm(p => ({ ...p, remediationSummary: e.target.value }))} rows={2} /></div>
                  <div><Label>Remediation Due Date</Label><Input type="date" value={completeForm.remediationDueDate} onChange={e => setCompleteForm(p => ({ ...p, remediationDueDate: e.target.value }))} /></div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCompleteId(null)}>Cancel</Button>
              <Button onClick={() => completeMut.mutate()} disabled={completeMut.isPending}>
                {completeMut.isPending ? "Completing…" : "Complete Evaluation"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Remediation Update Dialog */}
      {remId && (
        <Dialog open onOpenChange={() => setRemId(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Update Remediation</DialogTitle>
              <DialogDescription>Track remediation progress for this evaluation finding.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <div>
                <Label>Remediation Status *</Label>
                <select className="border rounded px-2 py-1 text-sm w-full" value={remForm.remediationStatus} onChange={e => setRemForm(p => ({ ...p, remediationStatus: e.target.value }))}>
                  {["Open","InProgress","Completed","Overdue"].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div><Label>Remediation Summary</Label><Textarea value={remForm.remediationSummary} onChange={e => setRemForm(p => ({ ...p, remediationSummary: e.target.value }))} rows={2} /></div>
              <div><Label>Due Date</Label><Input type="date" value={remForm.remediationDueDate} onChange={e => setRemForm(p => ({ ...p, remediationDueDate: e.target.value }))} /></div>
              {remForm.remediationStatus === "Completed" && (
                <div><Label>Evidence Reference *</Label><Input value={remForm.remediationEvidenceReference} onChange={e => setRemForm(p => ({ ...p, remediationEvidenceReference: e.target.value }))} placeholder="Required for completion" /></div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRemId(null)}>Cancel</Button>
              <Button onClick={() => remMut.mutate()} disabled={remMut.isPending}>
                {remMut.isPending ? "Saving…" : "Update Remediation"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

// ── Per-bot compliance card with 10 tabbed HIPAA modules ─────────────────────
function BotHipaaComplianceCard({
  botId, botName, sources, canManage, reverifyingId, onReverify, onRefresh,
}: {
  botId: string;
  botName: string;
  sources: SourceVerification[];
  canManage: boolean;
  reverifyingId: string | undefined;
  onReverify: (sourceId: string) => void;
  onRefresh: () => void;
}) {
  const [expanded, setExpanded] = useState(false);

  // Aggregate stats from this bot's knowledge source controls
  const totalPassed  = sources.reduce((s, v) => s + v.passedCount,          0);
  const totalFailed  = sources.reduce((s, v) => s + v.failedCount,          0);
  const totalPending = sources.reduce((s, v) => s + v.manualRequiredCount,  0);
  const riskAccepted = sources.filter(v => v.overallStatus === "allowed").length;
  const blockedCount = sources.filter(v => v.overallStatus === "blocked").length;
  const total        = totalPassed + totalFailed;
  const pct          = total > 0 ? Math.round((totalPassed / total) * 100) : 100;
  const pctColor     = pct >= 90 ? "text-green-700" : pct >= 70 ? "text-amber-600" : "text-red-600";

  const isUnknown = botId === UNKNOWN_BOT_ID;

  return (
    <div className={`border rounded-lg overflow-hidden shadow-sm ${isUnknown ? "border-amber-300" : ""}`}>
      {/* ── Collapsible header ── */}
      <div
        className={`flex items-start gap-3 px-4 py-3.5 cursor-pointer hover:bg-muted/40 transition-colors ${isUnknown ? "bg-amber-50" : "bg-slate-50"}`}
        onClick={() => setExpanded(e => !e)}
        role="button"
        aria-expanded={expanded}
      >
        <div className="flex-1 min-w-0">
          {/* Bot name + counts */}
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="font-bold text-sm">{botName}</span>
            {isUnknown && (
              <Badge className="text-xs bg-amber-500 hover:bg-amber-500 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" aria-hidden="true" />
                Unresolved Bot
              </Badge>
            )}
            <Badge variant="outline" className="text-xs">
              {sources.length} Knowledge Source{sources.length !== 1 ? "s" : ""}
            </Badge>
            {blockedCount  > 0 && <Badge variant="destructive" className="text-xs">{blockedCount} blocked</Badge>}
            {riskAccepted  > 0 && <Badge className="text-xs bg-amber-600 hover:bg-amber-600">{riskAccepted} risk accepted</Badge>}
          </div>
          {/* Compliance stats */}
          <div className="flex items-center gap-4 text-xs flex-wrap">
            <span className="flex items-center gap-1 text-green-700">
              <CheckCircle2 className="w-3 h-3" aria-hidden="true" />
              {totalPassed} passed
            </span>
            {totalFailed > 0 && (
              <span className="flex items-center gap-1 text-red-600 font-medium">
                <XCircle className="w-3 h-3" aria-hidden="true" />
                {totalFailed} failed
              </span>
            )}
            {totalPending > 0 && (
              <span className="flex items-center gap-1 text-amber-600">
                <Clock className="w-3 h-3" aria-hidden="true" />
                {totalPending} need evidence
              </span>
            )}
            <span className={`font-semibold ${pctColor}`}>{pct}% compliant</span>
          </div>
        </div>
        <div className="flex-shrink-0 pt-0.5">
          {expanded
            ? <ChevronUp   className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
            : <ChevronDown className="w-4 h-4 text-muted-foreground" aria-hidden="true" />}
        </div>
      </div>

      {/* ── Expanded view ── */}
      {expanded && (
        <div className="border-t">

          {/* Unknown-bot: sources only + resolution hint */}
          {isUnknown ? (
            <div className="px-4 py-4 space-y-3">
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" aria-hidden="true" />
                <p>
                  These knowledge sources could not be matched to a bot in the current chatbot list.
                  This can happen when a bot was deleted or its ID changed. Re-verify the affected
                  sources after confirming the correct bot assignment.
                </p>
              </div>
              <SingleBotKnowledgeSourcesTab
                sources={sources}
                canManage={canManage}
                onReverify={onReverify}
                reverifyingId={reverifyingId}
                onRefresh={onRefresh}
              />
            </div>
          ) : (

          /* Known bot: full 10-tab view */
          <Tabs defaultValue="sources">
            {/* Tab strip */}
            <div className="border-b bg-muted/10 px-3 pt-2 overflow-x-auto">
              <TabsList className="flex flex-wrap h-auto gap-1 bg-transparent pb-0 min-w-max">
                <TabsTrigger value="sources"      className="text-xs px-2.5 py-1.5 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">Knowledge Sources</TabsTrigger>
                <TabsTrigger value="csp"          className="text-xs px-2.5 py-1.5 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">CSP &amp; Web Security</TabsTrigger>
                <TabsTrigger value="transmission" className="text-xs px-2.5 py-1.5 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">Transmission Security</TabsTrigger>
                <TabsTrigger value="audit"        className="text-xs px-2.5 py-1.5 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">Audit Controls</TabsTrigger>
                <TabsTrigger value="workforce"    className="text-xs px-2.5 py-1.5 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">Workforce Security</TabsTrigger>
                <TabsTrigger value="access"       className="text-xs px-2.5 py-1.5 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">Access Controls</TabsTrigger>
                <TabsTrigger value="risk"         className="text-xs px-2.5 py-1.5 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">Risk Assessment</TabsTrigger>
                <TabsTrigger value="encryption"   className="text-xs px-2.5 py-1.5 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">Encryption Controls</TabsTrigger>
                <TabsTrigger value="backup"       className="text-xs px-2.5 py-1.5 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">Backup &amp; Devices</TabsTrigger>
                <TabsTrigger value="incident"     className="text-xs px-2.5 py-1.5 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">Incident Management</TabsTrigger>
                <TabsTrigger value="compliance"   className="text-xs px-2.5 py-1.5 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">Compliance &amp; Privacy</TabsTrigger>
              </TabsList>
            </div>

            {/* Tab panels */}
            <div className="px-4 py-5">

              {/* 1 — Knowledge Sources */}
              <TabsContent value="sources" className="mt-0">
                <SingleBotKnowledgeSourcesTab
                  sources={sources}
                  canManage={canManage}
                  onReverify={onReverify}
                  reverifyingId={reverifyingId}
                  onRefresh={onRefresh}
                />
              </TabsContent>

              {/* 2 — CSP & Web Security */}
              <TabsContent value="csp" className="mt-0 space-y-6">
                <HipaaCspSecuritySection                canManage={canManage} />
                <HipaaXssSecuritySection                canManage={canManage} />
                <HipaaSensitiveFieldAutocompleteSection canManage={canManage} />
                <HipaaBrowserDataProtectionSection      canManage={canManage} />
              </TabsContent>

              {/* 3 — Transmission Security */}
              <TabsContent value="transmission" className="mt-0 space-y-6">
                <HipaaTransmissionEncryptionSection canManage={canManage} />
                <HipaaTransmissionSecuritySection   canManage={canManage} />
                <HipaaIntegritySection              canManage={canManage} />
              </TabsContent>

              {/* 4 — Audit Controls */}
              <TabsContent value="audit" className="mt-0 space-y-6">
                <HipaaAuditControlsSection     canManage={canManage} />
                <HipaaPhiAuditLoggingSection   canManage={canManage} />
                <HipaaDisclosureAccountingSection canManage={canManage} />
              </TabsContent>

              {/* 5 — Workforce Security */}
              <TabsContent value="workforce" className="mt-0 space-y-6">
                <HipaaWorkforceSecuritySection     canManage={canManage} />
                <HipaaSecurityAwarenessSection     canManage={canManage} />
                <HipaaSecurityResponsibilitySection canManage={canManage} />
                <TrainingSection                   canManage={canManage} />
              </TabsContent>

              {/* 6 — Access Controls */}
              <TabsContent value="access" className="mt-0 space-y-6">
                <HipaaAccessControlSection          canManage={canManage} />
                <HipaaInformationAccessSection      canManage={canManage} />
                <HipaaMinimumNecessaryAccessSection canManage={canManage} />
                <HipaaMinimumNecessarySection       canManage={canManage} />
                <HipaaPersonAuthenticationSection   canManage={canManage} />
                <PhiAccessSection />
              </TabsContent>

              {/* 7 — Risk Assessment */}
              <TabsContent value="risk" className="mt-0 space-y-6">
                <HipaaRiskAssessmentSection  canManage={canManage} />
                <HipaaRiskManagementSection  canManage={canManage} />
                <HipaaSecurityEvaluationSection canManage={canManage} />
              </TabsContent>

              {/* 8 — Encryption Controls */}
              <TabsContent value="encryption" className="mt-0 space-y-6">
                <HipaaEncryptionAtRestSection canManage={canManage} />
                <HipaaKeyManagementSection   canManage={canManage} />
                <HipaaEncryptionSection      canManage={canManage} />
              </TabsContent>

              {/* 9 — Backup & Device Controls */}
              <TabsContent value="backup" className="mt-0 space-y-6">
                <HipaaContingencyPlanningSection canManage={canManage} />
                <HipaaDeviceMediaControlsSection canManage={canManage} />
                <HipaaWorkstationSecuritySection canManage={canManage} />
                <HipaaFacilityAccessSection      canManage={canManage} />
              </TabsContent>

              {/* 10 — Incident Management */}
              <TabsContent value="incident" className="mt-0 space-y-6">
                <HipaaSecurityIncidentSection    canManage={canManage} />
                <HipaaIncidentManagementSection  canManage={canManage} />
                <HipaaBreachNotificationSection  canManage={canManage} />
                <IncidentSection                 canManage={canManage} />
              </TabsContent>

              {/* 11 — Compliance & Privacy */}
              <TabsContent value="compliance" className="mt-0 space-y-6">
                <HipaaPolicyDocumentationSection canManage={canManage} />
                <HipaaBusinessAssociateSection   canManage={canManage} />
                <HipaaAmendmentSection           canManage={canManage} />
                <HipaaIndividualAccessSection    canManage={canManage} />
                <BaaSection                      canManage={canManage} />
              </TabsContent>

            </div>
          </Tabs>
          )} {/* end isUnknown ternary */}
        </div>
      )}
    </div>
  );
}

// ── Main export — HipaaTab ────────────────────────────────────────────────────

export function HipaaTab({ canManage }: { canManage: boolean }) {
  const { data: summary, isLoading: summaryLoading } = useQuery<HipaaSummary>({
    queryKey: ["/api/organization/compliance/hipaa/summary"],
    staleTime: 0,
  });

  const { isLoading, botOrder, byBot, botNameMap, reverifyM, refresh } = useBotVerificationsData();

  return (
    <div className="space-y-4">
      {/* Dashboard summary cards */}
      {summaryLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : summary ? (
        <HipaaSummaryCards summary={summary} />
      ) : null}

      {/* Compliance alerts panel */}
      <HipaaComplianceAlertsPanel canManage={canManage} />

      {/* Per-bot compliance cards — each with 10 HIPAA module tabs */}
      {isLoading ? (
        <>
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </>
      ) : botOrder.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            No bots found. Add a chatbot to begin HIPAA compliance tracking.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {botOrder.map(botId => (
            <BotHipaaComplianceCard
              key={botId}
              botId={botId}
              botName={botNameMap.get(botId) ?? "Unknown Bot"}
              sources={byBot.get(botId) ?? []}
              canManage={canManage}
              reverifyingId={reverifyM.isPending ? reverifyM.variables : undefined}
              onReverify={id => reverifyM.mutate(id)}
              onRefresh={refresh}
            />
          ))}
        </div>
      )}
    </div>
  );
}
